// screens.jsx — all screens for the Nurse CNE app

// Hooks are called as `React.useX(...)` member calls so react-hooks/
// rules-of-hooks can see them (it only recognises `/^use[A-Z]/` and
// `React.useX`). Only the non-hook `Fragment` keeps a short alias.
const { Fragment: F } = React;

// ─────────────────────────────────────────────────────────────────
// Humanize a raw schema field name (e.g. "venue_en", "registration_url")
// into a readable label for change-log diffs. Strips locale suffixes
// (_en/_zh), replaces underscores with spaces, fixes a few acronyms,
// and provides a curated map for fields that need a friendlier name.
// ─────────────────────────────────────────────────────────────────
const FIELD_LABELS = {
  en: {
    venue:                 'Venue',
    date:                  'Date',
    time_text:             'Time',
    fee_hkd:               'Fee',
    fee_per_session_hkd:   'Fee per session',
    cost:                  'Fee',
    cost_member:           'Member fee',
    cost_non_member:       'Non-member fee',
    cost_early_bird:       'Early bird fee',
    cne_points:            'CNE points',
    cne_per_session:       'CNE points per session',
    cne_points_note:       'CNE points note',
    cem_points:            'CEM points',
    points:                'Points',
    quota:                 'Capacity',
    spots:                 'Spots',
    online_platform:       'Online platform',
    online:                'Online',
    title:                 'Title',
    blurb:                 'Description',
    description_short:     'Short description',
    description_full:      'Description',
    objectives:            'Objectives',
    syllabus:              'Syllabus',
    target:                'Target audience',
    target_audience:       'Target audience',
    entry_requirement:     'Entry requirement',
    instructor:            'Instructor',
    instructors:           'Instructors',
    speakers:              'Speakers',
    topics:                'Topics',
    theme:                 'Theme',
    organizer:             'Organizer',
    provider:              'Provider',
    provider_full:         'Provider',
    provider_short:        'Provider',
    provider_code:         'Provider code',
    academy:               'Academy',
    specialty_area:        'Specialty',
    materials_language:    'Materials language',
    duration_text:         'Duration',
    sessions:              'Sessions',
    application_deadline:  'Application deadline',
    registration_deadline: 'Registration deadline',
    registration_url:      'Registration link',
    registration_method:   'Registration method',
    registration_contact:  'Registration contact',
    early_bird_registration_deadline: 'Early bird deadline',
    abstract_submission_deadline:     'Abstract deadline',
    assessment_required:        'Assessment required',
    assessment_format:          'Assessment format',
    assessment_passing_criteria:'Passing criteria',
    attendance_requirement:     'Attendance requirement',
    certificate_type:           'Certificate type',
    certificate_requirements:   'Certificate requirements',
    notes:                 'Notes',
    url:                   'Course page',
    source_url:            'Source',
  },
  zh: {
    venue: '地點', date: '日期', time_text: '時間',
    fee_hkd: '費用', fee_per_session_hkd: '每節費用',
    cost: '費用', cost_member: '會員費', cost_non_member: '非會員費', cost_early_bird: '早鳥費',
    cne_points: 'CNE 分數', cne_per_session: '每節分數', cne_points_note: '分數備註',
    cem_points: 'CEM 分數', points: '分數',
    quota: '名額', spots: '名額',
    online_platform: '網上平台', online: '網上課程',
    title: '課程名稱', blurb: '簡介',
    description_short: '簡介', description_full: '詳細介紹',
    objectives: '課程目標', syllabus: '課程大綱',
    target: '對象', target_audience: '對象',
    entry_requirement: '入學要求',
    instructor: '導師', instructors: '導師', speakers: '講者',
    topics: '研討主題', theme: '主題',
    organizer: '主辦', provider: '機構', provider_full: '機構', provider_short: '機構',
    provider_code: '機構編號', academy: '學院',
    specialty_area: '專科', materials_language: '教材語言',
    duration_text: '時數', sessions: '節數',
    application_deadline: '申請截止', registration_deadline: '報名截止',
    registration_url: '報名連結', registration_method: '報名方法', registration_contact: '聯絡',
    early_bird_registration_deadline: '早鳥截止',
    abstract_submission_deadline: '摘要截止',
    assessment_required: '需要評核', assessment_format: '評核方式',
    assessment_passing_criteria: '合格要求', attendance_requirement: '出席要求',
    certificate_type: '證書類型', certificate_requirements: '證書要求',
    notes: '備註', url: '課程頁', source_url: '原始資料',
  },
};
function humanField(raw, lang) {
  if (!raw) return '';
  // Strip locale suffixes so "venue_en" and "venue_zh" map to the same label.
  const key = String(raw).replace(/_(en|zh|hk|tc|sc)$/i, '');
  const dict = FIELD_LABELS[lang === 'zh' ? 'zh' : 'en'];
  if (dict[key]) return dict[key];
  // Fallback: titlecase the underscored name.
  return key
    .split('_')
    .map(w => w.length <= 3 ? w.toUpperCase() : (w[0].toUpperCase() + w.slice(1)))
    .join(' ');
}

// Bilingual label for a stored sex value. Returns a {en,zh} pair so callers
// can pass it straight through L().
function sexLabel(sex) {
  switch (sex) {
    case 'male':              return STR.sexMale;
    case 'female':            return STR.sexFemale;
    case 'prefer_not_to_say': return STR.sexPreferNot;
    default:                  return { en: '—', zh: '—' };
  }
}

// Map a Supabase profile row → the USER-shape the screens already render.
function userFromProfile(p, records, baseline) {
  if (!p) return null;
  // Single display name (with graceful fallback to the legacy name columns
  // for rows created before the display_name field existed). Default →
  // "User" / "用戶" when the user skipped onboarding and has no name at all.
  const displayName = (p.display_name && p.display_name.trim())
    || (p.name_en && p.name_en.trim())
    || (p.name_zh && p.name_zh.trim())
    || '';
  const nameEn = displayName || 'User';
  const nameZh = displayName || '用戶';
  const initials = displayName
    ? (displayName.trim().split(/\s+/).map(s => s[0]).join('').slice(0, 2) || 'U').toUpperCase()
    : 'U';
  const shortName = displayName ? displayName.split(' ')[0] : nameEn;

  // Resolve the workplace label through the shared resolver (hk-hospitals.js):
  // canonical key → stored label → best-effort match of a legacy free-text
  // value, and for a non-hospital sector (NGO / clinic / home / school / other)
  // the user's own organisation name, never fuzzy-matched onto a hospital.
  const wp = window.resolveWorkplace ? window.resolveWorkplace(p) : { en: '', zh: '', sector: null };
  const hospitalEn = wp.en || '—';
  const hospitalZh = wp.zh || wp.en || '—';

  // Dual-registration: one descriptor per practising certificate (primary in the
  // flat columns, optional secondary in license2). Each carries its own earned
  // total + category split via licenseProgress, so a nurse on both parts of the
  // register sees a card per cycle. Single-licence nurses get exactly one, and
  // the flat top-level fields below stay = the primary licence so every existing
  // consumer that reads u.cycleStart/renewalDate/required/earned is unchanged.
  const licenses = profileLicenses(p).map(lic => {
    const prog = licenseProgress(records, lic);
    return { ...lic, earned: prog.earned, split: prog.split };
  });
  const primary = licenses[0];
  return {
    name:      { en: nameEn, zh: nameZh },
    shortName: { en: p.short_name_en || shortName, zh: p.short_name_zh || shortName },
    initials,
    sex:      p.sex || null,
    hospital: { en: hospitalEn, zh: hospitalZh },
    hospitalSector: wp.sector || null,
    role:     { en: p.role_en || 'Registered Nurse', zh: p.role_zh || '註冊護士' },
    cycleStart:  p.cycle_start,
    // No fabricated fallback date: '2026-12-31' here used to hand every
    // no-date profile a fake countdown (and would read as "expired" from
    // 2027-01-01). Null is the already-exercised shape — profileFromSession
    // and profileLicenses both emit it and every consumer guards it.
    renewalDate: p.renewal_date || null,
    // Exemption-aware: primary.required is 0 for a first-PC certificate.
    // ⚠ never `|| 45` — see the guard note on profileLicenses in data.js.
    required: primary.required,
    earned: primary.earned,
    licenses,
  };
}

// Fallback shape when the user IS signed in but we can't load their
// profile from Supabase (Airplane Mode, captive portal, transient
// 5xx). Used in place of the demo USER constant from data.js — which
// is bilingual sample data ("Wing Yi Chan / 陳大文") and must NEVER
// be presented to a real user. We surface only what the session
// already gives us (email → name + initial) and leave numeric fields
// at safe zeros so the home screen shows accurate "no data yet"
// states rather than fake progress.
function profileFromSession(session, records) {
  if (!session?.user) return null;
  const email = session.user.email || '';
  const localPart = email.split('@')[0] || 'You';
  const display = localPart.charAt(0).toUpperCase() + localPart.slice(1);
  // No cycle dates on this fallback, so cycleRecords applies only the
  // future-date guard — still consistent with the rest of the app.
  const earned = cyclePointsSummary(records, null, null).total;
  return {
    name:      { en: display, zh: display },
    shortName: { en: display, zh: display },
    initials:  (localPart[0] || '?').toUpperCase(),
    sex:       null,
    hospital:  { en: '—', zh: '—' },
    hospitalSector: null,
    role:      { en: 'Registered Nurse', zh: '註冊護士' },
    cycleStart:  null,
    renewalDate: null,
    required:    45,
    earned,
    // No licence descriptors on the offline fallback — HomeScreen synthesises a
    // single card from the flat fields above (same as the demo USER constant).
    licenses:    [],
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// HOME / DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────
// One-time notice (Batch B): the CNE cycle dates were corrected to the NCHK
// official window (30 Jun 2026 letter — cycle ends 6 months before PC expiry).
// Shown once to a signed-in nurse who has a cycle, then dismissed permanently via
// localStorage. Honest + low-noise: only a handful of users' totals actually
// shifted, but every window moved to the correct −6mo dates, so we explain it.
// 公告 — the server-driven notice stack, rendered above everything else on Home.
//
// This is the channel the hardcoded cards below could never be: an announcement
// here needs no build and no App Store review, which is the difference between
// telling nurses about a typhoon closure today and telling them in two days.
//
// Routing lives here rather than in NoticeBanner because routing is a screens.jsx
// concern — the banner stays a presentational component that the admin preview
// can render safely.
function NoticeStack({ notices, lang, onDismiss, onTab, onOpenSheet }) {
  if (!notices || notices.length === 0) return null;

  const openCta = (n) => {
    const url = n.cta_url;
    // ‼️ RE-VALIDATED AT TAP TIME. The CHECK constraint is the authority and the
    // row already passed it, but this is the last point before we hand a URL to
    // a browser, and it costs one call.
    if (!url || !window.DB?.isSafeNoticeCtaUrl?.(url)) {
      console.warn('[notice] refusing unsafe CTA url');
      return;
    }
    if (/^vital:\/\//i.test(url)) {
      // In-app navigation. Deliberately routed through the SAME allowlist the
      // deep-link handler uses, so a notice can never reach a screen a
      // vital:// link could not.
      const target = url.replace(/^vital:\/\//i, '').split(/[/?#]/)[0].toLowerCase();
      if (target === 'log') { onOpenSheet?.('log'); return; }
      if (['home', 'discover', 'record', 'saved', 'profile'].includes(target)) {
        onTab?.(target);
        return;
      }
      console.warn(`[notice] vital:// target not navigable: ${target}`);
      return;
    }
    openExternalUrl(url);
  };

  return (
    <>
      {notices.map(n => (
        <NoticeBanner key={n.id} notice={n} lang={lang}
          onDismiss={onDismiss} onCta={openCta} />
      ))}
    </>
  );
}

function CneCycleNotice({ lang, show }) {
  const KEY = 'vital:cneCycleNoticeSeen';
  const [seen, setSeen] = React.useState(() => {
    try { return !!localStorage.getItem(KEY); } catch (_) { return true; }
  });
  if (seen || !show) return null;
  const dismiss = () => {
    try { localStorage.setItem(KEY, '1'); } catch (_) {}
    setSeen(true);
  };
  return (
    <div style={{ padding: '12px 20px 0' }}>
      <div className="card" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: 14,
          borderLeft: '3px solid var(--accent)' }}>
        <div style={{ flex: 1, fontSize: 13, lineHeight: 1.5 }}>
          <div style={{ fontWeight: 700, marginBottom: 3 }}>
            {lang === 'zh' ? 'CNE 週期已更新' : 'CNE cycle dates updated'}
          </div>
          <div className="muted-2">
            {lang === 'zh'
              ? '已依香港護士管理局最新公告（2026 年 6 月）調整 CNE 週期 — 週期於執業證書到期日前 6 個月結束。你的本週期學分可能因此略有變動,這是正確的計算方式。'
              : 'Updated to match the NCHK’s latest announcement (June 2026): your CNE cycle now ends 6 months before your certificate expires. Your cycle points may have shifted slightly — this is the corrected figure.'}
          </div>
        </div>
        <button type="button" onClick={dismiss}
          aria-label={lang === 'zh' ? '關閉' : 'Dismiss'}
          style={{ flexShrink: 0, appearance: 'none', border: 0, background: 'transparent',
            cursor: 'pointer', color: 'var(--fg-3)', fontSize: 18, lineHeight: 1, padding: 2 }}>
          ✕
        </button>
      </div>
    </div>
  );
}

// Free CNE entry — the one surface that says "there is CNE here that costs you
// nothing", and the reason it is FREE for everyone, guests included: the more
// nurses who find free CNE through Vital, the more course clicks the catalogue
// earns, which is what makes it worth a provider's while to be listed. Paywalling
// this would trade a network effect for a few subscriptions.
//
// ‼️ THE NUMBER IS A PROMISE. It comes from freeCneSummary(), whose predicate is
// deliberately identical to what the destination list shows (Discover, Free chip
// on, actionable tab). Do not compute a number here — if the count and the list
// ever disagree, she finds out by counting the rows on screen.
//
// ‼️ COPY STAYS HEDGED ("目錄內目前有最多 N 分" / "up to N"). The figure knows
// nothing about who is asking: not her register part, not her specialty, not
// which of these she has already completed. It may only become a personal claim
// ("你仍可取得 N 分") once eligibility filtering and already-completed
// de-duplication exist. Promoting the wording before then is the difference
// between a useful number and a false one.
//
// Renders NOTHING when the catalogue has no free courses — an empty state on
// Home would be noise, and a padded number would be a lie. Discover's own empty
// state handles the case where she taps through and the list is short.
function FreeCneCard({ lang, summary, onOpen }) {
  if (!summary || !summary.count || !onOpen) return null;
  const zh = lang === 'zh';
  // 14.5 stays 14.5; 15.0 renders as 15.
  const pts = Number(summary.points || 0);
  const ptsText = Number.isInteger(pts) ? String(pts) : pts.toFixed(1);
  return (
    <div style={{ padding: '14px 20px 0' }}>
      <button type="button" className="card" onClick={onOpen}
        style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12,
                 border: '1px solid var(--primary)', background: 'var(--primary-soft)', cursor: 'pointer' }}>
        <div style={{ width: 38, height: 38, flexShrink: 0, borderRadius: 19, background: 'var(--primary)',
                      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="sparkle" size={18} stroke={1.9} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 650 }}>
            {zh ? '免費 CNE 專區' : 'Free CNE'}
          </div>
          <div className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginTop: 2 }}>
            {zh
              ? `目錄內目前有最多 ${ptsText} 分免費 CNE · ${summary.count} 個課程`
              : `Up to ${ptsText} free CNE points in the catalogue right now · ${summary.count} course${summary.count === 1 ? '' : 's'}`}
          </div>
        </div>
        <Icon name="chev-r" size={18} color="var(--fg-3)" />
      </button>
    </div>
  );
}

// Planner interest — a DEMAND TEST for a feature that does not exist yet.
//
// WHAT IT IS FOR. Wanting to browse free courses is not the same as wanting
// Vital to plan the cheapest route to the remaining points, and building the
// planner before knowing which is true would be weeks spent on a guess. This
// card asks directly, and the opt-in is the answer.
//
// ‼️ IT MUST NEVER TAKE MONEY, open the paywall, or quote a price. Selling a
// feature that does not exist is a refund request, an App Review problem and a
// trust problem all at once. It does say the feature will be part of Pro,
// because letting someone opt in and only later discover it is paid would be
// the same bait in slower motion.
//
// ‼️ IT COLLECTS NO ANALYTICS. PRIVACY.md states in writing — repeated in-app
// and on the public site — that Vital collects no analytics events, so there is
// deliberately no impression counter, no click event and no funnel here. The
// measurable signal is the opt-in itself: a stored preference the nurse set on
// purpose, exactly like her reminder settings, which is what "data needed for
// the features the user opts in to" already covers. That is also a STRONGER
// signal than a click-through rate would have been — it costs her something to
// say yes. If you ever want the rate rather than the count, the denominator is
// "signed-in nurses with a positive gap", which is derivable from data already
// stored. Do not add an event stream to get it.
//
// Hidden entirely when there is no gap to plan for — including the first-PC
// exempt case, where `required` is 0 and the subtraction can never be positive.
// The route card — Pro's recurring surface, and the one place the app tells a
// nurse HOW to finish rather than how far she has to go.
//
// ‼️ FREE SEES THE GAP; PRO SEES THE ROUTE. The shortfall is her compliance
// status — a fact about her own records — and paywalling a compliance fact in a
// professional-compliance app is indefensible (see the gate rules at the top of
// paywall.jsx). What Pro buys is the WORK: which courses, in what order, at what
// cost. Never move the gap number behind `isPro`.
//
// ‼️ IT RENDERS NOTHING WHEN THERE IS NO ROUTE TO SHOW. Today the catalogue
// carries no cne_relation classification, so cneRoutePlan() legitimately returns
// zero coverage — and a paid card reading "0 of 27 points planned" is worse than
// no card. The Beta interest card below takes over in that state, so the app
// degrades to asking rather than to an empty promise. This is also why the route
// is NOT yet listed in PRO_BENEFITS: that list may only name things the gate
// actually delivers.
//
// ‼️ THE WORD "CHEAPEST" IS CONDITIONAL ON costCertainty. 84% of the catalogue
// publishes member and non-member prices; without a membership on file the plan
// is priced at the non-member tier and may still be beaten by a tier we cannot
// see. In that state the copy must say "from" and must not claim cheapest.
//
// ‼️ TWO PRECONDITIONS, BOTH ENFORCED BY PROP PRESENCE RATHER THAN BY A FLAG —
// the same convention ContinueLoggingDialog uses for `onUpgrade`, because a
// handler that cannot exist is impossible to render a dead button from:
//
//   • `onExplore`        — passed only when window.proPurchasable(). Without it
//     the Free variant of this card is nothing BUT an upsell whose only
//     destination is 「訂閱功能暫時無法使用」, so the whole card stands down.
//     Pro subscribers are unaffected on every platform: the entitlement is
//     server-side, so a nurse who subscribed on iOS keeps the full route in the
//     web app. Never gate the Pro branch on purchasability.
//   • `onNotStarted`     — passed only when the answer can actually be STORED,
//     i.e. the licence carries a cycleStart to key it under. See the no-window
//     note below: these are two different dates and two different questions.
//   • `onFixCycleDates`  — the way out of both date-shaped dead ends.
const ROUTE_ROWS_COLLAPSED = 4;
function RoutePlanCard({ lang, plan, isPro, expanded = true, onExpandedChange, personalised = false, notStarted = false, notStartedBusy = false, notStartedError = '', canSwap = false, swapped = false, enrolledIds, onExplore, onOpenCourse, onLogRecord, onPersonalise, onNotStarted, onFixCycleDates, onSwap, onResetSwap }) {
  // ⚠️ HOOK-ORDERING CONSTRAINT — every hook in this component must stay ABOVE
  // the early returns below (`!plan`, no courses, no records). Same rule as
  // HomeScreen: a hook after a conditional return changes the hook count
  // between renders and React tears the card down.
  //
  // How many of the route's courses are listed. The rest sit behind a
  // disclosure — see ROUTE_ROWS_COLLAPSED at the render site.
  const [showAllRows, setShowAllRows] = React.useState(false);
  // Collapse again whenever the route itself changes (換一個, a new cycle, a
  // newly logged record). Leaving it open would carry "show all 6" onto a route
  // that now has three, so the button state would describe the previous plan.
  // In-render reset rather than an effect: React's own pattern for deriving
  // state from changed input, and it re-renders before committing so there is
  // no flash of the stale list.
  const routeIdsKey = (plan?.plans?.cheapest?.items || []).map((i) => i.course?.id).join(',');
  const routeIdsRef = React.useRef(routeIdsKey);
  if (routeIdsRef.current !== routeIdsKey) {
    routeIdsRef.current = routeIdsKey;
    if (showAllRows) setShowAllRows(false);
  }

  if (!plan || !plan.plans) return null;
  const best = plan.plans.cheapest;
  if (!best || !best.courses.length) return null;      // nothing to show → show nothing

  const zh = lang === 'zh';
  const money = (n) => `HK$${Math.round(n).toLocaleString('en-US')}`;
  // ‼️ NON-FINITE MUST NOT THROW. This card sits on the Home tab, so an
  // undefined field here took the whole screen down rather than one line with
  // it — `n.toFixed` on undefined, found by the harness. An em dash is a poor
  // number but an honest one; printing 0 would be a lie about her progress.
  const num = (n) => (Number.isFinite(n) ? (Number.isInteger(n) ? String(n) : n.toFixed(1)) : '—');
  const certain = plan.costCertainty === 'confirmed';
  // Days to the CNE deadline. "在 N 日內" beats a bare date for anything close
  // enough to act on; further out, the date itself is the more useful fact.
  const dueDays = plan.window?.end && window.daysUntil
    ? window.daysUntil(plan.window.end, window.todayIso?.())
    : null;

  // ‼️ NO BOUNDED WINDOW ⇒ THERE IS NO "THIS CYCLE" TO TALK ABOUT.
  //
  // Every number below this line is scoped by cneWindow(cycleStart, renewalDate),
  // and cycleRecords' bounds are `(!start || …) && (!end || …)` — a null end is
  // not "no limit found", it is NO LIMIT AT ALL. So the card was printing
  // 「你尚欠 45 分」 against every record the nurse had ever logged, asking about
  // a cycle it could not delimit, and showing no deadline. The onboarding date
  // step is skippable and the majority of live profiles skip it (_calendarYear).
  //
  // ‼️ THE DISCRIMINATOR IS `plan.window.end`, NOT "are both date columns set".
  // Read cneWindow before changing this — pcFrom is IGNORED whenever pcTo is
  // present, so the two columns are not symmetric and one of them is not a
  // weaker version of the other:
  //
  //     cycleStart + renewalDate → { 2026-07-01, 2029-06-30 }   bounded
  //     renewalDate ALONE        → { 2026-07-01, 2029-06-30 }   bounded, IDENTICAL
  //     cycleStart ALONE         → { 2026-01-01, null       }   unbounded at the top
  //     neither                  → { null,       null       }   unbounded
  //
  // The expiry is the anchor (CycleDatesField derives the start from it via
  // pcStartFromEnd; the start is stored for exports and rollover). A guard
  // demanding BOTH columns would therefore stand this card down for a nurse
  // whose window, gap, deadline and route are all exactly right — and for a
  // subscriber that is a paid feature withheld. Deriving from the plan's own
  // window also means there is no second copy of the rule to drift.
  //
  // ‼️ THIS IS NOT THE SAME QUESTION AS 「我今個週期未開始」. That answer is keyed
  // `{ [licKey]: cycleStart }` so a rollover re-asks, so it needs cycleStart
  // specifically — which a renewalDate-only profile does not have even though
  // its window is perfect. Two dates, two predicates: this branch owns the
  // window, and `onNotStarted`'s presence owns the answer (see the call site).
  //
  // Fails CLOSED. Without a handler to reach the date field this card would be
  // the same dead end in different words, so it renders nothing at all.
  //
  // ‼️ THIS IS NOT A PRO STATE. It is a data-completeness prompt and it reads
  // identically for both tiers — knowing when her cycle ends is not something a
  // nurse should have to buy.
  if (!plan.window?.end) {
    if (!onFixCycleDates) return null;
    return (
      <div style={{ padding: '12px 20px 0' }}>
        <div className="card">
          <div style={{ fontSize: 13.5, fontWeight: 650 }}>
            {zh ? '仲未知你今個 CNE 週期幾時開始同結束'
                : 'We don’t know when your CNE cycle starts or ends'}
          </div>
          <div className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginTop: 3 }}>
            {zh
              ? '填返你執業證書嘅到期日,Vital 就可以定出你今個週期嘅範圍,計出你仲欠幾多同幾時截止。'
              : 'Add your practising certificate’s expiry date and Vital can work out your cycle window — what you still owe, and by when.'}
          </div>
          <button type="button" className="btn btn-primary"
            style={{ marginTop: 10, padding: '7px 16px', fontSize: 12.5 }}
            onClick={onFixCycleDates}>
            {zh ? '設定執業證書日期' : 'Set your certificate dates'}
          </button>
        </div>
      </div>
    );
  }

  // ‼️ NOTHING TO SELL ⇒ NOTHING TO SHOW. On a platform with no storefront the
  // Free variant of this card is, in its entirety, an advertisement: a pitch
  // line and a button, both leading to a paywall that cannot take her money.
  // The gap number in the header is NOT a reason to keep it — the wallet ring
  // directly above already states her progress, and 「FREE SEES THE GAP」 (see
  // the header) is a rule about not paywalling a compliance fact, not a licence
  // to render a headless advert. Pro is untouched: `isPro` comes from the
  // server, so a subscriber sees the full route here on web exactly as on iOS.
  //
  // ‼️ DELIBERATELY BELOW THE WINDOW GUARD, NOT ABOVE IT. The date prompt is not
  // an upsell — it carries no Pro branding, asks for nothing but her own
  // certificate expiry, and is the only thing on Home that closes that blank for
  // a nurse who finished onboarding and skipped the date step. Ordering this
  // first would have withheld it from every free nurse on the web, which is the
  // one platform where she has no App Store sheet to stumble into it from.
  if (!isPro && typeof onExplore !== 'function') return null;
  //
  // With nothing logged, `gap` is simply the entire requirement — an arithmetic
  // fact about an empty table, not a finding about her. Live data 2026-08-09:
  // 145 of 190 onboarded nurses have logged nothing, and 136 of those accounts
  // are 31–90 days old, so this is the MAJORITY case, not an edge one. Showing
  // "你尚欠 45 分 · HK$5,970" to them is wrong if they did the CNE and never
  // logged it, and alarming if they simply have not started.
  //
  // This state is deliberately identical for Free and Pro: honesty about what we
  // know is not a paid feature, and a Pro subscriber shown a confident wrong
  // number is a refund, not a retention win. No gap number, no price, no route —
  // one sentence and the fastest path to making the number real.
  // ‼️ ZERO RECORDS IS TWO DIFFERENT NURSES, AND ONLY ONE OF THEM CAN ACT ON
  // "log your first course". The other has genuinely not started this cycle —
  // she has nothing to log, so without a second answer the card is a dead end
  // she can never leave, and if she is paying for Pro the feature she bought
  // never renders anything at all. `notStarted` is her answer (stored per
  // licence per cycle — see markNotStarted in HomeScreen), and it turns the
  // unknown into a known: earned is zero, so the full requirement is a real
  // plan rather than an arithmetic fact about an empty table.
  if (!plan.cycleRecordCount && !notStarted) {
    // ‼️ WHAT A NEW SUBSCRIBER IS OFFERED HERE IS A SETUP STEP, NOT A CHORE.
    // The card used to hand her one instruction — "log a course first" — which
    // is a task she has to go and do before the thing she paid for does
    // anything. When Pro is on and the setup questions are still unanswered,
    // 開始使用 leads instead: memberships, topics and timing all feed the plan
    // and can be answered in about a minute, on the spot.
    //
    // ‼️ THE LOG PATH STAYS, as the secondary. It is the only thing that makes
    // the route appear at all — no records, no plan, however well personalised
    // — so removing it would leave the card able to start something it can
    // never finish. Primary vs secondary is the fix; a single button is not.
    //
    // ‼️ AND ONLY FOR A SUBSCRIBER. For a free nurse 開始使用 would open a
    // Pro-only sheet she has not bought — the headline stays identical for both
    // tiers (honesty about what we know is not a paid feature) and so does her
    // one CTA.
    // ‼️ WHEN SETUP LEADS, IT LEADS ALONE. The two progress answers move INSIDE
    // that flow (ProSetupSheet's last step), so this card asks one thing at a
    // time instead of putting three competing actions on a card whose own
    // headline is a question. Once she has been through setup — or if she is on
    // the free tier, where 開始使用 would open a sheet she has not bought — the
    // answers are offered here directly, because then this is the only place
    // left to give them.
    const offerSetup = isPro && !!onPersonalise && !personalised;
    return (
      <div style={{ padding: '12px 20px 0' }}>
        <div className="card">
          <div style={{ fontSize: 13.5, fontWeight: 650 }}>
            {zh ? '我哋仲未知你今個週期完成咗幾多分'
                : 'We don’t know your progress this cycle yet'}
          </div>
          <div className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginTop: 3 }}>
            {/* ‼️ "記錄第一個課程" PRESUPPOSED THAT SHE HAS ONE. For a nurse who
                has genuinely not started, that sentence describes something she
                cannot do — so it names the courses she has DONE (which may be
                none) and the answer for "none" sits directly below. */}
            {offerSetup
              ? (zh
                  ? '先花一分鐘設定你的會籍同興趣,跟住答一條問題,Vital 就可以計出你仲欠幾多、用邊啲課程補返最抵。'
                  : 'Spend a minute on your memberships and interests, answer one question, and Vital can work out what’s left and the cheapest way to cover it.')
              : (zh
                  ? '記錄咗你做過嘅課程之後,Vital 就可以計出你仲欠幾多,以及點樣補返最抵。'
                  : 'Once the courses you have done are logged, Vital can work out what’s left and the best way to cover it.')}
          </div>
          {offerSetup ? (
            // ONE action. 設定會籍同興趣 → then the sheet asks the progress
            // question as its last step (ProSetupSheet askProgress).
            <button type="button" className="btn btn-primary"
              style={{ marginTop: 10, padding: '7px 16px', fontSize: 12.5 }}
              onClick={onPersonalise}>
              {zh ? '開始使用' : 'Set up Vital Pro'}
            </button>
          ) : (
            <React.Fragment>
              {onLogRecord && (
                <button type="button" className="btn btn-ghost"
                  style={{ marginTop: 10, padding: '6px 14px', fontSize: 12.5 }}
                  onClick={onLogRecord}>
                  {zh ? '記錄一個課程' : 'Log a course'}
                </button>
              )}
              {/* The other answer. Deliberately quieter — most nurses in this
                  state DO have courses to log (measured 2026-08-09: 136 of the
                  145 zero-record accounts are 31–90 days old, so they are not
                  fresh cycles) — but for the ones who genuinely have not begun,
                  this is the only way out of the card.

                  ‼️ PRESENT ONLY WHEN THE ANSWER CAN BE STORED. It is keyed
                  `{ [licKey]: cycleStart }` so a rollover re-asks; with no
                  cycleStart there is no key, markNotStarted returned silently,
                  and this read as a broken button. The call site now withholds
                  the handler in that state rather than rendering a control whose
                  write cannot land — and the fallback below keeps the way out
                  open, because a renewalDate-only profile has a perfectly good
                  window and must not lose its exit with it.

                  What remains reachable is the write itself failing (offline,
                  RLS, a dropped connection), and THAT must be visible: a silent
                  `if (!error)` is indistinguishable from a broken button. */}
              {onNotStarted && (
                <button type="button"
                  disabled={notStartedBusy}
                  onClick={onNotStarted}
                  style={{
                    display: 'block', marginTop: 10, padding: '4px 0', minHeight: 32,
                    background: 'none', border: 'none', font: 'inherit', fontSize: 12,
                    color: 'var(--primary)', textDecoration: 'underline',
                    textUnderlineOffset: 3, cursor: 'pointer',
                  }}>
                  {notStartedBusy
                    ? (zh ? '儲存中…' : 'Saving…')
                    : (zh ? '我今個週期未開始 →' : 'I haven’t started this cycle →')}
                </button>
              )}
              {/* Same exit, for the nurse whose window is fine but whose
                  certificate START was never stored (renewalDate alone yields a
                  complete window — see the no-window note above). She cannot be
                  asked the per-cycle question, so she is offered the one field
                  that lets her answer it afterwards. Without this she would sit
                  in the pre-notStarted dead end the branch above exists to
                  close. */}
              {!onNotStarted && onFixCycleDates && (
                <button type="button"
                  onClick={onFixCycleDates}
                  style={{
                    display: 'block', marginTop: 10, padding: '4px 0', minHeight: 32,
                    background: 'none', border: 'none', font: 'inherit', fontSize: 12,
                    color: 'var(--primary)', textDecoration: 'underline',
                    textUnderlineOffset: 3, cursor: 'pointer',
                  }}>
                  {zh ? '我今個週期未開始 —— 先補上證書日期 →' : 'I haven’t started — add my certificate dates →'}
                </button>
              )}
              {!!notStartedError && (
                <div role="alert" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 6, color: 'var(--danger, inherit)' }}>
                  {notStartedError}
                </div>
              )}
            </React.Fragment>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 20px 0' }}>
      <div className={'card route-card' + (isPro ? ' is-pro' : '')}>
        {isPro && (
          <div className="route-pro-heading">
            <div className="route-pro-label">
              <Icon name="sparkle" size={11} stroke={2} />
              {zh ? 'Vital Pro · 你的補分路線' : 'Vital Pro · your CNE route'}
            </div>
            <button type="button" className="route-collapse-btn"
              aria-expanded={expanded}
              aria-controls="vital-pro-route-details"
              onClick={() => onExpandedChange?.(!expanded)}>
              <span>{expanded ? (zh ? '收起' : 'Collapse') : (zh ? '展開' : 'Expand')}</span>
              <span className={'route-collapse-icon' + (expanded ? ' is-open' : '')} aria-hidden="true">
                <Icon name="chev-d" size={14} stroke={2} />
              </span>
            </button>
          </div>
        )}
        {/* Free tier: the compliance fact, always.
            ‼️ A NURSE WHO ANSWERED "not started" IS NOT "SHORT" OF ANYTHING.
            `gap` is arithmetically the same number either way, but 「你尚欠 45
            分」 says she has fallen behind, and someone at the start of a
            three-year cycle has not. Same figure, true sentence. */}
        {/* Header: the two facts that frame everything below. The deadline is
            its own element rather than a "·"-joined fragment so it can carry an
            urgency state without the headline shifting. */}
        <div className="route-head">
          <span className="route-gap">
            {notStarted && !plan.earned
              ? (zh ? `今個週期需要 ${num(plan.required)} 分` : `${num(plan.required)} points needed`)
              : (zh ? `你尚欠 ${num(plan.gap)} 分` : `${num(plan.gap)} points to go`)}
          </span>
          {plan.window?.end && (
            <span className={'route-due' + (dueDays != null && dueDays <= 180 ? ' is-near' : '')}>
              {dueDays != null && dueDays >= 0 && dueDays <= 180
                ? (zh ? `${dueDays} 日內截止` : `${dueDays} days left`)
                : (zh ? `截止 ${fmtDate(plan.window.end, lang)}` : `by ${fmtDate(plan.window.end, lang)}`)}
            </span>
          )}
        </div>

        {!isPro ? (
          <React.Fragment>
            <div className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginTop: 4 }}>
              {zh
                ? `Vital Pro 可以按目錄現有課程，計出補完這些分數的路線同費用。`
                : 'Vital Pro works out which listed courses close that gap, and what they cost.'}
            </div>
            <button type="button" className="btn btn-ghost"
              style={{ marginTop: 10, padding: '6px 14px', fontSize: 12.5 }}
              onClick={onExplore}>
              {zh ? '了解 Vital Pro' : 'Explore Vital Pro'}
            </button>
          </React.Fragment>
        ) : (
          <React.Fragment>
            {/* THE COMMITMENT, once, at the weight of a decision — not a fourth
                sentence at the same size as everything else. The label carries
                what kind of route it is; the number carries what it costs. */}
            <div className="route-summary">
              <span className="route-summary-label">
                {plan.routeComplete
                  ? (swapped
                      ? (zh ? '另一條路線' : 'Another route')
                      : certain
                        ? (zh ? '最低成本路線' : 'Lowest-cost route')
                        : (zh ? '低成本候選路線' : 'Low-cost candidate route'))
                  : (zh ? '目前目錄可規劃' : 'Plannable today')}
                <span style={{ display: 'block', marginTop: 2, color: 'var(--fg-3)' }}>
                  {plan.routeComplete
                    ? (zh ? `${best.count} 個課程 · 補足 ${num(plan.gap)} 分`
                          : `${best.count} course${best.count === 1 ? '' : 's'} · covers ${num(plan.gap)} pts`)
                    : (zh ? `${num(plan.coveragePoints)} / ${num(plan.gap)} 分`
                          : `${num(plan.coveragePoints)} of ${num(plan.gap)} pts`)}
                </span>
              </span>
              <span className="route-summary-cost">
                {best.cost > 0
                  ? (<React.Fragment>
                      {!certain && <span className="route-from">{zh ? '由 ' : 'from '}</span>}
                      {money(best.cost)}
                      {!certain && <span className="route-from">{zh ? ' 起' : ''}</span>}
                    </React.Fragment>)
                  : (zh ? '免費' : 'Free')}
              </span>
            </div>

            {expanded && (
            <div id="vital-pro-route-details" className="route-details">

            {/* One course, one row: a real 44px target with a chevron, the
                points as the leading fact (that is what she is buying) and the
                price after it.

                ‼️ 已報名 IS PART OF THE PLAN, NOT DECORATION. A course she has
                already registered for legitimately stays in the route —
                enrolling is not completing, so the points are still unearned and
                it is genuinely the cheapest way to close that part of the gap.
                What was wrong was showing it identically to five courses she has
                never heard of: she cannot tell which row is already handled, and
                the obvious failure is registering for the same course twice.
                The badge says which. */}
            <div className="route-rows">
              {(showAllRows ? best.items : best.items.slice(0, ROUTE_ROWS_COLLAPSED)).map((item) => {
                const isEnrolled = !!(enrolledIds && enrolledIds.has && enrolledIds.has(item.course.id));
                return (
                <button key={item.course.id} type="button" className="route-row"
                  onClick={() => onOpenCourse && onOpenCourse(item.course.id)}
                  aria-label={`${L(item.course.title, lang)} · ${num(item.points)}${zh ? ' 分' : ' points'} · ${item.cost > 0 ? money(item.cost) : (zh ? '免費' : 'Free')}${isEnrolled ? ` · ${zh ? '已報名' : 'Enrolled'}` : ''}`}>
                  <span className="route-row-main">
                    <span className="route-row-title">{L(item.course.title, lang)}</span>
                    <span className="route-row-meta">
                      <span className="route-row-pts">{num(item.points)}{zh ? ' 分' : ' pts'}</span>
                      <span aria-hidden="true">·</span>
                      <span>{item.cost > 0 ? money(item.cost) : (zh ? '免費' : 'Free')}</span>
                      {item.course.online && (
                        <React.Fragment>
                          <span aria-hidden="true">·</span>
                          <span>{zh ? '網上' : 'Online'}</span>
                        </React.Fragment>
                      )}
                      {isEnrolled && (
                        <span className="route-row-enrolled">
                          <Icon name="check" size={10} stroke={2.6} />
                          {zh ? '已報名' : 'Enrolled'}
                        </span>
                      )}
                    </span>
                  </span>
                  <Icon name="chev-r" size={15} stroke={1.8} color="var(--fg-3)" />
                </button>
                );
              })}
              {/* ‼️ THIS WAS A DEAD END. It read 「及其他 2 個課程」 as plain text
                  with no way to reach them — the summary above promises 「6 個
                  課程」 and the card could only ever show four, so two of the
                  courses in her own plan were unreachable from the only screen
                  that lists them. A count is not a disclosure; it has to open. */}
              {best.items.length > ROUTE_ROWS_COLLAPSED && (
                <button type="button" className="route-row-more"
                  aria-expanded={showAllRows}
                  onClick={() => setShowAllRows((v) => !v)}>
                  <span>
                    {showAllRows
                      ? (zh ? '收起課程' : 'Show fewer')
                      : (zh ? `顯示其餘 ${best.items.length - ROUTE_ROWS_COLLAPSED} 個課程`
                            : `Show ${best.items.length - ROUTE_ROWS_COLLAPSED} more`)}
                  </span>
                  <span className={'route-more-icon' + (showAllRows ? ' is-open' : '')} aria-hidden="true">
                    <Icon name="chev-d" size={13} stroke={2} />
                  </span>
                </button>
              )}
            </div>

            {/* ‼️ 換一個 IS OFFERED ONLY WHEN THERE IS ONE. `canSwap` is the
                result of actually planning the next route (HomeScreen peeks
                before rendering this), so the button cannot be a dead tap. The
                reset appears only once she has swapped, because "back to the
                cheapest" is meaningless before that. */}
            {(canSwap || swapped) && (
              <div className="route-actions">
                {canSwap && onSwap && (
                  <button type="button" className="btn btn-ghost"
                    style={{ padding: '6px 14px', fontSize: 12.5 }}
                    onClick={onSwap}>
                    <Icon name="refresh" size={13} stroke={2} />
                    <span style={{ marginLeft: 5 }}>{zh ? '換一個' : 'Show another'}</span>
                  </button>
                )}
                {swapped && onResetSwap && (
                  <button type="button" onClick={onResetSwap}
                    style={{
                      padding: '6px 2px', minHeight: 32, background: 'none', border: 'none',
                      font: 'inherit', fontSize: 12, color: 'var(--fg-2)', cursor: 'pointer',
                      textDecoration: 'underline', textUnderlineOffset: 3,
                    }}>
                    {zh ? '返回最低成本' : 'Back to cheapest'}
                  </button>
                )}
              </div>
            )}
            {!plan.routeComplete && (
              <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 8 }}>
                {zh
                  ? `仍有 ${num(plan.uncoveredGap)} 分未找到符合條件的上架課程。Vital 會繼續監察，有新選擇時通知你。`
                  : `${num(plan.uncoveredGap)} points have no listed course that fits yet. Vital keeps watching and will tell you when one appears.`}
              </div>
            )}
            {/* ‼️ THE ONE PLACE THIS HINT CAN BE ACTED ON.
                Measured 2026-08-09: 133 of 133 live routes came back
                `conditional` — every price on this card said "from HK$X" —
                because no profile had a membership on file. This sentence has
                been here all along; what it never had was a way to DO it. The
                setup questions are skippable at purchase, so most subscribers
                skipped them, and the only route back was a button in the
                membership sheet that Profile did not even pass.
                Shown only when it would change something: memberships missing
                AND the cost genuinely unresolved. Once they are on file the
                button disappears rather than nagging. */}
            {!certain && (
              <div style={{ marginTop: 6 }}>
                <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
                  {zh
                    ? '部分課程設會員／非會員價。填寫你的會員資格後，Vital 才能確定最平路線。'
                    : 'Some courses price by membership. Add yours and Vital can name the cheapest route with certainty.'}
                </div>
                {onPersonalise && !personalised && (
                  <button type="button" className="btn btn-ghost"
                    style={{ marginTop: 8, padding: '6px 14px', fontSize: 12.5 }}
                    onClick={onPersonalise}>
                    {zh ? '開始使用' : 'Set this up'}
                  </button>
                )}
              </div>
            )}
            {/* ── The preference route, and the honest trade-off when it can't
                cover the gap. Preferences ADD a plan; the route above is always
                the full-choice one and is never narrowed by them. Measured
                2026-08-09: "online AND weekend" leaves 5 courses / 22.5 points
                in the live actionable pool, so this shortfall path is the common
                case, not a corner. Naming what relaxing one preference would buy
                turns a dead end into a decision. */}
            {plan.plans.preferred && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '0.5px solid var(--border)' }}>
                <div style={{ fontSize: 12.5, fontWeight: 600 }}>
                  {zh ? `符合你偏好的路線 ${plan.plans.preferred.cost > 0
                          ? (certain ? money(plan.plans.preferred.cost) : `由 ${money(plan.plans.preferred.cost)} 起`)
                          : '免費'}`
                      : `Matching your preferences ${plan.plans.preferred.cost > 0
                          ? (certain ? money(plan.plans.preferred.cost) : `from ${money(plan.plans.preferred.cost)}`)
                          : '· free'}`}
                </div>
                <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>
                  {zh ? `${plan.plans.preferred.count} 個課程` : `${plan.plans.preferred.count} courses`}
                </div>
              </div>
            )}
            {plan.preferredInfo && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '0.5px solid var(--border)' }}>
                <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
                  {zh
                    ? `符合你偏好的課程最多得 ${num(plan.preferredInfo.availablePoints)} 分,補唔完 ${num(plan.gap)} 分。`
                    : `Courses matching your preferences reach ${num(plan.preferredInfo.availablePoints)} points — not enough for ${num(plan.gap)}.`}
                  {plan.preferredInfo.relaxations[0] && (
                    zh
                      ? `放寬其中一項 → 可達 ${num(plan.preferredInfo.relaxations[0].availablePoints)} 分。`
                      : ` Relaxing one gets you to ${num(plan.preferredInfo.relaxations[0].availablePoints)}.`
                  )}
                </div>
              </div>
            )}
            {/* One line, not a wall of hedging. The plan totals LISTED CNE
                points; the Council caps points from programmes only indirectly
                related to nursing at 5 per cycle, and no per-course
                determination has been published — so this states the rule and
                lets her judge, rather than either ignoring it or pretending the
                app has resolved it. */}
            <div className="muted-2" style={{ fontSize: 11, lineHeight: 1.5, marginTop: 6 }}>
              {zh
                ? `按目錄列出的學分計算。與護理間接相關的課程,每週期上限 ${window.CNE_FACTS?.indirectCap ?? 5} 分。`
                : `Totals the points as listed. The Council caps programmes only indirectly related to nursing at ${window.CNE_FACTS?.indirectCap ?? 5} points per cycle.`}
            </div>
            </div>
            )}
          </React.Fragment>
        )}

        {/* ‼️ THE ASSUMPTION STAYS ON SCREEN, NEXT TO THE PRICE IT PRODUCED.
            This whole plan rests on one answer she tapped once: that she has
            done nothing yet. If that was wrong — or has since stopped being
            true — the plan over-buys, and courses cost real money. So say what
            it is standing on and keep the correction one tap away, for as long
            as there is still nothing logged. */}
        {(!isPro || expanded) && notStarted && !plan.cycleRecordCount && (
          <div className="muted-2" style={{ fontSize: 11, lineHeight: 1.5, marginTop: 6 }}>
            {zh ? '按你「未開始」的答案計算。' : 'Based on your answer that you haven’t started.'}
            {onLogRecord && (
              <button type="button" onClick={onLogRecord}
                style={{
                  marginLeft: 4, padding: 0, background: 'none', border: 'none',
                  font: 'inherit', color: 'var(--primary)', textDecoration: 'underline',
                  textUnderlineOffset: 3, cursor: 'pointer',
                }}>
                {zh ? '已經做過？記錄返' : 'Already done some? Log it'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PlannerInterestCard({ lang, gap, hasRecords, joined, busy, onJoin }) {
  if (!(gap > 0) || !onJoin) return null;
  const zh = lang === 'zh';
  const gapText = Number.isInteger(gap) ? String(gap) : gap.toFixed(1);
  // ‼️ SAME RULE AS RoutePlanCard: with nothing logged this cycle, `gap` is the
  // whole requirement — an arithmetic fact about an empty table, not a finding
  // about her. Quoting it here would reintroduce the exact wrong number the card
  // above exists to avoid, just in smaller type. Ask the question without the
  // figure instead; it reads the same and cannot be wrong.
  const ask = hasRecords
    ? (zh ? `想知道餘下 ${gapText} 分最平點樣完成？`
          : `Want the cheapest way to finish your last ${gapText} points?`)
    : (zh ? '想知道最平點樣補完你的 CNE？'
          : 'Want the cheapest way to finish your CNE?');
  return (
    <div style={{ padding: '12px 20px 0' }}>
      <div className="card" style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 650 }}>
            {joined
              ? (zh ? '已加入 Beta 名單' : 'You’re on the Beta list')
              : ask}
          </div>
          <div className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginTop: 3 }}>
            {joined
              ? (zh ? '功能推出時我們會通知你。' : 'We’ll let you know when it’s ready.')
              : (zh ? 'Vital 正在開發補分計劃：根據你的缺口，找出目前最省錢的完成路線。推出後會是 Vital Pro 功能。'
                    : 'We’re building a route planner: it works out the cheapest way to close your gap from what’s listed. It will be part of Vital Pro when it ships.')}
          </div>
          {!joined && (
            <button type="button" className="btn btn-ghost"
              style={{ marginTop: 10, padding: '6px 14px', fontSize: 12.5 }}
              disabled={busy} onClick={onJoin}>
              {busy ? (zh ? '處理中…' : 'Saving…') : (zh ? '加入 Beta 名單' : 'Join the Beta list')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// A temporary setup summary for subscribers who still have unfinished Pro
// benefits. Its counts come from the canonical readiness hook; Home never
// guesses from the mere presence of a preference. Once every benefit is ready,
// the card retires while the permanent badge, route and Profile membership
// controls remain. Entitlement and privileged work stay server-gated.
function ProHomeSummary({ lang, name, readiness, onOpen }) {
  const zh = lang === 'zh';
  const readyCount = Number(readiness?.readyCount || 0);
  const total = Number(readiness?.total || 0);
  const remaining = Math.max(0, total - readyCount);
  const facts = [
    {
      icon: 'check-circ',
      text: zh
        ? `已啟用 ${readyCount} / ${total} 項權益`
        : `${readyCount} of ${total} benefits active`,
    },
    remaining > 0 ? {
      icon: 'target',
      text: zh
        ? `尚有 ${remaining} 項等待設定，點按即可查看下一步`
        : `${remaining} still need setup — tap to see the next step`,
    } : null,
  ].filter(Boolean);
  const displayName = String(name || '').trim();

  return (
    <div style={{ padding: '12px 20px 0' }}>
      <button type="button" className="home-pro-summary" onClick={onOpen}
        aria-label={zh ? '查看 Vital Pro 會員功能' : 'View your Vital Pro membership'}>
        <span className="home-pro-summary-head">
          <span className="home-pro-summary-mark">
            <VitalLogo size={13} color="currentColor" /> Vital Pro
          </span>
          <Icon name="chev-r" size={16} stroke={1.8} color="var(--fg-3)" />
        </span>
        <span className="home-pro-summary-title">
          {zh
            ? `${displayName ? `${displayName}，` : ''}完成餘下的 Pro 設定`
            : `${displayName ? `${displayName}, f` : 'F'}inish setting up Vital Pro`}
        </span>
        <span className="home-pro-summary-facts">
          {facts.map((fact) => (
            <span key={fact.text} className="home-pro-summary-fact">
              <Icon name={fact.icon} size={12} stroke={1.9} />
              <span>{fact.text}</span>
            </span>
          ))}
        </span>
      </button>
    </div>
  );
}

// Scroll, focus and softly highlight a destination reached from the Pro
// readiness checklist. The signal is consumed only AFTER the destination has
// mounted; this matters for the NCHK flow, where the target is several steps
// inside a sheet. Programmatic focus gives VoiceOver the same context, while
// the CSS honours prefers-reduced-motion.
function useProTargetHighlight(active, onConsumed) {
  const ref = React.useRef(null);
  const consumedRef = React.useRef(onConsumed);
  React.useEffect(() => { consumedRef.current = onConsumed; }, [onConsumed]);
  React.useEffect(() => {
    if (!active || !ref.current) return undefined;
    const el = ref.current;
    const frame = requestAnimationFrame(() => {
      try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) { el.scrollIntoView(); }
      try { el.focus({ preventScroll: true }); } catch (_) { el.focus(); }
    });
    const timer = setTimeout(() => consumedRef.current?.(), 2200);
    return () => { cancelAnimationFrame(frame); clearTimeout(timer); };
  }, [active]);
  return ref;
}

function HomeScreen({ lang, setLang, onOpenCourse, onTab, onOpenNotifications, onOpenRecordHistory, onOpenSheet, onSignIn, savedIds, enrolledIds, session, profile, records = [], reminders = [], onRouteReminder, onEditProfile, onProfileChanged, onOpenFreeCne, proFeatureTarget, onProFeatureTargetConsumed }) {
  // ⚠️ HOOK-ORDERING CONSTRAINT — these must be at the very top of the
  // component, ABOVE the `if (isGuest) return ...` guest-variant early
  // return below. Otherwise the hook count differs between guest and
  // signed-in renders (or across a sign-in transition) and React tears
  // the component down. The guest branch ignores `customAll`/`attendance`
  // — calling the hooks costs nothing extra (localStorage read on mount).
  // See record-extras.jsx UpcomingCalendarSection for the same constraint.
  const [customAll] = useCustomSessions();
  const [attendance] = useAttendance();
  // Quick-look dialog for a tapped "Upcoming" row. Held here, above the
  // `if (isGuest) return …` early return below, so the hook count stays
  // identical across the guest→signed-in transition (same constraint as
  // `upcoming` below). Guests never open it — the state just sits at null.
  const [previewEntry, setPreviewEntry] = React.useState(null);
  const homeUserIdRef = React.useRef(session?.user?.id);
  homeUserIdRef.current = session?.user?.id;
  const routeTargetActive = proFeatureTarget?.target === 'route';
  const [routeExpanded, setRouteExpanded] = React.useState(
    () => profile?.prefs?.homeRouteCollapsed !== true,
  );
  const routePrefWriteRef = React.useRef(0);
  const routePrefQueueRef = React.useRef(Promise.resolve());
  React.useEffect(() => {
    routePrefWriteRef.current += 1;
    routePrefQueueRef.current = Promise.resolve();
    setRouteExpanded(profile?.prefs?.homeRouteCollapsed !== true);
  }, [session?.user?.id, profile?.prefs?.homeRouteCollapsed]);
  // A checklist deep link is an explicit request to see this content. Expand
  // for that visit without overwriting the nurse's saved default.
  React.useEffect(() => {
    if (routeTargetActive) setRouteExpanded(true);
  }, [routeTargetActive]);
  const routeTargetRef = useProTargetHighlight(routeTargetActive, onProFeatureTargetConsumed);

  // Free CNE headline, for the card rendered on BOTH the guest and signed-in
  // branches. ⚠️ Same HOOK-ORDERING CONSTRAINT as everything in this block: it
  // must stay above the `if (isGuest) return …` early return, because the guest
  // branch renders this card too. Keyed on the catalogue identity + today, so it
  // recomputes when a refresh swaps COURSES or the local day rolls over.
  const freeCne = React.useMemo(
    () => (window.freeCneSummary ? window.freeCneSummary(COURSES, window.todayIso?.()) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [COURSES, window.todayIso?.()],
  );

  // Planner-interest opt-in. ⚠️ Same HOOK-ORDERING CONSTRAINT as the block
  // above: declared before the `if (isGuest) return …` early return so the hook
  // count matches across the guest↔signed-in transition. Guests never render
  // the card (they have no licence, so no gap).
  //
  // Stored in profiles.prefs — the same client-writable bag as the notification
  // and reminder settings, because this IS a preference the nurse set, not an
  // observation we made about her. Seeded from the profile so the card stays
  // settled after a reload, and kept in local state so the confirmation is
  // immediate rather than waiting on a profile refetch.
  // Pro entitlement, for the route card's Free-vs-Pro split. ⚠️ Same
  // HOOK-ORDERING CONSTRAINT as this whole block — above the guest early return.
  // Guests are never Pro, and the card they see is the Free variant anyway.
  // Pass the account id as a refresh key. Without it, a guest -> sign-in or an
  // account switch could keep the previous account's rendering snapshot until
  // another entitlement event happened. This value is presentation-only; the
  // server remains authoritative for every Pro operation.
  const homeProEnt = useProEntitlement(session?.user?.id);
  const homeIsPro = !homeProEnt.loading && homeProEnt.pro;
  // The Home setup card uses the exact same canonical readiness contract as
  // the membership checklist. This prevents a loose local approximation from
  // claiming completion (or nagging forever) when notification delivery,
  // route progress or another real prerequisite says otherwise.
  const homeProReadiness = useProBenefitReadiness(
    session?.user?.id,
    profile,
    records,
    homeIsPro,
  );

  // ── 換一個 ────────────────────────────────────────────────────────────────
  //
  // The route is one answer to a question with many: the cheapest combination.
  // A nurse can have perfectly good reasons to reject it that the planner cannot
  // see — the date clashes with her roster, the venue is across the harbour, she
  // has taken that provider before. So she can swap the whole route out and get
  // the genuine runner-up (cneRoutePlan's `exclude`, still solved for cheapest
  // over what remains — not a shuffle).
  //
  // ‼️ SESSION-ONLY, ON PURPOSE. Nothing is persisted: a rejected course returns
  // on the next launch. Permanently hiding a course she tapped past once — while
  // the catalogue, her cycle and her budget all keep moving — would be a worse
  // failure than showing it again, and it would be invisible to her.
  //
  // ⚠️ DECLARED HERE, ABOVE THE GUEST EARLY RETURN, like every other hook in
  // this component. The route card renders far below; putting its state next to
  // it would change the hook count between the guest and signed-in variants.
  const [routeSwapped, setRouteSwapped] = React.useState([]);
  const routeCycleRef = React.useRef('');

  const [plannerBetaJoined, setPlannerBetaJoined] = React.useState(
    () => !!profile?.prefs?.plannerBeta?.joinedAt,
  );
  const [plannerBetaBusy, setPlannerBetaBusy] = React.useState(false);
  React.useEffect(() => {
    setPlannerBetaJoined(!!profile?.prefs?.plannerBeta?.joinedAt);
  }, [profile?.prefs?.plannerBeta?.joinedAt]);
  // ── "I haven't started this cycle yet" ──────────────────────────────────
  //
  // ‼️ THE ONE THING WE MAY ASK, AND THE ONE WE MAY NOT.
  //
  // With no records this cycle we cannot tell two very different nurses apart:
  // one who has done the CNE and not logged it, and one who has genuinely not
  // begun. The route card therefore refuses to name a number (see
  // cycleRecordCount in data.js) — but for the second nurse that leaves a dead
  // end: she has nothing to log, so the card can never become useful, and if
  // she is paying for Pro the feature she bought stays blank forever. She is
  // also the one for whom planning is worth the most: her whole requirement is
  // ahead of her.
  //
  // So we ask the ONE question whose answer cannot corrupt anything: "have you
  // started?" — and we only ever store the NO.
  //
  //   • A "no" is zero. Zero adds no points, no Annex A rows and no baseline —
  //     the NCHK export is built from records and stays byte-identical. It
  //     unlocks planning and touches nothing else.
  //   • A NUMBER would be a self-reported baseline flowing into a compliance
  //     export unevidenced. That is why PointsStep was removed from onboarding
  //     (onboarding.jsx:151) and it must stay removed. ‼️ NEVER extend this into
  //     "roughly how many points do you have?" — the honesty of the export
  //     depends on every point in it having a record behind it.
  //
  // Stored per LICENCE and per CYCLE (`{ [licKey]: cycleStart }`), so a
  // dual-registered nurse answers for each certificate and a rollover re-asks
  // rather than carrying a stale "not started" into a new cycle. Logging a
  // record makes the flag irrelevant on its own — cycleRecordCount > 0 takes a
  // different branch — so no undo is needed and none is offered.
  const [notStartedMap, setNotStartedMap] = React.useState(
    () => (profile?.prefs?.cneNotStarted && typeof profile.prefs.cneNotStarted === 'object'
      ? profile.prefs.cneNotStarted : {}),
  );
  const [notStartedBusy, setNotStartedBusy] = React.useState(false);
  // ‼️ EVERY EARLY RETURN IN A CLICK HANDLER IS A BUTTON THAT DOES NOTHING.
  // This handler had two silent ones and shipped with a control that, for the
  // majority of live profiles (no PC dates on file), could never succeed and
  // never said so. The structural fix is upstream — RoutePlanCard no longer
  // renders the question without a cycle to scope it to — and this string is
  // the backstop for the failures that remain reachable (offline, RLS, a
  // dropped connection). Do not add a `return` here without a message.
  const [notStartedErr, setNotStartedErr] = React.useState('');
  React.useEffect(() => {
    setNotStartedMap(profile?.prefs?.cneNotStarted && typeof profile.prefs.cneNotStarted === 'object'
      ? profile.prefs.cneNotStarted : {});
  }, [profile?.prefs?.cneNotStarted]);
  const markNotStarted = async (lic) => {
    const uid = session?.user?.id;
    if (!uid || !lic || notStartedBusy || !window.DB?.setPrefs) return;
    const key = lic.key || 'primary';
    // No cycle to key the answer under. Unreachable from the card now — it is
    // handed no onNotStarted at all in this state — and it must stay
    // unreachable: the map is keyed `{ [licKey]: cycleStart }` precisely so a
    // rollover re-asks, and a null key would carry "not started" into every
    // future cycle. Kept as a last-line guard — but a guard that speaks.
    //
    // ‼️ NOT the same condition as the card's window guard. renewalDate ALONE
    // produces a complete window (cneWindow ignores pcFrom when pcTo is set),
    // so a nurse can be here with every figure on her card correct and still
    // have nothing to key this answer under.
    if (!lic.cycleStart) {
      setNotStartedErr(lang === 'zh'
        ? '請先設定執業證書日期,我哋先可以記住呢個答案。'
        : 'Set your certificate dates first — that answer is stored per cycle.');
      return;
    }
    const next = { ...notStartedMap, [key]: lic.cycleStart };
    setNotStartedBusy(true);
    setNotStartedErr('');
    const { error } = await window.DB.setPrefs(uid, { cneNotStarted: next });
    setNotStartedBusy(false);
    // Same contract as joinPlannerBeta above: only reflect a write that landed,
    // and a failure is recoverable by tapping again — but SAY SO, otherwise a
    // failed write and a broken button look identical from the nurse's side.
    if (error) {
      setNotStartedErr(lang === 'zh' ? '儲存唔到,請再試一次。' : 'Could not save. Please try again.');
      return;
    }
    setNotStartedMap(next);
  };

  const joinPlannerBeta = async () => {
    const uid = session?.user?.id;
    if (!uid || plannerBetaBusy || !window.DB?.setPrefs) return;
    setPlannerBetaBusy(true);
    // Optimistic: the opt-in is a preference, and a failed write is recoverable
    // by tapping again — but never leave her looking at an unchanged button
    // after a successful save.
    const { error } = await window.DB.setPrefs(uid, {
      plannerBeta: { joinedAt: new Date().toISOString() },
    });
    setPlannerBetaBusy(false);
    if (!error) setPlannerBetaJoined(true);
  };

  // One-time "AI scan is live" announcement — dismissable, shown once ever to
  // signed-in users. Declared ABOVE the `if (isGuest) return …` guard below so
  // the hook count stays stable across the guest↔signed-in transition (same
  // constraint as `upcoming`/`notifUnread`). Guests never render it.
  const [aiLiveSeen, setAiLiveSeen] = React.useState(() => {
    try { return !!localStorage.getItem('vital:aiScanLiveSeen'); } catch { return true; }
  });
  const [scanCredits, setScanCredits] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    if (!session?.user?.id || !window.DB?.getScanCreditStatus) return undefined;
    window.DB.getScanCreditStatus().then((status) => { if (!cancelled) setScanCredits(status); });
    return () => { cancelled = true; };
  }, [session?.user?.id]);
  const dismissAiLive = () => {
    try { localStorage.setItem('vital:aiScanLiveSeen', '1'); } catch {}
    setAiLiveSeen(true);
  };

  // The first-scan nudge is an account preference, not an app-wide device
  // flag: two nurses sharing a device must not dismiss it for one another, and
  // an explicit dismissal should follow the same nurse to her other device.
  // The timestamp is bounded client-generated metadata only; it never affects
  // credits, records, onboarding or any server-side entitlement decision.
  const firstScanDismissedAt = profile?.prefs?.homePrompts?.firstScanDismissedAt;
  const [firstScanDismissed, setFirstScanDismissed] = React.useState(!!firstScanDismissedAt);
  React.useEffect(() => {
    setFirstScanDismissed(!!firstScanDismissedAt);
  }, [session?.user?.id, firstScanDismissedAt]);
  const dismissFirstScan = async () => {
    const uid = session?.user?.id;
    if (!uid || !window.DB?.setPrefs) return;
    setFirstScanDismissed(true);
    const homePrompts = {
      ...(profile?.prefs?.homePrompts && typeof profile.prefs.homePrompts === 'object'
        ? profile.prefs.homePrompts : {}),
      firstScanDismissedAt: new Date().toISOString(),
    };
    const { data, error } = await window.DB.setPrefs(uid, { homePrompts });
    if (homeUserIdRef.current !== uid) return;
    if (error) {
      setFirstScanDismissed(false);
      window.showVitalToast?.(lang === 'zh' ? '未能儲存，請再試一次' : 'Could not save. Please try again.');
      return;
    }
    if (data) onProfileChanged?.(data);
  };
  const changeRouteExpanded = (nextExpanded) => {
    const uid = session?.user?.id;
    const next = !!nextExpanded;
    setRouteExpanded(next);
    if (!uid || !window.DB?.setPrefs) return;
    const write = ++routePrefWriteRef.current;
    // Serialize writes because setPrefs is a read-merge-write operation. Two
    // rapid taps must not let an older network response land last and persist
    // the opposite of the nurse's final choice.
    routePrefQueueRef.current = routePrefQueueRef.current.then(async () => {
      const { data, error } = await window.DB.setPrefs(uid, { homeRouteCollapsed: !next });
      // Ignore an older response after a faster second tap. This avoids the
      // first save visibly undoing the nurse's latest choice.
      if (write !== routePrefWriteRef.current || homeUserIdRef.current !== uid) return;
      if (error) {
        setRouteExpanded(!next);
        window.showVitalToast?.(lang === 'zh' ? '未能儲存顯示設定' : 'Could not save display setting');
        return;
      }
      if (data) onProfileChanged?.(data);
    }).catch(() => {
      if (write !== routePrefWriteRef.current || homeUserIdRef.current !== uid) return;
      setRouteExpanded(!next);
      window.showVitalToast?.(lang === 'zh' ? '未能儲存顯示設定' : 'Could not save display setting');
    });
  };

  // ⚠️ HOOK-ORDERING CONSTRAINT (cont.) — `upcoming` MUST stay above the
  // `if (isGuest) return ...` early return below. Otherwise the hook count
  // is 2 for guests and 3 for signed-in, so the guest→signed-in sign-in
  // transition tears the tree down with "rendered more hooks than during
  // the previous render" (symptom: blank page right after email login; a
  // manual refresh masks it by mounting straight into the signed-in path).
  // Now that hooks use `React.useMemo(...)` member calls, react-hooks/
  // rules-of-hooks WILL flag a regression here — keep the guard anyway.
  // Guests carry an empty enrolledIds, so this memo computes to [] and the
  // guest branch simply ignores it.
  const upcoming = React.useMemo(() => {
    return COURSES
      .filter(c => enrolledIds.has(c.id))
      .map(c => {
        const sessions = resolveSessions(c, customAll);
        const my = attendance[c.id] || {};
        const next = sessions.find(s => !s.isPast && my[s.idx] !== 'attended');
        return next ? { course: c, next } : null;
      })
      .filter(Boolean)
      .sort((a, b) => (a.next.iso < b.next.iso ? -1 : a.next.iso > b.next.iso ? 1 : 0));
  }, [enrolledIds, customAll, attendance]);

  // Unread notification-center count — drives the header bell badge. Hoisted
  // above the `if (isGuest) return …` guard below to keep the hook count stable
  // across the guest↔signed-in transition (the guest header renders no bell, so
  // this value just sits unused — same constraint as `upcoming`). Refetched per
  // mount: the home header unmounts on every tab switch, so returning from the
  // center re-runs this. The center no longer marks all read on open, so the
  // dot now counts items the user hasn't pressed (or cleared via "Mark all
  // read"). Mirrors ProfileScreen's notifUnread effect.
  const [notifUnread, setNotifUnread] = React.useState(0);
  React.useEffect(() => {
    let cancelled = false;
    const uid = session?.user?.id;
    if (!uid || !window.DB?.getUnreadNotificationCount) return;
    const refresh = () => window.DB.getUnreadNotificationCount(uid)
      .then((n) => { if (!cancelled) setNotifUnread(n || 0); });
    refresh();
    window.addEventListener('vital:notifications-changed', refresh);
    return () => { cancelled = true; window.removeEventListener('vital:notifications-changed', refresh); };
  }, [session?.user?.id]);

  // 公告 — operator-authored notices from the server.
  //
  // ⚠️ HOOK-ORDERING CONSTRAINT (cont.) — like every hook above, this MUST stay
  // above the `if (isGuest) return …` early return. Guests render notices too
  // (the 'all' and 'guests' audiences are for them), so unlike most of the
  // block this value is genuinely used on BOTH branches.
  const [notices, setNotices] = React.useState([]);
  React.useEffect(() => {
    let cancelled = false;
    if (!window.DB?.getActiveNotices) return undefined;
    const load = () => window.DB.getActiveNotices().then((rows) => {
      if (cancelled) return;
      setNotices(rows);
      // Reach, once per load, fire-and-forget. Never awaited: instrumentation
      // does not get to slow a dashboard down.
      if (rows.length && window.DB.markNoticesSeen) {
        window.DB.markNoticesSeen(rows.map(n => n.id));
      }
    });
    load();
    // Re-check when the app comes back to the foreground — the whole point of
    // this channel is the notice that goes up while she is not looking. NOT a
    // realtime subscription and NOT a per-render query: one fetch per mount and
    // one per foreground is the entire budget.
    const onForeground = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onForeground);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onForeground);
    };
    // Re-runs on sign-in/out: the audience buckets resolve differently for a
    // guest than for the nurse she becomes a second later.
  }, [session?.user?.id]);

  const dismissNotice = React.useCallback(async (n) => {
    // Optimistic: the banner goes the moment she taps. If the server refuses
    // (a non-dismissible notice), the next load brings it back — which is the
    // correct outcome and needs no error copy.
    setNotices(prev => prev.filter(x => x.id !== n.id));
    try { await window.DB?.dismissNotice?.(n.id, n.slug); } catch (_) { /* next load re-syncs */ }
  }, []);

  const noticeStack = (
    <NoticeStack notices={notices} lang={lang} onDismiss={dismissNotice}
      onTab={onTab} onOpenSheet={onOpenSheet} />
  );
  // A service-affecting notice outranks "scan your first certificate". Only
  // 'critical' does this — anything lower coexists with the one-time cards.
  const hasCriticalNotice = notices.some(n => n.severity === 'critical');

  const isGuest = !session;

  // ── Guest variant ──────────────────────────────────────────────────────
  if (isGuest) {
    // ‼️ restrictedRecommendable, NOT restrictedVisibleByDefault. A guest has no
    // profile at all, so every workplace-gated course is 'unknown' for her —
    // which is enough to be LISTED in Discover (benefit of the doubt) and
    // deliberately not enough to be RECOMMENDED here. Swapping these two back
    // would start pitching 醫院管理局 courses to signed-out visitors on the
    // first screen they ever see. See restrictedRecommendable in data.js.
    const guestRecommendationPool = COURSES
      .filter(c => (window.courseIsActionable?.(c) ?? isUpcomingCourse(c))
        && (window.restrictedRecommendable
            ? window.restrictedRecommendable(c, profile) : !c.isRestricted));
    const recommended = (window.sortForDiscover?.(guestRecommendationPool) || guestRecommendationPool)
      .sort((a, b) => Number(window.deriveRegistrationStatus?.(b) === 'open')
        - Number(window.deriveRegistrationStatus?.(a) === 'open'))
      .slice(0, 6);
    const hour = new Date().getHours();
    const greet = hour < 12 ? STR.goodMorning : hour < 18 ? STR.goodAfternoon : STR.goodEvening;

    // Benefits shown to guests in place of a generic "how it works" — every
    // main function of the app, framed as a reason to create a free account.
    const benefits = [
      { icon: 'book',
        title: lang === 'zh' ? '追蹤週期學分' : 'Track cycle points',
        sub:   lang === 'zh' ? '自動計算核心與選修分數,清楚知道距離續期還差多少。'
                              : 'Core & elective points tallied automatically — always know how far you are from renewal.' },
      { icon: 'sparkle',
        title: lang === 'zh' ? 'AI 智能識別' : 'AI scan',
        sub:   lang === 'zh' ? '拍下證書、課程海報,或貼上課程網址,AI 自動填寫名稱、日期及分數。'
                              : 'Snap a certificate or poster, or paste a course link — AI fills in the title, dates and points.' },
      { icon: 'upload',
        title: lang === 'zh' ? '上載並保存證書' : 'Store your certificates',
        sub:   lang === 'zh' ? 'PDF 與相片加密儲存於你的帳戶,查核時隨時取用。'
                              : 'PDFs and photos kept encrypted in your account — ready for audit anytime.' },
      { icon: 'bell',
        title: lang === 'zh' ? '續期提醒' : 'Renewal reminders',
        sub:   lang === 'zh' ? '在週期結束前提醒你尚欠多少學分,不再錯過續期。'
                              : 'A nudge before your cycle deadline so you never miss renewal.' },
      { icon: 'bookmark',
        title: lang === 'zh' ? '儲存與報名課程' : 'Save & enrol in courses',
        sub:   lang === 'zh' ? '收藏 NCHK 認可課程,追蹤即將舉行的課堂。'
                              : 'Bookmark NCHK-accredited courses and track the sessions you’ll attend.' },
    ];
    const providerCount = new Set(COURSES.map(c => c.provider?.en || c.provider?.zh).filter(Boolean)).size;
    const latestCatalogueUpdate = COURSES.reduce((latest, c) => {
      const updated = c.raw?.last_updated || c.raw?.updated_at || c.raw?.source_updated_at || '';
      return updated > latest ? updated : latest;
    }, '');

    return (
      <div className="scroll fade-in" style={{ paddingBottom: 32 }}>
        <div className="top sticky">
          <div style={{ flex: 1 }}>
            <div className="h-greet">{L(greet, lang)}</div>
            <div className="h-screen" style={{ marginTop: 4 }}>
              {lang === 'zh' ? '探索 CNE 課程' : 'Explore CNE courses'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <LangPill lang={lang} setLang={setLang} />
          </div>
        </div>

        {/* 公告 — guests see the 'all' and 'guests' audiences. Their dismissal
            is localStorage-only (no account to key a row on), which is why a
            guests-targeted notice should always carry an end date. */}
        {noticeStack}

        {/* Welcome / sign-in card */}
        <div style={{ padding: '14px 20px 0' }}>
          <div className="guest-card">
            <div className="gc-eyebrow">
              {lang === 'zh' ? '本港護士 CNE 助手' : 'Hong Kong nurse · CNE companion'}
            </div>
            <h2 className="gc-title">
              {lang === 'zh'
                ? '續期時，一鍵生成 NCHK 附件 A + B。'
                : 'At renewal, generate NCHK Annex A + B in one tap.'}
            </h2>
            <p className="gc-sub">
              {lang === 'zh'
                ? '建立免費帳戶即可記錄課程和證書，續期時整理成可提交的官方文件。'
                : 'Create a free account to log courses and certificates, then prepare an official document ready to submit at renewal.'}
            </p>
            <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5, margin: '10px 0 18px' }}>
              {lang === 'zh'
                ? `${COURSES.length} 個課程資料 · ${providerCount} 個機構${latestCatalogueUpdate ? ` · 最近更新 ${fmtDate(latestCatalogueUpdate, lang)}` : ''}`
                : `${COURSES.length} course listings · ${providerCount} providers${latestCatalogueUpdate ? ` · updated ${fmtDate(latestCatalogueUpdate, lang)}` : ''}`}
              <br />
              {lang === 'zh' ? '你的紀錄和證書只屬於你，並可隨時讀取、匯出或刪除。' : 'Your records and certificates stay private to you, and you can read, export or delete them anytime.'}
            </div>
            <div className="gc-cta">
              <button className="btn btn-primary" onClick={onSignIn}>
                <Icon name="user" size={16} stroke={1.9} />
                {lang === 'zh' ? '登入或建立帳戶' : 'Sign in or create account'}
              </button>
              <button className="btn btn-ghost" onClick={() => onTab('discover')}>
                {lang === 'zh' ? '先看看課程' : 'Browse courses first'}
                <Icon name="arrow-r" size={15} stroke={1.7} />
              </button>
            </div>
          </div>
        </div>

        {/* Free CNE — shown to guests too: no account is needed to browse the
            catalogue, and this is the single clearest reason to come back. */}
        <FreeCneCard lang={lang} summary={freeCne} onOpen={onOpenFreeCne} />

        {/* Recommended */}
        <Section title={L(STR.recommended, lang)} action={L(STR.viewAll, lang)} onAction={() => onTab('discover')}>
          <div className="scroll-x">
            {recommended.map(c => (
              <MiniCourseCard key={c.id} course={c} lang={lang} onClick={() => onOpenCourse(c.id)} />
            ))}
          </div>
        </Section>

        {/* Why create an account — every main function, framed as a benefit,
            with a sign-up CTA. Replaces the old generic "how it works" steps. */}
        <Section title={lang === 'zh' ? '建立免費帳戶,解鎖全部功能' : 'Create a free account — unlock everything'}>
          <div style={{ display: 'grid', gap: 10 }}>
            {benefits.map((b, i) => (
              <div key={i} className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '14px 16px' }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 11,
                  background: 'color-mix(in oklab, var(--accent) 12%, transparent)',
                  color: 'var(--accent)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Icon name={b.icon} size={18} stroke={1.7} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="h-row" style={{ fontSize: 14 }}>{b.title}</div>
                  <div className="muted" style={{ fontSize: 12.5, marginTop: 2, lineHeight: 1.45 }}>{b.sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Conversion CTA */}
          <div style={{ padding: '18px 0 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <button className="btn btn-primary" onClick={onSignIn} style={{ minWidth: 220 }}>
              <Icon name="user" size={16} stroke={1.9} />
              {lang === 'zh' ? '免費建立帳戶' : 'Create a free account'}
            </button>
            <div className="muted-2" style={{ fontSize: 11.5 }}>
              {lang === 'zh' ? '免費 · 數分鐘即可建立帳戶' : 'Free · takes under a minute'}
            </div>
          </div>
        </Section>
      </div>
    );
  }

  // ── Signed-in variant ──────────────────────────────────────────────────
  // Order matters: real profile → session-derived placeholder (offline
  // safety net) → demo USER. The demo constant is sample bilingual data
  // ("Wing Yi Chan / 陳大文") and must never reach a signed-in user; it
  // remains as the final fallback only for the design-canvas preview.
  const u = userFromProfile(profile, records) || profileFromSession(session, records) || USER;
  // One wallet card per practising certificate (see profileLicenses/data.js). A
  // dual-registered nurse (Part I + Part II) has an entry per cycle in
  // u.licenses; the offline placeholder and demo USER carry no array, so
  // synthesise a single card from the flat fields — its split comes from the
  // same cyclePointsSummary the ring has always used, keeping that path
  // byte-for-byte identical to the pre-dual-licence layout.
  const walletCards = (u.licenses && u.licenses.length)
    ? u.licenses
    : [{
        key: 'primary',
        part: null,
        cycleStart: u.cycleStart,
        renewalDate: u.renewalDate,
        required: u.required,
        earned: u.earned,
        split: cyclePointsSummary(records, u.cycleStart, u.renewalDate).byCategory,
      }];

  // Outstanding points, for the planner-interest card only.
  //
  // ‼️ NEVER COERCE `required` WITH `|| 45`. A first-PC-exempt certificate
  // carries required: 0 on purpose (profileLicenses in data.js spells out why),
  // and the fallback pattern silently resurrects a 45-point requirement for the
  // one nurse who does not have one. Reading it as-is means her gap comes out
  // ≤ 0 and the card correctly never appears.
  //
  // Takes the LARGEST gap across certificates rather than the sum: a
  // dual-registered nurse has two separate cycles with separate requirements,
  // and adding them together would state a number that is true of neither.
  const outstandingPoints = walletCards.reduce((worst, lic) => {
    const req = Number(lic?.required);
    const earned = Number(lic?.earned) || 0;
    if (!Number.isFinite(req)) return worst;
    return Math.max(worst, req - earned);
  }, 0);

  // The route plan, for the certificate with the biggest shortfall — the one she
  // has to act on first. Not a useMemo: this sits BELOW the guest early return
  // (walletCards does not exist above it), so making it a hook would break the
  // hook-count invariant this component is built around. cneRoutePlan() is a
  // pure pass over the catalogue and returns null cheaply for the exempt and
  // already-met cases, which are the common ones.
  // ‼️ SHARED RULE, NOT A LOCAL ONE. app.jsx picks the same certificate when it
  // decides which one the setup sheet's "have you started?" step is about, and
  // that answer is stored per licence key — see cnePlanLicence in data.js.
  const routeLicense = window.cnePlanLicence
    ? window.cnePlanLicence(walletCards)
    : null;
  // ‼️ NO HOOKS HERE — this is below the guest early return (see the
  // HOOK-ORDERING CONSTRAINT at the top of this component). `routeSwapped` and
  // its reset live above it; everything below is plain computation.
  //
  // A new certificate or a new cycle is a different question, so the swaps are
  // dropped. Adjusting state DURING render (rather than in an effect) is React's
  // own pattern for "derive state from a changed input": it re-renders before
  // committing, so no flash of the stale route — and it needs no hook, which is
  // what makes it legal at this point in the function.
  const routeCycleKey = `${routeLicense?.key || ''}:${routeLicense?.cycleStart || ''}`;
  if (routeCycleRef.current !== routeCycleKey) {
    routeCycleRef.current = routeCycleKey;
    if (routeSwapped.length) setRouteSwapped([]);
  }

  const routePlan = (routeLicense && window.cneRoutePlan)
    ? window.cneRoutePlan({
        records, lic: routeLicense, courses: COURSES, profile, today: window.todayIso?.(),
        exclude: routeSwapped,
      })
    : null;
  // ‼️ PEEK BEFORE OFFERING. A "換一個" that produces nothing is worse than no
  // button, so the next route is computed BEFORE it is offered — and it is the
  // very plan the tap will show, so the peek is never wasted work. Guarded to
  // subscribers with a real route on screen, which is the only state that can
  // render the button.
  const routeNextExclude = [
    ...routeSwapped,
    ...((routePlan?.plans?.cheapest?.courses || []).map((c) => c.id)),
  ];
  const routeAlternative = (homeProEnt.pro && routeLicense && routePlan?.plans?.cheapest?.courses?.length
    && window.cneRoutePlan)
    ? window.cneRoutePlan({
        records, lic: routeLicense, courses: COURSES, profile, today: window.todayIso?.(),
        exclude: routeNextExclude,
      })
    : null;

  // The "即將舉行" list (`upcoming`) reflects the user's actual session
  // schedule (managed under 我的記錄 — custom_sessions override the
  // catalog), via the same resolveSessions chain the Record screen uses.
  // It is computed by the hoisted useMemo at the top of this function
  // (above the isGuest early return) so the hook order stays stable.
  // Only recommend restricted courses she POSITIVELY qualifies for — her own
  // hospital, or a membership she could join.
  //
  // ‼️ restrictedRecommendable, NOT restrictedVisibleByDefault. The two used to
  // be the same call and they must not be again: recommending is a stronger
  // claim than listing, so the 'unknown' verdict (workplace never filled in)
  // passes the Discover filter and is refused here. Otherwise loosening
  // Discover would silently push courses at a nurse we cannot tell are open to
  // her — on the Home screen, where the app is making a promise.
  const recommendationPool = COURSES
    .filter(c => !enrolledIds.has(c.id) && (window.courseIsActionable?.(c) ?? isUpcomingCourse(c))
      && (window.restrictedRecommendable
          ? window.restrictedRecommendable(c, profile) : !c.isRestricted));
  // Recommendations stay inside the same eligibility boundary as Discover,
  // then rank courses relevant to the nurse's hospital and (when present on a
  // profile imported in the future) specialty before generic open listings.
  // ‼️ `profile.specialty` and `profile.specialty_area_en` DO NOT EXIST as
  // columns — checked against the live schema 2026-08-09 — so this scoring has
  // only ever matched on `role`. They stay in the list because a future import
  // may add them and reading a missing key is free, but the thing that actually
  // carries a nurse's interests now is the Pro setup sheet's topic picker
  // (prefs.coursePrefs.topics), which uses the bounded TOPIC_LABEL vocabulary
  // every course is classified into. Matched separately below against
  // `course.topic` rather than the free-text specialty strings.
  const profileSpecialties = [profile?.specialty, profile?.specialty_area_en, profile?.role]
    .flatMap(v => Array.isArray(v) ? v : [v])
    .map(v => String(v || '').toLowerCase())
    .filter(Boolean);
  const preferredTopics = Array.isArray(profile?.prefs?.coursePrefs?.topics)
    ? profile.prefs.coursePrefs.topics
    : [];
  const recommendationRelevance = (course) => {
    const restriction = course.restriction;
    const hospitalScore = restriction?.kind === 'hospital'
      ? (restriction.hospitalKey && restriction.hospitalKey === profile?.hospital_key ? 4
        : restriction.sector === profile?.hospital_sector ? 3 : 0)
      : restriction?.kind === 'members' ? 1 : 0;
    const specialties = (course.raw?.specialty_area_en || []).map(v => String(v).toLowerCase());
    const specialtyScore = specialties.some(v => profileSpecialties.some(p => v.includes(p) || p.includes(v))) ? 2 : 0;
    // A topic she actually chose outranks an inferred specialty match: this is
    // the one signal she gave us on purpose. Ranking only — nothing is hidden,
    // which is what keeps a preference from quietly emptying the list.
    const topicScore = preferredTopics.includes(course.topic) ? 3 : 0;
    return hospitalScore + specialtyScore + topicScore;
  };
  const recommended = [...(window.sortForDiscover?.(recommendationPool) || recommendationPool)]
    .sort((a, b) => recommendationRelevance(b) - recommendationRelevance(a)
      || Number(window.deriveRegistrationStatus?.(b) === 'open')
        - Number(window.deriveRegistrationStatus?.(a) === 'open'))
    .slice(0, 5);
  const thisYear = new Date().getFullYear();
  const recentRecord = (records || []).filter(r => r.year === thisYear || r.year === thisYear - 1).slice(0, 3);

  const hour = new Date().getHours();
  const greet = hour < 12 ? STR.goodMorning : hour < 18 ? STR.goodAfternoon : STR.goodEvening;

  // Profile-incomplete state — the user has an account but skipped (or never
  // finished) onboarding, so the wallet shows placeholder "0 / 45 · User"
  // data. We surface a prominent complete-profile card as the first thing on
  // Home (the tappable greeting alone isn't an obvious affordance). Gated on a
  // LOADED profile so it can't flash for an onboarded user whose profile row is
  // still in flight (or fails to load offline). onEditProfile routes a
  // not-onboarded user straight into OnboardingFlow (see goToProfileEdit).
  const needsProfile = !!(onEditProfile && profile && !profile.onboarded);
  const expiredLicenses = profile
    ? walletCards.filter(lic => (window.licenseLifecycleState
        ? window.licenseLifecycleState(lic, profile) : null) === 'expired')
    : [];
  // One first-screen notice, with a deterministic priority. This keeps the
  // wallet visible without making Home feel like an announcement feed.
  //
  // ‼️ A 'critical' 公告 SUPPRESSES THIS ENTIRE CHAIN. These cards are all
  // growth prompts — finish onboarding, scan a certificate, try the AI. When
  // the operator has posted something service-affecting, stacking "Scan your
  // first certificate" underneath it is the app talking over itself. Only
  // 'critical' does this; info/success/warning notices coexist.
  const homeNoticeKind = hasCriticalNotice
    ? null
    : profile?.onboarded && records.length === 0 && !firstScanDismissed
    ? 'firstScan'
    : expiredLicenses.length
      ? 'rollover'
    : needsProfile
      ? 'profile'
      : !aiLiveSeen
        ? 'ai'
        : (u.cycleStart || u.renewalDate) ? 'cycle' : null;

  return (
    <div className={'scroll fade-in home-screen' + (homeIsPro ? ' is-pro' : '')} style={{ paddingBottom: 32 }}>
      <div className="top sticky">
        {/* Greeting + name — tappable shortcut to Edit profile (or onboarding
            if the profile isn't finished yet), mirroring the Profile identity
            card. Only interactive for signed-in users (onEditProfile set). */}
        <div
          style={{ flex: 1, cursor: onEditProfile ? 'pointer' : undefined }}
          role={onEditProfile ? 'button' : undefined}
          tabIndex={onEditProfile ? 0 : undefined}
          aria-label={onEditProfile ? (lang === 'zh' ? '編輯個人資料' : 'Edit profile') : undefined}
          onClick={onEditProfile || undefined}
          onKeyDown={onEditProfile ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEditProfile(); }
          } : undefined}
        >
          <div className="h-greet">{L(greet, lang)}</div>
          <div className="home-member-name" style={{ marginTop: 4 }}>
            <span className="h-screen">{L(u.shortName, lang)}</span>
            {homeIsPro && typeof ProBadge === 'function' ? <ProBadge lang={lang} /> : null}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <LangPill lang={lang} setLang={setLang} />
          <button
            className="icon-btn"
            onClick={() => (onOpenNotifications ? onOpenNotifications() : onTab('profile', 'notificationCenter'))}
            aria-label={
              notifUnread > 0
                ? (lang === 'zh' ? `通知記錄,${notifUnread} 則未讀` : `Notification center, ${notifUnread} unread`)
                : (lang === 'zh' ? '通知記錄' : 'Notification center')
            }
            style={{ position: 'relative' }}>
            <Icon name="bell" size={18} />
            {notifUnread > 0 && <span className="dot" style={{ position: 'absolute' }} aria-hidden="true"></span>}
          </button>
        </div>
      </div>

      {/* 公告 — server-driven, above everything the client hardcodes. */}
      {noticeStack}

      <CneCycleNotice lang={lang} show={homeNoticeKind === 'cycle'} />

      {homeNoticeKind === 'firstScan' && (
        <div className="card" style={{ margin: '12px 20px 0', display: 'flex', alignItems: 'center', gap: 12,
                     position: 'relative', paddingRight: 44, border: '1px solid var(--primary)', background: 'var(--primary-soft)' }}>
          <div style={{ width: 38, height: 38, flexShrink: 0, borderRadius: 12, background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="sparkle" size={18} stroke={1.9} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{lang === 'zh' ? '掃描第一張證書' : 'Scan your first certificate'}</div>
            <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.45, marginTop: 3 }}>{lang === 'zh' ? '讓 AI 填寫第一筆紀錄，提交前由你核對。' : 'Let AI prefill your first record, then review it before saving.'}</div>
          </div>
          <button type="button" className="btn btn-primary" style={{ flexShrink: 0, fontSize: 13, padding: '8px 12px' }} onClick={() => onOpenSheet?.('log:ai')}>{lang === 'zh' ? '開始' : 'Start'}</button>
          <button type="button" aria-label={lang === 'zh' ? '關閉掃描第一張證書提醒' : 'Dismiss first certificate reminder'}
            onClick={dismissFirstScan}
            style={{ position: 'absolute', top: 6, right: 6, width: 32, height: 32, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                     background: 'none', border: 'none', borderRadius: 16, color: 'var(--fg-3)', cursor: 'pointer' }}>
            <Icon name="close" size={16} stroke={1.8} />
          </button>
        </div>
      )}

      {/* Complete-profile prompt — shown when the user has an account but hasn't
          finished onboarding, so the wallet below is showing placeholder
          numbers. Deliberately the FIRST card and given a prominent, highlighted
          treatment (primary border + soft fill + a full primary button) so the
          fix is an obvious tap rather than the subtle tappable greeting alone.
          Mirrors the Profile page's "完善你的個人資料" card but louder, since Home
          is where the placeholder data is most visible. */}
      {homeNoticeKind === 'profile' && (
        <div className="card" style={{ margin: '12px 20px 0', display: 'flex', alignItems: 'center', gap: 12,
                     border: '1px solid var(--primary)', background: 'var(--primary-soft)' }}>
          <div style={{ width: 38, height: 38, flexShrink: 0, borderRadius: 12, background: 'var(--primary)',
                        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="user" size={18} stroke={1.9} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              {lang === 'zh' ? '完善你的個人資料' : 'Complete your profile'}
            </div>
            <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5, marginTop: 3 }}>
              {lang === 'zh'
                ? '加入顯示名稱、工作單位和續期週期,讓主畫面的數據顯示得準確一些。'
                : 'Add your name, workplace and renewal cycle so the dashboard shows your real numbers.'}
            </div>
          </div>
          <M.button className="btn btn-primary" style={{ flexShrink: 0, fontSize: 13, padding: '9px 16px' }}
            onClick={onEditProfile} whileTap={{ scale: 0.97 }}>
            {lang === 'zh' ? '繼續' : 'Continue'}
          </M.button>
        </div>
      )}

      {/* One-time announcement: the AI certificate scan is now live. Points to
          the Record tab, where Log → AI fill lives. Dismissable; persists via
          localStorage (vital: prefix, like the other one-time keys). Held back
          until the profile is complete so a brand-new user isn't shown two
          competing cards — finishing setup takes priority. */}
      {homeNoticeKind === 'ai' && (
        <div className="card" style={{ margin: '12px 20px 0', display: 'flex', alignItems: 'flex-start', gap: 12,
                     border: '1px solid var(--primary)', background: 'var(--primary-soft)' }}>
          <div style={{ width: 34, height: 34, flexShrink: 0, borderRadius: 17, background: 'var(--primary)',
                        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="sparkle" size={17} stroke={2} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>
              {lang === 'zh' ? '新功能:AI 智能識別' : 'New: AI scan'}
            </div>
            <div className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginTop: 3 }}>
              {lang === 'zh'
                ? `上載證書、課程海報,或貼上課程網址,AI 自動幫你填寫。已完成或啱報咗名嘅課程都得。${homeProEnt.loading
                    ? ''
                    : homeIsPro
                      ? 'Vital Pro 已開啟，不限年度次數。'
                      : scanCredits?.available != null ? `現時可用 ${scanCredits.available} 次。` : ''}`
                : `Upload a certificate or poster, or paste a course link — AI fills in the record. Works for finished and just-enrolled courses.${homeProEnt.loading
                    ? ''
                    : homeIsPro
                      ? ' Vital Pro is active, with no annual scan quota.'
                      : scanCredits?.available != null ? ` ${scanCredits.available} scans available now.` : ''}`}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <M.button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: 12.5 }}
                onClick={() => { dismissAiLive(); onOpenSheet && onOpenSheet('log:ai'); }}
                whileTap={{ scale: 0.97 }}>
                {lang === 'zh' ? '立即試用' : 'Try it now'}
              </M.button>
              <button className="btn btn-ghost" style={{ padding: '6px 14px', fontSize: 12.5 }}
                onClick={dismissAiLive}>
                {lang === 'zh' ? '知道了' : 'Got it'}
              </button>
            </div>
          </div>
          <button type="button" aria-label={lang === 'zh' ? '關閉' : 'Dismiss'}
            onClick={dismissAiLive}
            style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer',
                     color: 'var(--fg-3)', padding: 2, lineHeight: 0 }}>
            <Icon name="close" size={16} stroke={1.8} />
          </button>
        </div>
      )}

      {/* Wallet(s) — one card per practising certificate. A dual-registered
          nurse (Part I + Part II) sees a card per cycle, each labelled with its
          register part; a single-licence nurse sees exactly one, unlabelled and
          visually unchanged. Each card is clickable → the Record tab's
          completed-history view (已完成記錄). */}
      <div style={{ padding: '14px 20px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Expired-certificate rollover prompt — one per lapsed licence, above
            its card. Neutral copy: live data shows years-stale dates (nurses who
            entered an old registration period), so the DATA may be old rather
            than the licence — never scold. default_dates (untouched onboarding
            seed pair) is handled by the confirm-date paths instead, on purpose. */}
        {homeNoticeKind === 'rollover' && expiredLicenses.slice(0, 1).map(lic => {
          return (
            <button key={'roll-' + lic.key} type="button"
              onClick={() => onOpenSheet && onOpenSheet('rollover:' + lic.key)}
              className="card"
              style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
                cursor: 'pointer', font: 'inherit', color: 'inherit',
                border: '1px solid var(--danger, #c0392b)', padding: '13px 14px' }}>
              <div style={{ width: 34, height: 34, borderRadius: 11, background: 'var(--accent-soft)',
                color: 'var(--danger, #c0392b)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name="clock" size={16} stroke={1.8} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="h-row" style={{ fontSize: 13.5 }}>
                  {lang === 'zh'
                    ? `你的執業證書已於 ${fmtDate(lic.renewalDate, lang)} 到期。已續期？`
                    : `Your certificate expired on ${fmtDate(lic.renewalDate, lang)}. Renewed?`}
                  {walletCards.length > 1 && lic.role ? ` · ${L(lic.role, lang)}` : ''}
                </div>
                <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                  {lang === 'zh' ? '一撳更新你的新週期' : 'One tap to set up your new cycle'}
                </div>
              </div>
              <Icon name="chev-r" size={14} color="var(--fg-3)" stroke={1.7} />
            </button>
          );
        })}
        {walletCards.length > 1 ? (
          // Dual registration → one combined card holding both certificates'
          // cycles (see CombinedWalletCard); a single certificate keeps its own
          // full card, visually unchanged.
          <CombinedWalletCard
            licenses={walletCards}
            lang={lang}
            isPro={homeIsPro}
            lifecycleOf={(lic) => (profile && window.licenseLifecycleState ? window.licenseLifecycleState(lic, profile) : null)}
            onOpen={() => (onOpenRecordHistory ? onOpenRecordHistory() : onTab && onTab('record'))}
          />
        ) : (
          walletCards.map(lic => (
            <WalletCard
              key={lic.key}
              lic={lic}
              lang={lang}
              isPro={homeIsPro}
              lifecycle={profile && window.licenseLifecycleState ? window.licenseLifecycleState(lic, profile) : null}
              showRole={false}
              onOpen={() => (onOpenRecordHistory ? onOpenRecordHistory() : onTab && onTab('record'))}
            />
          ))
        )}
        {/* Dual-certificate counting note: the same activity is counted toward
            every certificate whose CNE window covers its date. Surfaced once,
            below both cards, so the two totals aren't mistaken for independent
            tallies — the nurse confirms each activity's relevance per NCHK. */}
        {walletCards.length > 1 ? (
          <div className="wallet-cycle-note" style={{ padding: '0 4px' }}>
            {lang === 'zh'
              ? '同一活動若日期同時落入兩張證書的 CNE 週期，會分別計入兩張。請自行確認每項活動符合相應註冊部分的要求。'
              : 'An activity dated within both certificates\' CNE cycles counts toward each. Please confirm every activity meets the requirements of the relevant registration.'}
          </div>
        ) : null}
      </div>

      {homeIsPro && !homeProReadiness.loading
        && homeProReadiness.readyCount < homeProReadiness.total && (
        <ProHomeSummary
          lang={lang}
          name={L(u.shortName, lang)}
          readiness={homeProReadiness}
          onOpen={() => onOpenSheet?.('pro:renewal', { initialEnt: homeProEnt })}
        />
      )}

      {/* Ward leaderboard entry — the density engine's front door on Home. */}
      {session && onTab && (
        <div style={{ padding: '0 4px 4px' }}>
          <button type="button" className="card"
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, width: '100%', cursor: 'pointer',
                     textAlign: 'left', font: 'inherit', color: 'inherit' }}
            onClick={() => onTab('wardgroup')}>
            <div style={{ width: 38, height: 38, borderRadius: 11, flexShrink: 0, background: 'var(--primary-soft)',
                          color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="users" size={19} stroke={1.7} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 650 }}>{L(STR.wardGroups, lang)}</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 1 }}>{L(STR.wardGroupsSub, lang)}</div>
            </div>
            <Icon name="chev-r" size={18} color="var(--fg-3)" />
          </button>
        </div>
      )}

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <Section title={L(STR.upcoming, lang)} action={L(STR.viewAll, lang)} onAction={() => onTab && onTab('record')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {upcoming.map(({ course, next }) => (
              <UpcomingRow key={course.id} course={course} next={next} lang={lang} onClick={() => setPreviewEntry({ course, next })} />
            ))}
          </div>
        </Section>
      )}

      {/* Free CNE — above the generic recommendations on purpose: "costs you
          nothing" outranks "you might like these". */}
      <FreeCneCard lang={lang} summary={freeCne} onOpen={onOpenFreeCne} />

      {/* The route, when one can actually be built — otherwise the demand test.
          Exactly one of these renders. RoutePlanCard returns null whenever the
          catalogue cannot produce a route (today: no course carries a
          cne_relation classification, so it always can't), which is what makes
          the fallback the honest default rather than a placeholder. */}
      <div ref={routeTargetRef}
        tabIndex={routeTargetActive ? -1 : undefined}
        className={routeTargetActive ? 'pro-target-highlight' : undefined}>
      {routePlan && routePlan.plans?.cheapest?.courses?.length ? (
        <RoutePlanCard lang={lang} plan={routePlan} isPro={homeProEnt.pro}
          expanded={routeExpanded}
          onExpandedChange={changeRouteExpanded}
          /* "Been through setup" — memberships on file OR the questions saved.
             Using memberships alone would keep asking a nurse who belongs to no
             society at all, forever, for something she has already answered. */
          personalised={!!((Array.isArray(profile?.memberships) && profile.memberships.length > 0)
            || profile?.prefs?.coursePrefs?.setAt)}
          notStarted={!!(routeLicense?.cycleStart
            && notStartedMap[routeLicense.key || 'primary'] === routeLicense.cycleStart)}
          notStartedBusy={notStartedBusy}
          notStartedError={notStartedErr}
          /* ‼️ ONLY WHEN THE ANSWER CAN ACTUALLY BE STORED. cneNotStarted is
             keyed `{ [licKey]: cycleStart }` so a rollover re-asks, so a licence
             with no cycleStart has nothing to key it under and markNotStarted
             could only return silently — which is exactly how this shipped.
             ‼️ NOT the same test as the card's window guard: renewalDate ALONE
             yields a complete window (cneWindow ignores pcFrom when pcTo is
             set), so this state is reachable with every figure on the card
             correct. Withhold the handler, not the card. */
          onNotStarted={routeLicense?.cycleStart
            ? () => markNotStarted(routeLicense)
            : undefined}
          /* Lands on the practising-certificate section of Edit profile — or on
             onboarding, if she never finished it (goToProfileEdit branches).
             'cycle' is auditReadiness' existing gap id for exactly this blank,
             so the readiness card and this card send her to the same field.

             ‼️ ONE SETUP PROMPT AT A TIME. When Home is already showing the
             完善個人資料 card, a second card asking for the same certificate
             dates — and routing to the same place — is the app talking over
             itself, the very thing the notice chain above is ordered to prevent
             ("a brand-new user isn't shown two competing cards"). Withholding
             the handler stands the date prompt down via RoutePlanCard's
             fail-closed path; a nurse who FINISHED onboarding and skipped the
             date step still gets it, and she is the one with no other prompt
             anywhere on Home. */
          onFixCycleDates={onEditProfile && homeNoticeKind !== 'profile'
            ? () => onEditProfile('cycle')
            : undefined}
          /* Offered only when a real alternative was found (see the peek above),
             and it is exactly the plan this tap will show. */
          canSwap={!!routeAlternative?.plans?.cheapest?.courses?.length}
          swapped={routeSwapped.length > 0}
          onSwap={() => setRouteSwapped(routeNextExclude)}
          onResetSwap={() => setRouteSwapped([])}
          /* Marks the rows she has already registered for — see the 已報名
             note on the rows themselves. */
          enrolledIds={enrolledIds}
          onOpenCourse={onOpenCourse}
          onLogRecord={() => onOpenSheet && onOpenSheet('log:ai')}
          onPersonalise={() => onOpenSheet && onOpenSheet('pro:setup')}
          /* ‼️ ONLY WHERE A SUBSCRIPTION CAN ACTUALLY BE BOUGHT — the same
             convention as ContinueLoggingDialog's onUpgrade, and for the same
             reason. window.proPurchasable() is false on the web, so this card's
             Free variant stood down there rather than sending a nurse to a
             paywall that cannot take her money. A subscriber is unaffected:
             isPro comes from the server, so the full route still renders on the
             web for anyone who bought on iOS or redeemed a code. Do not replace
             this with a platform check, and do not make it unconditional. */
          onExplore={window.proPurchasable?.()
            ? () => onOpenSheet && onOpenSheet('pro:route', { initialEnt: homeProEnt })
            : undefined} />
      ) : (
        <PlannerInterestCard lang={lang} gap={outstandingPoints}
          hasRecords={(routePlan?.cycleRecordCount ?? 0) > 0}
          joined={plannerBetaJoined} busy={plannerBetaBusy} onJoin={joinPlannerBeta} />
      )}
      </div>

      {/* Recommended (horizontal scroll) */}
      <Section title={homeIsPro ? (lang === 'zh' ? '為你精選' : 'Chosen for you') : L(STR.recommended, lang)} action={L(STR.viewAll, lang)} onAction={() => onTab && onTab('discover')}>
        <div className="scroll-x">
          {recommended.map(c => (
            <MiniCourseCard key={c.id} course={c} lang={lang} onClick={() => onOpenCourse(c.id)} />
          ))}
        </div>
      </Section>

      {/* Reminders — surfaced inline so the user sees what needs action
          right under the recommendation strip. Tapping a row routes to
          the relevant destination (course detail / record / overview). */}
      {reminders && reminders.length > 0 && (
        <Section
          title={lang === 'zh' ? '提醒' : 'Reminders'}
          action={reminders.length > 4 ? (lang === 'zh' ? '查看全部' : 'View all') : null}
          onAction={() => onOpenSheet && onOpenSheet('reminders')}>
          {typeof RemindersInlineList === 'function' && (
            <RemindersInlineList
              lang={lang}
              reminders={reminders}
              onRouteReminder={onRouteReminder}
              max={4}
            />
          )}
        </Section>
      )}

      {/* Recent activity */}
      <Section title={L(STR.recentActivity, lang)} action={L(STR.viewAll, lang)} onAction={() => onTab && onTab('record')}>
        {recentRecord.length ? <div className="card flush">
          {recentRecord.map((r, i) => (
            <div key={r.id} className="card-row" style={{ borderBottom: i === recentRecord.length - 1 ? 0 : undefined }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <CatDot cat={r.category} />
                  <div className="mono muted-2" style={{ fontSize: 10.5, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                    {fmtShort(r.date, lang)} · {L(r.provider, lang)}
                  </div>
                </div>
                <div className="h-row">{L(r.title, lang)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="serif" style={{ fontSize: 22, lineHeight: 1, letterSpacing: '-0.02em' }}>+{r.points}</div>
                {r.certificate
                  ? <Icon name="check-circ" size={13} color="var(--success)" stroke={1.8} />
                  : <span style={{ fontSize: 9.5, color: 'var(--accent)' }}>!</span>}
              </div>
            </div>
          ))}
        </div> : <div className="vital-empty" style={{ margin: '0 20px' }}>
          <div className="vital-empty-icon"><Icon name="book" size={22} stroke={1.7} /></div>
          <div className="vital-empty-title">{lang === 'zh' ? '尚未有課程紀錄' : 'No course records yet'}</div>
          <div className="vital-empty-sub">{lang === 'zh' ? '記錄第一個完成的課程，這裡會顯示最近活動。' : 'Log your first completed course to see your recent activity here.'}</div>
        </div>}
      </Section>

      {previewEntry && (
        <UpcomingPreviewDialog
          entry={previewEntry}
          lang={lang}
          onClose={() => setPreviewEntry(null)}
          onOpenCourse={onOpenCourse}
        />
      )}
    </div>
  );
}

// Category breakdown row. The app's core/elective/specialty buckets are an
// INTERNAL grouping — the NCHK sets no per-category minimum (only the cycle
// TOTAL of 45/30 counts), so a "/20" per-category target was fictional and read
// as absurd once a nurse exceeded it (e.g. 76.5/20). Instead we show the
// category's absolute points and a bar proportional to its SHARE of the
// categorised total, so the legend mirrors the ring's coloured arcs rather than
// implying a target that doesn't exist.
function CatLegend({ label, cat, val, total }) {
  const pct = total > 0 ? Math.round((val / total) * 100) : 0;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--fg-2)', fontWeight: 500 }}>
          <CatDot cat={cat} size={6} />
          {label}
        </span>
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg)', fontVariantNumeric: 'tabular-nums' }}>
          {val}
        </span>
      </div>
      <div className="progress-bar" style={{ height: 4 }}>
        <div style={{ width: pct + '%', background: CAT_COLORS[cat] }} />
      </div>
    </div>
  );
}

// One CNE-wallet card: the progress ring, category legend and cycle-window note
// for a single practising certificate. HomeScreen renders one per licence a
// dual-registered nurse holds; `lic` is a descriptor from profileLicenses()
// carrying its own earned/split/required and cycle dates, so two cards never
// share state. `showRole` labels the card with its role (which encodes the
// register part, e.g. 精神科) — omitted for the common single-licence case so
// that view is unchanged.
// `lifecycle` is the derived state from licenseLifecycleState (null for the
// offline/demo synthesized card): 'expired' and 'default_dates' replace the
// days-to-renewal chip, which otherwise froze at "0 日內續期" forever once a
// certificate lapsed.
// Ring segments for one licence — a neutral baseline arc + the category arcs,
// cumulatively clamped to `required`. The ring reads as a PERCENTAGE OF THE
// GOAL: ProgressRing divides each arc by Σ(seg.total), so `required` rides on
// the first surviving segment (0 on the rest) → each arc = value / required; an
// over-goal user shows a full circle while the centre keeps the true number.
// Shared by the full ring (WalletCardBody) and the compact dual-registration
// rows (CombinedWalletCard) so the two renderings can never drift.
function walletRingSegments(lic) {
  const earned = lic.earned;
  const required = lic.required || 0;
  const split = lic.split || { core: 0, elective: 0, specialty: 0 };
  // Exempt / no-requirement cycle has no goal to fill. An empty track reads as
  // broken ("why is my ring blank?"), so paint a full, MUTED ring instead —
  // "settled, nothing outstanding". A genuine 0-progress cycle (required>0,
  // nothing earned yet) still gets an empty ring, with its 0% shown in the centre.
  if (required <= 0) {
    return lic.exempt ? [{ value: 1, color: 'var(--border-strong)', total: 1 }] : [];
  }
  // baseline_points isn't categorised; surface it as a neutral leading arc so
  // the ring fill still equals the centre `earned` number.
  const baseline = Math.max(0, earned - (split.core + split.elective + split.specialty));
  let ringRemaining = required;
  return [
    { value: baseline,        color: 'var(--fg-2)' },
    { value: split.core,      color: CAT_COLORS.core },
    { value: split.elective,  color: CAT_COLORS.elective },
    { value: split.specialty, color: CAT_COLORS.specialty },
  ].map(s => {
    const value = Math.max(0, Math.min(s.value, ringRemaining));
    ringRemaining -= value;
    return { value, color: s.color };
  }).filter(s => s.value > 0)
  .map((s, i) => ({ ...s, total: i === 0 ? required : 0 }));
}

// Centre glyph for the COMPACT dual-registration ring, which (unlike the full
// card) has no big number inside — so an empty hole looked purposeless. Encodes
// state: goal met → primary check; exempt → muted check ("you're clear"); in
// progress → percent-of-goal, the one figure not already shown beside the ring.
function WalletRingGlyph({ lic }) {
  const required = lic.required || 0;
  if (required > 0 && lic.earned >= required) {
    return <Icon name="check" size={16} stroke={2.4} color="var(--primary)" />;
  }
  if (lic.exempt || required <= 0) {
    return <Icon name="check" size={15} stroke={2.2} color="var(--fg-3)" />;
  }
  return (
    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--fg-2)',
      fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>
      {Math.round((lic.earned / required) * 100)}%
    </span>
  );
}

// Status chip for one licence: 已過期 / 請確認日期 (from licenseLifecycleState)
// or the days-to-renewal countdown. Shared by the full card and the compact rows.
function WalletStatusChip({ lic, lang, lifecycle }) {
  if (lifecycle === 'expired') {
    return (
      <span className="chip ghost" style={{ background: 'transparent', border: '0.5px solid var(--danger, #c0392b)', color: 'var(--danger, #c0392b)' }}>
        <Icon name="clock" size={11} stroke={1.7} />
        {lang === 'zh' ? '已過期' : 'Expired'}
      </span>
    );
  }
  if (lifecycle === 'default_dates') {
    return (
      <span className="chip ghost" style={{ background: 'transparent', border: '0.5px solid var(--border)' }}>
        <Icon name="clock" size={11} stroke={1.7} />
        {lang === 'zh' ? '請確認日期' : 'Confirm dates'}
      </span>
    );
  }
  if (!lic.renewalDate) return null;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const days = Math.max(0, daysBetween(today, lic.renewalDate));
  return (
    <span className="chip ghost" style={{ background: 'transparent', border: '0.5px solid var(--border)' }}>
      <Icon name="clock" size={11} stroke={1.7} />
      {days} {L(STR.daysToRenewal, lang)}
    </span>
  );
}

// Cycle-window + deadline note for one licence. Spells out the window the ring
// counts (so out-of-cycle records don't look vanished), then the earn-by (cycle
// end, 6 months BEFORE PC expiry — the renewal-processing window) and renew-by
// dates, with the voluntary/exempt framing gated on lic.isMandatory. Shared by
// the full card and the compact rows' expanded detail.
function WalletCycleNote({ lic, lang }) {
  if (!lic.cycleStart && !lic.renewalDate) return null;
  const w = cneWindow(lic.cycleStart || null, lic.renewalDate || null);
  return (
    <div className="wallet-cycle-note">
      <div>
        {lang === 'zh'
          ? `本 CNE 週期內學分 · ${fmtDate(w.start, lang)} 至 ${fmtDate(w.end, lang)}`
          : `Points in this CNE cycle · ${fmtDate(w.start, lang)} – ${fmtDate(w.end, lang)}`}
      </div>
      {(w.end && lic.renewalDate) ? (
        <div style={{ marginTop: 3, color: 'var(--fg-3)' }}>
          {lang === 'zh'
            ? `學分死線 ${fmtDate(w.end, lang)} · 續證到期 ${fmtDate(lic.renewalDate, lang)}`
            : `Earn by ${fmtDate(w.end, lang)} · Renew by ${fmtDate(lic.renewalDate, lang)}`}
          {/* Say WHY it's voluntary, not just that it is: the first mandatory
              cycle binds certificates expiring on/after 31 Dec 2029 (NCHK
              letter 30 Jun 2026, verified against the PDF 2026-07-10) — a
              threshold the user can check their own renewal date against. */}
          {!lic.isMandatory ? (
            <span style={{ color: 'var(--fg-2)' }}> · {lang === 'zh'
              ? (lic.exempt ? '首次執業證免 CNE' : '自願 · 2029年12月31日起到期先強制')
              : (lic.exempt ? 'First PC — CNE exempt' : 'Voluntary — mandatory from expiries on/after 31 Dec 2029')}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// Shared BODY of a full-size CNE-wallet card — header (eyebrow + status chip) +
// 132px ring + category legend + cycle note, WITHOUT the outer card wrapper or
// the CTA row. Used by the single-certificate WalletCard; the dual-registration
// compact rows (CombinedWalletCard) reuse the same helpers above instead.
// `lifecycle` is the derived state from licenseLifecycleState (null for the
// offline/demo synthesized card).
function WalletCardBody({ lic, lang, lifecycle, showRole, isPro = false }) {
  const earned = lic.earned;
  const required = lic.required || 0;
  const split = lic.split || { core: 0, elective: 0, specialty: 0 };
  // Categorised total (excludes uncategorised baseline) — the denominator the
  // category bars are shown as a share of, mirroring the ring's arcs.
  const catTotal = split.core + split.elective + split.specialty;
  const goalMet = required > 0 && earned >= required;
  const segments = walletRingSegments(lic);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 8 }}>
        <div className="wallet-title-line">
          <div className="eyebrow">
            {L(STR.pointsWallet, lang)}
            {showRole && lic.role
              ? <span style={{ color: 'var(--fg-3)', fontWeight: 500 }}> · {L(lic.role, lang)}</span>
              : null}
          </div>
          {isPro && typeof ProBadge === 'function' ? <ProBadge lang={lang} /> : null}
        </div>
        <WalletStatusChip lic={lic} lang={lang} lifecycle={lifecycle} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, position: 'relative', zIndex: 1 }}>
        <div style={{ position: 'relative', width: 132, height: 132, flexShrink: 0 }}>
          <ProgressRing size={132} stroke={9} segments={segments} />
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <div className="ring-num">{earned}</div>
            <div className="mono muted-2" style={{ fontSize: 9.5, letterSpacing: '0.10em', textTransform: 'uppercase', marginTop: 2 }}>
              {/* First-PC exempt cycle carries required 0 — "/ 0 分" reads like a
                  bug, so name the exemption instead. */}
              {lic.exempt && required === 0
                ? (lang === 'zh' ? '暫免 CNE' : 'CNE exempt')
                : <>/ {required} {L(STR.pts, lang)}</>}
            </div>
            {goalMet && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 5,
                fontSize: 9.5, fontWeight: 600, lineHeight: 1, color: 'var(--primary)',
              }}>
                <Icon name="check" size={9} stroke={2.2} />
                {L(STR.goalMet, lang)}
              </div>
            )}
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <CatLegend label={L(STR.core, lang)}      cat="core"      val={split.core} total={catTotal} />
          <CatLegend label={L(STR.elective, lang)}  cat="elective"  val={split.elective} total={catTotal} />
          <CatLegend label={L(STR.specialty, lang)} cat="specialty" val={split.specialty} total={catTotal} />
        </div>
      </div>
      <WalletCycleNote lic={lic} lang={lang} />
    </>
  );
}

// Shared CTA row for the wallet card(s) — clickable "view earned courses".
function WalletCtaRow({ lang }) {
  return (
    <div className="wallet-cta-row">
      <span>{lang === 'zh' ? '查看已修課程' : 'View earned courses'}</span>
      <Icon name="arrow-r" size={13} stroke={1.7} />
    </div>
  );
}

const WALLET_MOTION = {
  whileHover: { y: -1 }, whileTap: { scale: 0.995 }, transition: { duration: 0.16 },
};

// Single certificate — the full wallet card (body + CTA in one clickable card).
function WalletCard({ lic, lang, lifecycle, showRole, isPro = false, onOpen }) {
  return (
    <M.div
      className={'wallet wallet-clickable' + (isPro ? ' is-pro' : '')}
      role="button" tabIndex={0}
      aria-label={lang === 'zh' ? '查看已修課程記錄' : 'View earned record'}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      {...WALLET_MOTION}
    >
      <WalletCardBody lic={lic} lang={lang} lifecycle={lifecycle} showRole={showRole} isPro={isPro} />
      {(lic.required > 0 && lic.earned >= lic.required) && (
        <button
          type="button"
          className="btn btn-ghost"
          style={{ position: 'relative', zIndex: 1, width: '100%', justifyContent: 'center',
                   marginTop: 10, fontSize: 12.5, gap: 6 }}
          onClick={async (e) => {
            e.stopPropagation();
            await shareWithReward({
              title: lang === 'zh' ? 'Vital · 香港護士 CNE' : 'Vital · HK Nurse CNE',
              text: L(STR.goalShareText, lang),
              url: window.DB.buildShareUrl('/'), lang,
            });
          }}
        >
          🎉 {L(STR.goalShareChip, lang)}
        </button>
      )}
      <WalletCtaRow lang={lang} />
    </M.div>
  );
}

// Dual registration — ONE card with a COMPACT ROW per certificate ("Direction
// B"): a small ring + role + score + status chip, with a one-line category
// breakdown, both certificates visible at once at roughly 40% the height of two
// stacked full cards. Tapping a row expands that certificate's cycle-window /
// deadline / mandatory-framing detail (WalletCycleNote — same content the full
// card shows inline). The card itself is NOT clickable (rows toggle detail);
// the shared CTA row at the bottom is the route to the earned-courses history.
function CombinedWalletCard({ licenses, lang, lifecycleOf, isPro = false, onOpen }) {
  const zh = lang === 'zh';
  // Per-row expanded state, keyed by licence key — both rows may be open at once.
  const [openKeys, setOpenKeys] = React.useState({});
  const toggle = (k) => setOpenKeys(p => ({ ...p, [k]: !p[k] }));

  const catInline = (cat, label, val) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
      <CatDot cat={cat} size={6} />
      {label} <span style={{ color: 'var(--fg)', fontWeight: 600 }}>{val}</span>
    </span>
  );

  return (
    <div className={'wallet' + (isPro ? ' is-pro' : '')}>
      <div className="wallet-title-line" style={{ position: 'relative', zIndex: 1, marginBottom: 14 }}>
        <div className="eyebrow">
          {L(STR.pointsWallet, lang)} · {zh ? '兩張證書' : 'Two certificates'}
        </div>
        {isPro && typeof ProBadge === 'function' ? <ProBadge lang={lang} /> : null}
      </div>
      {licenses.map((lic, i) => {
        const required = lic.required || 0;
        const split = lic.split || { core: 0, elective: 0, specialty: 0 };
        const goalMet = required > 0 && lic.earned >= required;
        const open = !!openKeys[lic.key];
        return (
          <React.Fragment key={lic.key}>
            {i > 0 ? <div style={{ borderTop: '0.5px solid var(--border)', margin: '16px 0', position: 'relative', zIndex: 1 }} /> : null}
            {/* Row layout is ring | full-width stack — the status chip lives on
                its OWN line inside the stack, never sharing a line with the role
                name, score or categories. A right-hand chip column squeezed the
                text to ~100pt on a 390pt phone (wrapping 已達標 one character
                per line and stacking the categories vertically), so nothing
                except name+chevron shares a line, and the number/unit/goal-met
                fragments are nowrap so they can never shatter. */}
            <button
              type="button"
              aria-expanded={open}
              onClick={() => toggle(lic.key)}
              style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent',
                border: 0, padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit',
                position: 'relative', zIndex: 1 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ position: 'relative', width: 56, height: 56, flexShrink: 0 }}>
                  <ProgressRing size={56} stroke={5} segments={walletRingSegments(lic)} />
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <WalletRingGlyph lic={lic} />
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 600, letterSpacing: '-0.005em', minWidth: 0 }}>
                      {L(lic.role, lang) || (zh ? '執業證書' : 'Certificate')}
                    </span>
                    <span aria-hidden="true" style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--fg-3)',
                      transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .18s ease' }}>
                      <Icon name="chev-r" size={13} stroke={1.8} />
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 26, fontWeight: 500, letterSpacing: '-0.02em', lineHeight: 1, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {lic.earned}
                    </span>
                    <span className="mono muted-2" style={{ fontSize: 9.5, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      {/* First-PC exempt cycle carries required 0 — "/ 0 分" reads
                          like a bug, so name the exemption instead (mirrors the
                          full card's ring centre). */}
                      {lic.exempt && required === 0
                        ? (zh ? '暫免 CNE' : 'CNE exempt')
                        : <>/ {required} {L(STR.pts, lang)}</>}
                    </span>
                    {goalMet && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 600, color: 'var(--primary)', whiteSpace: 'nowrap' }}>
                        <Icon name="check" size={9} stroke={2.2} />
                        {L(STR.goalMet, lang)}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', fontSize: 11, color: 'var(--fg-2)', fontVariantNumeric: 'tabular-nums' }}>
                    {catInline('core',      L(STR.core, lang),      split.core)}
                    {catInline('elective',  L(STR.elective, lang),  split.elective)}
                    {catInline('specialty', L(STR.specialty, lang), split.specialty)}
                  </div>
                  <div>
                    <WalletStatusChip lic={lic} lang={lang} lifecycle={lifecycleOf ? lifecycleOf(lic) : null} />
                  </div>
                </div>
              </div>
            </button>
            {open ? (
              // Full width — an indent to the text column costs too much line
              // length for the bilingual deadline copy on a 390pt phone.
              <div style={{ margin: '0 0 2px', position: 'relative', zIndex: 1 }}>
                <WalletCycleNote lic={lic} lang={lang} />
              </div>
            ) : null}
          </React.Fragment>
        );
      })}
      <button
        type="button"
        className="wallet-cta-row"
        onClick={onOpen}
        style={{ width: '100%', background: 'transparent', cursor: 'pointer',
          font: 'inherit', border: 0, borderTop: '0.5px solid var(--border)',
          padding: '10px 0 0', position: 'relative', zIndex: 1 }}
      >
        <span>{zh ? '查看已修課程' : 'View earned courses'}</span>
        <Icon name="arrow-r" size={13} stroke={1.7} />
      </button>
    </div>
  );
}

function UpcomingRow({ course, next, lang, onClick }) {
  // `next` is the user's resolved next-upcoming session (from 我的記錄's
  // session schedule). Falls back to the catalog start date if absent.
  const iso = next?.iso || course.date;
  const todayIso = todayLocalIso();
  const d = Math.max(0, daysBetween(todayIso, iso));
  const dateObj = new Date(iso + 'T00:00:00');
  const displayTime = (next && next.time) || course.time;
  return (
    <div className="card" style={{ padding: 14, cursor: 'pointer', display: 'flex', gap: 14, alignItems: 'center' }} onClick={onClick}>
      <div style={{
        width: 52, flexShrink: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 0, padding: '6px 0',
        borderRight: '0.5px solid var(--border)',
        marginRight: 4,
      }}>
        <div className="mono" style={{ fontSize: 9.5, letterSpacing: '0.10em', textTransform: 'uppercase', color: 'var(--fg-3)' }}>
          {dateObj.toLocaleString('en', { month: 'short' })}
        </div>
        <div className="serif" style={{ fontSize: 26, lineHeight: 1, letterSpacing: '-0.02em' }}>
          {dateObj.getDate()}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 5, alignItems: 'center' }}>
          <TopicPill topic={course.topic} label={L(course.topicLabel, lang)} />
        </div>
        <div className="h-row" style={{ fontSize: 14 }}>{L(course.title, lang)}</div>
        <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>
          {displayTime} · {L(course.venue, lang).split(',')[0]}
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div className="chip solid" style={{ marginBottom: 4 }}>
          {d === 0 ? (lang === 'zh' ? '今日' : 'Today') : `${d}${lang === 'zh' ? '日' : 'd'}`}
        </div>
        <div className="mono muted-2" style={{ fontSize: 9.5, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
          {course.points} {L(STR.pts, lang)}
        </div>
      </div>
    </div>
  );
}

// Quick-look shown when an "Upcoming" row on Home is tapped, instead of
// jumping straight to the full course page: the session essentials at a
// glance, a one-tap hand-off to the device's maps app for directions to
// the venue, and a button through to the full detail screen.
function UpcomingPreviewDialog({ entry, lang, onClose, onOpenCourse }) {
  const { course, next } = entry;
  const iso = (next && next.iso) || course.date;
  const dateObj = iso ? new Date(iso + 'T00:00:00') : null;
  const weekday = dateObj
    ? dateObj.toLocaleDateString(lang === 'zh' ? 'zh-HK' : 'en-US', { weekday: lang === 'zh' ? 'long' : 'short' })
    : '';
  const dateText = iso
    ? (weekday ? `${fmtDate(iso, lang)} · ${weekday}` : fmtDate(iso, lang))
    : (lang === 'zh' ? '日期待定' : 'Date TBC');
  const timeText = ((next && next.time) || course.time) || (lang === 'zh' ? '時間待定' : 'Time TBC');
  const address = L(course.venue, lang);
  // Only offer directions for a real, physical address — placeholders
  // ("Venue TBC", "Online") have no raw venue and can't be navigated to.
  const hasVenue = !course.online && !!(course.raw?.venue_en || course.raw?.venue_zh);

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Hand off to the device's maps app for turn-by-turn directions. Apple
  // Maps on iOS / Mac (daddr = destination), Google Maps everywhere else.
  // Both are universal-link forms openExternalUrl already knows how to
  // launch (native maps app on device, new tab on the web).
  const openDirections = () => {
    if (!hasVenue) return;
    const q = encodeURIComponent(address);
    const isNative = !!globalThis.Capacitor?.isNativePlatform?.();
    const isApple = isNative || /iP(hone|ad|od)|Macintosh/.test(globalThis.navigator?.userAgent || '');
    const url = isApple
      ? `https://maps.apple.com/?daddr=${q}`
      : `https://www.google.com/maps/dir/?api=1&destination=${q}`;
    openExternalUrl(url);
  };

  const metaRow = (icon, text) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <Icon name={icon} size={15} stroke={1.7} color="var(--fg-3)" />
      <span style={{ fontSize: 13.5, color: 'var(--fg-2)', lineHeight: 1.45, flex: 1, minWidth: 0 }}>{text}</span>
    </div>
  );

  return (
    <div className="register-dialog-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="register-dialog" role="dialog" aria-modal="true">
        <button className="register-dialog-close icon-btn" onClick={onClose} aria-label={lang === 'zh' ? '關閉' : 'Close'}>
          <Icon name="close" size={16} />
        </button>

        <div className="register-dialog-eyebrow mono">
          {L(STR.upcoming, lang)} · {L(course.providerShort, lang)}
        </div>
        <h3 className="register-dialog-title">{L(course.title, lang)}</h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
          {metaRow('calendar', dateText)}
          {metaRow('clock', timeText)}
          {metaRow(course.online ? 'globe' : 'pin', course.online ? (lang === 'zh' ? '網上課程' : 'Online course') : address)}
          {course.points != null && metaRow('sparkle', `${course.points} ${L(STR.pts, lang)}`)}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
          {hasVenue && (
            <button className="btn btn-ghost btn-fill" onClick={openDirections}>
              <Icon name="compass" size={16} stroke={1.8} />
              {lang === 'zh' ? '導航前往' : 'Get directions'}
            </button>
          )}
          <button className="btn btn-primary btn-fill" onClick={() => { onClose(); onOpenCourse(course.id); }}>
            {lang === 'zh' ? '查看課程詳情' : 'View course details'}
            <Icon name="arrow-r" size={15} stroke={2} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, action, onAction, children }) {
  // Outer div gets `padding: 0 20px`. Without it, any `.scroll-x` child
  // (which uses `margin: 0 -20px` to break out of the parent's
  // horizontal padding) escapes into the screen edges and the first
  // card in the horizontal scroller flushes to x=0 — the pill /
  // "Wound Care" label hits the very edge. With the outer padding the
  // break-out cancels cleanly and the cards keep their 20 px inset.
  return (
    <div style={{ marginTop: 26, padding: '0 20px' }}>
      <div style={{ padding: '0 0 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h3 className="h-section" style={{ margin: 0 }}>{title}</h3>
        {action && (
          <button onClick={onAction} style={{
            appearance: 'none', background: 'transparent', border: 0,
            color: 'var(--fg-2)', font: 'inherit', fontSize: 12.5,
            display: 'inline-flex', gap: 4, alignItems: 'center', cursor: 'pointer',
          }}>
            {action} <Icon name="chev-r" size={13} stroke={1.6} />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function LangPill({ lang, setLang }) {
  return (
    <div className="lang-toggle">
      <button className={lang === 'en' ? 'on' : ''} onClick={() => setLang('en')}>EN</button>
      <button className={lang === 'zh' ? 'on' : ''} onClick={() => setLang('zh')}>繁</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DISCOVER
// ─────────────────────────────────────────────────────────────────────────────
// `hasRecords` was a prop here until 2026-08-10. It gated the two contribution
// banners, which hid them from first-session nurses — the people most likely to
// search for a course that isn't listed. Submitting needs no prior record, only
// an account, and that is enforced at the tap (openSubmit → onRequireAuth).
function DiscoverScreen({ lang, setLang, onOpenCourse, view, setView, initialFilters, coursesVersion, profile, submitSignal, onSubmitConsumed, savedIds, session, onOpenSaved, savedSignal, onSavedConsumed, onRequireAuth, onOpenContributions }) {
  const [query, setQuery] = React.useState('');
  // Quick filters model independent dimensions: price and delivery mode can
  // be combined (e.g. Free + Online) instead of masquerading as a radio group.
  const [quickFilters, setQuickFilters] = React.useState({ free: false, online: false });
  const [savedOnly, setSavedOnly] = React.useState(false);
  const [viewTab, setViewTab] = React.useState('actionable');
  // "Bookable only" requires positive registration evidence. Unknown and
  // confirmed-full rows are both excluded; waitlists remain actionable.
  const [onlyWithSeats, setOnlyWithSeats] = React.useState(false);

  // Advanced filter state — multi-facet, surfaced via <DiscoverFilterSheet>.
  // restrictedMode remains a pool control inside the filter sheet.
  const [advFilters, setAdvFilters] = React.useState(() =>
    (window.DiscoverFilters?.emptyFilters?.() || {})
  );
  const [filterSheetOpen, setFilterSheetOpen] = React.useState(false);
  // Crowdsource Phase 1A — submit-course sheet, opened from empty state.
  const [submitOpen, setSubmitOpen] = React.useState(false);
  // Submission is a write, so authenticate at every entry point before the
  // long form opens. The queued callback resumes straight into the form after
  // sign-in, matching Saved's established behaviour.
  const openSubmit = () => {
    if (session) { setSubmitOpen(true); return; }
    onRequireAuth?.(() => setSubmitOpen(true), 'submit');
  };
  // Open Submit from elsewhere (the AI earn menu → app.jsx bumps a nonce).
  // ONE-SHOT: this effect re-runs on every mount, and DiscoverScreen unmounts
  // when you leave the tab — so a persistent signal would re-open Submit every
  // time you return to Discover. We consume the signal (reset it to 0 in
  // app.jsx, which outlives this component) the moment we act on it, so a later
  // remount sees 0 and does nothing. Guard the initial 0 too.
  React.useEffect(() => {
    if (submitSignal) { setSubmitOpen(true); onSubmitConsumed?.(); }
  }, [submitSignal]);
  React.useEffect(() => {
    if (savedSignal) { setSavedOnly(true); onSavedConsumed?.(); }
  }, [savedSignal]);

  // restrictedMode — 'relevant' (default) shows restricted courses the user
  //    may qualify for (their hospital + any membership course); 'all' reveals
  //    ineligible ones; 'none' hides all restricted. See restrictedVisibleByDefault.
  const restrictedMode = advFilters.restrictedMode || 'relevant';

  // Inbound provider filter (when arriving from ProvidersScreen)
  React.useEffect(() => {
    // Provider deep link carries an ENTITY ID, not a provider_code — the facet
    // is keyed on identity (RELEASE.md §78) and a code no longer selects
    // anything. Nothing sets this today; the provider directory will (P1).
    if (initialFilters?.provider_entity_id) {
      setAdvFilters(f => ({ ...(f || {}), provider_entity_id: [initialFilters.provider_entity_id] }));
    }
  }, [initialFilters?.provider_entity_id]);

  // Inbound "show me the free CNE" filter (from HomeScreen's Free CNE card).
  //
  // Depends on the OBJECT IDENTITY, not on `initialFilters.free`. The flag is a
  // primitive that stays `true` once set, so a value dependency would fire once
  // and never again — but app.jsx hands us a freshly built object on every tap,
  // so the identity changes exactly when the card is tapped. That is also why
  // it does not fight the user: if she turns the Free chip off and comes back to
  // Discover on her own, the object is unchanged, the effect does not re-run,
  // and her filter stays off. Re-tapping the card is what re-applies it.
  React.useEffect(() => {
    if (!initialFilters?.free) return;
    setQuickFilters(q => ({ ...q, free: true }));
    // ‼️ MUST mirror freeCneSummary()'s predicate. That count is computed with
    // courseIsActionable (registration open/waitlist), so the destination has to
    // apply the same bookable filter — otherwise the card promises N points and
    // the list she lands on shows more rows than the number, or rows she cannot
    // book. `onlyWithSeats` is that filter: it requires positive registration
    // evidence and excludes unknown and full, exactly as courseIsActionable does.
    setOnlyWithSeats(true);
  }, [initialFilters]);

  const activeAdvCount = (window.DiscoverFilters?.countActive?.(advFilters)) || 0;
  // Local date, never UTC — see the guard comment on todayIso in data.js. This
  // value is threaded into deriveRegistrationStatus / courseBucket, so a UTC
  // "today" ran every Discover axis a day behind between 00:00 and 08:00 HKT
  // (expired rows kept reading as open/ongoing). sortForDiscover no longer
  // takes it: the list sorts on the start date alone, with no status axis.
  const today = window.todayIso ? window.todayIso() : new Date().toLocaleDateString('en-CA');

  const catalogueUpdated = React.useMemo(() => {
    const pool = (typeof allSearchableCourses === 'function') ? allSearchableCourses() : COURSES;
    return pool.reduce((latest, course) => {
      const fetched = course.raw?.fetched_at;
      return fetched && (!latest || fetched > latest) ? fetched : latest;
    }, null);
  }, [coursesVersion]);

  // The pre-advanced-filter pool is shared with DiscoverFilterSheet. Keeping
  // it as one memo makes the sheet's Apply count include search, quick chips,
  // saved scope, restriction scope and Bookable-only exactly like the list.
  // ‼️ ONE definition of the pre-advanced-filter pipeline, parameterised by the
  // restricted mode, because it is now run TWICE: once as the user has it, and
  // once as if she had chosen 'all'. The difference between the two is the
  // honest answer to "how many did we hide from you?" — computed as a real diff
  // of the same code rather than a second implementation that would drift from
  // this one the first time a filter is added.
  const buildFilterBase = React.useCallback((mode) => {
    const hasQuery = !!(query && query.trim());
    const pool = (typeof allSearchableCourses === 'function') ? allSearchableCourses() : COURSES;
    let r = pool.slice();
    if (savedOnly) r = r.filter(c => savedIds?.has?.(c.id));
    if (quickFilters.free)   r = r.filter(c => c.isFree);
    if (quickFilters.online) r = r.filter(c => c.online);
    if (onlyWithSeats) {
      // "只顯示可報名" keeps everything NOT known to be unavailable. 'unknown'
      // (provider hasn't published availability) is included: those courses are
      // potentially bookable and were the largest bucket, so hiding them defeated
      // the toggle. Only 'full'/'closed'/completed are excluded.
      r = r.filter(c => ['open', 'waitlist', 'unknown'].includes(window.deriveRegistrationStatus?.(c, today)));
    }
    // Restricted (members-only / internal) courses — tri-state:
    //   none     — hide all restricted
    //   relevant — hide only the ones she is KNOWN not to qualify for (default);
    //              an active search bypasses this (same precedent as the
    //              expired-course search expansion above) so a nurse can still
    //              find a known internal course by name rather than a blank.
    //   all      — show everything
    //
    // 'relevant' now hides strictly less than it used to: restrictedVisibleByDefault
    // only refuses a KNOWN mismatch, so a guest or a profile with no workplace
    // sees these rows (badged) instead of losing them silently. Whatever it does
    // still hide is counted below and stated on screen.
    if (mode === 'none') r = r.filter(c => !c.isRestricted);
    else if (mode === 'relevant' && !hasQuery) {
      r = r.filter(c => !c.isRestricted
        || (window.restrictedVisibleByDefault
            ? window.restrictedVisibleByDefault(c, profile) : true));
    }
    if (hasQuery) {
      // ‼️ NOT `lang`-aware, and that is the fix: courseMatchesQuery (data.js)
      // matches every language variant of title / provider / topic, so an
      // English keyword still finds a Chinese-titled course while she browses
      // in 繁中, and vice versa. This predicate is SHARED with the
      // log-from-catalogue search (the `catalog` memo below) — one definition,
      // because two copies drifted the moment one of them was localised.
      r = r.filter(c => courseMatchesQuery(c, query));
    }
    return r;
    // ‼️ restrictedMode is deliberately NOT a dependency — it arrives as the
    // `mode` argument so the same builder can be called for both modes.
    // ‼️ `lang` is gone from this list on purpose. It was only ever here for the
    // search predicate, which is now language-blind (courseMatchesQuery) — the
    // pool this builds is identical in EN and 繁中.
  }, [quickFilters, query, onlyWithSeats, profile, coursesVersion, savedOnly, savedIds, today]);

  const filterBase = React.useMemo(
    () => buildFilterBase(restrictedMode), [buildFilterBase, restrictedMode]);

  const filtered = React.useMemo(() => {
    let r = filterBase;
    if (activeAdvCount > 0 && window.DiscoverFilters?.applyFilters) {
      r = window.DiscoverFilters.applyFilters(r, advFilters);
    }
    const tabCounts = window.discoverStatusCounts?.(r, today) || {
      total: r.length, actionable: r.length, tbc: 0, past: 0,
    };
    let list = r.filter(c => (window.courseBucket?.(c, today) || 'actionable') === viewTab);
    if (viewTab === 'actionable') {
      list = window.sortForDiscover?.(list) || list;
    } else if (viewTab === 'tbc') {
      list = list.slice().sort((a, b) => String(b.raw?.last_updated || '').localeCompare(String(a.raw?.last_updated || '')));
    } else {
      list = list.slice().sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
    }
    const counts = window.discoverStatusCounts?.(list, today) || { total: list.length };
    return { list, tabCounts, counts };
  }, [filterBase, advFilters, activeAdvCount, viewTab, today]);

  // ── How many rows the eligibility gate is holding back, exactly ────────────
  // Hiding was silent: a nurse whose workplace does not match an internal
  // course simply never saw it, with no indication that anything had been
  // removed and no route to the switch that brings it back. The filter sheet is
  // two taps away and gives no reason to suspect it is worth opening. That is
  // the real defect in "hide" — not the hiding, but that it is unfalsifiable
  // from the outside.
  //
  // The number is the SAME pipeline run with mode 'all', diffed at the SAME
  // tab, so it can never disagree with what she is looking at. Cost is one
  // extra pass over ~264 rows.
  //
  // Zero in the two cases where a notice would be noise: 'none' and 'all' are
  // explicit choices she made in the sheet (which already shows an active-filter
  // count), and an active query bypasses the gate entirely.
  const hiddenByEligibility = React.useMemo(() => {
    if (restrictedMode !== 'relevant') return 0;
    if (query && query.trim()) return 0;
    let r = buildFilterBase('all');
    if (activeAdvCount > 0 && window.DiscoverFilters?.applyFilters) {
      r = window.DiscoverFilters.applyFilters(r, advFilters);
    }
    const inTab = r.filter(c => (window.courseBucket?.(c, today) || 'actionable') === viewTab);
    return Math.max(0, inTab.length - filtered.list.length);
  }, [buildFilterBase, restrictedMode, query, advFilters, activeAdvCount, viewTab, today, filtered.list.length]);

  // Dismissal is component state, so it lasts the session and resets on a fresh
  // mount. Deliberately not persisted: the count changes as she filters, and a
  // permanently silenced notice would recreate the silent hiding it exists to
  // undo.
  const [eligibilityNoticeDismissed, setEligibilityNoticeDismissed] = React.useState(false);

  // (The `closingSoon` memo that fed the removed 即將截止 carousel lived here.
  // See the note further down, where that section used to render, for why it
  // went and what to answer before bringing it back.)

  const visibleCourses = filtered.list;
  // 可報名 folds in registration-status 'unknown' — courses whose provider
  // hasn't published availability are potentially bookable, so we count them as
  // available rather than a separate 待確認 group (which confused new users, was
  // the largest bucket, and was hidden by "只顯示可報名"). Only 已滿 / 已截止 remain
  // as distinct "unavailable" segments.
  // ‼️ 未公佈 IS ITS OWN SEGMENT. This added `unknown` into the 可報名 figure —
  // "the provider published no availability" counted as "you can book this".
  // Same error the fee contract refuses (`fee null ≠ free`), on the axis a
  // nurse acts on by budgeting and travelling.
  //
  // The line was BUILT for four segments: the note below it still reads
  // 「可報名 · 已滿 · 已截止 · 待確認」 from when this split existed and was folded
  // away. What justified the fold was that unknown was then the largest bucket;
  // on 2026-08-16 it is 29 against 65 open, so the premise expired and only the
  // wording stayed. The pool is unchanged — nothing is hidden by naming it.
  const headerSegments = viewTab === 'actionable' ? [
    { count: (filtered.counts.open || 0) + (filtered.counts.waitlist || 0), label: L(STR.open, lang) },
    { count: filtered.counts.unknown || 0, label: L(STR.notPublished, lang) },
    { count: filtered.counts.full || 0, label: L(STR.full, lang) },
    { count: filtered.counts.closed || 0, label: L(STR.closed, lang) },
  ].filter(item => item.count > 0) : [];

  const hasQuickFilters = quickFilters.free || quickFilters.online;
  const onPickFilter = (k) => {
    if (k === 'all') { setQuickFilters({ free: false, online: false }); return; }
    setQuickFilters(f => ({ ...f, [k]: !f[k] }));
  };

  return (
    <div className="scroll fade-in statusbar-capped" style={{ paddingBottom: 24 }}>
      <ScreenHeader
        title={L(STR.discover, lang)}
        eyebrow={L(STR.discoverEyebrow, lang)}
        actions={<LangPill lang={lang} setLang={setLang} />}
      />

      <div className="profile-tabs" role="tablist" aria-label={lang === 'zh' ? '課程檢視' : 'Course view'}>
        <button role="tab" aria-selected={!savedOnly}
          className={'profile-tab' + (!savedOnly ? ' on' : '')}
          onClick={() => setSavedOnly(false)}>
          {lang === 'zh' ? '全部課程' : 'All courses'}
        </button>
        <button role="tab" aria-selected={savedOnly}
          className={'profile-tab' + (savedOnly ? ' on' : '')}
          onClick={() => {
            if (!session) { onOpenSaved?.(); return; }
            setSavedOnly(true);
          }}>
          {lang === 'zh' ? '已收藏' : 'Saved'}
          {session && savedIds?.size ? <span className="record-viewbar-badge" style={{ marginLeft: 6 }}>{savedIds.size}</span> : null}
        </button>
      </div>

      <div className="discover-status-tabs" role="tablist" aria-label={lang === 'zh' ? '課程日期狀態' : 'Course date status'}>
        {[
          ['actionable', STR.tabCurrent],
          ['tbc', STR.dateTbc],
          ['past', STR.tabEnded],
        ].map(([key, label]) => (
          <button key={key} role="tab" aria-selected={viewTab === key}
            className={'discover-status-tab' + (viewTab === key ? ' on' : '')}
            onClick={() => setViewTab(key)}>
            <span>{L(label, lang)}</span>
            <span className="record-viewbar-badge">{filtered.tabCounts[key] || 0}</span>
          </button>
        ))}
      </div>

      {/* Sticky filter / search bar — stays pinned to the top of the
          scroll container so the controls follow the user down. */}
      <div className="discover-sticky">
      <div style={{ padding: '8px 20px 0' }}>
        <div className="search-bar">
          <Icon name="search" size={17} color="var(--fg-3)" stroke={1.6} />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={L(STR.searchCourses, lang)}
          />
        </div>
        {/* Browse-by-provider removed — narrowing by institution now lives in the
            Filter sheet's 主辦機構 facet, so the header stays compact (one filter
            surface instead of two). */}
      </div>

      <div className="scroll-x" style={{ marginTop: 12 }}>
        {[
          ['all', STR.all], ['free', STR.free], ['online', STR.online],
        ].map(([k, s]) => (
          <button key={k}
            aria-pressed={k === 'all' ? !hasQuickFilters : !!quickFilters[k]}
            className={'filter-chip' + ((k === 'all' ? !hasQuickFilters : quickFilters[k]) ? ' on' : '')}
            onClick={() => onPickFilter(k)}>
            {L(s, lang)}
          </button>
        ))}
      </div>

      {/* Result-count breakdown + catalogue freshness — its own full-width line
          so the 可報名 · 已滿 · 已截止 · 待確認 segments never wrap into the
          filter controls below. Kept on a single line (scrolls, never hides) so
          the 待確認 count stays visible even on narrow phones. */}
      <div className="discover-meta-line muted" style={{ padding: '2px 20px 0', fontSize: 12 }}>
        {viewTab === 'actionable' && headerSegments.length
          ? headerSegments.map((item, index) => (
              <React.Fragment key={item.label}>
                {index > 0 && ' · '}{item.count} {item.label}
              </React.Fragment>
            ))
          : <>{filtered.counts.total || 0} {lang === 'zh' ? '個課程' : 'courses'}</>}
        {catalogueUpdated && (
          <span className="muted-2" style={{ fontSize: 10.5, marginLeft: 8 }}>
            · {L(STR.catalogueUpdated, lang)} {fmtShort(String(catalogueUpdated).slice(0, 10), lang, true)}
          </span>
        )}
      </div>

      {/* Filter controls — advanced-filter trigger + bookable toggle on one
          compact row directly under the count summary. */}
      <div style={{ padding: '4px 20px 2px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        {/* Advanced filters trigger — surfaces provider / specialty / language / mode / etc. */}
        <button
          type="button"
          className={'filters-trigger' + (activeAdvCount > 0 ? ' has-active' : '')}
          onClick={() => setFilterSheetOpen(true)}
          aria-label={L(STR.filters, lang)}>
          <Icon name="filter" size={14} stroke={1.7} />
          <span>{L(STR.filters, lang)}</span>
          {activeAdvCount > 0 && <span className="filters-trigger-count">{activeAdvCount}</span>}
        </button>

        {/* Inline toggle — positive registration evidence only. */}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <span className="muted" style={{ fontSize: 12 }}>
            {L(STR.openOnly, lang)}
          </span>
          <Toggle on={onlyWithSeats} onChange={() => setOnlyWithSeats(v => !v)}
            ariaLabel={L(STR.openOnly, lang)} />
        </label>

        {/* (Removed list/grid view switcher — list view is the standard
            presentation; the grid alternative was distracting and rarely
            used.) */}
      </div>

      {/* Active filter chips bar — removable chips for every selected facet,
          so a glance tells the user what's narrowing their results. */}
      {activeAdvCount > 0 && (
        <div className="active-filters-bar">
          {(window.DiscoverFilters?.FACETS || []).flatMap(facet => {
            const sel = advFilters[facet.key] || [];
            return sel.map(value => (
              <span key={facet.key + ':' + value} className="active-filter-chip">
                <span>{window.DiscoverFilters?.label?.(facet.key, value, lang) || value}</span>
                <button className="active-filter-chip-x"
                  aria-label={lang === 'zh' ? '移除' : 'Remove'}
                  onClick={() => setAdvFilters(f => ({ ...f, [facet.key]: (f[facet.key] || []).filter(v => v !== value) }))}>
                  <Icon name="close" size={11} stroke={2} />
                </button>
              </span>
            ));
          })}
          {advFilters.cost && (
            <span className="active-filter-chip">
              <span>{advFilters.cost === 'free' ? L(STR.free, lang) : (lang === 'zh' ? '付費' : 'Paid')}</span>
              <button className="active-filter-chip-x" onClick={() => setAdvFilters(f => ({ ...f, cost: null }))}>
                <Icon name="close" size={11} stroke={2} />
              </button>
            </span>
          )}
          {advFilters.priceMax != null && (
            <span className="active-filter-chip">
              <span>{`≤ $${Number(advFilters.priceMax).toLocaleString()}`}</span>
              <button className="active-filter-chip-x"
                aria-label={lang === 'zh' ? '移除' : 'Remove'}
                onClick={() => setAdvFilters(f => ({ ...f, priceMax: null }))}>
                <Icon name="close" size={11} stroke={2} />
              </button>
            </span>
          )}
          {advFilters.deadlineDays != null && (
            <span className="active-filter-chip">
              <span>{lang === 'zh' ? `${advFilters.deadlineDays} 日內截止` : `Deadline ≤ ${advFilters.deadlineDays}d`}</span>
              <button className="active-filter-chip-x" onClick={() => setAdvFilters(f => ({ ...f, deadlineDays: null }))}>
                <Icon name="close" size={11} stroke={2} />
              </button>
            </span>
          )}
          {advFilters.points && (
            <span className="active-filter-chip">
              <span>{advFilters.points[0]}–{advFilters.points[1]} {L(STR.pts, lang)}</span>
              <button className="active-filter-chip-x" onClick={() => setAdvFilters(f => ({ ...f, points: null }))}>
                <Icon name="close" size={11} stroke={2} />
              </button>
            </span>
          )}
          {restrictedMode !== 'relevant' && (
            <span className="active-filter-chip">
              <span>{restrictedMode === 'all'
                ? (lang === 'zh' ? '全部受限' : 'All restricted')
                : (lang === 'zh' ? '隱藏受限' : 'Restricted hidden')}</span>
              <button className="active-filter-chip-x" onClick={() => setAdvFilters(f => ({ ...f, restrictedMode: 'relevant' }))}>
                <Icon name="close" size={11} stroke={2} />
              </button>
            </span>
          )}
          <button className="active-filters-clear"
            onClick={() => setAdvFilters(window.DiscoverFilters?.emptyFilters?.() || {})}>
            {L(STR.filterClearAll, lang)}
          </button>
        </div>
      )}
      </div>{/* /.discover-sticky */}

      {/* Prominent, persistent contribution CTA — sits directly under the
          sticky filter bar so it's visible on load, then scrolls away. It is
          also the incentive surface: submitting a course earns an AI-scan
          credit on approval (scan_credit_config → +1 per approved submission,
          cap 10/mo). NB copy promises submission only — field corrections
          (apply_report) grant no credit. Replaces the old dismissable welcome
          card, whose invite had no action button. Shown only with a non-empty
          list; the empty state owns its own submit CTA.

          ‼️ NO LONGER GATED ON `hasRecords`. It was, and that hid the banner
          from exactly the people most likely to hit a missing course: nurses in
          their first session, who have logged nothing yet. Submitting is not a
          record-keeping action and needs no prior record — it is auth-gated at
          the tap instead (openSubmit → onRequireAuth), which is the correct
          boundary and the one that also protects the write. */}
      {visibleCourses.length > 0 && (
        <M.button type="button"
          onClick={openSubmit}
          whileTap={{ scale: 0.99 }} whileHover={{ y: -1 }}
          style={{ display: 'flex', alignItems: 'center', gap: 12, width: 'calc(100% - 40px)',
                   margin: '10px 20px 2px', padding: '12px 14px', textAlign: 'left',
                   background: 'var(--primary-soft)', border: 'none', borderRadius: 14,
                   cursor: 'pointer', font: 'inherit', color: 'inherit' }}>
          <span style={{ width: 38, height: 38, flexShrink: 0, borderRadius: 19,
                         background: 'var(--primary)', color: '#fff', display: 'flex',
                         alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="plus" size={20} stroke={2.4} />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--primary)' }}>
              {lang === 'zh' ? '提交課程' : 'Submit a course'}
            </span>
            <span style={{ display: 'block', fontSize: 11.5, lineHeight: 1.45, marginTop: 2, color: 'var(--fg-2)' }}>
              {lang === 'zh'
                ? '找不到想報的課程？提交獲批可賺取 AI 掃描額度。'
                : "Can't find a course? Submit it and earn AI scan credits on approval."}
            </span>
          </span>
          <Icon name="chev-r" size={16} stroke={2} color="var(--primary)" />
        </M.button>
      )}

      {/* NOTE: a 「即將截止 / Closing soon」 carousel used to sit here, between
          the submit banner and the results. It was REMOVED on 2026-08-10, and
          reintroducing it needs a better answer than "it was useful":
            • It duplicated a control that already exists. Its "view all" wrote
              `deadlineDays: 7`, which is exactly what the 截止日期 chips in the
              filter sheet set (discover-filters.jsx) — so the same intent had
              two entry points, the same reason the "Browse by specialty" strip
              below was removed.
            • It cost the contribution banner its position. The banner is the
              only surface that grows the catalogue, and the carousel pushed it
              out of the first screenful on a phone.
            • It is thin on real data: only ~8% of active courses publish a
              registration_deadline at all, so it was empty most days and
              lopsided the rest.
          Deadline urgency is not lost — the 截止日期 facet in the filter sheet,
          the active-filter chip it produces, and each course card's own
          deadline line all still carry it.

          NOTE: a "Browse by specialty" tile strip used to sit here. It was
          removed deliberately — specialty is still reachable (and stays the
          most-used facet) through the filter sheet, which is the single place
          filters live. Do not reintroduce a second, parallel filter entry
          point on the Discover home: two controls writing the same
          `specialty_area_en` state is how they drift out of sync. The CNE
          information centre's specialty answer points at the filter sheet to
          match (see cne-info.jsx). */}

      {/* ── "N hidden" — the eligibility gate, said out loud ──────────────────
          Placed ABOVE the empty-state branch on purpose: the case that matters
          most is a tab filtered down to nothing BY this gate, where the empty
          state would otherwise blame the query or the filters and send her off
          adjusting the wrong thing.

          One tap flips restrictedMode to 'all' — the same control the filter
          sheet owns, so this is a shortcut to an existing setting, never a
          fourth state. */}
      {hiddenByEligibility > 0 && !eligibilityNoticeDismissed && (
        <div role="status" style={{
          display: 'flex', alignItems: 'center', gap: 10,
          margin: '10px 20px 2px', padding: '9px 12px',
          border: '1px solid var(--border)', borderRadius: 12,
          background: 'var(--surface)', fontSize: 12.5, lineHeight: 1.35,
        }}>
          <Icon name="shield" size={14} stroke={1.7} color="var(--fg-3)" />
          <span style={{ flex: 1, minWidth: 0 }} className="muted-2">
            {lang === 'zh'
              ? `已隱藏 ${hiddenByEligibility} 個你未必符合資格的受限課程`
              : `${hiddenByEligibility} restricted course${hiddenByEligibility === 1 ? '' : 's'} you may not qualify for ${hiddenByEligibility === 1 ? 'is' : 'are'} hidden`}
          </span>
          <button type="button"
            className="btn btn-ghost"
            style={{ flex: 0, padding: '4px 10px', fontSize: 12.5 }}
            onClick={() => setAdvFilters(f => ({ ...(f || {}), restrictedMode: 'all' }))}>
            {lang === 'zh' ? '顯示' : 'Show'}
          </button>
          <button type="button"
            aria-label={lang === 'zh' ? '關閉提示' : 'Dismiss'}
            className="btn btn-ghost"
            style={{ flex: 0, padding: '4px 8px' }}
            onClick={() => setEligibilityNoticeDismissed(true)}>
            <Icon name="close" size={14} stroke={1.8} />
          </button>
        </div>
      )}

      {/* Empty state names the mutually-exclusive date tabs, so a past-only
          search remains discoverable without silently widening the list. */}
      {visibleCourses.length === 0 ? (
        <div className="vital-empty" style={{ marginTop: 12 }}>
          <div className="vital-empty-icon">
            <Icon name="search" size={22} stroke={1.7} />
          </div>
          <div className="vital-empty-title">
            {lang === 'zh' ? '找不到符合的課程' : 'No matching courses'}
          </div>
          <div className="vital-empty-sub">
            {query
              ? (lang === 'zh'
                  ? `這個分頁沒有「${query}」的結果；舊課程請查看「已結束」分頁。`
                  : `No “${query}” results in this tab. Check the Ended tab for older courses.`)
              : (lang === 'zh'
                  // ‼️ NAMES THE TOGGLE BY READING IT, NOT BY REPEATING IT. This
                  // said 「只顯示可報名」 — the label STR.openOnly carried until §68
                  // renamed it 「隱藏已滿／已截止」 — so the empty state pointed at a
                  // control that no longer existed by that name. Hardcoding a
                  // second copy of a label is how a rename ships half-applied.
                  ? `試試其他分頁、調整篩選，或關閉「${L(STR.openOnly, lang)}」。`
                  : `Try another tab, adjust the filters, or turn off “${L(STR.openOnly, lang)}”.`)}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            {query && (
              <M.button className="btn btn-ghost" onClick={() => setQuery('')}
                whileTap={{ scale: 0.97 }} whileHover={{ y: -1 }}>
                {lang === 'zh' ? '清除搜尋' : 'Clear search'}
              </M.button>
            )}
            {hasQuickFilters && (
              <M.button className="btn btn-ghost" onClick={() => onPickFilter('all')}
                whileTap={{ scale: 0.97 }} whileHover={{ y: -1 }}>
                {lang === 'zh' ? '重設篩選' : 'Reset filters'}
              </M.button>
            )}
          </div>
          {/* Crowdsource Phase 1A: convert search failure into a contribution
              entry point. Only shown when the user actually typed a query —
              empty-state without a query is an "adjust your filter" moment,
              not a "this course doesn't exist" moment. */}
          {query && filtered.tabCounts.total === 0 && (
            <M.button className="btn btn-primary" style={{ marginTop: 14 }}
              onClick={openSubmit}
              whileTap={{ scale: 0.97 }} whileHover={{ y: -1 }}>
              <Icon name="plus" size={14} stroke={2} />
              <span style={{ marginLeft: 6 }}>
                {lang === 'zh' ? `提交「${query}」到社群課程庫` : `Submit "${query}" to the catalogue`}
              </span>
            </M.button>
          )}
          {/* Reinforce the community benefit — why bother submitting. */}
          {query && filtered.tabCounts.total === 0 && (
            <div className="muted-2" style={{ fontSize: 11.5, marginTop: 8 }}>
              {lang === 'zh' ? '你加入之後，其他護士搜尋時也會找到。' : 'Once you add it, other nurses will find it when they search.'}
            </div>
          )}
        </div>
      ) : view === 'list' ? (
        <Stagger
          className=""
          delay={28}
          initialDelay={20}
          style={{ padding: '4px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visibleCourses.map(c => (
            <CourseCard key={c.id} course={c} lang={lang} profile={profile} onClick={() => onOpenCourse(c.id)} />
          ))}
        </Stagger>
      ) : (
        <Stagger
          className="discover-grid"
          delay={22}
          initialDelay={20}
          style={{ padding: '4px 20px' }}>
          {visibleCourses.map(c => (
            <GridCourseCard key={c.id} course={c} lang={lang} profile={profile} onClick={() => onOpenCourse(c.id)} />
          ))}
        </Stagger>
      )}

      {/* End-of-list contribution entry point — a secondary catch for long
          lists so a user who scrolled past every result without finding their
          course needn't scroll back up to the top banner. Same sheet via
          setSubmitOpen; shown only alongside a non-empty list. Same gate as the
          top banner (see the note there on why `hasRecords` was dropped). */}
      {visibleCourses.length > 0 && (
        <div style={{ padding: '20px 20px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <div className="muted" style={{ fontSize: 12.5, textAlign: 'center' }}>
            {lang === 'zh' ? '找不到想報的課程？' : "Can't find the course you want?"}
          </div>
          <M.button className="btn btn-ghost"
            onClick={openSubmit}
            whileTap={{ scale: 0.97 }} whileHover={{ y: -1 }}>
            <Icon name="plus" size={14} stroke={2} />
            <span style={{ marginLeft: 6 }}>{lang === 'zh' ? '提交課程' : 'Submit a course'}</span>
          </M.button>
        </div>
      )}

      {/* Advanced filter sheet — multi-facet picker (provider, specialty, language…)
          `archived={[]}` is intentional, NOT a bug: filterBase is built from
          allSearchableCourses(), which already unions COURSES with
          ARCHIVED_COURSES, so the sheet's facet counts already include archived
          rows and match the "Ended" tab. Passing the pool again would be a
          no-op (the sheet dedupes by id). */}
      {typeof DiscoverFilterSheet === 'function' && (
        <DiscoverFilterSheet
          open={filterSheetOpen}
          onClose={() => setFilterSheetOpen(false)}
          filters={advFilters}
          onApply={setAdvFilters}
          lang={lang}
          courses={filterBase}
          archived={[]}
          profile={profile}
          viewTab={viewTab}
        />
      )}

      {/* Crowdsource Phase 1A — opens from empty-state CTA. Prefills the
          title with whatever the user just searched for, turning a dead-end
          search into a contribution entry point. */}
      {submitOpen && (
        <SubmitCourseSheet
          lang={lang}
          prefillTitle={query}
          onClose={() => setSubmitOpen(false)}
          onOpenCourse={(id) => { setSubmitOpen(false); onOpenCourse(id); }}
          onOpenContributions={() => { setSubmitOpen(false); onOpenContributions?.(); }}
        />
      )}
    </div>
  );
}

function GridCourseCard({ course, lang, onClick, profile }) {
  const axes = window.deriveCourseAxes?.(course);
  const ended = typeof isPastCourse === 'function' ? isPastCourse(course) : false;
  // ‼️ The grid tile rendered NO restriction indicator until 2026-08-10: a
  // 只限公立醫院員工 course looked exactly like one anybody can book. That was
  // only survivable while the default view hid the ones a nurse could not
  // qualify for. It no longer does (restrictionVerdict 'unknown', data.js), so
  // the badge is now mandatory here — same component and same state helper as
  // CourseCard, so the list and the grid cannot drift.
  const { dimmed: restrictedDimmed } = (typeof restrictionCardState === 'function')
    ? restrictionCardState(course, profile) : { dimmed: false };
  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick && onClick(); }
  };
  return (
    <M.div
      role="button"
      tabIndex={0}
      aria-label={L(course.title, lang)}
      className={'course-card' + (ended ? ' is-ended' : '') + (restrictedDimmed ? ' is-restricted' : '')}
      style={restrictedDimmed
        ? { padding: 12, gap: 8, filter: 'saturate(0.55)' }
        : { padding: 12, gap: 8 }}
      onClick={onClick}
      onKeyDown={onKey}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.985 }}
      transition={{ duration: 0.18 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
          <TopicPill topic={course.topic} label={L(course.topicLabel, lang)} />
          {typeof RestrictionChips === 'function' && (
            <RestrictionChips course={course} lang={lang} profile={profile} />
          )}
          {ended && (
            <span className="chip" style={{ background: 'var(--bg-2)', color: 'var(--fg-3)' }}>
              {lang === 'zh' ? '已過期' : 'Ended'}
            </span>
          )}
        </div>
        <div className="serif" style={{ fontSize: 22, lineHeight: 1, letterSpacing: '-0.02em', flexShrink: 0 }}>{course.points}</div>
      </div>
      <div className="h-row" style={{ fontSize: 13.5, lineHeight: 1.25, minHeight: 50 }}>
        {L(course.title, lang)}
      </div>
      <div className="mono muted-2" style={{ fontSize: 9.5, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        {/* Expired courses show the year (see CourseCard) — disambiguates a
            course that ended last year from one three years ago. */}
        {axes?.eventStatus === 'date_tbc' && !course.date
          ? L(STR.dateTbc, lang)
          : fmtShort(course.date, lang, ended)} · {L(course.providerShort, lang)}
        {/* Fee — free shows 免費, a known positive price shows $X; unknown fee
            (feeUnknown) shows nothing, per the deriveFees contract (data.js). */}
        {!ended && course.isFree && <> · {lang === 'zh' ? '免費' : 'Free'}</>}
        {!ended && !course.isFree && !course.feeUnknown && course.costFrom > 0 && (
          <> · {!course.cost
            ? (lang === 'zh' ? `$${course.costFrom.toLocaleString('en')}起` : `from $${course.costFrom.toLocaleString('en')}`)
            : `$${course.costFrom.toLocaleString('en')}`}</>
        )}
      </div>
      {/* Same component as CourseCard's — the grid tile is denser, but a peer
          count that means one thing in the list and another in the grid would
          be worse than a slightly tighter row. Silent when there's no data. */}
      {typeof PeerSignalChips === 'function' && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <PeerSignalChips course={course} lang={lang} />
        </div>
      )}
    </M.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COURSE DETAIL
// ─────────────────────────────────────────────────────────────────────────────
// Share + reward. Fires the native share sheet (Web Share API; clipboard
// fallback on desktop) and, on a COMPLETED share, claims the caller's monthly
// share-referral credit (server enforces the 1/month cap + self-guard). Shared
// by the course "Share" action and the Profile "Share Vital" row.
async function shareWithReward({ title, text, url, lang }) {
  const toast = (msg) => {
    if (window.showToast) window.showToast(msg, { tone: 'success' });
    else if (typeof showVitalToast === 'function') showVitalToast(msg);
  };
  if (navigator.share) {
    try { await navigator.share({ title, text: text || title, url }); }
    catch { return; }  // user cancelled — no reward, no toast
  } else {
    try { await navigator.clipboard.writeText(url); toast(lang === 'zh' ? '已複製連結' : 'Link copied'); }
    catch { return; }
  }
  // Best-effort reward: signed-in only, server-capped + self-guarded. granted is
  // 0 when this month's credit was already claimed → then no thank-you toast.
  try {
    const res = window.DB?.grantShareCredit ? await window.DB.grantShareCredit() : null;
    if (res && res.granted > 0) {
      toast(lang === 'zh' ? '多謝分享!已獲 +1 次 AI 掃描額度 🎉' : 'Thanks for sharing! +1 AI scan credit 🎉');
    }
  } catch { /* ignore */ }
}

// ── Restricted-course notice: per-course dismissal ──────────────────────────
// The notice is docked with the CTA bar, so on a phone it eats the space right
// above the button the nurse came here to press. She can close it — and a ✕ that
// forgets is a decorative ✕, so the dismissal is remembered PER COURSE.
//
// ‼️ CLOSING HIDES CONTEXT, NEVER THE RULE. 報名要求 above still carries the
// provider's own wording and Discover still badges the card, so a dismissed
// notice cannot make a restriction invisible. If this notice ever becomes the
// only place a restriction is stated, the ✕ has to go with it.
//
// Bounded to the last 50 ids for the same reason db.js bounds the guest notice
// list: browsing should not grow a localStorage key without limit. Any storage
// failure (private mode) just means the notice returns next visit — the safe
// direction to fail in.
const RESTRICT_NOTE_DISMISS_KEY = 'vital:restrictNoteClosed';
function restrictNoteDismissedIds() {
  try {
    const raw = localStorage.getItem(RESTRICT_NOTE_DISMISS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter(x => typeof x === 'string') : []);
  } catch (_) { return new Set(); }
}
function rememberRestrictNoteDismissal(id) {
  try {
    const next = restrictNoteDismissedIds();
    next.add(id);
    localStorage.setItem(RESTRICT_NOTE_DISMISS_KEY, JSON.stringify([...next].slice(-50)));
  } catch (_) { /* private mode — the notice simply returns next visit */ }
}

// ‼️ FOUR OUTCOMES, NOT ONE. This function exists because onReact used to
// collapse every non-success into `setReactionSummary(prev); return;` — a silent
// revert. A nurse tapping 👍 on a course whose occurrence identity was never
// minted saw the count blip and come back, with no message, forever. That is the
// bug this whole release is named after: the server refused, and the client
// swallowed the refusal.
//
//   'ok'        the fresh summary          -> keep the optimistic bump
//   'deferred'  sign-in overlay was opened -> revert and say NOTHING; the
//                                            overlay IS the feedback, and a
//                                            toast behind it reads as an error
//   'blocked'   the server refused on domain grounds -> revert + final-sounding
//                                            copy. NEVER invite a retry here.
//   'failed'    network / unknown          -> revert + retryable copy
//
// ‼️ MATCH ON message, NOT code. PostgREST returns {code, message} for a plpgsql
// RAISE (verified live: {"code":"42501","message":"authentication required"}),
// but errcode P0002 carries BOTH `no_series_identity` and `unknown_course`, so
// the code alone cannot tell a permanent refusal from a missing row.
//
// ‼️ COPY MAY NOT CLAIM MORE THAN THE EVIDENCE. 「資料整理緊」 was rejected in
// review: it promises cataloguing is under way and will finish, and a course
// withheld by review may never resolve. Likewise `unknown_course` proves a
// lookup miss, not a delisting. Only the network case invites a retry.
function reactionOutcome(res, lang) {
  const zh = lang === 'zh';
  if (res && !res.deferred && !res.error) return { kind: 'ok', message: null };
  if (res && res.deferred) return { kind: 'deferred', message: null };

  const msg = String((res && res.error && res.error.message) || '');
  const has = (needle) => msg.includes(needle);

  if (has('no_series_identity') || has('no_offering_identity')) {
    return {
      kind: 'blocked',
      message: zh ? '呢個課程目前未能接受呢項評價。'
                  : "This reaction isn't available for this course.",
    };
  }
  if (has('attendance_required')) {
    return {
      kind: 'blocked',
      message: zh ? '要先喺紀錄標記為已完成先可以評價。'
                  : 'Mark it completed in your records first.',
    };
  }
  if (has('unknown_course')) {
    return {
      kind: 'blocked',
      message: zh ? '搵唔到呢個課程,可能已下架。'
                  : "This course can't be found. It may no longer be available.",
    };
  }
  return {
    kind: 'failed',
    message: zh ? '提交失敗,請稍後再試。' : "Couldn't submit — please try again.",
  };
}

function CourseDetailScreen({ courseId, lang, onBack, savedIds, toggleSaved, enrolledIds, toggleEnrolled, records, onLogCourse, offlineMode, toggleCourseReaction, profile }) {
  // ⚠️ HOOK-ORDERING CONSTRAINT — `c` can be missing: COURSES hydrates
  // async (cache → live fetch) and an invalid/stale courseId resolves to
  // undefined. The `if (!c) return null` guard therefore MUST sit BELOW all
  // hooks (see below) — placing it here changes the hook count between a
  // found and a not-found render and tears the tree down ("rendered more
  // hooks than during the previous render"). The c-derived values are
  // computed null-safe so the hooks above the guard never throw.
  //
  // Resolve from the full searchable set (active ∪ archived), NOT just
  // COURSES: when "Show past courses" is on, Discover renders archived
  // rows (separate ARCHIVED_COURSES pool) — looking those up in COURSES
  // alone returns undefined and the guard below paints a blank screen.
  // Read fresh each render (no memo) so async ARCHIVED_COURSES hydration
  // still resolves a course the user already tapped.
  const c = (typeof allSearchableCourses === 'function'
    ? allSearchableCourses()
    : COURSES).find(x => x.id === courseId);
  const saved = savedIds.has(c?.id);
  const enrolled = enrolledIds.has(c?.id);
  const tint = TOPIC_TINT[c?.topic];
  const axes = c ? window.deriveCourseAxes?.(c) : null;
  const enrollmentInfo = c && typeof enrolmentStatusInfo === 'function'
    ? enrolmentStatusInfo(c) : null;
  const ended = c ? isPastCourse(c) : false;
  // Has the user already logged a completion record for THIS course?
  // Records carry courseId, so an ended course that's already in the
  // audit log shows a "Logged" confirmation instead of the log action.
  const logged = !!(c && (records || []).some(r => r.courseId === c.id));

  // Three-dot menu state + scroll-aware chrome
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const [registerDialog, setRegisterDialog] = React.useState(null);   // null | 'options' | 'external'
  // True when the user just came back from the provider site for THIS
  // course and hasn't enrolled yet. Drives the return-state banner.
  const [returnCtaOpen, setReturnCtaOpen] = React.useState(false);
  // Restricted-course notice — dismissed ids (see restrictNoteDismissedIds).
  // Held as a SET rather than a boolean so it stays correct when this screen is
  // reused for a different courseId without remounting: the answer is derived
  // per course instead of needing an effect to reset a flag.
  const [restrictNoteHidden, setRestrictNoteHidden] = React.useState(restrictNoteDismissedIds);
  const closeRestrictNote = React.useCallback((id) => {
    if (!id) return;
    rememberRestrictNoteDismissal(id);
    setRestrictNoteHidden(prev => new Set(prev).add(id));
  }, []);
  // Crowdsource Phase 1A — report-issue sheet.
  // Phase 2 added per-field ✎ shortcuts: reportInitial carries the
  // preselected fieldPath/reportType when the sheet was opened from a
  // specific fact row. null = opened from the three-dot menu (defaults).
  const [reportOpen, setReportOpen] = React.useState(false);
  const [reportInitial, setReportInitial] = React.useState(null);
  const openReport = (initial) => {
    setReportInitial(initial || null);
    setReportOpen(true);
  };
  const scrollRef = React.useRef(null);
  const menuRef = React.useRef(null);

  // ── Room for the floating bottom chrome ───────────────────────────────────
  // .cta-dock (the CTA bar + whichever reminders are up) is absolutely
  // positioned at the bottom of .detail-shell, so it sits ON TOP of the
  // scrolling content and the scroller needs matching bottom padding.
  //
  // ‼️ MEASURED, NOT HARD-CODED — this is the whole dock, not just a banner.
  // Everything in it grows as its text wraps, and the wrap point moves with the
  // language (the English copy is longer), the viewport width, and the OS
  // text-size setting; the bar itself also grows with the safe-area inset and
  // when a button label breaks to two lines. Any constant is therefore right on
  // one device and wrong on the next — which is how the previous pair of
  // constants (88 here, `bottom: 76px` on the notes) came to be wrong, the
  // second of them leaving the CTA bar painted over the notice above it.
  // null = not measured yet (first paint); a number = measured, and 0 is a
  // MEANINGFUL measurement (docked in flow, or nothing in the dock at all) —
  // which is why this is not seeded to 0 and never coalesced with `||`.
  const [floatChromeH, setFloatChromeH] = React.useState(null);
  const floatChromeRoRef = React.useRef(null);
  const floatChromeRef = React.useCallback((node) => {
    if (floatChromeRoRef.current) {
      floatChromeRoRef.current.disconnect();
      floatChromeRoRef.current = null;
    }
    if (!node) { setFloatChromeH(null); return; }
    // ‼️ At ≥768 responsive.css un-anchors the dock (position: static), so it
    // takes real flow space at the bottom of the column. Padding the scroller
    // by its height there would reserve the gap TWICE and leave a dead band
    // under the content. Ask the layout which mode we are in rather than
    // duplicating the breakpoint here — one less constant to keep in sync.
    const measure = () => setFloatChromeH(
      window.getComputedStyle(node).position === 'static' ? 0 : node.offsetHeight);
    measure();
    // Same availability guard as tweaks-panel.jsx: without ResizeObserver the
    // one-shot measurement above still holds, it just stops tracking re-wraps.
    if (typeof ResizeObserver === 'undefined') return;
    // Crossing the breakpoint also changes the dock's width (the sidebar
    // appears), so the observer fires and re-reads `position` for free.
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    floatChromeRoRef.current = ro;
  }, []);
  React.useEffect(() => () => floatChromeRoRef.current?.disconnect(), []);

  // Reset transient state when navigating between courses
  React.useEffect(() => {
    setRegisterDialog(null);
    setMenuOpen(false);
    setReturnCtaOpen(false);
    // Scroll the detail layer back to the top when the user opens a
    // different course (e.g. via "related courses"). The overlay is a
    // separate scroll container from the underlying tab, so this only
    // affects detail-screen scroll — the tab's scrollTop is untouched.
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [courseId]);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => setScrolled(el.scrollTop > 8);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [courseId]);

  React.useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  // Clear the pending marker only after enrollment actually succeeded
  // for THIS course. The banner's "Mark enrolled" button deliberately
  // does NOT pre-clear, because toggleEnrolled bounces through
  // requireAuth — if the user cancels sign-in, the toggle is a no-op
  // and we want the next visibility flip to still find a valid marker
  // and re-offer the prompt. Scoping by course id prevents enrolling
  // in course A from wiping a still-pending marker for course B.
  React.useEffect(() => {
    if (!enrolled) return;
    if (readPendingEnrol(c?.id)) clearPendingEnrol();
  }, [c?.id, enrolled]);

  // Return-state CTA: surface "did you finish registering?" once per
  // mount of this course detail, gated by an in-TTL marker for THIS
  // course and not-already-enrolled.
  //
  // Platform contract:
  //   - Native iOS: Capacitor Browser fires browserFinished when the
  //     SFSafariViewController is dismissed; openExternalUrl turns that
  //     into a synthetic 'pending-enrol-return' event — a reliable
  //     "user just came back" signal.
  //   - Web: there is no reliable equivalent. Different browsers fire
  //     different subsets of visibilitychange / focus / pageshow when a
  //     backgrounded tab is restored, and a discarded tab may surface
  //     none of them. We therefore treat the marker as a best-effort
  //     "reminder still pending" flag and re-check it on every signal
  //     we can subscribe to, plus once on mount. The 10-minute TTL on
  //     the marker is what bounds staleness, not any individual event.
  React.useEffect(() => {
    if (enrolled) return;
    let shown = false;
    const tryShow = () => {
      if (shown) return;
      const marker = readPendingEnrol(c?.id);
      if (!marker) return;
      shown = true;
      setReturnCtaOpen(true);
    };
    // visibilitychange fires for both directions — only act on 'visible'.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') tryShow();
    };
    // Mount-time check covers (a) native dismiss that beat React mount,
    // (b) the user re-entering the route fresh from elsewhere in the
    // SPA, and (c) tab reload while the marker is still valid.
    tryShow();
    document.addEventListener('visibilitychange', onVisibility);
    // focus + pageshow cover the gaps where visibilitychange doesn't
    // fire — focus catches window-level refocus (switching back from
    // another app/window), pageshow catches bfcache restores (Safari
    // swipe-back, mobile tab-restore after memory pressure).
    window.addEventListener('focus', tryShow);
    window.addEventListener('pageshow', tryShow);
    // Native-only: dispatched from openExternalUrl on browserFinished.
    window.addEventListener('pending-enrol-return', tryShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', tryShow);
      window.removeEventListener('pageshow', tryShow);
      window.removeEventListener('pending-enrol-return', tryShow);
    };
  }, [c?.id, enrolled]);

  // ── Community layer — contributors + reactions (Phase 3) ──────────────────
  // Hooks live ABOVE the `if (!c) return null` guard and read c null-safely.
  // Both fetches degrade silently so a network / RLS hiccup never blocks the
  // detail page.
  //
  // Gate on ONLINE only. The read-only bits (attribution, reaction counts, the
  // contributions dialog) must NOT depend on the reaction WRITE callback — that
  // coupling used to hide the entire block (reactions AND dialog) whenever
  // toggleCourseReaction wasn't threaded. Only the reaction TAP needs it (onReact
  // guards via canReact). The offline catalogue passes offlineMode=true, so it
  // still renders nothing there.
  const communityEnabled = !offlineMode;
  const canReact = typeof toggleCourseReaction === 'function';
  const [contributors, setContributors] = React.useState([]);
  const [reactionSummary, setReactionSummary] = React.useState(null);
  const [reactionBusy, setReactionBusy] = React.useState(false);
  const [handleReport, setHandleReport] = React.useState(null);       // { handle } | null
  const [contribDialog, setContribDialog] = React.useState(false);    // "who contributed what fields" dialog
  // Local badge override so an escalation flips the SourceBadge live without
  // mutating the shared module-level COURSES pool (`c` is .find()'d fresh each
  // render). The DB is authoritative; a later catalogue refresh reconciles.
  const [verificationOverride, setVerificationOverride] = React.useState(null);
  // Whether the community backend is actually deployed. Starts false so the
  // section stays hidden until the reaction-summary RPC succeeds — this keeps
  // the whole block invisible when the DB isn't migrated yet (the RPCs 404),
  // instead of showing an empty/broken shell during the client-ahead-of-schema
  // rollout window.
  const [communityAvailable, setCommunityAvailable] = React.useState(false);

  React.useEffect(() => {
    setVerificationOverride(null);
    if (!communityEnabled || !c?.id) { setContributors([]); setReactionSummary(null); setCommunityAvailable(false); return; }
    let alive = true;
    (async () => {
      const [contribRes, sumRes] = await Promise.all([
        window.DB.getCourseContributors(c.id),
        window.DB.getCourseReactionSummary(c.id),
      ]);
      if (!alive) return;
      setCommunityAvailable(!!sumRes && !sumRes.error);
      setContributors((contribRes && contribRes.data) || []);
      setReactionSummary((sumRes && sumRes.data) || null);
    })();
    return () => { alive = false; };
  }, [communityEnabled, c?.id]);

  // Guard lives HERE, below every hook, so the hook count is identical for
  // found and not-found renders. Everything past this point can assume `c`.
  if (!c) return null;

  // Reaction handler — optimistic bump, then reconcile with the authoritative
  // server summary. Reverts on sign-in-needed / cancel / error. On escalation
  // it flips the badge (override) + toasts the nurse whose vote tipped it.
  const onReact = async (type) => {
    if (reactionBusy || !c?.id || !canReact) return;
    const prev = reactionSummary;
    // v2 servers return a generic `counts` map so a new reaction type needs no
    // client deploy to be counted. The legacy *_count keys are still mirrored
    // for the two original types so an older server keeps working.
    const legacyKey = type === 'helpful' ? 'helpful_count'
      : type === 'still_accurate' ? 'still_accurate_count' : null;
    setReactionBusy(true);
    setReactionSummary(s => {
      const base = s || { helpful_count: 0, still_accurate_count: 0, counts: {}, my_reactions: [], supporter_count: 0 };
      const mine = new Set(base.my_reactions || []);
      const had = mine.has(type);
      had ? mine.delete(type) : mine.add(type);
      const counts = { ...(base.counts || {}) };
      counts[type] = Math.max(0, (counts[type] || 0) + (had ? -1 : 1));
      const next = { ...base, counts, my_reactions: [...mine] };
      if (legacyKey) next[legacyKey] = Math.max(0, (base[legacyKey] || 0) + (had ? -1 : 1));
      return next;
    });
    const res = await toggleCourseReaction(c.id, type);
    setReactionBusy(false);
    const outcome = reactionOutcome(res, lang);
    if (outcome.kind !== 'ok') {
      setReactionSummary(prev);
      // 'deferred' carries no message on purpose — the sign-in overlay is
      // already on screen and a toast behind it reads as a failure.
      if (outcome.message) showVitalToast(outcome.message);
      return;
    }
    setReactionSummary(res);
    if (res.is_upgraded) {
      setVerificationOverride('community_supported');
      showVitalToast(lang === 'zh'
        ? '你的確認讓此課程升級為「社群多人支持」🎉'
        : 'Your confirmation upgraded this course to Community-supported 🎉');
    }
  };

  // Add to calendar. The outcome comes from the system sheet, so 'cancelled'
  // is reported honestly — never toast "added" for an event the user declined.
  const onAddToCalendar = async () => {
    setMenuOpen(false);
    if (!window.CalendarAPI) return;
    const { result } = await window.CalendarAPI.addCourse(c, lang);
    if (result === 'saved') {
      showVitalToast(lang === 'zh' ? '已加入日曆' : 'Added to your calendar');
    } else if (result === 'downloaded') {
      showVitalToast(lang === 'zh' ? '已下載日曆檔案（.ics）' : 'Calendar file (.ics) downloaded');
    } else if (result === 'denied') {
      showVitalToast(lang === 'zh'
        ? '未有日曆權限,可到「設定」開啟'
        : 'Calendar access is off — you can turn it on in Settings', 'warning');
    } else if (result === 'failed') {
      showVitalToast(lang === 'zh' ? '加入日曆失敗' : 'Could not add to calendar', 'danger');
    }
    // 'cancelled' → say nothing. The user chose to back out.
  };

  const providerUrl = c.raw?.url || null;

  // Share a rich "course info card": the vitalcne.com /c/<id> landing page (a
  // Universal Link that opens straight to this screen when the app is installed,
  // else shows the course + App Store button) carrying the sharer's ?ref= so a
  // colleague who signs up activates the referral bonus. The share TEXT is the
  // card itself — title · CNE points · date · provider — so it reads well in a
  // WhatsApp ward group even before the link is tapped.
  const onShare = async () => {
    setMenuOpen(false);
    const url = window.DB.buildShareUrl(`/c/${encodeURIComponent(c.id)}`);
    const dateStr = c.date ? fmtShort(c.date, lang, true) : '';
    const line2 = [];
    if (c.points != null && c.points !== '') line2.push(lang === 'zh' ? `⭐ ${c.points} CNE 分` : `⭐ ${c.points} CNE pts`);
    if (dateStr) line2.push(`📅 ${dateStr}`);
    const text = lang === 'zh'
      ? `【CNE 課程情報】${L(c.title, 'zh')}` + (line2.length ? `\n${line2.join(' · ')}` : '') + `\n主辦:${L(c.providerShort, 'zh')}\n用 Vital 記低你嘅 CNE ↓`
      : `CNE course: ${L(c.title, 'en')}` + (line2.length ? `\n${line2.join(' · ')}` : '') + `\nProvider: ${L(c.providerShort, 'en')}\nTrack your CNE with Vital ↓`;
    await shareWithReward({ title: L(c.title, lang), text, url, lang });
  };

  const onReport = () => {
    setMenuOpen(false);
    openReport(null);
  };

  // Column names an admin corrected via apply_report (admin-owned, protected
  // from the scraper routine). The EditFieldBtn fieldPaths are already column
  // names, so membership here lines up 1:1 with the fact rows.
  const lockedFields = c.lockedFields || [];

  // Per-field ✎ shortcut — small button rendered next to each whitelisted
  // fact row. Opens ReportCourseSheet preset to incorrect_field + the
  // given field_path. Hidden in offlineMode (mutating action), for
  // restricted courses (admin-only anyway), and when not signed in.
  //
  // When the field is in locked_fields we also render a small "admin-verified"
  // mark BEFORE the ✎ — this is read-only info, so it shows even in
  // offlineMode (the early-return below only suppresses the mutating ✎ button,
  // not the mark). It tells the user this value was confirmed by an admin
  // rather than scraped, so they don't re-report an already-fixed value.
  const EditFieldBtn = ({ fieldPath, ariaLabel }) => {
    const isVerified = lockedFields.includes(fieldPath);
    // The verified mark is now a BUTTON: a locked field was corrected/confirmed,
    // so tapping it opens the per-field contributions dialog ("who changed what").
    const verifiedMark = isVerified ? (
      <button
        type="button"
        aria-label={lang === 'zh' ? '查看貢獻紀錄' : 'View contributions'}
        title={lang === 'zh' ? '此數值由社群更正 — 查看貢獻者' : 'Community-corrected — see who contributed'}
        onClick={(e) => { e.stopPropagation(); setContribDialog(true); }}
        style={{
          marginLeft: 6, padding: 0, display: 'inline-flex', alignItems: 'center',
          background: 'transparent', border: 0, cursor: 'pointer',
          color: 'var(--primary)', verticalAlign: 'middle',
        }}
      >
        <Icon name="shield" size={12} stroke={2} />
      </button>
    ) : null;
    if (offlineMode) return verifiedMark;
    return (
      <>
      {verifiedMark}
      <button
        type="button"
        aria-label={ariaLabel || (lang === 'zh' ? '回報此欄位' : 'Suggest correction')}
        title={lang === 'zh' ? '回報此欄位' : 'Suggest correction'}
        onClick={(e) => {
          e.stopPropagation();
          openReport({ fieldPath, reportType: 'incorrect_field' });
        }}
        style={{
          marginLeft: 6, padding: 2,
          background: 'transparent', border: 0, cursor: 'pointer',
          color: 'var(--fg-3)', opacity: 0.55, transition: 'opacity 140ms',
          verticalAlign: 'middle', borderRadius: 4,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.55')}
      >
        <Icon name="edit" size={12} stroke={1.7} />
      </button>
      </>
    );
  };

  return (
    <div className="detail-shell fade-in">
      {/* Floating top chrome — back + three-dot, always pinned above the scroll */}
      <div className={'detail-topbar' + (scrolled ? ' is-scrolled' : '')}>
        <button className="icon-btn" onClick={onBack} aria-label={lang === 'zh' ? '返回' : 'Back'}>
          <Icon name="chev-l" size={18} />
        </button>
        <div className="detail-topbar-title" aria-hidden={!scrolled}>
          {L(c.title, lang)}
        </div>
        <div className="detail-menu-wrap" ref={menuRef}>
          <button className="icon-btn" onClick={() => setMenuOpen(v => !v)}
            aria-label={lang === 'zh' ? '更多' : 'More'} aria-expanded={menuOpen}>
            <Icon name="dot-vert" size={18} />
          </button>
          {menuOpen && (
            <div className="detail-menu" role="menu">
              <button className="detail-menu-item" role="menuitem" onClick={onShare}>
                <Icon name="upload" size={16} stroke={1.7} />
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span>{lang === 'zh' ? '分享課程' : 'Share course'}</span>
                  <small className="muted-2" style={{ fontSize: 10.5 }}>{lang === 'zh' ? '每月可獲 1 次 AI 掃描額度' : 'Earn 1 AI scan credit monthly'}</small>
                </span>
              </button>
              {/* Add to calendar — the shift-worker feature. Only offered when
                  the course has a date to add; a TBC course would produce an
                  event with nothing useful in it. */}
              {c.date && (
                <button className="detail-menu-item" role="menuitem" onClick={onAddToCalendar}>
                  <Icon name="calendar" size={16} stroke={1.7} />
                  <span>{lang === 'zh' ? '加入日曆' : 'Add to calendar'}</span>
                </button>
              )}
              {!offlineMode && (<>
                <button className="detail-menu-item" role="menuitem" onClick={() => { setMenuOpen(false); toggleSaved(c.id); }}>
                  <Icon name="bookmark" size={16} stroke={1.7} color={saved ? 'var(--primary)' : 'currentColor'} fill={saved ? 'var(--primary)' : 'none'} />
                  <span>{saved ? (lang === 'zh' ? '取消收藏' : 'Remove from saved') : (lang === 'zh' ? '加入收藏' : 'Add to saved')}</span>
                </button>
                <div className="detail-menu-divider" />
              </>)}
              <button className="detail-menu-item danger" role="menuitem" onClick={onReport}>
                <Icon name="shield" size={16} stroke={1.7} />
                <span>{lang === 'zh' ? '回報資料錯誤' : 'Report incorrect info'}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Hero — colored topic strip */}
      {/* Bottom padding = the measured dock height + a breathing gap, so the
          last rows of course data are never hidden behind the CTA bar or the
          very notice commenting on them. See floatChromeRef for why this is
          measured rather than a constant. The 88 fallback only covers the first
          paint before the observer reports (and a no-ResizeObserver browser
          mid-transition); it is never the steady-state value. */}
      <div className="scroll" ref={scrollRef}
           style={{ paddingBottom: (floatChromeH == null ? 88 : floatChromeH) + 12 }}>
        <div style={{
          background: tint.bg,
          padding: '64px 20px 24px',
          position: 'relative',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                <div className="topic-pill" style={{ background: 'rgba(255,255,255,0.6)', color: tint.fg }}>
                  {L(c.topicLabel, lang)}
                </div>
                {/* Source provenance — visible only for non-default rows
                    (community-submitted, pending, flagged…). Silent for
                    the 118 official NCHK courses so the header stays clean. */}
                <SourceBadge source={c.source} verification={verificationOverride || c.verificationStatus} lang={lang} />
              </div>
              <h1 style={{
                margin: 0,
                fontFamily: 'var(--font-serif)',
                fontSize: 28, lineHeight: 1.12, letterSpacing: '-0.022em',
                fontWeight: 500, color: tint.fg,
              }}>
                {L(c.title, lang)}
              </h1>
              <div style={{ marginTop: 12, color: tint.fg, opacity: 0.75, fontSize: 13.5 }}>
                {L(c.provider, lang)}
              </div>
              {c.courseCode && (
                <div className="mono" style={{ marginTop: 6, color: tint.fg, opacity: 0.55, fontSize: 10.5, letterSpacing: '0.06em' }}>
                  {c.courseCode}
                </div>
              )}
            </div>
            <div style={{
              flexShrink: 0,
              background: 'rgba(255,255,255,0.85)',
              borderRadius: 14,
              padding: '14px 16px',
              textAlign: 'center',
              minWidth: 76,
            }}>
              <div className="serif" style={{ fontSize: 36, lineHeight: 0.9, letterSpacing: '-0.02em', color: tint.fg }}>
                {c.points}
              </div>
              <div className="mono" style={{ fontSize: 9, letterSpacing: '0.10em', textTransform: 'uppercase', color: tint.fg, opacity: 0.7, marginTop: 4 }}>
                CNE {L(STR.pts, lang)}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 16, flexWrap: 'wrap' }}>
            {(() => {
              const info = typeof enrolmentStatusInfo === 'function' ? enrolmentStatusInfo(c) : null;
              // Skip 'unknown' — an unconfirmed-availability course reads as
              // available (no scary 待確認 pill); closed/full still surface.
              if (!info || info.kind === 'unknown') return null;
              return (
                <span className={'cap-pill tone-' + info.tone} style={{ background: 'rgba(255,255,255,0.78)' }}>
                  {L(info.label, lang)}
                </span>
              );
            })()}
            {c.eventType && (
              <span className="chip" style={{ background: 'rgba(255,255,255,0.6)' }}>
                {L(c.eventType, lang)}
              </span>
            )}
            {c.online && <span className="chip" style={{ background: 'rgba(255,255,255,0.6)' }}><Icon name="globe" size={11} stroke={1.7} /> {L(STR.online, lang)}</span>}
            {c.isFree && <span className="chip" style={{ background: 'rgba(255,255,255,0.6)' }}>{L(STR.free, lang)}</span>}
            <span className="chip" style={{ background: 'rgba(255,255,255,0.6)' }}>{L(c.formatLabel, lang)}</span>
          </div>
        </div>

        {/* Facts + About — 2-col at ≥1024 */}
        <div className="detail-cols" style={{ paddingTop: 18 }}>
          <div className="detail-body">
            <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 22 }}>

              {/* About */}
              <div>
                <h3 className="h-section" style={{ margin: '0 0 10px', fontSize: 18 }}>{L(STR.about, lang)}</h3>
                <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.5, color: 'var(--fg-2)', textWrap: 'pretty' }}>
                  {L(c.blurb, lang)}
                </p>
              </div>

              {/* Audience */}
              {(c.raw?.target_en || c.raw?.target_zh) && (
                <div>
                  <h3 className="h-section" style={{ margin: '0 0 8px', fontSize: 16 }}>
                    {lang === 'zh' ? '對象' : 'Who this is for'}
                  </h3>
                  <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: 'var(--fg-2)' }}>
                    {lang === 'zh' ? (c.raw.target_zh || c.raw.target_en) : (c.raw.target_en || c.raw.target_zh)}
                  </p>
                </div>
              )}

              {/* Entry requirement */}
              {c.raw?.entry_requirement_en && (
                <div>
                  <h3 className="h-section" style={{ margin: '0 0 8px', fontSize: 16 }}>
                    {lang === 'zh' ? '報名要求' : 'Entry requirements'}
                  </h3>
                  <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: 'var(--fg-2)' }}>
                    {c.raw.entry_requirement_en}
                  </p>
                </div>
              )}

              {/* Speakers / instructor section removed by request — info is
                  still on the side facts pane and via the provider page. */}

              {/* Conference topics */}
              {(c.raw?.topics_en?.length || c.raw?.topics_zh?.length) && (
                <div>
                  <h3 className="h-section" style={{ margin: '0 0 10px', fontSize: 16 }}>
                    {lang === 'zh' ? '研討主題' : 'Topics covered'}
                  </h3>
                  <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--fg-2)', fontSize: 13.5, lineHeight: 1.6 }}>
                    {(lang === 'zh' ? (c.raw.topics_zh || c.raw.topics_en) : (c.raw.topics_en || c.raw.topics_zh)).map((t, i) => (
                      <li key={i} style={{ marginBottom: 2 }}>{t}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Conference theme */}
              {(c.raw?.theme_en || c.raw?.theme_zh) && (
                <div className="card" style={{ background: 'var(--primary-soft-2)', borderColor: 'var(--primary-soft)' }}>
                  <div className="eyebrow" style={{ color: 'var(--primary)', marginBottom: 6 }}>
                    {lang === 'zh' ? '會議主題' : 'Theme'}
                  </div>
                  <div style={{ fontFamily: 'var(--font-serif)', fontSize: 16, fontWeight: 500, color: 'var(--fg)', lineHeight: 1.3 }}>
                    {lang === 'zh' ? (c.raw.theme_zh || c.raw.theme_en) : (c.raw.theme_en || c.raw.theme_zh)}
                  </div>
                </div>
              )}

              {/* Organizer (when different from provider) */}
              {c.raw?.organizer && c.raw.organizer !== (c.raw.provider_full_en || c.raw.provider_full_zh) && (
                <div>
                  <div className="eyebrow" style={{ marginBottom: 6 }}>
                    {lang === 'zh' ? '主辦' : 'Organiser'}
                  </div>
                  <div style={{ fontSize: 13.5, color: 'var(--fg)' }}>{c.raw.organizer}</div>
                </div>
              )}

              {/* Objectives */}
              {(c.raw?.objectives_en || c.raw?.objectives_zh) && (
                <div>
                  <h3 className="h-section" style={{ margin: '0 0 8px', fontSize: 16 }}>
                    {lang === 'zh' ? '課程目標' : 'Objectives'}
                  </h3>
                  <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: 'var(--fg-2)', textWrap: 'pretty', whiteSpace: 'pre-line' }}>
                    {lang === 'zh' ? (c.raw.objectives_zh || c.raw.objectives_en) : (c.raw.objectives_en || c.raw.objectives_zh)}
                  </p>
                </div>
              )}

              {/* Syllabus */}
              {(c.raw?.syllabus_en || c.raw?.syllabus_zh) && (
                <div>
                  <h3 className="h-section" style={{ margin: '0 0 8px', fontSize: 16 }}>
                    {lang === 'zh' ? '課程大綱' : 'Syllabus'}
                  </h3>
                  <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: 'var(--fg-2)', textWrap: 'pretty', whiteSpace: 'pre-line' }}>
                    {lang === 'zh' ? (c.raw.syllabus_zh || c.raw.syllabus_en) : (c.raw.syllabus_en || c.raw.syllabus_zh)}
                  </p>
                </div>
              )}

              {/* Assessment */}
              {(c.raw?.assessment_required || c.raw?.assessment_format_en || c.raw?.assessment_passing_criteria_en || c.raw?.attendance_requirement) && (
                <div>
                  <h3 className="h-section" style={{ margin: '0 0 10px', fontSize: 16 }}>
                    {lang === 'zh' ? '評核' : 'Assessment'}
                  </h3>
                  <div className="card flush" style={{ padding: 0 }}>
                    {c.raw.assessment_required != null && (
                      <div className="fact-row" style={{ borderBottom: '0.5px solid var(--border)' }}>
                        <div className="k">{lang === 'zh' ? '需評核' : 'Required'}</div>
                        <div className="v">{c.raw.assessment_required ? (lang === 'zh' ? '是' : 'Yes') : (lang === 'zh' ? '否' : 'No')}</div>
                      </div>
                    )}
                    {c.raw.assessment_format_en && (
                      <div className="fact-row" style={{ borderBottom: '0.5px solid var(--border)' }}>
                        <div className="k">{lang === 'zh' ? '形式' : 'Format'}</div>
                        <div className="v">{lang === 'zh' ? (c.raw.assessment_format_zh || c.raw.assessment_format_en) : c.raw.assessment_format_en}</div>
                      </div>
                    )}
                    {c.raw.assessment_passing_criteria_en && (
                      <div className="fact-row" style={{ borderBottom: '0.5px solid var(--border)' }}>
                        <div className="k">{lang === 'zh' ? '合格準則' : 'Passing'}</div>
                        <div className="v">{c.raw.assessment_passing_criteria_en}</div>
                      </div>
                    )}
                    {c.raw.attendance_requirement && (
                      <div className="fact-row">
                        <div className="k">{lang === 'zh' ? '出席要求' : 'Attendance'}</div>
                        <div className="v">{c.raw.attendance_requirement}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Certificate */}
              {(c.raw?.certificate_type_en || c.raw?.certificate_requirements_en) && (
                <div>
                  <h3 className="h-section" style={{ margin: '0 0 10px', fontSize: 16 }}>
                    {lang === 'zh' ? '證書' : 'Certificate'}
                  </h3>
                  <div className="card flush" style={{ padding: 0 }}>
                    {c.raw.certificate_type_en && (
                      <div className="fact-row" style={{ borderBottom: c.raw.certificate_requirements_en ? '0.5px solid var(--border)' : 'none' }}>
                        <div className="k">{lang === 'zh' ? '類型' : 'Type'}</div>
                        <div className="v">{lang === 'zh' ? (c.raw.certificate_type_zh || c.raw.certificate_type_en) : c.raw.certificate_type_en}</div>
                      </div>
                    )}
                    {c.raw.certificate_requirements_en && (
                      <div className="fact-row">
                        <div className="k">{lang === 'zh' ? '取得條件' : 'Requirements'}</div>
                        <div className="v">{c.raw.certificate_requirements_en}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {c.raw?.notes && (
                <div className="card" style={{ background: 'var(--accent-soft)', borderColor: 'var(--accent-soft)' }}>
                  <div className="eyebrow" style={{ color: 'var(--accent)', marginBottom: 6 }}>
                    {lang === 'zh' ? '備註' : 'Notes'}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--fg)', lineHeight: 1.5 }}>{c.raw.notes}</div>
                </div>
              )}

              {/* Provenance footer */}
              {(c.raw?.last_updated || c.raw?.first_seen) && (
                <div className="mono muted-2" style={{ fontSize: 10, letterSpacing: '0.10em', textTransform: 'uppercase', marginTop: 2 }}>
                  {c.raw.last_updated && <>{lang === 'zh' ? '最後更新' : 'Updated'} {c.raw.last_updated}</>}
                  {c.raw.first_seen && c.raw.first_seen !== c.raw.last_updated && (
                    <> · {lang === 'zh' ? '首次收錄' : 'First seen'} {c.raw.first_seen}</>
                  )}
                </div>
              )}

              {/* Stale-data notice. STALE_DATA_DAYS is a PRODUCT threshold,
                  not a cache detail: past it, the enrolment/fee facts on this
                  card can no longer be presumed to match the organiser (the
                  pipeline's source-health gate fails closed upstream, which
                  is invisible here), so the nurse is told to double-check
                  instead of being silently served old data. Live non-past
                  courses only — archived/past cards state their own state. */}
              {(() => {
                if ((c.lifecycleStatus || 'active') !== 'active' || isPastCourse(c)) return null;
                const STALE_DATA_DAYS = 30;
                const refreshed = String(c.raw?.fetched_at || c.raw?.last_updated || '').slice(0, 10);
                if (!refreshed) return null;
                const age = Math.floor((Date.now() - new Date(`${refreshed}T00:00:00`).getTime()) / 86400000);
                if (!Number.isFinite(age) || age <= STALE_DATA_DAYS) return null;
                return (
                  <div style={{ fontSize: 11.5, marginTop: 4, color: 'color-mix(in oklab, var(--warning) 55%, var(--fg-2))' }}>
                    {lang === 'zh'
                      ? `資料已 ${age} 日未更新，報名及費用請以主辦機構最新公佈為準`
                      : `Not refreshed for ${age} days — confirm enrolment and fees with the organiser`}
                  </div>
                );
              })()}

              {/* Change log (collapsible) — moved to the bottom; humanized timeline */}
              {(() => {
                const rawLog = Array.isArray(c.raw?.change_log) ? c.raw.change_log : [];
                // Show only entries with ≥1 field that changed TO a concrete value.
                // A field whose new value is null/empty is suppressed: the daily
                // aggregator historically recorded "field → None" diffs its merge
                // never actually applied (the old value was preserved), surfacing
                // phantom "已修改" rows here; a deliberate clear (e.g. a wrong
                // registration link removed) is likewise not useful to a course-
                // seeker. See docs/coexistence-contract.md §5 (Bug class J, 7/04).
                const visibleLog = rawLog
                  .map((entry) => ({
                    entry,
                    fields: (Array.isArray(entry.fields_changed) ? entry.fields_changed : [])
                      .filter((f) => entry.new_values?.[f] != null),
                  }))
                  .filter((x) => x.fields.length > 0);
                if (visibleLog.length === 0) return null;
                return (
                <details className="changelog">
                  <summary className="changelog-summary">
                    <span className="changelog-summary-label">
                      <Icon name="clock" size={13} stroke={1.6} />
                      {lang === 'zh' ? '更新記錄' : 'Change history'}
                    </span>
                    <span className="changelog-summary-meta">
                      <span className="changelog-count">{visibleLog.length}</span>
                      <Icon name="chev-d" size={14} stroke={1.7} />
                    </span>
                  </summary>
                  <ol className="changelog-list">
                    {visibleLog.slice().reverse().map(({ entry, fields }, i) => (
                        <li key={i} className="changelog-item">
                          <div className="changelog-bullet" aria-hidden="true" />
                          <div className="changelog-body">
                            <div className="changelog-date">{fmtDate(entry.date, lang)}</div>
                            <div className="changelog-changes">
                                {fields.map((f) => {
                                  const oldVal = entry.old_values?.[f];
                                  const newVal = entry.new_values?.[f];
                                  const hasDiff = oldVal != null;  // newVal is non-null here
                                  return (
                                    <div key={f} className="changelog-change">
                                      <div className="changelog-field">{humanField(f, lang)}</div>
                                      {hasDiff ? (
                                        <div className="changelog-diff">
                                          <div className="changelog-diff-row">
                                            <span className="changelog-diff-tag was">{lang === 'zh' ? '舊' : 'Was'}</span>
                                            <span className="changelog-diff-old">{String(oldVal)}</span>
                                          </div>
                                          <div className="changelog-diff-row">
                                            <span className="changelog-diff-tag now">{lang === 'zh' ? '新' : 'Now'}</span>
                                            <span className="changelog-diff-new">{String(newVal)}</span>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="changelog-diff-row">
                                          <span className="changelog-diff-tag now">{lang === 'zh' ? '更新' : 'Updated'}</span>
                                          <span className="changelog-diff-new">{String(newVal)}</span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        </li>
                    ))}
                  </ol>
                </details>
                );
              })()}

            </div>
          </div>

          <div className="detail-side">
            <div className="detail-facts">

              {/* Contributor attribution — kept at the TOP of the facts so it
                  is discoverable rather than buried below the CTA. It stays
                  here now that the community-data card has moved to the bottom:
                  "who built this" belongs with the data it describes, while
                  "fix this" belongs after you have read the data. */}
              {/* ‼️ THE PEER-FEEDBACK CHIPS USED TO BE HERE — 值得上 / 安排妥當 /
                  資料仍準確 / 資料過時？ — and they are gone ON PURPOSE. Do not
                  put them back on this screen.

                  They asked a nurse browsing a course she has not attended to
                  judge it. The live catalogue measured what that produced: one
                  single use of worth_it across all 362 courses (and zero of
                  well_organised), against 51 for the 👍 in the CTA bar. Same
                  feature, same vocabulary, 51 : 1 — decided by position.

                  The experience question now runs at the only moment it can
                  honestly be answered: after 標記為已完成, on the record itself
                  (ExperienceAskCard, record-extras.jsx). That placement is also
                  what makes the server-side attendance gate satisfiable — the
                  gate needs a records row, which does not exist while you are
                  merely browsing.

                  Nothing was lost from the other paths:
                    • helpful      — still the 👍 in the CTA bar below, and it
                                     remains the escalation signal (~96% of all
                                     reaction volume).
                    • 資料過時？   — was a preset shortcut into the SAME report
                                     sheet that 更新課程資料 opens. The report
                                     path is unchanged; only the duplicate
                                     entry point is gone.
                    • still_accurate — no longer has a UI entry point. It stays
                                     in reaction_positive_types() so existing
                                     rows keep counting; helpful carries the
                                     ladder. */}

              {communityEnabled && communityAvailable && contributors.length > 0 && (() => {
                const creator = contributors.find(x => x.role === 'creator');
                const correctors = contributors.filter(x => x.role === 'corrector');
                const primary = creator || correctors[0];
                const extra = correctors.length - (creator ? 0 : 1);
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 2px 10px' }}>
                    <button type="button" onClick={() => setContribDialog(true)}
                      aria-label={lang === 'zh' ? '查看貢獻紀錄' : 'View contributions'}
                      style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10,
                        background: 'transparent', border: 0, padding: 4, cursor: 'pointer',
                        textAlign: 'left', font: 'inherit', color: 'inherit' }}>
                      <UserAvatar size={30} displayText={primary.display_text} hue={primary.hue} emoji={primary.emoji} isPrivate={primary.is_private} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
                        {creator
                          ? (lang === 'zh' ? <>由 <strong>{creator.display_text}</strong> 提交</> : <>Submitted by <strong>{creator.display_text}</strong></>)
                          : (lang === 'zh' ? <><strong>{primary.display_text}</strong> 協助更正</> : <>Corrected by <strong>{primary.display_text}</strong></>)}
                        {extra > 0 && (lang === 'zh' ? ` · +${extra} 位護士協助更正` : ` · +${extra} more`)}
                      </span>
                      <Icon name="chev-r" size={14} stroke={1.6} color="var(--fg-3)" />
                    </button>
                    {!primary.is_private && (
                      <button title={lang === 'zh' ? '檢舉不當化名' : 'Report handle'}
                        aria-label={lang === 'zh' ? '檢舉不當化名' : 'Report handle'}
                        onClick={() => setHandleReport({ handle: primary.display_text })}
                        style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--fg-2)', padding: 4 }}>
                        <Icon name="tag" size={13} stroke={1.6} />
                      </button>
                    )}
                  </div>
                );
              })()}

              <div className="fact-row">
                <div className="k">{L(STR.whenWhere, lang)}</div>
                <div className="v">
                  {fmtDate(c.date, lang)}
                  {c.endDate && c.endDate !== c.date && (
                    <span style={{ color: 'var(--fg-2)', fontWeight: 400 }}>
                      {' '}— {fmtDate(c.endDate, lang)}
                    </span>
                  )}
                  <div style={{ fontSize: 12.5, color: 'var(--fg-2)', fontWeight: 400, marginTop: 2 }}>
                    {c.time}
                    {(c.dayOfWeek?.en || c.dayOfWeek?.zh) && <> · {L(c.dayOfWeek, lang)}</>}
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--fg-2)', fontWeight: 400, marginTop: 2 }}>
                    {L(c.venue, lang)}
                    {/* sessions_list is editable from here even when absent, so a
                        reporter can propose a full multi-session schedule. */}
                    <EditFieldBtn fieldPath="sessions_list" />
                  </div>
                </div>
              </div>

              {/* Canonical per-session list, read-only. Only when the catalog
                  carries an explicit sessions_list (not the synthesised
                  fallback). */}
              {Array.isArray(c.sessionsList) && c.sessionsList.length > 0 && typeof resolveSessions === 'function' && (
                <div className="fact-row">
                  <div className="k">{lang === 'zh' ? '每節時間' : 'Sessions'}</div>
                  <div className="v">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {resolveSessions(c, null).map((s) => (
                        <div key={s.idx} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                          <span className="mono muted-2" style={{ minWidth: 22, fontSize: 10, letterSpacing: '0.06em' }}>
                            #{s.idx + 1}
                          </span>
                          <span style={{ color: 'var(--fg-2)' }}>
                            {fmtDate(s.iso, lang)}{s.timeLabel ? ` · ${s.timeLabel}` : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {(c.sessions > 1 || c.raw?.duration_text_en) && (
                <div className="fact-row">
                  <div className="k">{lang === 'zh' ? '節數' : 'Sessions'}</div>
                  <div className="v">
                    {c.sessions > 0 ? `${c.sessions} ${lang === 'zh' ? '節' : 'session' + (c.sessions === 1 ? '' : 's')}` : '—'}
                    <EditFieldBtn fieldPath="sessions" />
                    {c.raw?.duration_text_en && (
                      <div style={{ fontSize: 12.5, color: 'var(--fg-2)', fontWeight: 400, marginTop: 2 }}>
                        {c.raw.duration_text_en}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ONE registration-deadline row. There used to be a second
                  "Register by" row guarded on
                  `registration_deadline !== application_deadline`, which could
                  never render: normaliseSupabaseCourse (data.js) sets
                  raw.application_deadline = raw.registration_deadline, so the
                  two are always equal for Supabase rows. Its EditFieldBtn was
                  therefore unreachable and has been folded in here — keep the
                  correction affordance on this row. */}
              {c.raw?.application_deadline && (
                <div className="fact-row">
                  <div className="k">{lang === 'zh' ? '申請截止' : 'Apply by'}</div>
                  <div className="v">
                    {fmtDate(c.raw.application_deadline, lang)}
                    <EditFieldBtn fieldPath="registration_deadline" />
                  </div>
                </div>
              )}
              {c.raw?.early_bird_registration_deadline && (
                <div className="fact-row">
                  <div className="k">{lang === 'zh' ? '早鳥截止' : 'Early bird by'}</div>
                  <div className="v" style={{ color: 'var(--accent)' }}>
                    {fmtDate(c.raw.early_bird_registration_deadline, lang)}
                  </div>
                </div>
              )}
              {c.raw?.abstract_submission_deadline && (
                <div className="fact-row">
                  <div className="k">{lang === 'zh' ? '摘要截止' : 'Abstract by'}</div>
                  <div className="v">{fmtDate(c.raw.abstract_submission_deadline, lang)}</div>
                </div>
              )}

              <div className="fact-row">
                <div className="k">{L(STR.format, lang)}</div>
                <div className="v">
                  {L(c.formatLabel, lang)}
                  <EditFieldBtn fieldPath="event_type_en" />
                  {c.teachingMode && (
                    <div style={{ fontSize: 12.5, color: 'var(--fg-2)', fontWeight: 400, marginTop: 2 }}>
                      {L(c.teachingMode, lang)}
                      <EditFieldBtn fieldPath="teaching_mode_en" />
                    </div>
                  )}
                </div>
              </div>

              <div className="fact-row">
                <div className="k">{L(STR.language, lang)}</div>
                <div className="v">
                  {/* Language contract (data.js normalisers): evidence-only —
                      unknown → 待確認, never a guessed EN/廣東話 default */}
                  {c.language ? L(c.language, lang) : L(STR.languageTbc, lang)}
                  <EditFieldBtn fieldPath="language_en" />
                </div>
              </div>

              <div className="fact-row">
                <div className="k">{L(STR.cost, lang)}</div>
                <div className="v">
                  {/* Fee contract (deriveFees, data.js): unknown → 待確認, never 免費 */}
                  {c.feeUnknown ? L(STR.feeTbc, lang)
                    : c.isFree ? L(STR.free, lang)
                    : `HKD $${c.costFrom.toLocaleString('en')}`}
                  <EditFieldBtn fieldPath="fee_hkd" />
                  {c.feeUnknown && (
                    <div style={{ fontSize: 12.5, color: 'var(--fg-2)', fontWeight: 400, marginTop: 2 }}>
                      {L(STR.feeTbcHint, lang)}
                    </div>
                  )}
                  {/* Member / non-member / early-bird breakdown */}
                  {(c.costMember !== null || c.costNonMember !== null || c.costEarlyBird !== null || c.raw?.fee_per_session_hkd) && (
                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {c.costMember !== null && c.costNonMember !== null && c.costMember !== c.costNonMember && (
                        <>
                          <div className="fee-line">
                            <span>{lang === 'zh' ? '會員' : 'Member'}</span>
                            <span className="mono">${c.costMember.toLocaleString('en')}</span>
                          </div>
                          <div className="fee-line">
                            <span>{lang === 'zh' ? '非會員' : 'Non-member'}</span>
                            <span className="mono">${c.costNonMember.toLocaleString('en')}</span>
                          </div>
                          {/* The number the nurse is actually deciding on. null
                              whenever the two tiers are equal (deriveFees). */}
                          {c.memberSaving != null && (
                            <div className="fee-line" style={{ color: 'var(--success, var(--accent))', fontWeight: 600 }}>
                              <span>{lang === 'zh' ? '會員可慳' : 'Member saves'}</span>
                              <span className="mono">${c.memberSaving.toLocaleString('en')}</span>
                            </div>
                          )}
                        </>
                      )}
                      {c.costEarlyBird !== null && (
                        <div className="fee-line" style={{ color: 'var(--accent)' }}>
                          <span>{lang === 'zh' ? '早鳥' : 'Early bird'}</span>
                          <span className="mono">${c.costEarlyBird.toLocaleString('en')}</span>
                        </div>
                      )}
                      {c.raw?.fee_per_session_hkd && (
                        <div className="fee-line">
                          <span>{lang === 'zh' ? '每節' : 'Per session'}</span>
                          <span className="mono">${Number(c.raw.fee_per_session_hkd).toLocaleString('en')}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Fee notes — the ONLY place subsidy / refund / deposit
                      conditions can surface. Published on ~9% of active
                      courses, and it is exactly the fine print that changes a
                      decision, so it is worth the one conditional. */}
                  {(c.raw?.fee_notes_zh || c.raw?.fee_notes_en) && (
                    <div style={{ fontSize: 12.5, color: 'var(--fg-2)', fontWeight: 400, marginTop: 6, lineHeight: 1.45 }}>
                      {(lang === 'zh' ? c.raw.fee_notes_zh : c.raw.fee_notes_en)
                        || c.raw.fee_notes_en || c.raw.fee_notes_zh}
                    </div>
                  )}
                </div>
              </div>

              <div className="fact-row">
                <div className="k">{L(STR.points, lang)}</div>
                <div className="v">
                  {c.points ?? '—'} {L(STR.pts, lang)}
                  <EditFieldBtn fieldPath="cne_points" />
                  {c.raw?.cne_per_session && (
                    <div style={{ fontSize: 12.5, color: 'var(--fg-2)', fontWeight: 400, marginTop: 2 }}>
                      {c.raw.cne_per_session} {lang === 'zh' ? '分／節' : 'pts/session'}
                    </div>
                  )}
                  {axes?.cneStatus === 'confirmed' ? (
                    <div style={{ fontSize: 11.5, color: 'var(--success)', fontWeight: 600, marginTop: 3 }}
                      title={L(STR.cneConfirmed, lang)}>
                      ✓ {L(STR.cneConfirmed, lang)}
                    </div>
                  ) : c.points != null ? (
                    <div style={{ fontSize: 11.5, color: 'var(--fg-3)', fontWeight: 400, marginTop: 3, fontStyle: 'italic' }}>
                      {L(STR.cneUnverifiedNote, lang)}
                    </div>
                  ) : null}
                  {c.raw?.cne_points_note && (
                    <div style={{ fontSize: 11.5, color: 'var(--accent)', fontWeight: 400, marginTop: 2, fontStyle: 'italic' }}>
                      {c.raw.cne_points_note}
                    </div>
                  )}
                  {c.raw?.cem_points > 0 && (
                    <div style={{ fontSize: 12.5, color: 'var(--fg-2)', fontWeight: 400, marginTop: 2 }}>
                      +{c.raw.cem_points} CEM
                    </div>
                  )}
                </div>
              </div>

              {/* Enrolment status — derived from enrolmentStatusInfo so the
                  badge always matches the CTA state. */}
              {(() => {
                const info = typeof enrolmentStatusInfo === 'function' ? enrolmentStatusInfo(c) : null;
                // Hide the row for 'unknown' — we don't have a published status,
                // and surfacing 待確認 here just confuses. The CTA's 報名 button
                // (and the RegisterDialog → provider) is where the nurse confirms.
                if (!info || info.kind === 'unknown') return null;
                return (
                  <div className="fact-row">
                    <div className="k">{L(STR.filterEnrollment, lang)}</div>
                    <div className="v">
                      <span className={'cap-pill tone-' + info.tone}>{L(info.label, lang)}</span>
                      <EditFieldBtn fieldPath="enrollment_status_en" />
                    </div>
                  </div>
                );
              })()}

              {/* Capacity — show only whether the course is full or still
                  open. Exact remaining/total counts are intentionally hidden:
                  providers update them inconsistently and showing precise
                  numbers implies a confidence the source data doesn't have. */}
              {c.seats && c.seats.kind === 'full' && (
                <div className="fact-row">
                  <div className="k">{lang === 'zh' ? '名額' : 'Capacity'}</div>
                  <div className="v">
                    <span style={{ color: 'var(--danger)', fontWeight: 500 }}>
                      {L(STR.full, lang)}
                    </span>
                    <EditFieldBtn fieldPath="remaining_capacity" />
                  </div>
                </div>
              )}

              {/* Registration deadline countdown — only when meaningful */}
              {c.registrationDeadline && (() => {
                const dLeft = typeof daysUntil === 'function' ? daysUntil(c.registrationDeadline) : null;
                return (
                  <div className="fact-row">
                    <div className="k">{L(STR.registerBy, lang)}</div>
                    <div className="v">
                      {fmtDate(c.registrationDeadline, lang)}
                      {dLeft != null && dLeft >= 0 && dLeft <= 30 && (
                        <span className={'deadline-pill' + (dLeft <= 3 ? ' urgent' : '')} style={{ marginLeft: 8 }}>
                          <Icon name="clock" size={11} stroke={1.7} />
                          {dLeft === 0
                            ? L(STR.closesToday, lang)
                            : `${dLeft} ${L(dLeft === 1 ? STR.dayLeft : STR.daysLeft, lang)}`}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}

              {c.raw?.online_platform && !/zoom|online/i.test(L(c.venue, lang)) && (
                <div className="fact-row">
                  <div className="k">{lang === 'zh' ? '平台' : 'Platform'}</div>
                  <div className="v">
                    {c.raw.online_platform}
                    <EditFieldBtn fieldPath="online_platform" />
                  </div>
                </div>
              )}

              {(Array.isArray(c.raw?.target_audience_en) && c.raw.target_audience_en.length > 0) && (
                <div className="fact-row">
                  <div className="k">{lang === 'zh' ? '對象' : 'For'}</div>
                  <div className="v" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                    {(lang === 'zh' && Array.isArray(c.raw.target_audience_zh) ? c.raw.target_audience_zh : c.raw.target_audience_en).map((t, i) => (
                      <span key={i} className="chip" style={{ background: 'var(--surface-2)', border: '0.5px solid var(--border)' }}>{t}</span>
                    ))}
                    <EditFieldBtn fieldPath="target_audience_en" />
                  </div>
                </div>
              )}

              {(Array.isArray(c.raw?.specialty_area_en) && c.raw.specialty_area_en.length > 0) && (
                <div className="fact-row">
                  <div className="k">{lang === 'zh' ? '專科' : 'Specialty'}</div>
                  <div className="v" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {(lang === 'zh' && Array.isArray(c.raw.specialty_area_zh) ? c.raw.specialty_area_zh : c.raw.specialty_area_en).map((t, i) => (
                      <span key={i} className="chip" style={{ background: 'var(--surface-2)', border: '0.5px solid var(--border)' }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {c.raw?.materials_language_en && (
                <div className="fact-row">
                  <div className="k">{lang === 'zh' ? '教材語言' : 'Materials'}</div>
                  <div className="v">{lang === 'zh' ? (c.raw.materials_language_zh || c.raw.materials_language_en) : c.raw.materials_language_en}</div>
                </div>
              )}

              <div className="fact-row">
                <div className="k">{L(STR.provider, lang)}</div>
                <div className="v">
                  {L(c.provider, lang)}
                  <div style={{ fontSize: 11.5, color: 'var(--fg-2)', fontWeight: 400, marginTop: 2 }}>
                    {lang === 'zh' ? '已認可 · NCHK' : 'Accredited · NCHK'}
                    {c.raw?.provider_code && <> · {c.raw.provider_code}</>}
                  </div>
                </div>
              </div>

              {c.courseCode && (
                <div className="fact-row">
                  <div className="k">{lang === 'zh' ? '課程編號' : 'Course code'}</div>
                  <div className="v mono" style={{ fontSize: 12.5, letterSpacing: '0.04em' }}>
                    {c.courseCode}
                    <EditFieldBtn fieldPath="course_code" />
                  </div>
                </div>
              )}

              {c.raw?.academy_en && (
                <div className="fact-row">
                  <div className="k">{lang === 'zh' ? '學院' : 'Academy'}</div>
                  <div className="v">{lang === 'zh' ? (c.raw.academy_zh || c.raw.academy_en) : c.raw.academy_en}</div>
                </div>
              )}

              {/* Community-data card — the catalogue is crowd-built, so fields
                  can be wrong. The PRIMARY entry point for corrections: a
                  visible "update course info" button (the per-field ✎ shortcuts
                  stay as the precise path) plus the AI-scan-credit incentive,
                  surfaced BEFORE the sheet opens so contributors learn
                  corrections are rewarded without having to open it first.
                  Hidden offline (report flow is disabled there, so the button
                  would dead-end).

                  ‼️ POSITION IS DELIBERATE — it belongs AFTER the fact rows, not
                  before them. Above the data it asked a nurse to correct fields
                  she had not read yet, and pushed the thing she opened the page
                  for (the date, the fee, the venue) below the fold. "This might
                  be wrong, fix it" only makes sense once she has seen what
                  "this" is. */}
              {!offlineMode && (
                <div style={{
                  padding: '10px 10px 8px', background: 'var(--surface)',
                  borderRadius: 10, marginTop: 12,
                }}>
                  <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5, padding: '0 2px' }}>
                    {lang === 'zh'
                      ? '課程資料由社群共同建立及維護,可能有誤。按欄位旁 ✎ 可更正該欄。'
                      : 'Course details are community-maintained and may contain errors — tap ✎ next to a field to correct it.'}
                  </div>
                  <button type="button" className="btn btn-fill"
                    style={{
                      marginTop: 8, padding: '9px 14px', fontSize: 13, fontWeight: 600,
                      background: 'var(--primary-soft)', color: 'var(--primary)', borderRadius: 9,
                    }}
                    onClick={() => openReport(null)}>
                    <Icon name="edit" size={14} stroke={1.9} />
                    <span>{lang === 'zh' ? '更新課程資料' : 'Update course info'}</span>
                  </button>
                  {/* Incentive line — payout is on admin apply (apply_report →
                      bonus_report), so the copy stays conditional; keep wording
                      in sync with the ReportCourseSheet reward lines. */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                                marginTop: 7, fontSize: 11.5, lineHeight: 1.4, color: 'var(--primary)' }}>
                    <Icon name="sparkle" size={12} stroke={1.8} />
                    <span>{lang === 'zh'
                      ? '更正獲採納後,可獲 +1 次 AI 掃描額度'
                      : 'Applied corrections earn +1 AI scan credit'}</span>
                  </div>
                </div>
              )}

              {/* Peer reviews — published comments from nurses who took this
                  programme. Below the facts on purpose: she came for the date,
                  the fee and the venue; other people's opinions are what she
                  reads once those check out. */}
              {!offlineMode && typeof CourseReviewsSection === 'function' && (
                <CourseReviewsSection courseId={c.id} lang={lang} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Bottom dock ────────────────────────────────────────────────────
          The CTA bar and every reminder that must sit above it, stacked in
          ONE bottom-anchored flex column (.cta-dock in styles.css).
          ‼️ Each reminder used to be its own absolutely-positioned box offset
          by a hard-coded `bottom: 76px` guess at the CTA bar's height — so the
          moment a button label wrapped, the bar painted over the notice.
          Keep new bottom chrome INSIDE this dock; never re-anchor it
          separately. floatChromeRef measures the dock to pad the scroller. */}
      <div className="cta-dock" ref={floatChromeRef}>

      {/* Return-state CTA — appears once after the user comes back from
          the provider's site for THIS course. Non-modal so it never
          blocks the page; "記錄" and "稍後" both clear the marker so the
          prompt can't re-fire on the next visibility flip. */}
      {!offlineMode && returnCtaOpen && (
        <div className="return-enrol-banner" role="status" aria-live="polite">
          <div className="return-enrol-banner-text">
            <div className="return-enrol-banner-title">
              {lang === 'zh' ? '已完成報名？' : 'Finished registering?'}
            </div>
            <div className="return-enrol-banner-sub">
              {lang === 'zh'
                ? '把這個課程加入「已報名」,方便追蹤進度。'
                : 'Add this course to your enrolled list to track progress.'}
            </div>
          </div>
          <div className="return-enrol-banner-actions">
            <button
              className="btn btn-ghost"
              onClick={() => { clearPendingEnrol(); setReturnCtaOpen(false); }}>
              {lang === 'zh' ? '稍後' : 'Later'}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                // Hide the banner optimistically; the marker is cleared
                // by the enrolled-transition effect only if the toggle
                // actually goes through (auth may intercept).
                setReturnCtaOpen(false);
                toggleEnrolled(c.id);
                showVitalToast(lang === 'zh' ? '已加入「已報名」' : 'Marked as enrolled');
              }}>
              <Icon name="check" size={14} stroke={2} />
              {lang === 'zh' ? '記錄' : 'Mark enrolled'}
            </button>
          </div>
        </div>
      )}

      {/* Restricted-course notice — informational context for members-only /
          internal courses. It no longer replaces the CTA: restricted courses
          are enrollable/loggable too (a nurse joins via their hospital or a
          membership they hold/apply for, then records it here). The guidance
          adapts to the user's eligibility. */}
      {c.isRestricted && !restrictNoteHidden.has(c.id) && (() => {
        const elig = (typeof window.restrictionEligibility === 'function')
          ? window.restrictionEligibility(c, profile) : null;
        // Tail guidance keyed by eligibility. All four branches are distinct —
        // 'ineligible' and 'unknown' used to share the "contact provider"
        // fallback, which said the same thing to a nurse we KNOW works
        // somewhere else and to one whose workplace we never asked for. The
        // second is the larger group (signed-out visitors, plus every profile
        // predating the workplace picker), and telling her to go ask the
        // provider hides the fact that the app could answer it itself the
        // moment she fills the field in.
        // ‼️ THE 'eligible' BRANCH SPEAKS ABOUT THE RULE, NOT ABOUT HER. All
        // restrictionVerdict established is that her workplace does not clash
        // with THIS restriction; the catalogue carries no register, prerequisite
        // or membership facts to check (eligible_registers is null on every live
        // row), so 「你符合資格」 claims a whole-course verdict from a single
        // matched axis. Say what was actually checked, and point at the rest.
        const tail = elig === 'eligible'
          ? (lang === 'zh'
              ? '你的工作機構符合這項限制。其他報名要求請以主辦機構公布為準。'
              : 'Your workplace matches this restriction. Check the provider for any other entry requirements.')
          : elig === 'open'
            ? (lang === 'zh' ? '你可申請成為會員後報名。' : 'You can apply to join, then enrol.')
            : elig === 'unknown'
              ? (lang === 'zh'
                  ? '在個人資料填寫工作機構後,我們就能告訴你是否符合。'
                  : 'Add your workplace in your profile and we can tell you whether you qualify.')
              : (lang === 'zh' ? '如你符合資格,請聯絡 provider。' : 'If eligible, please contact the provider.');
        const body = c.restrictionNote
          ? `${c.restrictionNote} — ${tail}`
          : (lang === 'zh' ? `會員 / 內部限定 — ${tail}` : `Members / internal only — ${tail}`);
        return (
          <div className="cta-restrict-note" role="note">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600 }}>
              <Icon name="shield" size={14} stroke={1.8} />
              {lang === 'zh' ? '受限課程' : 'Restricted course'}
              {/* NO verdict chip here, deliberately. The card pill carries the
                  verdict as tone + tick because a list row has no space for a
                  sentence (RestrictionChips); this screen does, and `tail` below
                  already says which axis was checked and what was not. A pill
                  repeating it would be the same fact twice — and the shorter,
                  louder half is the one a reader keeps. */}
              <button type="button"
                onClick={() => closeRestrictNote(c.id)}
                aria-label={lang === 'zh' ? '關閉' : 'Dismiss'}
                style={{ marginLeft: 'auto', flexShrink: 0, appearance: 'none', border: 0,
                         background: 'transparent', cursor: 'pointer', color: 'var(--fg-3)',
                         padding: 6, margin: '-6px -6px -6px auto', lineHeight: 0 }}>
                <Icon name="close" size={15} stroke={1.9} />
              </button>
            </div>
            <div className="muted-2" style={{ fontSize: 11, lineHeight: 1.45 }}>{body}</div>
          </div>
        );
      })()}

      {/* CTA — hidden in offline mode (save / enroll / log are all
          account-dependent and would silently no-op without network +
          session). Course detail remains read-only. Restricted courses render
          the CTA too, but with 我已報名 as the primary (no public registration
          flow) — the notice above gives the eligibility context. */}

      {/* Registration-status reminder — floats just above the CTA (mirrors the
          return-from-provider banner) so the always-present 報名 button stays
          honest about 已截止 / 已滿 / 即將開放 without hiding it. Not shown for
          open or unknown (both treated as available), for restricted/enrolled/
          ended courses, or while the return banner is up. */}
      {!offlineMode && !ended && !enrolled && !c.isRestricted && !returnCtaOpen && (() => {
        const note = enrollmentInfo?.kind === 'closed'
            ? (lang === 'zh' ? '報名已截止 — 你仍可標記「我已報名」或前往主辦機構查詢。' : "Registration closed — you can still mark it registered or check with the provider.")
          : enrollmentInfo?.kind === 'full'
            ? (lang === 'zh' ? '名額已滿 — 你仍可前往主辦機構查詢候補或最新情況。' : 'Full — you can still ask the provider about a waitlist or updates.')
          : enrollmentInfo?.kind === 'soon'
            ? (lang === 'zh' ? '報名即將開放 — 可前往主辦機構了解詳情。' : 'Registration opening soon — check with the provider for details.')
          : window.deriveRegistrationStatus?.(c) === 'unknown'
            ? (lang === 'zh' ? '主辦機構未公布報名狀況，請前往查證。' : 'The provider has not published registration status — please check directly.')
          : null;
        return note
          ? <div className="cta-status-note" role="note" ref={floatChromeRef}>{note}</div>
          : null;
      })()}

      {!offlineMode && (
        <div className="cta-bar detail" style={{ alignItems: 'stretch' }}>
          <button className="btn btn-ghost" onClick={() => toggleSaved(c.id)} style={{ flex: 0 }}>
            <Icon name="bookmark" size={17} stroke={1.7} color={saved ? 'var(--primary)' : 'currentColor'} fill={saved ? 'var(--primary)' : 'none'} />
          </button>
          {ended ? (
            // Course is over — "register" no longer applies, but a nurse who
            // attended still needs to LOG it for their renewal audit. So the
            // old dead-end disabled button is replaced with a "Log completion"
            // action that opens the LogSheet prefilled from this course.
            (() => {
              const ls = c.lifecycleStatus;
              // rejected_low_confidence (or any unknown non-active, non-archived
              // state) means the course DATA itself is untrusted — we must NOT
              // prefill it into an audit record, so keep it informational.
              const untrusted = ls && ls !== 'active' && ls !== 'archived';
              if (logged) {
                // Already in the audit log. Static confirmation — the record
                // itself lives in the Record tab; we don't deep-link there.
                return (
                  <div className="btn btn-ghost" style={{ flex: 1, cursor: 'default', pointerEvents: 'none' }}>
                    <Icon name="check" size={16} stroke={2} color="var(--primary)" />
                    {lang === 'zh' ? '已記錄' : 'Logged'}
                  </div>
                );
              }
              if (untrusted) {
                // Data flagged unreliable (OCR noise / low parse confidence /
                // bad duplicate). NOT a dead-end: the nurse's own attendance +
                // certificate are the real audit evidence, so we let her log —
                // but through a verification gate. The LogSheet opens on a
                // warning step with the audit-critical fields (dates, points)
                // blanked. A distinct label + ghost (not primary) styling
                // signals this is the slower, careful path — not a quick log.
                return (
                  <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => onLogCourse && onLogCourse(c.id)}>
                    <Icon name="shield" size={16} stroke={1.8} />
                    {lang === 'zh' ? '核實後記錄' : 'Verify & log'}
                  </button>
                );
              }
              return (
                <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => onLogCourse && onLogCourse(c.id)}>
                  <Icon name="check" size={16} stroke={2} />
                  {lang === 'zh' ? '記錄完成' : 'Log completion'}
                </button>
              );
            })()
          ) : enrolled ? (
            // Already-enrolled state always wins (lets user undo).
            <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => toggleEnrolled(c.id)}>
              <Icon name="check" size={16} stroke={2} /> {L(STR.enrolled, lang)}
            </button>
          ) : c.isRestricted ? (
            // Restricted course — there is no public registration flow to send
            // the user to (they join via their hospital / a membership). The
            // primary action is to RECORD it, so a nurse who is (or becomes)
            // eligible can track it toward renewal. Direct toggle, no dialog.
            <button className="btn btn-primary" style={{ flex: 1 }}
              onClick={() => {
                toggleEnrolled(c.id);
                showVitalToast(lang === 'zh' ? '已加入「已報名」' : 'Marked as enrolled');
              }}>
              <Icon name="check" size={16} stroke={2} />
              {lang === 'zh' ? '我已報名' : "I've registered"}
            </button>
          ) : (
            // Live course (not ended/cancelled/archived, not already enrolled,
            // not restricted). Registration is ALWAYS offered — even 已截止 /
            // 已滿: the nurse may have registered before it closed, the provider
            // may take late entries, or she just wants to mark 我已報名. The
            // RegisterDialog gives 我已報名 / 前往報名; a status reminder floats
            // above the bar (.cta-status-note) so 報名 stays honest about a
            // closed / full / opening-soon state instead of dead-ending on a
            // non-interactive label. An ongoing multi-session course also gets
            // 記錄完成 to log attendance mid-run.
            <>
              {/* ‼️ THE LABEL IS DERIVED, NEVER HARDCODED. This button was
                  STR.register (報名) for every live row regardless of what the
                  tap could actually accomplish. Measured 2026-08-19 over 270
                  live active rows: 147 render this button and only 58 resolve to
                  a real booking destination — the other 89 are contact_organizer
                  rows whose only link is the provider's course outline, so the
                  button promised registration and the dialog behind it then
                  offered 查看課程資料. deriveCourseAction (data.js) owns the
                  intent; see the source-hierarchy note there.
                  Do NOT reintroduce a constant here "for consistency" — the
                  inconsistency IS the fact being reported. */}
              {(() => {
                // ‼️ NO GUARDED FALLBACK. The obvious defensive shape here —
                // `window.deriveCourseAction ? … : { label: STR.register }` —
                // fails OPEN: if the resolver is ever missing (tree-shaken,
                // load order, a bad build) EVERY course silently reverts to the
                // hardcoded 報名 promise this whole change exists to delete, and
                // the louder the failure the quieter the symptom. A semantic
                // layer that degrades into its own bug is worse than one that is
                // absent. deriveCourseAction is a REQUIRED dependency: build.mjs
                // asserts it in REQUIRED_GLOBALS and fails the build without it,
                // so a missing resolver should throw here and be seen, never be
                // papered over with the strongest claim in the vocabulary.
                //
                //   A fallback must never be more confident than the thing it
                //   is standing in for.
                //
                // No `today` argument, matching axes/enrollmentInfo above: the
                // default is _axisToday(), the HK-shifted day. A device-local or
                // UTC "today" here would disagree with the status note beside it.
                const action = window.deriveCourseAction(c);
                // ‼️ P2 — ORDER, NOT VOCABULARY. On a closed / full row the
                // register action is DEMOTED to a ghost button, never relabelled
                // and never removed: the destination is still this course's own
                // page (late entries, a waitlist that reopens, provenance), and
                // the derived label still states truthfully what it is. What
                // changes is only which action the nurse is offered first —
                // 我已報名 is the one she can actually complete, and it is the
                // same direct toggle the restricted branch above uses.
                // deriveCourseAction owns the decision (`primary`); do NOT
                // re-derive it here from a status or a label string.
                if (action.primary === 'already_registered') {
                  return (
                    <>
                      <button className="btn btn-primary" style={{ flex: 1 }}
                        onClick={() => {
                          toggleEnrolled(c.id);
                          showVitalToast(lang === 'zh' ? '已加入「已報名」' : 'Marked as enrolled');
                        }}>
                        <Icon name="check" size={16} stroke={2} />
                        {lang === 'zh' ? '我已報名' : "I've registered"}
                      </button>
                      <button className="btn btn-ghost" style={{ flex: 1 }}
                        onClick={() => setRegisterDialog('options')}>
                        {L(action.label, lang)}
                      </button>
                    </>
                  );
                }
                return (
                  <button className="btn btn-primary" style={{ flex: 1 }}
                    disabled={!action.enabled}
                    onClick={() => action.enabled && setRegisterDialog('options')}>
                    {L(action.label, lang)}
                  </button>
                );
              })()}
              {axes?.eventStatus === 'ongoing' && !logged && (
                <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => onLogCourse && onLogCourse(c.id)}>
                  <Icon name="check" size={16} stroke={2} />
                  {lang === 'zh' ? '記錄完成' : 'Log completion'}
                </button>
              )}
            </>
          )}

          {/* Like — one-tap vouch for this course's data. It is the sole
              reaction surface now (the 🔁 type still exists server-side but is
              no longer shown). Read gate = communityAvailable (backend ready);
              the tap itself needs canReact. Reaching the threshold escalates a
              community course to 社群多人支持 (onReact handles the toast + badge). */}
          {communityAvailable && (() => {
            const liked = ((reactionSummary && reactionSummary.my_reactions) || []).includes('helpful');
            // Prefer the v2 generic map; fall back to the legacy key so this
            // still renders against a server that predates reactions v2.
            const count = (reactionSummary
              && ((reactionSummary.counts && reactionSummary.counts.helpful)
                  ?? reactionSummary.helpful_count)) || 0;
            return (
              <button className="btn btn-ghost" aria-pressed={liked}
                disabled={reactionBusy || !canReact}
                onClick={() => onReact('helpful')}
                aria-label={lang === 'zh' ? '喜歡' : 'Like'}
                title={lang === 'zh' ? '為課程資料背書' : 'Vouch for this course'}
                style={{ flex: 0, display: 'inline-flex', alignItems: 'center', gap: 5,
                  color: liked ? 'var(--primary)' : undefined }}>
                <Icon name="thumb-up" size={18} stroke={1.8}
                  color={liked ? 'var(--primary)' : 'currentColor'} fill={liked ? 'var(--primary)' : 'none'} />
                {count > 0 && <span style={{ fontSize: 13, fontWeight: 600 }}>{count}</span>}
              </button>
            );
          })()}
        </div>
      )}

      </div>{/* /.cta-dock */}

      {/* Register dialog — two-step flow */}
      {!offlineMode && registerDialog && (
        <RegisterDialog
          step={registerDialog}
          setStep={setRegisterDialog}
          lang={lang}
          course={c}
          onMarkEnrolled={() => {
            toggleEnrolled(c.id);
            setRegisterDialog(null);
            showVitalToast(lang === 'zh' ? '已加入「已報名」' : 'Marked as enrolled');
          }}
        />
      )}

      {/* Crowdsource Phase 1A — report incorrect / missing info on this course.
          `initial` is null when opened from the three-dot menu, and {fieldPath,
          reportType} when opened from a per-field ✎ shortcut (Phase 2). */}
      {!offlineMode && reportOpen && (
        <ReportCourseSheet
          lang={lang}
          course={c}
          initial={reportInitial}
          onClose={() => { setReportOpen(false); setReportInitial(null); }}
        />
      )}

      {communityEnabled && handleReport && (
        <HandleReportSheet
          lang={lang}
          handle={handleReport.handle}
          onClose={() => setHandleReport(null)}
          onSubmit={async (reason) => {
            const res = await window.DB.reportInappropriateHandle(c.id, handleReport.handle, reason);
            setHandleReport(null);
            showVitalToast((!res || res.error)
              ? (lang === 'zh' ? '檢舉失敗,請稍後再試' : 'Report failed, please try again')
              : (lang === 'zh' ? '已收到檢舉,多謝' : 'Report received — thank you'));
          }}
        />
      )}

      {communityEnabled && contribDialog && (
        <FieldContributionsDialog lang={lang} courseId={c.id} onClose={() => setContribDialog(false)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Helper: ephemeral toast (called from CourseDetailScreen menu/dialog)
// ─────────────────────────────────────────────────────────────────
function showVitalToast(msg, tone = 'info') {
  if (window.showToast) { window.showToast(msg, { tone }); return; }
  const t = document.createElement('div');
  t.className = 'vital-toast' + (tone === 'danger' ? ' is-danger' : tone === 'success' ? ' is-success' : tone === 'warning' ? ' is-warning' : '');
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.remove(), 1600);
}

// ─────────────────────────────────────────────────────────────────
// Open an external URL with the best in-app affordance per platform.
// On Capacitor iOS, SFSafariViewController keeps the user one tap from
// returning to Vital; on web we keep a plain new tab. The native path
// also fires the 'pending-enrol-return' window event when the sheet
// closes so the course screen can show the "did you finish?" CTA without
// waiting on visibilitychange (which behaves oddly inside the WKWebView).
// ─────────────────────────────────────────────────────────────────
async function openExternalUrl(url) {
  const isNative = !!globalThis.Capacitor?.isNativePlatform?.();
  const Browser = isNative ? window.CapacitorBrowser : null;
  if (Browser?.open) {
    let handle = null;
    try {
      handle = await Browser.addListener('browserFinished', () => {
        try { handle?.remove(); } catch {}
        window.dispatchEvent(new Event('pending-enrol-return'));
      });
      await Browser.open({ url, presentationStyle: 'popover' });
      // Browser.open resolves once the sheet is presented — at this
      // point we know the user actually left for the provider site.
      return { opened: true };
    } catch (e) {
      // open() rejected after the listener was registered; pull it down
      // so a future browserFinished from an unrelated open doesn't fire
      // our return CTA. Then fall through to the web path as a backup.
      try { handle?.remove(); } catch {}
      console.warn('[Vital] Browser.open failed, falling back to window.open', e);
    }
  }
  // On web with `noopener`, window.open returns null BY SPEC in
  // Chromium-based browsers (the WindowProxy is intentionally severed),
  // so a null return is NOT a reliable signal of a blocked popup —
  // mixing the two would suppress the marker on every Chromium open.
  // We trust the call: since it originates from a user gesture (the
  // dialog's Continue button), browsers overwhelmingly allow it; the
  // failure cost is one spurious "did you finish?" prompt the user can
  // dismiss with 稍後. The marker's TTL bounds the worst case.
  window.open(url, '_blank', 'noopener,noreferrer');
  return { opened: true };
}

// Vital's App Store listing (apps.apple.com/hk/app/vital-nurse-cne).
const APP_STORE_APP_ID = '6772449124';

// Open the App Store "Write a Review" composer. Used by both explicit,
// user-initiated entry points: the Profile row and ReviewAskSheet's positive
// action. Not openExternalUrl on native:
// SFSafariViewController can't load non-http schemes, so we navigate directly
// and Capacitor's delegation hands itms-apps:// to UIApplication.open — the
// same routing that makes the mailto: anchors work.
function openAppStoreReview() {
  if (globalThis.Capacitor?.isNativePlatform?.()) {
    window.location.href = `itms-apps://apps.apple.com/app/id${APP_STORE_APP_ID}?action=write-review`;
    return;
  }
  openExternalUrl(`https://apps.apple.com/hk/app/vital-nurse-cne/id${APP_STORE_APP_ID}?action=write-review`);
}

// Marker the RegisterDialog drops in sessionStorage when the user
// commits to leaving for the provider site. The course-detail screen
// reads it whenever something suggests the user is back (native:
// browserFinished; web: visibility/focus/pageshow/mount) and offers a
// one-tap "mark enrolled" CTA. Keyed per-course so an in-flight prompt
// for course A doesn't fire when the user navigates to course B.
//
// The 10-minute TTL is the actual contract on web: there is no reliable
// "user came back" event across browsers, so the marker is treated as a
// reminder state. While valid, any re-entry to the same course screen
// (focus, reload, route re-mount) should surface the CTA. sessionStorage
// (not localStorage) is intentional — the marker doesn't need to cross
// tabs or survive a full browser restart, and bounding scope to "this
// tab" cleanly handles users who close and re-open the app.
const PENDING_ENROL_KEY = 'vital:pendingEnrol';
const PENDING_ENROL_TTL_MS = 10 * 60 * 1000;
function setPendingEnrol(courseId) {
  try {
    sessionStorage.setItem(PENDING_ENROL_KEY, JSON.stringify({ courseId, clickedAt: Date.now() }));
  } catch {}
}
function readPendingEnrol(courseId) {
  try {
    const raw = sessionStorage.getItem(PENDING_ENROL_KEY);
    if (!raw) return null;
    const m = JSON.parse(raw);
    if (!m || m.courseId !== courseId) return null;
    if (Date.now() - (m.clickedAt || 0) > PENDING_ENROL_TTL_MS) {
      sessionStorage.removeItem(PENDING_ENROL_KEY);
      return null;
    }
    return m;
  } catch {
    return null;
  }
}
function clearPendingEnrol() {
  try { sessionStorage.removeItem(PENDING_ENROL_KEY); } catch {}
}

// ─────────────────────────────────────────────────────────────────
// Register dialog — two-step flow
//   1. options: pick "我已報名" or "前往報名"
//   2. external: confirm before leaving Vital
// ─────────────────────────────────────────────────────────────────
function RegisterDialog({ step, setStep, lang, course, onMarkEnrolled }) {
  // ‼️ WHERE THE TAP GOES AND WHAT WE CALL IT ARE TWO SEPARATE READS. Both used
  // to come from one boolean ("did we get a URL"), which captioned a provider's
  // course-outline PDF as 「前往報名」 on every contact_organizer row — 137 of the
  // 191 unfinished courses on 2026-08-16. registrationDestination (data.js) owns
  // the split: `url` is the same best-available fallback as before, `kind` is
  // decided by the provider's registration_url_type. See the note there.
  const destination = window.registrationDestination
    ? window.registrationDestination(course)
    : { url: course.registrationUrl || null, targetSource: 'none', method: null, kind: 'none' };
  const targetUrl = destination.url;
  // Kept for the "is this a real booking page" styling/copy split below. Reading
  // destination.kind — NEVER `!!targetUrl`, which is the conflation above.
  const isRegistration = destination.kind === 'registration';
  // ‼️ THE DIALOG MUST NOT OUTRANK THE BUTTON THAT OPENED IT. destination.kind
  // is 'registration' for the live Zoom row (an explicit registration_url), so
  // this card captioned it 「前往報名」 while the CTA said 「立即參加」 — the tap
  // led to the right URL under a claim the evidence does not support, which is
  // the same defect one surface further in. The intent owner decides the word;
  // `kind` still decides everything about the destination itself.
  const joinAction = window.deriveCourseAction
    && window.deriveCourseAction(course).intent === 'join';
  // Whether registering actually happens by contacting the organiser. This is
  // the provider's own statement, so it decides whether contact details show —
  // previously that was tied to which URL tier won, and the 137 rows that most
  // needed the instruction were the ones whose fallback URL suppressed it.
  const isContactMethod = destination.method === 'contact_organizer';
  // Contact instruction shown when there's no external URL (contact_organizer,
  // email, phone, walk_in) so the "go to provider" option still tells the user
  // how to actually register instead of dead-ending on a missing link.
  // Fallback order: current language → whatever instruction exists in the other
  // language (contact details are usually a language-neutral phone/email, so a
  // populated value beats a generic string) → per-language generic default.
  // L() already cross-falls to .en, so this only adds the .zh-only case (e.g.
  // an English UI where only the Chinese instruction was filled in). Guarantees
  // non-blank text in both UIs.
  const contactInstr = L(course.registrationContact, lang)
    || course.registrationContact?.zh
    || course.registrationContact?.en
    || (lang === 'zh' ? '請直接聯絡主辦機構報名。' : 'Please contact the organiser directly to register.');
  // True only when a REAL contact instruction was filled (not the generic
  // fallback above) — so the homepage tier can append genuine phone/email
  // details without echoing a generic "contact the organiser" string.
  const hasContact = !!(L(course.registrationContact, lang)
    || course.registrationContact?.zh
    || course.registrationContact?.en);

  // Open the external page first, and only arm the "left for provider"
  // marker once the open is confirmed. Closing the dialog optimistically
  // is safe: even if open fails, the user is back where they started
  // with no marker to misfire later. On native Browser.open resolves
  // before any browserFinished can fire (presentation completes before
  // the user can dismiss), so setting the marker after open still wins
  // the race against the return-CTA listener.
  const continueToProvider = async () => {
    if (!targetUrl) return;
    setStep(null);
    const result = await openExternalUrl(targetUrl);
    // Arm the gentle "did you finish registering?" return prompt for both a
    // direct link and the homepage fallback — the user's intent is to enrol
    // either way, and the prompt is non-modal and dismissible.
    if (result?.opened) setPendingEnrol(course.id);
  };

  // Keyboard: Esc closes, Enter on external step opens the link
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setStep(null);
      if (e.key === 'Enter' && step === 'external' && targetUrl) continueToProvider();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [step, targetUrl]);

  return (
    <div className="register-dialog-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setStep(null); }}>
      <div className="register-dialog" role="dialog" aria-modal="true">
        <button className="register-dialog-close icon-btn" onClick={() => setStep(null)} aria-label={lang === 'zh' ? '關閉' : 'Close'}>
          <Icon name="close" size={16} />
        </button>

        {step === 'options' && (
          <>
            <div className="register-dialog-eyebrow mono">
              {lang === 'zh' ? '報名選項' : 'Registration'}
            </div>
            <h3 className="register-dialog-title">
              {L(course.title, lang)}
            </h3>
            <p className="register-dialog-sub">
              {lang === 'zh'
                ? '請告訴我們你的狀態:'
                : "Tell us where you are with this course:"}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
              {/* Option 1: already registered (with provider) */}
              <M.button
                className="register-dialog-card"
                onClick={() => { clearPendingEnrol(); onMarkEnrolled(); }}
                whileTap={{ scale: 0.985 }}
                whileHover={{ y: -1 }}
                transition={{ duration: 0.16 }}
              >
                <div className="rd-card-icon" style={{ background: 'var(--primary-soft)', color: 'var(--primary)' }}>
                  <Icon name="check-circ" size={20} stroke={1.7} />
                </div>
                <div className="rd-card-body">
                  <div className="rd-card-title">{lang === 'zh' ? '我已報名' : "I've already registered"}</div>
                  <div className="rd-card-sub">
                    {lang === 'zh' ? '把這個課程加入我的「已報名」' : 'Add this course to your enrolled list'}
                  </div>
                </div>
                <Icon name="chev-r" size={16} color="var(--fg-3)" stroke={1.7} />
              </M.button>

              {/* Option 2, three tiers:
                  • direct registration link  → "前往報名"
                  • no link but provider homepage known → "到主辦機構官網查看"
                    (honestly flagged as not a direct booking page)
                  • neither → disabled contact-only dead-end card */}
              <M.button
                className="register-dialog-card"
                onClick={() => setStep('external')}
                disabled={!targetUrl}
                whileTap={!targetUrl ? null : { scale: 0.985 }}
                whileHover={!targetUrl ? null : { y: -1 }}
                transition={{ duration: 0.16 }}
                style={!targetUrl ? { cursor: 'default' } : undefined}>
                <div className="rd-card-icon" style={{
                  background: isRegistration ? 'var(--accent-soft)' : 'var(--primary-soft)',
                  color: isRegistration ? 'var(--accent)' : 'var(--primary)',
                }}>
                  <Icon
                    name={isRegistration ? 'arrow-r' : (destination.kind === 'none' ? 'user' : 'globe')}
                    size={20} stroke={1.8} />
                </div>
                <div className="rd-card-body">
                  <div className="rd-card-title">
                    {{
                      registration:  joinAction
                        ? (lang === 'zh' ? '開啟課堂連結' : 'Open course link')
                        : (lang === 'zh' ? '前往報名' : 'Go to registration page'),
                      course_info:   lang === 'zh' ? '查看課程資料' : 'View course details',
                      contact_link:  lang === 'zh' ? '查看聯絡方式' : 'View contact details',
                      provider_info: lang === 'zh' ? '到主辦機構官網查看' : "Find it on the provider's site",
                      none:          lang === 'zh' ? '聯絡主辦機構報名' : 'Register via organiser',
                    }[destination.kind]}
                  </div>
                  <div className="rd-card-sub">
                    {/* Every non-registration kind says the same two things: what
                        the link actually is, and that registering happens
                        elsewhere. A real instruction/contact on the row beats the
                        generic sentence — and it is gated on the METHOD, so the
                        rows that register by contacting the organiser get their
                        contact details whichever URL tier won. */}
                    {destination.kind === 'registration'
                      ? (joinAction
                        // ‼️ The URL proves the destination is a classroom. It does
                        // not prove the classroom is open, unlocked, or started —
                        // Zoom rooms have waiting rooms, sign-in walls and start
                        // times. "takes you straight into the session" claimed all
                        // three. Say what the link opens, not what will happen.
                        ? (lang === 'zh' ? '此課程無需報名,此連結會開啟課堂/會議頁面。' : 'No registration needed — this link opens the session page.')
                        : (lang === 'zh' ? '到主辦機構網站完成報名' : "Open the provider's registration page"))
                      : destination.kind === 'none'
                        ? contactInstr
                        : ((destination.kind === 'course_info'
                            ? (lang === 'zh'
                                ? '尚未提供直接報名連結。可先查看主辦機構提供的課程資料,再按指示聯絡報名。'
                                : 'No direct registration link yet — open the provider’s course details, then follow their instructions to register.')
                            : destination.kind === 'contact_link'
                              ? (lang === 'zh'
                                  ? '尚未提供直接報名連結。此連結為主辦機構的聯絡頁面。'
                                  : 'No direct registration link yet — this opens the provider’s contact page.')
                              : (lang === 'zh'
                                  ? '尚未提供直接報名連結。可到主辦機構官網搜尋此課程並按指示報名。'
                                  : "No direct link yet — open the provider's site to find this course and register."))
                          + ((hasContact || isContactMethod) ? ' ' + contactInstr : ''))}
                  </div>
                </div>
                {targetUrl && <Icon name="chev-r" size={16} color="var(--fg-3)" stroke={1.7} />}
              </M.button>
            </div>
          </>
        )}

        {step === 'external' && (
          <>
            <div className="register-dialog-eyebrow mono" style={{ color: 'var(--accent)' }}>
              {lang === 'zh' ? '即將離開 Vital' : 'Leaving Vital'}
            </div>
            <h3 className="register-dialog-title">
              {lang === 'zh' ? '前往外部網站' : 'Opening external site'}
            </h3>
            <p className="register-dialog-sub">
              {/* The leaving-Vital sentence makes the same claim the card does,
                  so it reads the same axis. Saying 「完成報名」 over a course
                  outline is the identical over-claim one screen later. */}
              {isRegistration
                ? (lang === 'zh'
                    ? '你將被帶到主辦機構的網站完成報名。Vital 對外部網站的內容並不負責。'
                    : "You're about to leave Vital and go to the provider's site to finish registering. Vital isn't responsible for the content of external sites.")
                : destination.kind === 'course_info'
                  ? (lang === 'zh'
                      ? '你將前往主辦機構提供的課程資料(並非報名頁)。報名須按主辦機構的指示辦理。Vital 對外部網站的內容並不負責。'
                      : "You're about to open the provider's course details (not a registration page). Registering is handled by the provider. Vital isn't responsible for the content of external sites.")
                  : (lang === 'zh'
                      ? '你將前往主辦機構的官方網站(並非直接報名頁)。請在網站上尋找此課程並按指示報名。Vital 對外部網站的內容並不負責。'
                      : "You're about to open the provider's official website (not a direct registration page). Find this course there and follow their instructions to register. Vital isn't responsible for the content of external sites.")}
            </p>

            <div className="register-dialog-url" title={targetUrl}>
              <Icon name="globe" size={14} stroke={1.7} color="var(--fg-3)" />
              <span>{targetUrl}</span>
            </div>

            <div className="register-dialog-actions">
              <button className="btn btn-ghost" onClick={() => setStep('options')}>
                {lang === 'zh' ? '取消' : 'Cancel'}
              </button>
              <button className="btn btn-primary" autoFocus onClick={continueToProvider}>
                <Icon name="arrow-r" size={15} stroke={2} />
                {lang === 'zh' ? '繼續前往' : 'Continue'}
                <kbd className="register-dialog-kbd">↵</kbd>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MY RECORD (timeline)
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// NCHK export UI — drives the official Annex A + B PDF (generator lives in
// data.js: buildNchkHtml / exportNchkPdf / exportBlankNchkPdf). Flow: choose
// Official vs Blank; Official gates on the declaration identity fields (reg no.
// + legal name, saved once to the profile) then opens an editable PREVIEW.
// Preview edits are PDF-ONLY — passed straight to the generator, NEVER written
// back to records/profiles. CNE-window bucketing reuses cycleRecords/cneWindow.
// ─────────────────────────────────────────────────────────────────────────────
let _nchkRowSeq = 0;
function nchkRowId() {
  try { if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID(); } catch (_) {}
  return 'nr' + (++_nchkRowSeq) + '_' + Date.now().toString(36);
}

function NchkDateField({ label, value, onChange, lang }) {
  return (
    <div className="auth-field">
      <div className="auth-label mono">{label}</div>
      <DateField className="auth-input" value={value || ''} onChange={onChange} lang={lang} />
    </div>
  );
}

function NchkModeChooser({ lang, onFilled, onBlank }) {
  const card = { display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
    gap: 3, padding: '14px 16px', height: 'auto', textAlign: 'left' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 8 }}>
      <button type="button" className="btn btn-primary btn-fill" style={card} onClick={onFilled}>
        <span style={{ fontWeight: 700 }}>{L(STR.nchkModeFilled, lang)}</span>
        <span style={{ fontSize: 12, opacity: 0.85, fontWeight: 400 }}>{L(STR.nchkModeFilledHint, lang)}</span>
      </button>
      <button type="button" className="btn btn-ghost btn-fill" style={card} onClick={onBlank}>
        <span style={{ fontWeight: 700 }}>{L(STR.nchkModeBlank, lang)}</span>
        <span style={{ fontSize: 12, opacity: 0.7, fontWeight: 400 }}>{L(STR.nchkModeBlankHint, lang)}</span>
      </button>
    </div>
  );
}

// Declaration identity for the NCHK export — the nurse's name + THIS certificate's
// registration number, saved to the profile so it's entered once per certificate
// and reused on every future export (not PDF-only). A dual-registered nurse has a
// separate number per certificate: the primary's lives in profiles.license_no,
// the second's inside the license2 jsonb — the save routes to the right one via
// `lic`. `showRole` labels which certificate this card is for.
function NchkProfileGateForm({ lang, session, profile, lic, showRole, onSaved, onCancel }) {
  const isZh = lang === 'zh';
  const userId = session?.user?.id;
  const isPrimary = !lic || lic.key === 'primary';
  const [form, setForm] = React.useState({
    license_no: (lic ? lic.licenseNo : profile?.license_no) || '',
    name_en: profile?.name_en || profile?.display_name || '',
    name_zh: profile?.name_zh || '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const [busy, setBusy] = React.useState(false);

  const canSave = (form.license_no || '').trim() &&
    ((form.name_en || '').trim() || (form.name_zh || '').trim());

  const save = async () => {
    if (!canSave || busy) return;
    setBusy(true);
    const regNo = (form.license_no || '').trim();
    const nameEn = (form.name_en || '').trim();
    const nameZh = (form.name_zh || '').trim();
    // Name is shared across certificates; the registration number is per
    // certificate. Primary → license_no; second → merge into the license2 jsonb
    // (read-modify-write so the rest of that blob — role, cycle — is preserved).
    const patch = isPrimary
      ? { name_en: nameEn, name_zh: nameZh, license_no: regNo }
      : { name_en: nameEn, name_zh: nameZh,
          license2: { ...(profile?.license2 && typeof profile.license2 === 'object' ? profile.license2 : {}),
            license_no: regNo } };
    try {
      // Don't block the export on a write failure (e.g. a column missing on a
      // lagging DB) — merge the entered values over whatever came back and go.
      const { data } = (await window.DB.upsertProfile(userId, patch)) || {};
      onSaved && onSaved({ ...(profile || {}), ...(data || {}), ...patch });
    } catch (e) {
      console.warn('NCHK gate upsert failed', e);
      onSaved && onSaved({ ...(profile || {}), ...patch });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-form" style={{ paddingTop: 8 }}>
      <p className="muted-2" style={{ fontSize: 12, marginTop: 0 }}>{L(STR.nchkDetailsHint, lang)}</p>
      {showRole && lic && lic.role ? (
        <div className="auth-label mono" style={{ marginBottom: 2 }}>{L(lic.role, lang)}</div>
      ) : null}
      {/* Identity card — persisted, so entered once per certificate. */}
      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <AuthField label={L(STR.nchkNameEn, lang)} value={form.name_en}
          onChange={v => set('name_en', v)} placeholder="Chan Tai Man" />
        <AuthField label={L(STR.nchkNameZh, lang)} value={form.name_zh}
          onChange={v => set('name_zh', v)} placeholder="陳大文" />
        <AuthField label={L(STR.nchkRegNo, lang)} value={form.license_no}
          onChange={v => set('license_no', v)} placeholder="RNGM1234567" />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
        <LoadingButton className="btn btn-primary btn-fill" isLoading={busy} disabled={!canSave}
          loadingLabel={isZh ? '儲存中…' : 'Saving…'} onClick={save}>
          {isZh ? '儲存並繼續' : 'Save & continue'}
        </LoadingButton>
        <button type="button" className="btn btn-ghost btn-fill" disabled={busy} onClick={onCancel}>
          {isZh ? '返回' : 'Back'}
        </button>
      </div>
    </div>
  );
}

async function auditPackEvidence(rows, records, onProgress) {
  const byId = new Map((records || []).map(r => [r.id, r]));
  const candidates = [];
  const seen = new Set();
  (rows || []).forEach((row, index) => {
    const record = byId.get(row.recordId);
    if (!record) return;
    const noteImages = typeof parseNoteBlob === 'function'
      ? (parseNoteBlob(record.notes).imagePaths || []) : [];
    [record.certificateUrl, ...noteImages].filter(Boolean).forEach(path => {
      const key = `${record.id}:${path}`;
      if (!seen.has(key)) { seen.add(key); candidates.push({ path, line: index + 1, activity: row.activity }); }
    });
  });
  const limited = candidates.slice(0, 30);
  let skippedPdf = 0;
  let skippedOther = candidates.length - limited.length;
  const items = [];
  for (let i = 0; i < limited.length; i++) {
    const item = limited[i];
    onProgress?.(i + 1, limited.length);
    if (/\.pdf(?:$|[?#])/i.test(item.path)) { skippedPdf++; continue; }
    const source = await window.DB?.getCertificateDataUrl?.(item.path);
    if (!source) { skippedOther++; continue; }
    if (/^data:application\/pdf/i.test(source)) { skippedPdf++; continue; }
    let dataUrl = source;
    // Re-encode images before embedding them in the one HTML string handed to
    // the native print bridge. This keeps a 20–30-certificate pack practical.
    try {
      if (typeof compressImage === 'function' && typeof File !== 'undefined') {
        const blob = await fetch(source).then(r => r.blob());
        const file = new File([blob], 'certificate', { type: blob.type || 'image/jpeg' });
        const compressed = await compressImage(file, { maxEdge: 800, quality: 0.6 });
        if (compressed && compressed.type?.startsWith('image/')) {
          dataUrl = await new Promise(resolve => {
            const reader = new FileReader();
            reader.onerror = () => resolve(source);
            reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : source);
            reader.readAsDataURL(compressed);
          });
        }
      }
    } catch (_) { /* keep the downloaded image if recompression is unavailable */ }
    if (!/^data:image\//i.test(dataUrl)) { skippedOther++; continue; }
    items.push({ ...item, dataUrl });
  }
  return { items, skippedPdf, skippedOther, limited: limited.length, total: candidates.length };
}

function NchkPreview({ lang, user, profile, records, lic, showRole, highlightAudit = false, onHighlightConsumed, onEditIdentity, onDone }) {
  const isZh = lang === 'zh';
  // Per-licence export: bucket records + seed the declaration against the chosen
  // certificate's own cycle window and registration number, so a dual-registered
  // nurse's two forms each cover their own cycle. Falls back to the flat
  // user/profile fields for the single-licence path (lic === primary there).
  const pcFrom = (lic ? lic.cycleStart : user?.cycleStart) || null;
  const pcTo   = (lic ? lic.renewalDate : user?.renewalDate) || null;
  const licUser = lic ? { ...user, cycleStart: pcFrom, renewalDate: pcTo } : user;
  const licProfile = lic ? { ...profile, license_no: lic.licenseNo || '' } : profile;
  // Seed Annex A from the cycle-filtered records (CNE window) and the
  // declaration from the profile. State is LOCAL — only generate() reads it.
  const [rows, setRows] = React.useState(() => {
    const filtered = cycleRecords(records, pcFrom, pcTo);
    return (window.nchkAnnexARows(filtered, lang) || []).map(r => ({ ...r, id: nchkRowId() }));
  });
  const [decl, setDecl] = React.useState(() => window.nchkAnnexB({ profile: licProfile, user: licUser, lang }));

  // Archived cycles (rollover history) for THIS licence — after a renewal the
  // current window no longer covers the cycle the nurse is actually submitting,
  // so the export must be able to target a closed cycle. Rows are recomputed
  // from the records by the archived window (not from the stored earned_total),
  // so later record edits are reflected.
  const licKey = lic ? lic.key : 'primary';
  const archivedCycles = React.useMemo(() => (
    (Array.isArray(profile?.cycle_history) ? profile.cycle_history : [])
      .filter(h => h && (h.license || 'primary') === licKey && h.cycle_start && h.renewal_date)
  ), [profile, licKey]);
  const [cycleSel, setCycleSel] = React.useState('current');
  const [includeEvidence, setIncludeEvidence] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [evidenceProgress, setEvidenceProgress] = React.useState(null);
  const [evidenceSummary, setEvidenceSummary] = React.useState(null);
  // Shared entitlement (paywall.jsx). It re-reads on `vital:entitlement-changed`,
  // so buying Pro from anywhere unlocks this sheet's audit-pack toggle without
  // the nurse having to close and reopen it. No user-id key here: this preview
  // is only ever mounted inside an authenticated export flow.
  const proEnt = useProEntitlement();
  const isPro = proEnt.pro;
  const [proOpen, setProOpen] = React.useState(false);
  const auditFocusRef = useProTargetHighlight(highlightAudit, onHighlightConsumed);
  // ‼️ TWO DIFFERENT QUESTIONS, AND THEY USED TO BE ONE.
  //
  // This card was gated on PurchaseAPI.isSupported() alone, which asks "can a
  // subscription be BOUGHT on this device". On the web build that is false — so
  // a nurse who subscribed on her iPhone opened the export sheet on the web app
  // and the feature she pays for was not there at all. Nothing about the audit
  // pack is native: auditPackEvidence() is fetch + canvas + FileReader, and
  // printNchkHtml() has a web path (a laid-out iframe). It works.
  //
  // So: `isPro` decides whether she can USE it, `canBuyPro` whether anyone can
  // still be SOLD it. Show the card when either is true — and never render the
  // upsell button on a build that cannot take the purchase (a dead "Explore
  // Vital Pro" is worse than no card, and linking out to a web purchase from
  // iOS is a Guideline 3.1.2 violation, which is why there is no third branch).
  const canBuyPro = !!window.PurchaseAPI?.isSupported?.();
  const showAuditPack = isPro || canBuyPro;
  const applyCycle = (val) => {
    setCycleSel(val);
    const h = val === 'current' ? null : archivedCycles[Number(val)];
    const from = h ? h.cycle_start : pcFrom;
    const to   = h ? h.renewal_date : pcTo;
    const filtered = cycleRecords(records, from, to);
    setRows((window.nchkAnnexARows(filtered, lang) || []).map(r => ({ ...r, id: nchkRowId() })));
    setDecl(window.nchkAnnexB({ profile: licProfile, user: { ...licUser, cycleStart: from, renewalDate: to }, lang }));
  };

  const setRow = (id, k, v) => setRows(rs => rs.map(r => (r.id === id ? { ...r, [k]: v } : r)));
  const removeRow = (id) => setRows(rs => rs.filter(r => r.id !== id));
  const addRow = () => setRows(rs => [...rs, { id: nchkRowId(), nature: 'course',
    natureOther: '', activity: '', from: '', to: '', points: 0, org: '' }]);
  const setD = (k, v) => setDecl(d => ({ ...d, [k]: v }));

  const total = rows.reduce((s, r) => s + (Number(r.points) || 0), 0);
  const natureOpts = window.NCHK_NATURE_OPTIONS || [];

  const generate = async () => {
    if (exporting) return;
    setExporting(true);
    // PDF-ONLY: the locally-edited rows + declaration go straight to the
    // generator. No window.DB.* here — records/profile tables are untouched.
    // titleSuffix names the licence's role only for a dual-registered nurse, so
    // the two PDFs are distinguishable; single-licence titles are unchanged.
    const titleSuffix = (showRole && lic && lic.role) ? L(lic.role, lang) : '';
    let evidence = null;
    if (includeEvidence && isPro) {
      setEvidenceProgress({ current: 0, total: 0 });
      evidence = await auditPackEvidence(rows, records, (current, total) => setEvidenceProgress({ current, total }));
      setEvidenceSummary(evidence);
      setEvidenceProgress(null);
      if (evidence.skippedPdf) {
        showVitalToast(isZh
          ? `${evidence.skippedPdf} 份 PDF 證書未併入，請另行附上。`
          : `${evidence.skippedPdf} PDF certificate${evidence.skippedPdf === 1 ? ' was' : 's were'} not merged; attach ${evidence.skippedPdf === 1 ? 'it' : 'them'} separately.`, 'warning');
      }
    }
    window.exportNchkPdf({ annexA: rows.map(({ id, recordId, ...r }) => r), annexB: decl, lang, titleSuffix, evidence });
    // Forward audit (Batch B): stamp that this nurse exported an Annex B, so a
    // targeted re-issue nudge is possible if declared dates ever need updating
    // (e.g. this cycle-date correction). Fire-and-forget — the PDF is the point;
    // never block or fail the export on this write.
    try {
      const uid = profile && profile.id;
      if (uid && window.DB && window.DB.setPrefs) {
        Promise.resolve(window.DB.setPrefs(uid, { lastAnnexExport: new Date().toISOString() })).catch(() => {});
      }
    } catch (_) { /* audit is best-effort */ }
    setExporting(false);
    onDone && onDone();
  };

  return (
    <div style={{ paddingTop: 4 }}>
      <p className="muted-2" style={{ fontSize: 12, marginTop: 0 }}>{L(STR.nchkPreviewHint, lang)}</p>

      {/* Cycle selector — only when this licence has archived (rolled-over)
          cycles. Switching re-seeds the annex rows + declaration window; any
          manual row edits are intentionally reset (they belonged to the other
          cycle's window). */}
      {archivedCycles.length > 0 ? (
        <div className="auth-field" style={{ marginBottom: 12 }}>
          <div className="auth-label mono">{isZh ? '匯出週期' : 'Cycle to export'}</div>
          <select className="auth-input" value={cycleSel} onChange={e => applyCycle(e.target.value)}>
            <option value="current">
              {(isZh ? '現時週期 ' : 'Current cycle ') +
                `${fmtDate(pcFrom, lang) || '—'} → ${fmtDate(pcTo, lang) || '—'}`}
            </option>
            {archivedCycles.map((h, i) => (
              <option key={i} value={String(i)}>
                {`${fmtDate(h.cycle_start, lang)} → ${fmtDate(h.renewal_date, lang)}` +
                  (isZh ? '（已存檔）' : ' (archived)')}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {/* Declaration identity — the name + this certificate's registration
          number, saved via the details card (tap Edit to change + re-save). Shown
          read-only here so the printed declaration and the saved profile can't
          diverge. */}
      <div className="card" style={{ marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="mono muted-2" style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>
            {L(STR.nchkDetailsTitle, lang)}
          </div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {[decl.nameEn, decl.nameZh].filter(Boolean).join(' · ') || '—'}
          </div>
          <div className="muted-2" style={{ fontSize: 12 }}>{decl.regNo || '—'}</div>
        </div>
        {onEditIdentity ? (
          <button type="button" className="btn btn-ghost" style={{ flexShrink: 0 }} onClick={onEditIdentity}>
            {isZh ? '編輯' : 'Edit'}
          </button>
        ) : null}
      </div>

      {/* Declaration period (edits are PDF-only) */}
      <div className="auth-form" style={{ marginBottom: 14 }}>
        <div className="auth-label mono" style={{ marginBottom: 4 }}>{L(STR.nchkPeriodTitle, lang)}</div>
        <div className="nchk-field-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <NchkDateField label={L(STR.nchkCnePeriod, lang) + ' · ' + L(STR.nchkColFrom, lang)} value={decl.cneFrom} onChange={v => setD('cneFrom', v)} lang={lang} />
          <NchkDateField label={L(STR.nchkColTo, lang)} value={decl.cneTo} onChange={v => setD('cneTo', v)} lang={lang} />
        </div>
        <div className="nchk-field-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <NchkDateField label={L(STR.nchkPcValid, lang) + ' · ' + L(STR.nchkColFrom, lang)} value={decl.pcFrom} onChange={v => setD('pcFrom', v)} lang={lang} />
          <NchkDateField label={L(STR.nchkColTo, lang)} value={decl.pcTo} onChange={v => setD('pcTo', v)} lang={lang} />
        </div>
      </div>

      {/* Annex A activities */}
      <div className="auth-label mono" style={{ marginBottom: 8 }}>
        {isZh ? '進修活動' : 'CNE activities'} · {rows.length} · {total} {L(STR.pts, lang)}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((r, i) => (
          <div key={r.id} style={{ border: '0.5px solid var(--border)', borderRadius: 12, padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span className="mono muted-2" style={{ fontSize: 11 }}>#{i + 1}</span>
              <button type="button" className="icon-btn" style={{ width: 28, height: 28 }}
                onClick={() => removeRow(r.id)} aria-label={L(STR.nchkRemoveRow, lang)}>
                <Icon name="close" size={14} />
              </button>
            </div>
            <label className="auth-field">
              <div className="auth-label mono">{L(STR.nchkColNature, lang)}</div>
              <select className="auth-input" value={r.nature} onChange={e => setRow(r.id, 'nature', e.target.value)}>
                {natureOpts.map(o => <option key={o.id} value={o.id}>{L(o, lang)}</option>)}
              </select>
            </label>
            {r.nature === 'other' && (
              <AuthField label={isZh ? '請註明' : 'Specify'} value={r.natureOther}
                onChange={v => setRow(r.id, 'natureOther', v)} />
            )}
            <AuthField label={L(STR.nchkColActivity, lang)} value={r.activity}
              onChange={v => setRow(r.id, 'activity', v)} />
            <div className="nchk-field-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <NchkDateField label={L(STR.nchkColFrom, lang)} value={r.from} onChange={v => setRow(r.id, 'from', v)} lang={lang} />
              <NchkDateField label={L(STR.nchkColTo, lang)} value={r.to} onChange={v => setRow(r.id, 'to', v)} lang={lang} />
            </div>
            <div className="nchk-field-grid" style={{ gridTemplateColumns: '1fr 2fr' }}>
              <label className="auth-field">
                <div className="auth-label mono">{L(STR.nchkColPoints, lang)}</div>
                <input className="auth-input" type="number" inputMode="decimal" value={r.points}
                  onChange={e => setRow(r.id, 'points', e.target.value)} />
              </label>
              <AuthField label={L(STR.nchkColOrg, lang)} value={r.org} onChange={v => setRow(r.id, 'org', v)} />
            </div>
          </div>
        ))}
      </div>

      <button type="button" className="btn btn-ghost btn-fill" style={{ marginTop: 10 }} onClick={addRow}>
        + {L(STR.nchkAddRow, lang)}
      </button>

      {/* Counting-rule disclaimer — this record is bucketed purely by activity
          DATE into the selected certificate's CNE window. The NCHK has not
          published how points are attributed across registrations, so the nurse
          (not the app) is responsible for confirming each activity is eligible
          for the registration they're declaring — and, for a dual-registered
          nurse, that the same activity legitimately counts toward both. */}
      <div className="onb-info-note" role="note" style={{ marginTop: 16 }}>
        {isZh
          ? '學分按活動日期計入所選證書的 CNE 週期。提交前，請自行確認每項活動符合該註冊部分的持續進修要求。'
          : 'Points are counted by activity date within the selected certificate\'s CNE cycle. Before submitting, please confirm each activity meets the CNE requirements of that registration.'}
        {showRole ? (isZh
          ? '同一活動若日期同時落入你另一張證書的週期，亦會計入該證書。'
          : ' An activity dated within your other certificate\'s cycle also counts toward it.') : ''}
      </div>

      {(() => {
        const readiness = window.auditReadiness
          ? window.auditReadiness({ profile, user: licUser, records, today: window.todayIso?.() }) : null;
        return readiness?.gaps?.length ? (
          <div className="onb-info-note" role="note" style={{ marginTop: 12 }}>
            {isZh
              ? `匯出前提醒：仍有 ${readiness.gaps.length} 項未齊備（例如缺證書或資料）。附件 A + B 仍可免費匯出。`
              : `Export check: ${readiness.gaps.length} audit gap${readiness.gaps.length === 1 ? '' : 's'} remain (such as missing evidence or details). Annex A + B stays free to export.`}
          </div>
        ) : null;
      })()}

      {/* ‼️ THE NAME HERE IS THE NAME ON THE PAYWALL. Pro is sold as
          「連證書一齊交 / Append certificates」; it used to be called
          「附上證書 / Append certificates」, so a subscriber went looking for a
          feature name that appeared nowhere in the app and concluded she had
          not got what she paid for. PRO_BENEFITS' `where` string points at this
          label — rename them in the same change or not at all. */}
      {showAuditPack && (
        <div ref={auditFocusRef} tabIndex={highlightAudit ? -1 : undefined}
          className={'card' + (highlightAudit ? ' pro-target-highlight' : '')}
          style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon name="cert" size={18} stroke={1.8} color="var(--primary)" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700 }}>{isZh ? '連證書一齊交（Vital Pro）' : 'Append certificates (Vital Pro)'}</div>
            <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.45, marginTop: 2 }}>
              {isZh ? '自動壓縮、對應附件 A 行號並併入同一份文件；PDF 會提示你另行附上。' : 'Compress images, match Annex A line numbers and merge into one document; PDFs are flagged for separate attachment.'}
            </div>
            {!isPro && canBuyPro && <button type="button" className="btn btn-ghost" style={{ marginTop: 8, fontSize: 12, padding: '6px 10px' }} onClick={() => setProOpen(true)}>{isZh ? '了解 Vital Pro' : 'Explore Vital Pro'}</button>}
          </div>
          <Toggle on={includeEvidence && isPro} disabled={!isPro || exporting}
            onChange={() => setIncludeEvidence(v => !v)} ariaLabel={isZh ? '連證書一齊交' : 'Append certificates'} />
        </div>
      )}
      {evidenceProgress && (
        <div className="muted-2" style={{ fontSize: 12, marginTop: 8 }}>
          {isZh ? `正在準備證書 ${evidenceProgress.current}/${evidenceProgress.total}…` : `Preparing certificates ${evidenceProgress.current}/${evidenceProgress.total}…`}
        </div>
      )}
      {evidenceSummary && (evidenceSummary.skippedPdf || evidenceSummary.skippedOther || evidenceSummary.total > evidenceSummary.limited) ? (
        <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.45, marginTop: 8 }}>
          {isZh
            ? `${evidenceSummary.items.length} 張圖片會併入${evidenceSummary.skippedPdf ? `；${evidenceSummary.skippedPdf} 張 PDF 請另行附上` : ''}${evidenceSummary.total > evidenceSummary.limited ? '；為控制檔案大小，最多併入 30 張圖片' : ''}。`
            : `${evidenceSummary.items.length} image${evidenceSummary.items.length === 1 ? '' : 's'} will be merged${evidenceSummary.skippedPdf ? `; attach ${evidenceSummary.skippedPdf} PDF certificate${evidenceSummary.skippedPdf === 1 ? '' : 's'} separately` : ''}${evidenceSummary.total > evidenceSummary.limited ? '; at most 30 images are merged to control file size' : ''}.`}
        </div>
      ) : null}
      {/* ProSheet, not PaywallSheet: if this ever opens for someone who is
          already subscribed (a stale render, a purchase in another tab), she
          sees her membership rather than a second price tag. `isPro` above
          updates itself from the broadcast, so no onPurchased handler. */}
      {proOpen && typeof ProSheet === 'function' && (
        <ProSheet lang={lang} profile={profile} trigger="audit" records={records}
          userId={profile?.id}
          initialEnt={proEnt}
          onClose={() => setProOpen(false)} />
      )}

      <div style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-primary btn-fill" onClick={generate} disabled={exporting}>
          {exporting ? (isZh ? '正在準備…' : 'Preparing…') : L(STR.nchkGenerate, lang)}
        </button>
      </div>
    </div>
  );
}

function NchkExportSheet({ open, onClose, user, profile, records = [], lang, session, highlightAudit = false, onHighlightConsumed, onProfileSaved }) {
  const [stage, setStage] = React.useState('mode');  // 'mode' | 'pick' | 'gate' | 'preview'
  const [prof, setProf] = React.useState(profile);
  // A dual-registered nurse (Part I + Part II) exports one form per certificate,
  // so pick which licence this export is for. Single-licence users skip 'pick'
  // and go straight through with the sole (primary) licence.
  const licenses = React.useMemo(
    () => (window.profileLicenses ? window.profileLicenses(prof) : []), [prof]);
  const [selLic, setSelLic] = React.useState(null);
  // Where the identity card returns on Back / after Save — 'mode'/'pick' when
  // reached on the way in, 'preview' when reopened via the preview's Edit button.
  const [gateReturn, setGateReturn] = React.useState('mode');

  // Reset to the chooser whenever the sheet (re)opens; resync the local profile.
  React.useEffect(() => {
    if (open) { setStage('mode'); setProf(profile); setSelLic(null); }
  }, [open, profile]);

  // Per-certificate: needs the identity card when the name OR *this* licence's own
  // registration number is missing (the second cert's number lives in license2,
  // not the primary license_no column).
  const needsGate = (p, lic) => !((p?.name_en || p?.name_zh) && (lic ? lic.licenseNo : p?.license_no));

  // After choosing the Official form: pick a licence first when the nurse holds
  // two, else go straight to the primary. `advance` continues from a chosen
  // licence into the identity card (missing name/reg) or the preview.
  const startFilled = () => {
    if (licenses.length > 1) { setStage('pick'); return; }
    advance(licenses[0] || null);
  };
  const advance = (lic) => {
    setSelLic(lic);
    setGateReturn(licenses.length > 1 ? 'pick' : 'mode');
    setStage(needsGate(prof, lic) ? 'gate' : 'preview');
  };

  // The identity card saved: adopt the updated profile, refresh the selected
  // licence descriptor from it (so the preview seeds from the just-saved name +
  // this certificate's number), then continue to the preview.
  const onIdentitySaved = (saved) => {
    setProf(saved);
    onProfileSaved && onProfileSaved(saved);
    const fresh = window.profileLicenses ? window.profileLicenses(saved) : [];
    const key = selLic?.key || 'primary';
    setSelLic(fresh.find(l => l.key === key) || selLic);
    setStage('preview');
  };

  const title = stage === 'preview' ? L(STR.nchkPreviewTitle, lang)
    : stage === 'gate' ? L(STR.nchkDetailsTitle, lang)
    : stage === 'pick' ? L(STR.nchkExportTitle, lang)
    : L(STR.nchkExportTitle, lang);

  return (
    <Sheet open={open} onClose={onClose} title={title}>
      <div style={{ padding: '4px 20px 24px' }}>
        {stage === 'mode' && (
          <NchkModeChooser lang={lang}
            onFilled={startFilled}
            onBlank={() => { window.exportBlankNchkPdf && window.exportBlankNchkPdf({ lang }); onClose && onClose(); }} />
        )}
        {stage === 'pick' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 8 }}>
            <p className="muted-2" style={{ fontSize: 12, marginTop: 0 }}>
              {lang === 'zh'
                ? '你持有多於一張執業證明書，請選擇要匯出的一張。'
                : 'You hold more than one practising certificate — choose which one to export.'}
            </p>
            {licenses.map(lic => (
              <button key={lic.key} type="button" className="btn btn-ghost btn-fill"
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3, padding: '14px 16px', height: 'auto', textAlign: 'left' }}
                onClick={() => advance(lic)}>
                <span style={{ fontWeight: 700 }}>{L(lic.role, lang) || (lang === 'zh' ? '執業證明書' : 'Practising certificate')}</span>
                <span style={{ fontSize: 12, opacity: 0.75, fontWeight: 400 }}>
                  {(lic.licenseNo ? lic.licenseNo + ' · ' : '') + `${fmtDate(lic.cycleStart, lang) || '—'} → ${fmtDate(lic.renewalDate, lang) || '—'}`}
                </span>
              </button>
            ))}
          </div>
        )}
        {stage === 'gate' && (
          <NchkProfileGateForm lang={lang} session={session} profile={prof}
            lic={selLic} showRole={licenses.length > 1}
            onCancel={() => setStage(gateReturn)}
            onSaved={onIdentitySaved} />
        )}
        {stage === 'preview' && (
          <NchkPreview lang={lang} user={user} profile={prof} records={records}
            lic={selLic} showRole={licenses.length > 1}
            highlightAudit={highlightAudit} onHighlightConsumed={onHighlightConsumed}
            onEditIdentity={() => { setGateReturn('preview'); setStage('gate'); }}
            onDone={() => onClose && onClose()} />
        )}
      </div>
    </Sheet>
  );
}

// Explainer revealed by the "?" on the History year card. Clears up why the
// completed COUNT and the points TOTAL can differ: completed counts everything
// finished (incl. later-dated courses marked done), while points only add up
// records the user is counting — and points can be turned off per record.
function RecordCountInfo({ lang }) {
  return (
    <div className="onb-info-note" role="note">
      {lang === 'zh' ? (
        <>
          <p><strong>已完成</strong>會計算你所有已完成的課程,包括日期較後但你已標記為完成的課程 — 所以這個數目可能與分數不同。</p>
          <p><strong>CNE 分</strong>只計算「計分」的紀錄。在任何紀錄的詳情頁關閉「計入 CNE 分數」,該紀錄仍會保留在你的紀錄中,但不會計入分數,亦不會出現在匯出的表格。</p>
        </>
      ) : (
        <>
          <p><strong>Completed</strong> counts every course you've finished — including ones dated later this cycle that you've marked done — so it can differ from your points.</p>
          <p><strong>CNE points</strong> only add up records that are counted. Open any record and turn off “Count toward CNE points” to keep it in your history without affecting your points or the export.</p>
        </>
      )}
    </div>
  );
}

function RecordScreen({ lang, setLang, onLog, onSignIn, session, profile, records = [], qualifications = [], walletItems = null, licenceEvidence = [], licenceEvidenceReady = false, onEvidenceChanged, qualificationsReady = true, onUploadCert, toggleCourseReaction, onUpdateRecord, onDeleteRecord, onCreateRecord, onSaveQualification, onUpdateQualification, onArchiveQualification, onDeleteQualificationVersion, onToggleEnrolled, enrolledIds, onOpenCourse, focusRecordId, onFocusRecordHandled, focusQualificationId, onFocusQualificationHandled, initialView, onInitialViewHandled, embedded = false, onEditProfile, onOpenPro }) {
  // ── Guest variant: intro + sample preview + sign-in CTA ───────────────
  // Goal: when a guest taps 記錄, they should understand WHAT this area
  // does for them before signing in — feature highlights + a faded preview
  // of the real timeline (using clearly-labelled sample data).
  //
  // ⚠️ This is a deferred render (a function called AFTER the hooks below),
  // NOT a top-of-component early return. RecordScreen calls 11 hooks
  // (React.useState/useEffect/useMemo/useCallback) that all live below;
  // gating them behind an early `if (!session) return` made the hook count
  // 0 for guests and 11 for signed-in, so the guest→signed-in sign-in
  // transition tore the tree down ("rendered more hooks than during the
  // previous render" — symptom: blank page right after email login, gone
  // after a manual refresh). Hooks now use `React.useX` member calls, so
  // react-hooks/rules-of-hooks enforces this — keep the guard at the bottom.
  const renderGuestView = () => {
    const sampleRows = [
      { date: '2025-09-12',
        provider: lang === 'zh' ? '香港護士管理局' : 'Nursing Council HK',
        title:    lang === 'zh' ? '感染控制基礎課程'   : 'Infection Control Fundamentals',
        category: 'core',     points: 6, cert: true, aiScan: true },
      { date: '2025-06-04',
        provider: lang === 'zh' ? '醫院管理局' : 'Hospital Authority',
        title:    lang === 'zh' ? '臨終護理溝通技巧' : 'End-of-Life Care Communication',
        category: 'elective', points: 6, cert: true },
      { date: '2025-03-21',
        provider: lang === 'zh' ? '香港大學護理學院' : 'HKU School of Nursing',
        title:    lang === 'zh' ? '糖尿病管理工作坊' : 'Diabetes Management Workshop',
        category: 'core',     points: 6, cert: false },
    ];
    const features = [
      { icon: 'book',
        title: lang === 'zh' ? '完成紀錄'     : 'Completion log',
        sub:   lang === 'zh' ? '按年份儲存所有完成的 CNE 課程,自動計算核心與選修週期學分。'
                              : 'Year-by-year log of every CNE course you finish, with core & elective cycle points tallied.' },
      { icon: 'sparkle',
        title: lang === 'zh' ? 'AI 智能識別'   : 'AI scan',
        sub:   lang === 'zh' ? '證書、課程海報或網址,AI 自動讀取名稱、日期及分數,免手動輸入。'
                              : 'Certificate, course poster or link — AI reads the title, dates and points so you skip the typing.' },
      { icon: 'upload',
        title: lang === 'zh' ? '證書上載'     : 'Certificate upload',
        sub:   lang === 'zh' ? '直接附加 PDF 或相片,加密儲存,查核時隨時取用。'
                              : 'Attach PDFs or photos to each record — encrypted and ready for audit anytime.' },
      { icon: 'download',
        title: lang === 'zh' ? '匯出官方文件'  : 'Export official documents',
        sub:   lang === 'zh' ? '一鍵生成 NCHK 附件 A + B PDF,續期呈交即用。'
                              : 'Generate the NCHK Annex A + B PDF in one tap, ready to submit at renewal.' },
      { icon: 'bell',
        title: lang === 'zh' ? '續期提醒'     : 'Renewal reminders',
        sub:   lang === 'zh' ? '在週期結束前提醒你還欠多少學分。'
                              : 'Get a nudge before your cycle deadline so you never miss renewal.' },
    ];

    return (
      <div className="scroll fade-in" style={{ paddingBottom: 32 }}>
        {!embedded && (
          <ScreenHeader
            title={L(STR.myRecord, lang)}
            eyebrow={lang === 'zh' ? '個人 CNE 紀錄' : 'Your CNE record'}
            actions={<LangPill lang={lang} setLang={setLang} />}
          />
        )}

        {/* Intro card */}
        <div style={{ padding: '8px 20px 0' }}>
          <div className="guest-card">
            <div className="gc-eyebrow">
              {lang === 'zh' ? '紀錄區' : 'Record'}
            </div>
            <h2 className="gc-title">
              {lang === 'zh' ? '把你的 CNE 進度集中在一個地方' : 'Your CNE journey, all in one place'}
            </h2>
            <p className="gc-sub">
              {lang === 'zh'
                ? '記錄已完成的課程、上傳證書、追蹤本週期所得學分,並設定續期提醒。所有資料均加密儲存於你的帳戶。'
                : 'Log completed courses, upload certificates, track cycle points, and set renewal reminders. Everything stays securely in your account.'}
            </p>
            <div className="gc-cta">
              <button className="btn btn-primary" onClick={onSignIn}>
                <Icon name="user" size={16} stroke={1.9} />
                {lang === 'zh' ? '登入' : 'Sign in'}
              </button>
              <button className="btn btn-ghost" onClick={onSignIn}>
                {lang === 'zh' ? '建立帳戶' : 'Create account'}
              </button>
            </div>
          </div>
        </div>

        {/* What this area helps with */}
        <div style={{ padding: '24px 20px 0' }}>
          <div className="eyebrow" style={{ marginBottom: 10 }}>
            {lang === 'zh' ? '此區域可協助你' : 'What this area helps with'}
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {features.map((f, i) => (
              <div key={i} className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '14px 16px' }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 11,
                  background: 'color-mix(in oklab, var(--accent) 12%, transparent)',
                  color: 'var(--accent)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Icon name={f.icon} size={18} stroke={1.7} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="h-row" style={{ fontSize: 14 }}>{f.title}</div>
                  <div className="muted" style={{ fontSize: 12.5, marginTop: 2, lineHeight: 1.45 }}>{f.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sample preview — clearly labelled, non-interactive */}
        <div style={{ padding: '26px 20px 0' }}>
          <div className="eyebrow" style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{lang === 'zh' ? '預覽' : 'Preview'}</span>
            <span className="mono muted-2" style={{
              fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase',
              padding: '2px 6px', border: '0.5px solid var(--border)', borderRadius: 4,
            }}>
              {lang === 'zh' ? '範例' : 'Sample'}
            </span>
          </div>

          <div
            aria-hidden="true"
            style={{
              pointerEvents: 'none',
              userSelect: 'none',
              opacity: 0.78,
              filter: 'saturate(0.92)',
            }}>
            {/* Year summary mock */}
            <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div>
                <div className="mono muted-2" style={{ fontSize: 9.5, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                  2025 {L(STR.points, lang)}
                </div>
                <div className="serif" style={{ fontSize: 44, lineHeight: 0.95, letterSpacing: '-0.02em', marginTop: 4 }}>
                  18
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                  3 {lang === 'zh' ? '項已完成' : 'completed'}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span className="chip" style={{ background: 'transparent', border: '0.5px solid var(--border)' }}>
                    <CatDot cat="core" size={6} />
                    {L(STR.core, lang)} <span className="mono" style={{ marginLeft: 3 }}>12</span>
                  </span>
                  <span className="chip" style={{ background: 'transparent', border: '0.5px solid var(--border)' }}>
                    <CatDot cat="elective" size={6} />
                    {L(STR.elective, lang)} <span className="mono" style={{ marginLeft: 3 }}>6</span>
                  </span>
                </div>
              </div>
              <div className="icon-btn" style={{ opacity: 0.5 }}>
                <Icon name="download" size={17} stroke={1.7} />
              </div>
            </div>

            {/* Sample timeline */}
            <div className="timeline" style={{ marginTop: 14 }}>
              {sampleRows.map((r, i) => (
                <div key={i} className="tl-item done" style={{ paddingBottom: i === sampleRows.length - 1 ? 0 : 18 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="mono muted-2" style={{ fontSize: 10, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 4 }}>
                        {fmtShort(r.date, lang)} · {r.provider}
                      </div>
                      <div className="h-row" style={{ fontSize: 14.5 }}>{r.title}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                        <span className="chip">
                          <CatDot cat={r.category} size={6} />
                          {L(STR[r.category], lang)}
                        </span>
                        {r.aiScan && (
                          <span className="chip" style={{
                            color: 'var(--accent)',
                            border: '0.5px solid color-mix(in oklab, var(--accent) 30%, transparent)',
                            background: 'color-mix(in oklab, var(--accent) 8%, var(--surface))',
                          }}>
                            <Icon name="sparkle" size={11} stroke={1.8} /> {lang === 'zh' ? 'AI 掃描' : 'AI scan'}
                          </span>
                        )}
                        {r.cert ? (
                          <span className="chip" style={{ color: 'var(--success)' }}>
                            <Icon name="check" size={11} stroke={2} /> {L(STR.cert, lang)}
                          </span>
                        ) : (
                          <span className="chip" style={{
                            color: 'var(--accent)',
                            border: '0.5px dashed color-mix(in oklab, var(--accent) 40%, transparent)',
                            background: 'color-mix(in oklab, var(--accent) 8%, var(--surface))',
                          }}>
                            <Icon name="upload" size={11} stroke={2} /> {L(STR.uploadCert, lang)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div className="serif" style={{ fontSize: 26, lineHeight: 0.95, letterSpacing: '-0.02em' }}>
                        +{r.points}
                      </div>
                      <div className="mono muted-2" style={{ fontSize: 9, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                        {L(STR.pts, lang)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div style={{ padding: '24px 20px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <button className="btn btn-primary" onClick={onSignIn} style={{ minWidth: 220 }}>
            <Icon name="user" size={16} stroke={1.9} />
            {lang === 'zh' ? '登入以開始記錄' : 'Sign in to start your record'}
          </button>
          <div className="muted-2" style={{ fontSize: 11.5 }}>
            {lang === 'zh' ? '免費 · 數分鐘即可建立帳戶' : 'Free · takes under a minute'}
          </div>
        </div>
      </div>
    );
  };

  const recordYearOptions = React.useMemo(() => {
    const licenses = window.profileLicenses ? window.profileLicenses(profile) : [];
    return window.recordYears
      ? window.recordYears(records, licenses, window.todayIso?.())
      : [new Date().getFullYear()];
  }, [records, profile]);
  // ‼️ The opening year comes from defaultRecordYear (data.js), NEVER from
  // recordYearOptions[0] — the list is sorted descending, so [0] is the
  // furthest-out (emptiest) year. Same helper in the fallback effect below.
  const [year, setYear] = React.useState(() => window.defaultRecordYear
    ? window.defaultRecordYear(recordYearOptions, window.todayIso?.())
    : recordYearOptions[0]);
  const [filter, setFilter] = React.useState('all');
  // Sub-section within Record: 'active' (in-progress + pending) / 'history' (timeline)
  const [recordView, setRecordView] = React.useState(initialView || 'active');
  // Reveal for the count explainer on the History year card — answers the
  // "why does 已完成紀錄 differ from 項已完成 / points?" question (12 vs 27).
  const [countInfoOpen, setCountInfoOpen] = React.useState(false);
  // Currently-edited record (opens RecordDetailSheet)
  const [editingRecord, setEditingRecord] = React.useState(null);
  // { courseId, title } | null — the just-completed catalog course whose
  // experience card is showing. Transient and inline; see ExperienceAskCard.
  const [experienceAsk, setExperienceAsk] = React.useState(null);
  // The record whose review sheet is open (second entry point, from 已完成紀錄).
  const [reviewFor, setReviewFor] = React.useState(null);
  // { [course_id]: { course_stars, editable } } for the CALLER only — one call
  // for the whole history list rather than one per row. Empty on any failure,
  // which just renders every row as "not yet rated".
  const [myReviews, setMyReviews] = React.useState({});
  // Reviews are switched off for this database (unmigrated, or a kill switch).
  // The ★ chip is hidden entirely rather than opening a sheet that cannot save.
  const [reviewsOff, setReviewsOff] = React.useState(false);
  const reloadMyReviews = React.useCallback(async () => {
    if (!session || !window.DB?.fetchMyCourseReviews) return;
    try {
      const { data, missing } = await window.DB.fetchMyCourseReviews();
      if (missing) { setReviewsOff(true); setMyReviews({}); return; }
      setReviewsOff(false);
      setMyReviews(data || {});
    } catch (_) { /* best-effort */ }
  }, [session]);
  React.useEffect(() => { reloadMyReviews(); }, [reloadMyReviews]);
  // Audit-readiness gap list — collapsed by default. MUST be declared up here
  // with the other hooks, never below the `if (!session)` guard: see the
  // renderGuestView comment about the hook-count tear-down bug.
  const [readinessOpen, setReadinessOpen] = React.useState(false);
  // Enrolled course chosen via 進行中 → 編輯紀錄 that has no record row yet —
  // opens the log sheet prefilled with that course, presented as 編輯紀錄.
  const [logCourse, setLogCourse] = React.useState(null);
  // NCHK Annex A + B export sheet (mode → optional gate → editable preview).
  const [nchkOpen, setNchkOpen] = React.useState(false);
  // Per-record upload state: which record is currently uploading + last-err
  const [uploadingId, setUploadingId] = React.useState(null);
  const [uploadErr, setUploadErr] = React.useState(null);
  const fileInputRef = React.useRef(null);
  const pendingRecordRef = React.useRef(null);

  // The selected year vanished from the list (profile edit, record deleted, a
  // new year turning over). Re-pick through the SAME helper as the initialiser
  // above — falling back to recordYearOptions[0] here would quietly re-introduce
  // the "opens on an empty future year" bug through the side door.
  React.useEffect(() => {
    if (recordYearOptions.includes(year)) return;
    setYear(window.defaultRecordYear
      ? window.defaultRecordYear(recordYearOptions, window.todayIso?.())
      : recordYearOptions[0]);
  }, [recordYearOptions, year]);

  // Deep-link from a certificate reminder → open that record's detail sheet so
  // the user can upload straight away. The parent clears focusRecordId once
  // consumed, so tapping the same reminder again re-opens it.
  React.useEffect(() => {
    if (!focusRecordId) return;
    const rec = (records || []).find(r => r.id === focusRecordId);
    if (rec) {
      // Align the underlying list with the target record so it's actually
      // visible once the detail sheet is dismissed: pick the matching view
      // and clear any category/year filter that could otherwise hide it.
      const todayIso = (typeof todayLocalIso === 'function')
        ? todayLocalIso() : new Date().toISOString().slice(0, 10);
      const done = (typeof isRecordCompleted === 'function') && isRecordCompleted(rec);
      const inProgress = rec.date && rec.date > todayIso && !done;
      setRecordView(inProgress ? 'active' : 'history');
      if (!inProgress) {
        setFilter('all');
        if (rec.year) setYear(rec.year);
      }
      setEditingRecord(rec);
    }
    onFocusRecordHandled && onFocusRecordHandled();
  }, [focusRecordId, records]);

  // Deep-link from the Home wallet (學分卡) → force the completed-history view.
  // RecordScreen stays mounted across tab switches, so a plain initial-state
  // default wouldn't re-apply on a second wallet tap; honouring the prop here
  // and clearing it (onInitialViewHandled) makes every tap land on 已完成記錄.
  React.useEffect(() => {
    if (!initialView) return;
    setRecordView(initialView);
    onInitialViewHandled && onInitialViewHandled();
  }, [initialView]); // eslint-disable-line react-hooks/exhaustive-deps

  // File picker — uses a single hidden <input>, retargeted per record.
  const handleUploadClick = (recordId) => {
    if (!onUploadCert) {
      // No upload handler wired → tell the user
      setUploadErr({ id: recordId, msg: lang === 'zh' ? '上傳功能未啟用' : 'Upload not available' });
      return;
    }
    setUploadErr(null);
    pendingRecordRef.current = recordId;
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so the same file can be re-selected
    const recordId = pendingRecordRef.current;
    pendingRecordRef.current = null;
    if (!file || !recordId) return;
    // Soft client-side limit — Supabase storage allows much more but the
    // UI should fail fast if the user picks a 50MB scan.
    if (file.size > 8 * 1024 * 1024) {
      setUploadErr({ id: recordId, msg: lang === 'zh' ? '檔案太大（>8MB）。相片會自動壓縮；如這是 PDF，請改用相片上載。' : 'File too large (>8MB). Photos are compressed automatically; if this is a PDF, try uploading a photo instead.' });
      return;
    }
    setUploadingId(recordId);
    try {
      const res = await onUploadCert(recordId, file);
      if (res?.error) {
        setUploadErr({ id: recordId, msg: res.error.message || String(res.error) });
      } else {
        // success — tiny inline toast
        const t = document.createElement('div');
        t.className = 'vital-toast';
        t.textContent = lang === 'zh' ? '證書已上傳' : 'Certificate uploaded';
        document.body.appendChild(t);
        setTimeout(() => t.remove(), 1600);
      }
    } catch (e) {
      setUploadErr({ id: recordId, msg: e.message || String(e) });
    } finally {
      setUploadingId(null);
    }
  };

  // Records split by completion: anything with an end date in the future
  // is treated as "in progress" and surfaces in the Active view; everything
  // else (today or earlier) goes to the History timeline.
  const TODAY_ISO = (typeof todayLocalIso === 'function') ? todayLocalIso() : new Date().toISOString().slice(0, 10);
  // A record is "in progress" only when its end date is still in the future
  // AND the user hasn't explicitly marked it complete. The explicit-complete
  // flag (`COMPLETED:1` in the notes blob) lets a user finish a course early
  // — all sessions ticked — without us having to overwrite the real end date.
  const _isDone = (r) => (typeof isRecordCompleted === 'function') && isRecordCompleted(r);
  // _isExcluded — record the user manually flipped to "don't count toward
  // points" (EXCLUDED:1). Points-only: such records STILL render in the
  // timeline and STILL count in the 已完成 tallies (badge + 項已完成); they are
  // only subtracted from totalPts and the per-category chips below.
  const _isExcluded = (r) => (typeof isRecordExcluded === 'function') && isRecordExcluded(r);
  const inProgressRecords = React.useMemo(
    () => (records || []).filter(x => x.date && x.date > TODAY_ISO && !_isDone(x)),
    [records, TODAY_ISO]
  );

  // Shared mark-complete handler — used by the in-progress card's
  // "標記為已完成" button AND the pending-verification card's
  // "標記為已完成 (稍後上傳證書)" escape hatch. Defined once at the
  // RecordScreen level so the two surfaces stay behaviourally identical.
  const markCourseComplete = React.useCallback(async (course, resolvedSessions) => {
    const todayIso = (typeof todayLocalIso === 'function') ? todayLocalIso() : new Date().toISOString().slice(0, 10);
    const _parse  = (typeof parseNoteBlob === 'function') ? parseNoteBlob : null;
    const _build  = (typeof buildNoteBlob === 'function') ? buildNoteBlob : null;

    if (course.isManual && course._record) {
      if (!onUpdateRecord) return;
      const rec = course._record;
      // ‼️ A points-pending record must NEVER complete silently. The nurse
      // logged this before it ran (enrolled-course capture from a poster or a
      // course link) and its CNE points weren't published yet, so completing
      // it now would file a 0-point record toward their licence renewal — the
      // exact quiet undercount PTSTBD:1 exists to prevent. Send her to the
      // editor instead; that is where the points field lives.
      if (typeof isRecordPointsPending === 'function' && isRecordPointsPending(rec)) {
        setEditingRecord(rec);
        if (window.showToast) {
          window.showToast(
            lang === 'zh' ? '請先填寫學分點數,再標記為已完成。'
                          : 'Add the points before marking this complete.',
            { tone: 'warning' });
        }
        return;
      }
      const parsed = _parse ? _parse(rec.notes) : { startDate: null, imagePaths: [], text: '' };
      // `...parsed` preserves the user's EXCLUDED:1 (don't-count) choice across a
      // mark-complete — do NOT drop the spread. The string fallback below keeps
      // rec.notes verbatim, so the flag survives there too.
      const newNotes = _build
        ? _build({ ...parsed, completed: true })
        : ((rec.notes ? rec.notes + '\n' : '') + 'COMPLETED:1');
      await onUpdateRecord(rec.id, { notes: newNotes });
      return;
    }
    if (!onCreateRecord) return;
    const titleObj    = course.title    || { en: '', zh: '' };
    const providerObj = course.provider || { en: '', zh: '' };
    const sessList = Array.isArray(resolvedSessions) && resolvedSessions.length
      ? resolvedSessions
      : ((typeof parseSessions === 'function') ? parseSessions(course) : []);
    const startIso = course.date    || sessList[0]?.iso                       || todayIso;
    const endIso   = course.endDate || sessList[sessList.length - 1]?.iso     || course.date || todayIso;
    const notesBlob = _build
      ? _build({ startDate: startIso, completed: true })
      : `START:${startIso}\nCOMPLETED:1`;
    await onCreateRecord({
      courseId:    course.id,
      title:       L(titleObj, lang),
      title_en:    titleObj.en || '',
      title_zh:    titleObj.zh || null,
      provider:    L(providerObj, lang),
      provider_en: providerObj.en || '',
      provider_zh: providerObj.zh || null,
      date:        endIso,
      points:      Number(course.points) || 0,
      category:    course.category || 'elective',
      source:      'catalog',
      notes:       notesBlob,
    });
    if (onToggleEnrolled && enrolledIds && enrolledIds.has(course.id)) {
      onToggleEnrolled(course.id);
    }
    // The completion moment — the one instant where "how was it?" is both
    // answerable and not an interruption. Set AFTER onCreateRecord resolves:
    // the server-side attendance gate requires the record to already exist, so
    // asking any earlier would offer a control that is guaranteed to 42501.
    // Catalog courses only — reactions are keyed on a catalogue course id, and
    // a manually typed course has none to key on.
    setExperienceAsk({ courseId: course.id, title: L(titleObj, lang) });
  }, [enrolledIds, lang, onCreateRecord, onUpdateRecord, onToggleEnrolled, setEditingRecord]);

  const yearRecord = React.useMemo(() => {
    // Completed timeline = records whose end date has passed OR that the user
    // has explicitly marked complete (early-finish via 標記為已完成).
    let r = (records || []).filter(x => x.date && (x.date <= TODAY_ISO || _isDone(x)));
    r = r.filter(x => x.year === year);
    if (filter !== 'all') r = r.filter(x => x.category === filter);
    return r;
  }, [year, filter, records, TODAY_ISO]);

  // ⚠️ HOOK-ORDERING CONSTRAINT — every hook above (React.useState/useEffect/
  // useMemo/useCallback) runs unconditionally so the hook count is identical
  // for guests and signed-in users. Render the guest variant HERE, after the
  // hooks — never as an early return above them. See the renderGuestView comment.
  if (!session) return renderGuestView();

  const u = userFromProfile(profile, records) || profileFromSession(session, records) || USER;
  const urgentRenewal = (window.profileLicenses ? window.profileLicenses(profile) : [])
    .map(lic => ({ lic, days: lic.renewalDate ? daysBetween(TODAY_ISO, lic.renewalDate) : Infinity }))
    .filter(x => x.days >= 0 && x.days <= 90)
    .sort((a, b) => a.days - b.days)[0] || null;
  // Points-only exclusion: the headline total drops manually-excluded records,
  // even though they remain visible in the timeline + the 項已完成 count.
  const totalPts = yearRecord.filter(r => !_isExcluded(r)).reduce((s, r) => s + r.points, 0);
  // Audit readiness. Plain const (not a memo) so it stays below the session
  // guard alongside urgentRenewal — the whole computation is a few array passes
  // over one cycle's records.
  const readiness = window.auditReadiness
    ? window.auditReadiness({ profile, user: u, records, today: TODAY_ISO })
    : null;
  // Eyebrow = EVERY certificate's CNE window, not just the primary one. A dual
  // registration's year chips below span both windows, so labelling the header
  // with licence [0] alone made it contradict the list right under it (chips
  // reaching 2029 beneath a "週期 2023–2026" header). Identical ranges — two
  // certificates renewing together — collapse back into one.
  const cycleRanges = (window.profileLicenses ? window.profileLicenses(profile) : [])
    .map(lic => (window.cneWindow
      ? window.cneWindow(lic?.cycleStart || null, lic?.renewalDate || null) : null))
    .filter(w => w && w.start && w.end)
    .map(w => `${w.start.slice(0, 4)}–${w.end.slice(0, 4)}`)
    .filter((range, i, all) => all.indexOf(range) === i);
  const cycleEyebrow = cycleRanges.length
    ? (lang === 'zh' ? `週期 ${cycleRanges.join(' · ')}` : `Cycle ${cycleRanges.join(' · ')}`)
    : (lang === 'zh' ? 'CNE 紀錄' : 'CNE records');

  return (
    <div className={'scroll fade-in' + (embedded ? '' : ' statusbar-capped')} style={{ paddingBottom: 24 }}>
      {!embedded && (
        <ScreenHeader
          title={L(STR.myRecord, lang)}
          eyebrow={cycleEyebrow}
          actions={<LangPill lang={lang} setLang={setLang} />}
        />
      )}

      {/* Hidden file input — single instance, retargeted per record via ref */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        style={{ display: 'none' }}
        onChange={onFileChosen}
      />

      {urgentRenewal && (
        <div style={{ padding: '10px 20px 0' }}>
          <button type="button" className="card" onClick={() => setNchkOpen(true)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit', border: '1px solid var(--primary)', background: 'var(--primary-soft-2)' }}>
            <div style={{ width: 38, height: 38, borderRadius: 12, flexShrink: 0, background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="download" size={18} stroke={1.9} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="h-row">{lang === 'zh' ? '準備續期文件' : 'Prepare your renewal documents'}</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>
                {lang === 'zh'
                  ? `距離執業證書到期尚餘 ${urgentRenewal.days} 日 · 匯出 NCHK 附件 A + B`
                  : `${urgentRenewal.days} days until certificate expiry · Export NCHK Annex A + B`}
              </div>
            </div>
            <Icon name="chev-r" size={15} stroke={1.8} color="var(--primary)" />
          </button>
          {onOpenPro && window.PurchaseAPI?.isSupported?.() && (
            <button type="button" className="btn btn-ghost btn-fill" style={{ marginTop: 8 }} onClick={onOpenPro}>
              <Icon name="shield" size={15} stroke={1.7} />
              {lang === 'zh' ? '用 Vital Pro 連證書一齊交' : 'Append your certificates with Vital Pro'}
            </button>
          )}
        </div>
      )}

      {/* Audit readiness — "if the Council asked tomorrow, could you hand your
          records over?". Every gap maps to a specific blank on Annex A or B
          (see auditReadiness in data.js), so the number can't drift into
          feel-good completeness theatre. Hidden at 100%: a permanent green
          badge is just noise once there is nothing to fix. */}
      {readiness && readiness.gaps.length > 0 && (
        <div style={{ padding: '10px 20px 0' }}>
          <button type="button" className="card" onClick={() => setReadinessOpen(o => !o)}
            aria-expanded={readinessOpen}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' }}>
            <div style={{ width: 38, height: 38, borderRadius: 12, flexShrink: 0, background: 'var(--surface-2, var(--surface))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 750 }}>{readiness.score}/{readiness.max}</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="h-row">{lang === 'zh' ? '查核準備度' : 'Audit readiness'}</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>
                {lang === 'zh'
                  ? `${readiness.gaps.length} 項待補 · 補齊後附件內容會更完整`
                  : `${readiness.gaps.length} item${readiness.gaps.length > 1 ? 's' : ''} to complete for a more complete NCHK export`}
              </div>
            </div>
            {/* Icon takes no style prop — rotate a wrapper instead. */}
            <span style={{ display: 'inline-flex', transform: readinessOpen ? 'rotate(180deg)' : 'none', transition: 'transform .18s' }}>
              <Icon name="chev-d" size={15} stroke={1.8} />
            </span>
          </button>
          {readinessOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              {readiness.gaps.map(g => {
                const tone = g.severity === 'high' ? 'danger' : g.severity === 'medium' ? 'warning' : 'muted';
                // Record-level gaps jump straight into that record's editor,
                // reusing the same state the focusRecordId deep link drives.
                const target = g.recordId ? (records || []).find(r => r.id === g.recordId) : null;
                const isProfileGap = ['name', 'regNo', 'cycle'].includes(g.id);
                const jump = target
                  ? () => { setRecordView('history'); setFilter('all'); setEditingRecord(target); }
                  : (isProfileGap && onEditProfile ? () => onEditProfile(g.id) : null);
                const Tag = jump ? 'button' : 'div';
                return (
                  <Tag key={g.id} type={jump ? 'button' : undefined} className="card readiness-gap"
                    onClick={jump || undefined}
                    style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', cursor: jump ? 'pointer' : 'default' }}>
                    <span className={'cap-pill tone-' + tone} style={{ flexShrink: 0, marginTop: 1 }}>
                      {g.severity === 'high' ? (lang === 'zh' ? '必要' : 'Required')
                        : g.severity === 'medium' ? (lang === 'zh' ? '建議' : 'Advised')
                        : (lang === 'zh' ? '次要' : 'Minor')}
                    </span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13 }}>{L(g.label, lang)}</div>
                      <div className="muted-2" style={{ fontSize: 11.5, marginTop: 2 }}>{L(g.fix, lang)}</div>
                      {jump ? (
                        <div className="readiness-gap-action">
                          {lang === 'zh' ? '前往處理' : 'Fix now'}
                        </div>
                      ) : null}
                    </div>
                    {jump ? <Icon name="chev-r" size={15} stroke={1.8} color="var(--fg-3)" /> : null}
                  </Tag>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* View switcher — "Active" (in-progress + pending verification) vs. "History".
          Wrapped in a sticky shell so it stays reachable as the user scrolls
          deeper into the active-course list (which can get tall once a few
          multi-session courses are enrolled). */}
      <div className="record-viewbar-sticky">
        <div className="record-viewbar">
          <button
            className={'record-viewbar-tab' + (recordView === 'active' ? ' on' : '')}
            onClick={() => setRecordView('active')}>
            <span className="record-viewbar-tab-label">{lang === 'zh' ? '進行中' : 'In progress'}</span>
            {(() => {
              const logged = inProgressRecords.length;
              // enrolledCourseCount, not enrolledIds.size — an enrolment whose
              // course has left the catalogue has no card in InProgressSection,
              // and counting it produced a badge of 2 over an empty list.
              // See resolveEnrolledCourses in data.js.
              const enrolled = enrolledCourseCount(enrolledIds);
              const total = logged + enrolled;
              // Single compact count. The In-progress list already merges
              // manual logs and enrolments, and two worded badges ("2 log" /
              // "3 enrol") left no room for the label on one line at 375px.
              // The split stays available in the aria-label.
              return total > 0
                ? <span className="record-viewbar-badge" aria-label={lang === 'zh'
                    ? `${total} 項進行中：${logged} 項紀錄，${enrolled} 項已報名`
                    : `${total} active: ${logged} in-progress records, ${enrolled} enrolled courses`}>{total}</span>
                : null;
            })()}
          </button>
          <button
            className={'record-viewbar-tab' + (recordView === 'history' ? ' on' : '')}
            onClick={() => setRecordView('history')}>
            <span className="record-viewbar-tab-label">{lang === 'zh' ? '已完成紀錄' : 'History'}</span>
            {(() => {
              const n = (records || []).filter(x => x.date && x.date <= TODAY_ISO).length;
              return n > 0 ? <span className="record-viewbar-badge">{n}</span> : null;
            })()}
          </button>
          <button
            className={'record-viewbar-tab' + (recordView === 'qualifications' ? ' on' : '')}
            onClick={() => setRecordView('qualifications')}>
            <span className="record-viewbar-tab-label">{lang === 'zh' ? '我的資格' : 'Qualifications'}</span>
          </button>
        </div>
      </div>

      {recordView === 'active' ? (
        <div style={{ padding: '4px 20px 0', display: 'flex', flexDirection: 'column', gap: 22 }}>
          {/* "How was it?" — above even the pending-verification block, and only
              for the seconds after a completion. This is the placement the
              feature lives or dies on: the identical chips sit at the bottom of
              the course detail page and have been used once across the whole
              catalogue. */}
          {/* ‼️ NOT gated on reviewsOff any more. That flag means the REVIEWS
              backend is unmigrated; this card writes a REACTION, a different
              table with a different RPC. Gating one on the other would hide a
              working ask because an unrelated feature was missing. */}
          {experienceAsk && typeof toggleCourseReaction === 'function'
            && typeof ExperienceAskCard === 'function' && (
            <ExperienceAskCard
              lang={lang}
              courseId={experienceAsk.courseId}
              title={experienceAsk.title}
              onToggleReaction={toggleCourseReaction}
              onDismiss={() => setExperienceAsk(null)} />
          )}

          {/* Pending verification — sits at the top because it needs action */}
          {typeof PendingVerificationSection === 'function' && (
            <PendingBlock
              lang={lang} enrolledIds={enrolledIds} records={records}
              onUploadCert={onUploadCert}
              onMarkComplete={markCourseComplete} />
          )}

          {/* Session calendar — month-grid overview of upcoming sessions
              across all enrolled courses. Collapsed by default; the
              header previews the next session so users see value before
              expanding. Hides itself when there's nothing upcoming. */}
          {typeof UpcomingCalendarSection === 'function' && (
            <UpcomingCalendarSection
              lang={lang}
              enrolledIds={enrolledIds}
              onOpenCourse={onOpenCourse}
            />
          )}

          {/* In-progress courses — catalog enrolments + manually-logged
              in-progress records, merged into a single list by
              InProgressSection. Tapping a card opens either the catalog
              detail action picker or the record editor directly. */}
          <div>
            <div className="eyebrow" style={{ marginBottom: 10 }}>
              {lang === 'zh' ? '正在參與的課程' : 'Active courses'}
            </div>
            {typeof InProgressSection === 'function' && (
              <InProgressSection
                lang={lang}
                enrolledIds={enrolledIds}
                inProgressRecords={inProgressRecords}
                records={records}
                profile={profile}
                onOpenCourse={onOpenCourse}
                onEditRecord={(record) => setEditingRecord(record)}
                onLogCourse={(c) => setLogCourse(c)}
                onScanCourse={() => onLog && onLog('ai')}
                onMarkComplete={markCourseComplete} />
            )}
          </div>

          {/* Quick log CTA */}
          <button
            type="button"
            className="record-log-cta"
            onClick={() => onLog && onLog()}>
            <Icon name="plus" size={16} stroke={2} />
            <span>{lang === 'zh' ? '手動記錄課程' : 'Manually log a course'}</span>
          </button>
        </div>
      ) : recordView === 'qualifications' ? (
        <QualificationsView
          lang={lang} qualifications={qualifications} items={walletItems} ready={qualificationsReady} records={records}
          onEditLicence={onEditProfile}
          licenceEvidence={licenceEvidence} licenceEvidenceReady={licenceEvidenceReady}
          onEvidenceChanged={onEvidenceChanged} userId={session?.user?.id}
          onSave={onSaveQualification} onUpdate={onUpdateQualification}
          onArchive={onArchiveQualification} onDeleteVersion={onDeleteQualificationVersion}
          onAiScan={() => onLog && onLog('ai:qualification')}
          focusQualificationId={focusQualificationId} onFocusHandled={onFocusQualificationHandled} />
      ) : (
      <F>
      {/* Year selector */}
      <div style={{ padding: '10px 20px 0', display: 'flex', gap: 6, overflowX: 'auto' }}>
        {recordYearOptions.map(y => (
          <button key={y}
            className={'filter-chip' + (year === y ? ' on' : '')}
            onClick={() => setYear(y)}>
            {y}
          </button>
        ))}
      </div>

      {/* Year summary card */}
      <div style={{ padding: '14px 20px 0' }}>
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div>
            <div className="mono muted-2" style={{ fontSize: 9.5, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
              {year} {L(STR.points, lang)}
            </div>
            <div className="serif" style={{ fontSize: 44, lineHeight: 0.95, letterSpacing: '-0.02em', marginTop: 4 }}>
              {totalPts}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>{yearRecord.length} {lang === 'zh' ? '項已完成' : 'completed'}</span>
              <button
                type="button"
                className="record-info-btn"
                aria-expanded={countInfoOpen}
                aria-label={lang === 'zh' ? '分數計算說明' : 'How points are counted'}
                onClick={() => setCountInfoOpen(o => !o)}>
                <span className="record-info-glyph" aria-hidden="true">?</span>
              </button>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {['core','elective','specialty'].map(cat => {
                const n = yearRecord.filter(r => r.category === cat && !_isExcluded(r)).reduce((s,r) => s + r.points, 0);
                if (n === 0) return null;
                return (
                  <span key={cat} className="chip" style={{ background: 'transparent', border: '0.5px solid var(--border)' }}>
                    <CatDot cat={cat} size={6} />
                    {L(STR[cat], lang)} <span className="mono" style={{ marginLeft: 3 }}>{n}</span>
                  </span>
                );
              })}
            </div>
          </div>
          <button className="icon-btn" title={L(STR.exportPdf, lang)}
            onClick={() => setNchkOpen(true)}>
            <Icon name="download" size={17} stroke={1.7} />
          </button>
        </div>
        {countInfoOpen && <RecordCountInfo lang={lang} />}
      </div>

      {/* Filter */}
      <div className="scroll-x" style={{ marginTop: 14 }}>
        {[
          ['all', STR.all], ['core', STR.core], ['elective', STR.elective], ['specialty', STR.specialty],
        ].map(([k, s]) => (
          <button key={k}
            className={'filter-chip' + (filter === k ? ' on' : '')}
            onClick={() => setFilter(k)}>
            {k !== 'all' && <CatDot cat={k} size={6} />}
            {L(s, lang)}
          </button>
        ))}
      </div>

      {/* Timeline */}
      <div style={{ padding: '10px 20px 0' }}>
        <div className="timeline">
          {yearRecord.map((r, i) => {
            // Excluded records still render here (and still count in 項已完成);
            // they're just visually marked and their points de-emphasised.
            const excluded = _isExcluded(r);
            return (
            <div key={r.id} className={'tl-item done is-clickable' + (excluded ? ' is-uncounted' : '')}
                 role="button"
                 tabIndex={0}
                 onClick={() => setEditingRecord(r)}
                 onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditingRecord(r); } }}
                 style={{ paddingBottom: i === yearRecord.length - 1 ? 0 : 18, cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mono muted-2" style={{ fontSize: 10, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 4 }}>
                    {fmtShort(r.date, lang)} · {L(r.provider, lang)}
                  </div>
                  <div className="h-row" style={{ fontSize: 14.5 }}>
                    {L(r.title, lang)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                    <span className="chip">
                      <CatDot cat={r.category} size={6} />
                      {L(STR[r.category], lang)}
                    </span>
                    {r.certificate ? (
                      <span className="chip" style={{ color: 'var(--success)' }}>
                        <Icon name="check" size={11} stroke={2} /> {L(STR.cert, lang)}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="chip cert-upload-chip"
                        onClick={(e) => { e.stopPropagation(); handleUploadClick(r.id); }}
                        disabled={uploadingId === r.id}
                        style={{
                          color: 'var(--accent)',
                          appearance: 'none',
                          border: '0.5px dashed color-mix(in oklab, var(--accent) 40%, transparent)',
                          background: 'color-mix(in oklab, var(--accent) 8%, var(--surface))',
                          cursor: uploadingId === r.id ? 'wait' : 'pointer',
                        }}
                        aria-label={L(STR.uploadCert, lang)}>
                        {uploadingId === r.id ? (
                          <>
                            <span className="cert-upload-spinner" aria-hidden="true" />
                            {lang === 'zh' ? '上傳中…' : 'Uploading…'}
                          </>
                        ) : (
                          <>
                            <Icon name="upload" size={11} stroke={2} /> {L(STR.uploadCert, lang)}
                          </>
                        )}
                      </button>
                    )}
                    {excluded && (
                      <span className="chip chip-uncounted">
                        {lang === 'zh' ? '不計分' : 'Not counted'}
                      </span>
                    )}
                    {/* Review entry point — the SECOND one, for everyone who
                        dismissed the card at the completion moment or logged
                        the course long after taking it.
                        Only for catalogue courses: a review is keyed on a
                        course id, and a hand-typed record has none.
                        stopPropagation because the whole row already opens the
                        record editor. */}
                    {r.courseId && !reviewsOff && typeof CourseReviewSheet === 'function' && (() => {
                      const mine = myReviews && myReviews[r.courseId];
                      // Her own review is always visible to her, whatever the
                      // moderation state — a rating she cannot see afterwards
                      // is indistinguishable from one that was dropped. The
                      // state is shown, not hidden: 🕓 awaiting review.
                      // ‼️ 🕓 MEANS "COMMENT AWAITING REVIEW", NEVER "rating
                      // awaiting review". Stars are not moderated, so a
                      // star-only review (body_status 'none') must show a plain
                      // count — the old code showed 🕓 on every new rating.
                      const pending = mine && mine.body_status === 'pending';
                      return (
                        <button type="button" className="chip"
                          onClick={(e) => { e.stopPropagation(); setReviewFor(r); }}
                          style={{ appearance: 'none', cursor: 'pointer',
                                   border: '0.5px solid var(--border)',
                                   background: 'var(--surface)',
                                   color: mine ? 'var(--warning)' : 'var(--fg-2)' }}
                          aria-label={mine
                            ? (lang === 'zh' ? '查看或修改你的評價' : 'View or edit your rating')
                            : (lang === 'zh' ? '評價課程' : 'Rate this course')}>
                          <Icon name="star" size={11} stroke={1.9}
                                fill={mine ? 'var(--warning)' : 'none'} />
                          {mine
                            ? ` ${mine.course_stars}${pending ? ' 🕓' : ''}`
                            : ` ${lang === 'zh' ? '評價' : 'Rate'}`}
                        </button>
                      );
                    })()}
                    {/* 學分待補 follows the record here. A course captured
                        before it ran can reach this timeline on its own —
                        simply by its end date passing — and a 0-point row is
                        otherwise indistinguishable from a real one. The chip
                        is the only thing standing between "points weren't
                        published yet" and a silently undercounted cycle. */}
                    {typeof isRecordPointsPending === 'function' && isRecordPointsPending(r) && (
                      <span className="chip" style={{
                        color: 'var(--warning)',
                        background: 'color-mix(in oklch, var(--warning) 12%, transparent)',
                        border: '0.5px solid color-mix(in oklch, var(--warning) 35%, transparent)',
                      }}>
                        <Icon name="shield" size={11} stroke={1.9} /> {L(STR.pointsTbdChip, lang)}
                      </span>
                    )}
                  </div>
                  {uploadErr && uploadErr.id === r.id && (
                    <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 6 }}>
                      {uploadErr.msg}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0, opacity: excluded ? 0.45 : undefined }}>
                  <div className="serif" style={{ fontSize: 26, lineHeight: 0.95, letterSpacing: '-0.02em', textDecoration: excluded ? 'line-through' : undefined }}>
                    +{r.points}
                  </div>
                  <div className="mono muted-2" style={{ fontSize: 9, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                    {L(STR.pts, lang)}
                  </div>
                </div>
              </div>
            </div>
            );
          })}
          {yearRecord.length === 0 && (
            <div className="muted" style={{ textAlign: 'center', padding: 40 }}>
              {/* The chip above is a CALENDAR YEAR, not a cycle — the old copy
                  ("此週期未有記錄") named the wrong unit, and on a year the nurse
                  never picked it read like their history had vanished. A year
                  still ahead of today says so plainly instead: nothing is
                  missing, it simply has not happened yet. */}
              {year > Number(TODAY_ISO.slice(0, 4))
                ? (lang === 'zh' ? `${year} 年尚未開始` : `${year} hasn’t started yet`)
                : (lang === 'zh' ? `${year} 年未有記錄` : `No records in ${year}`)}
            </div>
          )}
        </div>
      </div>
      </F>
      )}

      {/* Record detail / edit sheet */}
      {editingRecord && typeof RecordDetailSheet === 'function' && (
        <RecordDetailSheet
          record={editingRecord}
          lang={lang}
          onClose={() => setEditingRecord(null)}
          onUploadCert={onUploadCert}
          onSave={onUpdateRecord}
          onDelete={onDeleteRecord} />
      )}

      {logCourse && typeof LogSheet === 'function' && (
        <LogSheet
          lang={lang}
          profile={profile}
          initialCourse={logCourse}
          onClose={() => setLogCourse(null)}
          onSave={onCreateRecord}
          onSaveQualification={onSaveQualification} />
      )}

      {/* Review sheet — second entry point, opened from a row in 已完成紀錄.
          A Sheet is right here and wrong at the completion moment: app.jsx's
          one-ask-per-session budget governs UNSOLICITED asks, and this one is
          a tap she chose to make. */}
      {reviewFor && typeof CourseReviewSheet === 'function' && (
        <CourseReviewSheet
          open={true}
          lang={lang}
          courseId={reviewFor.courseId}
          title={L(reviewFor.title, lang)}
          onSaved={reloadMyReviews}
          onClose={() => { setReviewFor(null); reloadMyReviews(); }} />
      )}

      <NchkExportSheet open={nchkOpen} onClose={() => setNchkOpen(false)}
        user={u} profile={profile} records={records} lang={lang} session={session} />
    </div>
  );
}

// PendingBlock — wraps PendingVerificationSection with a header. The
// visibility check has to use the SAME session resolution
// (resolveSessions, custom-aware) as the inner section, otherwise the
// header can render with an empty body when the user has edited a
// course's schedule — looks like a blank section to the user.
function PendingBlock({ lang, enrolledIds, records, onUploadCert, onMarkComplete }) {
  const today = new Date().toISOString().slice(0, 10);
  // Subscribe to custom-session changes so the header tracks edits made
  // in the in-progress card's SessionScheduleDialog without a refresh.
  // (`useCustomSessions` is defined in record-extras.jsx, which loads
  // before screens.jsx in both dev and prod — no defensive guard needed,
  // and a ternary around a hook violates Rules of Hooks.)
  const [customAll] = useCustomSessions();
  const catalogueVersion = window.CATALOGUE_VERSION || 0;
  const pendingExists = React.useMemo(() => {
    const loggedIds = new Set((records || []).map(r => r.courseId).filter(Boolean));
    const resolver = (typeof resolveSessions === 'function')
      ? (c) => resolveSessions(c, customAll)
      : (typeof parseSessions === 'function')
        ? (c) => parseSessions(c)
        : () => [];
    // Same resolver as the inner section (active ∪ archived) — see the header
    // note: divergence here renders a header above an empty body.
    return resolveEnrolledCourses(enrolledIds).courses.some(c => {
      if (loggedIds.has(c.id))    return false;
      const ses = resolver(c);
      const last = ses[ses.length - 1];
      return last && last.iso < today;
    });
  }, [enrolledIds, records, customAll, catalogueVersion]);
  if (!pendingExists) return null;
  return (
    <div>
      <div className="eyebrow" style={{ marginBottom: 10, color: 'var(--accent)' }}>
        {lang === 'zh' ? '⚠ 待上傳證書' : 'Awaiting verification'}
      </div>
      <PendingVerificationSection
        lang={lang}
        enrolledIds={enrolledIds}
        records={records}
        onUploadCert={onUploadCert}
        onMarkComplete={onMarkComplete}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SAVED (sheet)
// ─────────────────────────────────────────────────────────────────────────────
function SavedSheet({ lang, savedIds, onOpenCourse, onClose }) {
  const saved = COURSES.filter(c => savedIds.has(c.id));
  return (
    <Sheet open={true} onClose={onClose} title={L(STR.saved, lang)}>
      <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {saved.length === 0
          ? <div className="muted" style={{ textAlign: 'center', padding: 40 }}>
              {lang === 'zh' ? '尚未收藏任何課程' : 'You haven\u2019t saved any courses yet'}
            </div>
          : saved.map(c => (
              <CourseCard key={c.id} course={c} lang={lang} onClick={() => { onClose(); onOpenCourse(c.id); }} />
            ))}
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PRIVACY sheet — full Privacy Policy, surfaced from Profile → 私隱與資料.
// Kept inline (not fetched from PRIVACY.md) so the text is available
// offline and inside the Capacitor build without any network roundtrip.
// Keep this in sync with PRIVACY.md when either changes.
// ─────────────────────────────────────────────────────────────────────────────
function PrivacySheet({ lang, onClose }) {
  const zh = lang === 'zh';
  const dataRows = zh ? [
    { d: '電郵地址', w: '登入識別、密碼重設;以 Apple 登入時可能提供轉發地址 (*@privaterelay.appleid.com),我們會原樣接收並儲存。', t: '註冊或首次以 Apple 登入時' },
    { d: '姓名 (中、英文)、執照編號、醫院、職位', w: '個人化檔案頁面與可列印 CNE 摘要', t: '註冊流程中' },
    { d: 'CNE 課程紀錄 (名稱、主辦機構、完成日期、學分、類別、選填證書圖片)', w: '使用者的 CNE 紀錄 — 本 App 的核心功能', t: '使用者建立紀錄時' },
    { d: '已收藏課程、已報名課程、提醒事項', w: '書籤與未來日程', t: '使用者點擊收藏 / 報名 / 設定提醒時' },
    { d: 'APNs 裝置 Token', w: '在使用者於檔案 → 提醒中明確啟用時,用於發送課程提醒推送', t: '首次啟用提醒時' },
    { d: '認證 Token (refresh + access)', w: '保持使用者在多次啟動之間維持登入狀態', t: '登入時' },
  ] : [
    { d: 'Email address', w: 'Sign-in identifier; password recovery; Sign in with Apple may provide a private-relay address (*@privaterelay.appleid.com) which we accept and store as-is', t: 'At sign-up / first SIWA' },
    { d: 'Name (English, Chinese), license number, hospital, role', w: 'Personalising the profile screen and printable CNE summary', t: 'During onboarding' },
    { d: 'CNE course records (title, provider, date completed, points, category, optional certificate image)', w: "The user's CNE log — the core feature of the app", t: 'Whenever the user logs a record' },
    { d: 'Saved courses, enrolled courses, reminders', w: 'Bookmarks and forward-looking schedule', t: 'When the user taps Save / Enrol / sets a reminder' },
    { d: 'APNs device token', w: 'Sending course reminder push notifications when the user has explicitly enabled them in Profile → Reminders', t: 'First time the user enables Reminders' },
    { d: 'Authentication tokens (refresh + access)', w: 'Keeping the user signed in across launches', t: 'At sign-in' },
  ];

  return (
    <Sheet open={true} onClose={onClose} title={zh ? '私隱政策' : 'Privacy Policy'}>
      <div className="privacy-doc" style={{ padding: '0 20px 28px' }}>
        <div className="privacy-eyebrow">
          {zh ? '生效日期:2026-05-23' : 'Effective date: 2026-05-23'}
        </div>
        <p className="privacy-lead">
          {zh
            ? 'Vital(「我們」、「本 App」)是專為香港註冊護士提供的持續護理教育(CNE)伴侶 App。本頁說明我們收集哪些資料、為何收集、儲存於何處,以及如何刪除。'
            : 'Vital ("we", "us", "the app") is a continuing nursing education (CNE) companion for Hong Kong-registered nurses. This page explains what data we collect, why, where it lives, and how to delete it.'}
        </p>
        <p className="privacy-lead">
          {zh
            ? '如有任何疑問,請電郵至 info@vitalcne.com — 詳見支援頁面。'
            : 'If anything below is unclear, write to info@vitalcne.com — see Support.'}
        </p>

        <h3 className="privacy-h">{zh ? '1. 我們收集的資料及原因' : '1. Data we collect and why'}</h3>
        <p>
          {zh
            ? 'Vital 只收集使用者所選用功能所需的資料。'
            : 'Vital collects only the data needed for the features the user opts in to.'}
        </p>
        <dl className="privacy-table">
          {dataRows.map((r, i) => (
            <div key={i} className="privacy-row">
              <dt>{r.d}</dt>
              <dd>
                <div><strong>{zh ? '用途:' : 'Why: '}</strong>{r.w}</div>
                <div><strong>{zh ? '時機:' : 'When: '}</strong>{r.t}</div>
              </dd>
            </div>
          ))}
        </dl>
        <p>
          {zh
            ? '我們不收集:位置、健康資料、聯絡人、行事曆、相片(除使用者主動上傳的證書外)、咪高峰、相機(除檔案選擇器外)、廣告識別碼或分析事件。'
            : 'We do not collect: location, health data, contacts, calendar, photos (beyond the certificate the user explicitly uploads), microphone, camera beyond file picker, advertising identifiers, or analytics events.'}
        </p>

        <h3 className="privacy-h">{zh ? '2. 資料儲存位置' : '2. Where data is stored'}</h3>
        <ul className="privacy-list">
          <li>
            <strong>{zh ? '伺服器端 (Supabase, PostgreSQL + Storage):' : 'Server-side (Supabase, PostgreSQL + Storage): '}</strong>
            {zh
              ? '檔案資料、紀錄、證書、已收藏 / 已報名課程、提醒、裝置 Token。每一列均受 Row-Level Security 保護,只有資料擁有者本人可存取;Service-role 金鑰絕不會嵌入用戶端。'
              : 'profile, records, certificates, saved/enrolled courses, reminders, device tokens. Row-Level Security restricts every row to its owning user; the service-role key is never embedded in the client.'}
          </li>
          <li>
            <strong>{zh ? '裝置端 (iOS Keychain):' : 'On-device (iOS Keychain): '}</strong>
            {zh
              ? '只在使用者啟用生物識別 (Face ID / Touch ID) 登入時,儲存 Supabase 的 refresh token。這是裝置上唯一儲存的機密資料。Keychain 屬於 App 專用、由 iOS 加密,並透過 Secure Enclave 控制生物識別讀取。登出或刪除帳戶時清除。'
              : "the Supabase refresh token, only when the user has enabled biometric (Face ID / Touch ID) login. This is the only secret stored on-device. Keychain is per-app, encrypted at rest by iOS, and tied to the device's secure enclave for biometric-gated reads. Wiped on sign-out and on account deletion."}
          </li>
          <li>
            <strong>{zh ? '裝置端 (Capacitor Preferences / localStorage):' : 'On-device (Capacitor Preferences / localStorage): '}</strong>
            {zh
              ? '只儲存非機密的偏好設定 — 介面語言、主題、上次登入方式(「email」/「apple」)、生物識別啟用旗標、課程目錄離線快取。除已公開的課程資料外,不會儲存任何個人資料。'
              : "non-secret preferences only — UI language, theme, last login method (\"email\" / \"apple\"), biometric-enrolment boolean, course-catalogue cache for offline browsing. No personal data is held in these stores beyond what's already public in the course catalogue."}
          </li>
        </ul>

        <h3 className="privacy-h">{zh ? '3. 推送通知' : '3. Push notifications'}</h3>
        <p>
          {zh
            ? '推送通知預設為關閉。在檔案 → 提醒中啟用時,iOS 會請求授權並產生 APNs 裝置 Token,該 Token 會連同你的使用者 ID 一同儲存於伺服器。關閉開關(或登出 / 刪除帳戶)會立即將 Token 從伺服器移除。iOS 系統層級的通知權限只能在 iOS 設定中撤回 — 詳見支援頁面。'
            : 'Push notifications are off by default. Enabling them in Profile → Reminders causes iOS to ask for permission and produce an APNs device token, which we store linked to your user ID. Disabling the toggle (or signing out, or deleting the account) removes the token from our server immediately. iOS notification permission itself can only be revoked via iOS Settings — see Support for the path.'}
        </p>

        <h3 className="privacy-h">{zh ? '4. 以 Apple 登入' : '4. Sign in with Apple'}</h3>
        <p>
          {zh
            ? '以 Apple 登入時,Apple 可能透過私密轉發地址轉送訊息。我們會儲存 Apple 提供的地址。除了 Supabase 用以識別帳戶的標準 OpenID-Connect sub 之外,我們不會收到你的 Apple ID 或其他 Apple 端識別資料。'
            : 'When you sign in with Apple, Apple may relay messages through a private address. We store whatever address Apple gives us. We do not receive your Apple ID or any other Apple-side identifier beyond the standard OpenID-Connect sub claim used by Supabase to identify your account.'}
        </p>
        <p>
          {zh
            ? '若你在 Apple 登入面板選擇「顯示我的電郵」,我們會收到並儲存你的真實電郵地址。若你選擇「隱藏我的電郵」,我們會收到並儲存 @privaterelay.appleid.com 的地址,Apple 會在不向我們披露真實郵箱的情況下將郵件轉發給你。'
            : "If you choose \"Show My Email\" in Apple's sign-in sheet, we receive and store your real email. If you choose \"Hide My Email\", we receive and store the @privaterelay.appleid.com address, and Apple forwards mail to your real inbox without ever revealing it to us."}
        </p>

        <h3 className="privacy-h">{zh ? '5. 刪除' : '5. Deletion'}</h3>
        <ul className="privacy-list">
          <li>
            <strong>{zh ? '登出 ' : 'Sign out '}</strong>
            {zh
              ? '會清除本機 session 及生物識別 Keychain 項目,伺服器資料維持不變。'
              : 'clears the local session and biometric Keychain entry. Server data is unchanged.'}
          </li>
          <li>
            <strong>{zh ? '刪除帳戶 (檔案 → 刪除帳戶) ' : 'Delete account (Profile → Delete account) '}</strong>
            {zh
              ? '會永久且不可復原地刪除:'
              : 'permanently and irrevocably deletes:'}
            <ul className="privacy-list privacy-list-nested">
              <li>{zh ? '你在 Supabase 的 auth.users 列(連帶刪除檔案、紀錄、報名、已收藏課程、提醒、裝置 Token)' : 'your auth.users row in Supabase (cascading to profile, records, enrollments, saved courses, reminders, device tokens)'}</li>
              <li>{zh ? '你的使用者資料夾下,certificates 儲存桶中的所有物件' : 'every object in the certificates storage bucket under your user folder'}</li>
              <li>{zh ? '本機生物識別 Keychain 項目、本機 Preferences 快取、所有已排程的本機通知,以及所有 APNs 裝置 Token' : 'the local biometric Keychain entry, the local Preferences cache, any scheduled local notifications, and any APNs device tokens'}</li>
            </ul>
          </li>
        </ul>
        <p>
          {zh
            ? '刪除流程由伺服器端的 Edge Function 處理,先驗證你的身份,然後依序執行儲存與資料庫的連帶刪除,只有每一步都成功時才回傳成功。'
            : 'The deletion is processed by a server-side Edge Function that verifies your identity, performs the storage and database cascade in a defined order, and returns success only if every step completed.'}
        </p>
        <p>
          {zh
            ? '帳戶一經刪除,如需繼續使用 App 必須重新註冊;我們不會保留任何可復原的備份。'
            : 'If your account is deleted you will need to sign up again to use the app; we keep no recoverable backup of your data.'}
        </p>

        <h3 className="privacy-h">{zh ? '6. 資料共享' : '6. Sharing'}</h3>
        <p>
          {zh
            ? '我們不會出售、出租或與任何第三方共享使用者資料。下列基礎設施供應商以服務提供者身份為我們處理資料:'
            : 'We do not sell, rent, or share user data with any third party. The following infrastructure providers process data on our behalf in their roles as service providers:'}
        </p>
        <ul className="privacy-list">
          <li><strong>Supabase</strong> — {zh ? '託管的 Postgres、Storage、Auth 及 Edge Functions。' : 'managed Postgres, Storage, Auth, and Edge Functions.'}</li>
          <li><strong>Apple Push Notification service</strong> — {zh ? '在啟用時負責推送通知的傳遞。傳送至 Apple 的只有裝置 Token 與通知內容。' : 'delivery of push notifications when enabled. Only the device token + payload reach Apple.'}</li>
        </ul>
        <p>
          {zh
            ? '我們不整合任何第三方分析、廣告或歸因 SDK。'
            : 'We do not integrate any third-party analytics, advertising, or attribution SDKs.'}
        </p>

        <h3 className="privacy-h">{zh ? '7. 兒童' : '7. Children'}</h3>
        <p>
          {zh
            ? 'Vital 服務對象為註冊或學生護士,並非針對 13 歲以下兒童。如你認為有 13 歲以下兒童向我們提供了資料,請聯絡我們,我們將立即刪除。'
            : 'Vital is intended for registered or student nurses and is not directed at children under 13. If you believe a child under 13 has provided us data, contact us and we will delete it.'}
        </p>

        <h3 className="privacy-h">{zh ? '8. 政策變更' : '8. Changes to this policy'}</h3>
        <p>
          {zh
            ? '重大變更會在 App 內公告,並透過已登記的電郵(如有)通知。非重大的釐清更新可能會在更新生效日期後直接套用。最新版本始終可於本網址查閱,並由 App Store Connect 及檔案 → 私隱與資料 連結。'
            : 'Material changes will be announced in-app and via the email on file (if any). Non-material clarifications may be made silently with the effective date updated. The current version is always at this URL and linked from App Store Connect and Profile → Privacy & data.'}
        </p>

        <h3 className="privacy-h">{zh ? '9. 聯絡' : '9. Contact'}</h3>
        <p>
          {zh
            ? '如有任何私隱問題、資料匯出請求或其他事宜,請電郵至 '
            : 'For privacy questions, data export requests, or anything else: '}
          <a href="mailto:info@vitalcne.com">info@vitalcne.com</a>
        </p>
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// REMINDERS sheet
// ─────────────────────────────────────────────────────────────────────────────
function RemindersSheet({ lang, reminders = [], onClose, onOpenCourse, onRouteReminder }) {
  const iconFor = (kind) => kind === 'enrolled' ? 'calendar' : kind === 'cert' ? 'upload' : 'clock';
  const list = reminders.length ? reminders : REMINDERS;
  const route = (rm) => {
    onClose && onClose();
    if (onRouteReminder) return onRouteReminder(rm);
    // Fallback if no router supplied: open course detail if we have it.
    if (rm.courseId && onOpenCourse) onOpenCourse(rm.courseId);
  };
  return (
    <Sheet open={true} onClose={onClose} title={lang === 'zh' ? '提醒' : 'Reminders'}>
      <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {list.length === 0 && (
          <div className="muted" style={{ textAlign: 'center', padding: 30 }}>
            {lang === 'zh' ? '暫無提醒' : 'No reminders yet'}
          </div>
        )}
        {list.map(rm => (
          <M.button key={rm.id}
            type="button"
            onClick={() => route(rm)}
            className="card reminder-row"
            whileTap={{ scale: 0.985 }}
            whileHover={{ x: 2 }}
            transition={{ duration: 0.16 }}
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              appearance: 'none', font: 'inherit', color: 'inherit',
              textAlign: 'left', cursor: 'pointer', width: '100%',
            }}>
            <div style={{
              width: 38, height: 38, borderRadius: 12,
              background: rm.kind === 'renewal' ? 'var(--accent-soft)' : 'var(--primary-soft)',
              color: rm.kind === 'renewal' ? 'var(--accent)' : 'var(--primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Icon name={iconFor(rm.kind)} size={18} stroke={1.6} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="h-row" style={{ fontSize: 14 }}>{L(rm.title, lang)}</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{L(rm.sub, lang)}</div>
            </div>
            <Icon name="chev-r" size={16} color="var(--fg-3)" stroke={1.6} />
          </M.button>
        ))}
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// <RemindersInlineList> — same row visual as RemindersSheet but inline,
// for placing inside Home under 為你推薦. Returns null when the list
// is empty so the host can decide whether to render a header.
// ─────────────────────────────────────────────────────────────────────────────
function RemindersInlineList({ lang, reminders = [], onRouteReminder, max = 4 }) {
  const iconFor = (kind) => kind === 'enrolled' ? 'calendar' : kind === 'cert' ? 'upload' : 'clock';
  if (!reminders || reminders.length === 0) return null;
  const list = reminders.slice(0, max);
  return (
    <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {list.map(rm => (
        <M.button key={rm.id}
          type="button"
          onClick={() => onRouteReminder && onRouteReminder(rm)}
          className="card reminder-row"
          whileTap={{ scale: 0.985 }}
          whileHover={{ x: 2 }}
          transition={{ duration: 0.16 }}
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 14px',
            appearance: 'none', font: 'inherit', color: 'inherit',
            textAlign: 'left', cursor: 'pointer', width: '100%',
          }}>
          <div style={{
            width: 34, height: 34, borderRadius: 11,
            background: rm.kind === 'renewal' ? 'var(--accent-soft)' : 'var(--primary-soft)',
            color: rm.kind === 'renewal' ? 'var(--accent)' : 'var(--primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon name={iconFor(rm.kind)} size={16} stroke={1.7} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="h-row" style={{ fontSize: 13.5 }}>{L(rm.title, lang)}</div>
            <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>{L(rm.sub, lang)}</div>
          </div>
          <Icon name="chev-r" size={14} color="var(--fg-3)" stroke={1.7} />
        </M.button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// <RolloverSheet> — confirm a licence renewal and roll the cycle forward.
//
// One write, four effects (single upsert so it can't half-apply):
// The six outcomes performLicenceRollover can return, in the nurse's words.
// ‼️ EVERY SCREEN USES THIS ONE MAPPER. Two screens each interpreting
// 23514/42501/40001 for themselves is how one ends up failing closed while the
// other invents a fallback — and the difference would only show up in an audit.
function rolloverMessage(status, isZh) {
  switch (status) {
    case 'identityNeedsResolution':
      return isZh
        ? '你的執業資格類別剛有更改，需要先確認，之後才能結算這個週期。'
        : 'Your registration type changed and needs confirming before this cycle can be closed.';
    case 'staleCycle':
      return isZh
        ? '這個週期已經被更新過，請重新開啟此頁再試。'
        : 'This cycle has already changed — reopen this page and try again.';
    case 'identityUnavailable':
      return isZh
        ? '找不到此執業證書的資格紀錄，請先在個人資料補回證書資料。'
        : 'No licence record for this certificate — add its details in your profile first.';
    default:
      return isZh ? '無法儲存，請稍後再試。' : 'Could not save. Please try again.';
  }
}

//   1. the closing cycle is APPENDED to profiles.cycle_history (audit + the
//      NCHK export for the cycle being submitted must survive the date change);
//   2. cycle_start / renewal_date advance to the new certificate period;
//   3. first_pc clears — the first-PC CNE exemption legally ends with the
//      first certificate, so it must not leak into cycle 2 (where CNE applies);
//   4. baseline_points resets to 0 (points are per-cycle).
// Records are NOT touched: the ring recomputes from the new CNE window, and the
// old cycle's numbers stay reachable through cycle_history.
// Multi-cycle-stale profiles (live data has renewal dates back to 2002 — nurses
// who entered an ORIGINAL registration period) are prefilled at the CURRENT
// 3-year block via nextCycleStartFrom, with neutral copy: their data is old,
// not their licence.
// ─────────────────────────────────────────────────────────────────────────────
function RolloverSheet({ lang, session, profile, records = [], licKey = 'primary', onSaved, onClose }) {
  const isZh = lang === 'zh';
  const lic = (window.profileLicenses(profile) || []).find(l => l.key === licKey) || null;
  const prefillStart = lic && lic.renewalDate ? window.nextCycleStartFrom(lic.renewalDate) : '';
  const [form, setForm] = React.useState(() => ({
    cycle_start: prefillStart || '',
    renewal_date: prefillStart ? window.pcEndFromStart(prefillStart) : '',
  }));
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  if (!lic || !lic.renewalDate || !session?.user) return null;
  const catchUp = daysBetween(lic.renewalDate, prefillStart) > 2; // prefill skipped ≥1 whole block
  // New cycle must start after the closed one ends, and be a valid range.
  const canSave = !!form.cycle_start && !!form.renewal_date &&
    form.cycle_start > lic.renewalDate && form.renewal_date > form.cycle_start;
  // What the requirement becomes once the exemption ends — the STORED
  // required_points (role-seeded), not the derived 0.
  const nextRequired = licKey === 'secondary'
    ? (Number(profile?.license2?.required_points) || 45)
    : (Number(profile?.required_points) || 45);

  const save = async () => {
    if (!canSave || busy) return;
    setBusy(true); setErr('');
    try {
      const userId = session.user.id;
      // Read-modify-write against the freshest row so a concurrent edit's
      // cycle_history / license2 fields aren't clobbered.
      const existing = await window.DB.getProfile(userId).catch(() => null);
      const hist = Array.isArray(existing?.cycle_history) ? existing.cycle_history : [];
      const closedEarned = (Number(lic.baseline) || 0) +
        cycleRecords(records, lic.cycleStart, lic.renewalDate)
          .reduce((s, r) => s + (Number(r.points) || 0), 0);
      const entry = {
        license: lic.key,
        role_en: lic.role.en, role_zh: lic.role.zh,
        license_no: lic.licenseNo || null,
        cycle_start: lic.cycleStart, renewal_date: lic.renewalDate,
        required_points: lic.required, // derived: 0 records that this cycle was exempt
        baseline_points: Number(lic.baseline) || 0,
        first_pc: !!lic.exempt,
        earned_total: closedEarned,
        closed_at: new Date().toISOString().slice(0, 10),
      };
      // ‼️ ONE OPERATION OWNER. This sheet no longer assembles a cycle_history
      // patch: the server owns the archive, the identity attribution and the
      // date advance, in one transaction. `lic.renewalDate` is the cycle this
      // sheet was opened against and travels as the concurrency token, so a
      // double tap or a resubmit cannot advance the period twice.
      const res = await window.DB.performLicenceRollover({
        userId, slot: licKey,
        expectedRenewalDate: lic.renewalDate,
        cycle: entry,
        nextCycleStart: form.cycle_start,
        nextRenewalDate: form.renewal_date,
      });
      if (res.status !== 'completed' && res.status !== 'alreadyCompletedAfterRefetch') {
        setErr(rolloverMessage(res.status, isZh));
        return;
      }
      if (window.showToast) {
        window.showToast(
          res.status === 'alreadyCompletedAfterRefetch'
            ? (isZh ? '此週期已更新' : 'This cycle was already updated')
            : (isZh ? '新週期已開始' : 'New cycle started'),
          { tone: 'success' });
      }
      onSaved && onSaved(res.profile);
      onClose && onClose();
    } catch (e) {
      setErr(e?.message || (isZh ? '無法儲存，請稍後再試。' : 'Could not save. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={true} onClose={onClose} title={isZh ? '更新新週期' : 'Start your new cycle'}>
      <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
          {catchUp
            ? (isZh
                ? '你的證書日期似乎已過去——更新你的週期以繼續追蹤。已按 3 年一期推算至現時週期，如有需要可再調整。'
                : 'Your certificate dates look out of date — update your cycle to keep tracking. We advanced it in 3-year blocks to the current period; adjust if needed.')
            : (isZh
                ? `上一週期（${fmtDate(lic.cycleStart, lang) || '—'} → ${fmtDate(lic.renewalDate, lang)}）會自動存檔，已記錄的課程不會消失。`
                : `Your previous cycle (${fmtDate(lic.cycleStart, lang) || '—'} → ${fmtDate(lic.renewalDate, lang)}) is archived automatically — logged courses are kept.`)}
        </div>

        <CycleDatesField lang={lang} form={form} set={set} />

        {lic.exempt ? (
          <div className="onb-info-note" role="note">
            {isZh
              ? `首證豁免將於確認後結束——由此週期起需 ${nextRequired} 分。`
              : `The first-certificate exemption ends with this renewal — from this cycle you need ${nextRequired} points.`}
          </div>
        ) : null}

        {err ? <div style={{ color: 'var(--danger, #c0392b)', fontSize: 12.5 }}>{err}</div> : null}

        <LoadingButton className="btn btn-primary btn-fill"
          isLoading={busy}
          disabled={!canSave}
          loadingLabel={isZh ? '儲存中…' : 'Saving…'}
          leftIcon={<Icon name="check" size={16} stroke={2.2} />}
          onClick={save}>
          {isZh ? '確認已續期' : 'Confirm renewal'}
        </LoadingButton>
        <button className="btn btn-ghost btn-fill" disabled={busy} onClick={onClose}>
          {isZh ? '仍未續期' : 'Not renewed yet'}
        </button>
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Community-submission restriction picker (shared by LogSheet + SubmitCourseSheet)
//
// Replaces the old free-text "who is this for?" note with a structured choice so
// hospital-scoped courses resolve to a canonical name instead of inconsistent
// typing: members-only, or a specific hospital chosen via the same public/private
// + dropdown control used in onboarding (sourced from window.HK_HOSPITALS). The
// parent stores the raw selection and derives the persisted restriction_note
// string via buildRestrictionNote().
//
// Two audience-specific shapes:
//   • members  — a free-text `memberName` names the body the course is open to
//     (e.g. a specific association), since memberships aren't an enumerable list.
//   • hospital — public hospitals share one internal catalogue (Hospital
//     Authority), so the public sector needs NO per-hospital pick; only the
//     private sector, where each hospital runs its own training, shows the
//     hospital dropdown.
//
// `value` shape: { restricted, audience: 'members'|'hospital', sector, hospitalKey, memberName }
// ─────────────────────────────────────────────────────────────────────────────
function CommunityRestriction({ lang, value, onChange }) {
  const groups = window.HK_HOSPITALS || { public: [], private: [] };
  const patch = (p) => onChange({ ...value, ...p });
  return (
    <>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12.5 }}>
        <input type="checkbox"
          checked={value.restricted}
          onChange={e => patch({ restricted: e.target.checked })} />
        <span>{lang === 'zh' ? '此課程為會員 / 內部人士限定' : 'Members-only / internal course'}</span>
      </label>
      {value.restricted && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { id: 'members',  zh: '會員', en: 'Members' },
              { id: 'hospital', zh: '醫院', en: 'Hospital' },
            ].map(a => (
              <button key={a.id} type="button"
                className={'filter-chip' + (value.audience === a.id ? ' on' : '')}
                style={{ flex: 1, justifyContent: 'center' }}
                onClick={() => patch({ audience: a.id })}>
                {lang === 'zh' ? a.zh : a.en}
              </button>
            ))}
          </div>
          {value.audience === 'members' && (
            <input className="field-input"
              value={value.memberName || ''}
              onChange={e => patch({ memberName: e.target.value })}
              placeholder={lang === 'zh' ? '哪個會員 / 組織?(例:香港護士協會)' : 'Which membership / body? (e.g. HK Nurses Association)'} />
          )}
          {value.audience === 'hospital' && (
            <>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { id: 'public',  zh: '公立', en: 'Public' },
                  { id: 'private', zh: '私立', en: 'Private' },
                ].map(s => (
                  <button key={s.id} type="button"
                    className={'filter-chip' + (value.sector === s.id ? ' on' : '')}
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={() => { if (value.sector !== s.id) patch({ sector: s.id, hospitalKey: '' }); }}>
                    {lang === 'zh' ? s.zh : s.en}
                  </button>
                ))}
              </div>
              {/* Public hospitals share one internal catalogue, so no per-hospital
                  pick — only private hospitals (each running its own training)
                  need to resolve to a specific hospital. */}
              {value.sector === 'private' ? (
                <select className="field-input field-select"
                  value={value.hospitalKey}
                  onChange={e => patch({ hospitalKey: e.target.value })}>
                  <option value="">{lang === 'zh' ? '選擇醫院…' : 'Select hospital…'}</option>
                  {groups.private.map(h => (
                    <option key={h.key} value={h.key}>{lang === 'zh' ? (h.zh || h.en) : h.en}</option>
                  ))}
                </select>
              ) : (
                <div className="muted-2" style={{ fontSize: 11, lineHeight: 1.5 }}>
                  {lang === 'zh'
                    ? '所有公立醫院共用內部課程,毋須選擇個別醫院。'
                    : 'All public hospitals share internal courses — no need to pick one.'}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

// Derive the persisted restriction_note from a CommunityRestriction selection.
// Built in the submitter's current language, matching the old free-text field.
//
// Delegates to window.RESTRICTION.build (data.js) — the note format and its
// inverse (RESTRICTION.parse, which drives eligibility-aware discovery) MUST
// stay in lockstep, so both halves live together there. Do NOT re-inline the
// phrases here; edit them in data.js so parse tracks the change.
function buildRestrictionNote({ audience, sector, hospitalKey, memberName }, lang) {
  return window.RESTRICTION.build({ audience, sector, hospitalKey, memberName }, lang);
}

// ─────────────────────────────────────────────────────────────────────────────
// LOG sheet (add a completed course)
// ─────────────────────────────────────────────────────────────────────────────
// Duplicate detection (advisory only). NCHK: a repeat of the same activity in
// the same cycle earns no extra points. We WARN, never block — re-sits and
// annual re-certs (BLS/ACLS) are legitimate. Matching is intentionally exact
// (after normalisation), NOT fuzzy similarity: precision over recall so we don't
// false-positive on different-year courses ("BLS 2025" vs "BLS 2026").
function normaliseTitleForMatch(s) {
  return String(s || '')
    // full-width ASCII → half-width, full-width space → normal space
    .replace(/[！-～]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0))
    .replace(/　/g, ' ')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ') // drop punctuation/symbols → spaces
    .replace(/\s+/g, ' ')
    .trim();
}
function isDuplicateInCycle(form, records, licenses) {
  if (!records || !records.length) return false;
  const cid = form.courseId || null;
  // Catalog prefill carries title_en/title_zh while a manual entry carries
  // title. Compare every supplied spelling: an English catalog prefill must
  // still warn about an existing Chinese record (and vice versa).
  const titleKeys = [form.title, form.title_en, form.title_zh]
    .map(normaliseTitleForMatch)
    .filter(Boolean);
  const matches = (r) => {
    if (cid && r.courseId && r.courseId === cid) return true; // same catalog course
    if (!titleKeys.length) return false;
    // The user may have logged the course under either language; match against both.
    const existing = [r.title, r.title_en, r.title_zh, r.title?.en, r.title?.zh]
      .map(normaliseTitleForMatch)
      .filter(Boolean);
    return titleKeys.some(k => existing.includes(k));
  };
  // A record already logged in the current cycle of ANY held practising
  // certificate counts as a duplicate — check each licence's CNE window. A
  // single-licence nurse passes one descriptor; the empty fallback keeps the
  // open-window behaviour (future-date guard only) for guests/incomplete data.
  const lics = (licenses && licenses.length) ? licenses : [{ cycleStart: null, renewalDate: null }];
  return lics.some(lic =>
    cycleRecords(records, lic.cycleStart || null, lic.renewalDate || null).some(matches));
}
// Build the LogSheet manual-entry form prefilled from a catalog course.
// Shared by the in-sheet "pick from catalog" flow and the 編輯紀錄 entry
// point (Record → 進行中 → course → 編輯紀錄), which opens the sheet
// already pointed at a specific enrolled course that has no record yet.
function logFormFromCourse(c, lang) {
  // For catalog courses we don't know a separate start date — use the
  // single course date for both fields; the user can adjust before saving.
  const courseDate = c.date || new Date().toISOString().slice(0, 10);
  return {
    title:    L(c.title, lang),
    title_en: c.title?.en || '',
    title_zh: c.title?.zh || '',
    provider:    L(c.provider, lang),
    provider_en: c.provider?.en || '',
    provider_zh: c.provider?.zh || '',
    startDate: courseDate,
    endDate:   courseDate,
    points:   String(c.points ?? ''),
    category: c.category || 'elective',
    courseId: c.id,
  };
}

// Variant for a low-confidence catalog row (lifecycle_status =
// 'rejected_low_confidence': OCR noise / parse below threshold / bad
// duplicate). We seed the eyeball-able reference fields (title, provider,
// category — visible and easy to correct) but BLANK the audit-critical
// ones (both dates + points) so an unreliable number can never reach the
// record without the user actively typing it. courseId is kept so the
// course detail screen still flips to "已記錄" afterwards.
function logFormFromUnverifiedCourse(c, lang) {
  return { ...logFormFromCourse(c, lang), startDate: '', endDate: '', points: '' };
}

// `initialCourse` (optional): when provided, the sheet opens straight on
// the prefilled manual form for that course and presents as 編輯紀錄
// rather than 記錄課程 — used by the 進行中 → 編輯紀錄 action when the
// enrolled course doesn't have a record row yet.
// `needsVerification` (optional): the catalog row is flagged unreliable.
// The sheet then opens on a 'verify' warning step first, prefills via
// logFormFromUnverifiedCourse (critical fields blanked), and keeps a
// persistent caution banner in the manual form.
// ─────────────────────────────────────────────────────────────────────────────
// <AiScanDialog> — blocking progress dialog for AI document scans (LogSheet +
// SubmitCourseSheet). Portaled to <body> (the Sheet's slide-up transform would
// trap position:fixed). Deliberately NON-dismissible: the barrier has no click
// handler, so a stray tap can't kill a 10–30s Vertex call — the only exits are
// 取消 (a real fetch abort via onCancel) or completion. Visuals live in
// styles.css (.ai-scan-*); prefers-reduced-motion keeps only a slow ring as
// the indeterminate cue.
// ─────────────────────────────────────────────────────────────────────────────
function AiScanDialog({ lang, onCancel }) {
  return window.ReactDOM.createPortal(
    <div className="ai-scan-backdrop" role="alertdialog" aria-modal="true"
         aria-busy="true" aria-label={L(STR.aiScanning, lang)}>
      <div className="ai-scan-dialog">
        <div className="ai-scan-orb">
          <span className="ai-scan-spark"><Icon name="sparkle" size={26} stroke={1.6} /></span>
        </div>
        <div className="ai-scan-title">{L(STR.aiScanning, lang)}</div>
        <div className="ai-scan-sub">{L(STR.aiScanWait, lang)}</div>
        <div className="ai-scan-track" />
        <button type="button" className="btn btn-ghost"
          style={{ width: '100%', justifyContent: 'center' }} onClick={onCancel}>
          {lang === 'zh' ? '取消' : 'Cancel'}
        </button>
      </div>
    </div>,
    document.body
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// <ContinueLoggingDialog> — shown when the user taps AI scan but has 0 credits.
//
// ‼️ THE QUESTION THIS DIALOG ASKS IS "how do you want to keep adding records?",
// NOT "how do you want more scan credits?". It used to be <AiEarnDialog>: three
// earn rows (submit / correct / share) plus 稍後. Every option was a way to
// acquire scans, and NONE of them answered the only thing a nurse mid-backfill
// actually wants — to carry on entering courses. One wrote in asking whether
// Vital caps CNE records. It does not: records and certificate storage are
// unlimited on the free tier (paywall.jsx), and manual entry never touches the
// credit ledger. That fact existed only as a code comment; now it is row one.
//
// Order is load-bearing: FREE path → earn path → paid path. Reordering this so
// Pro or the earn loop comes first re-creates the dead end, and — because the
// free tier PROMISES unlimited records — burying the manual route to push a
// subscription would make that promise technically true and practically false.
//
// Dismissible (barrier closes) — unlike the blocking scan-progress dialog.
// Portaled to <body> (Sheet transform traps position:fixed). Reuses the
// .ai-scan-* backdrop; rows are plain cards.
// ─────────────────────────────────────────────────────────────────────────────
function ContinueLoggingDialog({ lang, qualificationOnly, onManual, onSubmitCourse, onCorrectCourse, onShare, onUpgrade, onClose }) {
  // Per-channel monthly progress (earned this month / cap). Best-effort — caps
  // live server-side so we never hardcode them; a load failure just hides the
  // progress line (the base sub-text still explains each channel).
  const [earn, setEarn] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const e = window.DB?.getScanEarnStatus ? await window.DB.getScanEarnStatus() : null;
      if (!cancelled) setEarn(e);
    })();
    return () => { cancelled = true; };
  }, []);

  // "本月 X/Y" — X grants earned this month, Y the monthly cap. When maxed
  // out (share is the common case at 1/1) the row still works but the line
  // reads "本月已達上限" so the user knows to come back next month.
  const progress = (key) => {
    const c = earn && earn[key];
    if (!c || typeof c.cap !== 'number') return null;
    const capped = c.earned >= c.cap;
    return { text: capped
      ? (lang === 'zh' ? `本月已達上限 · ${c.earned}/${c.cap}` : `Monthly max reached · ${c.earned}/${c.cap}`)
      : (lang === 'zh' ? `本月 ${c.earned}/${c.cap}` : `${c.earned}/${c.cap} this month`), capped };
  };

  const rows = [
    { key: 'submission', icon: 'globe',  label: L(STR.aiEarnSubmit, lang),  sub: L(STR.aiEarnSubmitSub, lang),  onClick: onSubmitCourse },
    { key: 'correction', icon: 'edit',   label: L(STR.aiEarnCorrect, lang), sub: L(STR.aiEarnCorrectSub, lang), onClick: onCorrectCourse },
    { key: 'share',      icon: 'upload', label: L(STR.aiEarnShare, lang),   sub: L(STR.aiEarnShareSub, lang),   onClick: onShare },
  ].filter((r) => typeof r.onClick === 'function');
  return window.ReactDOM.createPortal(
    <div className="ai-scan-backdrop" role="dialog" aria-modal="true"
         aria-label={L(STR.aiEarnTitle, lang)} onClick={onClose}>
      <div className="ai-scan-dialog" style={{ textAlign: 'left', padding: '22px 20px 16px' }}
           onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Icon name="plus" size={18} stroke={2} color="var(--primary)" />
          <div style={{ fontSize: 15, fontWeight: 650 }}>{L(STR.aiEarnTitle, lang)}</div>
        </div>
        {/* Says the metered thing out loud AND states what is NOT metered. The
            second half is the more important sentence — it is the one the
            feedback proved was missing. */}
        <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
          {L(qualificationOnly ? STR.aiEarnSubQual : STR.aiEarnSub, lang)}
        </div>
        {/* ROW ONE, ALWAYS: the free route that finishes the job. Styled like
            the chooser's primary card so it reads as the recommended action,
            not as a consolation prize under three credit offers. */}
        {typeof onManual === 'function' && (
          <button type="button" className="card"
            style={{ display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer', textAlign: 'left',
                     font: 'inherit', color: 'inherit', padding: 12, marginBottom: 10, width: '100%',
                     border: '1px solid var(--primary)', background: 'var(--primary-soft-2)' }}
            onClick={onManual}>
            <div style={{ width: 34, height: 34, borderRadius: 11, background: 'var(--primary)',
                          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name="plus" size={16} stroke={2} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="h-row" style={{ fontSize: 13.5 }}>{L(STR.aiEarnManual, lang)}</div>
              <div className="muted" style={{ fontSize: 11.5, marginTop: 1 }}>{L(STR.aiEarnManualSub, lang)}</div>
            </div>
            <Icon name="chev-r" size={16} color="var(--primary)" stroke={1.8} />
          </button>
        )}
        {rows.length > 0 && (
          <div className="muted-2" style={{ fontSize: 11, fontWeight: 600, margin: '0 0 6px 2px' }}>
            {L(STR.aiEarnMore, lang)}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((r) => {
            const p = progress(r.key);
            const per = earn && earn[r.key] && typeof earn[r.key].per === 'number' ? earn[r.key].per : 1;
            return (
              <button key={r.key} type="button" className="card"
                style={{ display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer', textAlign: 'left',
                         font: 'inherit', color: 'inherit', border: '0.5px solid var(--border)', padding: 12 }}
                onClick={r.onClick}>
                <div style={{ width: 34, height: 34, borderRadius: 11, background: 'var(--primary-soft)',
                              color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon name={r.icon} size={16} stroke={1.7} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="h-row" style={{ fontSize: 13.5 }}>{r.label}</div>
                  <div className="muted" style={{ fontSize: 11.5, marginTop: 1 }}>{r.sub}</div>
                  {p && (
                    <div style={{ fontSize: 10.5, marginTop: 2, fontVariantNumeric: 'tabular-nums',
                                  color: p.capped ? 'var(--warning)' : 'var(--fg-3)' }}>{p.text}</div>
                  )}
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)', flexShrink: 0 }}>+{per}</span>
              </button>
            );
          })}
        </div>
        {/* LAST, and only where a subscription can actually be bought. The
            parent passes onUpgrade only when PurchaseAPI.isSupported() — on web
            it is undefined and this row does not render, rather than showing a
            buy button that dead-ends in "subscriptions aren't available". */}
        {typeof onUpgrade === 'function' && (
          <button type="button" className="card"
            style={{ display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer', textAlign: 'left',
                     font: 'inherit', color: 'inherit', padding: 12, marginTop: 8, width: '100%',
                     border: '0.5px solid var(--border)' }}
            onClick={onUpgrade}>
            <div style={{ width: 34, height: 34, borderRadius: 11, background: 'var(--primary-soft)',
                          color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name="sparkle" size={16} stroke={1.7} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="h-row" style={{ fontSize: 13.5 }}>{L(STR.aiEarnPro, lang)}</div>
              <div className="muted" style={{ fontSize: 11.5, marginTop: 1 }}>{L(STR.aiEarnProSub, lang)}</div>
            </div>
            <Icon name="chev-r" size={16} color="var(--fg-3)" stroke={1.7} />
          </button>
        )}
        <button type="button" className="btn btn-ghost"
          style={{ width: '100%', justifyContent: 'center', marginTop: 12 }} onClick={onClose}>
          {L(STR.aiEarnLater, lang)}
        </button>
      </div>
    </div>,
    document.body
  );
}

// Where a record with this end date will land. ONE definition, because the
// log sheet's status hint, the AI-scan toast and the review-step chip must
// never disagree about whether something is 進行中 or 已完成 — they are the
// same promise made three times.
function recordDestination(endDateIso) {
  const today = (typeof todayLocalIso === 'function')
    ? todayLocalIso() : new Date().toISOString().slice(0, 10);
  return (endDateIso && endDateIso > today) ? 'in_progress' : 'completed';
}

// Client-side mirror of the edge function's assertSafeUrl
// (supabase/functions/extract-certificate/url-guard.ts). Purely so obvious
// junk never leaves the device and the user gets an instant, specific error —
// ‼️ the SERVER is the authority; never relax the server rule because this
// exists. Deliberately looser than the server (no DNS check, no shape rule) to
// avoid rejecting a link the server would happily fetch.
function aiScanUrlProblem(raw, lang) {
  const v = (raw || '').trim();
  if (!v) return null;
  let u;
  try { u = new URL(v); } catch (_) {
    return lang === 'zh' ? '請輸入有效的連結。' : 'Enter a valid link.';
  }
  if (u.protocol !== 'https:') {
    return lang === 'zh' ? '只支援 https:// 開頭的連結。' : 'Only https:// links are supported.';
  }
  if (u.username || u.password) {
    return lang === 'zh' ? '連結不可包含帳號密碼。' : 'Links must not contain credentials.';
  }
  if (u.port && u.port !== '443') {
    return lang === 'zh' ? '只支援標準 https 連結(443 埠)。' : 'Only standard https links (port 443) are supported.';
  }
  if (v.length > 2048) {
    return lang === 'zh' ? '連結過長。' : 'That link is too long.';
  }
  return null;
}

function AiUrlDialog({ open, lang, onClose, onSubmit }) {
  const [value, setValue] = React.useState('');
  const problem = aiScanUrlProblem(value, lang);
  const valid = !!value.trim() && !problem;
  React.useEffect(() => { if (!open) setValue(''); }, [open]);
  if (!open) return null;
  const paste = async () => {
    try {
      const text = await navigator.clipboard?.readText?.();
      if (text) setValue(text.trim());
    } catch (_) { /* Clipboard permission is optional; typing still works. */ }
  };
  return (
    <div className="register-dialog-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="register-dialog" role="dialog" aria-modal="true"
           aria-label={lang === 'zh' ? '貼上課程連結' : 'Paste course link'}>
        <button className="register-dialog-close icon-btn" onClick={onClose}
          aria-label={lang === 'zh' ? '關閉' : 'Close'}><Icon name="close" size={16} /></button>
        <div className="register-dialog-eyebrow mono">{lang === 'zh' ? 'AI 自動填寫' : 'AI auto-fill'}</div>
        <h3 className="register-dialog-title">{lang === 'zh' ? '貼上課程或海報連結' : 'Paste a course or poster link'}</h3>
        <p className="register-dialog-sub">
          {lang === 'zh'
            ? '啱啱報咗名?貼上課程網頁,即刻記低。支援公開的 HTTPS 課程頁、PDF 或圖片連結。'
            : 'Just enrolled? Paste the course page to capture it now. Public HTTPS course pages, PDFs and image links all work.'}
        </p>
        <div className="field" style={{ marginTop: 14 }}>
          <div className="field-label">URL</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="field-input" value={value} autoFocus inputMode="url"
              onChange={e => setValue(e.target.value)} placeholder="https://provider.org/course" />
            <button type="button" className="btn btn-ghost" onClick={paste}>
              {lang === 'zh' ? '貼上' : 'Paste'}
            </button>
          </div>
          {problem ? (
            <div role="alert" style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 6 }}>{problem}</div>
          ) : null}
        </div>
        <div className="register-dialog-actions">
          <button className="btn btn-ghost" onClick={onClose}>{lang === 'zh' ? '取消' : 'Cancel'}</button>
          <button className="btn btn-primary" disabled={!valid}
            onClick={() => { onSubmit({ url: value.trim() }); onClose(); }}>
            <Icon name="sparkle" size={15} stroke={1.8} /> {lang === 'zh' ? '開始讀取' : 'Start scan'}
          </button>
        </div>
      </div>
    </div>
  );
}

// `profile` is not used by the form itself — it is forwarded to the Pro sheet
// this component can open when the free scan quota runs out, so that sheet can
// argue from her own records instead of a generic feature list.
function LogSheet({ lang, onClose, onSave, onSaveQualification, initialCourse = null, needsVerification = false, initialMode = null, initialDestination = 'record', profile = null }) {
  // The qualification-wallet entry uses exactly the same secure AI extraction
  // pipeline as CNE logging, but lands in a short credential review rather than
  // making a nurse complete irrelevant CNE fields.
  const qualificationOnly = initialDestination === 'qualification';
  const [step, setStep] = React.useState(
    needsVerification ? 'verify' : (initialCourse ? 'manual' : (initialMode === 'ai' ? 'ai' : 'chooser')));
  const [manualStage, setManualStage] = React.useState(0);
  const [form, setForm] = React.useState(() => initialCourse
    ? (needsVerification ? logFormFromUnverifiedCourse(initialCourse, lang) : logFormFromCourse(initialCourse, lang))
    : {
        title: '', title_en: '', title_zh: '',
        provider: '', provider_en: '', provider_zh: '',
        startDate: '', endDate: '', points: '', category: 'core',
        // Optional qualification expiry — only ever set by the AI scan (an AHA
        // card's "Renew by") or by the user in the record editor.
        expiresOn: '',
        sessionsList: [],
        venue: '',
        courseId: null,
      });
  const [certFile, setCertFile] = React.useState(null);
  const [trackQualification, setTrackQualification] = React.useState(qualificationOnly);
  const [saveAsCne, setSaveAsCne] = React.useState(!qualificationOnly);
  const [qualificationKind, setQualificationKind] = React.useState('other');
  const [qualificationDoesNotExpire, setQualificationDoesNotExpire] = React.useState(false);
  const [qualificationVerified, setQualificationVerified] = React.useState(false);
  const [certFileError, setCertFileError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const fileRef = React.useRef(null);
  // AI certificate scan — aiPrefilled drives the mandatory "please verify"
  // banner in the manual step; scanCredits is a best-effort balance snapshot
  // (null = unknown → the count is simply hidden, feature still works and
  // the server remains the authority on quota).
  const [aiScanning, setAiScanning] = React.useState(false);
  const [aiPrefilled, setAiPrefilled] = React.useState(false);
  const [aiNotes, setAiNotes] = React.useState(null);
  // Snapshot of what the AI filled in, kept for the quality loop: at save we
  // record WHICH fields the user edited (names only — PRIVACY.md §5).
  const [aiSnap, setAiSnap] = React.useState(null);
  // Non-certificate scan file (poster etc.) — held as co-submit evidence,
  // never attached to the record as its certificate.
  const [aiEvidence, setAiEvidence] = React.useState(null);
  const [scanCredits, setScanCredits] = React.useState(null);
  const aiFileRef = React.useRef(null);
  const aiAbortRef = React.useRef(null); // AiScanDialog's 取消 aborts the in-flight fetch
  const [showContinue, setShowContinue] = React.useState(false); // 0-credit → "keep adding records"
  const [aiUrlOpen, setAiUrlOpen] = React.useState(false);
  const [aiFileError, setAiFileError] = React.useState('');
  // Pro paywall. Opened ONLY at a genuine value moment — the scan that would
  // have consumed the last free credit. Never at onboarding, never on a timer:
  // a nurse who has just felt the limit is the only one for whom this is an
  // answer rather than an interruption.
  const [paywallOpen, setPaywallOpen] = React.useState(false);
  // Shared entitlement (paywall.jsx) — re-reads on `vital:entitlement-changed`,
  // so the AI gate opens the instant a purchase lands anywhere in the app.
  const proEnt = useProEntitlement();
  const isPro = proEnt.pro;

  // Where a 0-credit nurse should be sent.
  //
  // ‼️ ALWAYS the continuation dialog, on EVERY platform — never straight to
  // the paywall. This used to branch: purchasable → ProSheet, otherwise → the
  // earn menu. Both branches shared one defect, and web only made it louder:
  // neither told her that MANUAL ENTRY IS FREE AND UNLIMITED, so running out of
  // scans looked like running out of records. ContinueLoggingDialog leads with
  // the free route and carries Pro as its last row, which keeps the paywall one
  // tap from the same value moment while making the job finishable without it.
  //
  // ‼️ onUpgrade is passed ONLY when a subscription can actually be bought.
  // PurchaseAPI.isSupported() is false on web, and false on native until
  // REVENUECAT_PUBLIC_KEY is set and the products exist; without the guard the
  // Pro row dead-ends in "subscriptions aren't available".
  //
  // ‼️ THE RULE NOW LIVES IN ONE PLACE — window.proPurchasable() (data.js).
  // This used to be a local re-derivation, and Home's route card, written later,
  // never asked the question at all: on the web it offered 「了解 Vital Pro」 and
  // landed the nurse in 「訂閱功能暫時無法使用」. Every new upsell asks the
  // shared helper; do not re-inline `PurchaseAPI.isSupported()` here.
  const offerContinueOptions = () => setShowContinue(true);
  const canPurchase = () => !!window.proPurchasable?.();

  // AI card / URL tap when the year's free scans are gone. Pro users are never
  // gated.
  const gateAiScan = () => {
    if (isPro) return false;
    if (scanCredits && scanCredits.available === 0) { offerContinueOptions(); return true; }
    return false;
  };
  // Earn routes. Submit / correct navigate via the app's window-event bus
  // (app.jsx closes this sheet, switches to Discover, opens Submit). Share
  // happens in place — the existing shareWithReward grants +1 (server-capped),
  // then we re-read the balance so the AI card unlocks without a reopen.
  const earnSubmit = () => { setShowContinue(false); window.dispatchEvent(new CustomEvent('vital:earn-credit', { detail: { method: 'submit' } })); };
  const earnCorrect = () => { setShowContinue(false); window.dispatchEvent(new CustomEvent('vital:earn-credit', { detail: { method: 'correct' } })); };
  const refreshCredits = async () => {
    if (window.DB?.getScanCreditStatus) {
      const c = await window.DB.getScanCreditStatus();
      if (c) setScanCredits(c);
    }
  };
  const earnShare = async () => {
    await shareWithReward({
      title: lang === 'zh' ? 'Vital · 香港護士 CNE' : 'Vital · HK Nurse CNE',
      text: lang === 'zh' ? '我用緊 Vital 記錄同搵 CNE 課程,幾好用,分享俾你。'
        : "I'm using Vital to track and find CNE courses — sharing it with you.",
      url: window.DB.buildShareUrl('/'), lang,
    });
    await refreshCredits();
    setShowContinue(false);
  };
  // Crowdsource Phase 1B — co-submit lets a user offer their just-logged
  // manual course to the community catalogue at the same time. Only meaningful
  // when form.courseId is null (truly manual; not picked from the catalog).
  const [coSubmit, setCoSubmit] = React.useState(false);
  // ‼️ THE HINT IS OPTIONAL AND THE PICKER IS NOT A GATE. LogSheet's job is
  // "I finished a CNE → record it"; contributing the course is a secondary
  // action. §98 measured that this door carried 29 of 70 submissions and could
  // not name an organisation at all — but the fix is to make the evidence
  // available, not to make it required. A null hint is exactly what the
  // resolver handled before any of this shipped.
  const [coProviderSelection, setCoProviderSelection] = React.useState(null);
  // ‼️ null = "not typed in yet, seed from the record's provider wording".
  // An empty STRING is a real state — the nurse cleared the box — and must not
  // silently repopulate.
  const [coProviderQuery, setCoProviderQuery] = React.useState(null);
  const [coRestrict, setCoRestrict] = React.useState({ restricted: false, audience: 'members', sector: 'public', hospitalKey: '', memberName: '' });

  // ── THE OTHER DOOR NOW KNOWS ABOUT THE FIRST ──────────────────────────────
  // Two of the four real duplicate pairs crossed the two entry points minutes
  // apart, from the same re-scanned image: the nurse submitted the course in
  // 提交課程 and then ticked this box while logging her record, not knowing the
  // checkbox does the same thing. Asking the server the moment she ticks it is
  // what makes the two doors one system.
  //
  // ‼️ ADVISORY, LIKE EVERY PREFLIGHT HERE. The write re-decides; this only
  // changes what the checkbox says.
  const [coGuard, setCoGuard] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    if (!coSubmit || form.courseId || !window.DB?.checkSubmissionCandidate) {
      setCoGuard(null);
      return undefined;
    }
    const title = (form.title_en || form.title || '').trim();
    if (title.length < 6 || !(form.provider_en || form.provider || '').trim()) {
      setCoGuard(null);
      return undefined;
    }
    const t = setTimeout(async () => {
      const { data } = await window.DB.checkSubmissionCandidate({
        payload: {
          title_en: form.title_en || form.title || null,
          title_zh: form.title_zh || null,
          provider_full_en: form.provider_en || form.provider || null,
          start_date: form.startDate || form.endDate || null,
          sessions_list: (form.sessionsList && form.sessionsList.length) ? form.sessionsList : undefined,
        },
      });
      if (!cancelled && data) setCoGuard(data);
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [coSubmit, form.courseId, form.title, form.title_en, form.title_zh,
      form.provider, form.provider_en, form.startDate, form.endDate, form.sessionsList]);
  const coAlready = coGuard?.verdict === 'ALREADY_PENDING' || coGuard?.verdict === 'ALREADY_APPROVED';

  // ‼️ THE SAME EXPRESSION THE PAYLOAD USES for provider_full_en. If these two
  // ever diverge, the hint would be validated against one wording and submitted
  // beside another.
  const coProviderText = (form.provider_en || form.provider || '').trim();
  // ‼️ A SELECTION IS ABOUT THE TEXT IT WAS MADE AGAINST. Edit the provider
  // afterwards and the old entity is no longer an answer to the current
  // question — so it is not sent, and the picker returns to search rather than
  // showing a chip that quietly refers to the previous wording. Derived, not an
  // effect: nothing to sequence, nothing to loop.
  const { stale: coHintStale, entityId: coProviderHint } =
    providerHintState(coProviderSelection, coProviderText);

  // ‼️ ASK THE RULE THAT WILL JUDGE IT, NOT A LOCAL COPY OF THE RULE. db.js
  // refuses a submission with no official link and no stored file, and rolls the
  // created row back — correctly. What was wrong is that this checkbox offered
  // the action anyway: tick it with no certificate and the nurse got her record
  // saved, her co-submit silently deleted, and an error blaming an upload that
  // never happened. §53's rule again — never render a control whose handler
  // cannot succeed.
  //
  // LogSheet has no URL field at all, so in a manual log the certificate IS the
  // only route to verifiable; the AI path can also supply one from the poster.
  const coEvidenceCount = certFile ? 1 : (aiEvidence ? 1 : 0);
  const coVerifiable = typeof window.DB?.submissionIsVerifiable === 'function'
    ? window.DB.submissionIsVerifiable({
        payload: { url: aiSnap?.extras?.url, registration_url: aiSnap?.extras?.registration_url },
        evidenceCount: coEvidenceCount,
      })
    : coEvidenceCount > 0;
  // ‼️ DERIVED, NOT A setState. Tick the box, then remove the certificate, and a
  // stored `true` would still send a submission that is certain to be rolled
  // back. The effective value is recomputed from what is actually attached.
  // ‼️ ASK THE RULE THAT WILL JUDGE IT — the community catalogue takes courses
  // nurses can still join, and never a credential (db.js submissionIsShareable;
  // the database trigger enforces the same last-day rule for old builds). Not
  // offered at all when it cannot apply: unlike a missing certificate, "this
  // course has ended" is nothing she can fix, so a disabled box would only be
  // noise on every back-logged certificate.
  const coShareable = (typeof window.DB?.submissionIsShareable === 'function'
    ? window.DB.submissionIsShareable({
        payload: { start_date: form.startDate, end_date: form.endDate, sessions_list: form.sessionsList },
        docType: aiSnap?.docType,
      }).ok
    : true) && !qualificationOnly && saveAsCne;
  const coSubmitEffective = coSubmit && coVerifiable && coShareable;

  // Duplicate-warning data — loaded by the sheet itself (no prop threading) so
  // app.jsx is untouched. Best-effort: a fetch failure leaves records empty →
  // no warning. The `cancelled` guard prevents setState after an early close.
  const [existingRecords, setExistingRecords] = React.useState([]);
  // Every held practising certificate's cycle, so the duplicate check spans both
  // windows for a dual-registered nurse (see profileLicenses/data.js).
  const [licenses, setLicenses] = React.useState([]);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const session = await window.AuthAPI?.getSession?.();
        const uid = session?.user?.id;
        if (!uid || !window.DB?.getRecords) return;
        const [recs, prof, credits] = await Promise.all([
          window.DB.getRecords(uid),
          window.DB.getProfile ? window.DB.getProfile(uid).catch(() => null) : Promise.resolve(null),
          window.DB.getScanCreditStatus ? window.DB.getScanCreditStatus() : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setExistingRecords(recs || []);
        if (prof) setLicenses(window.profileLicenses ? window.profileLicenses(prof) : []);
        setScanCredits(credits);
      } catch (e) {
        console.warn('LogSheet duplicate-check load failed (non-fatal)', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  // Derived (not imperatively set) so it recomputes both on each keystroke AND
  // the instant the async records fetch resolves — no silent gap.
  const isDuplicate = React.useMemo(
    () => isDuplicateInCycle(form, existingRecords, licenses),
    [form.title, form.title_en, form.title_zh, form.courseId, existingRecords, licenses]
  );

  const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024*1024 ? `${(b/1024).toFixed(1)} KB` : `${(b/1024/1024).toFixed(1)} MB`;

  // Points may be left blank ONLY for a course that hasn't finished yet — the
  // enrolled-course case, where the poster usually hasn't published them. A
  // finished course with no points is a data-entry mistake, so the gate stays.
  const pointsOptional = recordDestination(form.endDate) === 'in_progress';
  // Blank points on an unfinished course = "not published yet", which is NOT
  // the same as a deliberate 0 and must not silently become one when the end
  // date passes. Persisted as PTSTBD:1 (see isRecordPointsPending).
  const pointsPending = pointsOptional && !String(form.points).trim();
  // ‼️ ONE predicate for "points are missing in a way that must block saving".
  // Step 2's Next button and step 3's 加入紀錄 button MUST both read this.
  // They diverged once (fixed 2026-09-02): Next honoured pointsOptional but the
  // final save gate still spelled `!form.points`, so a nurse who left CNE 分
  // blank on an unfinished course walked to step 3 and found 加入紀錄 dead with
  // nothing on screen explaining why — the field two steps back had literally
  // told her it was 可選. Never re-inline a points check at either button.
  const pointsMissing = !pointsOptional && !String(form.points ?? '').trim();

  // Prefill the manual form from a catalog course and jump to manual step.
  const pickCourse = (c) => {
    setForm(logFormFromCourse(c, lang));
    setManualStage(0);
    setStep('manual');
  };

  const catalog = React.useMemo(() => {
    const qRaw = search.trim();
    // Log-from-catalog spans the whole catalogue (you might be logging an
    // upcoming session you've decided to attend, or a past one that
    // already happened). Past + today + future are all valid — including
    // archived/withdrawn courses, so a nurse can record CNE for a course
    // that's since been pulled from the provider's site.
    let r = (typeof window.allSearchableCourses === 'function')
      ? window.allSearchableCourses()
      : (typeof COURSES !== 'undefined' ? COURSES : []);
    // Language-blind by design — see courseMatchesQuery in data.js. A nurse
    // logging a course she already attended types whatever is printed on the
    // certificate, which is routinely English even when the app is in 繁中.
    if (qRaw) r = r.filter(c => courseMatchesQuery(c, qRaw));
    // Order: upcoming first (soonest at the top), then past (most recent
    // first). This puts what the user is most likely logging — a course
    // happening soon or one that just ended — at the top of the list.
    const todayIso = new Date().toISOString().slice(0, 10);
    return r.sort((a, b) => {
      const ad = a.date || '0000', bd = b.date || '0000';
      const aFuture = ad >= todayIso, bFuture = bd >= todayIso;
      if (aFuture && !bFuture) return -1;
      if (!aFuture && bFuture) return 1;
      // Both same side of today — upcoming: soonest first; past: most recent first.
      if (aFuture) return ad < bd ? -1 : ad > bd ? 1 : 0;
      return ad > bd ? -1 : ad < bd ? 1 : 0;
    }).slice(0, 20);
    // ‼️ `lang` is deliberately NOT a dependency: courseMatchesQuery reads every
    // language variant, so the result set no longer changes when she toggles
    // EN/繁. Re-adding it would be harmless but misleading about what this
    // memo depends on.
  }, [search]);

  // Ephemeral confirmation toast. Prefers the tone-aware showToast from
  // motion-kit (nicer in/out animation); falls back to the plain local
  // helper so a save is always acknowledged even if that isn't loaded.
  const notifyLog = (msg, tone) => {
    if (window.showToast) window.showToast(msg, { tone });
    else showVitalToast(msg);
  };

  // AI document scan — sends the picked file (or a pasted course-page URL,
  // input = { url }) to the extract-certificate edge function (Vertex AI
  // Gemini), pre-fills the manual form and — for files — keeps the SAME file
  // as the certificate attachment so the normal save → uploadCertificate path
  // stores it. doc_type routing: certificate → plain prefill;
  // course_announcement (poster / webpage) → prefill + auto-tick co-submit so
  // the course flows to the community catalogue. Nothing is auto-saved:
  // aiPrefilled switches on the verify banner and the user must review + Save.
  const runAiScan = async (input) => {
    if (!input || aiScanning || !window.DB?.extractCertificate) return;
    const isUrl = !(typeof File !== 'undefined' && input instanceof File);
    if (!isPro && scanCredits && scanCredits.available === 0) {
      offerContinueOptions(); // value moment — never a dead end
      return;
    }
    setAiScanning(true);
    const ctl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    aiAbortRef.current = ctl;
    try {
      const res = await window.DB.extractCertificate(input, ctl ? { signal: ctl.signal } : undefined);
      if (res?.error) {
        if (res.error.code === 'cancelled') return; // user tapped 取消 — silent
        if (res.error.code === 'quota_exhausted') {
          // The SERVER is the quota authority (local status may be stale) —
          // sync the count, then offer every way forward: manual entry (free,
          // unlimited, and the only one that always works), earning a credit,
          // or Pro where it can be bought.
          setScanCredits(c => ({ ...(c || {}), available: 0 }));
          offerContinueOptions();
        } else if (res.error.code === 'pro_daily_cap') {
          // Pro is unmetered but not unbounded — say so plainly rather than
          // showing a generic failure the nurse cannot act on.
          notifyLog(lang === 'zh'
            ? '今日掃描次數已達上限,請明天再試。'
            : 'You have reached today’s scan limit. Please try again tomorrow.', 'warning');
        } else if (res.error.code === 'bad_url' || res.error.code === 'url_fetch_failed') {
          notifyLog(L(STR.aiScanUrlFailed, lang), 'danger');
        } else {
          notifyLog(L(STR.aiScanFailed, lang), 'danger');
        }
        return;
      }
      const x = res.data?.extraction || {};
      if (res.data?.creditsRemaining != null) {
        setScanCredits(c => ({ ...(c || {}), available: res.data.creditsRemaining }));
      }
      const docType = x.doc_type || (x.is_cne_certificate ? 'certificate' : 'other');
      // Route by DATE, not just by document type. A poster for a course that
      // hasn't run yet is the enrolled-course capture this flow now exists to
      // support — so state where it lands, rather than apologising for it not
      // being a certificate. A past-dated poster genuinely is a weaker source
      // than a certificate, so that one keeps the cautionary wording.
      const upcoming = recordDestination(x.end_date || x.start_date) === 'in_progress';
      // ‼️ THE SAME RULE THE CO-SUBMIT BOX AND THE WRITE DOOR USE (db.js
      // submissionIsShareable). A poster for a course that has already ended is
      // not ticked for the community: shared that late, 3 in 59 were ever used.
      const shareableNow = typeof window.DB?.submissionIsShareable === 'function'
        ? window.DB.submissionIsShareable({ payload: { start_date: x.start_date, end_date: x.end_date, sessions_list: x.sessions_list }, docType }).ok
        : upcoming;
      if (docType === 'course_announcement') {
        notifyLog(L(upcoming ? STR.aiScanUpcoming : (shareableNow ? STR.aiScanPoster : STR.aiScanPosterPast), lang),
          upcoming ? 'success' : 'warning');
        if (shareableNow) setCoSubmit(true); // announcement → offer it to the community catalogue
      } else if (docType === 'qualification') {
        // The wallet flow exists FOR this document — a practising certificate,
        // a degree, a competency authorisation. Warning her there would be
        // telling her the thing she asked us to scan was the wrong thing.
        //
        // ‼️ IN THE CNE FLOW IT NOW GOES TO THE WALLET TOO, not into a CNE record.
        // 2026-11-01: a practising certificate scanned here was saved as a CNE
        // record "completed" 2029-12-31 AND offered to the community — the file
        // became co-submit evidence, so the box was tickable — and reached the
        // review queue as a course. The AI had already said what it was; the
        // flow did not listen. She can still switch 「同時記錄為 CNE」 back on.
        if (!qualificationOnly) {
          notifyLog(L(STR.aiScanCredential, lang), 'warning');
          setSaveAsCne(false);
          setCoSubmit(false);
        }
      } else if (docType !== 'certificate') {
        notifyLog(L(STR.aiScanNotCert, lang), 'warning');
      }
      setForm(fm => ({
        ...fm,
        // Display title follows the app language when both are printed;
        // title_en/title_zh keep whatever the certificate carries.
        title:       (lang === 'zh' && x.title_zh) ? x.title_zh : (x.title_en || x.title_zh || fm.title),
        title_en:    x.title_en || '',
        title_zh:    x.title_zh || '',
        provider:    x.provider || fm.provider,
        provider_en: x.provider || fm.provider_en,
        provider_zh: '',
        startDate:   x.start_date || fm.startDate,   // server guarantees YYYY-MM-DD
        endDate:     x.end_date || x.start_date || fm.endDate,
        // Qualification expiry — a SEPARATE field from end_date (an AHA card's
        // "Renew by" is not its completion date). The server drops any value
        // at or before end_date, so anything arriving here is a real expiry.
        expiresOn:   x.expires_on || fm.expiresOn,
        points:      x.points != null ? String(x.points) : fm.points,
        category:    x.category || fm.category,      // guess only; chips stay editable
        venue:       x.venue || fm.venue,            // announcements carry a venue
        // Per-session moments feed the 逐節 (課堂時間) editor directly.
        sessionsList: (Array.isArray(x.sessions_list) && x.sessions_list.length) ? x.sessions_list : [],
        courseId:    null,
      }));
      // Only a real certificate auto-attaches as the record's 上傳證書 — a
      // poster/announcement file becomes co-submit EVIDENCE instead (attaching
      // a poster as the "certificate" would pollute the CNE record).
      //
      // A doc_type='qualification' file attaches ONLY in the wallet flow, where
      // it is the credential's own evidence. In the CNE flow it is not proof of
      // attendance and must not become the record's certificate.
      if (!isUrl) {
        // A credential's file is the wallet entry's own evidence — in either flow,
        // now that the CNE flow routes a credential to the wallet as well.
        if (docType === 'certificate' || docType === 'qualification') setCertFile(input);
        else setAiEvidence(input);
      }
      setAiNotes(x.notes_for_user || null);
      setAiSnap({
        ref: res.data?.ref || null,
        isCert: !!x.is_cne_certificate,
        docType,
        // Announcement-only extras (v2) — not shown in the log form, but if
        // the user co-submits, they ride along into the community payload
        // (approve_submission inserts these keys into cne_courses).
        extras: docType === 'course_announcement' ? {
          // ‼️ WAS `isUrl ? input.url : null` — which made 官方來源 URL
          // STRUCTURALLY UNFILLABLE on the poster path, however clearly the
          // poster printed the address. source_url now carries it for both
          // doors; the pasted-URL fallback stays for a client running against
          // an older deployment of the function.
          url:                   x.source_url || (isUrl ? input.url : null),
          registration_url:      x.registration_url || null,
          registration_deadline: x.registration_deadline || null,
          fee_hkd:               x.fee_hkd ?? null,
          language_en:           x.language_en || null,
          teaching_mode_en:      x.teaching_mode_en || null,
          time_text:             x.time_text || null,
          description_short_en:  x.description_short_en || null,
          description_short_zh:  x.description_short_zh || null,
          sessions:              x.sessions || null,
        } : null,
        values: {
          title: ((lang === 'zh' && x.title_zh) ? x.title_zh : (x.title_en || x.title_zh)) || '',
          provider: x.provider || '',
          start_date: x.start_date || '',
          end_date: x.end_date || x.start_date || '',
          points: x.points != null ? String(x.points) : '',
          category: x.category || '',
          venue: x.venue || '',
        },
      });
      setAiPrefilled(true);
      // The model's own shelf classification wins over the title regex: the
      // regex only ever sees the title, which on an NCHK practising
      // certificate or a hospital competency form may not name the credential
      // at all. The regex stays as the fallback for older deployments of the
      // function, which return no qualification_kind.
      const suggestedQualificationKind =
        (window.QUALIFICATION_CATEGORIES || []).some(c => c.id === x.qualification_kind)
          ? x.qualification_kind
          : qualificationKindFromTitle(x.title_en || x.title_zh || '');
      if (qualificationOnly) {
        // AI suggests a broad professional category; the review screen makes
        // the nurse choose or correct it before anything is stored.
        setTrackQualification(true);
        setSaveAsCne(false);
        setQualificationKind(suggestedQualificationKind || 'other');
      } else if (docType === 'qualification' || (x.expires_on && (suggestedQualificationKind || /american heart association|aha/i.test(x.provider || '')))) {
        setTrackQualification(true);
        setQualificationKind(suggestedQualificationKind || 'other');
      }
      setManualStage(0);
      setStep('manual');
    } finally {
      aiAbortRef.current = null;
      setAiScanning(false);
    }
  };

  const handleSave = async () => {
    if (busy) return;
    setBusy(true);
    let coSubmitOutcome = 'not_requested';
    try {
      const qualificationPayload = trackQualification ? {
        kind: qualificationKind || qualificationKindFromTitle(form.title_en || form.title) || 'other',
        titleEn: form.title_en || form.title,
        titleZh: form.title_zh || null,
        issuerEn: form.provider_en || form.provider || null,
        issuerZh: form.provider_zh || null,
        issuedOn: (qualificationOnly || aiSnap?.docType === 'qualification')
          ? (form.startDate || form.endDate || null) : (form.endDate || null),
        expiresOn: qualificationDoesNotExpire ? null : (form.expiresOn || null),
        doesNotExpire: qualificationDoesNotExpire,
      } : null;
      if (qualificationPayload && !saveAsCne) {
        const onlyResult = await onSaveQualification?.(qualificationPayload, certFile);
        if (!onSaveQualification || onlyResult?.error) {
          // Same localised mapping the manual editor uses — a raw `invalid kind`
          // is neither readable nor actionable, and a generic "try again" makes
          // her retry a save that cannot succeed.
          notifyLog(onlyResult?.error
            ? qualificationSaveErrorMessage(onlyResult.error, lang)
            : (lang === 'zh' ? '資格儲存失敗，請再試一次' : 'Qualification save failed — please try again'), 'danger');
          return;
        }
        if (onlyResult?.evidenceError) {
          notifyLog(lang === 'zh'
            ? '資格已儲存，但證書檔案上載失敗，可稍後再上載。'
            : 'Saved, but the certificate file did not upload — you can add it later.', 'warning');
          onClose?.();
          return;
        }
        notifyLog(lang === 'zh' ? '資格已儲存到「我的資格」' : 'Saved to My qualifications', 'success');
        onClose?.();
        return;
      }
      if (onSave) {
        // End date determines status:
        //   • endDate > today  → in-progress  (shows on 進行中)
        //   • endDate ≤ today  → completed    (shows on 已完成紀錄)
        // We persist the completion date via `date` (maps to completed_date
        // in the DB). startDate has no dedicated column so we tuck it into
        // the freeform notes field as `START:YYYY-MM-DD` — RecordScreen
        // can parse it back when displaying the entry.
        const noteParts = [];
        if (form.startDate) noteParts.push(`START:${form.startDate}`);
        // PTSTBD:1 — "points not published yet", set only when the user left
        // the field blank on a course that hasn't finished. Without it a
        // 0-point record slides into 已完成 the day its end date passes and
        // quietly undercounts the nurse's cycle. Same marker vocabulary as
        // START: / COMPLETED:1 / EXCLUDED:1 (record-extras.jsx parseNoteBlob).
        if (pointsPending) noteParts.push('PTSTBD:1');
        const saveResult = await onSave({
          courseId:    form.courseId,
          title:       form.title,
          title_en:    form.title_en || form.title,
          title_zh:    form.title_zh || null,
          provider:    form.provider,
          provider_en: form.provider_en || form.provider,
          provider_zh: form.provider_zh || null,
          date:        form.endDate,
          startDate:   form.startDate,
          points:      Number(form.points) || 0,
          category:    form.category,
          source:      aiPrefilled ? 'ai' : (form.courseId ? 'catalog' : 'manual'),
          expiresOn:   form.expiresOn || null,
          notes:       noteParts.length ? noteParts.join('\n') : null,
          certificateFile: certFile,
          qualification: qualificationPayload,
        });

        // A failed write returns { error }. Keep the sheet open and tell the
        // user rather than closing silently — closing on error would drop
        // everything they typed and look identical to success.
        if (saveResult?.error) {
          notifyLog(lang === 'zh' ? '儲存失敗，請再試一次' : 'Save failed — please try again', 'danger');
          return; // finally still resets `busy`
        }
        if (saveResult?.qualificationError) {
          notifyLog(lang === 'zh' ? 'CNE 紀錄已儲存，但資格未能加入；可稍後在「我的資格」重試。' : 'CNE record saved, but the qualification could not be added. Try again in My qualifications.', 'warning');
        }

        // Persist the per-session schedule (if the user set one) as this user's
        // attendance override for the new manual record. course_attendance is
        // keyed `manual-<record.id>`, so the record must exist first — hence we
        // write it after onSave returns the row id. Failure is non-fatal: the
        // record itself already saved.
        if (form.sessionsList && form.sessionsList.length && saveResult?.data?.id) {
          try {
            const session = await window.AuthAPI.getSession();
            if (session?.user?.id && window.DB?.upsertCourseAttendance) {
              await window.DB.upsertCourseAttendance(session.user.id, `manual-${saveResult.data.id}`, {
                customSessions: form.sessionsList,
              });
            }
          } catch (e) {
            console.warn('saving manual session schedule failed (record still saved):', e?.message || e);
          }
        }
        // AI quality loop — record which fields the user corrected after an
        // AI pre-fill. Field NAMES only (PRIVACY.md §5); fire-and-forget.
        if (aiPrefilled && aiSnap && window.DB?.logAiScanFeedback) {
          const current = {
            title: form.title || '', provider: form.provider || '',
            start_date: form.startDate || '', end_date: form.endDate || '',
            points: form.points === '' ? '' : String(Number(form.points) || 0),
            category: form.category || '',
            venue: form.venue || '',
          };
          const aiFilledFields = Object.keys(aiSnap.values).filter(k => aiSnap.values[k] !== '');
          const editedFields = Object.keys(aiSnap.values).filter(k => aiSnap.values[k] !== current[k]);
          window.DB.logAiScanFeedback({
            refId: aiSnap.ref, isCert: aiSnap.isCert, docType: aiSnap.docType || null,
            aiFilledFields, editedFields,
          });
        }
        // Crowdsource Phase 1B — co-submit the course to the community
        // catalogue. Only fires when:
        //   - the user opted in via the checkbox (coSubmit)
        //   - this is truly a manual entry, not a catalog pick (no courseId)
        //   - the addRecord above returned a row we can link to
        // We swallow any submission failure here — the record itself
        // already saved successfully, and we don't want to roll that back
        // just because the community submission hit a snag. The user can
        // re-open the record later and submit again in Phase 2.
        if (coSubmitEffective && !form.courseId && saveResult?.data?.id) {
          try {
            const session = await window.AuthAPI.getSession();
            if (session?.user?.id) {
              const coDerived = (form.sessionsList && form.sessionsList.length
                && typeof deriveScheduleFields === 'function')
                ? deriveScheduleFields(form.sessionsList) : null;
              const payload = {
                title_en:         form.title_en || form.title || null,
                title_zh:         form.title_zh || null,
                provider_full_en: form.provider_en || form.provider || null,
                provider_full_zh: form.provider_zh || null,
                start_date:       coDerived ? coDerived.start_date : (form.startDate || form.endDate || null),
                end_date:         coDerived ? coDerived.end_date : (form.endDate || null),
                // ‼️ THE BUG THIS ISSUE IS ABOUT (#42). This was
                // `Number(form.points) || null`, and 0 is falsy after
                // Number(), so a DELIBERATE zero — an AGM, a mandatory
                // refresher, an AHA card that certifies no CNE value, all
                // real in production — became "unknown" in the community
                // submission. Same information loss §30 built PTSTBD:1 to
                // prevent, in the opposite direction.
                cne_points:       cnePointsForSubmission(form.points),
                venue_en:         (form.venue || '').trim() || null,
                // AI-extracted announcement extras ride along only after the
                // user has reviewed them. approval now supports fee_hkd; the
                // payload-v2 form wiring below is the authority for whether a
                // fee is actually present (0 means explicitly free).
                ...(aiSnap?.extras?.registration_url ? { registration_url: aiSnap.extras.registration_url } : {}),
                ...(aiSnap?.extras?.url ? { url: aiSnap.extras.url } : {}),
                ...(aiSnap?.extras?.registration_deadline ? { registration_deadline: aiSnap.extras.registration_deadline } : {}),
                ...(aiSnap?.extras?.fee_hkd != null ? { fee_hkd: aiSnap.extras.fee_hkd } : {}),
                ...(aiSnap?.extras?.language_en ? { language_en: aiSnap.extras.language_en } : {}),
                ...(aiSnap?.extras?.teaching_mode_en ? { teaching_mode_en: aiSnap.extras.teaching_mode_en } : {}),
                ...(aiSnap?.extras?.description_short_en ? { description_short_en: aiSnap.extras.description_short_en } : {}),
                ...(aiSnap?.extras?.description_short_zh ? { description_short_zh: aiSnap.extras.description_short_zh } : {}),
                ...(aiSnap?.extras?.time_text ? { time_text: aiSnap.extras.time_text } : {}),
                ...(!coDerived && aiSnap?.extras?.sessions ? { sessions: aiSnap.extras.sessions } : {}),
                ...(coDerived ? {
                  sessions:      coDerived.sessions,
                  sessions_list: form.sessionsList,
                  ...(!aiSnap?.extras?.time_text && coDerived.time_text ? { time_text: coDerived.time_text } : {}),
                } : {}),
              };
              const coResult = await window.DB.submitCourse({
                userId:           session.user.id,
                payload,
                isRestricted:     coRestrict.restricted,
                restrictionNote:  coRestrict.restricted ? buildRestrictionNote(coRestrict, lang) : null,
                evidenceFiles:    certFile ? [certFile] : (aiEvidence ? [aiEvidence] : []),
                sourceOrigin:     'co_submit_from_log',
                sourceRecordId:   saveResult.data.id,
                // ‼️ THE ID TRAVELS, THE WORDING DOES NOT. payload's
                // provider_full_en above is still exactly what the nurse typed;
                // this is the only thing the picker adds. Null when she did not
                // choose, and null when her chosen entity no longer matches the
                // wording she has since edited.
                providerEntityIdHint: coProviderHint,
              });
              if (coResult?.verdict && coResult.verdict !== 'CREATED') {
                // ‼️ NOT A FAILURE. The catalogue already has this, or already
                // has her submission of it — the co-submit half was a no-op
                // because it had nothing left to do. Reporting it as an error
                // would teach nurses that the checkbox is unreliable.
                coSubmitOutcome = coResult.verdict === 'NEEDS_CONFIRMATION'
                  ? 'needs_confirmation' : 'already';
              } else if (coResult?.error) {
                coSubmitOutcome = 'failed';
                console.warn('co-submit failed (record still saved):', coResult.error);
              } else if (coResult?.warning) {
                coSubmitOutcome = 'partial';
                console.warn('co-submit evidence partially uploaded:', coResult.warning);
              } else {
                coSubmitOutcome = 'success';
              }
            }
          } catch (e) {
            coSubmitOutcome = 'failed';
            console.warn('co-submit failed (record still saved):', e?.message || e);
          }
        }
      }
      onClose();
      // Confirm the save. When the user also co-submitted to the community
      // catalogue, mention the pending review so they know that half is
      // in-flight; otherwise a plain "added to your records".
      const coSubmitMessage = coSubmitOutcome === 'success'
        ? (lang === 'zh' ? '已加入紀錄 · 課程已提交待審核' : 'Added to your records · course submitted for review')
        : coSubmitOutcome === 'already'
          ? (lang === 'zh' ? '已加入紀錄 · 這堂課你已經提交過' : 'Added to your records · you had already submitted this course')
        : coSubmitOutcome === 'needs_confirmation'
          ? (lang === 'zh' ? '已加入紀錄 · 課程未提交(與你之前的提交相似,請在「提交課程」確認)'
                           : 'Added to your records · course not submitted (similar to one of yours — confirm it in 提交課程)')
        : coSubmitOutcome === 'partial'
          ? (lang === 'zh' ? '紀錄已儲存 · 課程已提交，但部分證據上傳失敗' : 'Record saved · course submitted, but some evidence failed to upload')
          : coSubmitOutcome === 'failed'
            ? (lang === 'zh' ? '紀錄已儲存 · 社群課程提交未完成' : 'Record saved · community submission was not completed')
            : (lang === 'zh' ? '已加入紀錄' : 'Added to your records');
      notifyLog(coSubmitMessage,
        coSubmitOutcome === 'failed' ? 'danger'
          : (coSubmitOutcome === 'partial' || coSubmitOutcome === 'needs_confirmation') ? 'warning'
            : 'success');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={true} onClose={onClose} title={qualificationOnly
      ? (lang === 'zh' ? 'AI 掃描資格證書' : 'AI scan a credential')
      : needsVerification ? (lang === 'zh' ? '核實後記錄' : 'Verify & log')
        : initialCourse ? (lang === 'zh' ? '編輯紀錄' : 'Edit record') : L(STR.logCourse, lang)}>
      <div style={{ padding: '0 20px 24px' }}>
        {aiScanning && <AiScanDialog lang={lang} onCancel={() => aiAbortRef.current?.abort()} />}
        {showContinue && (
          <ContinueLoggingDialog lang={lang} qualificationOnly={qualificationOnly}
            /* The free route. Mirrors the chooser's manual card exactly
               (manualStage reset + step change) so the nurse lands on the same
               empty form she would have reached had she never tapped AI. */
            onManual={() => { setShowContinue(false); setManualStage(0); setStep('manual'); }}
            onSubmitCourse={earnSubmit} onCorrectCourse={earnCorrect} onShare={earnShare}
            onUpgrade={canPurchase() ? () => { setShowContinue(false); setPaywallOpen(true); } : undefined}
            onClose={() => setShowContinue(false)} />
        )}
        {/* Pro paywall. `onEarnInstead` returns to the continuation dialog —
            contributing a course still earns a scan credit AND manual entry is
            still free, so hiding both behind the subscribe button would be a
            dark pattern. Manual logging is free and unlimited regardless. */}
        {paywallOpen && typeof ProSheet === 'function' && (
          <ProSheet lang={lang} profile={profile} trigger="quota"
            userId={profile?.id}
            initialEnt={proEnt}
            onClose={() => setPaywallOpen(false)}
            onEarnInstead={() => { setPaywallOpen(false); setShowContinue(true); }}
            /* isPro now updates itself from the broadcast; the credit strip
               still needs a nudge because Pro scans stop spending credits. */
            onPurchased={() => { refreshCredits(); }} />
        )}
        <AiUrlDialog open={aiUrlOpen} lang={lang} onClose={() => setAiUrlOpen(false)} onSubmit={runAiScan} />
        {step === 'verify' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card" style={{
              border: '0.5px solid color-mix(in oklch, var(--warning) 45%, transparent)',
              background: 'color-mix(in oklch, var(--warning) 10%, transparent)',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 14 }}>
                <Icon name="shield" size={18} stroke={1.8} color="var(--warning)" />
                {lang === 'zh' ? '課程資料需要核實' : 'Course details need checking'}
              </div>
              <div style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--fg-2)' }}>
                {lang === 'zh'
                  ? '此課程的部分資料未能通過系統核實。請在記錄前自行確認以下資訊:'
                  : 'Some of this course’s details could not be verified. Please confirm the following before logging:'}
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.7, color: 'var(--fg-2)' }}>
                <li>{lang === 'zh' ? '課程名稱' : 'Course title'}</li>
                <li>{lang === 'zh' ? '提供機構 (Provider)' : 'Provider'}</li>
                <li>{lang === 'zh' ? '日期' : 'Dates'}</li>
                <li>{lang === 'zh' ? 'CME/CNE 學分點數' : 'CME/CNE points'}</li>
                <li>{lang === 'zh' ? '課程分類' : 'Category'}</li>
              </ul>
              <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
                {lang === 'zh'
                  ? '建議對照你的出席證明或 NCHK 官方記錄再填寫。日期與學分已留空,請自行輸入。'
                  : 'Check against your attendance certificate or official NCHK record. Dates and points have been left blank for you to fill in.'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>
                {lang === 'zh' ? '取消' : 'Cancel'}
              </button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => setStep('manual')}>
                {lang === 'zh' ? '繼續填寫記錄' : 'Continue to log'}
              </button>
            </div>
          </div>
        )}

        {step === 'chooser' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Fastest, most trustworthy path first. The dedicated AI source
                step is also the deep-link target for Home's "Try it now". */}
            {window.DB?.extractCertificate && (
              <button className="card" style={{ display: 'flex', gap: 14, alignItems: 'center', cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'inherit', border: '1px solid var(--primary)', background: 'var(--primary-soft-2)' }}
                      onClick={() => setStep('ai')}>
                <div style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="sparkle" size={18} stroke={1.8} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="h-row">{L(STR.aiScan, lang)}</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{L(STR.aiScanSub, lang)}</div>
                  {/* The three inputs, spelled out. This card used to be named
                      「AI 掃描證書」 and nobody found the poster/link paths —
                      naming only the certificate hid two thirds of the feature
                      and, worse, implied it was only for finished courses.
                      Labels, not buttons: the whole card stays one tap target. */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 7 }}>
                    {[
                      { icon: 'cert',   label: lang === 'zh' ? '證書'      : 'Certificate' },
                      { icon: 'upload', label: lang === 'zh' ? '海報/PDF'  : 'Poster/PDF' },
                      { icon: 'globe',  label: lang === 'zh' ? '課程網址'  : 'Course link' },
                    ].map(k => (
                      <span key={k.icon} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        fontSize: 10.5, fontWeight: 600, lineHeight: 1,
                        padding: '4px 8px', borderRadius: 999,
                        color: 'var(--primary)',
                        background: 'color-mix(in oklch, var(--primary) 10%, transparent)',
                        border: '0.5px solid color-mix(in oklch, var(--primary) 28%, transparent)',
                      }}>
                        <Icon name={k.icon} size={11} stroke={1.9} /> {k.label}
                      </span>
                    ))}
                  </div>
                </div>
                <Icon name="chev-r" size={16} color="var(--primary)" stroke={1.8} />
              </button>
            )}
            {aiNotes && (
              <div style={{ fontSize: 12, padding: '9px 11px', borderRadius: 9,
                            background: 'var(--warning-soft, #fff6dd)', color: 'var(--warning-text, #7a5200)' }}>
                {aiNotes}
              </div>
            )}
            <button className="card" style={{ display: 'flex', gap: 14, alignItems: 'center', cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'inherit', border: '0.5px solid var(--border)' }}
                    onClick={() => setStep('catalog')}>
              <div style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--primary-soft)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="search" size={18} stroke={1.7} />
              </div>
              <div style={{ flex: 1 }}>
                <div className="h-row">{L(STR.fromCatalog, lang)}</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                  {lang === 'zh' ? '從已認可課程中選取' : 'Pick from accredited courses'}
                </div>
              </div>
              <Icon name="chev-r" size={16} color="var(--fg-3)" stroke={1.7} />
            </button>
            <button className="card" style={{ display: 'flex', gap: 14, alignItems: 'center', cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'inherit', border: '0.5px solid var(--border)' }}
                    onClick={() => { setManualStage(0); setStep('manual'); }}>
              <div style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--accent-soft)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="plus" size={18} stroke={2} />
              </div>
              <div style={{ flex: 1 }}>
                <div className="h-row">{L(STR.manualEntry, lang)}</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                  {lang === 'zh' ? '輸入自訂課程詳情' : 'Enter your own course details'}
                </div>
              </div>
              <Icon name="chev-r" size={16} color="var(--fg-3)" stroke={1.7} />
            </button>
          </div>
        )}

        {step === 'ai' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button className="filter-chip" onClick={() => setStep('chooser')} style={{ alignSelf: 'flex-start' }}>
              <Icon name="chev-l" size={13} stroke={1.7} /> {L(STR.back, lang)}
            </button>
            <div className="card" style={{ textAlign: 'center', padding: '20px 16px', background: 'var(--primary-soft-2)', borderColor: 'var(--primary-soft)' }}>
              <div style={{ width: 48, height: 48, borderRadius: 24, margin: '0 auto 10px', background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="sparkle" size={22} stroke={1.8} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, fontWeight: 650 }}>
                <span>{L(STR.aiScan, lang)}</span>
                {isPro && typeof ProBadge === 'function' ? <ProBadge lang={lang} /> : null}
              </div>
              <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5, marginTop: 5 }}>
                {qualificationOnly
                  ? (lang === 'zh'
                    ? '上載資格證書或貼上資料連結。AI 會讀取名稱、簽發機構與日期，並由你確認分類後才儲存。'
                    : 'Upload a credential or paste a source link. AI reads its title, issuer and dates; you confirm the category before saving.')
                  : (lang === 'zh'
                    ? '上載證書、課程海報,或貼上課程網頁連結。我們會先填好資料,再由你核對及儲存。'
                    : 'Upload a certificate or course poster, or paste a course page link. We prefill the record, then you review and save.')}
              </div>
              {/* The enrolled-course case, said out loud. It is the reason the
                  link input exists, and it is not obvious from "scan". */}
              {!qualificationOnly && <div style={{
                marginTop: 9, paddingTop: 9, fontSize: 11.5, lineHeight: 1.45,
                borderTop: '0.5px solid color-mix(in oklch, var(--primary) 22%, transparent)',
                color: 'var(--primary)', fontWeight: 600,
              }}>
                {L(STR.aiScanBoth, lang)}
              </div>}
            </div>
            <input ref={aiFileRef} type="file"
              accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0]; e.target.value = '';
                if (!f) return;
                if (f.size > 8 * 1024 * 1024) {
                  setAiFileError(lang === 'zh' ? '檔案過大。請選擇 8 MB 以內的 PDF、JPG 或 PNG。' : 'File too large. Choose a PDF, JPG or PNG up to 8 MB.');
                  return;
                }
                setAiFileError(''); runAiScan(f);
              }} />
            {/* Two PEER actions. The link path used to be a grey ghost button
                labelled 「或者貼上課程連結」 below the upload — the word "或者"
                and the ghost styling together told nurses it was a fallback,
                so the fastest way to capture a course they'd just enrolled in
                went unused. Same size, same row, no "instead". */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button className="btn btn-primary" style={{ minWidth: 0 }} disabled={aiScanning}
                onClick={() => { if (gateAiScan()) return; aiFileRef.current?.click(); }}>
                <Icon name="upload" size={16} stroke={1.8} />
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {lang === 'zh' ? '選擇檔案或相片' : 'File or photo'}
                </span>
              </button>
              <button type="button" className="btn btn-soft" style={{ minWidth: 0 }} disabled={aiScanning}
                onClick={() => { if (gateAiScan()) return; setAiUrlOpen(true); }}>
                <Icon name="globe" size={15} stroke={1.8} />
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {L(STR.aiScanUrl, lang)}
                </span>
              </button>
            </div>
            {aiFileError ? (
              <div role="alert" style={{ color: 'var(--danger)', fontSize: 12, lineHeight: 1.45 }}>{aiFileError}</div>
            ) : null}
            <div className="muted-2" style={{ fontSize: 10.5, lineHeight: 1.5 }}>{L(STR.aiScanPrivacy, lang)}</div>
            {/* A Pro subscription bypasses the annual credit ledger on the
                server. Keep the dormant free balance for a future lapse, but
                never render scarcity copy while Pro is active. While the
                entitlement is loading, render neither state so a paid user
                cannot see a one-frame free-tier count. */}
            {!proEnt.loading && !isPro && scanCredits?.available != null && (
              <div className="muted" style={{ fontSize: 11.5, textAlign: 'center' }}>
                {/* ‼️ NAME THE METERED THING. This read 「今年次數已用完」 —
                    "次數" with no object, on a screen a nurse reaches while
                    entering CNE records, which is precisely how a scan quota
                    gets mistaken for a record cap. */}
                {scanCredits.available === 0
                  ? (lang === 'zh' ? '今年的免費 AI 掃描已用完 · 手動輸入不限次數' : 'Free AI scans used up · manual entry is still unlimited')
                  : (lang === 'zh' ? `今年剩餘 ${scanCredits.available} 次 AI 掃描` : `${scanCredits.available} AI scans left this year`)}
              </div>
            )}
          </div>
        )}

        {step === 'catalog' && (
          <div>
            <button className="filter-chip" onClick={() => setStep('chooser')} style={{ marginBottom: 12 }}>
              <Icon name="chev-l" size={13} stroke={1.7} /> {L(STR.back, lang)}
            </button>
            <div className="search-bar" style={{ marginBottom: 12 }}>
              <Icon name="search" size={17} color="var(--fg-3)" stroke={1.6} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={lang === 'zh' ? '搜尋課程名稱或課程編號' : 'Search by course name or code'}
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {catalog.length === 0 && (
                <div className="muted" style={{ textAlign: 'center', padding: '20px 0', fontSize: 13 }}>
                  {lang === 'zh' ? '找不到符合的課程' : 'No matching courses'}
                </div>
              )}
              {catalog.map(c => (
                <CourseCard key={c.id} course={c} lang={lang}
                  onClick={() => pickCourse(c)} compact />
              ))}
            </div>
            <div className="muted-2" style={{ fontSize: 11, textAlign: 'center', marginTop: 14 }}>
              {lang === 'zh' ? '選擇課程後可調整日期及上傳證書' : 'Pick a course — you can adjust the date and upload a certificate next'}
            </div>
          </div>
        )}

        {step === 'manual' && qualificationOnly && (
          <div className="qualification-scan-review">
            <button type="button" className="filter-chip" onClick={() => setStep('ai')} style={{ alignSelf: 'flex-start' }}>
              <Icon name="chev-l" size={13} stroke={1.7} /> {L(STR.back, lang)}
            </button>
            <div className="qualification-scan-title">
              <div className="eyebrow">{lang === 'zh' ? '步驟 2 · 核對資格資料' : 'STEP 2 · REVIEW CREDENTIAL'}</div>
              <div className="h-row">{lang === 'zh' ? '核對後儲存至資格錢包' : 'Review, then save to your wallet'}</div>
              <div className="muted">{lang === 'zh' ? '此流程不會建立 CNE 紀錄；證書只會儲存在你的私人資格錢包。' : 'This creates no CNE record. The credential is stored only in your private wallet.'}</div>
            </div>
            {aiPrefilled && (
              <div className="qualification-ai-review-note">
                <Icon name="sparkle" size={18} stroke={1.7} color="var(--warning)" />
                <div><strong>{lang === 'zh' ? 'AI 已填入以下資料，請逐項核對。' : 'AI filled these details. Please check each one.'}</strong><span>{lang === 'zh' ? '名稱、分類、簽發機構、簽發日期及有效期都可以修改。AI 不會驗證證書真偽。' : 'You can change the title, category, issuer, issue date and expiry. AI does not verify authenticity.'}</span>{aiNotes && <small>{aiNotes}</small>}</div>
              </div>
            )}
            <div className="field">
              <label className="field-label">{lang === 'zh' ? '資格名稱' : 'Credential name'}</label>
              <input className="field-input" value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value, title_en: e.target.value, title_zh: lang === 'zh' ? e.target.value : form.title_zh })}
                placeholder={lang === 'zh' ? '例如：高級心臟生命支援（ACLS）' : 'e.g. Advanced Cardiovascular Life Support (ACLS)'} />
            </div>
            <div className="field">
              <label className="field-label">{lang === 'zh' ? '資格分類' : 'Credential category'}</label>
              <QualificationCategoryPicker lang={lang} value={qualificationKind} onChange={setQualificationKind} />
            </div>
            <div className="field">
              <label className="field-label">{lang === 'zh' ? '簽發機構（選填）' : 'Issuer (optional)'}</label>
              <input className="field-input" value={form.provider}
                onChange={e => setForm({ ...form, provider: e.target.value, provider_en: e.target.value, provider_zh: '' })}
                placeholder={lang === 'zh' ? '例如：美國心臟協會 / 醫院名稱' : 'e.g. American Heart Association / hospital'} />
            </div>
            <div className="qualification-date-grid">
              <div className="field"><label className="field-label">{lang === 'zh' ? '簽發日期（選填）' : 'Issued on (optional)'}</label><DateField lang={lang} value={form.startDate} onChange={v => setForm({ ...form, startDate: v })} max={qualificationDoesNotExpire ? undefined : (form.expiresOn || undefined)} /></div>
              {!qualificationDoesNotExpire && <div className="field"><label className="field-label">{lang === 'zh' ? '有效期至' : 'Expires on'}</label><DateField lang={lang} value={form.expiresOn} onChange={v => setForm({ ...form, expiresOn: v })} min={form.startDate || undefined} /></div>}
            </div>
            <label className="toggle-row qualification-toggle"><input type="checkbox" checked={qualificationDoesNotExpire} onChange={e => setQualificationDoesNotExpire(e.target.checked)} /><span>{lang === 'zh' ? '此資格不設到期日' : 'This credential does not expire'}</span></label>
            {certFile && <div className="qualification-scan-file"><Icon name="check-circ" size={16} color="var(--success)" /><span>{lang === 'zh' ? '已附上掃描的證書：' : 'Scanned document attached: '}{certFile.name}</span></div>}
            <label className="qualification-verify-toggle"><input type="checkbox" checked={qualificationVerified} onChange={e => setQualificationVerified(e.target.checked)} /><span>{lang === 'zh' ? '我已按原始證書核對名稱、分類與日期。' : 'I have checked the title, category and dates against the original credential.'}</span></label>
            <LoadingButton className="btn btn-primary btn-fill" isLoading={busy}
              disabled={!form.title.trim() || (!qualificationDoesNotExpire && !form.expiresOn) || (aiPrefilled && !qualificationVerified)}
              loadingLabel={lang === 'zh' ? '儲存中…' : 'Saving…'} leftIcon={<Icon name="check" size={16} stroke={2.2} />} onClick={handleSave}>
              {lang === 'zh' ? '儲存至資格錢包' : 'Save to qualification wallet'}
            </LoadingButton>
          </div>
        )}

        {step === 'manual' && !qualificationOnly && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* When the sheet was opened pre-pointed at a course (編輯紀錄),
                there's no in-sheet step to go back to — "back" closes it. */}
            <button className="filter-chip" onClick={() => {
              if (manualStage > 0) { setManualStage(s => s - 1); return; }
              if (needsVerification) setStep('verify');
              else if (initialCourse) onClose();
              else setStep(form.courseId ? 'catalog' : 'chooser');
            }}>
              <Icon name="chev-l" size={13} stroke={1.7} /> {L(STR.back, lang)}
            </button>
            <div aria-label={lang === 'zh' ? `記錄步驟 ${manualStage + 1} / 3` : `Log step ${manualStage + 1} of 3`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 7 }}>
                <span className="eyebrow" style={{ margin: 0 }}>
                  {manualStage === 0
                    ? (lang === 'zh' ? '1 · 課程與日期' : '1 · Course & dates')
                    : manualStage === 1
                      ? (lang === 'zh' ? '2 · 分數與分類' : '2 · Points & category')
                      : (lang === 'zh' ? '3 · 證書與確認' : '3 · Certificate & review')}
                </span>
                <span className="mono muted-2" style={{ fontSize: 10 }}>{manualStage + 1}/3</span>
              </div>
              <div className="onb-bar"><div className="onb-bar-fill" style={{ width: `${((manualStage + 1) / 3) * 100}%` }} /></div>
            </div>
            {manualStage === 0 && <F>
            {/* Destination, stated up front. The sheet now accepts finished
                courses AND ones the nurse has only just enrolled in, so "where
                does this end up" is a real question with two answers — never
                make her infer it from the date she just typed. Same
                recordDestination() the save path and the AI toast use. */}
            {form.endDate && (() => {
              const dest = recordDestination(form.endDate);
              const inProgress = dest === 'in_progress';
              return (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
                  fontSize: 11.5, fontWeight: 600, padding: '6px 11px', borderRadius: 999,
                  color: inProgress ? 'var(--accent)' : 'var(--success)',
                  background: `color-mix(in oklch, ${inProgress ? 'var(--accent)' : 'var(--success)'} 12%, transparent)`,
                  border: `0.5px solid color-mix(in oklch, ${inProgress ? 'var(--accent)' : 'var(--success)'} 32%, transparent)`,
                }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: inProgress ? 'var(--accent)' : 'var(--success)',
                  }} />
                  {L(inProgress ? STR.destInProgress : STR.destCompleted, lang)}
                </div>
              );
            })()}
            {/* AI-prefill caution — same visual weight as the needsVerification
                banner: the numbers below affect licence renewal, so the human
                check is the product's guarantee, not a nicety. */}
            {aiPrefilled && (
              <div className="card" style={{
                display: 'flex', gap: 10, alignItems: 'flex-start',
                border: '0.5px solid color-mix(in oklch, var(--warning) 45%, transparent)',
                background: 'color-mix(in oklch, var(--warning) 10%, transparent)',
              }}>
                <Icon name="sparkle" size={18} stroke={1.7} color="var(--warning)" />
                <div style={{ flex: 1, fontSize: 12.5, color: 'var(--fg-2)', lineHeight: 1.5 }}>
                  {L(STR.aiScanVerify, lang)}
                  {/* Enumerate exactly which fields the AI filled, so the user
                      knows what to check (and, implicitly, what it couldn't
                      read and left for them). */}
                  {aiSnap && (() => {
                    const fieldLabels = {
                      title:      { en: 'Title',      zh: '名稱' },
                      provider:   { en: 'Provider',   zh: '主辦機構' },
                      start_date: { en: 'Start date', zh: '開始日期' },
                      end_date:   { en: 'End date',   zh: '結束日期' },
                      points:     { en: 'Points',     zh: '分數' },
                      category:   { en: 'Category',   zh: '類別' },
                      venue:      { en: 'Venue',      zh: '地點' },
                    };
                    const filled = Object.keys(aiSnap.values)
                      .filter(k => aiSnap.values[k] !== '' && fieldLabels[k])
                      .map(k => L(fieldLabels[k], lang));
                    if (!filled.length) return null;
                    return (
                      <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
                        {(lang === 'zh' ? 'AI 已填:' : 'AI filled: ') + filled.join(lang === 'zh' ? '、' : ', ')}
                      </div>
                    );
                  })()}
                  {aiNotes && (
                    <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>{aiNotes}</div>
                  )}
                </div>
              </div>
            )}
            {!aiPrefilled && form.courseId && (needsVerification ? (
              // Persistent caution — the pre-step warning is easy to forget
              // once the user is typing the audit-critical number, so the
              // reminder stays in view right next to the fields.
              <div className="card" style={{
                display: 'flex', gap: 10, alignItems: 'center',
                border: '0.5px solid color-mix(in oklch, var(--warning) 45%, transparent)',
                background: 'color-mix(in oklch, var(--warning) 10%, transparent)',
              }}>
                <Icon name="shield" size={18} stroke={1.7} color="var(--warning)" />
                <div style={{ flex: 1, fontSize: 12.5, color: 'var(--fg-2)' }}>
                  {lang === 'zh'
                    ? '請根據證書核實類別、開始/結束日期及學分點數,確認後再儲存。'
                    : 'Check the category, start/end dates and points against your certificate before saving.'}
                </div>
              </div>
            ) : (
              <div className="card" style={{ display: 'flex', gap: 10, alignItems: 'center', background: 'var(--primary-soft-2)', borderColor: 'var(--primary-soft)' }}>
                <Icon name="check-circ" size={18} stroke={1.7} color="var(--primary)" />
                <div style={{ flex: 1, fontSize: 12.5, color: 'var(--fg-2)' }}>
                  {lang === 'zh' ? '從目錄選取的課程詳情已自動填入' : 'Course details prefilled from the catalog'}
                </div>
              </div>
            ))}
            <div className="field">
              <div className="field-label">{L(STR.courseTitle, lang)}</div>
              <input className="field-input" value={form.title}
                     onChange={e => setForm({...form, title: e.target.value, title_en: e.target.value, title_zh: ''})}
                     placeholder={lang === 'zh' ? '例：壓瘡預防工作坊' : 'e.g. Pressure Injury Workshop'} />
            </div>

            {(form.expiresOn || qualificationKindFromTitle(form.title_en || form.title)) && (
              <div className="card" style={{display:'flex',flexDirection:'column',gap:10}}>
                <div><div className="h-row">{lang==='zh'?'加入資格錢包':'Add to qualification wallet'}</div><div className="muted" style={{fontSize:11.5,marginTop:3}}>{lang==='zh'?'把可續期證書、專科資格或臨床能力認證獨立保存；到期前會提醒你。':'Store renewable cards, specialty qualifications and clinical competencies separately; we will remind you before they expire.'}</div></div>
                <label style={{display:'flex',gap:8,alignItems:'flex-start',fontSize:13}}><input type="checkbox" checked={trackQualification} onChange={e=>{setTrackQualification(e.target.checked); if(e.target.checked) setQualificationKind(qualificationKindFromTitle(form.title_en || form.title) || 'other'); else setSaveAsCne(true);}}/><span><strong>{lang==='zh'?'同時加入我的資格':'Also add to My qualifications'}</strong><div className="muted" style={{fontSize:11.5}}>{lang==='zh'?'資格與 CNE 紀錄分開管理，可保留兩者的連結。':'Qualifications are managed separately while keeping a link to this CNE record.'}</div></span></label>
                {trackQualification && <>
                  <div className="field"><label className="field-label">{lang==='zh'?'資格分類':'Qualification category'}</label><QualificationCategoryPicker lang={lang} value={qualificationKind} onChange={setQualificationKind} compact /></div>
                  {!qualificationDoesNotExpire && <div className="field"><label className="field-label">{lang==='zh'?'有效期至（Renew By）':'Renew by / expires on'}</label><DateField lang={lang} value={form.expiresOn || ''} onChange={v=>setForm({...form,expiresOn:v})}/></div>}
                  <label className="toggle-row qualification-toggle"><input type="checkbox" checked={qualificationDoesNotExpire} onChange={e=>setQualificationDoesNotExpire(e.target.checked)}/><span>{lang==='zh'?'此資格不設到期日':'This qualification does not expire'}</span></label>
                  <label style={{display:'flex',gap:8,alignItems:'flex-start',fontSize:13}}><input type="checkbox" checked={saveAsCne} onChange={e=>setSaveAsCne(e.target.checked)}/><span>{lang==='zh'?'同時儲存為 CNE 完成紀錄':'Also save as a completed CNE record'}</span></label>
                </>}
              </div>
            )}
            <div className="field">
              <div className="field-label">{L(STR.provider, lang)}</div>
              <input className="field-input" value={form.provider}
                     onChange={e => setForm({...form, provider: e.target.value, provider_en: e.target.value, provider_zh: ''})}
                     placeholder={lang === 'zh' ? '主辦機構' : 'Organising body'} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="field">
                <div className="field-label">{L(STR.startDate, lang)}</div>
                <DateField className="field-input" lang={lang} value={form.startDate}
                       max={form.endDate || undefined}
                       onChange={v => setForm({...form, startDate: v, sessionsList: []})} />
              </div>
              <div className="field">
                <div className="field-label">{L(STR.endDate, lang)}</div>
                <DateField className="field-input" lang={lang} value={form.endDate}
                       min={form.startDate || undefined}
                       onChange={v => setForm({...form, endDate: v, sessionsList: []})} />
              </div>
            </div>

            <ScheduleFieldButton
              lang={lang}
              sessionsList={form.sessionsList}
              seedDate={form.startDate}
              onChange={(list) => {
                const d = (list && list.length && typeof deriveScheduleFields === 'function')
                  ? deriveScheduleFields(list) : null;
                setForm(f => ({
                  ...f,
                  sessionsList: list,
                  startDate: d ? (d.start_date || f.startDate) : f.startDate,
                  endDate: d ? (d.end_date || '') : f.endDate,
                }));
              }} />
            <button type="button" className="btn btn-primary btn-fill"
              disabled={!form.title.trim() || !form.provider.trim() || !form.startDate || !form.endDate || form.endDate < form.startDate}
              onClick={() => setManualStage(1)}>
              {lang === 'zh' ? '下一步：分數與分類' : 'Next: points & category'}
              <Icon name="arrow-r" size={15} stroke={1.9} />
            </button>
            </F>}

            {manualStage === 1 && <F>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="field">
                <div className="field-label">
                  {L(STR.points, lang)}
                  {pointsOptional && (
                    <span className="muted-2"> ({L(STR.optional, lang)})</span>
                  )}
                </div>
                <input className="field-input" type="number" min="0" step="0.5"
                       value={form.points} onChange={e => setForm({...form, points: e.target.value})} placeholder="0" />
              </div>
              <div className="field" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                {/* Status hint — same recordDestination() as the chip on the
                    previous step and the AI toast. */}
                {form.endDate && (
                  <div className="muted-2" style={{
                    fontSize: 11, padding: '8px 10px',
                    border: '0.5px solid var(--border)',
                    borderRadius: 8,
                    background: 'var(--surface)',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: pointsOptional ? 'var(--accent)' : 'var(--success)',
                    }} />
                    {L(pointsOptional ? STR.destInProgress : STR.destCompleted, lang)}
                  </div>
                )}
              </div>
            </div>
            {/* ‼️ Points are OPTIONAL only for a course that hasn't finished.
                Posters for courses a nurse has just enrolled in very often
                don't print the CNE points yet, and requiring them here was a
                hard block on the whole enrolled-course flow. The record saves
                as 0 with a PTSTBD:1 marker so it can never quietly age into a
                0-point completed record — see isRecordPointsPending. */}
            {pointsOptional && !String(form.points).trim() && (
              <div className="card" style={{
                display: 'flex', gap: 10, alignItems: 'flex-start',
                background: 'var(--bg-2)', borderColor: 'var(--border)',
              }}>
                <Icon name="clock" size={16} stroke={1.7} color="var(--accent)" />
                <div style={{ flex: 1, fontSize: 12, color: 'var(--fg-2)', lineHeight: 1.5 }}>
                  {L(STR.pointsTbdHint, lang)}
                  <div className="muted-2" style={{ fontSize: 11, marginTop: 3 }}>
                    {L(STR.pointsTbdWhy, lang)}
                  </div>
                </div>
              </div>
            )}
            <div className="field">
              {/* Category is the one field that's prefilled (so it *looks*
                  done) yet still affects the audit — it drives the NCHK
                  core-points minimum. Blank fields self-signal "fill me";
                  this one needs an explicit "verify me" marker so it isn't
                  sailed past. Only shown for unverified catalog rows. */}
              <div className="field-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {L(STR.category, lang)}
                {needsVerification && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontSize: 10.5, fontWeight: 600, lineHeight: 1, color: 'var(--warning)',
                    padding: '3px 7px', borderRadius: 999,
                    background: 'color-mix(in oklch, var(--warning) 12%, transparent)',
                    border: '0.5px solid color-mix(in oklch, var(--warning) 35%, transparent)',
                  }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--warning)' }} />
                    {lang === 'zh' ? '需核實' : 'Check'}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {['core','elective','specialty'].map(cat => (
                  <button key={cat}
                    className={'filter-chip' + (form.category === cat ? ' on' : '')}
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={() => setForm({...form, category: cat})}>
                    <CatDot cat={cat} size={6} /> {L(STR[cat], lang)}
                  </button>
                ))}
              </div>
            </div>
            {/* Static NCHK education note — purely informational. The indirect
                5-pt/cycle cap isn't enforced/tracked yet (deferred); this just
                makes nurses aware so they classify realistically. No control,
                stores nothing. */}
            <div className="card" style={{
              display: 'flex', gap: 10, alignItems: 'flex-start',
              background: 'var(--bg-2)', borderColor: 'var(--border)',
            }}>
              <Icon name="book" size={16} stroke={1.7} color="var(--fg-2)" />
              <div style={{ flex: 1, fontSize: 12, color: 'var(--fg-2)', lineHeight: 1.5 }}>
                {lang === 'zh'
                  ? '如課程屬一般管理、語言或 IT 等間接相關範疇,根據 NCHK 指引每週期最多計 5 分。'
                  : 'Indirectly related courses (e.g. management, language, IT) are capped at 5 pts/cycle per NCHK guidelines.'}
              </div>
            </div>
            <button type="button" className="btn btn-primary btn-fill" disabled={pointsMissing}
              onClick={() => setManualStage(2)}>
              {lang === 'zh' ? '下一步：證書與確認' : 'Next: certificate & review'}
              <Icon name="arrow-r" size={15} stroke={1.9} />
            </button>
            </F>}

            {manualStage === 2 && <F>
            {isDuplicate && (
              <div className="card" style={{
                display: 'flex', gap: 10, alignItems: 'flex-start',
                border: '0.5px solid color-mix(in oklch, var(--warning) 45%, transparent)',
                background: 'color-mix(in oklch, var(--warning) 10%, transparent)',
              }}>
                <Icon name="shield" size={18} stroke={1.7} color="var(--warning)" />
                <div style={{ flex: 1, fontSize: 12.5, color: 'var(--fg-2)', lineHeight: 1.5 }}>
                  {lang === 'zh'
                    ? '你在本週期似乎已記錄相同名稱的課程。重複課程不會重複計分；如屬不同場次或重考，可繼續儲存。'
                    : 'A course with the same title already appears in this cycle. Repeats do not earn points again; a different session or re-sit can still be saved.'}
                </div>
              </div>
            )}
            <div className="field">
              <div className="field-label">
                {L(STR.uploadCert, lang)} <span className="muted-2">({L(STR.optional, lang)})</span>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (f.size > 8 * 1024 * 1024) {
                    setCertFileError(lang === 'zh'
                      ? '檔案過大。請選擇 8 MB 以內的 PDF、JPG 或 PNG。'
                      : 'File too large. Choose a PDF, JPG or PNG up to 8 MB.');
                    e.target.value = '';
                    return;
                  }
                  setCertFileError('');
                  setCertFile(f);
                }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-ghost"
                  style={{ flex: 1, justifyContent: 'flex-start', padding: 14, minWidth: 0 }}
                  onClick={() => fileRef.current?.click()}>
                  <Icon
                    name={certFile ? 'check-circ' : 'upload'}
                    size={16} stroke={1.7}
                    color={certFile ? 'var(--success)' : 'currentColor'} />
                  <span style={{ flex: 1, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {certFile
                      ? `${certFile.name} · ${fmtSize(certFile.size)}`
                      : (lang === 'zh' ? '選擇 PDF / 圖片' : 'Choose PDF or image')}
                  </span>
                </button>
                {certFile && (
                  <button type="button" className="btn btn-ghost"
                    style={{ padding: '0 14px', flex: '0 0 auto' }}
                    onClick={() => { setCertFile(null); setCertFileError(''); if (fileRef.current) fileRef.current.value = ''; }}
                    aria-label="Remove file">
                    <Icon name="close" size={16} stroke={1.7} />
                  </button>
                )}
              </div>
              {/* Secondary AI entry: certificate already picked → offer to
                  read it and fill the form (chooser card is the primary). */}
              {certFile && !aiPrefilled && window.DB?.extractCertificate && (
                <>
                  <button type="button" className="btn btn-ghost"
                    style={{ marginTop: 8, width: '100%', justifyContent: 'center' }}
                    disabled={aiScanning}
                    onClick={() => runAiScan(certFile)}>
                    {aiScanning
                      ? <><span className="vital-spinner" /> {L(STR.aiScanning, lang)}</>
                      : <><Icon name="sparkle" size={15} stroke={1.7} /> {L(STR.aiScanFill, lang)}</>}
                  </button>
                  <div className="muted-2" style={{ fontSize: 10.5, marginTop: 4, letterSpacing: '0.01em' }}>
                    {L(STR.aiScanPrivacy, lang)}
                  </div>
                </>
              )}
              <div className="muted-2" style={{ fontSize: 10.5, marginTop: 4, letterSpacing: '0.02em' }}>
                {lang === 'zh'
                  ? '可選 — PDF / JPG / PNG · 最大 8 MB'
                  : 'Optional — PDF / JPG / PNG · 8 MB max'}
              </div>
              {certFileError ? (
                <div role="alert" style={{ color: 'var(--danger)', fontSize: 11.5, marginTop: 6 }}>
                  {certFileError}
                </div>
              ) : null}
            </div>

            {/* Crowdsource co-submit — only meaningful for truly manual
                entries (catalog picks already exist as cne_courses rows).
                Cert file, if uploaded, doubles as evidence. */}
            {!form.courseId && coShareable && (
              <div className="field" style={{
                padding: 12, border: '0.5px solid var(--border)', borderRadius: 10,
                background: 'var(--surface)',
              }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                  {/* ‼️ UNTICKING CLEARS THE SELECTION. Hidden state that
                      survives a cancel comes back on the next tick as a choice
                      the nurse does not remember making — and would travel with
                      the submission. */}
                  <input type="checkbox" style={{ marginTop: 2 }}
                    checked={coSubmitEffective}
                    disabled={!coVerifiable}
                    onChange={e => {
                      const on = e.target.checked;
                      setCoSubmit(on);
                      if (!on) { setCoProviderSelection(null); setCoProviderQuery(null); }
                    }} />
                  <span style={{ flex: 1 }}>
                    <strong>{lang === 'zh' ? '提交到社群課程庫' : 'Submit to community catalogue'}</strong>
                    <div className="muted" style={{ fontSize: 11.5, marginTop: 3, lineHeight: 1.45 }}>
                      {coAlready
                        ? (coGuard.verdict === 'ALREADY_APPROVED'
                            ? (lang === 'zh' ? '✓ 這堂課已收錄,不會重複提交。'
                                             : '✓ Already in the catalogue — it will not be submitted again.')
                            : (lang === 'zh' ? '✓ 已提交 · 審核中,不會重複提交。'
                                             : '✓ Already submitted and under review — it will not be submitted again.'))
                        : (lang === 'zh'
                            ? '審核通過後，其他護士也能找到這堂課。'
                            : 'After review, other nurses will be able to find this course.')}
                    </div>
                    {/* ‼️ SAY WHY, WHERE THE DECISION IS MADE. A disabled
                        checkbox with no reason is the same dead end as one that
                        fails after saving — the nurse still cannot tell what to
                        do about it. She is already attaching a certificate for
                        her own record, so this asks for nothing extra. */}
                    {!coVerifiable && !coAlready && (
                      <div className="muted" style={{ fontSize: 11.5, marginTop: 5, lineHeight: 1.45 }}>
                        {lang === 'zh'
                          ? '要先加證書相片或檔案 —— 審核員要有嘢可以核對,先可以收錄。'
                          : 'Add the certificate photo or file first — a reviewer needs something to check it against.'}
                      </div>
                    )}
                    {/* Privacy reassurance — the biggest opt-in barrier is fear
                        that the uploaded certificate (which carries the nurse's
                        name/ID) becomes public. It doesn't: the cert goes to the
                        private, admin-only `submissions` bucket as evidence, and
                        only the course facts in `payload` ever become a public
                        cne_courses row. Say so at the point of decision. */}
                    <div className="muted" style={{ fontSize: 11, marginTop: 5, lineHeight: 1.45, display: 'flex', alignItems: 'flex-start', gap: 5 }}>
                      <span style={{ flexShrink: 0, marginTop: 1, display: 'inline-flex' }}>
                        <Icon name="shield" size={11} stroke={1.8} />
                      </span>
                      <span>{lang === 'zh'
                        ? '你上傳的證書或圖片僅供審核,不會公開;只有課程資料(名稱、機構、日期、分數)會加入課程庫。'
                        : 'Any certificate or image you upload is used only for review and never shared — only the course details (title, provider, date, points) become public.'}</span>
                    </div>
                  </span>
                </label>
                {coSubmitEffective && (
                  <div style={{ marginTop: 10, paddingLeft: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* ‼️ OPTIONAL, AND THE LABEL SAYS SO. This asks a nurse who
                        came to log a record to answer a catalogue question. If it
                        ever reads as required, it has taken something from the
                        logging funnel to give to the catalogue. Submitting
                        without choosing is a supported answer, not a skipped
                        step (§99).
                        ‼️ AND IT IS THE SAME <ProviderPicker> SubmitCourseSheet
                        uses — one matcher, one dictionary, one set of three
                        loading states. A second picker here would be a second
                        thing to keep true. */}
                    <div className="field">
                      <div className="field-label">
                        {lang === 'zh' ? '配對課程機構' : 'Match the organising body'}{' '}
                        <span className="muted-2">({lang === 'zh' ? '可選' : 'optional'})</span>
                      </div>
                      {coHintStale && (
                        <div className="muted" style={{ fontSize: 11.5, marginBottom: 6 }}>
                          {lang === 'zh'
                            ? '你修改咗主辦機構名稱,之前揀嘅機構已經取消,可以重新揀或者直接提交。'
                            : 'You changed the provider name, so the earlier match was dropped — pick again, or just submit.'}
                        </div>
                      )}
                      {/* ‼️ `rawText` IS A SEARCH BOX, NOT THE RECORD'S FIELD.
                          `onRawText` writes to a local query and never to
                          form.provider: the wording the nurse typed for her own
                          record is hers, and the payload must carry it unchanged.
                          Seeded from that wording so the likely matches are
                          already on screen — prefilling the QUERY is not the same
                          as choosing for her. */}
                      <ProviderPicker
                        lang={lang}
                        rawText={coProviderQuery ?? coProviderText}
                        onRawText={setCoProviderQuery}
                        selection={coHintStale ? null : coProviderSelection}
                        onSelect={(sel) => setCoProviderSelection(
                          sel ? { ...sel, forText: coProviderText } : null)}
                        placeholder={lang === 'zh' ? '搜尋及揀選機構' : 'Search for an organisation'} />
                      <div className="muted" style={{ fontSize: 11, marginTop: 5, lineHeight: 1.45 }}>
                        {lang === 'zh'
                          ? '揀咗之後可以幫我哋更準確辨認課程機構。唔揀都可以提交。'
                          : 'Choosing one helps us identify the organising body accurately. You can submit without it.'}
                      </div>
                    </div>

                    <div className="field">
                      <div className="field-label">
                        {lang === 'zh' ? '地點' : 'Venue'}{' '}
                        <span className="muted-2">({lang === 'zh' ? '可選' : 'optional'})</span>
                      </div>
                      <input className="field-input" value={form.venue || ''}
                        onChange={e => setForm({...form, venue: e.target.value})}
                        placeholder={lang === 'zh' ? '例：醫院演講廳 / Zoom' : 'e.g. hospital lecture hall / Zoom'} />
                    </div>
                    <CommunityRestriction lang={lang} value={coRestrict} onChange={setCoRestrict} />
                  </div>
                )}
              </div>
            )}

            {/* LoadingButton centralises debounce: while `busy` is true
                onClick is short-circuited AND a spinner replaces the
                icon. The form-invalid case stays separately gated by
                disabled so it never spuriously shows "Saving…". */}
            <LoadingButton
              className="btn btn-primary btn-fill"
              style={{ marginTop: 6 }}
              isLoading={busy}
              disabled={!form.title.trim() || (!trackQualification && !form.provider.trim()) || !form.endDate || (saveAsCne && (!form.startDate || form.endDate < form.startDate || pointsMissing)) || (trackQualification && !qualificationDoesNotExpire && !form.expiresOn)}
              loadingLabel={lang === 'zh' ? '儲存中…' : 'Saving…'}
              leftIcon={<Icon name="check" size={16} stroke={2.2} />}
              onClick={handleSave}>
              {L(STR.saveRecord, lang)}
            </LoadingButton>
            </F>}
          </div>
        )}
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SUBMIT COURSE — crowdsource Phase 1A
//
// Opens from the DiscoverScreen empty state when search returns nothing.
// Captures the candidate course's basic facts + optional restriction +
// optional evidence, and writes a pending row to course_submissions.
// Admin reviews via Supabase dashboard (1A) until Phase 4 ships the admin UI.
//
// Required fields: title + provider + start date + (registration URL OR
// at least one evidence file). The OR is the verifiability minimum —
// admin needs something to verify against.
// ─────────────────────────────────────────────────────────────────────────────
// Optional schedule editor used by SubmitCourseSheet and the manual LogSheet.
// Keeps the quick `開始日期` input as the default; this is the richer path for
// multi-session / irregular courses. Holds a canonical sessions_list (array of
// serialised "YYYY-MM-DD[THH:MM]" strings) and reuses SessionScheduleDialog
// (record-extras.jsx) for the actual editing. `seedDate` pre-fills the picker's
// anchor from whatever start date the user already typed.
function ScheduleFieldButton({ lang, sessionsList, seedDate, onChange }) {
  const [open, setOpen] = React.useState(false);
  const summary = (typeof summariseSessionList === 'function')
    ? summariseSessionList(sessionsList, lang) : null;
  const seedSessions = (sessionsList && sessionsList.length && typeof normaliseSessionEntry === 'function')
    ? sessionsList.map(normaliseSessionEntry).filter(Boolean)
    : [];
  return (
    <div className="field">
      <div className="field-label">
        {lang === 'zh' ? '課堂時間' : 'Session schedule'}{' '}
        <span className="muted-2">({lang === 'zh' ? '可選' : 'optional'})</span>
      </div>
      <button type="button" className="btn btn-ghost"
        style={{ justifyContent: 'flex-start', padding: 14, width: '100%' }}
        onClick={() => setOpen(true)}>
        <Icon name="calendar" size={16} stroke={1.7} />
        <span style={{ flex: 1, textAlign: 'left' }}>
          {summary || (lang === 'zh' ? '逐節設定日期與時間' : 'Set each session date & time')}
        </span>
        <Icon name="chev-r" size={14} stroke={1.7} />
      </button>
      <div className="muted-2" style={{ fontSize: 10.5, marginTop: 4 }}>
        {lang === 'zh'
          ? '多節 / 不固定日期課程可逐節填寫;單次課程可略過。'
          : 'For multi-session / irregular courses — single-date courses can skip this.'}
      </div>
      {open && typeof SessionScheduleDialog === 'function' && (
        <SessionScheduleDialog
          course={{ id: '__schedule_editor__', date: seedDate || '', sessions: 1 }}
          lang={lang}
          sessions={seedSessions}
          hasOverride={false}
          onClose={() => setOpen(false)}
          onSave={(entries) => {
            onChange((entries || []).map(serialiseSessionEntry).filter(Boolean));
            setOpen(false);
          }}
          onReset={() => { onChange([]); setOpen(false); }} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SUBMISSION GUARD NOTICE — renders the server's verdict, and decides nothing.
//
// ‼️ THE THREE TIERS STAY VISUALLY DISTINCT. Collapsing them was the first
// version's mistake in miniature: telling a nurse that a course she can already
// open is 「審核中」 sends her to look for a review that finished, and dressing an
// informational look-alike as a wall stops a legitimate second sitting.
//
//   ALREADY_PENDING    the write was refused; it is hers to manage → 我的貢獻
//   ALREADY_APPROVED   the write was refused; it is a real course → 查看課程,
//                      and NO edit/withdraw affordance, because there is nothing
//                      of hers left to edit
//   NEEDS_CONFIRMATION not refused. The evidence is insufficient, not contrary,
//                      so this asks rather than blocks.
//   candidates[]       catalogue likeness. Never gates.
// ─────────────────────────────────────────────────────────────────────────────
function SubmissionGuardNotice({ lang, guard, onOpenCourse, onOpenContributions }) {
  if (!guard) return null;
  const { verdict, existing = [], candidates = [] } = guard;
  const approved = existing.find((e) => e.status === 'approved' && e.approved_course_id);
  const tone = verdict === 'ALREADY_APPROVED' ? 'var(--primary)'
    : verdict === 'ALREADY_PENDING' ? 'var(--warning)'
      : verdict === 'NEEDS_CONFIRMATION' ? 'var(--warning)' : 'var(--fg-2)';
  const box = {
    display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, lineHeight: 1.5,
    border: `0.5px solid color-mix(in oklch, ${tone} 45%, transparent)`,
    background: `color-mix(in oklch, ${tone} 10%, transparent)`,
  };
  const link = {
    background: 'none', border: 'none', padding: 0, marginTop: 6,
    color: 'var(--primary)', font: 'inherit', fontSize: 12, cursor: 'pointer',
    textDecoration: 'underline', textUnderlineOffset: 2,
  };

  if (verdict === 'ALREADY_APPROVED') {
    return (
      <div className="card" style={box}>
        <Icon name="check" size={16} stroke={2} color="var(--primary)" />
        <div style={{ flex: 1 }}>
          <strong>{lang === 'zh' ? '這堂課已收錄' : 'This course is already in the catalogue'}</strong>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
            {lang === 'zh'
              ? '你之前提交的版本已經通過審核，不用再提交一次。'
              : 'A version you submitted has already been approved — no need to submit it again.'}
          </div>
          {approved && onOpenCourse && (
            <button type="button" style={link} onClick={() => onOpenCourse(approved.approved_course_id)}>
              {lang === 'zh' ? '查看課程' : 'View the course'}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (verdict === 'ALREADY_PENDING') {
    return (
      <div className="card" style={box}>
        <Icon name="shield" size={16} stroke={1.7} color="var(--warning)" />
        <div style={{ flex: 1 }}>
          <strong>{lang === 'zh' ? '你已提交這堂課 · 審核中' : 'You already submitted this course · under review'}</strong>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
            {lang === 'zh'
              ? '不用再提交一次。如果資料有錯，可以直接修改那一筆，或者撤回。'
              : 'No need to submit it again. If something is wrong, edit that submission or withdraw it.'}
          </div>
          {onOpenContributions && (
            <button type="button" style={link} onClick={onOpenContributions}>
              {lang === 'zh' ? '查看 / 修改 / 撤回' : 'View, edit or withdraw it'}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (verdict === 'NEEDS_CONFIRMATION') {
    return (
      <div className="card" style={box}>
        <Icon name="shield" size={16} stroke={1.7} color="var(--warning)" />
        <div style={{ flex: 1 }}>
          <strong>{lang === 'zh' ? '你提交過同名同日的課程' : 'You already submitted this course on this date'}</strong>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
            {lang === 'zh'
              ? '如果這是同一日的另一場，或者另一節，請按下面的按鈕確認再提交。'
              : "If this is a different session on the same day, confirm below and it will go through."}
          </div>
          {onOpenContributions && (
            <button type="button" style={link} onClick={onOpenContributions}>
              {lang === 'zh' ? '先看看我提交過什麼' : 'See what I already submitted'}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!candidates.length) return null;
  // Informational only — likeness is not identity, and this must never block.
  return (
    <div className="card" style={{ ...box, border: '0.5px solid var(--border)', background: 'var(--surface)' }}>
      <Icon name="search" size={16} stroke={1.7} />
      <div style={{ flex: 1 }}>
        {lang === 'zh' ? '課程庫入面有相近的課程:' : 'The catalogue has a similar course:'}
        {candidates.slice(0, 3).map((c) => (
          <button key={c.id} type="button" style={{ ...link, display: 'block', textAlign: 'left' }}
                  onClick={() => onOpenCourse?.(c.id)}>
            {c.title_zh && lang === 'zh' ? c.title_zh : (c.title_en || c.title_zh)}
            {c.start_date ? ` · ${c.start_date}` : ''}
          </button>
        ))}
        <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
          {lang === 'zh'
            ? '如果這是同名但不同日期的新一期，請照提交。'
            : 'If this is a new run on different dates, go ahead and submit.'}
        </div>
      </div>
    </div>
  );
}

function SubmitCourseSheet({ lang, prefillTitle, onClose, onSubmitted, onOpenCourse, onOpenContributions }) {
  const [form, setForm] = React.useState({
    titleEn: lang === 'zh' ? '' : (prefillTitle || ''),
    titleZh: lang === 'zh' ? (prefillTitle || '') : '',
    provider: '',
    startDate: '',
    endDate: '',
    points: '',
    fee: '',
    language: '',
    teachingMode: '',
    timeText: '',
    description: '',
    descriptionZh: '',
    courseUrl: '',
    registrationUrl: '',
    venue: '',
    sessionsList: [],
    isRestricted: false,
    restrictAudience: 'members',
    restrictSector: 'public',
    restrictHospitalKey: '',
    restrictMemberName: '',
  });
  // ‼️ THE SELECTION IS A SECOND SLOT, NOT A REPLACEMENT FOR form.provider.
  // form.provider stays the RAW observation and is what reaches
  // payload.provider_full_en; this holds the identity claim. Keeping them apart
  // is what lets §90 hand the resolver two independent sources — and what keeps
  // "she picked X while writing 養和" recoverable for alias learning.
  const [providerSelection, setProviderSelection] = React.useState(null);
  const [files, setFiles] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);
  // Whether this is the user's first-ever successful submission — the
  // role-forming moment. Local heuristic only (no displayed count); set when
  // success renders so the first thank-you can carry stronger framing.
  const [firstTime, setFirstTime] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [submitWarning, setSubmitWarning] = React.useState(null);
  const [aiNotes, setAiNotes] = React.useState(null);
  const fileRef = React.useRef(null);

  // v2.2 AI prefill (poster / PDF / photo / URL) — same extract-certificate
  // pipeline + quota as the Log flow. A scanned File doubles as evidence; a
  // pasted URL lands in registrationUrl — so hasVerifiable is satisfied
  // either way and the submission stays verifiable for the moderator.
  const [aiScanning, setAiScanning] = React.useState(false);
  const [aiUrlOpen, setAiUrlOpen] = React.useState(false);
  const aiFileRef = React.useRef(null);
  const aiAbortRef = React.useRef(null);
  const runAiPrefill = async (input) => {
    if (!input || aiScanning || !window.DB?.extractCertificate) return;
    setAiScanning(true);
    const ctl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    aiAbortRef.current = ctl;
    try {
      // purpose:'submission' → contribution scans are FREE (no quota consume)
      const res = await window.DB.extractCertificate(input, { signal: ctl?.signal, purpose: 'submission' });
      if (res?.error) {
        if (res.error.code === 'cancelled') return; // user tapped 取消 — silent
        setError(res.error.code === 'quota_exhausted' ? L(STR.aiScanQuotaOut, lang)
          // Contribution scans are free but daily-capped. Hitting the cap means
          // this nurse is a heavy contributor, so the copy thanks her rather
          // than reading like an abuse block (see FREE_SUBMISSION_DAILY_CAP).
          : res.error.code === 'free_daily_cap' ? L(STR.aiScanFreeCapped, lang)
          : (res.error.code === 'bad_url' || res.error.code === 'url_fetch_failed') ? L(STR.aiScanUrlFailed, lang)
          : L(STR.aiScanFailed, lang));
        return;
      }
      const x = res.data?.extraction || {};
      const isUrl = !(typeof File !== 'undefined' && input instanceof File);
      if (x.doc_type !== 'course_announcement') {
        setAiNotes(x.notes_for_user || (lang === 'zh'
          ? '這份文件不是課程海報，未有自動覆蓋表格。'
          : 'This document is not a course announcement, so the form was not changed.'));
        return;
      }
      setError(null);
      setAiNotes(x.notes_for_user || null);
      setForm(fm => ({ ...fm,
        titleEn: x.title_en || fm.titleEn,
        titleZh: x.title_zh || fm.titleZh,
        provider: x.provider || fm.provider,
        startDate: x.start_date || fm.startDate,
        endDate:   x.end_date || fm.endDate,
        points:    x.points != null ? String(x.points) : fm.points,
        fee:       x.fee_hkd != null ? String(x.fee_hkd) : fm.fee,
        language:  x.language_en || fm.language,
        teachingMode: x.teaching_mode_en || fm.teachingMode,
        timeText: x.time_text || fm.timeText,
        description: x.description_short_en || fm.description,
        descriptionZh: x.description_short_zh || fm.descriptionZh,
        venue:     x.venue || fm.venue,
        // Same fix as the co-submit path: a printed official address reaches
        // 官方課程來源 from a poster now, not only from a pasted link.
        courseUrl: x.source_url || (isUrl ? input.url : '') || fm.courseUrl,
        registrationUrl: x.registration_url || fm.registrationUrl,
        sessionsList: (Array.isArray(x.sessions_list) && x.sessions_list.length) ? x.sessions_list : fm.sessionsList,
      }));
      if (!isUrl) addFiles([input]); // the scanned poster doubles as evidence
    } finally {
      aiAbortRef.current = null;
      setAiScanning(false);
    }
  };

  // ── THE DUPLICATE GUARD NOW LIVES IN THE DATABASE ─────────────────────────
  // What used to be here was a normalised-containment match against
  // window.allSearchableCourses() — approved ∪ archived cne_courses. It could
  // not see a submission from two minutes ago, which is precisely the shape of
  // every real duplicate we found, and the co-submit door had no guard at all.
  //
  // The authority is now submission_candidate_check / create_course_submission,
  // so BOTH doors inherit it and a third one added later does too. Three tiers,
  // and the client renders them without deciding any of them:
  //   ALREADY_PENDING / ALREADY_APPROVED  the write was refused. No override.
  //   NEEDS_CONFIRMATION                  hers to override, with a token.
  //   candidates[]                        catalogue likeness. Informational.
  //
  // ‼️ THIS PREFLIGHT IS ADVISORY. It can be stale by the time the row is
  // written; create_course_submission re-decides and the unique indexes converge
  // the race. Never gate on this alone.
  const [guard, setGuard] = React.useState(null);
  // ‼️ THE GUARD READS THE CANONICAL TEXT, THE PAYLOAD READS THE RAW TEXT.
  // Selecting an entity deliberately MOVES this key and re-runs the candidate
  // check: canonical wording matches the catalogue better, so the look-alike
  // list improves. That is a LIKENESS refresh and nothing more — §79's finding
  // stands, LIKENESS ≠ IDENTITY, and the hint never reaches the guard (it has
  // no parameter that could carry it). The refresh is achieved by DERIVING a
  // guard input, never by overwriting form.provider.
  const providerGuardText = providerGuardTextOf(form.provider, providerSelection);
  const guardKey = `${form.titleEn}|${form.titleZh}|${providerGuardText}|${form.startDate}`;
  React.useEffect(() => {
    let cancelled = false;
    const title = (form.titleEn || form.titleZh || '').trim();
    if (title.length < 6 || !providerGuardText.trim() || !window.DB?.checkSubmissionCandidate) {
      setGuard(null);
      return undefined;
    }
    const t = setTimeout(async () => {
      const { data } = await window.DB.checkSubmissionCandidate({
        payload: {
          title_en: form.titleEn.trim() || null,
          title_zh: form.titleZh.trim() || null,
          provider_full_en: providerGuardText.trim(),
          start_date: form.startDate || null,
          sessions_list: (form.sessionsList && form.sessionsList.length) ? form.sessionsList : undefined,
          time_text: form.timeText.trim() || null,
        },
      });
      if (cancelled || !data) return;
      setGuard(data.verdict === 'CREATED' && !(data.candidates || []).length
        ? null
        : { verdict: data.verdict, existing: data.own || [],
            candidates: data.candidates || [], token: data.confirm_token || null });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [guardKey, form.sessionsList, form.timeText]);

  const hasVerifiable = !!(form.courseUrl.trim() || form.registrationUrl.trim()) || files.length > 0;
  // ‼️ A SELECTION IS A PROVIDER TOO. Requiring typed text would make the picker
  // unusable on its own — pick HKSH, type nothing, and the button stays dead.
  // An empty raw observation is a TRUE statement; manufacturing one from the
  // canonical label is exactly what this split exists to prevent.
  // ‼️ THE SAME RULE AS THE CO-SUBMIT BOX AND THE WRITE DOOR (db.js
  // submissionIsShareable; the database trigger applies it to old builds). Here
  // the nurse came specifically to share, so the reason is SAID, beside the
  // disabled button, rather than the control silently disappearing.
  const shareable = typeof window.DB?.submissionIsShareable === 'function'
    ? window.DB.submissionIsShareable({ payload: { start_date: form.startDate, end_date: form.endDate, sessions_list: form.sessionsList } })
    : { ok: true };
  const canSubmit = !!((form.titleEn.trim() || form.titleZh.trim())
    && (form.provider.trim() || providerSelection) && form.startDate && hasVerifiable && shareable.ok);

  const addFiles = (fl) => {
    if (!fl || !fl.length) return;
    const next = [...files];
    for (const f of Array.from(fl)) {
      if (f.size > 8 * 1024 * 1024) {
        setError(lang === 'zh'
          ? `${f.name} 超過 8 MB。請壓縮或重新選擇檔案。`
          : `${f.name} exceeds 8 MB. Compress it or choose another file.`);
        continue;
      }
      next.push(f);
    }
    setFiles(next.slice(0, 5));
  };

  const onSubmit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(null);
    setSubmitWarning(null);
    try {
      const session = await window.AuthAPI.getSession();
      if (!session?.user?.id) {
        setError(lang === 'zh' ? '請先登入' : 'Please sign in first');
        setBusy(false);
        return;
      }
      // When the optional schedule editor was used, the canonical sessions_list
      // is authoritative — derive start_date/end_date/sessions from it so the
      // scalars stay consistent (mirrors the SQL derivation on approve). The
      // date inputs are kept in sync on save, so form.startDate already matches.
      const derived = (form.sessionsList && form.sessionsList.length
        && typeof deriveScheduleFields === 'function')
        ? deriveScheduleFields(form.sessionsList)
        : null;
      // ‼️ CHECK THE STATUS AND STOP. cnePointsForSubmission THROWS on invalid
      // rather than returning null, because "the nurse typed something
      // impossible" and "the nurse said nothing" are the two facts issue #42
      // exists to keep apart. Today all three doors silently wrote a negative
      // through; refusing it is the contract being enforced, not new policy.
      {
        const pts = normalizeCnePointsInput(form.points);
        if (pts.status === 'invalid') {
          setError(lang === 'zh' ? 'CNE 分數無效,請輸入 0 或正數。'
                                 : 'CNE points are invalid — enter 0 or a positive number.');
          setBusy(false);
          return;
        }
      }
      const payload = {
        title_en: form.titleEn.trim() || null,
        title_zh: form.titleZh.trim() || null,
        provider_full_en: form.provider.trim(),
        start_date: derived ? derived.start_date : form.startDate,
        end_date: derived ? derived.end_date : (form.endDate || null),
        cne_points: cnePointsForSubmission(form.points),
        fee_hkd: form.fee === '' ? null : Number(form.fee),
        language_en: form.language.trim() || null,
        teaching_mode_en: form.teachingMode || null,
        time_text: form.timeText.trim() || derived?.time_text || null,
        description_short_en: form.description.trim() || null,
        description_short_zh: form.descriptionZh.trim() || null,
        url: form.courseUrl.trim() || null,
        registration_url: form.registrationUrl.trim() || null,
        venue_en: form.venue.trim() || null,
        ...(derived ? {
          sessions: derived.sessions,
          sessions_list: form.sessionsList,
        } : {}),
      };
      const result = await window.DB.submitCourse({
        userId: session.user.id,
        payload,
        // ‼️ THE ID TRAVELS, THE LABEL DOES NOT. payload.provider_full_en above
        // is still the raw wording; this is the only thing the picker adds.
        // §91 made the write door able to carry it — before that, this value
        // would have been dropped in silence.
        providerEntityIdHint: providerSelection?.entityId || null,
        isRestricted: form.isRestricted,
        restrictionNote: form.isRestricted
          ? buildRestrictionNote({ audience: form.restrictAudience, sector: form.restrictSector, hospitalKey: form.restrictHospitalKey, memberName: form.restrictMemberName }, lang)
          : null,
        evidenceFiles: files,
        sourceOrigin: 'manual_submit',
        // Only ever the token the SERVER just issued for THIS state. It is
        // recomputed against the current similarity state on arrival, so a token
        // from a superseded preflight is rejected rather than honoured.
        confirmToken: guard?.verdict === 'NEEDS_CONFIRMATION' ? guard.token : null,
      });
      const e = result?.error;
      if (e) {
        setError(e.message || (lang === 'zh' ? '提交失敗' : 'Submission failed'));
        setBusy(false);
        return;
      }
      // ‼️ A VERDICT IS NOT AN ERROR. The server refused to write, and it handed
      // back the row that already exists so the nurse gets 查看/修改/撤回 instead
      // of a dead end. NEEDS_CONFIRMATION is hers to override; ALREADY_* is not.
      if (result?.verdict && result.verdict !== 'CREATED') {
        setGuard({ verdict: result.verdict, existing: result.existing || [],
                   token: result.confirmToken || null });
        setBusy(false);
        return;
      }
      setSubmitWarning(result?.warning || null);
      // First-ever submission? Read the flag before setting it so the
      // success copy can differentiate the role-forming first moment.
      try {
        setFirstTime(!localStorage.getItem('vital:hasSubmittedCourse'));
        localStorage.setItem('vital:hasSubmittedCourse', '1');
      } catch {}
      setDone(true);
      if (onSubmitted) onSubmitted();
    } catch (err) {
      setError(err?.message || (lang === 'zh' ? '提交失敗' : 'Submission failed'));
      setBusy(false);
    }
  };

  return (
    <Sheet open={true} onClose={onClose}
           title={lang === 'zh' ? '提交課程' : 'Submit a course'}>
      <div style={{ padding: '0 20px 24px' }}>
        {aiScanning && <AiScanDialog lang={lang} onCancel={() => aiAbortRef.current?.abort()} />}
        <AiUrlDialog open={aiUrlOpen} lang={lang} onClose={() => setAiUrlOpen(false)} onSubmit={runAiPrefill} />
        {done ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '24px 0' }}>
            <div style={{ width: 56, height: 56, borderRadius: 28, background: 'var(--primary-soft)',
                          color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="check" size={26} stroke={2.2} />
            </div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>
              {lang === 'zh' ? '已提交' : 'Submitted'}
            </div>
            <div className="muted" style={{ fontSize: 13, textAlign: 'center', maxWidth: 280 }}>
              {firstTime
                ? (lang === 'zh'
                    ? '你提交的課程正在審核中，通過後其他護士也會看到。多謝你一起建立更完整的課程庫。'
                    : "Your course is in review. Once approved, other nurses will see it too. Thanks for helping build a more complete catalogue.")
                : (lang === 'zh'
                    ? '審核完成後會通知你。多謝你幫忙建立社群課程庫。'
                    : "We'll notify you once it's reviewed. Thanks for helping build the community catalogue.")}
            </div>
            {submitWarning && (
              <div style={{ fontSize: 12.5, lineHeight: 1.45, padding: '9px 11px',
                            borderRadius: 9, background: 'var(--warning-soft, #fff6dd)',
                            color: 'var(--warning-text, #7a5200)', maxWidth: 300 }}>
                {lang === 'zh'
                  ? `課程已提交，但有 ${submitWarning.failedFiles?.length || 1} 個證據未能上傳；管理員只會看到已成功保存的附件。`
                  : `Course submitted, but ${submitWarning.failedFiles?.length || 1} evidence file(s) failed to upload. The reviewer will only see saved attachments.`}
              </div>
            )}
            {/* Concrete reward at the moment of contribution — approval grants
                +1 AI-scan credit (scan_credit_config.bonus_per_approval). Mirrors
                the Discover banner's incentive, stated here as a specific
                outcome the submitter will receive. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5,
                          padding: '10px 12px', borderRadius: 10, background: 'var(--primary-soft)',
                          color: 'var(--primary)', maxWidth: 300, lineHeight: 1.4 }}>
              <Icon name="sparkle" size={15} stroke={1.8} />
              <span>{lang === 'zh'
                ? '課程獲批准後，可獲 +1 次 AI 掃描額度。'
                : "Once approved, you'll get +1 AI scan credit."}</span>
            </div>
            <button className="btn btn-primary btn-fill" style={{ marginTop: 8 }} onClick={onClose}>
              {lang === 'zh' ? '完成' : 'Done'}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* v2.2: AI-assisted submission — scan a poster/PDF/photo or paste
                the course page link; extraction prefills the form below. Same
                quota + Google-AI disclosure as the Log flow. */}
            {window.DB?.extractCertificate && (
              <div className="card" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8,
                   background: 'var(--primary-soft-2, var(--surface))', borderColor: 'var(--primary-soft)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
                  <Icon name="sparkle" size={16} stroke={1.8} color="var(--primary)" />
                  {lang === 'zh' ? '用 AI 讀取海報或網址自動填寫' : 'Auto-fill with AI from a poster or link'}
                </div>
                <input ref={aiFileRef} type="file"
                  accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0]; e.target.value = '';
                    if (!f) return;
                    if (f.size > 8 * 1024 * 1024) {
                      setError(lang === 'zh' ? '檔案過大。請選擇 8 MB 以內的檔案。' : 'File too large. Choose a file up to 8 MB.');
                      return;
                    }
                    runAiPrefill(f);
                  }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }}
                    disabled={aiScanning} onClick={() => aiFileRef.current?.click()}>
                    <Icon name="upload" size={14} stroke={1.7} /> {lang === 'zh' ? '揀 PDF / 相片' : 'PDF / photo'}
                  </button>
                  <button type="button" className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }}
                    disabled={aiScanning}
                    onClick={() => setAiUrlOpen(true)}>
                    <Icon name="globe" size={14} stroke={1.7} /> {L(STR.aiScanUrl, lang)}
                  </button>
                </div>
                <div className="muted-2" style={{ fontSize: 10.5 }}>{L(STR.aiScanPrivacy, lang)}</div>
              </div>
            )}
            {/* The model's caveat — multiple courses on one poster, tiered fees
                it refused to pick between, or "this is not a course poster, so
                nothing was filled in". This sheet set aiNotes and never rendered
                it, which made the not-an-announcement answer look like the scan
                had silently done nothing. */}
            {aiNotes && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 11.5,
                   lineHeight: 1.5, padding: '9px 11px', borderRadius: 9,
                   background: 'var(--surface)', border: '0.5px solid var(--border)' }}>
                <span style={{ flexShrink: 0, marginTop: 1, display: 'inline-flex', color: 'var(--primary)' }}>
                  <Icon name="sparkle" size={12} stroke={1.8} />
                </span>
                <span className="muted">{aiNotes}</span>
              </div>
            )}
            {/* The three tiers. Each renders differently on purpose: collapsing
                them would tell a nurse a course she can already open is
                「審核中」, or make an informational look-alike feel like a wall. */}
            <SubmissionGuardNotice lang={lang} guard={guard} onOpenCourse={onOpenCourse}
                                   onOpenContributions={onOpenContributions} />
            <div className="muted-2" style={{ fontSize: 11.5, padding: '10px 12px',
                 background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, lineHeight: 1.5 }}>
              {lang === 'zh'
                ? '此資料將進入待審核狀態，由管理員確認後才會公開。學分與報名資訊請以 provider 官方為準。請遮蔽截圖中第三方的個人資料。'
                : "This will be reviewed before going public. Treat credits and registration info as provisional until verified against the provider's official source. Please mask third-party personal info in any screenshots."}
            </div>

            <div className="field">
              <div className="field-label">{lang === 'zh' ? '英文課程名稱' : 'English course title'} *</div>
              <input className="field-input" value={form.titleEn}
                     onChange={e => setForm({ ...form, titleEn: e.target.value })}
                     placeholder="e.g. Pressure Injury Workshop" />
            </div>
            <div className="field">
              <div className="field-label">{lang === 'zh' ? '中文課程名稱' : 'Chinese course title'} *</div>
              <input className="field-input" value={form.titleZh}
                     onChange={e => setForm({ ...form, titleZh: e.target.value })}
                     placeholder="例：壓瘡預防工作坊" />
              <div className="muted-2" style={{ fontSize: 10.5, marginTop: 4 }}>
                {lang === 'zh' ? '中英文名稱填寫其中一項即可' : 'At least one title language is required'}
              </div>
            </div>

            <div className="field">
              <div className="field-label">{lang === 'zh' ? '主辦機構' : 'Provider'} *</div>
              <ProviderPicker
                lang={lang}
                rawText={form.provider}
                onRawText={(v) => setForm({ ...form, provider: v })}
                selection={providerSelection}
                onSelect={setProviderSelection}
                placeholder={lang === 'zh' ? '主辦機構全名' : 'Organising body'} />
            </div>

            {/* Editing the simple date inputs reverts to single-date mode — it
                clears any sessions_list set via the picker so the two never
                disagree. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="field">
                <div className="field-label">{lang === 'zh' ? '開始日期' : 'Start date'} *</div>
                <DateField className="field-input" lang={lang} value={form.startDate}
                       max={form.endDate || undefined}
                       onChange={v => setForm({ ...form, startDate: v, sessionsList: [] })} />
              </div>
              <div className="field">
                <div className="field-label">{lang === 'zh' ? '結束日期' : 'End date'}</div>
                <DateField className="field-input" lang={lang} value={form.endDate}
                       min={form.startDate || undefined}
                       onChange={v => setForm({ ...form, endDate: v, sessionsList: [] })} />
              </div>
            </div>

            <ScheduleFieldButton
              lang={lang}
              sessionsList={form.sessionsList}
              seedDate={form.startDate}
              onChange={(list) => {
                const d = (list && list.length && typeof deriveScheduleFields === 'function')
                  ? deriveScheduleFields(list) : null;
                setForm(f => ({
                  ...f,
                  sessionsList: list,
                  startDate: d ? (d.start_date || f.startDate) : f.startDate,
                  endDate: d ? (d.end_date || '') : f.endDate,
                }));
              }} />

            <div className="field">
              <div className="field-label">{lang === 'zh' ? 'CNE 學分' : 'CNE points'}</div>
              <input className="field-input" type="number" min="0" step="0.5"
                     value={form.points} onChange={e => setForm({ ...form, points: e.target.value })} placeholder="0" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="field">
                <div className="field-label">{lang === 'zh' ? '費用（HKD）' : 'Fee (HKD)'}</div>
                <input className="field-input" type="number" min="0" step="1"
                       value={form.fee} onChange={e => setForm({ ...form, fee: e.target.value })}
                       placeholder={lang === 'zh' ? '免費請填 0' : 'Enter 0 if free'} />
              </div>
              {/* 4 wheels — start hour/minute, end hour/minute — committing the
                  canonical "HH:MM-HH:MM" that cne_courses.time_text expects. It
                  was a free-text box, which is why the live column carries
                  "7:00pm - 9:00pm" next to "18:30-21:30" next to prose. */}
              <div className="field">
                <div className="field-label">{lang === 'zh' ? '時間' : 'Time'}</div>
                <TimeRangeField className="field-input" lang={lang} value={form.timeText}
                       onChange={v => setForm({ ...form, timeText: v })} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="field">
                <div className="field-label">{lang === 'zh' ? '授課語言' : 'Language'}</div>
                <input className="field-input" value={form.language}
                       onChange={e => setForm({ ...form, language: e.target.value })}
                       placeholder="Cantonese" />
              </div>
              <div className="field">
                <div className="field-label">{lang === 'zh' ? '授課模式' : 'Teaching mode'}</div>
                <select className="field-input" value={form.teachingMode}
                        onChange={e => setForm({ ...form, teachingMode: e.target.value })}>
                  <option value="">{lang === 'zh' ? '待確認' : 'To confirm'}</option>
                  <option value="Online">Online</option>
                  <option value="In-person">In-person</option>
                  <option value="Hybrid">Hybrid</option>
                </select>
              </div>
            </div>
            {/* TWO summaries, because an approved submission becomes a catalogue
                row that both language builds have to render — and data.js's
                descZh falls back to description_short_en, so a Chinese reader
                used to be shown the English one. AI prefill fills both (the
                same summary, translated); either can be corrected by hand, and
                both are optional. */}
            {(lang === 'zh'
              ? [['descriptionZh', '課程簡介（中文）'], ['description', '課程簡介（英文）']]
              : [['description', 'Short description (English)'], ['descriptionZh', 'Short description (中文)']]
            ).map(([key, label]) => (
              <div className="field" key={key}>
                <div className="field-label">{label}</div>
                <textarea className="field-input" rows="3" value={form[key]}
                          onChange={e => setForm({ ...form, [key]: e.target.value })} />
              </div>
            ))}
            <div className="field">
              <div className="field-label">{lang === 'zh' ? '地點' : 'Venue'}</div>
              <input className="field-input" value={form.venue}
                     onChange={e => setForm({ ...form, venue: e.target.value })}
                     placeholder={lang === 'zh' ? '可選' : 'Optional'} />
            </div>

            <div className="field">
              <div className="field-label">{lang === 'zh' ? '官方課程來源' : 'Official course source URL'}</div>
              <input className="field-input" type="url" value={form.courseUrl}
                     onChange={e => setForm({ ...form, courseUrl: e.target.value })}
                     placeholder="https://..." />
            </div>
            <div className="field">
              <div className="field-label">{lang === 'zh' ? '官方報名連結' : 'Official registration URL'}</div>
              <input className="field-input" type="url" value={form.registrationUrl}
                     onChange={e => setForm({ ...form, registrationUrl: e.target.value })}
                     placeholder="https://..." />
              <div className="muted-2" style={{ fontSize: 10.5, marginTop: 4 }}>
                {lang === 'zh' ? '填官方連結 或 附證據（兩者其一即可）' : 'Provide an official URL or attach evidence (at least one)'}
              </div>
            </div>

            <div className="field">
              <CommunityRestriction
                lang={lang}
                value={{
                  restricted:  form.isRestricted,
                  audience:    form.restrictAudience,
                  sector:      form.restrictSector,
                  hospitalKey: form.restrictHospitalKey,
                  memberName:  form.restrictMemberName,
                }}
                onChange={v => setForm({
                  ...form,
                  isRestricted:        v.restricted,
                  restrictAudience:    v.audience,
                  restrictSector:      v.sector,
                  restrictHospitalKey: v.hospitalKey,
                  restrictMemberName:  v.memberName,
                })} />
            </div>

            <div className="field">
              <div className="field-label">
                {lang === 'zh' ? '證據' : 'Evidence'} <span className="muted-2">({lang === 'zh' ? '可選' : 'optional'})</span>
              </div>
              <input ref={fileRef} type="file" multiple
                     accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                     style={{ display: 'none' }}
                     onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
              <button type="button" className="btn btn-ghost"
                style={{ justifyContent: 'flex-start', padding: 14, width: '100%' }}
                onClick={() => fileRef.current?.click()}>
                <Icon name="upload" size={16} stroke={1.7} />
                <span style={{ flex: 1, textAlign: 'left' }}>
                  {files.length
                    ? (lang === 'zh' ? `已選 ${files.length} 個檔案` : `${files.length} file${files.length > 1 ? 's' : ''} selected`)
                    : (lang === 'zh' ? '加海報 / PDF / 截圖' : 'Add poster / PDF / screenshot')}
                </span>
              </button>
              {files.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {files.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5 }}>
                      <Icon name="check-circ" size={12} stroke={1.7} color="var(--success)" />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                      <button className="icon-btn" style={{ padding: 4 }}
                              onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                        <Icon name="close" size={11} stroke={1.7} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="muted-2" style={{ fontSize: 10.5, marginTop: 4 }}>
                {lang === 'zh' ? 'PDF / JPG / PNG · 每檔 8 MB · 最多 5 個' : 'PDF / JPG / PNG · 8 MB each · up to 5'}
              </div>
              {/* Same private-evidence promise as the log-sheet co-submit: these
                  files land in the admin-only `submissions` bucket for review and
                  are never published — only the course facts go into the catalogue. */}
              <div className="muted-2" style={{ fontSize: 10.5, marginTop: 4, lineHeight: 1.45, display: 'flex', alignItems: 'flex-start', gap: 5 }}>
                <span style={{ flexShrink: 0, marginTop: 1, display: 'inline-flex' }}>
                  <Icon name="shield" size={10} stroke={1.8} />
                </span>
                <span>{lang === 'zh'
                  ? '證據只供審核,不會公開;審核通過後只有課程資料會加入課程庫。'
                  : 'Evidence is used for review only and never published — after approval, only the course details become public.'}</span>
              </div>
            </div>

            {error && (
              <div style={{ fontSize: 12, color: 'var(--danger)', padding: '8px 10px',
                            background: 'color-mix(in oklch, var(--danger) 12%, transparent)', borderRadius: 8 }}>
                {error}
              </div>
            )}

            {!shareable.ok && shareable.reason === 'course_ended' && (
              <div role="status" className="muted" style={{ fontSize: 12, padding: '8px 10px', lineHeight: 1.5,
                            background: 'var(--surface-2, var(--surface))', borderRadius: 8 }}>
                {lang === 'zh'
                  ? '呢個課程已經完結。社群課程庫收錄同事仲可以報名嘅課程,所以已完結嘅課程唔可以提交。多謝你嘅分享心意!'
                  : 'This course has already ended. The community catalogue lists courses colleagues can still join, so ended courses can’t be submitted. Thank you for wanting to share!'}
              </div>
            )}

            {/* ‼️ ALREADY_* IS NOT OVERRIDABLE FROM HERE. The server refused to
                write and the button goes away; the notice above carries the way
                forward. NEEDS_CONFIRMATION is different in kind — the evidence
                is insufficient, not contrary — so it stays a button, relabelled
                so the second tap is a decision rather than a repeat. */}
            {guard?.verdict === 'ALREADY_PENDING' || guard?.verdict === 'ALREADY_APPROVED' ? null : (
              <LoadingButton
                className="btn btn-primary btn-fill"
                style={{ marginTop: 6 }}
                isLoading={busy}
                disabled={!canSubmit}
                loadingLabel={lang === 'zh' ? '提交中…' : 'Submitting…'}
                leftIcon={<Icon name="check" size={16} stroke={2.2} />}
                onClick={onSubmit}>
                {guard?.verdict === 'NEEDS_CONFIRMATION'
                  ? (lang === 'zh' ? '這是另一場，仍要提交' : "This is a different session — submit anyway")
                  : (lang === 'zh' ? '提交' : 'Submit')}
              </LoadingButton>
            )}
          </div>
        )}
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// REPORT COURSE — crowdsource Phase 1A
//
// Single-page sheet, evidence optional. 30-second target for the common
// "wrong credits" case. Field-path whitelist matches the apply-report
// Edge Function whitelist (Phase 1B).
// ─────────────────────────────────────────────────────────────────────────────
// The REPORT_FIELD_GROUPS / REPORT_FIELDS_FLAT whitelist lives in
// crowdsource-fields.js (loaded before this file). It's the single source
// of truth — the SQL apply_report() function enumerates the same keys.
// See crowdsource-fields.js header for the "how to add a field" checklist.
// Read via window.REPORT_FIELD_GROUPS / window.REPORT_FIELDS_BY_KEY below.

// Type-aware input for the "suggested value" / "apply value" boxes. The
// field whitelist in crowdsource-fields.js tags each key with an `input`
// descriptor; we render the matching control here so a date field never
// accepts a string like "未滿" (which the SQL ::date cast would then
// reject with `invalid input syntax for type date`).
// Schedule editor for the report flow. `value` is a JSON ARRAY STRING of
// serialised sessions (apply_report casts it ::jsonb). Seeds the picker from the
// reporter's proposed value if any, otherwise from the course's current
// schedule so they edit rather than re-enter.
function ScheduleReportInput({ course, value, onChange, lang }) {
  const [open, setOpen] = React.useState(false);
  let parsed = [];
  try { const a = JSON.parse(value || '[]'); if (Array.isArray(a)) parsed = a; } catch (_) {}
  const summary = (typeof summariseSessionList === 'function')
    ? summariseSessionList(parsed, lang) : null;
  const seedSessions = (parsed.length && typeof normaliseSessionEntry === 'function')
    ? parsed.map(normaliseSessionEntry).filter(Boolean)
    : (course && typeof resolveSessions === 'function' ? resolveSessions(course, null) : []);
  return (
    <>
      <button type="button" className="btn btn-ghost"
        style={{ justifyContent: 'flex-start', padding: 14, width: '100%' }}
        onClick={() => setOpen(true)}>
        <Icon name="calendar" size={16} stroke={1.7} />
        <span style={{ flex: 1, textAlign: 'left' }}>
          {summary || (lang === 'zh' ? '設定正確的課堂時間' : 'Set the correct schedule')}
        </span>
        <Icon name="chev-r" size={14} stroke={1.7} />
      </button>
      {open && typeof SessionScheduleDialog === 'function' && (
        <SessionScheduleDialog
          course={course || { id: '__report__', date: '', sessions: 1 }}
          lang={lang}
          sessions={seedSessions}
          hasOverride={false}
          onClose={() => setOpen(false)}
          onSave={(entries) => {
            const ser = (entries || []).map(serialiseSessionEntry).filter(Boolean);
            onChange(JSON.stringify(ser));
            setOpen(false);
          }}
          onReset={() => { onChange('[]'); setOpen(false); }} />
      )}
    </>
  );
}

// Dropdown for `suggest` fields (teaching mode, language, event type, day of
// week). These have a common option list but an OPEN schema, so we render a
// native <select> (which becomes the iOS wheel picker) PLUS an "Other" item
// that reveals a free-text box for values outside the list (e.g. 講座, a
// bilingual combo, or a legacy free-typed value seeded into the admin apply
// dialog).
//
// ⚠️  Do NOT go back to <input list> + <datalist> here: iOS WebView / Safari
// does not support <datalist>, so it silently degrades to a plain text box —
// the exact bug this replaced. The eslint.config.mjs `no-restricted-syntax`
// rule bans <datalist> to keep it from creeping back.
//
// otherMode is seeded ONCE (lazy initializer) and never auto-flipped by an
// effect: flipping it while the user types would yank the text box away
// mid-word (typing "Zoom" letter-by-letter). Callers pass key={fieldKey} so
// switching fields remounts this with a fresh seed.
function SuggestSelect({ value, onChange, lang, options, placeholder }) {
  const OTHER = '__other__';
  const [otherMode, setOtherMode] = React.useState(
    () => value !== '' && value != null && !options.some(o => o.value === value)
  );
  const selectValue = otherMode
    ? OTHER
    : (options.some(o => o.value === value) ? value : '');
  return (
    <>
      <select className="field-input" value={selectValue}
        onChange={(e) => {
          const v = e.target.value;
          if (v === OTHER) { setOtherMode(true); onChange(''); }
          else { setOtherMode(false); onChange(v); }
        }}>
        <option value="">{lang === 'zh' ? '— 請選擇 —' : '— Select —'}</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{lang === 'zh' ? o.zh : o.en}</option>
        ))}
        <option value={OTHER}>{lang === 'zh' ? '其他（自行輸入）' : 'Other (type your own)'}</option>
      </select>
      {otherMode && (
        <input type="text" className="field-input" style={{ marginTop: 8 }}
          value={value ?? ''} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} autoFocus />
      )}
    </>
  );
}

function ReportFieldValueInput({ fieldKey, value, onChange, placeholder, lang, id, course }) {
  const meta = window.REPORT_FIELDS_BY_KEY?.[fieldKey];
  const input = meta?.input || { kind: 'text' };
  const commonProps = {
    className: 'field-input',
    value: value ?? '',
    onChange: (e) => onChange(e.target.value),
    placeholder,
  };

  if (input.kind === 'schedule') {
    return <ScheduleReportInput course={course} value={value} onChange={onChange} lang={lang} />;
  }
  if (input.kind === 'date') {
    // DateField takes the ISO string directly (not an event), so use the raw
    // `onChange`/`value` here instead of spreading commonProps (whose onChange
    // is event-shaped).
    return <DateField className="field-input" value={value ?? ''} onChange={onChange}
                      placeholder={placeholder} lang={lang} />;
  }
  if (input.kind === 'timerange') {
    // Same event-shape caveat as `date` above: TimeRangeField hands back the
    // string, not an event.
    return <TimeRangeField className="field-input" value={value ?? ''} onChange={onChange}
                           placeholder={placeholder} lang={lang} />;
  }
  if (input.kind === 'number' || input.kind === 'integer') {
    const step = input.kind === 'integer' ? '1' : (input.step || 'any');
    return <input type="number" inputMode="decimal" step={step}
                  min={input.min ?? undefined} {...commonProps} />;
  }
  if (input.kind === 'url') {
    return <input type="url" inputMode="url" {...commonProps} />;
  }
  if (input.kind === 'enum') {
    return (
      <select {...commonProps}>
        <option value="">{lang === 'zh' ? '— 請選擇 —' : '— Select —'}</option>
        {input.options.map(o => (
          <option key={o.value} value={o.value}>{lang === 'zh' ? o.zh : o.en}</option>
        ))}
      </select>
    );
  }
  if (input.kind === 'suggest') {
    // Dropdown + "Other" — NOT <input list>/<datalist> (unsupported on iOS
    // WebView; see SuggestSelect header). key remounts on field switch so
    // otherMode reseeds from the new value.
    return (
      <SuggestSelect
        key={fieldKey}
        value={value}
        onChange={onChange}
        lang={lang}
        options={input.options}
        placeholder={placeholder} />
    );
  }
  return <input type="text" {...commonProps} />;
}

// Per-report-type UX config: drives the reason label/placeholder/required
// state, the suggested-value wording, and whether the evidence drawer
// auto-expands. All five `report_type` values from the SQL check
// constraint are enumerated; adding one to the DB without an entry here
// silently falls back to the `other`-style flow.
const REPORT_TYPE_CONFIG = {
  incorrect_field: {
    needsField: true,
    suggestedValueLabel:  { en: 'Suggested value',                            zh: '建議值' },
    suggestedValuePlaceholder: { en: 'The correct value',                     zh: '正確的值' },
    reasonLabel:    { en: 'Reason',                                           zh: '說明' },
    reasonPlaceholder: { en: 'Anything else worth knowing (optional)',        zh: '其他說明（可選）' },
    reasonRequired: false,
  },
  missing_field: {
    needsField: true,
    suggestedValueLabel:  { en: 'Value to add',                               zh: '應有的值' },
    suggestedValuePlaceholder: { en: 'The value that should be here',         zh: '應該填入的值' },
    reasonLabel:    { en: 'Reason',                                           zh: '說明' },
    reasonPlaceholder: { en: 'Where did you find this value? (optional)',     zh: '此值來源（可選）' },
    reasonRequired: false,
  },
  duplicate: {
    needsField: false,
    reasonLabel:    { en: 'Which course is the duplicate?',                   zh: '與哪個課程重複？' },
    reasonPlaceholder: {
      en: "e.g. course code, title, or URL of the original course",
      zh: '例：原課程編號、名稱或網址',
    },
    reasonRequired: true,
    evidenceRecommended: false,
    typeHint: {
      en: 'Tell us which other listing this duplicates so an admin can merge them.',
      zh: '請告訴我們此課程與哪一個重複,方便管理員合併。',
    },
  },
  course_does_not_exist: {
    needsField: false,
    reasonLabel:    { en: 'How do you know?',                                 zh: '如何得知？' },
    // Placeholder ends with the OR reminder because the auto-expanded
    // evidence drawer below can otherwise read as "screenshot required".
    reasonPlaceholder: {
      en: "e.g. registration page said 'course not found' — or attach a screenshot",
      zh: '例：嘗試報名時顯示「課程不存在」,或附截圖即可',
    },
    reasonRequired: false,
    evidenceRecommended: true,
    // Lead with "either is fine" so the auto-expanded drawer doesn't
    // get misread as a hard requirement. Screenshots-are-faster is the
    // secondary nudge, not the first beat.
    typeHint: {
      en: "Tell us how you know, or attach a screenshot — either one is fine. Screenshots speed up review.",
      zh: '說明你如何得知,或附上截圖 — 任一項即可。附截圖可加快審核。',
    },
  },
  other: {
    needsField: false,
    reasonLabel:    { en: 'Reason',                                           zh: '說明' },
    reasonPlaceholder: { en: "Tell us what's wrong with this course",         zh: '請說明此課程的問題' },
    reasonRequired: true,
    typeHint: {
      en: "Tell us what's wrong — the more specific, the faster we can fix it.",
      zh: '請盡量具體描述問題,我們可以更快處理。',
    },
  },
};

// `initial` (optional) lets callers preset the sheet to a specific field +
// type — used by the per-field ✎ shortcuts on CourseDetailScreen so the
// user lands directly on "fix this field". Shape: { fieldPath?, reportType? }
function ReportCourseSheet({ lang, course, onClose, onSubmitted, initial }) {
  const [reportType, setReportType] = React.useState(initial?.reportType || 'incorrect_field');
  const [fieldPath, setFieldPath] = React.useState(initial?.fieldPath || 'cne_points');
  // initial.suggestedValue pre-fills the value box — used by the "share my
  // edited schedule" shortcut in 我的紀錄, which seeds the reporter's own
  // custom_sessions as the proposed sessions_list.
  const [suggestedValue, setSuggestedValue] = React.useState(initial?.suggestedValue || '');
  const [reason, setReason] = React.useState('');
  const [files, setFiles] = React.useState([]);
  const [showEvidence, setShowEvidence] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [submitWarning, setSubmitWarning] = React.useState(null);
  const fileRef = React.useRef(null);

  const cfg = REPORT_TYPE_CONFIG[reportType] || REPORT_TYPE_CONFIG.other;
  const needsField = cfg.needsField;
  // Submission gate per type:
  //   field types  — must pick a field and supply a value
  //   reason-required types (duplicate, other) — must supply a reason;
  //     evidence alone isn't enough because the admin still won't know
  //     which course / what's wrong
  //   reason-optional types (course_does_not_exist) — reason OR evidence
  //     is acceptable (evidence is the primary signal)
  const canSubmit = needsField
    ? !!(fieldPath && suggestedValue.trim())
    : cfg.reasonRequired
      ? !!reason.trim()
      : !!(reason.trim() || files.length > 0);

  // Auto-expand the evidence drawer when the type benefits from it
  // (currently only course_does_not_exist). User can still collapse if
  // they don't have evidence; this is a hint, not a forced state.
  React.useEffect(() => {
    if (cfg.evidenceRecommended) setShowEvidence(true);
  }, [reportType]);

  // Derive current value from the course object using the same field_path
  // names the SQL whitelist uses, so admin sees what the reporter saw.
  // For fields we can't find on the normalised shape we fall back to the
  // raw Supabase row stuffed onto `course.raw`.
  const currentValue = React.useMemo(() => {
    if (!needsField || !course) return null;
    const raw = course.raw || {};
    // Normalised-shape lookups come first; everything else falls through
    // to the raw row so the user always sees something even for fields
    // the UI doesn't render itself.
    switch (fieldPath) {
      case 'cne_points':            return course.points;
      case 'start_date':            return course.date;
      case 'end_date':              return course.endDate;
      case 'registration_deadline': return course.registrationDeadline || raw.registration_deadline;
      case 'venue_en':              return raw.venue_en || L(course.venue, 'en');
      case 'venue_zh':              return raw.venue_zh || L(course.venue, 'zh');
      case 'registration_url':      return course.registrationUrl || raw.registration_url;
      case 'fee_hkd':               return course.cost;
      case 'fee_hkd_member':        return course.costMember ?? raw.fee_hkd_member;
      case 'fee_hkd_nonmember':     return course.costNonMember ?? raw.fee_hkd_nonmember;
      case 'fee_early_bird_hkd':    return course.costEarlyBird ?? raw.fee_early_bird_hkd;
      case 'sessions':              return course.sessions;
      case 'sessions_list': {
        const list = Array.isArray(course.sessionsList) ? course.sessionsList : null;
        return (list && list.length && typeof summariseSessionList === 'function')
          ? summariseSessionList(list, lang) : null;
      }
      case 'time_text':             return course.time || raw.time_text;
      case 'day_of_week_en':        return L(course.dayOfWeek, 'en') || raw.day_of_week_en;
      case 'teaching_mode_en':      return raw.teaching_mode_en || (course.teachingMode && L(course.teachingMode, 'en'));
      case 'language_en':           return L(course.language, 'en') || raw.language_en;
      case 'title_en':              return L(course.title, 'en');
      case 'title_zh':              return L(course.title, 'zh');
      case 'course_code':           return course.courseCode || raw.course_code;
      case 'capacity':              return course.capacity ?? raw.capacity;
      case 'remaining_capacity':    return course.remainingCapacity ?? raw.remaining_capacity;
      case 'instructors_en':
        return Array.isArray(raw.instructors_en) ? raw.instructors_en.join(', ') : raw.instructors_en;
      case 'target_audience_en':
        return Array.isArray(raw.target_audience_en) ? raw.target_audience_en.join(', ') : raw.target_audience_en;
      default:
        return raw[fieldPath] ?? null;
    }
  }, [fieldPath, needsField, course, lang]);

  const addFiles = (fl) => {
    if (!fl || !fl.length) return;
    const next = [...files];
    for (const f of Array.from(fl)) {
      if (f.size > 8 * 1024 * 1024) {
        setError(lang === 'zh'
          ? `${f.name} 超過 8 MB。請壓縮或重新選擇檔案。`
          : `${f.name} exceeds 8 MB. Compress it or choose another file.`);
        continue;
      }
      next.push(f);
    }
    setFiles(next.slice(0, 5));
  };

  const onSubmit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(null);
    setSubmitWarning(null);
    try {
      const session = await window.AuthAPI.getSession();
      if (!session?.user?.id) {
        setError(lang === 'zh' ? '請先登入' : 'Please sign in first');
        setBusy(false);
        return;
      }
      const result = await window.DB.reportCourseIssue({
        userId: session.user.id,
        courseId: course.id,
        reportType,
        fieldPath: needsField ? fieldPath : null,
        currentValue: needsField ? currentValue : null,
        suggestedValue: needsField ? suggestedValue.trim() : null,
        reason: reason.trim() || null,
        evidenceFiles: files,
      });
      const e = result?.error;
      if (e) {
        setError(e.message || (lang === 'zh' ? '送出失敗' : 'Failed to submit'));
        setBusy(false);
        return;
      }
      setSubmitWarning(result?.warning || null);
      setDone(true);
      if (onSubmitted) onSubmitted();
      // Field corrections show a reward line on success — give it time to read.
      setTimeout(onClose, needsField ? 2600 : 1200);
    } catch (err) {
      setError(err?.message || (lang === 'zh' ? '送出失敗' : 'Failed to submit'));
      setBusy(false);
    }
  };

  return (
    <Sheet open={true} onClose={onClose}
           title={lang === 'zh' ? '回報問題' : 'Report a problem'}>
      <div style={{ padding: '0 20px 24px' }}>
        {done ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '20px 0' }}>
            <div style={{ width: 48, height: 48, borderRadius: 24, background: 'var(--primary-soft)',
                          color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="check" size={22} stroke={2.2} />
            </div>
            <div style={{ fontWeight: 600 }}>
              {lang === 'zh' ? '已收到，謝謝你幫忙改善' : "Got it — thanks for helping improve the data"}
            </div>
            {submitWarning && (
              <div style={{ fontSize: 12.5, lineHeight: 1.45, padding: '8px 10px',
                            borderRadius: 9, background: 'var(--warning-soft, #fff6dd)',
                            color: 'var(--warning-text, #7a5200)', maxWidth: 300 }}>
                {lang === 'zh'
                  ? `回報已送出，但有 ${submitWarning.failedFiles?.length || 1} 個附件未能上傳。`
                  : `Report sent, but ${submitWarning.failedFiles?.length || 1} attachment(s) failed to upload.`}
              </div>
            )}
            {/* Applied corrections earn the reporter an AI-scan credit
                (apply_report → bonus_report). Gated on needsField: only field
                corrections route through apply_report; duplicate / does-not-exist
                / other reports never write a field, so they earn nothing and must
                not promise it. Payout is on admin apply, hence the conditional. */}
            {needsField && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5,
                            padding: '8px 12px', borderRadius: 10, background: 'var(--primary-soft)',
                            color: 'var(--primary)', maxWidth: 300, lineHeight: 1.4, marginTop: 2 }}>
                <Icon name="sparkle" size={14} stroke={1.8} />
                <span>{lang === 'zh'
                  ? '若獲管理員採納，可獲 +1 次 AI 掃描額度。'
                  : "If an admin applies your fix, you'll get +1 AI scan credit."}</span>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="field">
              <div className="field-label">{lang === 'zh' ? '問題類型' : 'Issue type'}</div>
              <select className="field-input" value={reportType} onChange={e => setReportType(e.target.value)}>
                <option value="incorrect_field">{lang === 'zh' ? '欄位資料有誤' : 'Incorrect field'}</option>
                <option value="missing_field">{lang === 'zh' ? '缺少資料' : 'Missing information'}</option>
                <option value="duplicate">{lang === 'zh' ? '重複課程' : 'Duplicate course'}</option>
                <option value="course_does_not_exist">{lang === 'zh' ? '此課程已不存在' : 'Course does not exist'}</option>
                <option value="other">{lang === 'zh' ? '其他' : 'Other'}</option>
              </select>
            </div>

            {cfg.typeHint && (
              <div className="muted-2" style={{ fontSize: 11.5, padding: '8px 10px',
                   background: 'var(--surface)', borderRadius: 8, lineHeight: 1.5 }}>
                {lang === 'zh' ? cfg.typeHint.zh : cfg.typeHint.en}
              </div>
            )}

            {needsField && (
              <>
                <div className="field">
                  <div className="field-label">{lang === 'zh' ? '欄位' : 'Field'}</div>
                  <select className="field-input" value={fieldPath}
                          onChange={e => { setFieldPath(e.target.value); setSuggestedValue(''); }}>
                    {(window.REPORT_FIELD_GROUPS || []).map(g => (
                      <optgroup key={g.label_en} label={lang === 'zh' ? g.label_zh : g.label_en}>
                        {g.items.map(f => (
                          <option key={f.key} value={f.key}>{lang === 'zh' ? f.zh : f.en}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                {currentValue != null && currentValue !== '' && (
                  <div className="muted-2" style={{ fontSize: 11.5, padding: '6px 10px',
                       background: 'var(--surface)', borderRadius: 8 }}>
                    {lang === 'zh' ? '目前資料：' : 'Current: '} <strong>{String(currentValue)}</strong>
                  </div>
                )}
                <div className="field">
                  <div className="field-label">
                    {lang === 'zh' ? cfg.suggestedValueLabel.zh : cfg.suggestedValueLabel.en} *
                  </div>
                  <ReportFieldValueInput
                    fieldKey={fieldPath}
                    value={suggestedValue}
                    onChange={setSuggestedValue}
                    lang={lang}
                    course={course}
                    id="report-suggested"
                    placeholder={lang === 'zh' ? cfg.suggestedValuePlaceholder.zh : cfg.suggestedValuePlaceholder.en} />
                </div>
                {/* Proactively tell contributors the fix is rewarded — the
                    "tell users updating earns quota" surface. Payout lands on
                    admin apply (see success line + apply_report bonus_report). */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5,
                              color: 'var(--primary)', padding: '0 2px' }}>
                  <Icon name="sparkle" size={13} stroke={1.8} />
                  <span>{lang === 'zh'
                    ? '更正獲採納後，可獲 AI 掃描額度。'
                    : 'Applied corrections earn AI scan credits.'}</span>
                </div>
              </>
            )}

            <div className="field">
              <div className="field-label">
                {lang === 'zh' ? cfg.reasonLabel.zh : cfg.reasonLabel.en}
                {' '}
                {cfg.reasonRequired
                  ? '*'
                  : <span className="muted-2">({lang === 'zh' ? '可選' : 'optional'})</span>}
              </div>
              <textarea className="field-input" rows={2} value={reason}
                        onChange={e => setReason(e.target.value)}
                        placeholder={lang === 'zh' ? cfg.reasonPlaceholder.zh : cfg.reasonPlaceholder.en} />
            </div>

            <button className="btn btn-ghost"
                    style={{ justifyContent: 'flex-start', padding: 10, fontSize: 12.5 }}
                    onClick={() => setShowEvidence(v => !v)}>
              <Icon name={showEvidence ? 'chev-d' : 'chev-r'} size={12} stroke={1.7} />
              <span>{lang === 'zh' ? '附上證據（可選 — 可加快審核）' : 'Attach evidence (optional — speeds up review)'}</span>
            </button>

            {showEvidence && (
              <div className="field">
                <input ref={fileRef} type="file" multiple
                       accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                       style={{ display: 'none' }}
                       onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
                <button type="button" className="btn btn-ghost"
                        style={{ justifyContent: 'flex-start', padding: 14, width: '100%' }}
                        onClick={() => fileRef.current?.click()}>
                  <Icon name="upload" size={16} stroke={1.7} />
                  <span style={{ flex: 1, textAlign: 'left' }}>
                    {files.length
                      ? (lang === 'zh' ? `已選 ${files.length} 個檔案` : `${files.length} file${files.length > 1 ? 's' : ''} selected`)
                      : (lang === 'zh' ? '加截圖 / 官方 PDF' : 'Add screenshot / official PDF')}
                  </span>
                </button>
                {files.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {files.map((f, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5 }}>
                        <Icon name="check-circ" size={12} stroke={1.7} color="var(--success)" />
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                        <button className="icon-btn" style={{ padding: 4 }}
                                onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                          <Icon name="close" size={11} stroke={1.7} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {error && (
              <div style={{ fontSize: 12, color: 'var(--danger)', padding: '8px 10px',
                            background: 'color-mix(in oklch, var(--danger) 12%, transparent)', borderRadius: 8 }}>
                {error}
              </div>
            )}

            <LoadingButton
              className="btn btn-primary btn-fill"
              style={{ marginTop: 6 }}
              isLoading={busy}
              disabled={!canSubmit}
              loadingLabel={lang === 'zh' ? '送出中…' : 'Sending…'}
              leftIcon={<Icon name="check" size={16} stroke={2.2} />}
              onClick={onSubmit}>
              {lang === 'zh' ? '送出' : 'Send'}
            </LoadingButton>
          </div>
        )}
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OFFLINE CATALOGUE — explicit offline mode (Step 9 v2 narrowing).
//
// Triggered by app.jsx when cold launch detects offline at the OS level.
// Strictly read-only catalogue browsing: course list (Discover) + course
// detail. Save / enroll / register / report-incorrect remain available
// only when offline=false; in offlineMode the CourseDetailScreen hides
// its account-dependent CTAs (see `offlineMode` prop usage above).
//
// NOT rendered:
//   - SideNav / TabBar (no profile, no record, no reminders)
//   - Home (account-dependent stats)
//   - Auth screens (no point signing in without a network)
//   - Sheets that mutate (LogSheet, SavedSheet, RemindersSheet)
//
// Recovery: a "Try to reconnect" button reloads the WebView via
// window.location.reload(). Capacitor restarts the bundle from scratch;
// if the network is back, normal bootstrap proceeds.
// ─────────────────────────────────────────────────────────────────────────────
function OfflineCatalogueScreen({ lang, setLang }) {
  const [openCourseId, setOpenCourseId] = React.useState(null);
  // Stable no-op handlers — the screens require these props but in
  // offlineMode they're never actually invoked (CTAs are hidden).
  const noopSet = React.useMemo(() => new Set(), []);
  const noop = React.useMemo(() => () => {}, [])();

  return (
    <div className="app">
      <div className="offline-banner" role="status" aria-live="polite">
        <span>離線 — 顯示已快取的課程目錄 / Offline — showing cached course catalogue</span>
      </div>
      <div className="app-main">
        <div className="page-stack">
          <div
            className={'page-layer page-layer--tab' + (openCourseId ? ' is-hidden' : '')}
            aria-hidden={openCourseId ? true : false}
            {...(openCourseId ? { inert: '' } : {})}
          >
            <DiscoverScreen
              lang={lang}
              setLang={setLang}
              onOpenCourse={setOpenCourseId}
            />
          </div>
          {openCourseId ? (
            <div className="page-layer page-layer--detail">
              <CourseDetailScreen
                courseId={openCourseId}
                lang={lang}
                onBack={() => setOpenCourseId(null)}
                savedIds={noopSet}
                toggleSaved={noop}
                enrolledIds={noopSet}
                toggleEnrolled={noop}
                offlineMode={true}
              />
            </div>
          ) : null}
        </div>
      </div>
      <div className="offline-footer">
        <button
          className="btn btn-ghost btn-fill"
          onClick={() => { try { window.location.reload(); } catch (_) {} }}
        >
          {lang === 'zh' ? '重試連線' : 'Try to reconnect'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BIOMETRIC ENROL — one-time prompt shown after a fresh sign-in when the
// device supports Face ID / Touch ID and the user hasn't enrolled yet.
// Caller (app.jsx) owns the show/dismiss state, the per-user "asked"
// flag, and the underlying BiometricAPI.enable() call — this component
// just renders the question.
// ─────────────────────────────────────────────────────────────────────────────
function BiometricEnrolSheet({ lang, biometryType, onEnable, onDecline }) {
  const [busy, setBusy] = React.useState(false);
  const typeLabel = biometryType === 'touchId' ? 'Touch ID' : 'Face ID';

  const handleEnable = async () => {
    if (busy) return;
    setBusy(true);
    try { await onEnable(); }
    finally { setBusy(false); }
  };

  return (
    <Sheet
      open={true}
      onClose={busy ? undefined : onDecline}
      title={lang === 'zh' ? `啟用 ${typeLabel}?` : `Enable ${typeLabel}?`}
    >
      <div style={{ padding: '12px 20px 24px' }}>
        <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>
          {lang === 'zh'
            ? `下次開啟 Vital 時可以用 ${typeLabel} 快速登入。`
            : `Sign in faster next time using ${typeLabel}.`}
        </p>
        <ul style={{ fontSize: 13, lineHeight: 1.6, paddingLeft: 18, marginBottom: 20, color: 'var(--fg-2)' }}>
          <li>
            {lang === 'zh'
              ? '密碼不會儲存在裝置上'
              : 'Your password is never stored on this device'}
          </li>
          <li>
            {lang === 'zh'
              ? `${typeLabel} 只用於解鎖目前的登入狀態`
              : `${typeLabel} only unlocks your existing session`}
          </li>
          <li>
            {lang === 'zh'
              ? '可隨時在「我的 → 安全」關閉'
              : 'You can turn it off anytime in Profile → Security'}
          </li>
        </ul>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            className="btn btn-primary btn-fill"
            onClick={handleEnable}
            disabled={busy}
          >
            {busy
              ? (lang === 'zh' ? '處理中…' : 'Working…')
              : (lang === 'zh' ? `啟用 ${typeLabel}` : `Enable ${typeLabel}`)}
          </button>
          <button
            className="btn btn-ghost btn-fill"
            onClick={onDecline}
            disabled={busy}
          >
            {lang === 'zh' ? '稍後再說' : 'Not now'}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PUSH SOFT-ASK — pre-permission priming, offered ONCE after a value moment
// (first course saved, or first record logged). This is NOT the iOS system
// prompt: it's in-app UI that explains the benefit so a "Not now" never burns
// the one-shot system dialog. Only on "Turn on notifications" do we call the
// existing two-stage PushAPI.requestAndRegister(). `moment` ('save' | 'record')
// picks the benefit copy. Mirrors BiometricEnrolSheet's structure.
// ─────────────────────────────────────────────────────────────────────────────
function PushSoftAskSheet({ lang, moment, onEnable, onDismiss }) {
  const [busy, setBusy] = React.useState(false);
  const handleEnable = async () => {
    if (busy) return;
    setBusy(true);
    try { await onEnable(); }
    finally { setBusy(false); }
  };
  const body = moment === 'record' ? STR.pushAskBodyRecord : STR.pushAskBodySave;
  return (
    <Sheet open={true} onClose={busy ? undefined : onDismiss} title={L(STR.pushAskTitle, lang)}>
      <div style={{ padding: '12px 20px 24px' }}>
        <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>{L(body, lang)}</p>
        <p style={{ fontSize: 12, lineHeight: 1.5, marginBottom: 20, color: 'var(--fg-3)' }}>
          {L(STR.pushAskFootnote, lang)}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button className="btn btn-primary btn-fill" onClick={handleEnable} disabled={busy}>
            {busy ? (lang === 'zh' ? '處理中…' : 'Working…') : L(STR.pushAskEnable, lang)}
          </button>
          <button className="btn btn-ghost btn-fill" onClick={onDismiss} disabled={busy}>
            {L(STR.pushAskLater, lang)}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// REVIEW ASK — satisfaction triage, shown at most once per cooldown after a
// value moment (a successful record save; ALL gating lives in app.jsx
// maybeOfferReview). It routes:
//   好用 👍       → onPositive → app.jsx immediately opens Vital's App Store
//                   "Write a Review" page from the original tap;
//   有意見想反映   → onFeedback → the existing FeedbackSheet (admin-reply
//                   loop) — a dissatisfied user is never sent near the store;
//   close/swipe   → onDismiss → nothing; the ask is already spent (cooldown
//                   marked at open), so a dismissal never nags again.
// Mirrors PushSoftAskSheet's structure; no busy state (nothing async inside).
// ─────────────────────────────────────────────────────────────────────────────
function ReviewAskSheet({ lang, onPositive, onFeedback, onDismiss }) {
  return (
    <Sheet open={true} onClose={onDismiss} title={L(STR.reviewAskTitle, lang)}>
      <div style={{ padding: '12px 20px 24px' }}>
        <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>{L(STR.reviewAskBody, lang)}</p>
        <p style={{ fontSize: 12, lineHeight: 1.5, marginBottom: 20, color: 'var(--fg-3)' }}>
          {L(STR.reviewAskFootnote, lang)}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button className="btn btn-primary btn-fill" onClick={onPositive}>
            {L(STR.reviewAskPositive, lang)}
          </button>
          <button className="btn btn-ghost btn-fill" onClick={onFeedback}>
            {L(STR.reviewAskFeedback, lang)}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HandleReportSheet — report an inappropriate contributor handle. The handle is
// resolved to a user id SERVER-SIDE (report_inappropriate_handle), so the
// client never learns any contributor's user id. A dedicated minimal flow — NOT
// the field-correction ReportCourseSheet — so it shows no field inputs.
// ─────────────────────────────────────────────────────────────────────────────
function HandleReportSheet({ lang, handle, onClose, onSubmit }) {
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try { await onSubmit(reason.trim() || null); }
    finally { setBusy(false); }
  };
  return (
    <Sheet open={true} onClose={busy ? undefined : onClose} title={lang === 'zh' ? '檢舉不當化名' : 'Report handle'}>
      <div style={{ padding: '12px 20px 24px' }}>
        <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>
          {lang === 'zh'
            ? <>你正在檢舉貢獻者化名 <strong>{handle}</strong>。管理員會審核,並在需要時重置該化名。</>
            : <>You are reporting the contributor handle <strong>{handle}</strong>. Moderators will review it and reset the handle if needed.</>}
        </p>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder={lang === 'zh' ? '原因（選填）' : 'Reason (optional)'}
          rows={3}
          style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid var(--border)', marginBottom: 20, resize: 'vertical', font: 'inherit' }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button className="btn btn-primary btn-fill" onClick={submit} disabled={busy}>
            {busy ? (lang === 'zh' ? '送出中…' : 'Sending…') : (lang === 'zh' ? '送出檢舉' : 'Submit report')}
          </button>
          <button className="btn btn-ghost btn-fill" onClick={onClose} disabled={busy}>
            {lang === 'zh' ? '取消' : 'Cancel'}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FieldContributionsDialog — "who contributed what fields". Lists the course
// creator + each APPLIED field correction with the contributor's avatar/name
// (resolved server-side per their attribution_visibility). Opened from a
// per-field 🛡 mark or the top attribution line on the course detail page.
// ─────────────────────────────────────────────────────────────────────────────
function ContribRow({ r, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <UserAvatar size={30} displayText={r.display_text} hue={r.hue} emoji={r.emoji} isPrivate={r.is_private} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{r.display_text}</div>
        <div style={{ fontSize: 11.5, color: 'var(--fg-2)' }}>{label}</div>
      </div>
    </div>
  );
}

function FieldContributionsDialog({ lang, courseId, onClose }) {
  const [rows, setRows] = React.useState(null); // null = loading
  React.useEffect(() => {
    let alive = true;
    (async () => {
      const res = await window.DB.getCourseFieldContributions(courseId);
      if (alive) setRows((res && res.data) || []);
    })();
    return () => { alive = false; };
  }, [courseId]);

  // field_path → localized label, from the shared REPORT_FIELD_GROUPS.
  const fieldLabel = React.useMemo(() => {
    const map = {};
    (window.REPORT_FIELD_GROUPS || []).forEach(g => (g.items || []).forEach(it => { map[it.key] = it; }));
    return (key) => {
      const it = map[key];
      if (!it) return key || (lang === 'zh' ? '課程資料' : 'course details');
      return lang === 'zh' ? it.zh : it.en;
    };
  }, [lang]);

  const creators = (rows || []).filter(r => r.role === 'creator');
  const correctors = (rows || []).filter(r => r.role === 'corrector');

  return (
    <Sheet open={true} onClose={onClose} title={lang === 'zh' ? '貢獻紀錄' : 'Contributions'}>
      <div style={{ padding: '8px 20px 24px' }}>
        {rows === null ? (
          <div style={{ fontSize: 13, color: 'var(--fg-2)', padding: '12px 0' }}>{lang === 'zh' ? '載入中…' : 'Loading…'}</div>
        ) : rows.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--fg-2)', padding: '12px 0' }}>
            {lang === 'zh' ? '暫無社群貢獻紀錄。' : 'No community contributions yet.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {creators.map((r, i) => (
              <ContribRow key={'c' + i} r={r} label={lang === 'zh' ? '提交此課程' : 'Submitted this course'} />
            ))}
            {correctors.map((r, i) => (
              <ContribRow key={'x' + i} r={r}
                label={(lang === 'zh' ? '更正「' : 'Corrected ') + fieldLabel(r.field_path) + (lang === 'zh' ? '」' : '')} />
            ))}
          </div>
        )}
        <p style={{ fontSize: 11.5, color: 'var(--fg-3)', marginTop: 16, lineHeight: 1.5 }}>
          {lang === 'zh'
            ? '只顯示已通過審核的貢獻。選擇匿名的護士會顯示為「社群護士」。'
            : 'Only approved contributions are shown. Nurses who chose to stay anonymous appear as "Community nurse".'}
        </p>
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HandleInviteSheet — one-time D2 invite after a contribution is approved,
// offering to pick a public handle. "Set a handle" routes to Edit Profile →
// community settings; "Stay anonymous" just dismisses (already stamped, so it
// never re-appears).
// ─────────────────────────────────────────────────────────────────────────────
function HandleInviteSheet({ lang, onSetHandle, onDismiss }) {
  return (
    <Sheet open={true} onClose={onDismiss} title={lang === 'zh' ? '你的貢獻已通過審核 🎉' : 'Your contribution was approved 🎉'}>
      <div style={{ padding: '12px 20px 24px' }}>
        <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 20 }}>
          {lang === 'zh'
            ? '想讓其他護士看到是你幫忙建立這門課嗎?你可以設定一個公開化名。預設保持匿名(顯示「社群護士」),隨時可更改。'
            : 'Want other nurses to see you helped build this course? Set a public handle. You stay anonymous ("Community nurse") by default and can change this anytime.'}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button className="btn btn-primary btn-fill" onClick={onSetHandle}>
            {lang === 'zh' ? '設定化名' : 'Set a handle'}
          </button>
          <button className="btn btn-ghost btn-fill" onClick={onDismiss}>
            {lang === 'zh' ? '保持匿名' : 'Stay anonymous'}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

// One-time "what's new" announcement (dual practising certificates), shown once
// to a nurse who onboarded BEFORE this feature the first time they open the
// updated app — so existing users discover that they can now track a second
// certificate. Gated in app.jsx (localStorage + onboarded check); brand-new
// users never see it because onboarding already exposes the capability. The CTA
// jumps straight to Edit Profile where the second certificate is added.
function WhatsNewDualLicenseSheet({ lang, onGoToProfile, onDismiss }) {
  const isZh = lang === 'zh';
  return (
    <Sheet open={true} onClose={onDismiss} title={isZh ? '新功能：多張執業證書 🎉' : 'New: multiple certificates 🎉'}>
      <div style={{ padding: '12px 20px 24px' }}>
        <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 20 }}>
          {isZh
            ? '如果你持有多於一張護士執業證書（例如同時註冊普通科及精神科），現在可以分別追蹤每張證書的 CNE 週期、續期提醒及匯出紀錄。到「編輯個人資料」即可新增第二張證書。'
            : 'If you hold more than one nursing certificate (for example both a General and a Psychiatric registration), you can now track each certificate\'s CNE cycle, renewal reminder and export separately. Add your second certificate under Edit profile.'}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button className="btn btn-primary btn-fill" onClick={onGoToProfile}>
            {isZh ? '前往新增' : 'Add a certificate'}
          </button>
          <button className="btn btn-ghost btn-fill" onClick={onDismiss}>
            {isZh ? '稍後再說' : 'Maybe later'}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DELETE ACCOUNT — destructive confirmation with password re-auth.
//
// Two-phase Sheet so the user can't fat-finger Delete on first tap. Phase 1
// is the warning copy + Continue / Cancel. Phase 2 is provider-aware:
//   - 'email' users: type current password → onConfirm(password)
//   - 'apple' users: tap "Continue with Apple" → onConfirm(null), which
//     triggers a SIWA re-auth (native: inline; web: full-page redirect
//     resumed by the marker handler in app.jsx).
// In both cases onConfirm proves the caller still owns the session before
// any irreversible work runs.
//
// onConfirm(secret) is provided by app.jsx. For email it receives the
// typed password; for Apple it receives null. It returns { ok: true } on
// success or { error: { message } }, EXCEPT on web Apple where it never
// resolves locally — the page redirects to Apple and the deletion resumes
// after the OAuth round-trip via the app.jsx marker hook.
// ─────────────────────────────────────────────────────────────────────────────
function DeleteAccountSheet({ lang, email, provider, onClose, onConfirm }) {
  const [phase, setPhase] = React.useState('warn'); // 'warn' | 'confirm'
  const [pw, setPw] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  const isGoogle = provider === 'google';
  const isOAuth = provider === 'apple' || isGoogle;
  const providerName = isGoogle ? 'Google' : 'Apple';

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!isOAuth && !pw) return setErr(lang === 'zh' ? '請輸入密碼' : 'Please enter your password.');
    setBusy(true); setErr('');
    const res = await onConfirm(isOAuth ? null : pw);
    if (res?.error) {
      setBusy(false);
      setErr(res.error.message || (lang === 'zh' ? '無法刪除帳戶。' : 'Could not delete your account.'));
      return;
    }
    // onConfirm handles closing the sheet / routing — do not setBusy(false)
    // here, because by the time onConfirm resolves successfully the sheet
    // has already been unmounted (native OAuth + email paths). On web
    // OAuth the page redirects, so setBusy state is moot.
  };

  return (
    <Sheet open={true} onClose={onClose} title={lang === 'zh' ? '刪除帳戶' : 'Delete account'}>
      <div style={{ padding: '12px 20px 24px' }}>
        {phase === 'warn' ? (
          <F>
            <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>
              {lang === 'zh'
                ? '此操作無法復原。'
                : 'This action is permanent and cannot be undone.'}
            </p>
            <ul style={{ fontSize: 13, lineHeight: 1.6, paddingLeft: 18, marginBottom: 20, color: 'var(--fg-2)' }}>
              <li>{lang === 'zh' ? '個人資料、CNE 紀錄與已上傳的證書將被永久刪除' : 'Your profile, CNE records, and uploaded certificates will be permanently deleted'}</li>
              <li>{lang === 'zh' ? '已儲存與已報名課程將被移除' : 'Saved and enrolled courses will be removed'}</li>
              <li>{lang === 'zh' ? '已排定的提醒將被取消' : 'Scheduled reminders will be cancelled'}</li>
              <li>{lang === 'zh' ? '帳戶無法復原；如需再次使用，需重新註冊' : 'The account cannot be restored — you will need to sign up again'}</li>
            </ul>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost btn-fill" onClick={onClose}>
                {lang === 'zh' ? '取消' : 'Cancel'}
              </button>
              <button
                className="btn btn-fill"
                style={{ background: 'var(--danger, #c0392b)', color: '#fff' }}
                onClick={() => setPhase('confirm')}
              >
                {lang === 'zh' ? '繼續' : 'Continue'}
              </button>
            </div>
          </F>
        ) : isOAuth ? (
          <form onSubmit={submit}>
            <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 14 }}>
              {lang === 'zh'
                ? `請以 ${providerName} 重新驗證 ${email} 以確認永久刪除帳戶。`
                : `Re-verify ${email} with ${providerName} to permanently delete your account.`}
            </p>
            {err ? (
              <p style={{ marginBottom: 12, color: 'var(--danger, #c0392b)', fontSize: 13 }}>{err}</p>
            ) : null}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="submit"
                aria-label={lang === 'zh' ? `以 ${providerName} 確認刪除` : `Continue with ${providerName} to confirm deletion`}
                disabled={busy}
                style={{
                  width: '100%',
                  minHeight: 44,
                  padding: '11px 16px',
                  background: isGoogle ? '#4285F4' : '#000',
                  color: '#fff',
                  border: 0,
                  borderRadius: 12,
                  fontSize: 15,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  cursor: busy ? 'default' : 'pointer',
                  opacity: busy ? 0.7 : 1,
                }}
              >
                {busy ? (
                  <span
                    aria-hidden="true"
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      border: '2px solid rgba(255,255,255,0.35)',
                      borderTopColor: '#fff',
                      animation: 'vital-spin 0.7s linear infinite',
                    }}
                  />
                ) : isGoogle ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                ) : (
                  <svg width="16" height="20" viewBox="0 0 16 20" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M13.07 10.62c-.02-2.21 1.8-3.27 1.88-3.32-1.03-1.5-2.62-1.71-3.19-1.73-1.36-.14-2.65.8-3.34.8-.69 0-1.75-.78-2.88-.76-1.48.02-2.85.86-3.61 2.18-1.54 2.67-.39 6.62 1.11 8.79.73 1.06 1.6 2.25 2.73 2.21 1.1-.04 1.51-.71 2.84-.71 1.33 0 1.7.71 2.86.69 1.18-.02 1.93-1.08 2.65-2.14.84-1.23 1.18-2.42 1.2-2.48-.03-.01-2.31-.88-2.34-3.51zM10.86 4.16c.6-.74 1.01-1.75.9-2.76-.87.04-1.93.58-2.55 1.3-.55.64-1.04 1.68-.91 2.67.97.07 1.96-.49 2.56-1.21z"
                    />
                  </svg>
                )}
                <span>
                  {busy
                    ? (lang === 'zh' ? '處理中…' : 'Continuing…')
                    : (lang === 'zh' ? `以 ${providerName} 確認刪除` : `Continue with ${providerName} to confirm deletion`)}
                </span>
              </button>
              <button type="button" className="btn btn-ghost btn-fill" onClick={onClose} disabled={busy}>
                {lang === 'zh' ? '取消' : 'Cancel'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={submit}>
            <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 14 }}>
              {lang === 'zh'
                ? `輸入 ${email} 的密碼以確認永久刪除帳戶。`
                : `Enter the password for ${email} to permanently delete your account.`}
            </p>
            <input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder={lang === 'zh' ? '目前密碼' : 'Current password'}
              autoFocus
              autoComplete="current-password"
              disabled={busy}
              style={{
                width: '100%', padding: '12px 14px', fontSize: 16,
                border: '1px solid var(--border)', borderRadius: 12,
                marginBottom: 12, background: 'var(--surface)', color: 'var(--fg)',
              }}
            />
            {err ? (
              <p style={{ marginBottom: 12, color: 'var(--danger, #c0392b)', fontSize: 13 }}>{err}</p>
            ) : null}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-ghost btn-fill" onClick={onClose} disabled={busy}>
                {lang === 'zh' ? '取消' : 'Cancel'}
              </button>
              <button
                type="submit"
                className="btn btn-fill"
                style={{ background: 'var(--danger, #c0392b)', color: '#fff' }}
                disabled={busy}
              >
                {busy
                  ? (lang === 'zh' ? '刪除中…' : 'Deleting…')
                  : (lang === 'zh' ? '永久刪除帳戶' : 'Delete account permanently')}
              </button>
            </div>
          </form>
        )}
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PROFILE
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────
// Ward leaderboard (病房排行榜) — the density engine. A SideNav destination
// (not a tab): create/join private ward groups and see each colleague's CNE
// progress toward their requirement. Progress is computed SERVER-SIDE
// (get_group_progress mirrors licenseProgress); member point totals never cross
// RLS — only progress % + badges. Primary licence only (v1).
// ─────────────────────────────────────────────────────────────────
function wardHue(str) {
  let h = 0;
  for (let i = 0; i < (str || '').length; i++) h = (h * 31 + str.charCodeAt(i)) % 360;
  return h;
}

function WardMemberRow({ m, lang, onNudge, nudged, nudging }) {
  const hue = wardHue(m.handle);
  const initial = (m.handle || '?').trim().charAt(0).toUpperCase();
  // A colleague (not you) with a real handle can be nudged. Cleared-handle
  // members (SQL fallback '社群護士') can't be resolved server-side.
  const canNudge = !!onNudge && !m.is_self && m.handle !== '社群護士';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: '0.5px solid var(--border)' }}>
      <div style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: `hsl(${hue} 42% 92%)`, color: `hsl(${hue} 45% 30%)`, fontWeight: 700, fontSize: 14 }}>{initial}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, fontWeight: 600 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.handle}</span>
          {m.is_owner && <span title={L(STR.wardOwner, lang)} aria-label={L(STR.wardOwner, lang)}>👑</span>}
          {m.is_self && <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}>· {L(STR.wardYou, lang)}</span>}
          {m.goal_met && <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--success, #2E7D5B)' }}>· {L(STR.wardGoalMet, lang)}</span>}
        </div>
        {m.no_cycle ? (
          <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>{L(STR.wardNoCycle, lang)}</div>
        ) : m.exempt ? (
          <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>{L(STR.wardExempt, lang)}</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
            <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
              <div style={{ width: `${m.pct || 0}%`, height: '100%', borderRadius: 3,
                            background: m.goal_met ? 'var(--success, #2E7D5B)' : 'var(--primary)' }} />
            </div>
            <span style={{ fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums', minWidth: 30, textAlign: 'right' }}>{m.pct}%</span>
          </div>
        )}
      </div>
      {canNudge && (
        <button type="button" className="btn btn-ghost"
          disabled={nudged || nudging}
          style={{ flexShrink: 0, fontSize: 11.5, padding: '6px 12px', gap: 5,
                   ...(nudged ? { opacity: 0.55 } : { color: 'var(--primary)' }) }}
          onClick={() => onNudge(m.handle)}>
          {nudging ? '…' : nudged
            ? L(STR.wardNudged, lang)
            : <><span aria-hidden="true">👋</span> {L(STR.wardNudge, lang)}</>}
        </button>
      )}
    </div>
  );
}

// Derive a valid ASCII handle suggestion from the user's existing name/email so
// they can accept "use my name" instead of inventing one. The community handle
// is ASCII-only ([A-Za-z0-9_]{2,20}); a Chinese-only display name yields nothing
// usable, so we fall back through name_en → display_name → the email local part
// (always ASCII). Returns '' when nothing valid can be derived.
function deriveHandleSuggestion(profile, session) {
  const candidates = [
    profile && profile.short_name_en,
    profile && profile.name_en,
    profile && profile.display_name,
    (session && session.user && session.user.email || '').split('@')[0],
  ];
  for (const c of candidates) {
    const cleaned = (c || '').replace(/[^A-Za-z0-9_]/g, '').slice(0, 20);
    if (cleaned.length >= 2) return cleaned;
  }
  return '';
}

// Modal handle-setup dialog — shown BEFORE creating/joining a ward group when
// the user has no community handle (and as a fallback if a ward op returns
// P0030). Unlike the old inline card, this is a focused modal on top of
// everything, explains WHY, pre-fills a suggestion from the user's own name, and
// offers a one-tap "use my name" button. Reuses set_community_handle + the
// shared error mapping so it matches the Edit-profile flow. onSave(handle)
// returns a friendly error string or null; on null the parent runs the pending
// create/join action.
function HandleSetupDialog({ open, lang, suggestion, onSave, onClose }) {
  const zh = lang === 'zh';
  const [h, setH] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const HANDLE_RE = /^[A-Za-z0-9_]{2,20}$/;
  // Seed the field with the suggestion each time the dialog opens.
  React.useEffect(() => {
    if (open) { setH(suggestion || ''); setErr(''); }
  }, [open, suggestion]);
  if (!open) return null;
  const valid = HANDLE_RE.test((h || '').trim());
  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    const msg = await onSave((h || '').trim());
    setBusy(false);
    if (msg) setErr(msg);
  };
  return (
    <div className="register-dialog-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="register-dialog" role="dialog" aria-modal="true"
           aria-label={zh ? '設定社群顯示名' : 'Set a display name'}>
        <button className="register-dialog-close icon-btn" onClick={onClose}
          aria-label={zh ? '關閉' : 'Close'}><Icon name="close" size={16} /></button>
        <div style={{ padding: '4px 2px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Icon name="users" size={18} stroke={1.8} color="var(--primary)" />
            <div style={{ fontSize: 15, fontWeight: 650 }}>{zh ? '設定社群顯示名' : 'Set a display name'}</div>
          </div>
          <div className="muted" style={{ fontSize: 12.5, marginBottom: 14, lineHeight: 1.55 }}>{L(STR.wardHandleNeeded, lang)}</div>
          <input className="input" value={h} maxLength={20} autoCapitalize="none" autoCorrect="off" spellCheck={false}
            placeholder={zh ? '2–20 個英文字母、數字或底線' : '2–20 letters, digits or underscore'}
            onChange={(e) => { setH(e.target.value); setErr(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            style={{ width: '100%', marginBottom: 8 }} autoFocus />
          {suggestion && suggestion !== (h || '').trim() && (
            <button type="button" className="btn btn-ghost"
              style={{ width: '100%', justifyContent: 'center', marginBottom: 8, fontSize: 12.5, gap: 6 }}
              onClick={() => { setH(suggestion); setErr(''); }}>
              {zh ? `用我嘅名稱「${suggestion}」` : `Use my name “${suggestion}”`}
            </button>
          )}
          {err && <div style={{ color: 'var(--danger, #C0392B)', fontSize: 12, marginBottom: 10 }}>{err}</div>}
          <button type="button" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}
            onClick={submit} disabled={!valid || busy}>
            {busy ? '…' : (zh ? '儲存並繼續' : 'Save & continue')}
          </button>
        </div>
      </div>
    </div>
  );
}

function WardGroupScreen({ lang, session, profile, onSignIn, pendingJoinCode, onJoinCodeHandled, onProfileChanged }) {
  const isGuest = !session;
  const zh = lang === 'zh';
  const [groups, setGroups] = React.useState(null);        // null = loading
  const [view, setView] = React.useState('list');          // 'list' | 'detail'
  const [activeId, setActiveId] = React.useState(null);
  const [progress, setProgress] = React.useState(null);
  const [progressLoading, setProgressLoading] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [joinOpen, setJoinOpen] = React.useState(false);
  const [nameInput, setNameInput] = React.useState('');
  const [codeInput, setCodeInput] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [handleNeeded, setHandleNeeded] = React.useState(false);
  const pendingActionRef = React.useRef(null);
  // Remove-member sheet (owner only): tap-to-select from the loaded member list
  // instead of typing a name. removeConfirm holds the handle awaiting the
  // second (confirm) tap so no native prompt/confirm dialog is ever shown.
  const [removeOpen, setRemoveOpen] = React.useState(false);
  const [removeConfirm, setRemoveConfirm] = React.useState(null);
  const [removeBusy, setRemoveBusy] = React.useState(false);
  // Same arm-then-confirm pattern as member removal. Native confirm dialogs
  // are inconsistent inside the Capacitor shell and obscure the consequence.
  const [destructiveConfirm, setDestructiveConfirm] = React.useState(null);
  // Peer nudge state (per open group): which handles have been nudged this
  // session, and which one is in flight.
  const [nudgedHandles, setNudgedHandles] = React.useState(() => new Set());
  const [nudgingHandle, setNudgingHandle] = React.useState(null);

  const flash = (msg) => { if (window.showToast) window.showToast(msg, { tone: 'info' }); };

  const loadGroups = React.useCallback(async () => {
    if (isGuest) return;
    const { data } = await window.DB.getMyWardGroups();
    setGroups(Array.isArray(data) ? data : []);
  }, [isGuest]);
  React.useEffect(() => { loadGroups(); }, [loadGroups]);

  // Does the user already have a community handle? Groups require one; we gate
  // on it up front (below) so the handle dialog appears BEFORE the create/join
  // sheet, never as a silent background field.
  const hasHandle = !!(profile && profile.community_handle && String(profile.community_handle).trim());
  const handleSuggestion = React.useMemo(() => deriveHandleSuggestion(profile, session), [profile, session]);
  // Run `action` now if the user has a handle; otherwise open the handle dialog
  // first and run `action` once a handle is saved.
  const withHandle = (action) => {
    if (hasHandle) { action(); return; }
    pendingActionRef.current = action;
    setHandleNeeded(true);
  };

  // A join code arrived via deep link → gate on the handle, then open the join
  // sheet prefilled. Runs when the code arrives OR when the user finishes
  // signing in (isGuest flips), so a code captured while signed-out still opens
  // the flow afterwards.
  React.useEffect(() => {
    if (!pendingJoinCode || isGuest) return;
    setCodeInput(String(pendingJoinCode).toUpperCase());
    withHandle(() => setJoinOpen(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingJoinCode, isGuest]);

  // Map a ward RPC error → toast; P0030 raises the handle dialog (and stashes
  // the op to re-run once a handle is set). Closes any open sheet so the dialog
  // is unobstructed. This is the fallback for a stale client profile — the
  // up-front withHandle() gate normally means it never fires.
  const handleOpError = (error, retry) => {
    const code = error && error.code;
    if (code === 'P0030') {
      setCreateOpen(false); setJoinOpen(false);
      pendingActionRef.current = retry || null;
      setHandleNeeded(true);
      return true;
    }
    if (code === 'P0032') flash(L(STR.wardNotFound, lang));
    else if (code === 'P0033') flash(L(STR.wardFull, lang));
    else if (code === 'P0035') flash(L(STR.wardOwnerMustDelete, lang));
    else flash(zh ? '出咗啲問題,請再試。' : 'Something went wrong, please try again.');
    return false;
  };

  const openGroup = async (id) => {
    setActiveId(id); setView('detail'); setProgress(null); setProgressLoading(true);
    setNudgedHandles(new Set());   // nudge state is per-group-visit
    const { data, error } = await window.DB.getWardGroupProgress(id);
    setProgressLoading(false);
    if (error) { handleOpError(error); return; }
    setProgress(data);
  };

  // Send a peer nudge. 429 / P0040 means already nudged today — treat as "done"
  // (disable the button) rather than a failure.
  const doNudge = async (groupId, handle) => {
    if (nudgingHandle || nudgedHandles.has(handle)) return;
    setNudgingHandle(handle);
    const { error } = await window.DB.nudgeGroupMember(groupId, handle);
    setNudgingHandle(null);
    if (error) {
      if (error.status === 429 || error.code === 'P0040') {
        setNudgedHandles((s) => new Set(s).add(handle));
        flash(L(STR.wardNudgeDone, lang));
      } else {
        flash(L(STR.wardNudgeFail, lang));
      }
      return;
    }
    setNudgedHandles((s) => new Set(s).add(handle));
    flash(L(STR.wardNudgeSent, lang));
  };

  const doCreate = async () => {
    const name = nameInput.trim();
    if (name.length < 2 || busy) return;
    setBusy(true);
    const { data, error } = await window.DB.createWardGroup(name);
    setBusy(false);
    if (error) { handleOpError(error, () => { setCreateOpen(true); }); return; }
    setCreateOpen(false); setNameInput('');
    await loadGroups();
    if (data?.id) openGroup(data.id);
  };

  const doJoin = async () => {
    const code = codeInput.trim().toUpperCase();
    if (code.length !== 8 || busy) return;
    setBusy(true);
    const { data, error } = await window.DB.joinWardGroup(code);
    setBusy(false);
    if (error) { handleOpError(error, () => { setJoinOpen(true); }); return; }
    setJoinOpen(false); setCodeInput('');
    if (onJoinCodeHandled) onJoinCodeHandled();
    await loadGroups();
    if (data?.id) openGroup(data.id);
  };

  const doLeave = async (id) => {
    const { error } = await window.DB.leaveWardGroup(id);
    if (error) { handleOpError(error); return; }
    setView('list'); setActiveId(null); await loadGroups();
  };
  const doDelete = async (id) => {
    const { error } = await window.DB.deleteWardGroup(id);
    if (error) { handleOpError(error); return; }
    setView('list'); setActiveId(null); await loadGroups();
  };
  // Remove a selected member (two-tap confirm inside the sheet — first tap arms
  // the red confirm state, second tap executes). On success: close, toast,
  // refresh the leaderboard.
  const doRemoveMember = async (groupId, handle) => {
    if (removeBusy) return;
    setRemoveBusy(true);
    const { error } = await window.DB.removeGroupMember(groupId, handle);
    setRemoveBusy(false);
    setRemoveConfirm(null);
    if (error) { flash(zh ? '未能移除,請再試。' : 'Could not remove — please try again.'); return; }
    setRemoveOpen(false);
    flash(zh ? `已移除「${handle}」` : `Removed "${handle}"`);
    openGroup(groupId);
  };

  const shareGroupInvite = async (group) => {
    const url = window.DB.buildShareUrl(`/g/${group.invite_code}`);
    await shareWithReward({
      title: zh ? `加入「${group.name}」病房小組` : `Join "${group.name}" on Vital`,
      text: L(STR.wardShareText, lang), url, lang,
    });
  };

  const saveHandle = async (h) => {
    const { error } = await window.DB.setCommunityHandle(h);
    if (error) return handleErrMessage(error, lang);
    if (onProfileChanged) onProfileChanged({ ...(profile || {}), community_handle: h });
    setHandleNeeded(false);
    const retry = pendingActionRef.current; pendingActionRef.current = null;
    if (retry) retry();
    return null;
  };

  if (isGuest) {
    return (
      <div className="scroll fade-in" style={{ paddingBottom: 24 }}>
        <ScreenHeader title={L(STR.wardGroups, lang)} />
        <div style={{ padding: '8px 20px 0' }}>
          <div className="guest-card">
            <div className="gc-eyebrow">{L(STR.wardGroups, lang)}</div>
            <h2 className="gc-title">{zh ? '登入即可同同事一齊追進度' : 'Sign in to track progress with your ward'}</h2>
            <p className="gc-sub">{L(STR.wardGroupsEmpty, lang)}</p>
            <div className="gc-cta">
              <button className="btn btn-primary" onClick={onSignIn}>
                <Icon name="user" size={16} stroke={1.9} />{zh ? '登入' : 'Sign in'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Detail view ────────────────────────────────────────────────────────
  if (view === 'detail') {
    const g = progress && progress.group;
    const members = (progress && progress.members) || [];
    const isOwner = g && g.is_owner;
    return (
      <div className="scroll fade-in" style={{ paddingBottom: 32 }}>
        <ScreenHeader title={g ? g.name : L(STR.wardGroups, lang)} onBack={() => { setView('list'); setActiveId(null); }} lang={lang} />
        {progressLoading && <div className="muted" style={{ padding: '24px 20px', textAlign: 'center', fontSize: 13 }}>{zh ? '載入中…' : 'Loading…'}</div>}
        {g && (
          <div style={{ padding: '4px 20px 0' }}>
            <div className="muted" style={{ fontSize: 12.5, marginBottom: 12 }}>
              {g.member_count} {L(STR.wardMembers, lang)} · {zh ? '主要牌照進度' : 'Primary licence progress'}
            </div>
            <div className="card" style={{ padding: '4px 14px 8px' }}>
              {members.map((m, i) => (
                <WardMemberRow key={i} m={m} lang={lang}
                  onNudge={(h) => doNudge(g.id, h)}
                  nudged={nudgedHandles.has(m.handle)}
                  nudging={nudgingHandle === m.handle} />
              ))}
            </div>
            <button type="button" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 16, gap: 6 }}
              onClick={() => shareGroupInvite(g)}>
              <Icon name="users" size={16} stroke={1.8} />{L(STR.wardShareInvite, lang)}
            </button>
            <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
              {isOwner ? (
                <>
                  <button type="button" className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }}
                    onClick={() => { setRemoveConfirm(null); setRemoveOpen(true); }}>
                    {L(STR.wardRemoveMember, lang)}
                  </button>
                  <button type="button" className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center', color: 'var(--danger, #C0392B)', ...(destructiveConfirm?.type === 'delete' ? { background: 'var(--danger, #C0392B)', color: '#fff', borderColor: 'var(--danger, #C0392B)' } : {}) }}
                    onClick={() => {
                      if (destructiveConfirm?.type === 'delete' && destructiveConfirm.id === g.id) { setDestructiveConfirm(null); doDelete(g.id); }
                      else setDestructiveConfirm({ type: 'delete', id: g.id });
                    }}>
                    {destructiveConfirm?.type === 'delete' && destructiveConfirm.id === g.id
                      ? (zh ? '確認解散？所有成員會被移除' : 'Confirm dissolve? All members are removed')
                      : L(STR.wardDelete, lang)}
                  </button>
                </>
              ) : (
                <button type="button" className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center', color: 'var(--danger, #C0392B)', ...(destructiveConfirm?.type === 'leave' ? { background: 'var(--danger, #C0392B)', color: '#fff', borderColor: 'var(--danger, #C0392B)' } : {}) }}
                  onClick={() => {
                    if (destructiveConfirm?.type === 'leave' && destructiveConfirm.id === g.id) { setDestructiveConfirm(null); doLeave(g.id); }
                    else setDestructiveConfirm({ type: 'leave', id: g.id });
                  }}>
                  {destructiveConfirm?.type === 'leave' && destructiveConfirm.id === g.id
                    ? (zh ? '確認離開？' : 'Confirm leave?') : L(STR.wardLeave, lang)}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Remove-member sheet — tap to SELECT a member from the loaded list
            (no typing, no native prompt). Owner is excluded (can't be removed);
            a member who cleared their handle shows as the fallback name and
            can't be resolved server-side, so their row is disabled. */}
        {g && (
          <Sheet open={removeOpen} onClose={() => { setRemoveOpen(false); setRemoveConfirm(null); }} title={L(STR.wardRemoveMember, lang)}>
            <div style={{ padding: '4px 20px 24px' }}>
              {(() => {
                const removable = members.filter((m) => !m.is_owner);
                if (removable.length === 0) {
                  return <div className="muted" style={{ fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
                    {zh ? '未有其他成員可移除。' : 'No other members to remove.'}
                  </div>;
                }
                return (
                  <>
                    <div className="muted" style={{ fontSize: 12.5, marginBottom: 12, lineHeight: 1.5 }}>
                      {zh ? '選擇要移除嘅成員。被移除後,對方可以用邀請碼再次加入。'
                          : 'Select a member to remove. They can rejoin later with the invite code.'}
                    </div>
                    {removable.map((m, i) => {
                      const noHandle = m.handle === '社群護士';   // SQL fallback for a cleared handle (real handles are ASCII-only)
                      const arming = removeConfirm === m.handle;
                      const hue = wardHue(m.handle);
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
                                              borderBottom: '0.5px solid var(--border)' }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13,
                                        background: `hsl(${hue} 42% 92%)`, color: `hsl(${hue} 45% 30%)` }}>
                            {(m.handle || '?').trim().charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.handle}</div>
                            {noHandle && <div className="muted" style={{ fontSize: 11 }}>
                              {zh ? '未設定顯示名,暫時無法移除' : 'No display name — cannot remove yet'}
                            </div>}
                          </div>
                          <button type="button" className="btn btn-ghost"
                            disabled={noHandle || removeBusy}
                            style={{ flexShrink: 0, fontSize: 12.5, padding: '7px 14px',
                                     ...(arming ? { background: 'var(--danger, #C0392B)', color: '#fff', borderColor: 'var(--danger, #C0392B)' } : { color: 'var(--danger, #C0392B)' }) }}
                            onClick={() => { if (arming) doRemoveMember(g.id, m.handle); else setRemoveConfirm(m.handle); }}>
                            {removeBusy && arming ? '…' : arming ? (zh ? '確認移除?' : 'Confirm?') : (zh ? '移除' : 'Remove')}
                          </button>
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          </Sheet>
        )}
      </div>
    );
  }

  // ── List view ──────────────────────────────────────────────────────────
  return (
    <div className="scroll fade-in" style={{ paddingBottom: 32 }}>
      <ScreenHeader title={L(STR.wardGroups, lang)} />
      <div style={{ padding: '4px 20px 0' }}>
        {groups === null ? (
          <div className="muted" style={{ padding: '24px 0', textAlign: 'center', fontSize: 13 }}>{zh ? '載入中…' : 'Loading…'}</div>
        ) : groups.length === 0 ? (
          <div className="guest-card">
            <div className="gc-eyebrow">{L(STR.wardGroups, lang)}</div>
            <h2 className="gc-title">{zh ? '同你嘅病房一齊追 CNE' : 'Track CNE with your ward'}</h2>
            <p className="gc-sub">{L(STR.wardGroupsEmpty, lang)}</p>
            <div className="gc-cta">
              <button className="btn btn-primary" onClick={() => withHandle(() => { setNameInput(''); setCreateOpen(true); })}>
                <Icon name="plus" size={16} stroke={1.9} />{L(STR.wardCreate, lang)}
              </button>
              <button className="btn btn-ghost" onClick={() => withHandle(() => { setCodeInput(''); setJoinOpen(true); })}>
                {L(STR.wardJoin, lang)}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {groups.map((g) => (
                <button key={g.id} type="button" className="card"
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, cursor: 'pointer',
                           textAlign: 'left', font: 'inherit', color: 'inherit', width: '100%' }}
                  onClick={() => openGroup(g.id)}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0, background: 'var(--primary-soft)',
                                color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name="users" size={20} stroke={1.7} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 650, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.name}</span>
                      {g.is_owner && <span aria-label={L(STR.wardOwner, lang)}>👑</span>}
                    </div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{g.member_count} {L(STR.wardMembers, lang)}</div>
                  </div>
                  <Icon name="chev-r" size={18} color="var(--fg-3)" />
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button type="button" className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center', gap: 6 }}
                onClick={() => withHandle(() => { setNameInput(''); setCreateOpen(true); })}>
                <Icon name="plus" size={15} stroke={1.9} />{L(STR.wardCreate, lang)}
              </button>
              <button type="button" className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center' }}
                onClick={() => withHandle(() => { setCodeInput(''); setJoinOpen(true); })}>
                {L(STR.wardJoin, lang)}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Create sheet */}
      <Sheet open={createOpen} onClose={() => setCreateOpen(false)} title={L(STR.wardCreate, lang)}>
        <div style={{ padding: '4px 20px 24px' }}>
          <label className="muted" style={{ fontSize: 12.5, display: 'block', marginBottom: 8 }}>{L(STR.wardGroupName, lang)}</label>
          <input className="input" value={nameInput} maxLength={40} placeholder={L(STR.wardGroupNamePh, lang)}
            onChange={(e) => setNameInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') doCreate(); }}
            style={{ width: '100%', marginBottom: 16 }} autoFocus />
          <button type="button" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
            onClick={doCreate} disabled={nameInput.trim().length < 2 || busy}>
            {busy ? '…' : L(STR.wardCreate, lang)}
          </button>
        </div>
      </Sheet>

      {/* Join sheet */}
      <Sheet open={joinOpen} onClose={() => { setJoinOpen(false); if (onJoinCodeHandled) onJoinCodeHandled(); }} title={L(STR.wardJoin, lang)}>
        <div style={{ padding: '4px 20px 24px' }}>
          <label className="muted" style={{ fontSize: 12.5, display: 'block', marginBottom: 8 }}>{L(STR.inviteCode, lang)}</label>
          <input className="input" value={codeInput} maxLength={8} autoCapitalize="characters" autoCorrect="off"
            placeholder={L(STR.wardInviteCodePh, lang)}
            onChange={(e) => setCodeInput(e.target.value.toUpperCase())} onKeyDown={(e) => { if (e.key === 'Enter') doJoin(); }}
            style={{ width: '100%', marginBottom: 16, textAlign: 'center', letterSpacing: '0.16em', fontFamily: 'ui-monospace, Menlo, monospace' }} autoFocus />
          <button type="button" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
            onClick={doJoin} disabled={codeInput.trim().length !== 8 || busy}>
            {busy ? '…' : L(STR.wardJoin, lang)}
          </button>
        </div>
      </Sheet>

      {/* Handle-setup dialog — appears BEFORE create/join when the user has no
          community handle. Dismissing it clears the stashed create/join action. */}
      <HandleSetupDialog
        open={handleNeeded}
        lang={lang}
        suggestion={handleSuggestion}
        onSave={saveHandle}
        onClose={() => { setHandleNeeded(false); pendingActionRef.current = null; }} />
    </div>
  );
}

// Which Profile sections are rendered by cne-info.jsx. Kept as a tiny helper so
// the "is that screen actually loaded?" check reads clearly at the call site.
function safeSectionNeedsCneInfo(section) { return section === 'cneInfo'; }

function ProfileScreen({ lang, setLang, theme, setTheme, onOpenSheet, onSignIn, profile, session, records = [], qualifications = [], walletItems = null, qualificationsReady = true, onSignOut, onDeleteAccount, onCompleteProfile, onProfileSaved, initialSection, onSectionChange, editFocusTarget, onEditFocusTargetConsumed, nchkSignal = 0, onNchkConsumed, proFeatureTarget, onProFeatureTargetConsumed, onNavigateProFeature, onOpenNotifications, savedIds, enrolledIds, onOpenCourse, onTab, onUploadCert, onUpdateRecord, onDeleteRecord }) {
  const isGuest = !session;
  const u = !isGuest ? (userFromProfile(profile, records) || profileFromSession(session, records) || USER) : null;

  // Sub-section state (signed-in only) — Overview / Record / Saved are now
  // tabs inside Profile instead of separate top-level destinations.
  const [section, setSection] = React.useState(initialSection || 'overview');
  React.useEffect(() => { if (initialSection) setSection(initialSection); }, [initialSection]);
  const navigateSection = React.useCallback((next) => {
    setSection(next);
    onSectionChange?.(next);
  }, [onSectionChange]);

  // Unread notification-center count for the menu badge. Refreshed when we
  // land back on the overview (e.g. after opening the center and marking read).
  const [notifUnread, setNotifUnread] = React.useState(0);
  // Vital Pro entry point. `purchasable` is what makes the card appear at all —
  // it is false until REVENUECAT_PUBLIC_KEY is set AND the native plugin is
  // present, so a build that ships the Pro code inert shows nothing.
  // `pro` is read from the SERVER row (via the shared hook), so a subscriber
  // still sees their status even if the store offerings fail to load — and
  // sees it update the moment a purchase or restore lands anywhere in the app.
  //
  // ‼️ ASKS THE SHARED RULE — window.proPurchasable() (data.js) — and is named
  // canPurchasePro so it cannot SHADOW it. This was a third re-derivation of
  // `PurchaseAPI.isSupported()`; under its old name, anyone inside this
  // component writing `proPurchasable()` would have called a boolean and got a
  // TypeError, while the global they meant sat one scope away.
  const proEnt = useProEntitlement(session?.user?.id);
  const canPurchasePro = !!window.proPurchasable?.();
  const [proSheetOpen, setProSheetOpen] = React.useState(false);
  const openProCard = () => setProSheetOpen(true);

  // NCHK Annex A + B export sheet — owned here so the gate's onProfileSaved can
  // lift the freshly-upserted row to app.jsx (same as Edit profile).
  const [nchkOpen, setNchkOpen] = React.useState(false);
  // "Take me to the audit pack" (paywall.jsx → app.jsx goToProFeature). The
  // toggle lives inside this sheet, so a Pro benefit row cannot reach it any
  // other way. ONE-SHOT: consumed the moment we act, exactly like Discover's
  // submit signal — a persistent flag would re-open the export sheet every time
  // the user came back to the Profile tab. Guard the initial 0.
  React.useEffect(() => {
    if (!nchkSignal) return;
    setSection('overview');   // the sheet renders at ProfileScreen level, but
    setNchkOpen(true);        // landing on a sub-page behind it reads as a bug
    onNchkConsumed?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nchkSignal]);
  // "Leave a comment or suggestion" sheet — owned here like nchkOpen and
  // rendered at the ProfileScreen level so it overlays any sub-section.
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  // Monthly share-to-earn status for the "推薦 Vital 給朋友" row — so it can show
  // "本月已獲取" once this month's +1 is claimed. { earned, cap } | null.
  const [shareEarn, setShareEarn] = React.useState(null);
  const refreshShareEarn = React.useCallback(async () => {
    if (isGuest || !window.DB?.getScanEarnStatus) return;
    const e = await window.DB.getScanEarnStatus();
    setShareEarn(e && e.share ? e.share : null);
  }, [isGuest]);
  React.useEffect(() => { refreshShareEarn(); }, [refreshShareEarn]);
  const shareClaimed = !!(shareEarn && typeof shareEarn.cap === 'number' && shareEarn.earned >= shareEarn.cap);
  React.useEffect(() => {
    let cancelled = false;
    if (isGuest || !session?.user?.id || !window.DB?.getUnreadNotificationCount) return;
    const refresh = () => window.DB.getUnreadNotificationCount(session.user.id)
      .then((n) => { if (!cancelled) setNotifUnread(n || 0); });
    refresh();
    window.addEventListener('vital:notifications-changed', refresh);
    return () => { cancelled = true; window.removeEventListener('vital:notifications-changed', refresh); };
  }, [isGuest, session?.user?.id, section]);

  // Reusable appearance + preferences blocks
  const appearanceBlock = (
    <ListSection title={L(STR.appearance, lang)} lang={lang}>
      <Row icon="moon" label={L(STR.darkMode, lang)}
           sub={theme === 'dark' ? (lang === 'zh' ? '已開啟' : 'On') : (theme === 'auto' ? (lang === 'zh' ? '跟隨系統' : 'Follow system') : (lang === 'zh' ? '已關閉' : 'Off'))}
           trailing={
             <button
               onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
               aria-label="Dark mode"
               style={{
                 appearance: 'none', border: 0, cursor: 'pointer',
                 width: 44, height: 26, borderRadius: 999,
                 background: theme === 'dark' ? 'var(--accent)' : 'var(--border-strong)',
                 position: 'relative', transition: 'background .2s',
               }}>
               <span style={{
                 position: 'absolute', top: 3, left: theme === 'dark' ? 21 : 3,
                 width: 20, height: 20, borderRadius: '50%',
                 background: '#fff', transition: 'left .2s',
                 boxShadow: '0 1px 3px rgba(0,0,0,.2)',
               }} />
             </button>
           } />
      <Row icon="leaf" label={lang === 'zh' ? '主題' : 'Theme'}
           trailing={
             <div style={{ display: 'flex', gap: 6 }}>
               {[
                 { id: 'light', label: L(STR.light, lang) },
                 { id: 'dark',  label: L(STR.dark, lang) },
                 { id: 'auto',  label: L(STR.auto, lang) },
               ].map(o => (
                 <button key={o.id}
                   className={'filter-chip' + (theme === o.id ? ' on' : '')}
                   style={{ padding: '4px 10px', fontSize: 11 }}
                   onClick={() => setTheme(o.id)}>{o.label}</button>
               ))}
             </div>
           } />
    </ListSection>
  );

  const preferencesBlock = (
    <F>
    <ListSection title={L(STR.preferences, lang)} lang={lang}>
      <Row icon="globe" label={L(STR.appLanguage, lang)}
           trailing={<LangPill lang={lang} setLang={setLang} />} />
      {/* Notification log (通知記錄) lives in the Record section, not here —
          it's a history of past notifications, so it belongs with the other
          record/history affordances rather than under Preferences. */}
      <Row icon="clock" label={L(STR.notifications, lang)}
           value={isGuest ? L(STR.notifSignInToEnable, lang) : L(STR.notifManage, lang)}
           onClick={isGuest ? undefined : () => setSection('notifications')} />
    </ListSection>
    {/* Vital Pro — the PERMANENT entry point.
        Four jobs, all required:
          1. App Review must be able to FIND the in-app purchase. The only
             other way in is exhausting all 6 free AI scans, which a reviewer
             will never do → "unable to locate the in-app purchases"
             (Guideline 2.1 rejection).
          2. Restore Purchases must be reachable for someone reinstalling —
             otherwise a paying subscriber has no way back (Guideline 3.1.1).
          3. A paying nurse can see what she is paying for and until when, and
             can reach Apple's cancel/manage screen from inside the app.
          4. It is the only place a NON-subscriber is offered Pro without
             having first been blocked by a limit — so it carries the pitch.
        It is a card outside the list, not a row inside it: as row six of a
        settings list it read as a toggle nobody opens, and a subscriber's
        status deserves more than a grey subtitle. Hidden entirely while there
        is nothing to sell AND nothing bought, so it never renders a dead end. */}
    {!isGuest && (proEnt.pro || canPurchasePro) && (
      <div style={{ marginTop: 22, padding: '0 20px' }}>
        <ProEntryCard lang={lang} ent={proEnt} onOpen={openProCard} />
      </div>
    )}
    {!isGuest && (
    <ListSection
      title={proEnt.pro
        ? (lang === 'zh' ? '社群與貢獻' : 'Community & contributions')
        : (lang === 'zh' ? '社群與額度' : 'Community & credits')}
      lang={lang}>
      {/* My contributions — view of own submissions + reports + their
          review status. Closes the loop after submitCourse / reportCourseIssue:
          without this, a contributor has no way to see "what I sent in"
          or "did the admin act on it". RLS already confines fetchMy*
          to the caller's own rows, so this is pure UX. */}
      <Row icon="check-circ"
             label={lang === 'zh' ? '我的貢獻' : 'My contributions'}
             sub={lang === 'zh' ? '查看你的提交、回報與意見' : 'See your submissions, reports & comments'}
             onClick={() => setSection('contributions')} />
      {/* My reviews — separate from 我的貢獻 on purpose. Contributions are
          catalogue DATA she gave the community; reviews are her OPINIONS, held
          privately until moderated. Merging them would bury a pending review
          inside a list about something else, and the whole point of writing
          privately first is that she can find what she wrote. */}
      <Row icon="star"
             label={lang === 'zh' ? '我的評價' : 'My reviews'}
             sub={lang === 'zh' ? '你寫過的課程評分與評語' : 'Your course ratings and comments'}
             onClick={() => setSection('reviews')} />
      {/* CNE 資訊中心 — short answers to the regulatory questions, bound to
          this nurse's own cycle dates. Deep-links out to the full guides. */}
      <Row icon="book"
             label={lang === 'zh' ? 'CNE 資訊中心' : 'CNE information'}
             sub={lang === 'zh' ? '規例、學分要求與你的週期' : 'The rules, the points, and your cycle'}
             onClick={() => setSection('cneInfo')} />
      {/* Ward leaderboard — the density engine's home. Opens the WardGroupScreen
          (a SideNav destination, not a tab). */}
      <Row icon="users"
             label={L(STR.wardGroups, lang)}
             sub={L(STR.wardGroupsSub, lang)}
             onClick={() => onTab && onTab('wardgroup')} />
      {/* Share-to-earn — recommend the app; a completed share claims the caller's
          monthly share credit (server caps at 1/mo). */}
      <Row icon="upload"
             label={lang === 'zh' ? '推薦 Vital 給朋友' : 'Share Vital with a friend'}
             sub={proEnt.pro
               ? (lang === 'zh'
                   ? '分享畀同事，一齊更容易整理 CNE；免費額度會為你保留'
                   : 'Help a colleague organise CNE; any free credit stays saved for you')
               : shareClaimed
                 ? (lang === 'zh' ? '本月額度已獲取 · 下月可再獲(仍可分享)' : "This month's credit claimed · resets next month")
                 : (lang === 'zh' ? '分享 App,每月可獲 1 次 AI 掃描額度' : 'Share the app — earn 1 AI scan credit a month')}
             onClick={async () => {
               await shareWithReward({
                 title: lang === 'zh' ? 'Vital · 香港護士 CNE' : 'Vital · HK Nurse CNE',
                 text: lang === 'zh'
                   ? '我用緊 Vital 記錄同搵 CNE 課程,幾好用,分享俾你。'
                   : "I'm using Vital to track and find CNE courses — sharing it with you.",
                 url: window.DB.buildShareUrl('/'), lang,
               });
               refreshShareEarn();
             }} />
      {/* Admin queue — only rendered for users with role admin/moderator.
          Server-side RLS + assert_admin() also enforce, so this row's
          presence is purely a UX affordance. */}
      {!isGuest && window.DB.isAdmin(profile) && (
        <Row icon="shield"
             label={lang === 'zh' ? '管理員審核' : 'Admin review queue'}
             sub={lang === 'zh' ? '審核社群提交與回報' : 'Review submissions & reports'}
             onClick={() => setSection('admin')} />
      )}
    </ListSection>
    )}
    </F>
  );

  // ── Guest variant ──────────────────────────────────────────────────────
  if (isGuest) {
    return (
      <div className="scroll fade-in" style={{ paddingBottom: 24 }}>
        <ScreenHeader title={L(STR.profile, lang)} actions={<LangPill lang={lang} setLang={setLang} />} />

        {/* Sign-in card */}
        <div style={{ padding: '8px 20px 0' }}>
          <div className="guest-card">
            <div className="gc-eyebrow">
              {lang === 'zh' ? '帳戶' : 'Account'}
            </div>
            <h2 className="gc-title">
              {lang === 'zh' ? '登入以追蹤你的 CNE 進度' : 'Sign in to track your CNE journey'}
            </h2>
            <p className="gc-sub">
              {lang === 'zh'
                ? '建立免費帳戶後即可儲存、報名及記錄完成的課程,並讓系統提示你距離續期的日子。'
                : 'Create a free account to save courses, log your record, upload certificates, and get renewal reminders.'}
            </p>
            <div className="gc-cta">
              <button className="btn btn-primary" onClick={onSignIn}>
                <Icon name="user" size={16} stroke={1.9} />
                {lang === 'zh' ? '登入' : 'Sign in'}
              </button>
              <button className="btn btn-ghost" onClick={onSignIn}>
                {lang === 'zh' ? '建立帳戶' : 'Create account'}
              </button>
            </div>
          </div>
        </div>

        {appearanceBlock}
        {preferencesBlock}

        <div style={{ padding: '24px 20px 0', textAlign: 'center' }}>
          <div className="mono muted-2" style={{ fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            Vital
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            {lang === 'zh' ? `CNE · 香港 · v${window.VITAL_APP_VERSION || 'dev'}` : `CNE companion · Hong Kong · v${window.VITAL_APP_VERSION || 'dev'}`}
          </div>
        </div>
      </div>
    );
  }

  // ── Signed-in variant ──────────────────────────────────────────────────
  // Saved courses now continue the Discover intent. Normalize stale deep
  // links from older versions back to Overview.
  // Sections that no longer exist here collapse to the overview. `cneInfo` is
  // added conditionally: if cne-info.jsx failed to load, its component is
  // undefined and the branch below would fall through to the bottom render,
  // where `safeSection === 'overview'` is false — producing a header and NOTHING
  // ELSE. That blank page is exactly what shipped when index.html was missing
  // the script tag, and it gave no clue why. Collapsing to the overview keeps
  // the user somewhere usable and makes the failure obvious in the console.
  const cneInfoMissing = safeSectionNeedsCneInfo(section) && typeof CneInfoScreen !== 'function';
  if (cneInfoMissing) console.error('[Vital] CneInfoScreen is not loaded — check index.html / src/entry.js');
  const safeSection = (['record', 'saved', 'notificationCenter'].includes(section) || cneInfoMissing)
    ? 'overview' : section;

  // Notifications is a focused sub-page with its own header and back
  // button — render it standalone so the Profile title + tab strip
  // don't double-stack above it.
  if (safeSection === 'notifications') {
    return (
      <NotificationSettingsPage
        session={session}
        lang={lang}
        profile={profile}
        focusTarget={proFeatureTarget?.target}
        onFocusTargetConsumed={onProFeatureTargetConsumed}
        onBack={() => setSection('overview')}
      />
    );
  }

  // Admin review queue — same standalone-page treatment. Defense-in-depth:
  // even though the Row is only rendered for admin/moderator, recheck the
  // role here so a direct setSection('admin') call (e.g. from a deep link)
  // can't bypass the gate. RLS + RPC role checks still enforce server-side.
  if (safeSection === 'admin') {
    if (!window.DB.isAdmin(profile)) {
      setSection('overview');
      return null;
    }
    // profile drives the 公告 tab's admin-vs-moderator gate (isFullAdmin).
    return <AdminScreen lang={lang} profile={profile} onBack={() => setSection('overview')} />;
  }

  // Edit profile — standalone page (only reachable once onboarded; an un-
  // onboarded user is routed through OnboardingFlow via onCompleteProfile
  // instead). onProfileSaved lifts the freshly-upserted row to app.jsx so the
  // Overview + Home reflect the change immediately; then we drop back to
  // Overview.
  if (safeSection === 'editProfile') {
    return (
      <EditProfilePage
        session={session}
        lang={lang}
        profile={profile}
        records={records}
        focusTarget={editFocusTarget}
        onFocusConsumed={onEditFocusTargetConsumed}
        onBack={() => navigateSection('overview')}
        onSaved={(saved) => { onProfileSaved && onProfileSaved(saved); }}
      />
    );
  }

  // CNE 資訊中心 — the regulatory reference, bound to this nurse's own cycle.
  // Lives in cne-info.jsx (screens.jsx is already very large); the guard keeps
  // a missing/renamed export from blanking the section.
  if (safeSection === 'cneInfo') {
    return (
      <CneInfoScreen
        lang={lang}
        setLang={setLang}
        profile={profile}
        user={u}
        records={records}
        onBack={() => setSection('overview')}
        onProfileSaved={onProfileSaved}
      />
    );
  }

  // My reviews — standalone page listing this user's own ratings and comments
  // with their moderation status. Everything here is private to her until an
  // admin publishes it.
  if (safeSection === 'reviews') {
    return (
      <MyReviewsScreen
        lang={lang}
        onBack={() => setSection('overview')}
        onOpenCourse={onOpenCourse}
      />
    );
  }

  // My contributions — standalone page listing this user's submissions
  // and reports with their review status.
  if (safeSection === 'contributions') {
    return (
      <MyContributionsScreen
        lang={lang}
        userId={session?.user?.id}
        onBack={() => setSection('overview')}
        onOpenCourse={onOpenCourse}
        onGoDiscover={onTab ? () => onTab('discover') : undefined}
      />
    );
  }

  return (
    <div className="scroll fade-in" style={{ paddingBottom: 24 }}>
      <ScreenHeader title={L(STR.profile, lang)} actions={<LangPill lang={lang} setLang={setLang} />} />

      {safeSection === 'overview' && <ProfileOverview u={u} profile={profile} session={session} lang={lang}
        records={records} qualifications={qualifications} qualificationsReady={qualificationsReady} walletItems={walletItems}
        appearanceBlock={appearanceBlock} preferencesBlock={preferencesBlock}
        notifUnread={notifUnread} isPro={proEnt.pro}
        onOpenSheet={onOpenSheet} onSignOut={onSignOut} onDeleteAccount={onDeleteAccount}
        onCompleteProfile={onCompleteProfile}
        onEditProfile={profile?.onboarded ? () => navigateSection('editProfile') : undefined}
        onExportNchk={() => setNchkOpen(true)}
        onOpenAudit={onTab ? () => onTab('record') : undefined}
        onLeaveFeedback={() => setFeedbackOpen(true)}
        onOpenNotifications={onOpenNotifications}
        onOpenQualifications={onTab ? () => onTab('record', 'qualifications') : undefined}
        onGoTo={setSection} />}

      <NchkExportSheet open={nchkOpen} onClose={() => {
        setNchkOpen(false);
        if (proFeatureTarget?.target === 'audit') onProFeatureTargetConsumed?.();
      }} user={u} profile={profile} records={records} lang={lang} session={session}
        highlightAudit={proFeatureTarget?.target === 'audit'}
        onHighlightConsumed={onProFeatureTargetConsumed}
        onProfileSaved={onProfileSaved} />
      <FeedbackSheet open={feedbackOpen} onClose={() => setFeedbackOpen(false)}
        lang={lang} session={session} />
      {/* Vital Pro sheet, opened from the Profile card. ProSheet — not
          PaywallSheet — picks the membership sheet for a subscriber and the
          purchase sheet for everyone else; a subscriber being shown the price
          of what she already pays for was the original bug. `trigger` is
          omitted on purpose: the quota-specific line only makes sense when a
          scan was actually blocked. Restore Purchases lives inside both
          sheets, which is why the card above must exist — see its comment.
          No onPurchased handler is needed: DB.syncEntitlement() broadcasts and
          the shared hook re-reads. */}
      {proSheetOpen && typeof ProSheet === 'function' && (
        <ProSheet lang={lang} profile={profile} records={records}
          userId={session?.user?.id} initialEnt={proEnt}
          /* ‼️ BOTH HANDLERS ARE LOAD-BEARING FROM THIS ENTRY POINT.
             onPersonalise: the membership question is what turns a route's
             "from HK$X" into a real price, and it is skippable at purchase — so
             most subscribers have not answered it. Without this prop the
             "個人化設定" button does not render at all (paywall.jsx guards on
             it), and this card is where a nurse looks for her subscription.
             Until it was passed here there was NO way back to that question.
             onNavigate: makes each benefit row's "take me there" work. */
          onPersonalise={() => { setProSheetOpen(false); onOpenSheet?.('pro:setup'); }}
          onNavigate={(key, target) => { setProSheetOpen(false); onNavigateProFeature?.(key, target); }}
          onClose={() => setProSheetOpen(false)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Profile · Overview sub-section (extracted so the tab branches stay tidy)
// ─────────────────────────────────────────────────────────────────
// Security section — Face ID / Touch ID toggle for the biometric login
// flow. Local state mirrors the Keychain entry's existence; the source of
// truth is BiometricAPI.hasStoredCredentials(), which we poll on mount.
function SecuritySection({ session, lang }) {
  const [available, setAvailable] = React.useState(false);
  const [biometryType, setBiometryType] = React.useState('none'); // 'faceId' | 'touchId' | 'none'
  const [enrolled, setEnrolled] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const Bio = window.BiometricAPI;
      if (!Bio?.isSupported?.()) return;
      const info = await Bio.checkAvailability();
      if (cancelled) return;
      setAvailable(!!info.isAvailable);
      setBiometryType(info.biometryType || 'none');
      const has = await Bio.hasStoredCredentials();
      if (!cancelled) setEnrolled(has);
    };
    refresh();
    // Listen for cross-component biometric enrolment changes (e.g. the
    // first-login enrol sheet enabling biometric, or signOut clearing
    // Keychain). BiometricAPI dispatches 'vital:biometric-changed' from
    // storeCredentials and clearCredentials so the toggle stays in sync
    // with the underlying Keychain state.
    const onChange = () => { refresh(); };
    window.addEventListener('vital:biometric-changed', onChange);
    return () => {
      cancelled = true;
      window.removeEventListener('vital:biometric-changed', onChange);
    };
  }, []);

  // Hide the whole section on non-native builds or devices without
  // biometric hardware. Keeps the web build's profile screen unchanged.
  if (!window.BiometricAPI?.isSupported?.() || !available) return null;

  const typeLabelEn = biometryType === 'faceId' ? 'Face ID'
                    : biometryType === 'touchId' ? 'Touch ID'
                    : 'Biometric';
  const typeLabelZh = biometryType === 'faceId' ? 'Face ID'
                    : biometryType === 'touchId' ? 'Touch ID'
                    : '生物識別';
  const label = lang === 'zh'
    ? (enrolled && session?.user?.email
       ? `${typeLabelZh}(${session.user.email})`
       : typeLabelZh)
    : (enrolled && session?.user?.email
       ? `${typeLabelEn} for ${session.user.email}`
       : `Sign in with ${typeLabelEn}`);

  const toggle = async () => {
    if (busy) return;
    setBusy(true); setErr('');
    const Bio = window.BiometricAPI;
    if (enrolled) {
      await Bio.clearCredentials();
      setEnrolled(false);
      setBusy(false);
      return;
    }
    if (!session?.refresh_token) {
      setErr(lang === 'zh' ? '需要先登入' : 'Sign in first');
      setBusy(false);
      return;
    }
    const res = await Bio.enable(session);
    setBusy(false);
    if (res?.error) {
      setErr(res.error.message || (lang === 'zh' ? '無法啟用' : 'Could not enable'));
      return;
    }
    setEnrolled(true);
  };

  return (
    <ListSection title={lang === 'zh' ? '安全' : 'Security'} lang={lang}>
      <Row
        icon="shield"
        label={label}
        trailing={
          <Toggle on={enrolled} onChange={toggle} busy={busy} ariaLabel={label} />
        }
      />
      {err ? (
        <div style={{ padding: '6px 16px 10px', color: 'var(--danger, #c0392b)', fontSize: 12 }}>{err}</div>
      ) : null}
    </ListSection>
  );
}

// Notification Settings page.
//
// Section 1 toggles control the `channels.push` cell only. The
// `channels.email` cells are reserved and not yet driven by the UI — this
// app has no category-driven email sender today. When email delivery ships,
// make an explicit product decision: (a) expose per-channel UI, or
// (b) widen the category toggle to write BOTH cells. Do not silently
// change behaviour here. See db.js getNotificationPrefs header comment.
//
// Section 2 Push master controls device_tokens + OS permission only
// (deliverability). It does NOT mirror into the JSON matrix. Push state
// and category state are independent on purpose: effective send =
// matrix.push AND device_tokens-present AND permission-granted.
function NotificationSettingsPage({ session, lang, profile, focusTarget, onFocusTargetConsumed, onBack }) {
  // ── Section 1: category state (one cell per category: channels.push) ──
  const [categories, setCategories] = React.useState(() => window.DB.getNotificationPrefs(profile).categories);
  const [busyCat, setBusyCat] = React.useState(null);
  const [errCat, setErrCat] = React.useState({ id: null, msg: '' });

  // Delivery mode for the scheduled reminders: 'instant' | 'digest' | 'off'.
  const [delivery, setDelivery] = React.useState(() => window.DB.getNotificationPrefs(profile).delivery);
  const handleSetDelivery = async (mode) => {
    if (mode === delivery) return;
    const prev = delivery;
    setDelivery(mode);
    const { error } = await window.DB.setNotificationPrefs(session.user.id, { delivery: mode });
    if (error) setDelivery(prev);
  };

  // ── Class reminders (local notifications) — { enabled, leadDays, hour } ──
  const localSupported = !!(window.LocalAPI && window.LocalAPI.isSupported && window.LocalAPI.isSupported());
  const [srPrefs, setSrPrefs] = React.useState(() => window.DB.getSessionReminderPrefs(profile));
  const [busySr, setBusySr] = React.useState(false);
  const [errSr, setErrSr] = React.useState('');

  // ── Course watch rules (Pro) ──────────────────────────────────────────────
  // Stored in profiles.prefs.courseRules and read by notify-cron's
  // sweepCourseRule. Normalised through data.js on the way in AND re-clamped on
  // the server on the way out — prefs is client-writable, so the limits here are
  // UX and the ones in the sweep are the defence.
  const maxRules = (window.RULE_LIMITS && window.RULE_LIMITS.maxRules) || 10;
  const [rules, setRules] = React.useState(
    () => (window.normaliseCourseRules ? window.normaliseCourseRules(profile?.prefs?.courseRules) : []),
  );
  const [busyRules, setBusyRules] = React.useState(false);
  const [errRules, setErrRules] = React.useState('');
  const [ruleDraft, setRuleDraft] = React.useState(null);

  const saveRules = async (next) => {
    if (busyRules || !session?.user?.id || !window.DB?.setPrefs) return;
    const clean = window.normaliseCourseRules ? window.normaliseCourseRules(next) : next;
    setBusyRules(true); setErrRules('');
    const prev = rules;
    setRules(clean);                       // optimistic — this is a preference
    const { error } = await window.DB.setPrefs(session.user.id, { courseRules: clean });
    setBusyRules(false);
    setRuleDraft(null);
    if (error) {
      setRules(prev);                      // put it back rather than lie
      setErrRules(lang === 'zh' ? '儲存失敗,請稍後再試。' : 'Could not save. Please try again.');
    }
  };

  // One line describing what a watch actually matches, so a saved rule is not a
  // black box she has to re-open to understand.
  const describeRule = (r, lg) => {
    const zh = lg === 'zh';
    const T = window.TOPIC_LABEL || {};
    const bits = [];
    if (r.free === true) bits.push(zh ? '免費' : 'free');
    if (r.free === false) bits.push(zh ? '收費' : 'paid');
    if (r.online === true) bits.push(zh ? '網上' : 'online');
    if (r.online === false) bits.push(zh ? '實體' : 'in person');
    if (r.weekend === true) bits.push(zh ? '週末' : 'weekends');
    if (r.minPoints) bits.push(zh ? `≥ ${r.minPoints} 分` : `${r.minPoints}+ pts`);
    if (r.topics.length) {
      bits.push(r.topics.map(t => (T[t] ? (zh ? T[t].zh : T[t].en) : t)).join(zh ? '、' : ', '));
    }
    return bits.length ? bits.join(zh ? ' · ' : ' · ') : (zh ? '所有課程' : 'any course');
  };

  // Shared entitlement (paywall.jsx) — re-reads on `vital:entitlement-changed`,
  // so the stepper unlocks as soon as a purchase lands rather than on the next
  // time this screen happens to remount.
  const proEnt = useProEntitlement(session?.user?.id);
  const isPro = proEnt.pro;
  const [proOpen, setProOpen] = React.useState(false);
  // Keep the existing free behaviour until a real storefront is configured.
  // Once live, ordinary reminders remain available but changing their lead
  // time becomes the explicit Pro customization benefit.
  const proGateEnabled = !!window.PurchaseAPI?.isSupported?.();

  // Re-seed on mount in case the in-scope `profile` prop is stale.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!session?.user?.id) return;
      const fresh = await window.DB.getProfile(session.user.id);
      if (cancelled || !fresh) return;
      setCategories(window.DB.getNotificationPrefs(fresh).categories);
      setDelivery(window.DB.getNotificationPrefs(fresh).delivery);
      setSrPrefs(window.DB.getSessionReminderPrefs(fresh));
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  // Persist a class-reminder pref patch. Delivery is now SERVER-SIDE push
  // (the notify-cron 'class' sweep reads this stored pref); the screen only
  // owns the preference. The old on-device LocalAPI scheduling was retired to
  // avoid double-firing alongside the backend push — see the launch-time
  // cancelByKind cleanup. The class reminder is gated behind the push master
  // along with everything else, so no separate local permission prompt.
  const persistSr = async (patch) => {
    const prev = srPrefs;
    const next = { ...srPrefs, ...patch };
    setSrPrefs(next);
    setBusySr(true); setErrSr('');
    try {
      const { error } = await window.DB.setSessionReminderPrefs(session.user.id, patch);
      if (error) {
        setSrPrefs(prev);
        setErrSr(error.message || (lang === 'zh' ? '無法儲存' : 'Could not save'));
      }
    } finally {
      setBusySr(false);
    }
  };

  const handleToggleSr = async () => {
    if (busySr) return;
    await persistSr({ enabled: !srPrefs.enabled });
  };

  // Lead days are a SET, not a number: Pro can be reminded 7 and 3 and 1 days out
  // for the same class.
  //
  // ‼️ THE FIVE FIXED CHIPS ARE GONE (2026-08-10, user report). They offered
  // 14/7/3/1/0 and nothing else, so a nurse who wanted 10 days — or two weeks
  // AND five days — simply could not have it. Nothing in the STORAGE ever
  // required that: db.js normaliseLeadDays and notify-cron's sessionReminders
  // have always accepted any integer 0–14, deduplicated, sorted, capped at 4.
  // The restriction existed only in this row's markup. It is now an editor
  // (LeadTimeSheet) where any value in range can be added, with the common ones
  // still one tap away.
  const leadList = Array.isArray(srPrefs.leadDays)
    ? srPrefs.leadDays
    : [Number.isInteger(srPrefs.leadDays) ? srPrefs.leadDays : 1];
  const [leadSheetOpen, setLeadSheetOpen] = React.useState(false);
  const openLeadEditor = () => {
    if (proGateEnabled && !isPro) { setProOpen(true); return; }
    if (busySr) return;
    setLeadSheetOpen(true);
  };
  // ‼️ THE FLOOR IS NOT DECORATION. An empty set would silence class reminders
  // while the toggle above still read "on"; db.js re-applies the same floor
  // because a client-side guard alone decides nothing.
  const saveLeadDays = async (next) => {
    if (!next.length) return;
    await persistSr({ leadDays: next });
  };
  const leadLabel = (v) => (v === 0
    ? (lang === 'zh' ? '當日' : 'Day of')
    : (lang === 'zh' ? `${v} 日前` : `${v}d before`));

  const handleToggleCategory = async (id) => {
    if (busyCat) return;
    const prev = categories[id].channels.push;
    const next = !prev;
    setCategories(c => ({
      ...c,
      [id]: { ...c[id], channels: { ...c[id].channels, push: next } },
    }));
    setBusyCat(id); setErrCat({ id: null, msg: '' });
    try {
      const { error } = await window.DB.setNotificationPrefs(session.user.id, {
        categories: { [id]: { channels: { push: next } } },
      });
      if (error) {
        // revert optimistic flip
        setCategories(c => ({
          ...c,
          [id]: { ...c[id], channels: { ...c[id].channels, push: prev } },
        }));
        setErrCat({ id, msg: error.message || (lang === 'zh' ? '無法儲存' : 'Could not save') });
      }
    } finally {
      setBusyCat(null);
    }
  };

  // ── Section 2: Push master state (lifted from the old RemindersSection) ──
  const pushSupported = !!(window.PushAPI && window.PushAPI.isSupported && window.PushAPI.isSupported());
  const [permission, setPermission] = React.useState('unknown'); // 'granted' | 'denied' | 'prompt' | 'unknown'
  const [hasToken, setHasToken] = React.useState(false);
  const [tokenCount, setTokenCount] = React.useState(0);
  const [busyPush, setBusyPush] = React.useState(false);
  const [errPush, setErrPush] = React.useState('');
  const [infoPush, setInfoPush] = React.useState('');
  const pushFocusRef = useProTargetHighlight(focusTarget === 'push', onFocusTargetConsumed);
  const typesFocusRef = useProTargetHighlight(focusTarget === 'types', onFocusTargetConsumed);
  const deliveryFocusRef = useProTargetHighlight(focusTarget === 'delivery', onFocusTargetConsumed);
  const remindersFocusRef = useProTargetHighlight(focusTarget === 'reminders', onFocusTargetConsumed);
  const watchFocusRef = useProTargetHighlight(focusTarget === 'watch', onFocusTargetConsumed);

  const refreshTokenState = async () => {
    if (!session?.user?.id || !window.sb) {
      setHasToken(false); setTokenCount(0); return;
    }
    try {
      // Count-only: this screen needs deliverability, never the secret push
      // token values. `head:true` keeps the response body empty.
      const { count, error } = await window.sb
        .from('device_tokens')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', session.user.id);
      if (error) throw error;
      setHasToken(Number(count || 0) > 0);
      setTokenCount(Number(count || 0));
    } catch (e) {
      console.warn('[Vital push] device_tokens read failed', e?.message || e);
    }
  };

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!pushSupported) return;
      const p = await window.PushAPI.checkPermissions();
      if (cancelled) return;
      setPermission(p?.receive || 'unknown');
      await refreshTokenState();
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id, pushSupported]);

  const pushIsOn = hasToken && permission === 'granted';

  const handleTogglePush = async () => {
    if (busyPush) return;
    setBusyPush(true); setErrPush(''); setInfoPush('');
    try {
      if (pushIsOn) {
        // Disable: drop server-side tokens. iOS-level permission can only
        // be revoked from Settings (Apple policy).
        const res = await window.PushAPI.clearTokensForCurrentUser();
        if (res?.error) { setErrPush(res.error.message || L(STR.notifPushCannotDisable, lang)); return; }
        // Remember this account deliberately turned push OFF, so the silent
        // login re-register (ensureRegistered) won't quietly turn it back on.
        window.DB?.markPushOptedOut?.(session?.user?.id);
        await refreshTokenState();
        return;
      }
      // Permission is tri-state: prompt | granted | denied. After denied,
      // requestPermissions returns the prior decision WITHOUT re-showing
      // the system prompt — Apple HIG. Skip to the Settings hint.
      const current = await window.PushAPI.checkPermissions();
      const currentState = current?.receive || 'unknown';
      setPermission(currentState);
      if (currentState === 'denied') {
        setErrPush(L(STR.notifPushDeniedHint, lang));
        return;
      }
      setInfoPush(L(STR.notifPushRegistering, lang));
      const res = await window.PushAPI.requestAndRegister();
      setInfoPush('');
      if (!res.granted) {
        if (res.reason === 'denied') {
          setErrPush(L(STR.notifPushDeniedHint, lang));
        } else if (res.reason === 'timeout') {
          setErrPush(L(STR.notifPushTimeoutHint, lang));
        } else {
          setErrPush(res.reason || L(STR.notifPushCannotEnable, lang));
        }
        return;
      }
      // Explicit opt-IN — clear any prior in-app opt-out so future logins
      // keep re-provisioning this account's token.
      window.DB?.clearPushOptedOut?.(session?.user?.id);
      await refreshTokenState();
      const p = await window.PushAPI.checkPermissions();
      setPermission(p?.receive || 'unknown');
    } finally {
      setBusyPush(false);
    }
  };

  // Send a one-off test push to this user's own devices — instant proof that
  // push works (the category reminders are scheduled/server-side, so without
  // this there's no way to verify, and App Review's reviewer notes rely on it).

  // Gate the sub-settings whenever this platform supports push but the master
  // is OFF — flipping category / class toggles that can't be delivered is
  // misleading. On web (no push support) there is no master to turn on, so we
  // never gate; `pushSupported` guards that.
  const gated = pushSupported && !pushIsOn;

  // Small inline notice shown at the top of each gated section so the dimmed
  // toggles read as intentionally inactive rather than broken.
  const GatedHint = () => (gated ? (
    <div style={{ padding: '10px 16px', color: 'var(--fg-3)', fontSize: 12 }}>
      {L(STR.notifMasterHint, lang)}
    </div>
  ) : null);

  // Radio-style option row (used by delivery mode + class frequency). `dim`
  // disables it (gated, or class-mode while the class toggle is off).
  const OptionRow = ({ selected, label, desc, onClick, dim }) => (
    <button type="button" onClick={dim ? undefined : onClick} disabled={dim} style={{
      display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%', padding: '14px 16px',
      textAlign: 'left', font: 'inherit', color: 'inherit', background: 'transparent',
      border: 0, borderBottom: '0.5px solid var(--border)',
      cursor: dim ? 'not-allowed' : 'pointer', opacity: dim ? 0.4 : 1,
    }}>
      <div style={{
        width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 1,
        background: 'var(--surface)', transition: 'border .15s ease',
        border: selected ? '6px solid var(--primary)' : '2px solid var(--border-strong)',
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="h-row" style={{ fontSize: 14 }}>{L(label, lang)}</div>
        {desc ? <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{L(desc, lang)}</div> : null}
      </div>
    </button>
  );

  const pushStatusLabel = (() => {
    if (pushIsOn) {
      const word = tokenCount === 1 ? L(STR.notifPushDevice, lang) : L(STR.notifPushDevices, lang);
      return `${L(STR.notifPushStateOn, lang)} · ${tokenCount} ${word}`;
    }
    if (permission === 'denied') return L(STR.notifPushStateBlocked, lang);
    return L(STR.notifPushStateOff, lang);
  })();

  // Toggled categories + one locked row. Order mirrors NOTIF_CATEGORY_IDS in
  // db.js — keep the two lists in step when adding a category.
  const toggledCats = [
    { id: 'renewal_reminders',     title: STR.notifCatRenewalTitle,       desc: STR.notifCatRenewalDesc },
    { id: 'missing_records',       title: STR.notifCatMissingTitle,       desc: STR.notifCatMissingDesc },
    { id: 'saved_course_deadline', title: STR.notifCatSavedDeadlineTitle, desc: STR.notifCatSavedDeadlineDesc },
    { id: 'cert_expiry',           title: STR.notifCatCertExpiryTitle,    desc: STR.notifCatCertExpiryDesc },
    { id: 'regulatory_updates',    title: STR.notifCatRegulatoryTitle,    desc: STR.notifCatRegulatoryDesc },
    { id: 'community_contributions', title: STR.notifCatCommunityTitle,   desc: STR.notifCatCommunityDesc },
    { id: 'peer_nudge',            title: STR.notifCatNudgeTitle,         desc: STR.notifCatNudgeDesc },
  ];

  return (
    <div className="scroll fade-in" style={{ paddingBottom: 24 }}>
      <ScreenHeader title={L(STR.notifSettingsTitle, lang)} onBack={onBack} sticky />

      {/* ── Master section: Push notifications (the switch that governs
          everything below it) + the Email "coming soon" delivery row.
          Push is iOS / Capacitor only; on web only the Email row shows. ── */}
      <div ref={pushFocusRef} tabIndex={focusTarget === 'push' ? -1 : undefined}
        className={focusTarget === 'push' ? 'pro-target-highlight' : undefined}>
      <ListSection title={L(STR.notifMasterSection, lang)} lang={lang}>
        {pushSupported ? (
          <F>
            <Row
              icon="bell"
              label={L(STR.notifChannelPush, lang)}
              value={pushStatusLabel}
              trailing={
                <Toggle on={pushIsOn} onChange={handleTogglePush} busy={busyPush}
                  ariaLabel={L(STR.notifChannelPush, lang)} />
              }
            />
            {errPush ? (
              <div style={{ padding: '0 16px 10px', color: 'var(--danger, #c0392b)', fontSize: 12 }}>{errPush}</div>
            ) : null}
            {infoPush ? (
              <div style={{ padding: '0 16px 10px', color: 'var(--fg-3)', fontSize: 12 }}>{infoPush}</div>
            ) : null}
          </F>
        ) : null}
        {/* Email — shown as Coming soon, no toggle */}
        <Row
          icon="mail"
          label={L(STR.notifChannelEmail, lang)}
          sub={L(STR.notifChannelEmailDesc, lang)}
          trailing={
            <span style={{
              fontSize: 11, padding: '3px 8px', borderRadius: 999,
              background: 'var(--border)', color: 'var(--fg-3)',
              whiteSpace: 'nowrap',
            }}>{L(STR.notifComingSoon, lang)}</span>
          }
        />
        <div style={{ padding: '10px 16px 12px', color: 'var(--fg-3)', fontSize: 12 }}>
          {L(STR.notifPushOnlyCaption, lang)}
        </div>
      </ListSection>
      </div>

      {/* ── Section: Notification types — gated behind the push master ── */}
      <div ref={typesFocusRef} tabIndex={focusTarget === 'types' ? -1 : undefined}
        className={focusTarget === 'types' ? 'pro-target-highlight' : undefined}>
      <ListSection title={L(STR.notifTypesSection, lang)} lang={lang}>
        <GatedHint />
        {toggledCats.map(cat => (
          <F key={cat.id}>
            <Row
              icon="bell"
              label={L(cat.title, lang)}
              sub={L(cat.desc, lang)}
              trailing={
                <Toggle
                  on={!!categories[cat.id]?.channels?.push}
                  onChange={() => handleToggleCategory(cat.id)}
                  busy={busyCat === cat.id}
                  disabled={gated}
                  ariaLabel={L(cat.title, lang)}
                />
              }
            />
            {errCat.id === cat.id && errCat.msg ? (
              <div style={{ padding: '0 16px 10px', color: 'var(--danger, #c0392b)', fontSize: 12 }}>
                {errCat.msg}
              </div>
            ) : null}
          </F>
        ))}
        {/* Locked account_security row — no toggle, never interactive */}
        <Row
          icon="shield"
          label={L(STR.notifCatAccountTitle, lang)}
          sub={L(STR.notifCatAccountDesc, lang) + ' ' + L(STR.notifCatAccountAlwaysOn, lang)}
        />
      </ListSection>
      </div>

      {/* ── Section: How reminders arrive (delivery mode) — gated ──
          Governs the scheduled reminders only. In every mode the
          notification center still records each item (不打擾 ≠ 不留痕). ── */}
      <div ref={deliveryFocusRef} tabIndex={focusTarget === 'delivery' ? -1 : undefined}
        className={focusTarget === 'delivery' ? 'pro-target-highlight' : undefined}>
      <ListSection title={L(STR.notifDeliverySection, lang)} lang={lang}>
        <GatedHint />
        <OptionRow selected={delivery === 'instant'} dim={gated}
          label={STR.notifDeliveryInstant} desc={STR.notifDeliveryInstantDesc}
          onClick={() => handleSetDelivery('instant')} />
        <OptionRow selected={delivery === 'digest'} dim={gated}
          label={STR.notifDeliveryDigest} desc={STR.notifDeliveryDigestDesc}
          onClick={() => handleSetDelivery('digest')} />
        <OptionRow selected={delivery === 'off'} dim={gated}
          label={STR.notifDeliveryOff} desc={STR.notifDeliveryOffDesc}
          onClick={() => handleSetDelivery('off')} />
      </ListSection>
      </div>

      {/* ── Section: Class reminders — gated behind the push master.
          iOS / Capacitor only. Delivery is now server-side push (see
          notify-cron); this section only edits the stored preference. ── */}
      {localSupported ? (
        <div ref={remindersFocusRef} tabIndex={focusTarget === 'reminders' ? -1 : undefined}
          className={focusTarget === 'reminders' ? 'pro-target-highlight' : undefined}>
        <ListSection title={lang === 'zh' ? '課堂提醒' : 'Class reminders'} lang={lang}>
          <GatedHint />
          <Row
            icon="clock"
            label={lang === 'zh' ? '上課前提醒' : 'Remind me before class'}
            sub={lang === 'zh'
              ? '在每節課堂開始前發出推送通知。'
              : 'A push notification before each upcoming class.'}
            trailing={<Toggle on={!!srPrefs.enabled} onChange={handleToggleSr} busy={busySr} disabled={gated}
              ariaLabel={lang === 'zh' ? '上課前提醒' : 'Remind me before class'} />}
          />
          {srPrefs.enabled ? (
            <Row
              icon="bell"
              label={lang === 'zh' ? '提前時間' : 'Lead time'}
              /* The chosen times, in words, instead of a wall of chips the row
                 has no space for. Tapping opens the editor. */
              sub={proGateEnabled && !isPro
                ? (lang === 'zh' ? 'Vital Pro 可以同時設定多個提前日數；一般提醒照常免費。'
                                 : 'Vital Pro sets several lead times at once; standard reminders stay free.')
                : leadList.map(leadLabel).join(lang === 'zh' ? '、' : ', ')}
              value={proGateEnabled && !isPro
                ? undefined
                : (lang === 'zh' ? `${leadList.length} / 4` : `${leadList.length} of 4`)}
              onClick={gated || busySr ? undefined : openLeadEditor}
            />
          ) : null}
          {leadSheetOpen && (
            <LeadTimeSheet lang={lang} value={leadList} busy={busySr}
              onClose={() => setLeadSheetOpen(false)}
              onSave={saveLeadDays} />
          )}
          {srPrefs.enabled ? (
            <F>
              <div style={{ padding: '12px 16px 4px', color: 'var(--fg-3)', fontSize: 12, fontWeight: 600 }}>
                {L(STR.notifClassModeTitle, lang)}
              </div>
              <OptionRow selected={(srPrefs.mode || 'full') === 'full'} dim={gated || busySr}
                label={STR.notifClassModeFull} onClick={() => persistSr({ mode: 'full' })} />
              <OptionRow selected={srPrefs.mode === 'concise'} dim={gated || busySr}
                label={STR.notifClassModeConcise} desc={STR.notifClassModeConciseDesc}
                onClick={() => persistSr({ mode: 'concise' })} />
            </F>
          ) : null}
          {errSr ? (
            <div style={{ padding: '0 16px 10px', color: 'var(--danger, #c0392b)', fontSize: 12 }}>{errSr}</div>
          ) : null}
          <div style={{ padding: '10px 16px 12px', color: 'var(--fg-3)', fontSize: 12 }}>
            {srPrefs.enabled
              ? (lang === 'zh'
                  ? `提醒會在${leadList.slice().sort((a, b) => b - a)
                        .map(v => (v === 0 ? '課堂當日' : `課堂前 ${v} 日`)).join('、')}上午 9 時發出。`
                  : `Reminders fire at 9:00 AM ${leadList.slice().sort((a, b) => b - a)
                        .map(v => (v === 0 ? 'on the day' : `${v} day${v === 1 ? '' : 's'} before`)).join(', ')} of each class.`)
              : (lang === 'zh'
                  ? '開啟後,系統會為你正在進行的課程安排上課提醒。'
                  : 'When on, upcoming classes in your active courses get a reminder.')}
          </div>
        </ListSection>
        </div>
      ) : null}

      {/* ── Course watches (Pro) ─────────────────────────────────────────────
          "Tell me when a course like this appears." Fires at most once per
          course per rule, and only for courses CREATED in the last few days —
          see sweepCourseRule in notify-cron for why that window is both the
          correct meaning of "new" and the thing that stops a first-run flood.

          ‼️ THE FIELDS HERE ARE THE ONES THE SERVER CAN MATCH, and no more.
          topic / free / online / weekend / points / provider are generated
          columns or plain columns; a registration deadline or a price ceiling
          is not, so offering them would build a watch that silently never
          fires. If you add a field, add the column and the diff test first. */}
      <div ref={watchFocusRef} tabIndex={focusTarget === 'watch' ? -1 : undefined}
        className={focusTarget === 'watch' ? 'pro-target-highlight' : undefined}>
      <ListSection title={lang === 'zh' ? '課程監察' : 'Course watches'}>
        <Row
          icon="bookmark"
          label={lang === 'zh' ? '有符合條件的新課程時通知我' : 'Tell me when a matching course appears'}
          sub={proGateEnabled && !isPro
            ? (lang === 'zh' ? 'Vital Pro 功能。' : 'A Vital Pro feature.')
            : (rules.length
                ? (lang === 'zh' ? `已設定 ${rules.length} / ${maxRules} 個條件` : `${rules.length} of ${maxRules} watches set`)
                : (lang === 'zh' ? '未設定任何條件' : 'No watches yet'))}
          trailing={
            <button type="button" className="btn btn-ghost"
              style={{ padding: '6px 12px', fontSize: 12.5 }}
              disabled={busyRules}
              onClick={() => {
                if (proGateEnabled && !isPro) { setProOpen(true); return; }
                setRuleDraft({ topics: [], free: null, online: null, weekend: null, minPoints: null });
              }}>
              {lang === 'zh' ? '新增' : 'Add'}
            </button>
          }
        />
        {rules.map(r => (
          <Row key={r.id} icon="bell"
            label={r.label || (lang === 'zh' ? '未命名條件' : 'Untitled watch')}
            sub={describeRule(r, lang)}
            trailing={
              <button type="button" className="btn btn-ghost"
                style={{ padding: '6px 12px', fontSize: 12.5 }}
                disabled={busyRules}
                onClick={() => saveRules(rules.filter(x => x.id !== r.id))}>
                {lang === 'zh' ? '刪除' : 'Remove'}
              </button>
            }
          />
        ))}
        {errRules ? (
          <div style={{ padding: '0 16px 10px', color: 'var(--danger, #c0392b)', fontSize: 12 }}>{errRules}</div>
        ) : null}
        <div style={{ padding: '10px 16px 12px', color: 'var(--fg-3)', fontSize: 12, lineHeight: 1.5 }}>
          {lang === 'zh'
            ? '每個條件對同一個課程只會通知一次。只有新上架的課程會觸發 —— 已經喺目錄裡的唔會突然通知你。'
            : 'Each watch notifies once per course. Only newly listed courses trigger it — nothing already in the catalogue will suddenly arrive.'}
        </div>
      </ListSection>
      </div>

      {ruleDraft && (
        <RuleEditorSheet lang={lang} draft={ruleDraft} setDraft={setRuleDraft}
          busy={busyRules}
          onCancel={() => setRuleDraft(null)}
          onSave={(rule) => saveRules([...rules, rule])} />
      )}

      {proOpen && typeof ProSheet === 'function' && (
        <ProSheet lang={lang} profile={profile} userId={session?.user?.id} initialEnt={proEnt}
          onClose={() => setProOpen(false)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN — Crowdsource Phase 4
//
// In-app review queue for admin/moderator users. Replaces the curl SOP
// documented in RELEASE.md §10 (still kept as a fallback). All actions
// route through the same five Edge Functions as before — this screen is
// just an affordance.
//
// Three tabs:
//   1. Pending submissions — admin approves/rejects new courses
//   2. Pending reports     — admin accepts/rejects field corrections
//   3. Apply queue         — accepted reports that still need to be applied
//                            to cne_courses (two-stage flow, see §10)
//
// Server still enforces role via the assert_admin() RPC + RLS, so this
// page being shown is purely a UX affordance — a non-admin who somehow
// reaches it would see all action buttons return 403.
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// <AdminAiScanPanel> — Admin → AI 掃描 operations dashboard.
// Direction: "Clinical Minimal" (user-picked): white cards, thin borders,
// teal accent, KPI row → weekly bar trend → most-corrected-fields list.
// Data: admin_scan_stats() jsonb (SECURITY DEFINER; assert_admin inside), so
// the panel renders nothing sensitive for non-admins — the RPC just errors.
// ─────────────────────────────────────────────────────────────────────────────
function AdminAiScanPanel({ lang }) {
  const [stats, setStats] = React.useState(null);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await (window.DB.adminScanStats ? window.DB.adminScanStats() : null);
      if (cancelled) return;
      if (!res || res.error) setErr(res?.error?.message || 'unavailable');
      else setStats(res.data || {});
    })();
    return () => { cancelled = true; };
  }, []);

  if (err) {
    return (
      <div className="muted" style={{ textAlign: 'center', padding: '32px 20px', fontSize: 13 }}>
        {lang === 'zh' ? 'AI 掃描統計暫時無法載入(migration 未套用?)' : 'AI scan stats unavailable (migration not applied?)'}
      </div>
    );
  }
  if (!stats) {
    return <div className="muted" style={{ textAlign: 'center', padding: '32px 20px' }}>{lang === 'zh' ? '載入中…' : 'Loading…'}</div>;
  }

  // Fill the sparse weekly series into exactly 8 buckets ending this week.
  const mondayOf = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); return x; };
  const iso = (d) => d.toISOString().slice(0, 10);
  const byWeek = Object.fromEntries((stats.weekly || []).map(w => [w.week, w.n]));
  const weeks = Array.from({ length: 8 }, (_, i) => {
    const d = mondayOf(new Date()); d.setDate(d.getDate() - 7 * (7 - i));
    return { week: iso(d), n: byWeek[iso(d)] || 0 };
  });
  const maxN = Math.max(1, ...weeks.map(w => w.n));
  const notCertRate = stats.month_feedback > 0
    ? Math.round((stats.month_not_cert / stats.month_feedback) * 100) : null;
  const edited = Object.entries(stats.edited_field_counts || {}).sort((a, b) => b[1] - a[1]);
  const maxEdit = Math.max(1, ...edited.map(([, n]) => n));

  const kpis = [
    { lbl: lang === 'zh' ? '本月掃描' : 'Scans this month', val: stats.month_scans ?? 0 },
    { lbl: lang === 'zh' ? '本月退回' : 'Refunds this month', val: stats.month_refunds ?? 0, warn: (stats.month_refunds ?? 0) > 0 },
    { lbl: lang === 'zh' ? '本月獎勵發放' : 'Bonus granted', val: `+${stats.month_bonus ?? 0}` },
    { lbl: lang === 'zh' ? '用盡免費額度' : 'Free quota exhausted', val: `${stats.free_exhausted_users ?? 0} ${lang === 'zh' ? '人' : 'users'}`, warn: (stats.free_exhausted_users ?? 0) > 0 },
  ];

  return (
    <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
        {kpis.map((k, i) => (
          <div key={i} className="card" style={{ padding: '12px 14px' }}>
            <div className="muted" style={{ fontSize: 11.5 }}>{k.lbl}</div>
            <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', marginTop: 2,
                          color: k.warn ? 'var(--warning)' : 'inherit' }}>{k.val}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="muted" style={{ fontSize: 11.5, marginBottom: 10 }}>
          {lang === 'zh' ? '每週掃描次數(近 8 週)' : 'Weekly scans (last 8 weeks)'}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 84 }}>
          {weeks.map(w => (
            <div key={w.week} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}
                 title={`${w.week} · ${w.n}`}>
              {w.n > 0 && <div style={{ fontSize: 9.5, color: 'var(--fg-3)', fontVariantNumeric: 'tabular-nums' }}>{w.n}</div>}
              <div style={{
                width: '100%', borderRadius: '4px 4px 0 0',
                background: 'var(--primary)', opacity: 0.85,
                height: `${Math.max(2, (w.n / maxN) * 64)}px`,
              }} />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          <span className="muted-2" style={{ fontSize: 10 }}>{weeks[0].week}</span>
          <span className="muted-2" style={{ fontSize: 10 }}>{lang === 'zh' ? '本週' : 'this week'}</span>
        </div>
      </div>

      <div className="card">
        <div className="muted" style={{ fontSize: 11.5, marginBottom: 4 }}>
          {lang === 'zh' ? '抽取質素(本月)' : 'Extraction quality (this month)'}
        </div>
        <div style={{ fontSize: 12.5, lineHeight: 1.7 }}>
          {lang === 'zh'
            ? <>已回報掃描 {stats.month_feedback ?? 0} 次{notCertRate != null && <> · 非證書比率 {notCertRate}%</>}</>
            : <>{stats.month_feedback ?? 0} scans reported{notCertRate != null && <> · not-a-certificate rate {notCertRate}%</>}</>}
        </div>
        {/* v2: what users actually scan (90d) — decides where to invest:
            certificate OCR quality vs poster/link extraction vs junk-input UX. */}
        {stats.doc_type_90d && Object.keys(stats.doc_type_90d).length > 0 && (() => {
          const dtLabels = {
            certificate:         { en: 'Certificates',    zh: '證書' },
            course_announcement: { en: 'Posters & links', zh: '海報·連結' },
            other:               { en: 'Other',           zh: '其他' },
          };
          const parts = Object.entries(stats.doc_type_90d)
            .sort((a, b) => b[1] - a[1])
            .map(([t, n]) => `${L(dtLabels[t] || { en: t, zh: t }, lang)} ${n}`);
          return (
            <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
              {(lang === 'zh' ? '文件類型(90 日):' : 'Doc types (90d): ') + parts.join(' · ')}
            </div>
          );
        })()}
        {edited.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div className="muted-2" style={{ fontSize: 10.5, marginBottom: 6 }}>
              {lang === 'zh' ? '用戶最常修正嘅欄位(90 日)— prompt 調校訊號' : 'Most-corrected fields (90d) — prompt-tuning signal'}
            </div>
            {edited.map(([f, n]) => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 11.5, width: 86, flexShrink: 0, fontFamily: 'ui-monospace, monospace' }}>{f}</span>
                <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'var(--border)' }}>
                  <div style={{ width: `${(n / maxEdit) * 100}%`, height: '100%', borderRadius: 4, background: 'var(--accent)', opacity: 0.8 }} />
                </div>
                <span className="muted" style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {stats.generated_at && (
        <div className="muted-2" style={{ fontSize: 10.5, textAlign: 'center' }}>
          {lang === 'zh' ? '更新於' : 'Generated'} {stats.generated_at} (HKT)
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// <AdminGrowthPanel> — Admin → 增長 dashboard: referral virality + ward-group
// adoption + nudge activity. Data: admin_growth_stats() jsonb (SECURITY DEFINER;
// assert_admin inside), so non-admins get an error and see nothing sensitive.
// ─────────────────────────────────────────────────────────────────────────────
function AdminGrowthPanel({ lang }) {
  const zh = lang === 'zh';
  const [stats, setStats] = React.useState(null);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await (window.DB.adminGrowthStats ? window.DB.adminGrowthStats() : null);
      if (cancelled) return;
      if (!res || res.error) setErr(res?.error?.message || 'unavailable');
      else setStats(res.data || {});
    })();
    return () => { cancelled = true; };
  }, []);

  if (err) {
    return <div className="muted" style={{ textAlign: 'center', padding: '32px 20px', fontSize: 13 }}>
      {zh ? '增長統計暫時無法載入(migration 未套用?)' : 'Growth stats unavailable (migration not applied?)'}
    </div>;
  }
  if (!stats) {
    return <div className="muted" style={{ textAlign: 'center', padding: '32px 20px' }}>{zh ? '載入中…' : 'Loading…'}</div>;
  }

  // Weekly group-creation bars — same 8-bucket fill as the AI-scan panel.
  const mondayOf = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); return x; };
  const iso = (d) => d.toISOString().slice(0, 10);
  const byWeek = Object.fromEntries((stats.weekly_groups || []).map(w => [w.week, w.n]));
  const weeks = Array.from({ length: 8 }, (_, i) => {
    const d = mondayOf(new Date()); d.setDate(d.getDate() - 7 * (7 - i));
    return { week: iso(d), n: byWeek[iso(d)] || 0 };
  });
  const maxN = Math.max(1, ...weeks.map(w => w.n));

  const kpis = [
    { lbl: zh ? '已激活邀請(總)' : 'Activated referrals', val: stats.referrals_total ?? 0, sub: zh ? `近 30 日 +${stats.referrals_30d ?? 0}` : `+${stats.referrals_30d ?? 0} in 30d` },
    { lbl: zh ? 'K 值代理*' : 'K proxy*', val: stats.k_proxy ?? 0, sub: zh ? '已激活邀請 ÷ 總用戶' : 'referrals ÷ users' },
    { lbl: zh ? '病房小組(總)' : 'Ward groups', val: stats.groups_total ?? 0, sub: zh ? `近 30 日 +${stats.groups_30d ?? 0}` : `+${stats.groups_30d ?? 0} in 30d` },
    { lbl: zh ? '平均每組人數' : 'Avg group size', val: stats.avg_group_size ?? 0, sub: zh ? `最大 ${stats.largest_group ?? 0} 人` : `largest ${stats.largest_group ?? 0}` },
    { lbl: zh ? '入組護士(不重複)' : 'Nurses in a group', val: stats.members_distinct ?? 0, sub: zh ? `佔 ${stats.total_profiles ? Math.round((stats.members_distinct / stats.total_profiles) * 100) : 0}% 用戶` : `${stats.total_profiles ? Math.round((stats.members_distinct / stats.total_profiles) * 100) : 0}% of users` },
    { lbl: zh ? '提一提(總)' : 'Nudges sent', val: stats.nudges_total ?? 0, sub: zh ? `近 30 日 +${stats.nudges_30d ?? 0}` : `+${stats.nudges_30d ?? 0} in 30d` },
  ];

  return (
    <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
        {kpis.map((k, i) => (
          <div key={i} className="card" style={{ padding: '12px 14px' }}>
            <div className="muted" style={{ fontSize: 11.5 }}>{k.lbl}</div>
            <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', marginTop: 2 }}>{k.val}</div>
            {k.sub && <div className="muted-2" style={{ fontSize: 10.5, marginTop: 1 }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      <div className="card">
        <div className="muted" style={{ fontSize: 11.5, marginBottom: 10 }}>
          {zh ? '每週新建小組(近 8 週)' : 'New groups per week (last 8 weeks)'}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 84 }}>
          {weeks.map(w => (
            <div key={w.week} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }} title={`${w.week} · ${w.n}`}>
              {w.n > 0 && <div style={{ fontSize: 9.5, color: 'var(--fg-3)', fontVariantNumeric: 'tabular-nums' }}>{w.n}</div>}
              <div style={{ width: '100%', borderRadius: '4px 4px 0 0', background: 'var(--primary)', opacity: 0.85,
                            height: `${Math.max(2, (w.n / maxN) * 64)}px` }} />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          <span className="muted-2" style={{ fontSize: 10 }}>{weeks[0].week}</span>
          <span className="muted-2" style={{ fontSize: 10 }}>{zh ? '本週' : 'this week'}</span>
        </div>
      </div>

      <div className="card">
        <div className="muted" style={{ fontSize: 11.5, marginBottom: 4 }}>{zh ? '邀請漏斗' : 'Referral funnel'}</div>
        <div style={{ fontSize: 12.5, lineHeight: 1.7 }}>
          {zh
            ? <>已生成邀請碼 {stats.referral_codes ?? 0} · 邀請人 {stats.distinct_referrers ?? 0} 位 · 每位平均帶 {stats.referrals_per_referrer ?? 0} 人</>
            : <>{stats.referral_codes ?? 0} codes · {stats.distinct_referrers ?? 0} referrers · {stats.referrals_per_referrer ?? 0} activated each</>}
        </div>
        <div className="muted-2" style={{ fontSize: 10.5, marginTop: 8, lineHeight: 1.5 }}>
          {zh
            ? '*K 值代理 = 已激活邀請 ÷ 總用戶。因為分享次數未被追蹤,呢個係 K 值下限(唔係真 K),要同上面原始數字一齊睇。'
            : '*K proxy = activated referrals ÷ total users. Shares sent aren’t tracked, so this is a lower-bound K — read it with the raw numbers above.'}
        </div>
      </div>

      {stats.generated_at && (
        <div className="muted-2" style={{ fontSize: 10.5, textAlign: 'center' }}>
          {zh ? '更新於' : 'Generated'} {stats.generated_at} (HKT)
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// <AdminOnboardingPanel> — Admin → Funnel. The server returns aggregate-only
// data from admin_onboarding_funnel(); this panel deliberately presents the
// decisions (where users stop / how activation changes by cohort), not raw JSON.
// ─────────────────────────────────────────────────────────────────────────────
function AdminOnboardingPanel({ lang }) {
  const zh = lang === 'zh';
  const [stats, setStats] = React.useState(null);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await (window.DB.adminOnboardingFunnel ? window.DB.adminOnboardingFunnel() : null);
      if (cancelled) return;
      if (!res || res.error) setErr(res?.error?.message || 'unavailable');
      else setStats(res.data || {});
    })();
    return () => { cancelled = true; };
  }, []);

  if (err) {
    return <div className="muted" style={{ textAlign: 'center', padding: '32px 20px', fontSize: 13 }}>
      {zh ? 'Onboarding 漏斗暫時無法載入（migration 未套用？）' : 'Onboarding funnel unavailable (migration not applied?)'}
    </div>;
  }
  if (!stats) {
    return <div className="muted" style={{ textAlign: 'center', padding: '32px 20px' }}>{zh ? '載入中…' : 'Loading…'}</div>;
  }

  const totalProfiles = stats.total_profiles ?? 0;
  const asPct = (value, base = totalProfiles) => base > 0 ? Math.round((value / base) * 100) : 0;
  const status = [
    { label: zh ? '未完成設定' : 'Not finished', value: stats.never_finished ?? 0, tone: 'var(--warning)',
      sub: zh ? `${stats.never_finished_mature ?? 0} 位已超過 48 小時` : `${stats.never_finished_mature ?? 0} mature >48h` },
    { label: zh ? '已設定，未加紀錄' : 'Set up, no record', value: stats.onboarded_no_record ?? 0, tone: 'var(--accent)' },
    { label: zh ? '已 activate' : 'Activated', value: stats.activated ?? 0, tone: 'var(--primary)' },
    { label: zh ? 'Seed 日期' : 'Seed dates', value: stats.seed_dates ?? 0, tone: 'var(--danger)',
      sub: zh ? '需確認真實日期' : 'needs date confirmation' },
  ];
  const stepLabels = {
    no_breadcrumb: { en: 'No breadcrumb', zh: '未有 breadcrumb' },
    welcome: { en: 'Welcome', zh: '歡迎' },
    profile: { en: 'Profile', zh: '個人資料' },
    cycle: { en: 'Cycle', zh: '週期' },
    done: { en: 'Done', zh: '完成' },
    scan: { en: 'First record', zh: '第一個紀錄' },
  };
  const stepRows = Object.keys(stepLabels).map(id => ({
    id,
    label: zh ? stepLabels[id].zh : stepLabels[id].en,
    n: stats.step_dropoff?.[id] ?? 0,
  }));
  const maxStep = Math.max(1, ...stepRows.map(row => row.n));
  const cohortColumns = [
    { key: 'signed_up', en: 'Sign-up', zh: '註冊' },
    { key: 'set_cycle', en: 'Set cycle', zh: '設週期' },
    { key: 'onboarded', en: 'Onboarded', zh: '完成設定' },
    { key: 'first_record', en: '1st record', zh: '首筆紀錄' },
    { key: 'activated_24h', en: '≤24h', zh: '≤24 小時' },
    { key: 'activated_7d', en: '≤7d', zh: '≤7 日' },
    { key: 'first_scan', en: 'Scanned', zh: '已掃描' },
    { key: 'exported', en: 'Exported', zh: '已匯出' },
    { key: 'pro', en: 'Pro', zh: 'Pro' },
  ];
  const sourceLabels = {
    manual: { en: 'Manual', zh: '手動' },
    ai: { en: 'AI scan', zh: 'AI 掃描' },
    catalog: { en: 'Catalog', zh: '目錄' },
    unattributed: { en: 'Unattributed', zh: '未標記' },
  };
  const sourceRows = Object.keys(sourceLabels).map(id => ({
    id,
    label: zh ? sourceLabels[id].zh : sourceLabels[id].en,
    n: stats.first_record_sources?.[id] ?? 0,
  }));
  const maxSource = Math.max(1, ...sourceRows.map(row => row.n));
  // What nurses chose on the LAST setup step. 'browse' was added as a real
  // alternative to the dead-end skip, so this panel exists to answer one
  // question: does it convert into a first record as well as 'record' does?
  // Read it against the cohort table's 1st record / ≤7d columns — popularity
  // alone would not justify keeping it.
  const actionLabels = {
    record: { en: 'Record now', zh: '立即記錄' },
    browse: { en: 'Browse first', zh: '先看課程' },
    later: { en: 'Later', zh: '稍後' },
    unrecorded: { en: 'Pre-feature', zh: '功能前' },
  };
  const actionRows = Object.keys(actionLabels).map(id => ({
    id,
    label: zh ? actionLabels[id].zh : actionLabels[id].en,
    n: stats.first_action?.[id] ?? 0,
  }));
  const maxAction = Math.max(1, ...actionRows.map(row => row.n));

  return (
    <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="card" style={{ padding: '12px 14px' }}>
        <div className="muted" style={{ fontSize: 11.5, marginBottom: 10 }}>
          {zh ? `帳戶狀態 · 共 ${totalProfiles} 位` : `Account status · ${totalProfiles} total`}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
          {status.map(item => (
            <div key={item.label} style={{ minWidth: 0, borderLeft: `3px solid ${item.tone}`, paddingLeft: 8 }}>
              <div className="muted" style={{ fontSize: 10.5 }}>{item.label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', marginTop: 2 }}>{item.value}</div>
              <div className="muted-2" style={{ fontSize: 10.5 }}>{asPct(item.value)}%{item.sub ? ` · ${item.sub}` : ''}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="muted" style={{ fontSize: 11.5, marginBottom: 4 }}>
          {zh ? '設定流程停留位置' : 'Where incomplete setups stopped'}
        </div>
        <div className="muted-2" style={{ fontSize: 10.5, lineHeight: 1.5, marginBottom: 10 }}>
          {zh ? '舊帳戶沒有 breadcrumb；新帳戶會記錄已到達的最遠步驟。' : 'Older accounts have no breadcrumb; new accounts record the furthest step reached.'}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 112 }}>
          {stepRows.map(row => (
            <div key={row.id} style={{ flex: 1, minWidth: 0, display: 'flex', height: '100%', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', gap: 4 }}
              title={`${row.label} · ${row.n}`}>
              {row.n > 0 && <div className="muted" style={{ fontSize: 10, fontVariantNumeric: 'tabular-nums' }}>{row.n}</div>}
              <div style={{ width: '100%', minHeight: 2, borderRadius: '4px 4px 0 0',
                height: `${Math.max(2, (row.n / maxStep) * 72)}px`, background: 'var(--primary)', opacity: row.id === 'no_breadcrumb' ? 0.42 : 0.85 }} />
              <div className="muted-2" style={{ fontSize: 9.5, textAlign: 'center', lineHeight: 1.15, minHeight: 22 }}>{row.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: '12px 14px' }}>
        <div className="muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
          {zh ? '完成速度（有可用時間資料）' : 'Time to complete (where available)'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
          <div>
            <div className="muted-2" style={{ fontSize: 10.5 }}>{zh ? '首筆紀錄中位數' : 'Median to first record'}</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{stats.median_hours_to_first_record ?? '—'}{stats.median_hours_to_first_record != null && (zh ? ' 小時' : ' h')}</div>
          </div>
          <div>
            <div className="muted-2" style={{ fontSize: 10.5 }}>{zh ? '完成設定中位數' : 'Median to onboarded'}</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{stats.median_minutes_to_onboarded ?? '—'}{stats.median_minutes_to_onboarded != null && (zh ? ' 分鐘' : ' min')}</div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
          {zh ? '按註冊月份漏斗' : 'Signup cohorts'}
        </div>
        <div style={{ overflowX: 'auto', paddingBottom: 2 }}>
          <div style={{ minWidth: 850, display: 'grid', gridTemplateColumns: `86px repeat(${cohortColumns.length}, minmax(82px, 1fr))`, gap: '1px 6px', fontSize: 10.5 }}>
            <div className="muted-2" style={{ padding: '4px 0' }}>{zh ? '月份' : 'Month'}</div>
            {cohortColumns.map(col => <div key={col.key} className="muted-2" style={{ padding: '4px 0', textAlign: 'right' }}>{zh ? col.zh : col.en}</div>)}
            {(stats.cohorts || []).map(cohort => (
              <React.Fragment key={cohort.month}>
                <div style={{ padding: '5px 0', fontWeight: 650 }}>{cohort.month}</div>
                {cohortColumns.map((col, index) => {
                  const value = cohort[col.key] ?? 0;
                  const prior = index === 0 ? null : (cohort[cohortColumns[index - 1].key] ?? 0);
                  const pct = prior == null ? null : (prior > 0 ? Math.round((value / prior) * 100) : 0);
                  return <div key={col.key} style={{ padding: '5px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {value}{pct != null && <span className="muted-2" style={{ marginLeft: 3, fontSize: 9 }}>{pct}%</span>}
                  </div>;
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
        {(stats.cohorts || []).length === 0 && (
          <div className="muted" style={{ fontSize: 12.5, padding: '8px 0 2px' }}>{zh ? '近 8 個月尚無註冊資料。' : 'No sign-ups in the last eight months.'}</div>
        )}
      </div>

      <div className="card">
        <div className="muted" style={{ fontSize: 11.5, marginBottom: 10 }}>
          {zh ? '第一筆紀錄的加入方法' : 'How the first record was added'}
        </div>
        {sourceRows.map(row => (
          <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
            color: row.id === 'unattributed' && row.n > 0 ? 'var(--danger)' : undefined }}>
            <span style={{ width: 86, flexShrink: 0, fontSize: 11.5 }}>{row.label}</span>
            <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'var(--border)' }}>
              <div style={{ width: `${(row.n / maxSource) * 100}%`, height: '100%', borderRadius: 4,
                background: row.id === 'unattributed' && row.n > 0 ? 'var(--danger)' : 'var(--accent)', opacity: 0.82 }} />
            </div>
            <span style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{row.n}</span>
          </div>
        ))}
        {(stats.first_record_sources?.unattributed ?? 0) > 0 && (
          <div style={{ color: 'var(--danger)', fontSize: 10.5, marginTop: 4, lineHeight: 1.45 }}>
            {zh ? '未標記不應出現在新紀錄；請檢查仍在寫入 records 的路徑。' : 'Unattributed should not appear on new records; check every records write path.'}
          </div>
        )}
      </div>

      <div className="card">
        <div className="muted" style={{ fontSize: 11.5, marginBottom: 10 }}>
          {zh ? '設定最後一步的選擇' : 'Chosen path out of setup'}
        </div>
        {actionRows.map(row => (
          <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
            opacity: row.id === 'unrecorded' ? 0.6 : undefined }}>
            <span style={{ width: 86, flexShrink: 0, fontSize: 11.5 }}>{row.label}</span>
            <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'var(--border)' }}>
              <div style={{ width: `${(row.n / maxAction) * 100}%`, height: '100%', borderRadius: 4,
                background: row.id === 'later' ? 'var(--fg-3)' : 'var(--primary)', opacity: 0.82 }} />
            </div>
            <span style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{row.n}</span>
          </div>
        ))}
        <div className="muted-2" style={{ fontSize: 10.5, marginTop: 4, lineHeight: 1.45 }}>
          {zh
            ? '「先看課程」是用來取代死路一條的「稍後再說」。要對照上面世代表的「首筆紀錄 / ≤7 日」來看 — 若轉化率明顯較差，應該撤走而非保留。'
            : '“Browse first” replaced a dead-end skip. Judge it against the cohort table’s 1st record / ≤7d columns — if it converts worse, remove it rather than keep it.'}
        </div>
      </div>

      {stats.generated_at && (
        <div className="muted-2" style={{ fontSize: 10.5, textAlign: 'center' }}>
          {zh ? '更新於' : 'Generated'} {stats.generated_at} (HKT)
        </div>
      )}
    </div>
  );
}

function AdminScreen({ lang, profile, onBack }) {
  const [tab, setTab] = React.useState('submissions'); // 'submissions' | 'reports' | 'apply'
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [counts, setCounts] = React.useState({ submissions: 0, reports: 0, apply: 0, feedback: 0, reviews: 0 });
  // Per-item local state: notes text + which row is currently busy on an action.
  const [notesById, setNotesById] = React.useState({});
  const [busyId, setBusyId] = React.useState(null);
  // Apply dialog state — opened from a row in the Apply queue.
  const [applyDlg, setApplyDlg] = React.useState(null);
  // Map<userId, statsObj> for the contributor inline stat line. Empty
  // map until items load; cards fall back to "—" when missing.
  const [statsByUser, setStatsByUser] = React.useState(new Map());
  // Map<userId, displayName|null> for the feedback inbox — who left each
  // comment. Populated only while the feedback tab is active.
  const [namesByUser, setNamesByUser] = React.useState(new Map());
  // Feedback inbox status filter ('all' | new | reviewing | resolved |
  // archived). Purely client-side over the already-fetched rows.
  const [feedbackFilter, setFeedbackFilter] = React.useState('all');
  // True when the current tab's fetch came back at or above
  // PAGINATION_WARN_THRESHOLD. Surfaces as a banner so the admin can't
  // miss it (the console.warn path requires DevTools).
  const [paginationWarning, setPaginationWarning] = React.useState(false);
  // Click-through history dialog: { userId } | null.
  const [historyDlg, setHistoryDlg] = React.useState(null);
  // Map<courseId, string[]> — fresh locked_fields for the courses referenced
  // by the reports/apply queues. Drives the "this field already has an admin
  // correction" notice so reviewers don't redo resolved work. Re-fetched on
  // every refresh() (incl. after an apply) so same-session duplicates show.
  const [lockedByCourse, setLockedByCourse] = React.useState(new Map());
  // Submissions by the same submitters as the current queue, any status —
  // the corpus the pairwise duplicate finding compares against. Empty until it
  // lands, and empty on failure: a duplicate HINT must never break the queue.
  const [duplicateCorpus, setDuplicateCorpus] = React.useState([]);

  const refresh = React.useCallback(async () => {
    // The admin dashboard tabs have their own panel + state — skip the
    // review-queue fetch so we don't churn the network on every visit.
    if (tab === 'admins' || tab === 'aiscan' || tab === 'growth' || tab === 'funnel'
        || tab === 'promo' || tab === 'notices') {
      setLoading(false);
      setItems([]);
      setPaginationWarning(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Always recompute all counts so badges are consistent across tabs.
      const [subRes, repRes, appRes, fbRes, revRes] = await Promise.all([
        window.DB.fetchPendingSubmissions(),
        window.DB.fetchPendingReports(),
        window.DB.fetchAcceptedReports(),
        window.DB.fetchAllFeedback(),
        // Review moderation goes through an Edge Function (the RPCs are
        // service_role only), so a non-admin session simply gets 403 here and
        // the tab shows an empty queue rather than breaking the dashboard.
        window.DB.adminListPendingReviews().catch(() => ({ data: null })),
      ]);
      const subs = subRes.data || [];
      // ‼️ ONE query for the WHOLE queue, scoped to the submitters on screen.
      // Stage 1 of the duplicate finding requires an identical submitter, so no
      // other row can pair — and the pending queue alone is the wrong corpus:
      // the 「Lase→Last」 pair is one APPROVED row and one PENDING row, so
      // comparing within the queue would miss the case this was built for.
      // Failure is swallowed: a duplicate HINT must never break the queue.
      window.DB.fetchDuplicateCandidates(subs.map(x => x.submitter_user_id))
        .then(r => setDuplicateCorpus(r.data || []))
        .catch(() => { /* hint only */ });
      const reps = repRes.data || [];
      const apps = appRes.data || [];
      const fbs = fbRes.data || [];
      const revs = revRes?.data?.reviews || [];
      // Feedback is an inbox, so its badge counts only un-triaged ('new')
      // items — same "needs attention" semantics as the pending queues.
      setCounts({
        submissions: subs.length, reports: reps.length, apply: apps.length,
        feedback: fbs.filter(f => f.status === 'new').length,
        reviews: revs.length,
      });
      const list = tab === 'submissions' ? subs
        : tab === 'reports' ? reps
        : tab === 'feedback' ? fbs
        : tab === 'reviews' ? revs
        : apps;
      setItems(list);
      // Pagination warning is per-tab — pull from whichever response matches
      // the active tab so the banner reflects what the user is looking at.
      const activeRes = tab === 'submissions' ? subRes
        : tab === 'reports' ? repRes
        : tab === 'feedback' ? fbRes
        : appRes;
      setPaginationWarning(!!activeRes.paginationWarning);
      // Batch-load contributor stats for everyone visible on this page.
      // One round-trip total — see fetchContributorStatsBatch comment.
      const ids = list.map(it => it.submitter_user_id || it.reporter_user_id).filter(Boolean);
      if (ids.length > 0) {
        try {
          const m = await window.DB.fetchContributorStatsBatch(ids);
          setStatsByUser(m);
        } catch (e) {
          // Stats are decorative — failure must not block the queue.
          console.warn('contributor stats batch failed', e);
          setStatsByUser(new Map());
        }
      } else {
        setStatsByUser(new Map());
      }
      // Feedback inbox: resolve who left each comment (user_id → display name).
      // Gated on the feedback tab so other tabs don't run an extra profiles
      // query. Best-effort — a failure just shows the anonymised id fallback.
      if (tab === 'feedback') {
        try {
          setNamesByUser(await window.DB.fetchProfileNamesBatch(list.map(it => it.user_id)));
        } catch (e) {
          console.warn('feedback name batch failed', e);
          setNamesByUser(new Map());
        }
      } else {
        setNamesByUser(new Map());
      }
      // Fresh locked_fields for the courses in the reports/apply queues.
      // Only these two tabs carry course_id; submissions don't reference a
      // course yet. Best-effort — the helper swallows errors (e.g. column not
      // migrated) and the cards just won't show the lock notice.
      const courseIds = [...reps, ...apps].map(it => it.course_id).filter(Boolean);
      if (courseIds.length > 0) {
        try {
          setLockedByCourse(await window.DB.fetchLockedFieldsForCourses(courseIds));
        } catch (e) {
          console.warn('locked fields fetch failed', e);
          setLockedByCourse(new Map());
        }
      } else {
        setLockedByCourse(new Map());
      }
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  React.useEffect(() => { refresh(); }, [tab, refresh]);

  // Generic action runner — busies the row, calls db helper, toasts the
  // result, refreshes the list. Wipes the per-row notes on success so
  // re-opening a different row doesn't reuse stale notes.
  const runAction = async (id, fn, successMsg) => {
    if (busyId) return;
    setBusyId(id);
    try {
      const { error: e } = await fn();
      if (e) {
        showVitalToast(lang === 'zh' ? `失敗: ${e.message}` : `Failed: ${e.message}`);
        // Hand the error back. A toast is the right feedback for "it broke", but
        // a refusal that names its remedy (an identity verdict) needs the CARD to
        // react — otherwise the reviewer reads a dead end and has nowhere to go.
        return e;
      } else {
        showVitalToast(successMsg);
        setNotesById(prev => { const next = { ...prev }; delete next[id]; return next; });
        await refresh();
      }
    } finally {
      setBusyId(null);
    }
  };

  // Course title lookup for report rows (reports store only course_id).
  // Falls back gracefully when the course isn't in the local catalogue
  // (e.g. removed or pending from a prior session).
  const courseTitle = (courseId) => {
    const c = (typeof COURSES !== 'undefined' ? COURSES : []).find(x => x.id === courseId);
    return c ? L(c.title, lang) : courseId;
  };

  const tabs = [
    { id: 'submissions', label_en: 'Submissions', label_zh: '課程提交', count: counts.submissions },
    { id: 'reports',     label_en: 'Reports',     label_zh: '錯誤回報', count: counts.reports },
    { id: 'apply',       label_en: 'Apply queue', label_zh: '套用佇列', count: counts.apply },
    { id: 'feedback',    label_en: 'Feedback',    label_zh: '意見',      count: counts.feedback },
    { id: 'reviews',     label_en: 'Reviews',     label_zh: '評價審核',  count: counts.reviews },
    { id: 'promo',       label_en: 'Codes',       label_zh: '優惠碼',    count: 0 },
    // ‼️ ADMIN-ONLY (not moderator) — see isFullAdmin / is_full_admin(). Every
    // other tab here is a queue of things users did; this one is a microphone.
    ...(window.DB.isFullAdmin(profile)
      ? [{ id: 'notices', label_en: 'Notices', label_zh: '公告', count: 0 }]
      : []),
    { id: 'admins',      label_en: 'Admins',      label_zh: '管理員',    count: 0 },
    { id: 'aiscan',      label_en: 'AI scan',     label_zh: 'AI 掃描',   count: 0 },
    { id: 'growth',      label_en: 'Growth',      label_zh: '增長',      count: 0 },
    { id: 'funnel',      label_en: 'Funnel',      label_zh: '漏斗',      count: 0 },
  ];

  // Feedback inbox: client-side status filter over the already-fetched rows
  // (items already holds every feedback row on this tab, so filtering needs no
  // refetch). The tally powers the per-chip counts; 'all' shows everything.
  const feedbackFilters = ['all', 'new', 'reviewing', 'resolved', 'archived'];
  const feedbackFilterLabels = {
    all:       { en: 'All',       zh: '全部' },
    new:       { en: 'New',       zh: '新' },
    reviewing: { en: 'Reviewing', zh: '處理中' },
    resolved:  { en: 'Resolved',  zh: '已解決' },
    archived:  { en: 'Archived',  zh: '已封存' },
  };
  const feedbackTally = (tab === 'feedback')
    ? items.reduce((acc, it) => { acc[it.status] = (acc[it.status] || 0) + 1; return acc; }, {})
    : {};
  const visibleItems = (tab === 'feedback' && feedbackFilter !== 'all')
    ? items.filter(it => it.status === feedbackFilter)
    : items;

  return (
    <div className="scroll fade-in" style={{ paddingBottom: 80 }}>
      <ScreenHeader
        title={lang === 'zh' ? '管理員' : 'Admin'}
        onBack={onBack}
        actions={
          <button className="icon-btn" onClick={refresh} aria-label={lang === 'zh' ? '重新整理' : 'Refresh'}>
            <Icon name="refresh" size={16} stroke={1.7} />
          </button>
        }
      />

      {/* ‼️ --scroll, because this strip has nine tabs and counting. Without it
          .profile-tab's `flex: 1` splits 390px nine ways and every Chinese
          label renders as a vertical column of characters. See the note on
          .profile-tabs--scroll in discover-filters.css. */}
      <div className="profile-tabs profile-tabs--scroll" role="tablist" style={{ marginBottom: 8 }}>
        {tabs.map(t => (
          <button key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={'profile-tab' + (tab === t.id ? ' on' : '')}
            onClick={() => setTab(t.id)}>
            {lang === 'zh' ? t.label_zh : t.label_en}
            {t.count > 0 && (
              <span style={{
                marginLeft: 6, fontSize: 11, padding: '1px 6px',
                borderRadius: 999, background: tab === t.id ? 'var(--primary)' : 'var(--border-strong)',
                color: tab === t.id ? '#fff' : 'var(--fg-2)',
              }}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Pagination capacity warning — fires when a tab's row count is
          at/above PAGINATION_WARN_THRESHOLD. Copy leads with the product
          framing (queue throughput is outpacing one reviewer) because
          that's the action that actually scales review; pagination is
          the parenthetical technical fix. See memory:
          cap-signals-are-product-signals. */}
      {paginationWarning && !loading && (
        <div style={{
          margin: '4px 20px 12px', padding: '10px 12px', borderRadius: 10,
          background: 'color-mix(in oklch, var(--accent) 14%, transparent)',
          color: 'var(--accent)', fontSize: 12.5, lineHeight: 1.5,
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          <Icon name="clock" size={13} stroke={1.8} />
          <div>
            <strong>
              {lang === 'zh' ? '社群活躍度上升中' : 'Community throughput is climbing'}
            </strong>
            <div style={{ marginTop: 2, opacity: 0.9 }}>
              {lang === 'zh'
                ? `已累積 ${items.length} 筆待審 — 一個人審核的步調快要追不上社群提交的步調。考慮邀請一位資深護士擔任 moderator 來分擔審核。(同時：為此分頁加上 cursor pagination。)`
                : `${items.length} items pending — community contribution is outpacing single-reviewer cadence. Consider recruiting a moderator to share the queue. (Also: paginate this tab when convenient.)`}
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="muted" style={{ textAlign: 'center', padding: '32px 20px' }}>
          {lang === 'zh' ? '載入中…' : 'Loading…'}
        </div>
      )}

      {error && !loading && (
        <div style={{ margin: '12px 20px', padding: 12, borderRadius: 10,
                      background: 'color-mix(in oklch, var(--danger) 12%, transparent)',
                      color: 'var(--danger)', fontSize: 13 }}>
          {error}
        </div>
      )}

      {tab === 'promo' && (
        <AdminPromoPanel lang={lang} />
      )}

      {/* Gated twice: the tab is absent for a moderator, and every RPC behind
          this panel returns 42501 regardless of what the client renders. */}
      {tab === 'notices' && window.DB.isFullAdmin(profile) && (
        <AdminNoticesPanel lang={lang} />
      )}

      {tab === 'admins' && (
        <AdminManagementPanel lang={lang} profile={profile} />
      )}

      {tab === 'aiscan' && (
        <AdminAiScanPanel lang={lang} />
      )}

      {tab === 'growth' && (
        <AdminGrowthPanel lang={lang} />
      )}

      {tab === 'funnel' && (
        <AdminOnboardingPanel lang={lang} />
      )}

      {tab !== 'admins' && tab !== 'aiscan' && tab !== 'growth' && tab !== 'funnel'
        && tab !== 'promo' && tab !== 'notices' && !loading && items.length === 0 && (
        <div className="vital-empty" style={{ marginTop: 12 }}>
          <div className="vital-empty-icon">
            <Icon name={tab === 'feedback' ? 'mail' : 'check-circ'} size={22} stroke={1.7} />
          </div>
          <div className="vital-empty-title">
            {tab === 'feedback'
              ? (lang === 'zh' ? '尚無意見' : 'No feedback yet')
              : (lang === 'zh' ? '佇列已清空' : 'Queue is empty')}
          </div>
          <div className="vital-empty-sub">
            {tab === 'feedback'
              ? (lang === 'zh' ? '用戶的意見與建議會顯示在這裡。' : 'User comments and suggestions will appear here.')
              : (lang === 'zh' ? '沒有待處理項目。' : 'Nothing pending here.')}
          </div>
        </div>
      )}

      {/* Feedback inbox status filter — chips with live per-status counts.
          Shown only once there's at least one row to filter. */}
      {tab === 'feedback' && !loading && items.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '0 20px 8px' }}>
          {feedbackFilters.map(f => {
            const n = f === 'all' ? items.length : (feedbackTally[f] || 0);
            const on = feedbackFilter === f;
            return (
              <button key={f}
                className={'filter-chip' + (on ? ' on' : '')}
                style={{ padding: '5px 11px', fontSize: 12 }}
                aria-pressed={on}
                onClick={() => setFeedbackFilter(f)}>
                {lang === 'zh' ? feedbackFilterLabels[f].zh : feedbackFilterLabels[f].en}
                <span style={{ marginLeft: 5, opacity: 0.7 }}>{n}</span>
              </button>
            );
          })}
        </div>
      )}

      {tab !== 'admins' && tab !== 'aiscan' && tab !== 'growth' && tab !== 'funnel' && !loading && items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 20px' }}>
          {visibleItems.length === 0 && (
            <div className="muted" style={{ textAlign: 'center', padding: '18px 0', fontSize: 13 }}>
              {lang === 'zh' ? '沒有符合此狀態的意見。' : 'No feedback matches this filter.'}
            </div>
          )}
          {visibleItems.map(item => {
            const notes = notesById[item.id] || '';
            const setNotes = (v) => setNotesById(prev => ({ ...prev, [item.id]: v }));
            const isBusy = busyId === item.id;
            const contributorId = item.submitter_user_id || item.reporter_user_id;
            const stats = statsByUser.get(contributorId) || null;
            const onOpenHistory = contributorId ? () => setHistoryDlg({ userId: contributorId }) : null;
            if (tab === 'feedback') {
              return (
                <AdminFeedbackCard key={item.id}
                  item={item} lang={lang}
                  authorName={namesByUser.get(item.user_id) || null}
                  isBusy={isBusy} anyBusy={!!busyId}
                  onSetStatus={(s) => runAction(item.id,
                    () => window.DB.updateFeedbackStatus(item.id, s),
                    lang === 'zh' ? '已更新' : 'Updated')}
                  onReply={(reply, status) => runAction(item.id,
                    () => window.DB.adminReplyToFeedback(item.id, reply, status),
                    lang === 'zh' ? '已回覆用戶' : 'Reply sent')}
                />
              );
            }
            if (tab === 'reviews') {
              return (
                <AdminReviewCard key={item.id}
                  item={item} lang={lang}
                  notes={notes} setNotes={setNotes}
                  isBusy={isBusy} anyBusy={!!busyId}
                  onPublish={() => runAction(item.id,
                    () => window.DB.adminModerateReview(item.id, 'publish', notes || null),
                    lang === 'zh' ? '已公開' : 'Published')}
                  onReject={() => runAction(item.id,
                    () => window.DB.adminModerateReview(item.id, 'reject', notes || null),
                    lang === 'zh' ? '已拒絕' : 'Rejected')}
                  onSuspend={() => runAction(item.id,
                    () => window.DB.adminSuspendReviewAuthor(item.id, true, notes || null),
                    lang === 'zh' ? '已停用該作者' : 'Author suspended')}
                />
              );
            }
            if (tab === 'submissions') {
              return (
                <AdminSubmissionCard key={item.id}
                  item={item} lang={lang} duplicateCorpus={duplicateCorpus}
                  stats={stats} onOpenHistory={onOpenHistory}
                  notes={notes} setNotes={setNotes}
                  isBusy={isBusy} anyBusy={!!busyId}
                  // ‼️ `reviewedRevision` COMES FROM THE CARD, NOT FROM HERE.
                  // The card hands back the revision IT rendered, so the value
                  // that travels is the one the reviewer was looking at. Reading
                  // item.revision at this point would be reading whatever the
                  // last refresh happened to leave in the queue.
                  onApprove={async (reviewedPayload, identityDisposition, reviewedRevision) => {
                    const e = await runAction(item.id,
                      () => window.DB.adminApproveSubmission(
                        item.id, notes || null, reviewedPayload, identityDisposition, reviewedRevision),
                      lang === 'zh' ? '已通過' : 'Approved');
                    // A stale review is the one refusal whose remedy is "look
                    // again": pull the queue so the card re-renders at the
                    // revision the submitter actually left.
                    if (e && e.reason === 'stale_review') await refresh();
                    return e;
                  }}
                  onReject={() => runAction(item.id,
                    () => window.DB.adminRejectSubmission(item.id, notes || null),
                    lang === 'zh' ? '已拒絕' : 'Rejected')}
                />
              );
            }
            // 'reports' or 'apply' — same card layout, action set differs.
            return (
              <AdminReportCard key={item.id}
                item={item} lang={lang}
                stats={stats} onOpenHistory={onOpenHistory}
                courseTitle={courseTitle(item.course_id)}
                fieldLocked={!!(item.field_path && (lockedByCourse.get(String(item.course_id)) || []).includes(item.field_path))}
                notes={notes} setNotes={setNotes}
                isBusy={isBusy} anyBusy={!!busyId}
                mode={tab}
                onAccept={() => runAction(item.id,
                  () => window.DB.adminAcceptReport(item.id, notes || null),
                  lang === 'zh' ? '已接受' : 'Accepted')}
                onReject={() => runAction(item.id,
                  () => window.DB.adminRejectReport(item.id, notes || null),
                  lang === 'zh' ? '已拒絕' : 'Rejected')}
                onOpenApply={() => setApplyDlg({
                  reportId: item.id,
                  fieldPath: item.field_path,
                  currentValue: item.current_value,
                  suggestedValue: item.suggested_value || '',
                  courseTitle: courseTitle(item.course_id),
                  courseId: item.course_id,
                  alreadyLocked: !!(item.field_path && (lockedByCourse.get(String(item.course_id)) || []).includes(item.field_path)),
                })}
              />
            );
          })}
        </div>
      )}

      {applyDlg && (
        <AdminApplyDialog
          dlg={applyDlg} lang={lang}
          onClose={() => setApplyDlg(null)}
          onApplied={async () => { setApplyDlg(null); await refresh(); }}
        />
      )}

      {historyDlg && (
        <ContributorHistoryDialog
          userId={historyDlg.userId} lang={lang}
          onClose={() => setHistoryDlg(null)}
        />
      )}
    </div>
  );
}

// Admin-management is authorised by the server-controlled admin_users table.
// The confirmation below is a UX safeguard against accidental role changes;
// it is not an authorization mechanism.

// ─────────────────────────────────────────────────────────────────────────────
// AdminPromoPanel — Vital access codes (優惠碼)
//
// ‼️ WHAT THESE CODES ARE, BECAUSE THE NAME PROMISES MORE THAN THEY DO.
// A code grants Vital Pro FREE for N days. It is NOT a discount: Vital Pro is
// an App Store subscription, Apple sets the price, and nothing here can take a
// percentage off a purchase Apple bills. A real discounted offer is an App
// Store OFFER CODE, configured in App Store Connect and redeemed through
// Apple's own sheet — the paywall links to it separately. The panel says this
// out loud, because "why can't I set 20% off" is the first question it will be
// asked. Full reasoning: supabase-migration-promo-codes.sql.
//
// ‼️ NOTHING HERE DECIDES ANYTHING. Every button calls an RPC that re-checks
// is_admin_uid(auth.uid()) server-side. A non-admin who reaches this screen
// gets 42501 on every call and sees an error, not an empty dashboard — which is
// why the load failure is rendered rather than swallowed.
function AdminPromoPanel({ lang }) {
  const zh = lang === 'zh';
  const confirmer = useConfirm();
  const [rows, setRows] = React.useState(null);
  const [stats, setStats] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [busyId, setBusyId] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [form, setForm] = React.useState(null);   // null = form closed

  const load = React.useCallback(async () => {
    setErr('');
    const [listRes, statRes] = await Promise.all([
      window.DB.adminPromoList(),
      window.DB.adminPromoStats(),
    ]);
    if (listRes.error) {
      // 42501 is the server refusing, not an empty list. Saying "no codes"
      // here would send an admin looking for a data problem that isn't one.
      setErr(listRes.error.code === '42501'
        ? (zh ? '你沒有管理優惠碼的權限。' : 'You do not have permission to manage codes.')
        : (zh ? '載入失敗，請重試。' : 'Could not load. Please retry.'));
      setRows([]);
      return;
    }
    setRows(listRes.data || []);
    setStats(Array.isArray(statRes.data) ? statRes.data[0] : statRes.data);
  }, [zh]);
  React.useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    if (busyId) return;
    setBusyId(id);
    const { error } = await window.DB.adminPromoSetStatus(id, status);
    setBusyId(null);
    if (error) { showVitalToast(zh ? '操作失敗' : 'Action failed', 'warning'); return; }
    load();
  };

  // ‼️ A CONFIRM, because this is the one irreversible control on the panel —
  // and an IN-APP one, because window.confirm froze a real iPhone (§94).
  const remove = (id, code) => confirmer.ask({
    tone: 'danger',
    title: zh ? '刪除優惠碼？' : 'Delete this code?',
    subject: code,
    body: zh ? '已經用咗嘅優惠碼刪唔到 —— 兌換紀錄係我哋批出過權限嘅憑證。'
             : 'A code somebody has already redeemed cannot be deleted — the redemptions are the record of the access we granted.',
    confirmLabel: zh ? '刪除' : 'Delete',
    failureText: zh ? '刪除失敗' : 'Delete failed',
    onConfirm: () => removeNow(id),
  });

  const removeNow = async (id) => {
    setBusyId(id);
    const { data, error } = await window.DB.adminPromoDelete(id);
    setBusyId(null);
    if (error) { showVitalToast(zh ? '刪除失敗' : 'Delete failed', 'warning'); return; }
    // The server refuses to delete a code somebody has used — the redemptions
    // are the record of access we granted. Say what to do instead.
    if (data === 'has_redemptions') {
      showVitalToast(zh ? '已有人使用，改為「停用」' : 'Already redeemed — disable it instead', 'warning');
      return;
    }
    load();
  };

  const submit = async () => {
    if (creating || !form) return;
    const code = (form.code || '').trim().toUpperCase();
    const days = Number(form.grantDays);
    if (!/^[A-Z0-9][A-Z0-9-]{2,30}$/.test(code)) {
      showVitalToast(zh ? '優惠碼只可用英文字母、數字同「-」，4–32 個字元' : 'Codes are 4–32 chars: A–Z, 0–9 and -', 'warning');
      return;
    }
    if (!Number.isInteger(days) || days < 1 || days > 730) {
      showVitalToast(zh ? '日數要喺 1–730 之間' : 'Days must be 1–730', 'warning');
      return;
    }
    setCreating(true);
    const { error } = await window.DB.adminPromoCreate({
      code,
      grantDays: days,
      maxRedemptions: form.maxRedemptions === '' ? null : Number(form.maxRedemptions),
      perUserLimit: Number(form.perUserLimit) || 1,
      startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
      endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
      note: form.note || null,
    });
    setCreating(false);
    if (error) {
      // 23505 = the unique index on code. Naming it beats "action failed",
      // because the fix is entirely in the admin's hands.
      showVitalToast(error.code === '23505'
        ? (zh ? '呢個優惠碼已經存在' : 'That code already exists')
        : (zh ? '建立失敗' : 'Could not create'), 'warning');
      return;
    }
    setForm(null);
    load();
  };

  const fmt = (iso) => (iso ? fmtDate(String(iso).slice(0, 10), lang) : '—');
  const STATUS = {
    active:   { zh: '使用中', en: 'Active' },
    paused:   { zh: '已暫停', en: 'Paused' },
    disabled: { zh: '已停用', en: 'Disabled' },
  };

  return (
    <div style={{ padding: '4px 20px 24px' }}>
      {/* The constraint, stated before the first control rather than after a
          support question. */}
      <div className="onb-info-note" role="note" style={{ marginBottom: 12 }}>
        {zh
          ? '優惠碼會免費開通 Vital Pro 指定日數。由於 Vital Pro 經 App Store 收費，價格由 Apple 決定，所以呢度唔可以設定「打折」或「減 HK$」—— 折扣優惠要喺 App Store Connect 開 Offer Code。'
          : 'A code switches Vital Pro on free for a set number of days. Vital Pro is billed by the App Store and Apple sets the price, so a percentage or amount discount cannot be created here — those are App Store offer codes, configured in App Store Connect.'}
      </div>

      {err && (
        <div role="alert" style={{
          marginBottom: 12, padding: '10px 12px', borderRadius: 10,
          background: 'color-mix(in oklch, var(--danger) 8%, transparent)',
          color: 'var(--danger)', fontSize: 12.5, lineHeight: 1.5,
        }}>{err}</div>
      )}

      {/* ── Usage at a glance ────────────────────────────────────────────
          The last tile is the only one that answers "was it worth it": how
          many of the nurses who redeemed went on to pay. */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
          {[
            { k: 'redemptions_total', zh: '總使用次數', en: 'Redemptions' },
            { k: 'users_total',       zh: '使用人數',   en: 'People' },
            { k: 'active_promo_users', zh: '現正生效',  en: 'Active now' },
            { k: 'redemptions_7d',    zh: '近 7 日',    en: 'Last 7d' },
            { k: 'days_granted_total', zh: '已送出日數', en: 'Days given' },
            { k: 'converted_users',   zh: '之後付費',   en: 'Converted' },
          ].map(t => (
            <div key={t.k} className="card" style={{ padding: '10px 12px' }}>
              <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
                {Number(stats[t.k] ?? 0).toLocaleString('en-US')}
              </div>
              <div className="muted-2" style={{ fontSize: 10.5, marginTop: 2 }}>{zh ? t.zh : t.en}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Create ─────────────────────────────────────────────────────── */}
      {!form ? (
        <button type="button" className="btn btn-primary" style={{ width: '100%', marginBottom: 14 }}
          onClick={() => setForm({ code: '', grantDays: 30, maxRedemptions: '', perUserLimit: 1, startsAt: '', endsAt: '', note: '' })}>
          <Icon name="plus" size={14} stroke={2.2} />
          <span style={{ marginLeft: 6 }}>{zh ? '新增優惠碼' : 'New code'}</span>
        </button>
      ) : (
        <div className="card" style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 10 }}>
            {zh ? '新增優惠碼' : 'New code'}
          </div>
          <AuthField label={zh ? '優惠碼' : 'Code'} value={form.code}
            onChange={(v) => setForm({ ...form, code: v.toUpperCase() })}
            placeholder="VITAL-2026" />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <AuthField label={zh ? '開通日數' : 'Days granted'} type="number"
                value={String(form.grantDays)} onChange={(v) => setForm({ ...form, grantDays: v })} />
            </div>
            <div style={{ flex: 1 }}>
              <AuthField label={zh ? '可用次數（空=不限）' : 'Max uses (blank = ∞)'} type="number"
                value={String(form.maxRedemptions)} onChange={(v) => setForm({ ...form, maxRedemptions: v })} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <AuthField label={zh ? '每人上限' : 'Per person'} type="number"
                value={String(form.perUserLimit)} onChange={(v) => setForm({ ...form, perUserLimit: v })} />
            </div>
            <div style={{ flex: 1 }}>
              <AuthField label={zh ? '開始日期（選填）' : 'Starts (optional)'} type="date"
                value={form.startsAt} onChange={(v) => setForm({ ...form, startsAt: v })} />
            </div>
            <div style={{ flex: 1 }}>
              <AuthField label={zh ? '結束日期（選填）' : 'Ends (optional)'} type="date"
                value={form.endsAt} onChange={(v) => setForm({ ...form, endsAt: v })} />
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <AuthField label={zh ? '備註（只有管理員睇到）' : 'Note (admin only)'} value={form.note}
              onChange={(v) => setForm({ ...form, note: v })}
              placeholder={zh ? '例如：護士節推廣' : 'e.g. Nurses Day campaign'} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" style={{ flex: 1 }} disabled={creating} onClick={submit}>
              {creating ? (zh ? '建立中…' : 'Creating…') : (zh ? '建立' : 'Create')}
            </button>
            <button className="btn btn-ghost" style={{ padding: '6px 14px', fontSize: 12.5 }}
              disabled={creating} onClick={() => setForm(null)}>
              {zh ? '取消' : 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* ── The codes ──────────────────────────────────────────────────── */}
      {rows === null ? (
        <Skeleton h={72} r={14} />
      ) : rows.length === 0 ? (
        <div className="muted-2" style={{ fontSize: 12.5, textAlign: 'center', padding: '20px 0' }}>
          {zh ? '未有任何優惠碼。' : 'No codes yet.'}
        </div>
      ) : rows.map(r => {
        const used = Number(r.redemptions || 0);
        const cap = r.max_redemptions;
        const isBusy = busyId === r.id;
        const expired = r.ends_at && Date.parse(r.ends_at) <= Date.now();
        return (
          <div key={r.id} className="card" style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span className="mono" style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.04em' }}>
                {r.code}
              </span>
              <span className="chip" style={{ fontSize: 10.5 }}>
                {r.status === 'active' && expired
                  ? (zh ? '已過期' : 'Expired')
                  : (zh ? STATUS[r.status].zh : STATUS[r.status].en)}
              </span>
              <span className="muted-2" style={{ fontSize: 11.5, marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>
                {used}{cap ? ` / ${cap}` : ''} {zh ? '次' : 'used'}
              </span>
            </div>
            <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.6, marginTop: 6 }}>
              {zh ? `開通 ${r.grant_days} 日 · 每人 ${r.per_user_limit} 次` : `${r.grant_days} days · ${r.per_user_limit} per person`}
              {(r.starts_at || r.ends_at) && (zh ? ` · ${fmt(r.starts_at)} → ${fmt(r.ends_at)}` : ` · ${fmt(r.starts_at)} → ${fmt(r.ends_at)}`)}
              <br />
              {zh ? `${r.unique_users || 0} 人使用` : `${r.unique_users || 0} people`}
              {Number(r.converted || 0) > 0 && (zh ? ` · ${r.converted} 人之後付費` : ` · ${r.converted} later paid`)}
              {r.last_redeemed_at && (zh ? ` · 最近 ${fmt(r.last_redeemed_at)}` : ` · last ${fmt(r.last_redeemed_at)}`)}
              {r.note ? <><br />{r.note}</> : null}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              {r.status === 'active' ? (
                <button className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: 12 }}
                  disabled={isBusy} onClick={() => setStatus(r.id, 'paused')}>
                  {zh ? '暫停' : 'Pause'}
                </button>
              ) : (
                <button className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: 12 }}
                  disabled={isBusy} onClick={() => setStatus(r.id, 'active')}>
                  {zh ? '啟用' : 'Resume'}
                </button>
              )}
              {r.status !== 'disabled' && (
                <button className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: 12 }}
                  disabled={isBusy} onClick={() => setStatus(r.id, 'disabled')}>
                  {zh ? '停用' : 'Disable'}
                </button>
              )}
              {/* ‼️ DELETE IS ONLY OFFERED WHEN IT WILL WORK. The server refuses
                  to delete a redeemed code, so showing the button on one would
                  be a control that exists to fail. Disable is the answer there,
                  and it is the button sitting right beside it. */}
              {used === 0 && (
                <button className="btn btn-ghost"
                  style={{ padding: '6px 12px', fontSize: 12, color: 'var(--danger)' }}
                  disabled={isBusy} onClick={() => remove(r.id, r.code)}>
                  {zh ? '刪除' : 'Delete'}
                </button>
              )}
            </div>
          </div>
        );
      })}
      <ConfirmDialog request={confirmer.request} busy={confirmer.busy} lang={lang}
        onCancel={confirmer.cancel} onConfirm={confirmer.confirm} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 公告 — ADMIN PANEL
// ─────────────────────────────────────────────────────────────────────────────
//
// ‼️ EVERY CONTROL ON THIS PANEL WRITES TO EVERY NURSE'S DASHBOARD. It is the
// only admin surface in this app that is not a queue of things other people
// did — it is a microphone. Two consequences shape the design:
//
//   1. THE PREVIEW IS THE REAL COMPONENT (NoticeBanner, preview=true), in BOTH
//      languages, always visible while editing. An operator publishing to
//      thousands of nurses should never have to imagine what it looks like, and
//      a preview built from its own markup is a preview that eventually lies.
//   2. NOTHING GOES OUT WITHOUT A SECOND STEP. Publish confirms. Push confirms
//      with a real recipient count fetched from the sender itself.
//
// Authority is admin-only (is_full_admin in SQL, role='admin' in the Edge
// Function). This panel is not rendered for a moderator — and if one reached it
// anyway, every RPC returns 42501 and the copy says so rather than showing an
// empty list.
const NOTICE_SEVERITIES = ['info', 'success', 'warning', 'critical'];
const NOTICE_AUDIENCES = ['all', 'signed_in', 'guests', 'pro', 'free'];
const NOTICE_LABELS = {
  severity: {
    info:     { zh: '一般',   en: 'Info' },
    success:  { zh: '好消息', en: 'Good news' },
    warning:  { zh: '注意',   en: 'Warning' },
    critical: { zh: '緊急',   en: 'Critical' },
  },
  audience: {
    all:       { zh: '所有人',       en: 'Everyone' },
    signed_in: { zh: '已登入用戶',   en: 'Signed-in only' },
    guests:    { zh: '未登入訪客',   en: 'Guests only' },
    pro:       { zh: 'Pro 會員',     en: 'Pro members' },
    free:      { zh: '免費用戶',     en: 'Free users' },
  },
  status: {
    draft:     { zh: '草稿',   en: 'Draft' },
    published: { zh: '已發布', en: 'Published' },
    archived:  { zh: '已封存', en: 'Archived' },
  },
};

// ‼️ THE ADMIN'S DEVICE CLOCK IS THE AUTHORING CLOCK. <input type="datetime-local">
// reads and writes LOCAL time; the database stores timestamptz. Converting at
// exactly these two points (and nowhere else) is what stops a notice scheduled
// for "09:00" going live at 17:00. The operator is in Hong Kong, so local is
// HKT — but nothing here assumes that, which is the point.
const noticeToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
       + `T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const noticeFromLocalInput = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const emptyNoticeForm = () => ({
  id: null, slug: '', status: 'draft',
  titleEn: '', titleZh: '', bodyEn: '', bodyZh: '',
  severity: 'info', audience: 'all',
  ctaLabelEn: '', ctaLabelZh: '', ctaUrl: '',
  startsAt: '', endsAt: '', dismissible: true, priority: 0,
});

function AdminNoticesPanel({ lang }) {
  const confirmer = useConfirm();
  const zh = lang === 'zh';
  const [rows, setRows] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [busyId, setBusyId] = React.useState(null);
  const [form, setForm] = React.useState(null);      // null = editor closed
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setErr('');
    const { data, error } = await window.DB.adminNoticeList();
    if (error) {
      // 42501 is the server refusing, not an empty list — saying "no notices"
      // would send someone looking for a data problem that isn't one.
      setErr(error.code === '42501'
        ? (zh ? '只有管理員可以發布公告（版主無此權限）。'
              : 'Only administrators can publish notices — moderators cannot.')
        : (zh ? '載入失敗，請重試。' : 'Could not load. Please retry.'));
      setRows([]);
      return;
    }
    setRows(data || []);
  }, [zh]);
  React.useEffect(() => { load(); }, [load]);

  // ── Cap signal ───────────────────────────────────────────────────────────
  // get_active_notices() returns at most 3. Anything beyond that is invisible
  // to users, and the operator must be TOLD rather than discovering it when a
  // nurse says she never saw the notice. See memory: cap-signals-are-product-signals.
  const liveCount = (rows || []).filter(r => r.is_live).length;

  const openNew = () => setForm(emptyNoticeForm());
  const openEdit = (r) => setForm({
    id: r.id, slug: r.slug, status: r.status,
    titleEn: r.title_en || '', titleZh: r.title_zh || '',
    bodyEn: r.body_en || '', bodyZh: r.body_zh || '',
    severity: r.severity || 'info', audience: r.audience || 'all',
    ctaLabelEn: r.cta_label_en || '', ctaLabelZh: r.cta_label_zh || '',
    ctaUrl: r.cta_url || '',
    startsAt: noticeToLocalInput(r.starts_at), endsAt: noticeToLocalInput(r.ends_at),
    dismissible: r.dismissible !== false, priority: r.priority ?? 0,
  });

  // Client-side validation mirrors the CHECK constraints. It exists to give a
  // useful message at the keyboard, NOT to be the boundary — the database
  // refuses the same things regardless of what this function decides.
  const validate = (f) => {
    if (!/^[a-z0-9][a-z0-9-]{2,63}$/.test(f.slug.trim())) {
      return zh ? '識別碼只可用小階英文、數字同「-」，3–64 個字元。'
                : 'The id must be 3–64 chars: a–z, 0–9 and hyphens.';
    }
    for (const [v, name] of [[f.titleEn, 'English title'], [f.titleZh, '中文標題']]) {
      if (!v.trim() || v.trim().length > 80) {
        return zh ? `${name}：必填，最多 80 字。` : `${name}: required, 80 characters max.`;
      }
    }
    for (const [v, name] of [[f.bodyEn, 'English body'], [f.bodyZh, '中文內文']]) {
      if (!v.trim() || v.trim().length > 500) {
        return zh ? `${name}：必填，最多 500 字。` : `${name}: required, 500 characters max.`;
      }
    }
    const hasUrl = !!f.ctaUrl.trim();
    const hasLabels = !!f.ctaLabelEn.trim() && !!f.ctaLabelZh.trim();
    if (hasUrl !== hasLabels) {
      return zh ? '按鈕文字（中英）同連結要一齊填，或者一齊留空。'
                : 'The button needs both labels and a link — or leave all three blank.';
    }
    if (hasUrl && !window.DB.isSafeNoticeCtaUrl(f.ctaUrl.trim())) {
      return zh ? '連結只接受 https:// 網址或 vital:// 應用內連結。'
                : 'Links must be an https:// address or a vital:// in-app link.';
    }
    if (f.startsAt && f.endsAt && new Date(f.endsAt) <= new Date(f.startsAt)) {
      return zh ? '結束時間要喺開始時間之後。' : 'The end time must be after the start time.';
    }
    return null;
  };

  const save = async () => {
    if (saving || !form) return;
    const problem = validate(form);
    if (problem) { showVitalToast(problem, 'warning'); return; }
    setSaving(true);
    const { error } = await window.DB.adminNoticeUpsert({
      id: form.id, slug: form.slug.trim(),
      titleEn: form.titleEn.trim(), titleZh: form.titleZh.trim(),
      bodyEn: form.bodyEn.trim(), bodyZh: form.bodyZh.trim(),
      severity: form.severity, audience: form.audience,
      ctaLabelEn: form.ctaLabelEn.trim() || null,
      ctaLabelZh: form.ctaLabelZh.trim() || null,
      ctaUrl: form.ctaUrl.trim() || null,
      startsAt: noticeFromLocalInput(form.startsAt),
      endsAt: noticeFromLocalInput(form.endsAt),
      dismissible: form.dismissible, priority: Number(form.priority) || 0,
    });
    setSaving(false);
    if (error) {
      showVitalToast(error.code === '23505'
        ? (zh ? '呢個識別碼已經有人用咗' : 'That id is already taken')
        : (error.message || (zh ? '儲存失敗' : 'Could not save')), 'warning');
      return;
    }
    setForm(null);
    load();
  };

  // ‼️ PUBLISHING IS THE ONE ACTION THAT REACHES EVERYONE. It confirms — and
  // every other transition does not, which is why the ask is conditional rather
  // than the handler being split in two.
  const setStatus = (r, status) => {
    if (busyId) return;
    if (status !== 'published') { setStatusNow(r, status); return; }
    confirmer.ask({
      title: zh ? '發布這則公告？' : 'Publish this notice?',
      subject: zh ? r.title_zh : r.title_en,
      body: zh ? '所有符合對象嘅用戶會喺主畫面睇到。'
               : 'Every matching user will see it on their dashboard.',
      confirmLabel: zh ? '發布' : 'Publish',
      failureText: zh ? '操作失敗' : 'Action failed',
      onConfirm: () => setStatusNow(r, status),
    });
  };

  const setStatusNow = async (r, status) => {
    setBusyId(r.id);
    const { error } = await window.DB.adminNoticeSetStatus(r.id, status);
    setBusyId(null);
    if (error) { showVitalToast(zh ? '操作失敗' : 'Action failed', 'warning'); return; }
    load();
  };

  const remove = (r) => confirmer.ask({
    tone: 'danger',
    title: zh ? '刪除這則公告？' : 'Delete this notice?',
    subject: zh ? r.title_zh : r.title_en,
    body: zh ? '無法復原。' : 'This cannot be undone.',
    confirmLabel: zh ? '刪除' : 'Delete',
    failureText: zh ? '刪除失敗' : 'Delete failed',
    onConfirm: () => removeNow(r),
  });

  const removeNow = async (r) => {
    setBusyId(r.id);
    const { error } = await window.DB.adminNoticeDelete(r.id);
    setBusyId(null);
    if (error) { showVitalToast(zh ? '刪除失敗' : 'Delete failed', 'warning'); return; }
    load();
  };

  // ── Push ─────────────────────────────────────────────────────────────────
  // Two calls, always: a dry run that returns the real recipient count from the
  // sender itself, then the send. The number in the confirm is therefore the
  // number that will actually be used — not an estimate computed here.
  const broadcast = async (r) => {
    if (busyId) return;
    setBusyId(r.id);
    const dry = await window.DB.adminNoticeBroadcast(r.id, { dryRun: true });
    if (dry.error) {
      setBusyId(null);
      showVitalToast(dry.error.message || (zh ? '無法預估收件人' : 'Could not count recipients'), 'warning');
      return;
    }
    const n = dry.data?.result?.recipients ?? 0;
    setBusyId(null);

    // ‼️ THE DRY RUN RUNS BEFORE THE ASK, AND THAT IS STILL CORRECT. The number
    // in the question IS the thing being confirmed — asking "send to ? nurses"
    // would be asking nothing. With window.confirm this ordering was actively
    // dangerous: a native panel opened after an await has lost its user-gesture
    // token, which iOS drops silently (the same class as §75's window.open).
    // Opening a React dialog after an await has no such constraint.
    confirmer.ask({
      title: zh ? '發送推送通知？' : 'Send as a push notification?',
      subject: zh ? r.title_zh : r.title_en,
      subjectDetail: zh ? `${n.toLocaleString('en-US')} 位護士`
                        : `${n.toLocaleString('en-US')} nurses`,
      body: zh ? '每位只會收到一次（重複發送唔會再send）。已關閉「規例更新」通知嘅用戶只會喺通知中心見到。'
               : 'Each person receives it once — re-sending will not push again. Anyone who muted “Regulatory updates” will only see it in their notification centre.',
      confirmLabel: zh ? '發送' : 'Send',
      failureText: zh ? '發送失敗' : 'Send failed',
      onConfirm: () => broadcastNow(r),
    });
  };

  const broadcastNow = async (r) => {
    setBusyId(r.id);
    const { data, error } = await window.DB.adminNoticeBroadcast(r.id, { dryRun: false });
    setBusyId(null);
    if (error) { showVitalToast(error.message || (zh ? '發送失敗' : 'Send failed'), 'warning'); return; }
    const res = data?.result || {};
    showVitalToast(zh
      ? `已發送：${res.pushed ?? 0} 個推送、${res.claimed ?? 0} 條通知記錄`
      : `Sent: ${res.pushed ?? 0} pushes, ${res.claimed ?? 0} notification records`);
    load();
  };

  // The preview renders the SHIPPING component from the SHIPPING field values,
  // so what the operator signs off is what a nurse receives.
  const previewNotice = form && {
    id: 'preview', slug: form.slug,
    title_en: form.titleEn || 'Title', title_zh: form.titleZh || '標題',
    body_en: form.bodyEn || 'Body text', body_zh: form.bodyZh || '內文',
    severity: form.severity,
    cta_label_en: form.ctaLabelEn || null, cta_label_zh: form.ctaLabelZh || null,
    cta_url: form.ctaUrl || null, dismissible: form.dismissible,
  };

  const pill = (text, tone) => (
    <span style={{
      fontSize: 10.5, padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap',
      background: `color-mix(in oklch, ${tone} 14%, transparent)`, color: tone,
    }}>{text}</span>
  );

  const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString(zh ? 'zh-HK' : 'en-GB',
    { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : null);

  return (
    <div style={{ padding: '4px 20px 24px' }}>
      <div className="onb-info-note" role="note" style={{ marginBottom: 12 }}>
        {zh
          ? '公告會即時出現喺用戶主畫面頂部，唔使等 App Store 審核。內容只支援純文字（唔會當 HTML 處理）。'
          : 'A notice appears at the top of the dashboard immediately — no App Store review needed. Copy is plain text only and is never rendered as HTML.'}
      </div>

      {err && (
        <div role="alert" style={{
          marginBottom: 12, padding: '10px 12px', borderRadius: 10,
          background: 'color-mix(in oklch, var(--danger) 8%, transparent)',
          color: 'var(--danger)', fontSize: 12.5, lineHeight: 1.5,
        }}>{err}</div>
      )}

      {/* ‼️ The dashboard shows at most 3. Beyond that the lowest-precedence
          notices are invisible, and the operator has to hear it from us. */}
      {liveCount > 3 && (
        <div role="status" style={{
          marginBottom: 12, padding: '10px 12px', borderRadius: 10,
          background: 'color-mix(in oklch, var(--warning) 16%, transparent)',
          color: 'var(--fg)', fontSize: 12.5, lineHeight: 1.5,
        }}>
          <strong>{zh ? `現時有 ${liveCount} 則生效中` : `${liveCount} notices are live`}</strong>
          <div style={{ marginTop: 2, opacity: 0.9 }}>
            {zh
              ? '主畫面最多只顯示 3 則（按緊急程度、優先度排序）。其餘嘅用戶睇唔到 —— 考慮封存舊公告或者調整優先度。'
              : 'The dashboard shows only the top 3, ranked by severity then priority. The rest are invisible to users — archive an older one or adjust priority.'}
          </div>
        </div>
      )}

      {!form ? (
        <button type="button" className="btn btn-primary" style={{ width: '100%', marginBottom: 14 }}
          onClick={openNew}>
          <Icon name="plus" size={14} stroke={2.2} />
          <span style={{ marginLeft: 6 }}>{zh ? '新增公告' : 'New notice'}</span>
        </button>
      ) : (
        <div className="card" style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 10 }}>
            {form.id ? (zh ? '編輯公告' : 'Edit notice') : (zh ? '新增公告' : 'New notice')}
          </div>

          {/* Live preview, both languages. Non-negotiable: see the panel header. */}
          <div style={{ marginBottom: 12 }}>
            <div className="muted-2" style={{ fontSize: 11, marginBottom: 2, marginLeft: 2 }}>
              {zh ? '預覽（用戶睇到嘅樣）' : 'Preview (what a nurse sees)'}
            </div>
            <div style={{ margin: '0 -20px' }}>
              <NoticeBanner notice={previewNotice} lang="en" preview />
              <NoticeBanner notice={previewNotice} lang="zh" preview />
            </div>
          </div>

          <AuthField label={zh ? '識別碼（發布後不可更改）' : 'Id (frozen once published)'}
            value={form.slug}
            onChange={(v) => form.status === 'draft'
              && setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
            placeholder="typhoon-signal-8-oct" />
          {form.status !== 'draft' && (
            <div className="muted-2" style={{ fontSize: 11, marginTop: -4, marginBottom: 6 }}>
              {zh ? '已發布嘅公告唔可以改識別碼 —— 訪客嘅「已關閉」記錄同推送去重都靠佢。'
                  : 'Locked after publishing: guest dismissals and push de-duplication are both keyed on it.'}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <AuthField label={`English title (${form.titleEn.length}/80)`} value={form.titleEn}
                onChange={(v) => setForm({ ...form, titleEn: v })} />
            </div>
            <div style={{ flex: 1 }}>
              <AuthField label={`中文標題 (${form.titleZh.length}/80)`} value={form.titleZh}
                onChange={(v) => setForm({ ...form, titleZh: v })} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <AuthField label={`English body (${form.bodyEn.length}/500)`} value={form.bodyEn}
                onChange={(v) => setForm({ ...form, bodyEn: v })} />
            </div>
            <div style={{ flex: 1 }}>
              <AuthField label={`中文內文 (${form.bodyZh.length}/500)`} value={form.bodyZh}
                onChange={(v) => setForm({ ...form, bodyZh: v })} />
            </div>
          </div>

          {/* Severity + audience: chips rather than selects, because both are
              short closed vocabularies and both change what the preview shows. */}
          <div style={{ marginTop: 12 }}>
            <div className="auth-label mono" style={{ marginBottom: 5 }}>{zh ? '緊急程度' : 'Severity'}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {NOTICE_SEVERITIES.map(s => (
                <button key={s} type="button"
                  className={'chip' + (form.severity === s ? ' on' : '')}
                  onClick={() => setForm({ ...form, severity: s })}>
                  {zh ? NOTICE_LABELS.severity[s].zh : NOTICE_LABELS.severity[s].en}
                </button>
              ))}
            </div>
            {form.severity === 'critical' && (
              <div className="muted-2" style={{ fontSize: 11, marginTop: 5 }}>
                {zh ? '「緊急」會暫時收起主畫面其他提示卡（例如「掃描第一張證書」）。'
                    : '“Critical” temporarily suppresses the other dashboard prompts (e.g. “Scan your first certificate”).'}
              </div>
            )}
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="auth-label mono" style={{ marginBottom: 5 }}>{zh ? '對象' : 'Audience'}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {NOTICE_AUDIENCES.map(a => (
                <button key={a} type="button"
                  className={'chip' + (form.audience === a ? ' on' : '')}
                  onClick={() => setForm({ ...form, audience: a })}>
                  {zh ? NOTICE_LABELS.audience[a].zh : NOTICE_LABELS.audience[a].en}
                </button>
              ))}
            </div>
            {form.audience === 'guests' && (
              <div className="muted-2" style={{ fontSize: 11, marginTop: 5 }}>
                {zh ? '訪客冇帳戶，所以：唔可以推送，而佢哋嘅「已關閉」只存喺裝置上（重裝就會再見到）。建議設定結束時間。'
                    : 'Guests have no account, so this cannot be pushed and their dismissal is device-only (it returns after a reinstall). Give it an end time.'}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <div style={{ flex: 1 }}>
              <AuthField label={zh ? '開始時間（空=即時）' : 'Starts (blank = now)'} type="datetime-local"
                value={form.startsAt} onChange={(v) => setForm({ ...form, startsAt: v })} />
            </div>
            <div style={{ flex: 1 }}>
              <AuthField label={zh ? '結束時間（空=不限）' : 'Ends (blank = never)'} type="datetime-local"
                value={form.endsAt} onChange={(v) => setForm({ ...form, endsAt: v })} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <div style={{ flex: 2 }}>
              <AuthField label={zh ? '按鈕連結（選填）' : 'Button link (optional)'} value={form.ctaUrl}
                onChange={(v) => setForm({ ...form, ctaUrl: v })}
                placeholder="https://www.nchk.org.hk/… or vital://discover" />
            </div>
            <div style={{ flex: 1 }}>
              <AuthField label="Button (EN)" value={form.ctaLabelEn}
                onChange={(v) => setForm({ ...form, ctaLabelEn: v })} placeholder="Read more" />
            </div>
            <div style={{ flex: 1 }}>
              <AuthField label="按鈕（中）" value={form.ctaLabelZh}
                onChange={(v) => setForm({ ...form, ctaLabelZh: v })} placeholder="了解詳情" />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 14, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.dismissible}
                onChange={(e) => setForm({ ...form, dismissible: e.target.checked })} />
              {zh ? '可以關閉' : 'Dismissible'}
            </label>
            <div style={{ width: 120 }}>
              <AuthField label={zh ? '優先度' : 'Priority'} type="number"
                value={String(form.priority)} onChange={(v) => setForm({ ...form, priority: v })} />
            </div>
          </div>
          {!form.dismissible && (
            <div className="muted-2" style={{ fontSize: 11, marginTop: 6 }}>
              {zh ? '⚠️ 用戶冇辦法收起呢張卡，佢會一直霸住主畫面頂部直到結束時間。只適合真正影響服務嘅情況。'
                  : '⚠️ Nobody can clear this card — it holds the top of the dashboard until it expires. Reserve it for genuinely service-affecting notices.'}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button className="btn btn-primary" style={{ flex: 1 }} disabled={saving} onClick={save}>
              {saving ? (zh ? '儲存中…' : 'Saving…') : (zh ? '儲存草稿' : 'Save')}
            </button>
            <button className="btn btn-ghost" style={{ padding: '6px 14px', fontSize: 12.5 }}
              disabled={saving} onClick={() => setForm(null)}>
              {zh ? '取消' : 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {rows === null && (
        <div className="muted" style={{ textAlign: 'center', padding: '24px 0' }}>
          {zh ? '載入中…' : 'Loading…'}
        </div>
      )}

      {rows !== null && rows.length === 0 && !err && (
        <div className="vital-empty" style={{ marginTop: 12 }}>
          <div className="vital-empty-icon"><Icon name="bell" size={22} stroke={1.7} /></div>
          <div className="vital-empty-title">{zh ? '未有公告' : 'No notices yet'}</div>
          <div className="vital-empty-sub">
            {zh ? '有突發狀況或者新功能時，喺呢度出一則公告。'
                : 'When something changes or breaks, post a notice here.'}
          </div>
        </div>
      )}

      {(rows || []).map(r => {
        const tone = r.severity === 'critical' ? 'var(--danger)'
          : r.severity === 'warning' ? 'var(--warning)'
          : r.severity === 'success' ? 'var(--success)' : 'var(--primary)';
        const expired = r.status === 'published' && r.ends_at && new Date(r.ends_at) <= new Date();
        const scheduled = r.status === 'published' && r.starts_at && new Date(r.starts_at) > new Date();
        return (
          <div key={r.id} className="card" style={{ marginBottom: 10, padding: 12 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
              {r.is_live
                ? pill(zh ? '生效中' : 'Live', 'var(--success)')
                : expired ? pill(zh ? '已過期' : 'Expired', 'var(--fg-3)')
                : scheduled ? pill(zh ? '已排程' : 'Scheduled', 'var(--accent)')
                : pill(zh ? NOTICE_LABELS.status[r.status].zh : NOTICE_LABELS.status[r.status].en, 'var(--fg-3)')}
              {pill(zh ? NOTICE_LABELS.severity[r.severity]?.zh : NOTICE_LABELS.severity[r.severity]?.en, tone)}
              {pill(zh ? NOTICE_LABELS.audience[r.audience]?.zh : NOTICE_LABELS.audience[r.audience]?.en, 'var(--fg-2)')}
              {!r.dismissible && pill(zh ? '不可關閉' : 'Not dismissible', 'var(--danger)')}
            </div>

            <div style={{ fontWeight: 700, fontSize: 13 }}>{zh ? r.title_zh : r.title_en}</div>
            <div className="muted-2" style={{ fontSize: 12, lineHeight: 1.45, marginTop: 2 }}>
              {zh ? r.body_zh : r.body_en}
            </div>

            <div className="muted-2" style={{ fontSize: 11, marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>
              <code style={{ fontSize: 10.5 }}>{r.slug}</code>
              {' · '}
              {/* Reach, not just dismissals: the nurses who read it and left it
                  alone are the ones a dismissal count silently drops. */}
              {zh ? `${r.seen_count} 人睇過 · ${r.dismissed_count} 人關閉`
                  : `${r.seen_count} seen · ${r.dismissed_count} dismissed`}
              {(r.starts_at || r.ends_at) && (
                <>{' · '}{fmtWhen(r.starts_at) || (zh ? '即時' : 'now')} → {fmtWhen(r.ends_at) || (zh ? '不限' : '∞')}</>
              )}
            </div>

            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
              <button className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 11px' }}
                disabled={busyId === r.id} onClick={() => openEdit(r)}>
                {zh ? '編輯' : 'Edit'}
              </button>
              {r.status !== 'published' ? (
                <button className="btn btn-primary" style={{ fontSize: 12, padding: '5px 11px' }}
                  disabled={busyId === r.id} onClick={() => setStatus(r, 'published')}>
                  {zh ? '發布' : 'Publish'}
                </button>
              ) : (
                // ‼️ THE EMERGENCY BRAKE. One tap, always visible while live —
                // a notice with a wrong date in it gets pulled, not edited in
                // place while every nurse is reading it.
                <button className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 11px' }}
                  disabled={busyId === r.id} onClick={() => setStatus(r, 'draft')}>
                  {zh ? '收回' : 'Unpublish'}
                </button>
              )}
              {/* ‼️ is_live, NOT status === 'published'. A notice scheduled for
                  tomorrow or expired yesterday is published but is on nobody's
                  dashboard, so a push would buzz her phone and land her on a
                  screen with no banner. The Edge Function refuses it too — this
                  just avoids offering a button that cannot work. Guests have no
                  device to push to at all. */}
              {r.is_live && r.audience !== 'guests' && (
                <button className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 11px' }}
                  disabled={busyId === r.id} onClick={() => broadcast(r)}>
                  <Icon name="bell" size={12} stroke={1.9} />
                  <span style={{ marginLeft: 5 }}>{zh ? '同時推送' : 'Also push'}</span>
                </button>
              )}
              {r.status !== 'archived' && (
                <button className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 11px' }}
                  disabled={busyId === r.id} onClick={() => setStatus(r, 'archived')}>
                  {zh ? '封存' : 'Archive'}
                </button>
              )}
              <button className="btn btn-ghost"
                style={{ fontSize: 12, padding: '5px 11px', color: 'var(--danger)' }}
                disabled={busyId === r.id} onClick={() => remove(r)}>
                {zh ? '刪除' : 'Delete'}
              </button>
            </div>
          </div>
        );
      })}
      <ConfirmDialog request={confirmer.request} busy={confirmer.busy} lang={lang}
        onCancel={confirmer.cancel} onConfirm={confirmer.confirm} />
    </div>
  );
}

// ‼️ `profile` IS NOT DECORATION. admin_set_role refuses self-modification with
// 42501, so a 移除 button on the acting admin's own row is a control whose
// handler can never succeed — the same shape §53 pinned on Home's route card.
// Without an identity to compare against, this panel cannot know which row that is.
function AdminManagementPanel({ lang, profile }) {
  const meId = profile?.id || null;

  const [admins, setAdmins] = React.useState([]);
  const [adminsLoading, setAdminsLoading] = React.useState(false);
  const [adminsErr, setAdminsErr] = React.useState(null);

  const [searchQ, setSearchQ] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  // ‼️ THE QUERY THAT WAS ACTUALLY SEARCHED, not the one being typed. Keeping
  // only `searchQ` made the panel claim knowledge it did not have: the first
  // keystroke satisfied `searchQ.trim() && searchResults.length === 0`, so
  // 「沒有符合的使用者」 appeared before anything had been looked up — the
  // panel said a nurse did not exist while the admin was still typing her name.
  const [searchedQ, setSearchedQ] = React.useState('');
  const [searching, setSearching] = React.useState(false);
  const [busyId, setBusyId] = React.useState(null);
  const dirtyQuery = searchQ.trim() !== searchedQ;

  // ‼️ NOT window.confirm. On a real iPhone (Safari, 2026-09-01) tapping
  // 「設為管理員」 produced NO dialog and froze the page: window.confirm blocks
  // the JS main thread, and if WebKit defers presenting the panel for any
  // reason the app is wedged with no recourse — the dialog finally appeared
  // only after a reload and a re-entry into this tab. A React dialog cannot
  // fail that way: opening it is a state change, and the RPC does not start
  // until the admin has answered.
  const [confirmTarget, setConfirmTarget] = React.useState(null); // {userId, role, person}
  // ‼️ A REF, NOT `busyId`. Two taps in the same tick both read the old state
  // and both fire the RPC; a ref latches synchronously.
  const inFlight = React.useRef(false);

  const loadAdmins = React.useCallback(async () => {
    setAdminsLoading(true);
    setAdminsErr(null);
    const { data, error } = await window.DB.fetchAdminProfiles();
    if (error) setAdminsErr(error.message || String(error));
    else setAdmins(data || []);
    setAdminsLoading(false);
  }, []);

  React.useEffect(() => { loadAdmins(); }, [loadAdmins]);

  const runSearch = async () => {
    const q = searchQ.trim();
    if (!q) { setSearchResults([]); setSearchedQ(''); return; }
    setSearching(true);
    const { data, error } = await window.DB.searchProfiles(q);
    setSearching(false);
    if (error) {
      showVitalToast(lang === 'zh' ? `搜尋失敗: ${error.message}` : `Search failed: ${error.message}`);
      return;
    }
    setSearchResults(data || []);
    setSearchedQ(q);
  };

  const profileLabel = (p) => {
    const display = (p.display_name || '').trim();
    const en = (p.name_en || p.short_name_en || '').trim();
    const zh = (p.name_zh || p.short_name_zh || '').trim();
    const primary = display || (lang === 'zh' ? (zh || en) : (en || zh));
    return primary || (lang === 'zh' ? '未命名' : 'Unnamed');
  };

  // How this person is told apart from the next person with the same name.
  // ‼️ A LICENCE NUMBER IS THE IDENTITY IN THIS APP; a uuid is a database
  // detail. The roster that shipped led with the uuid and dropped the licence
  // line whenever license_tail was null, so two nurses both called CHONG JOHN
  // were distinguishable only by a 36-character string — while the field that
  // actually identifies a nurse silently disappeared. Say so instead.
  const identityLine = (p) => (p.license_tail
    ? (lang === 'zh' ? `執照尾碼 ${p.license_tail}` : `Licence ending ${p.license_tail}`)
    : (lang === 'zh' ? '未提供執照號碼' : 'No licence number on file'));

  // ‼️ SANITIZED ENGLISH + A SQLSTATE IS NOT AN ERROR MESSAGE. The Edge
  // Function returns a deliberately coarse public string and db.js appends the
  // code (that inline code is load-bearing — see adminCallEdge; do NOT strip
  // it). What was missing is the half an admin can act on, so map the four
  // codes this endpoint actually raises and keep the raw text after them.
  const roleErrorText = (error) => {
    const zh = lang === 'zh';
    const by = {
      42501: zh ? '權限不足 —— 你不能修改自己的權限，或你已不再是管理員。'
                : 'Not permitted — you cannot change your own role, and you may no longer be an admin.',
      P0002: zh ? '找不到這個使用者的個人檔案。' : 'No profile found for that user.',
      P0001: zh ? '不能移除最後一位管理員。' : 'The last remaining admin cannot be removed.',
      22023: zh ? '要求無效（角色或使用者不正確）。' : 'Invalid request (bad role or user).',
    }[error?.code];
    const raw = error?.message || String(error);
    return by ? `${by}（${raw}）` : raw;
  };

  // ‼️ ASKING AND DOING ARE NOW TWO FUNCTIONS. This one is synchronous and does
  // nothing but open the dialog — no await, no RPC, no blocking call. That is
  // the whole fix for the frozen tap: whatever WebKit does with native panels,
  // a setState always renders.
  const askRole = (userId, role, person) => {
    if (busyId || inFlight.current) return;
    setConfirmTarget({ userId, role, person });
  };

  const cancelRole = () => {
    if (inFlight.current) return;   // never strand a call that is already out
    setConfirmTarget(null);
  };

  const commitRole = async () => {
    const target = confirmTarget;
    // ‼️ THE LATCH IS CHECKED AND SET IN THE SAME TICK. `busyId` cannot do this:
    // a second tap in the same tick sees the pre-render value and fires again.
    if (!target || inFlight.current) return;
    inFlight.current = true;
    setBusyId(target.userId);
    try {
      const { error } = await window.DB.setProfileRole(target.userId, target.role);
      if (error) {
        showVitalToast(lang === 'zh' ? `失敗: ${roleErrorText(error)}` : `Failed: ${roleErrorText(error)}`, 'warning');
        return;
      }
      showVitalToast(
        target.role
          ? (lang === 'zh' ? '已新增管理員' : 'Admin added')
          : (lang === 'zh' ? '已移除管理員' : 'Admin removed')
      );
      await loadAdmins();
      // Refresh the search row in place so its "Make admin" / "Remove" state
      // matches the new role without forcing the admin to re-search.
      setSearchResults(prev => prev.map(p =>
        p.id === target.userId ? { ...p, role: target.role || 'user' } : p));
    } finally {
      // ‼️ FINALLY, NOT THE HAPPY PATH. A thrown fetch used to leave `busyId`
      // set forever — every control on the panel disabled, and the only way out
      // a reload. The dialog and the busy flag come down on every exit.
      inFlight.current = false;
      setBusyId(null);
      setConfirmTarget(null);
    }
  };


  const cardStyle = {
    padding: 12, borderRadius: 10,
    border: '0.5px solid var(--border)', background: 'var(--surface)',
    display: 'flex', alignItems: 'center', gap: 10,
  };
  const idStyle = {
    fontSize: 10.5, color: 'var(--fg-3)', fontFamily: 'JetBrains Mono, monospace',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  };
  // ‼️ 44px. Measured at 375px, the two privileged actions on this screen —
  // grant admin and remove admin — were the SMALLEST tap targets in it (29px),
  // and 移除 sits under the thumb. Apple's minimum is 44pt; the visual size
  // stays small, the target does not.
  const actionStyle = (busy, tone) => ({
    minHeight: 44, padding: '6px 12px', borderRadius: 8, fontSize: 12,
    fontWeight: 500, border: 0, flexShrink: 0,
    background: tone === 'danger' ? 'color-mix(in oklch, var(--danger) 12%, transparent)' : 'var(--primary)',
    color: tone === 'danger' ? 'var(--danger)' : '#fff',
    cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1,
  });
  // The non-control that replaces a control which could not have worked.
  const noteStyle = {
    fontSize: 11.5, color: 'var(--fg-3)', flexShrink: 0,
    maxWidth: 108, textAlign: 'right', lineHeight: 1.35,
  };

  // The identity block every row shares. Name and licence lead; the uuid is
  // last and smallest, because it is the only line here that identifies nobody.
  const identityBlock = (p, showBadge) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13.5, fontWeight: 500 }}>
        {profileLabel(p)}
        {showBadge && (
          <span style={{
            marginLeft: 8, fontSize: 10.5, padding: '1px 6px',
            borderRadius: 999, background: 'var(--primary-soft-2)',
            color: 'var(--primary)', verticalAlign: 'middle',
          }}>{p.role}</span>
        )}
      </div>
      <div className="muted" style={{ fontSize: 11.5, marginTop: 1 }}>{identityLine(p)}</div>
      <div style={idStyle}>{p.id}</div>
    </div>
  );

  return (
    <div style={{ padding: '4px 20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Current admins */}
      <section>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>
            {lang === 'zh' ? '目前的管理員' : 'Current admins'}
          </h3>
          <button type="button" className="btn btn-ghost"
            onClick={loadAdmins}
            style={{ fontSize: 12, padding: '4px 10px', minHeight: 44 }}>
            {lang === 'zh' ? '重新整理' : 'Refresh'}
          </button>
        </div>
        {adminsLoading && (
          <div className="muted" style={{ fontSize: 12.5 }}>
            {lang === 'zh' ? '載入中…' : 'Loading…'}
          </div>
        )}
        {adminsErr && (
          <div style={{ color: 'var(--danger)', fontSize: 12.5 }}>{adminsErr}</div>
        )}
        {!adminsLoading && !adminsErr && admins.length === 0 && (
          <div className="muted" style={{ fontSize: 12.5 }}>
            {lang === 'zh' ? '沒有管理員。' : 'No admins yet.'}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {admins.map(p => (
            <div key={p.id} style={cardStyle}>
              {identityBlock(p, true)}
              {/* ‼️ NO CONTROL WHOSE HANDLER CANNOT SUCCEED. admin_set_role
                  raises 42501 on self-modification, so 移除 on your own row was
                  a confirm dialog followed by a guaranteed failure toast. The
                  rule that produced this is the same one §53 pinned on Home. */}
              {p.id === meId ? (
                <span style={noteStyle}>
                  {lang === 'zh' ? '你自己 · 需由另一位管理員移除' : 'You · another admin must remove you'}
                </span>
              ) : (
                <button type="button"
                  disabled={busyId === p.id}
                  onClick={() => askRole(p.id, null, p)}
                  style={actionStyle(busyId === p.id, 'danger')}>
                  {lang === 'zh' ? '移除' : 'Remove'}
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Add admin */}
      <section>
        <h3 style={{ margin: '0 0 8px 0', fontSize: 14 }}>
          {lang === 'zh' ? '新增管理員' : 'Add an admin'}
        </h3>
        <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
          {lang === 'zh'
            ? '以姓名、執照號碼或使用者 ID 搜尋,再按「設為管理員」。'
            : 'Search by name, licence number, or user ID, then promote.'}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
            placeholder={lang === 'zh' ? '姓名、執照號碼或使用者 ID' : 'Name, licence number, or user ID'}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            // ‼️ NO INLINE fontSize. styles.css already carries an iOS auto-zoom
            // floor — `input, textarea, select { font-size: max(16px, 1em) }` —
            // and an inline style silently outranks it. This box declared 14px,
            // so tapping it zoomed the whole admin screen on a real iPhone and
            // re-clipped the right edge. Guarded by `npm run test:ios-zoom`.
            style={{
              flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: 8,
              border: '0.5px solid var(--border)', background: 'var(--surface)',
              color: 'var(--fg-1)',
            }}
          />
          <button type="button"
            onClick={runSearch}
            className="btn btn-primary"
            disabled={searching || !searchQ.trim()}
            style={{ padding: '8px 14px', minHeight: 44, flexShrink: 0 }}>
            {searching
              ? (lang === 'zh' ? '搜尋中…' : 'Searching…')
              : (lang === 'zh' ? '搜尋' : 'Search')}
          </button>
        </div>
        {/* ‼️ RESULTS BELONG TO THE QUERY THAT PRODUCED THEM. Edit the box and
            the old rows used to stay on screen with live 設為管理員 buttons, so
            an admin could grant privilege from a result set that no longer
            matched anything they could see. Say what is showing, or show
            nothing. */}
        {searchResults.length > 0 && dirtyQuery && (
          <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
            {lang === 'zh'
              ? `以下是「${searchedQ}」的搜尋結果 —— 按「搜尋」以更新。`
              : `Showing results for “${searchedQ}” — press Search to update.`}
          </div>
        )}
        {searchResults.length > 0 && (
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10,
            opacity: dirtyQuery ? 0.5 : 1,
          }}>
            {searchResults.map(p => {
              // ‼️ A MODERATOR IS NOT AN ADMIN. The old predicate collapsed both
              // into 「已是管理員」, which was untrue for a moderator AND blocked
              // the one path admin_set_role supports for them (ON CONFLICT DO
              // UPDATE re-roles an existing row) — so a moderator could never be
              // promoted from this screen at all.
              const isMe = p.id === meId;
              const isAdmin = p.role === 'admin';
              const isModerator = p.role === 'moderator';
              return (
                <div key={p.id} style={cardStyle}>
                  {identityBlock(p, isAdmin || isModerator)}
                  {isMe ? (
                    <span style={noteStyle}>{lang === 'zh' ? '你自己' : 'You'}</span>
                  ) : isAdmin ? (
                    <span style={noteStyle}>{lang === 'zh' ? '已是管理員' : 'Already an admin'}</span>
                  ) : (
                    <button type="button"
                      disabled={busyId === p.id || dirtyQuery}
                      onClick={() => askRole(p.id, 'admin', p)}
                      style={{
                        ...actionStyle(busyId === p.id, 'primary'),
                        opacity: dirtyQuery ? 0.5 : (busyId === p.id ? 0.6 : 1),
                      }}>
                      {isModerator
                        ? (lang === 'zh' ? '提升為管理員' : 'Promote to admin')
                        : (lang === 'zh' ? '設為管理員' : 'Make admin')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {/* ‼️ `searchedQ`, NOT `searchQ`. Keyed to the typed query, this told the
            admin the nurse did not exist on her first keystroke — before the
            panel had looked her up even once. */}
        {!searching && searchedQ && searchResults.length === 0 && (
          <div className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
            {lang === 'zh'
              ? `找不到符合「${searchedQ}」的使用者。試試執照號碼或完整的使用者 ID。`
              : `No user matches “${searchedQ}”. Try a licence number or the full user ID.`}
          </div>
        )}
      </section>

      <AdminRoleConfirmDialog
        target={confirmTarget}
        lang={lang}
        busy={busyId != null}
        label={confirmTarget?.person ? profileLabel(confirmTarget.person) : ''}
        identity={confirmTarget?.person ? identityLine(confirmTarget.person) : ''}
        onCancel={cancelRole}
        onConfirm={commitRole}
      />
    </div>
  );
}

// The confirmation for granting or removing administrator access.
//
// ‼️ `position: fixed` VIA .register-dialog-backdrop, DELIBERATELY. This renders
// from inside AdminManagementPanel, which lives inside `.scroll` — a container
// with `overflow-x: hidden` and its own stacking context. An absolutely
// positioned dialog there would scroll away with the list and be clipped by it;
// the app's existing centred-dialog classes are viewport-anchored, so the
// dialog is where the admin is looking no matter how far down the roster they
// have scrolled.
function AdminRoleConfirmDialog({ target, lang, busy, label, identity, onCancel, onConfirm }) {
  const zh = lang === 'zh';
  const granting = !!target?.role;

  // Escape cancels — but never while a call is in flight, or the admin would
  // dismiss a dialog whose write is already on the wire.
  React.useEffect(() => {
    if (!target) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [target, busy, onCancel]);

  if (!target) return null;

  const action = {
    minHeight: 44, padding: '10px 18px', borderRadius: 10,
    fontSize: 14, fontWeight: 500, border: 0, cursor: busy ? 'wait' : 'pointer',
  };
  return (
    <div className="register-dialog-backdrop"
      onClick={() => { if (!busy) onCancel(); }}>
      <div className="register-dialog" role="dialog" aria-modal="true"
        style={{ maxWidth: 380, padding: '22px 20px 18px' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="h-section" style={{ marginBottom: 10 }}>
          {granting
            ? (zh ? '授予管理員權限？' : 'Grant administrator access?')
            : (zh ? '移除管理員權限？' : 'Remove administrator access?')}
        </div>

        {/* Who — the whole reason this dialog exists rather than a bare yes/no. */}
        <div style={{
          padding: 10, borderRadius: 10, background: 'var(--surface)',
          border: '0.5px solid var(--border)', marginBottom: 12,
        }}>
          <div style={{ fontSize: 14.5, fontWeight: 500 }}>{label}</div>
          <div className="muted" style={{ fontSize: 12 }}>{identity}</div>
        </div>

        <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5, marginBottom: 16 }}>
          {granting
            ? (zh ? '管理員可以審核提交、發佈公告，並管理其他管理員。'
                  : 'Admins can review submissions, publish notices, and manage other admins.')
            : (zh ? '這位使用者將立即失去所有管理員權限。'
                  : 'This user immediately loses all administrator access.')}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel} disabled={busy}
            style={{ ...action, background: 'var(--surface)', color: 'var(--fg-1)',
                     border: '0.5px solid var(--border)', opacity: busy ? 0.5 : 1 }}>
            {zh ? '取消' : 'Cancel'}
          </button>
          {/* ‼️ `disabled={busy}` is the VISIBLE half of the double-submit guard;
              the ref latch in commitRole is the half that actually holds. */}
          <button type="button" onClick={onConfirm} disabled={busy}
            style={{ ...action,
                     background: granting ? 'var(--primary)' : 'var(--danger)',
                     color: '#fff', opacity: busy ? 0.6 : 1 }}>
            {busy
              ? (zh ? '處理中…' : 'Working…')
              : granting ? (zh ? '設為管理員' : 'Make admin') : (zh ? '移除' : 'Remove')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Reusable list of evidence file links. Image files render as inline
// thumbnails so the admin can see the proof without leaving the queue;
// tapping a thumbnail opens a fullscreen lightbox. Non-image files keep
// the open-in-new-tab behavior. Signed URLs are short-lived (10 min),
// so a stolen DOM snapshot can't leak access beyond that window.
function EvidenceList({ paths, purgedAt, purgedFileCount, lang }) {
  const [urls, setUrls] = React.useState({}); // path → signedUrl
  const [lightbox, setLightbox] = React.useState(null); // signed url currently open in fullscreen

  React.useEffect(() => {
    if (!paths || paths.length === 0) return;
    let cancelled = false;
    (async () => {
      const next = {};
      for (const p of paths) {
        const u = await window.DB.getSubmissionEvidenceUrl(p);
        if (cancelled) return;
        if (u) next[p] = u;
      }
      if (!cancelled) setUrls(next);
    })();
    return () => { cancelled = true; };
  }, [paths && paths.join('|')]);

  // ‼️ #52: with no files, say only what the system KNOWS. An empty
  // evidence_urls is storage state; the retention purge empties it on rows that
  // DID have files, so 「無附件證據」 here was a claim about the nurse that
  // storage cannot support. evidenceDisplayState() (data.js) decides from the
  // purge stamp and per-row count alone. Display only: no action, ever.
  const shown = evidenceDisplayState({
    evidence_urls: paths, evidence_purged_at: purgedAt, evidence_purged_file_count: purgedFileCount,
  });
  if (shown.kind !== 'files') {
    const when = shown.purgedOn ? fmtDate(shown.purgedOn, lang) : '';
    const text = shown.kind === 'removed'
      ? (lang === 'zh' ? `證據已按保留政策刪除 · ${when}` : `Evidence removed under the retention policy · ${when}`)
      : shown.kind === 'neutral'
        ? (lang === 'zh' ? `目前沒有存檔證據 · 保留期清理於 ${when}` : `No evidence on file · retention cleanup ran ${when}`)
        : (lang === 'zh' ? '目前沒有附件證據' : 'No evidence currently attached');
    return (
      <div className="muted-2" data-evidence-state={shown.kind} style={{ fontSize: 11.5, fontStyle: 'italic' }}>
        {text}
      </div>
    );
  }

  const isImage = (p) => /\.(jpe?g|png|webp|gif|heic|heif|bmp)$/i.test(p);

  const openFile = async (path) => {
    const url = urls[path] || await window.DB.getSubmissionEvidenceUrl(path);
    if (!url) {
      showVitalToast(lang === 'zh' ? '無法產生 URL' : 'Could not generate URL');
      return;
    }
    try { window.open(url, '_blank', 'noopener,noreferrer'); }
    catch (_) { window.location.href = url; }
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {paths.some(isImage) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {paths.filter(isImage).map((p, i) => {
              const url = urls[p];
              const name = p.split('/').slice(-1)[0];
              return (
                <button key={i} type="button"
                  onClick={() => url && setLightbox(url)}
                  title={name}
                  style={{
                    width: 88, height: 88, padding: 0, borderRadius: 8,
                    border: '0.5px solid var(--border)', background: 'var(--surface)',
                    overflow: 'hidden', cursor: url ? 'zoom-in' : 'default',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  {url ? (
                    <img src={url} alt={name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <Icon name="upload" size={16} stroke={1.7} color="var(--fg-3)" />
                  )}
                </button>
              );
            })}
          </div>
        )}
        {paths.filter(p => !isImage(p)).map((p, i) => (
          <button key={i} type="button"
            onClick={() => openFile(p)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
              padding: '6px 8px', borderRadius: 6,
              border: '0.5px solid var(--border)', background: 'var(--surface)',
              cursor: 'pointer', textAlign: 'left',
            }}>
            <Icon name="upload" size={12} stroke={1.7} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p.split('/').slice(-1)[0]}
            </span>
            <Icon name="chev-r" size={11} stroke={1.7} color="var(--fg-3)" />
          </button>
        ))}
      </div>

      {lightbox && (
        <div role="dialog" aria-modal="true"
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0, 0, 0, 0.88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20, cursor: 'zoom-out',
          }}>
          <img src={lightbox} alt=""
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
              borderRadius: 6, cursor: 'default',
            }} />
          <button type="button"
            onClick={() => setLightbox(null)}
            aria-label={lang === 'zh' ? '關閉' : 'Close'}
            style={{
              position: 'absolute', top: 16, right: 16,
              width: 36, height: 36, borderRadius: 18,
              background: 'rgba(255,255,255,0.15)', border: 0,
              color: '#fff', fontSize: 18, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>×</button>
        </div>
      )}
    </>
  );
}

// Inline summary of a contributor's track record. Shown on every admin
// card under the submitter/reporter id so the admin sees signal quality
// without leaving the queue. Tappable — opens the full history dialog.
//
// kind = 'submissions' for the submissions tab, 'reports' for the reports/
// apply tabs (each tab cares about the relevant counter).
function ContributorStatLine({ stats, kind, lang, onOpenHistory }) {
  if (!stats) return null;
  const s = kind === 'submissions' ? stats.submissions : stats.reports;
  // First-time contributors deserve a distinct label rather than "0 / 0".
  // The current pending row is included in the total, so the floor for
  // "any prior history at all" is total > 1 for submissions, and a
  // matching test for reports.
  const isFirstTime = (s.total <= 1);
  const labels = kind === 'submissions'
    ? {
        submitted: lang === 'zh' ? '提交' : 'submitted',
        approved:  lang === 'zh' ? '通過' : 'approved',
        rejected:  lang === 'zh' ? '拒絕' : 'rejected',
      }
    : {
        submitted: lang === 'zh' ? '回報' : 'reported',
        approved:  lang === 'zh' ? '接受' : 'accepted',
        rejected:  lang === 'zh' ? '拒絕' : 'rejected',
      };
  const approvedCount = kind === 'submissions' ? s.approved : s.accepted;
  const rejectedCount = s.rejected;
  // Approval rate is computed over completed (approved + rejected),
  // excluding pending. Skip the badge when nothing's been reviewed yet.
  const reviewed = approvedCount + rejectedCount;
  const ratePct = reviewed > 0 ? Math.round((approvedCount / reviewed) * 100) : null;

  return (
    <button type="button" onClick={onOpenHistory}
      disabled={!onOpenHistory}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0',
        background: 'transparent', border: 0, cursor: onOpenHistory ? 'pointer' : 'default',
        fontSize: 11.5, color: 'var(--fg-2)', textAlign: 'left',
      }}
      title={onOpenHistory ? (lang === 'zh' ? '查看完整紀錄' : 'View full history') : undefined}>
      {isFirstTime ? (
        <span style={{ color: 'var(--accent)' }}>
          <Icon name="plus" size={10} stroke={1.8} />
          {' '}
          {lang === 'zh' ? '首次貢獻' : 'First-time contributor'}
        </span>
      ) : (
        <>
          <span>{lang === 'zh' ? '過往: ' : 'Past: '}</span>
          <span>{s.total} {labels.submitted}</span>
          <span>·</span>
          <span style={{ color: 'var(--success)' }}>{approvedCount} {labels.approved}</span>
          {rejectedCount > 0 && (
            <>
              <span>·</span>
              <span style={{ color: 'var(--danger)' }}>{rejectedCount} {labels.rejected}</span>
            </>
          )}
          {ratePct !== null && (
            <span style={{
              marginLeft: 4, padding: '1px 6px', borderRadius: 999, fontSize: 10.5,
              background: ratePct >= 80 ? 'color-mix(in oklch, var(--success) 14%, transparent)'
                        : ratePct >= 50 ? 'color-mix(in oklch, var(--accent) 14%, transparent)'
                        : 'color-mix(in oklch, var(--danger) 14%, transparent)',
              color:      ratePct >= 80 ? 'var(--success)'
                        : ratePct >= 50 ? 'var(--accent)'
                        : 'var(--danger)',
            }}>{ratePct}%</span>
          )}
        </>
      )}
      {onOpenHistory && <Icon name="chev-r" size={10} stroke={1.7} color="var(--fg-3)" />}
    </button>
  );
}

// Admin · one feedback row in the inbox. The user's comment plus who / when /
// what device, a status control so a reviewer can triage new → reviewing →
// resolved | archived (and back), and an in-app REPLY the author reads in
// 我的貢獻. Status writes go through updateFeedbackStatus; the reply goes
// through adminReplyToFeedback (reply-feedback Edge Function → notifies the
// author). Both are RLS/role-gated. The mailto affordance stays as a private
// side channel when the user left a contact email.
// ─────────────────────────────────────────────────────────────────────────────
// AdminReviewCard — one pending course review awaiting publish/reject.
//
// ‼️ PRE-MODERATION, SO THIS QUEUE IS THE GATE. Nothing here has ever been seen
// by another nurse: a review sits at status='pending' from submission, counts
// toward no average, and only 'publish' makes it public. That is deliberate for
// a single-operator product — post-moderation would leave defamatory text about
// a named Hong Kong provider live overnight.
//
// ‼️ NO AUTHOR IS SHOWN, because admin_list_pending_reviews does not return one.
// Judging whether a review is publishable never requires knowing which nurse
// wrote it, and not having the id here means this screen cannot become the
// place where "who criticised my employer" is looked up.
//
// A rejection note is worth writing: it is surfaced to the author in
// Profile → 我的評價, so a refusal is explained rather than silent.
function AdminReviewCard({ item, lang, notes, setNotes, isBusy, anyBusy, onPublish, onReject, onSuspend }) {
  const chipLabel = (key) => {
    const all = (window.REVIEW_COURSE_CHIPS || []).concat(window.REVIEW_PROVIDER_CHIPS || []);
    const found = all.find(c => c.key === key);
    return found ? (lang === 'zh' ? found.zh : found.en) : key;
  };
  const chips = (item.course_chips || []).concat(item.provider_chips || []);
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 }}>
          {item.course_title || item.course_id}
        </div>
        <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 3,
                       color: 'var(--warning)' }}>
          <Icon name="star" size={13} stroke={1.8} color="currentColor" fill="currentColor" />
          <span style={{ fontSize: 13, fontWeight: 600 }}>{item.course_stars}</span>
        </span>
      </div>

      <div className="muted-2" style={{ fontSize: 11, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {item.provider_code && <span className="mono">{item.provider_code}</span>}
        {item.provider_stars != null && (
          <span>{lang === 'zh' ? `機構 ${item.provider_stars}★` : `Provider ${item.provider_stars}★`}</span>
        )}
        <span>{fmtShort(String(item.created_at).slice(0, 10), lang, true)}</span>
      </div>

      {chips.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {chips.map(k => <span key={k} className="chip">{chipLabel(k)}</span>)}
        </div>
      )}

      {/* The only part that needs judgement. Star-only rows are here too — they
          are published with one tap and carry nothing to read. */}
      {item.body ? (
        <div style={{ fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap',
                      padding: '10px 12px', borderRadius: 8, background: 'var(--bg-2)' }}>
          {item.body}
        </div>
      ) : (
        <div className="muted-2" style={{ fontSize: 11.5, fontStyle: 'italic' }}>
          {lang === 'zh' ? '(只有評分,沒有文字)' : '(rating only, no text)'}
        </div>
      )}

      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={lang === 'zh' ? '拒絕原因(會顯示畀作者)' : 'Reason (shown to the author on reject)'}
        rows={2}
        disabled={anyBusy}
        style={{ width: '100%', padding: 8, borderRadius: 8,
                 border: '1px solid var(--border)', resize: 'vertical', font: 'inherit' }} />

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" style={{ flex: 1 }}
          disabled={anyBusy} onClick={onPublish}>
          {isBusy ? '…' : (lang === 'zh' ? '公開' : 'Publish')}
        </button>
        <button className="btn btn-ghost" style={{ flex: 1 }}
          disabled={anyBusy} onClick={onReject}>
          {lang === 'zh' ? '拒絕' : 'Reject'}
        </button>
      </div>
      {/* Apple Guideline 1.2 requires a way to stop an abusive contributor.
          Addressed by REVIEW id — this screen never holds an author identity,
          which is also why it cannot show you who you are suspending. Scope is
          narrow: no new comments, published ones hidden. Her CNE records,
          points and ratings are untouched. */}
      {onSuspend && (
        <button type="button" className="btn btn-ghost"
          disabled={anyBusy} onClick={onSuspend}
          style={{ color: 'var(--danger)', fontSize: 12 }}>
          {lang === 'zh' ? '停用此作者的評語功能' : 'Suspend this author from commenting'}
        </button>
      )}
    </div>
  );
}

function AdminFeedbackCard({ item, lang, authorName, isBusy, anyBusy, onSetStatus, onReply }) {
  // Local draft, seeded from any existing reply so a reviewer edits in place.
  // The card is keyed by item.id in the list, so this re-seeds per row.
  const [reply, setReply] = React.useState(item.admin_reply || '');
  const catMap = {
    suggestion: { tone: 'success', en: 'Suggestion', zh: '建議' },
    bug:        { tone: 'danger',  en: 'Bug',        zh: '問題' },
    other:      { tone: 'muted',   en: 'Other',      zh: '其他' },
  };
  const statusLabels = {
    new:       { en: 'New',       zh: '新' },
    reviewing: { en: 'Reviewing', zh: '處理中' },
    resolved:  { en: 'Resolved',  zh: '已解決' },
    archived:  { en: 'Archived',  zh: '已封存' },
  };
  const cat = catMap[item.category] || catMap.other;
  const when = item.created_at ? fmtShort(String(item.created_at).slice(0, 10), lang) : '';
  const email = (item.contact_email || '').trim();
  // display name if we resolved it, else a short anonymised id so the reviewer
  // can still tell repeat submitters apart without exposing the full uuid.
  const author = authorName
    || (item.user_id ? item.user_id.slice(0, 8) + '…' : (lang === 'zh' ? '匿名' : 'anonymous'));
  const device = [item.platform, item.app_version && `v${item.app_version}`, item.locale]
    .filter(Boolean).join(' · ');

  const repliedWhen = item.replied_at ? fmtShort(String(item.replied_at).slice(0, 10), lang) : '';
  const replyTrimmed = reply.trim();
  const replyUnchanged = replyTrimmed === (item.admin_reply || '').trim();
  // Sending is blocked while another action runs, when the box is empty, or
  // when the text matches the reply already on file (nothing to re-send).
  const canSend = !anyBusy && replyTrimmed !== '' && !replyUnchanged;

  return (
    <div className="card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10, opacity: isBusy ? 0.6 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span className={'cap-pill tone-' + cat.tone}>{lang === 'zh' ? cat.zh : cat.en}</span>
        <span className="muted-2" style={{ fontSize: 11, marginLeft: 'auto' }}>{when}</span>
      </div>

      {/* the comment itself — preserve newlines and break long unbroken runs */}
      <div style={{ fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {item.message}
      </div>

      <div className="muted-2" style={{ fontSize: 11, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div>
          {lang === 'zh' ? '來自' : 'From'}:{' '}
          <span className={authorName ? undefined : 'mono'}>{author}</span>
          {email && (
            <>
              {' · '}
              <a href={`mailto:${email}?subject=${encodeURIComponent(lang === 'zh' ? 'Vital 意見回覆' : 'Re: your Vital feedback')}`}
                 style={{ color: 'var(--primary)' }}>{email}</a>
            </>
          )}
        </div>
        {device && <div>{device}</div>}
      </div>

      {/* triage — the highlighted chip is the current status; tap another to
          move it there. Disabled while any row action is in flight. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="muted-2" style={{ fontSize: 10.5, letterSpacing: '0.04em', textTransform: 'uppercase', marginRight: 2 }}>
          {lang === 'zh' ? '狀態' : 'Status'}
        </span>
        {['new', 'reviewing', 'resolved', 'archived'].map(s => {
          const active = item.status === s;
          return (
            <button key={s}
              className={'filter-chip' + (active ? ' on' : '')}
              style={{ padding: '5px 11px', fontSize: 12 }}
              disabled={anyBusy || active}
              onClick={() => onSetStatus(s)}>
              {lang === 'zh' ? statusLabels[s].zh : statusLabels[s].en}
            </button>
          );
        })}
      </div>

      {/* In-app reply — the author reads this in 我的貢獻, and it pushes a
          notification. Sending marks the item resolved (the common case);
          a reviewer who wants to reply while keeping it open can still flip
          the status chip back to 處理中 afterwards. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, borderTop: '0.5px solid var(--border)', paddingTop: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="muted-2" style={{ fontSize: 10.5, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {lang === 'zh' ? '回覆用戶' : 'Reply to user'}
          </span>
          {repliedWhen && (
            <span className="cap-pill tone-success" style={{ fontSize: 10, marginLeft: 'auto' }}>
              {(lang === 'zh' ? '已回覆 · ' : 'Replied · ') + repliedWhen}
            </span>
          )}
        </div>
        <textarea className="field-input" rows={2}
          placeholder={lang === 'zh' ? '寫下給用戶的回覆…' : 'Write a reply the user will see…'}
          value={reply} onChange={e => setReply(e.target.value)} disabled={anyBusy} />
        <button className="btn btn-primary" style={{ alignSelf: 'flex-start', padding: '6px 14px', fontSize: 13 }}
          disabled={!canSend} onClick={() => onReply(replyTrimmed, 'resolved')}>
          <Icon name="check" size={13} stroke={2} />
          {isBusy ? (lang === 'zh' ? '傳送中…' : 'Sending…')
            : item.admin_reply ? (lang === 'zh' ? '更新回覆' : 'Update reply')
              : (lang === 'zh' ? '傳送回覆並解決' : 'Send reply & resolve')}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// IDENTITY DISPOSITION — the admin's half of §76
// ═══════════════════════════════════════════════════════════════════════════
// ‼️ WHY A PICKER EXISTS AT ALL. §76 (identity at birth) forbids the machine
// from asserting a provider/series/offering identity it cannot prove: a
// canonical `entity_aliases` contract may resolve, proof-of-emptiness may mint,
// and NOTHING ELSE — a community approval that matches neither raises
// `identity_disposition_required` and rolls the whole approval back. That rule
// shipped without this panel, so from 2026-08-20 every 通過 tap on a community
// submission failed, surfacing as 「失敗: Submission not found (P0002)」 because
// the resolver's refusal and "no such row" share a SQLSTATE.
//
// ‼️ THE DEFAULT IS 'auto', AND 'auto' MEANS "SEND NOTHING". It does not mean
// "attach to the closest thing" — a default here would reintroduce exactly the
// silent machine assertion §76 removed. When the contract layer can answer, the
// admin never sees this panel open; when it cannot, the server refuses and the
// human answers. Absence of evidence is not evidence of novelty.
//
// ‼️ THIS FUNCTION MIRRORS `resolve_course_identity` AND MUST STAY IN STEP WITH
// IT (supabase-migration-identity-at-birth.sql §6). It exists only to tell the
// reviewer which questions are still open BEFORE the round trip — the server
// remains the sole authority. When they disagree the server wins and the panel
// shows its `detail`; a drift is a UX bug, never a correctness one.
function identityPlan({ disposition, contractHits, title, anchorOk }) {
  const kind = (level) => disposition?.[level]?.kind || 'auto';
  const unblocked = Array.isArray(contractHits)
    ? contractHits.filter(h => !h.blocked)
    : null;

  const out = { provider: {}, series: {}, offering: {} };

  // ── provider ──
  const pk = kind('provider');
  if (pk === 'attach') {
    out.provider = { state: disposition.provider.entity ? 'resolved' : 'incomplete', entity: disposition.provider.entity };
  } else if (pk === 'new') {
    out.provider = { state: 'resolved', minted: true };
  } else if (pk === 'withhold') {
    out.provider = { state: 'withheld' };
  } else if (unblocked === null) {
    out.provider = { state: 'unknown' };            // candidates not loaded yet
  } else if (unblocked.length === 1) {
    out.provider = { state: 'resolved', entity: unblocked[0].entity_id, byContract: true };
  } else {
    out.provider = { state: 'needs', hits: unblocked.length };
  }

  // ── series ── a non-resolved parent forces the same answer downward, so no
  // further question is asked; that is the SQL's behaviour, not a shortcut.
  const sk = kind('series');
  if (out.provider.state !== 'resolved') {
    out.series = { state: out.provider.state === 'unknown' ? 'unknown' : 'inherited' };
  } else if (sk === 'attach') {
    out.series = { state: disposition.series.entity ? 'resolved' : 'incomplete', entity: disposition.series.entity };
  } else if (sk === 'new') {
    out.series = title ? { state: 'resolved', minted: true } : { state: 'unmintable', why: 'title' };
  } else if (sk === 'withhold') {
    out.series = { state: 'withheld' };
  } else if (out.provider.minted) {
    // AUTO-NEW by proof of emptiness: the provider is being created in this very
    // transaction, so it provably holds no series yet. The only case where the
    // machine may assert novelty.
    out.series = title ? { state: 'resolved', minted: true, byProof: true } : { state: 'inherited' };
  } else {
    out.series = { state: 'needs' };
  }

  // ── offering ──
  const ok = kind('offering');
  if (out.series.state !== 'resolved') {
    out.offering = { state: out.series.state === 'unknown' ? 'unknown' : 'inherited' };
  } else if (ok === 'attach') {
    out.offering = { state: disposition.offering.entity ? 'resolved' : 'incomplete', entity: disposition.offering.entity };
  } else if (ok === 'new') {
    out.offering = anchorOk ? { state: 'resolved', minted: true } : { state: 'unmintable', why: 'anchor' };
  } else if (ok === 'withhold') {
    out.offering = { state: 'withheld' };
  } else if (out.series.minted) {
    out.offering = anchorOk ? { state: 'resolved', minted: true, byProof: true } : { state: 'inherited' };
  } else {
    out.offering = { state: 'needs' };
  }

  out.blocked = ['provider', 'series', 'offering']
    .some(l => out[l].state === 'needs' || out[l].state === 'incomplete' || out[l].state === 'unmintable');
  return out;
}

// Whether an occurrence has date material to be denoted by — i.e. whether a NEW
// offering verdict is mintable at all.
//
// ‼️ MIRRORS THE APPROVAL PATH, NOT `occurrence_anchor` ALONE. That function
// returns NULL when start_date contradicts the earliest session date, and a
// first reading of it here got this wrong: on THIS path the contradiction
// cannot arise, because approve_submission_reviewed_core derives start_date
// FROM sessions_list whenever that array is non-empty and only falls back to the
// submitted start_date when it is absent. The two can therefore never disagree
// by the time the resolver sees them. (The contradiction guard still matters —
// it protects the unattended official import, which has no such derivation.)
// Caught by npm run test:identity-disposition, which runs this function and the
// real engine over the same payloads.
function occurrenceAnchorOk(payload) {
  const sessions = Array.isArray(payload?.sessions_list) ? payload.sessions_list : [];
  const hasSessionDate = sessions
    .map(v => String(v || '').slice(0, 10))
    .some(v => /^\d{4}-\d{2}-\d{2}$/.test(v));
  if (hasSessionDate) return true;
  return !!payload?.start_date;
}

// ‼️ THE PICKER'S HAYSTACK IS BOTH LANGUAGES, ALWAYS — AND IT DOES NOT ASK WHAT
// LANGUAGE THE QUERY IS IN.
//
// This search used to read `label` (English) and `code` only, while
// identity-candidates served no Chinese name at all. Every one of the 105
// provider entities carries a display_name_zh, so the effect was a pure false
// negative: measured against production 2026-09-09, ZERO of the submissions
// naming their provider in Chinese could reach their entity through this box.
// Not "few" — none, because the only Chinese text in the request was the query
// and the only Chinese text in the answer was never sent. 12 of 81 submissions
// carry a Chinese provider string, and 10 of those have a matching entity —
//   6  found by pasting the whole provider string
//   4  found by a shorter fragment (payloads like 「養和醫療集團 (Hong Kong
//      Sanatorium & Hospital Medical Group)」 carry text around the org name;
//      substring search runs entity-contains-query, not the reverse)
//   2  have no entity at all (「護協」, 「名卓護綜合專科護理及顧問中心」)
// An admin typing 「基督教靈實協會」 got an empty list while eaa45d27 sat in the
// table. That is §83's "ZERO by Chinese" surviving in the picker half.
//
// ‼️ DO NOT branch on the query's script, and DO NOT pick the haystack from
// which payload field the text arrived in. The same measurement found the
// Chinese name in `provider_full_en` in 12 cases out of 12 — the submission's
// own language slots are wrong often enough that keying off them would feed
// that upstream error straight into identity resolution. One query, both names,
// every time.
//
// Substring + case-fold ONLY. No fuzzing, no normalisation, no ranking: this
// widens the candidate text, it does not become a matcher. A name that reaches
// the resolver is a uuid the admin clicked, never a string this scored.
function providerCandidateMatches(candidate, query) {
  const q = String(query == null ? '' : query).trim().toLowerCase();
  if (!q) return true;
  return [candidate?.label, candidate?.labelZh, candidate?.code]
    .some(v => String(v == null ? '' : v).toLowerCase().includes(q));
}

function IdentityDispositionPanel({ submissionId, payload, lang, disposition, setDisposition, serverDetail }) {
  const zh = lang === 'zh';
  const [providers, setProviders] = React.useState(null);
  const [contractHits, setContractHits] = React.useState(null);
  const [seriesList, setSeriesList] = React.useState(null);
  const [offeringList, setOfferingList] = React.useState(null);
  const [loadError, setLoadError] = React.useState(null);
  const [filter, setFilter] = React.useState('');

  const title = !!(payload?.title_en || payload?.title_zh);
  const anchorOk = occurrenceAnchorOk(payload);
  const plan = identityPlan({ disposition, contractHits, title, anchorOk });

  React.useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error } = await window.DB.adminIdentityCandidates({ level: 'provider', submissionId });
      if (!alive) return;
      if (error) { setLoadError(error.message); return; }
      setProviders(data?.candidates || []);
      setContractHits(data?.contractHits || []);
    })();
    return () => { alive = false; };
  }, [submissionId]);

  // The provider entity the series question hangs off: an explicit attach, or
  // the single unconflicted contract hit when the admin left provider on 自動.
  const resolvedProvider = plan.provider.state === 'resolved' && !plan.provider.minted
    ? plan.provider.entity : null;

  React.useEffect(() => {
    let alive = true;
    if (!resolvedProvider) { setSeriesList(null); return; }
    (async () => {
      const { data, error } = await window.DB.adminIdentityCandidates({
        level: 'series', providerEntityId: resolvedProvider,
      });
      if (!alive) return;
      if (error) { setLoadError(error.message); return; }
      setSeriesList(data?.candidates || []);
    })();
    return () => { alive = false; };
  }, [resolvedProvider]);

  const attachedSeries = disposition?.series?.kind === 'attach' ? disposition.series.entity : null;
  React.useEffect(() => {
    let alive = true;
    if (!attachedSeries) { setOfferingList(null); return; }
    (async () => {
      const { data, error } = await window.DB.adminIdentityCandidates({
        level: 'offering', seriesEntityId: attachedSeries,
      });
      if (!alive) return;
      if (error) { setLoadError(error.message); return; }
      setOfferingList(data?.candidates || []);
    })();
    return () => { alive = false; };
  }, [attachedSeries]);

  const setLevel = (level, patch) => setDisposition(prev => ({
    ...prev,
    [level]: { ...(prev?.[level] || {}), ...patch },
  }));

  const KINDS = [
    { id: 'auto',     zh: '自動',     en: 'Auto' },
    { id: 'attach',   zh: '沿用現有', en: 'Attach' },
    { id: 'new',      zh: '建立新的', en: 'New' },
    { id: 'withhold', zh: '暫不歸屬', en: 'Withhold' },
  ];

  const stateNote = (levelPlan, level) => {
    if (levelPlan.state === 'unknown') return zh ? '載入中…' : 'Loading…';
    if (levelPlan.state === 'needs') {
      return zh ? '需要你裁決 — 系統無法自行判斷' : 'Your verdict is required — the system cannot decide this';
    }
    if (levelPlan.state === 'incomplete') return zh ? '請選擇要沿用的項目' : 'Pick the entity to attach to';
    if (levelPlan.state === 'unmintable') {
      if (levelPlan.why === 'title') return zh ? '沒有課程名稱,無法建立新系列' : 'No title — a new series has nothing to identify it';
      return zh ? '沒有可用日期(或開始日期與堂次日期不符),無法建立新場次'
                : 'No usable date anchor (or start_date contradicts the first session)';
    }
    if (levelPlan.state === 'withheld') return zh ? '不歸屬 — 課程可通過,但暫時不能被 👍 或評價' : 'Withheld — the course is approved but cannot take a 👍 or a review yet';
    if (levelPlan.state === 'inherited') return zh ? '跟隨上一層' : 'Follows the level above';
    if (levelPlan.byContract) return zh ? '已由既有對照表自動對上' : 'Matched automatically by an existing alias contract';
    if (levelPlan.byProof) return zh ? '隨新建的上一層一併建立' : 'Minted alongside the new parent above';
    if (levelPlan.minted) return zh ? '會建立一個新的' : 'A new one will be created';
    return zh ? '已選定' : 'Set';
  };

  const tone = (levelPlan) =>
    ['needs', 'incomplete', 'unmintable'].includes(levelPlan.state) ? 'var(--danger)' : 'var(--fg-2)';

  const list = (level, items, labelOf) => (
    <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
      {(items || []).length === 0 && (
        <div className="muted" style={{ fontSize: 11.5, padding: 8 }}>
          {items === null ? (zh ? '載入中…' : 'Loading…') : (zh ? '沒有可沿用的項目' : 'Nothing to attach to')}
        </div>
      )}
      {(items || []).map(c => (
        <button key={c.entityId} type="button"
          onClick={() => setLevel(level, { entity: c.entityId })}
          style={{
            display: 'block', width: '100%', textAlign: 'left', padding: '7px 9px',
            fontSize: 12, border: 'none', cursor: 'pointer',
            background: disposition?.[level]?.entity === c.entityId ? 'var(--primary-soft)' : 'transparent',
            color: disposition?.[level]?.entity === c.entityId ? 'var(--primary)' : 'inherit',
          }}>
          {labelOf(c)}
        </button>
      ))}
    </div>
  );

  const row = (level, heading, levelPlan, body) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>{heading}</span>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {KINDS.map(k => {
            const active = (disposition?.[level]?.kind || 'auto') === k.id;
            return (
              <button key={k.id} type="button"
                onClick={() => setLevel(level, { kind: k.id, entity: null })}
                className="cap-pill"
                style={{
                  fontSize: 11, padding: '3px 8px', cursor: 'pointer',
                  border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                  background: active ? 'var(--primary-soft)' : 'transparent',
                  color: active ? 'var(--primary)' : 'var(--fg-2)',
                }}>
                {zh ? k.zh : k.en}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ fontSize: 11, color: tone(levelPlan) }}>{stateNote(levelPlan, level)}</div>
      {body}
    </div>
  );

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 12,
      padding: 10, borderRadius: 10, border: '1px solid var(--border)',
    }}>
      <div style={{ fontSize: 12, lineHeight: 1.55 }} className="muted">
        {zh ? '呢個課程要歸入邊個主辦機構、系列同場次。系統只會喺有對照紀錄或者連上一層都係新開嘅時候自己決定;其餘情況要你裁決,唔會亂估。'
            : 'Which provider, series and occurrence this course belongs to. The system decides only where a contract already says so, or where the parent is provably new — everything else is yours to answer, and it will not guess.'}
      </div>

      {serverDetail && (
        <div style={{
          fontSize: 11.5, lineHeight: 1.5, padding: '8px 10px', borderRadius: 8,
          background: 'var(--surface-2)', color: 'var(--danger)',
        }}>
          <span className="mono">{serverDetail}</span>
        </div>
      )}
      {loadError && (
        <div className="muted" style={{ fontSize: 11.5, color: 'var(--danger)' }}>{loadError}</div>
      )}

      {row('provider',
        zh ? '主辦機構' : 'Provider',
        plan.provider,
        (disposition?.provider?.kind === 'attach') ? (
          <>
            <input className="field-input" value={filter} onChange={e => setFilter(e.target.value)}
              placeholder={zh ? '搜尋機構…' : 'Search providers…'} />
            {list('provider',
              providers === null ? null : providers.filter(c => providerCandidateMatches(c, filter)),
              // BOTH names render, and the English one stays the primary line
              // because the list is ordered by display_name — showing the
              // Chinese name first would scramble a list that is still sorted
              // by the English one. The point of the second line is that an
              // admin who searched in Chinese can SEE the entity they matched
              // before they click it.
              c => <>{c.label}
                     <span className="muted" style={{ display: 'block', fontSize: 11 }}>
                       {c.labelZh ? <>{c.labelZh}{' · '}</> : null}
                       {c.code ? <>{c.code}{' · '}</> : null}
                       {c.courses}
                     </span></>)}
          </>
        ) : null)}

      {row('series',
        zh ? '課程系列' : 'Series',
        plan.series,
        (disposition?.series?.kind === 'attach' && resolvedProvider) ? list('series', seriesList,
          c => <>{c.label}{c.extra ? <span className="muted"> {c.extra}</span> : null}
                 <span className="muted"> · {c.courses}</span></>) : null)}

      {row('offering',
        zh ? '場次' : 'Occurrence',
        plan.offering,
        (disposition?.offering?.kind === 'attach' && attachedSeries) ? list('offering', offeringList,
          c => <>{c.label}{c.extra ? <span className="muted"> · {c.extra}</span> : null}</>) : null)}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SubmitterProviderHint — RELEASE.md §89 (B1 Step 3B2).
//
// ‼️ THE INVARIANT THIS RENDERS: any submitter hint that will one day influence
// identity resolution must be VISIBLE to the reviewer before they approve.
// Approval is consent, and a reviewer cannot consent to evidence the card never
// showed them. Today the resolver still receives an explicit NULL (§85/§88), so
// this is visibility ahead of authority — deliberately, and in that order.
//
// ‼️ THE LABEL COMES FROM THE ENTITY ID, NEVER FROM THE PAYLOAD TEXT.
// Re-deriving an entity by fuzzy-matching provider_full_en would rebuild the
// exact lossy round-trip §83 measured: the picker's label reaches only 39 of 105
// entities by English and ZERO by Chinese. The uuid IS the answer; look it up.
//
// ‼️ BOTH EVIDENCES ARE SHOWN, AND NEITHER IS REWRITTEN. The submitted text says
// what the poster/nurse saw; the entity says which canonical provider she
// believes that is. They answer different questions, and a disagreement between
// them is exactly what a reviewer needs to notice — so this component never
// "corrects" one from the other, and never hides one because the other exists.
function SubmitterProviderHint({ item, payload, lang }) {
  const zh = lang === 'zh';
  const hint = item?.provider_entity_id_hint || null;
  const submitted = payload?.provider_full_en || payload?.provider_full_zh || null;
  const [showId, setShowId] = React.useState(false);
  // The dictionary is a stable Map mutated in place (data.js) and is loaded at
  // app boot. It can still be empty — an offline cold start with no cache — and
  // an unresolved id must SAY so rather than render a blank that reads as "no
  // selection". Those are different facts.
  const labels = (typeof window !== 'undefined' && window.PROVIDER_LABELS) || null;
  const entry = hint && labels ? labels.get(String(hint)) : null;

  const row = (label, value, muted) => (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
      <span className="muted-2" style={{ fontSize: 10.5, minWidth: 72, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 12.5, color: muted ? 'var(--fg-3)' : 'inherit' }}>{value}</span>
    </div>
  );

  return (
    <div style={{ border: '0.5px solid var(--border)', borderRadius: 10, padding: '9px 10px',
                  background: 'var(--surface)', display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{ fontSize: 11, fontWeight: 600 }}>
        {zh ? '提交者選擇的主辦機構' : 'Provider the submitter selected'}
      </div>

      {!hint && (
        <div style={{ fontSize: 12.5, color: 'var(--fg-3)' }}>
          {zh ? '提交者沒有選擇現有機構' : 'The submitter did not select an existing organisation'}
        </div>
      )}

      {hint && entry && (
        <>
          <div style={{ fontSize: 13.5, fontWeight: 600 }}>{entry.zh || entry.en}</div>
          {entry.en && entry.zh && entry.en !== entry.zh && (
            <div className="muted" style={{ fontSize: 12 }}>{entry.en}</div>
          )}
          {/* ‼️ "待確認", never "✓ 已驗證". A submitter's pick is evidence the
              resolver may still refuse (§85); saying "verified" would claim an
              adjudication that has not happened. */}
          <span className="cap-pill tone-muted" style={{ alignSelf: 'flex-start', fontSize: 10.5 }}>
            {zh ? '已配對到現有機構 · 待確認' : 'Matched to an existing organisation · unconfirmed'}
          </span>
        </>
      )}

      {hint && !entry && (
        // An id we cannot name is not the same as no id. Say which one it is.
        <div style={{ fontSize: 12.5, color: 'var(--warn, var(--fg-2))' }}>
          {zh ? '已選擇機構，但目前無法解析其名稱' : 'An organisation was selected, but its name could not be resolved'}
        </div>
      )}

      {submitted && (
        <div style={{ marginTop: 3, paddingTop: 5, borderTop: '0.5px dashed var(--border)' }}>
          {row(zh ? '提交者原文' : 'As submitted', submitted)}
        </div>
      )}

      {hint && (
        <button type="button" onClick={() => setShowId(v => !v)}
          style={{ alignSelf: 'flex-start', background: 'none', border: 0, padding: 0,
                   font: 'inherit', fontSize: 10.5, color: 'var(--fg-3)', cursor: 'pointer' }}>
          {showId ? (zh ? '隱藏技術細節' : 'Hide details') : (zh ? '技術細節' : 'Details')}
        </button>
      )}
      {hint && showId && row('entity id', <code style={{ fontSize: 10.5 }}>{hint}</code>, true)}
    </div>
  );
}

// ‼️ THE REASONS THAT MEAN "A HUMAN MUST ANSWER AN IDENTITY QUESTION", and only
// those. approve-submission returns a stable machine-readable `reason` slug
// alongside the human `message`; this is the client half of that contract.
//
// It is an ALLOWLIST on purpose. Routing on "any reason except not_pending"
// would open the identity picker for a failure that has nothing to do with
// identity, and would do it silently the day someone adds a fourth slug.
// scripts/test-approve-reason-routing.mjs pins this against the edge function.
const IDENTITY_VERDICT_REASONS = [
  'identity_disposition_required',   // resolver: no contract candidate, or hint vs contract disagree
  'series_not_mintable',             // a NEW series verdict without identifying material
  'offering_not_mintable',           // novelty proven, representability not
];

// ─────────────────────────────────────────────────────────────────────────────
// ProviderIdentityFindings — Layer 1A, the deterministic half of review help.
//
// ‼️ A FINDING DESCRIBES. AN ADMIN DECIDES. Nothing in here writes on render,
// on mount, or on a timer: every change to the payload or the disposition is
// behind a button the reviewer presses. That is the whole boundary between this
// layer and "the machine filled in the form", and it is why the findings can be
// trusted enough to be shown at all.
//
// ‼️ IT READS window.PROVIDER_LABELS, WHICH IS LOADED AT BOOT. No per-card
// request: identity-candidates stays lazily loaded behind 身分歸屬 for the
// picker, exactly as before. If the dictionary is empty (offline cold start),
// every finding degrades to silence rather than to "no existing organisation" —
// those are different facts and only one of them is true.
function ProviderIdentityFindings({ payload, lang, onApplyPayload, onAttachProvider, attachedProvider }) {
  const zh = lang === 'zh';
  const dict = (typeof window !== 'undefined' && window.PROVIDER_LABELS) || null;
  const findings = (dict && dict.size && typeof window.providerIdentityFindings === 'function')
    ? window.providerIdentityFindings(payload || {}, dict)
    : [];
  if (!findings.length) return null;

  const TONE = {
    ok:   { bg: 'color-mix(in oklch, var(--primary) 8%, transparent)',  bd: 'var(--primary)', ic: 'check' },
    warn: { bg: 'color-mix(in oklch, var(--warning) 12%, transparent)', bd: 'var(--warning)', ic: 'shield' },
    info: { bg: 'var(--surface-2)',                                     bd: 'var(--border)',  ic: 'info' },
  };

  const btn = (label, onClick, done) => (
    <button type="button" onClick={onClick} disabled={done}
      className="btn btn-ghost"
      style={{ alignSelf: 'flex-start', fontSize: 11.5, padding: '3px 9px',
               opacity: done ? 0.55 : 1, cursor: done ? 'default' : 'pointer' }}>
      {done ? (zh ? '已套用' : 'Applied') : label}
    </button>
  );

  const render = (f, i) => {
    const tone = TONE[f.severity] || TONE.info;
    let title = null; let body = null; let action = null;

    if (f.code === 'PROVIDER_LABEL_MATCH') {
      if (f.verdict === 'exact_label_match') {
        const e = f.entities[0];
        title = zh ? '對到現有機構' : 'Matches an existing organisation';
        body = (
          <>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{e.zh || e.en}</div>
            {e.zh && e.en && <div className="muted" style={{ fontSize: 11.5 }}>{e.en}</div>}
          </>
        );
        action = btn(zh ? '設為歸屬' : 'Set as attachment',
          () => onAttachProvider(e.entityId), attachedProvider === e.entityId);
      } else if (f.verdict === 'ambiguous_label_match') {
        title = zh ? '對到多於一個機構' : 'Matches more than one organisation';
        body = (
          <>
            <div className="muted" style={{ fontSize: 11.5 }}>
              {zh ? '同一個名稱對應多個機構,請自行裁決。'
                  : 'This name answers to several entities — decide in 身分歸屬.'}
            </div>
            {f.entities.map(e => (
              <div key={e.entityId} style={{ fontSize: 12 }}>· {e.zh || e.en}</div>
            ))}
          </>
        );
        /* ‼️ Deliberately no button. See the verdict's comment. */
      } else {
        if (!f.submitted.length) return null;   // no provider text: say nothing
        // ‼️ ONE LINE, AND IT RETURNS EARLY RATHER THAN USING THE BOX ABOVE.
        // This verdict fires on 57 of 82 live submissions — roughly 70% of the
        // queue — and it carries no action, marks no defect and proves nothing
        // about novelty. Given three lines and a bordered card it would out-weigh
        // the mismatch and gap findings that DO need handling, and the reliable
        // end state of "every card has a notice" is an admin who reads none of
        // them. So it renders as a single muted row: present, and quiet.
        //
        // ‼️ THE DISCLAIMER STAYS ON SCREEN. Not a tooltip, not behind a
        // disclosure. Measured 2026-09-09: 41 of 43 submissions in this state
        // were still resolved onto an EXISTING entity, so "this might be a new
        // organisation" is the wrong inference 95% of the time — and an
        // inference that costs one hover to discover is one nobody makes.
        return (
          <div key={i} style={{
            display: 'flex', gap: 6, alignItems: 'baseline',
            padding: '5px 8px', borderRadius: 6, background: 'var(--surface-2)',
          }}>
            <Icon name="info" size={11} stroke={1.8} color="var(--fg-3)" />
            <span style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--fg-3)' }}>
              {zh ? '提交文字未直接匹配已知機構名稱;' : 'No direct match to a known organisation name; '}
              <strong style={{ color: 'var(--fg-2)', fontWeight: 600 }}>
                {zh ? '不代表這是新機構。' : 'this is not evidence it is new.'}
              </strong>
            </span>
          </div>
        );
      }
    }

    if (f.code === 'PROVIDER_LANGUAGE_FIELD_MISMATCH') {
      const it = f.issues[0];
      title = zh ? '中文機構名填在英文欄' : 'Chinese provider name in the English field';
      body = <div style={{ fontSize: 12.5 }}><span className="mono">{it.field}</span> = 「{it.value}」</div>;
      if (f.action) {
        action = btn(zh ? '對調' : 'Swap',
          () => onApplyPayload({ provider_full_en: null, provider_full_zh: it.value }),
          (payload || {}).provider_full_zh === it.value && !(payload || {}).provider_full_en);
      }
    }

    if (f.code === 'PROVIDER_BILINGUAL_GAP') {
      title = zh ? '現有機構有另一語言名稱,提交未填' : 'The matched organisation has a name this submission omits';
      body = (
        <>{f.missing.map(m => (
          <div key={m.field} style={{ fontSize: 12.5 }}>
            <span className="muted-2 mono" style={{ fontSize: 10.5 }}>{m.field}</span> 「{m.value}」
          </div>
        ))}</>
      );
      const patch = {};
      f.missing.forEach(m => { patch[m.field] = m.value; });
      const done = f.missing.every(m => (payload || {})[m.field] === m.value);
      action = btn(zh ? '採用' : 'Adopt', () => onApplyPayload(patch), done);
    }

    if (!title) return null;
    return (
      <div key={i} style={{
        display: 'flex', flexDirection: 'column', gap: 4,
        padding: '8px 10px', borderRadius: 8,
        background: tone.bg, border: `0.5px solid ${tone.bd}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name={tone.ic} size={12} stroke={1.9} />
          <span style={{ fontSize: 11.5, fontWeight: 600 }}>{title}</span>
        </div>
        {body}
        {action}
      </div>
    );
  };

  const rendered = findings.map(render).filter(Boolean);
  if (!rendered.length) return null;
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{rendered}</div>;
}

// ─────────────────────────────────────────────────────────────────────────────
// CompletenessFindings — Layer 1B.
//
// ‼️ EVERY LINE HERE REPORTS STORAGE STATE, NOT INTENT — and that stays true
// even though the door that motivated it is now fixed.
//
// Until issue #42 the co-submit door wrote `Number(form.points) || null`, which
// silently turned a deliberately-typed 0 into null while the other two doors
// stored 0. All three now share cnePointsForSubmission, so NEW rows no longer
// carry that ambiguity.
//
// ‼️ THE HISTORICAL ROWS STILL DO, AND NOTHING CAN UNDO THEM. #42 forbids a
// backfill: once 0 became null the information is gone, and absent row-specific
// provenance an existing null can only be read as "value unknown". So a null
// this card reports may be a genuine blank OR a zero destroyed before the fix,
// and 「未填寫」 would still be a claim about a person that the column cannot
// support. The copy says what the field CONTAINS — 「未有資料」, 「目前為 0」 —
// and, where it is provable, what that costs downstream.
//
// ‼️ AND NOTHING HERE INVENTS A VALUE. The only action is stripping whitespace,
// which is lossless. end_date is never mirrored from start_date, and a name
// minus its trailing punctuation is shown as a candidate, never as "correct".
function CompletenessFindings({ payload, lang, onApplyPayload }) {
  const zh = lang === 'zh';
  const dict = (typeof window !== 'undefined' && window.PROVIDER_LABELS) || null;
  const findings = (typeof window.completenessFindings === 'function')
    ? window.completenessFindings(payload || {}, dict) : [];
  if (!findings.length) return null;

  const row = (key, tone, icon, children, action) => (
    <div key={key} style={{
      display: 'flex', flexDirection: 'column', gap: 4,
      padding: tone === 'warn' ? '7px 9px' : '5px 8px', borderRadius: 6,
      background: tone === 'warn'
        ? 'color-mix(in oklch, var(--warning) 11%, transparent)' : 'var(--surface-2)',
      border: tone === 'warn'
        ? '0.5px solid color-mix(in oklch, var(--warning) 40%, transparent)' : 'none',
    }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
        <Icon name={icon} size={11} stroke={1.8}
          color={tone === 'warn' ? 'var(--warning)' : 'var(--fg-3)'} />
        <span style={{ fontSize: 11.5, lineHeight: 1.5,
                       color: tone === 'warn' ? 'var(--fg)' : 'var(--fg-3)' }}>
          {children}
        </span>
      </div>
      {action}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {findings.map((f, i) => {
        if (f.code === 'CNE_POINTS_MISSING') {
          // ‼️ 「未有資料」, never 「提交者未填寫」 — see the header.
          return row(i, 'warn', 'shield', (
            <>
              <strong style={{ fontWeight: 600 }}>{zh ? 'CNE 分數未有資料' : 'No CNE points on record'}</strong>
              {' · '}
              <span className="muted">
                {zh ? '目前沒有可供計算的分數' : 'nothing available to count toward a cycle'}
              </span>
            </>
          ), null);
        }
        if (f.code === 'CNE_POINTS_ZERO') {
          // ‼️ 「目前為 0」, never 「無效」 — nothing establishes that 0 is illegal.
          return row(i, 'info', 'info',
            zh ? 'CNE 分數目前為 0' : 'CNE points are currently 0', null);
        }
        if (f.code === 'END_DATE_MISSING') {
          return row(i, 'info', 'info',
            zh ? '有開始日期,但沒有結束日期' : 'Has a start date, but no end date', null);
        }
        if (f.code === 'PROVIDER_NAME_TRAILING_NOISE') {
          const it = f.issues[0];
          if (it.kind === 'trailing_whitespace') {
            const done = f.action.fields.every(x => (payload || {})[x.field] === x.value);
            // ‼️ Built OUTSIDE the handler so the handler stays a concise arrow:
            // the suite's rule is that every onApplyPayload call site reads
            // `() => onApplyPayload(`, and it is strict on purpose — a
            // block-bodied handler is where an accidental render-time call
            // hides. Keep the shape, don't widen the rule.
            const patch = {};
            f.action.fields.forEach(x => { patch[x.field] = x.value; });
            return row(i, 'info', 'info', (
              <>{zh ? '機構名稱首尾有多餘空白' : 'Provider name has stray whitespace'}</>
            ), (
              <button type="button" className="btn btn-ghost" disabled={done}
                onClick={() => onApplyPayload(patch)}
                style={{ alignSelf: 'flex-start', fontSize: 11.5, padding: '3px 9px',
                         opacity: done ? 0.55 : 1 }}>
                {done ? (zh ? '已套用' : 'Applied') : (zh ? '移除多餘空白' : 'Remove whitespace')}
              </button>
            ));
          }
          // ‼️ OBSERVATION ONLY. The candidate is "the value without its trailing
          // punctuation" — NOT "the correct name". The single live instance,
          // 「American Heart Association.」, matches no entity either way, so
          // calling it correct would invent an authority nothing supports.
          return row(i, 'info', 'info', (
            <>
              {zh ? '機構名稱末尾有標點' : 'Provider name ends in punctuation'}
              <span style={{ display: 'block', marginTop: 2 }}>
                {zh ? '移除末尾標點後:' : 'Without the trailing punctuation: '}
                <span style={{ color: 'var(--fg-2)' }}>{it.candidate}</span>
              </span>
            </>
          ), null);
        }
        return null;
      }).filter(Boolean)}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ConventionFindings — Layer 1C.
//
// ‼️ ONE FINDING, OUT OF THREE CANDIDATES. The calibration that preceded this
// REJECTED the restriction-precedent finding and DEFERRED submitter affiliation.
// That is a product decision, not unfinished work — see the Layer 1C section in
// RELEASE.md before proposing either again.
function ConventionFindings({ payload, lang }) {
  const zh = lang === 'zh';
  const dict = (typeof window !== 'undefined' && window.PROVIDER_LABELS) || null;
  const findings = (typeof window.conventionFindings === 'function')
    ? window.conventionFindings(payload || {}, dict) : [];
  if (!findings.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {findings.map((f, i) => (
        <div key={i} style={{
          display: 'flex', flexDirection: 'column', gap: 3,
          padding: '7px 9px', borderRadius: 6,
          background: 'color-mix(in oklch, var(--warning) 11%, transparent)',
          border: '0.5px solid color-mix(in oklch, var(--warning) 40%, transparent)',
        }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
            <Icon name="shield" size={11} stroke={1.8} color="var(--warning)" />
            <span style={{ fontSize: 11.5, lineHeight: 1.5 }}>
              {/* ‼️ 「似乎填了一間已知院舍」 — what the dictionary proves. NOT
                  「機構填錯了」, and NOT 「應歸屬 X」: which organisation owns the
                  row is a resolution question with a human in it. */}
              <strong style={{ fontWeight: 600 }}>
                {zh ? '機構欄似乎填了一間已知院舍'
                    : 'The provider field names a known facility'}
              </strong>
              <span style={{ display: 'block', marginTop: 2 }} className="muted">
                {zh ? '請確認應否歸屬其所屬機構。'
                    : 'Check whether it should be attributed to the organisation it belongs to.'}
              </span>
            </span>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--fg-2)', paddingLeft: 17 }}>
            {f.facility.zh || f.facility.en}
            {f.facility.zh && f.facility.en && (
              <span className="muted"> · {f.facility.en}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DuplicateReviewFindings — pairwise, and never a verdict.
//
// ‼️ PAIRWISE, NEVER GROUPED. A grouped view conflates distinct duplicates: one
// nurse, one date, one point value can legitimately cover two different
// courses, and grouping put four rows in one box that were really two separate
// pairs. Each pair is its own claim and is shown as one.
//
// ‼️ NO REJECT, NO DELETE, NO MERGE. This layer puts a pair in front of a human.
// The existing submission guard owns blocking and lifecycle, where a wrong
// answer is expensive; nothing here may grow into that.
//
// ‼️ AND A WEAK PAIR IS STILL SHOWN. Stage 2 (token overlap) ranks and explains;
// it does not filter. "No shared tokens" is evidence of weakness, never proof
// of difference — the corpus that produced the rule was hand-labelled and
// twelve pairs wide.
function DuplicateReviewFindings({ item, corpus, lang }) {
  const zh = lang === 'zh';
  const pairs = (Array.isArray(corpus) && typeof window.duplicateReviewFindings === 'function')
    ? window.duplicateReviewFindings(item, corpus) : [];
  if (!pairs.length) return null;

  const tokens = (label, list) => (list.length ? (
    <div style={{ fontSize: 11, marginTop: 3 }}>
      <span className="muted-2">{label}</span>{' '}
      {list.slice(0, 8).map((t, i) => (
        <span key={i} style={{ color: 'var(--fg-2)' }}>{i ? ' · ' : ''}{t}</span>
      ))}
      {list.length > 8 ? <span className="muted-2"> …</span> : null}
    </div>
  ) : null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {pairs.map(f => {
        const strong = f.evidence_strength === 'stronger_overlap';
        return (
          <div key={f.pairKey} style={{
            padding: '8px 10px', borderRadius: 8,
            background: strong
              ? 'color-mix(in oklch, var(--warning) 10%, transparent)' : 'var(--surface-2)',
            border: strong
              ? '0.5px solid color-mix(in oklch, var(--warning) 38%, transparent)' : 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <Icon name={strong ? 'shield' : 'info'} size={11} stroke={1.9}
                color={strong ? 'var(--warning)' : 'var(--fg-3)'} />
              <span style={{ fontSize: 11.5, fontWeight: 600 }}>
                {zh ? '疑似重複提交' : 'Possible duplicate submission'}
              </span>
              {!strong && (
                <span className="muted-2" style={{ fontSize: 10.5 }}>
                  {zh ? '· 證據較弱' : '· weaker evidence'}
                </span>
              )}
            </div>
            <div className="muted" style={{ fontSize: 11, lineHeight: 1.5 }}>
              {zh ? '同一提交者 · 同一開始日期 · 同一 CNE 分數' : 'Same submitter · same start date · same CNE points'}
              {' ('}{f.observed.start_date}{' · '}
              {f.observed.cne_points == null ? (zh ? '無分數' : 'no points') : f.observed.cne_points}{')'}
            </div>
            <div style={{ marginTop: 5, paddingTop: 5, borderTop: '0.5px dashed var(--border)' }}>
              <div className="muted-2" style={{ fontSize: 10.5 }}>
                {zh ? '另一筆提交' : 'The other submission'}
                {' · '}<span className="mono">{String(f.other.id).slice(0, 8)}</span>
                {' · '}{f.other.status}
                {f.other.approved_course_id ? (
                  <>{' · '}<span className="mono">{String(f.other.approved_course_id).slice(0, 12)}</span></>
                ) : null}
              </div>
              <div style={{ fontSize: 12.5, marginTop: 2 }}>{f.other.title || (zh ? '(無標題)' : '(no title)')}</div>
              {f.other.provider && (
                <div className="muted" style={{ fontSize: 11.5 }}>{f.other.provider}</div>
              )}
            </div>
            {/* ‼️ THE TOKENS ARE PRINTED, not summarised into a score. A reviewer
                can check "chronic · limb · threatening" themselves; nobody can
                check 0.87. Two independent lists, because provider text and
                title text drift independently and no true pair has yet lost
                both. */}
            {tokens(zh ? '共同標題字詞：' : 'Shared title words:', f.title_shared_tokens)}
            {tokens(zh ? '共同機構字詞：' : 'Shared provider words:', f.provider_shared_tokens)}
            {!f.title_shared_tokens.length && !f.provider_shared_tokens.length && (
              <div className="muted-2" style={{ fontSize: 11, marginTop: 3 }}>
                {zh ? '沒有共同字詞 —— 可能是同日的另一門課程,請自行比對。'
                    : 'No shared words — possibly a different course on the same day; compare them yourself.'}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ‼️ A SUGGESTION, NEVER A WRITE. find-registration-link returns candidate pages
// with the evidence each page showed (title words, the course date, registration
// wording). Search grounding can surface last year's meeting or a society
// homepage, so the reviewer opens the link and decides; only her tap copies it
// into the card's reviewed payload (RELEASE.md §156).
function RegistrationLinkFinder({ submissionId, reviewed, lang, onUse }) {
  const zh = lang === 'zh';
  const [state, setState] = React.useState({ status: 'idle' });
  const [used, setUsed] = React.useState({});
  if (typeof window.DB?.adminFindRegistrationLink !== 'function') return null;
  const run = async () => {
    setState({ status: 'loading' });
    const res = await window.DB.adminFindRegistrationLink({
      submissionId,
      title: reviewed.title_en || reviewed.title_zh || '',
      provider: reviewed.provider_full_en || reviewed.provider_full_zh || '',
      startDate: reviewed.start_date || null,
      venue: reviewed.venue_en || '',
    });
    if (res?.error) { setState({ status: 'error', message: res.error.message }); return; }
    setState({ status: 'done', data: res?.data || {} });
  };
  const verdictLabel = { strong: zh ? '高度吻合' : 'Strong match', possible: zh ? '可能吻合' : 'Possible', weak: zh ? '證據不足' : 'Weak' };
  const verdictTone = { strong: 'tone-success', possible: 'tone-warning', weak: 'tone-muted' };
  const list = state.data?.candidates || [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10, border: '0.5px solid var(--border)', borderRadius: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
        <div style={{ fontSize: 12.5, fontWeight: 600 }}>{zh ? 'AI 搵報名連結' : 'Find registration link (AI)'}</div>
        <button type="button" className="btn btn-ghost" style={{ fontSize: 12 }}
          disabled={state.status === 'loading' || !(reviewed.title_en || reviewed.title_zh)} onClick={run}>
          {state.status === 'loading' ? (zh ? '搜尋中…' : 'Searching…') : state.status === 'done' ? (zh ? '再搵一次' : 'Search again') : (zh ? '搵連結' : 'Find links')}
        </button>
      </div>
      <div className="muted" style={{ fontSize: 11, lineHeight: 1.45 }}>
        {zh ? '建議只供參考 —— 請打開連結核對日期同內容;只有你揀嘅連結先會填入。'
            : 'Suggestions only — open each link and check it; only a link you pick is filled in.'}
      </div>
      {state.status === 'error' && (
        <div style={{ fontSize: 12, color: 'var(--danger)' }}>{zh ? '搜尋失敗:' : 'Search failed: '}{state.message}</div>
      )}
      {state.status === 'done' && !list.length && (
        <div className="muted" style={{ fontSize: 12 }}>{zh ? '搵唔到可核實嘅連結。' : 'No verifiable link found.'}</div>
      )}
      {list.map((c) => (
        <div key={c.url} style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 6, borderTop: '0.5px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className={`cap-pill ${verdictTone[c.verdict] || 'tone-muted'}`}>{verdictLabel[c.verdict] || c.verdict}</span>
            <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--primary)', wordBreak: 'break-all' }}>{c.url}</a>
          </div>
          {c.pageTitle && <div className="muted" style={{ fontSize: 11 }}>{c.pageTitle}</div>}
          <div className="muted" style={{ fontSize: 11 }}>
            {zh ? '標題吻合' : 'Title'} {Math.round((c.titleScore || 0) * 100)}% · {zh ? '日期' : 'Date'} {c.dateFound ? `✓ ${c.dateFound}` : '✗'} · {zh ? '報名字眼' : 'Registration wording'} {c.registration ? '✓' : '✗'}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['registration_url', 'url'].map((field) => (
              <button key={field} type="button" className="btn btn-ghost" style={{ fontSize: 11.5 }}
                onClick={() => { onUse(field, c.url); setUsed((u) => ({ ...u, [field]: c.url })); }}>
                {used[field] === c.url ? '✓ ' : ''}{field === 'registration_url' ? (zh ? '用作報名連結' : 'Use as registration link') : (zh ? '用作官方連結' : 'Use as official link')}
                {c.suggestedRole === (field === 'registration_url' ? 'registration' : 'official') ? (zh ? '(建議)' : ' (suggested)') : ''}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AdminSubmissionCard({ item, lang, stats, onOpenHistory, notes, setNotes, isBusy, anyBusy, onApprove, onReject, duplicateCorpus }) {
  // ── THE REVISION THIS RENDER IS SHOWING ────────────────────────────────────
  // ‼️ READ IT HERE, IN THE RENDER BODY, AND NOWHERE ELSE.
  // Everything below — payload, evidence, the identity picker — is drawn from
  // THIS `item`. React closes the click handler over this render's props, so a
  // value read here is provably the version the reviewer was looking at when
  // they tapped. That is the whole content of the claim `expectedRevision`
  // makes to the server.
  //
  // ‼️ DO NOT replace this with a lookup at click time, and do not freeze it in
  // state at mount. A click-time re-read always agrees with itself and turns the
  // stale check into theatre; a mount-time freeze goes stale against the pixels
  // the moment the queue refreshes underneath an open card, and would report a
  // conflict that is not there.
  const reviewedRevision = Number.isInteger(item.revision) ? item.revision : null;
  const [staleNotice, setStaleNotice] = React.useState(null);
  const p = item.payload || {};
  const [reviewed, setReviewed] = React.useState(() => ({ ...p }));
  // ‼️ EMPTY DISPOSITION = "SEND NOTHING", NOT "NO IDENTITY". Every level starts
  // on 'auto', which omits it from the request entirely, so the resolver either
  // answers from a canonical contract or refuses. See IdentityDispositionPanel.
  const [disposition, setDisposition] = React.useState({});
  const [identityOpen, setIdentityOpen] = React.useState(false);
  const [identityDetail, setIdentityDetail] = React.useState(null);
  const fieldRegistry = window.REPORT_FIELDS_BY_KEY || {};
  const fieldLabel = (key, fallback) => {
    const f = fieldRegistry[key];
    return f ? (lang === 'zh' ? f.zh : f.en) : fallback;
  };
  const reviewKeys = [
    ['title_en', fieldLabel('title_en', 'Title (EN)'), 'text'],
    ['title_zh', fieldLabel('title_zh', '名稱（中）'), 'text'],
    ['provider_full_en', lang === 'zh' ? '主辦機構（英）' : 'Provider (EN)', 'text'],
    ['provider_full_zh', lang === 'zh' ? '主辦機構（中）' : 'Provider (ZH)', 'text'],
    ['start_date', fieldLabel('start_date', lang === 'zh' ? '開始日期' : 'Start date'), 'date'],
    ['end_date', fieldLabel('end_date', lang === 'zh' ? '結束日期' : 'End date'), 'date'],
    ['time_text', fieldLabel('time_text', lang === 'zh' ? '時間' : 'Time'), 'text'],
    ['cne_points', fieldLabel('cne_points', 'CNE'), 'number'],
    ['fee_hkd', fieldLabel('fee_hkd', lang === 'zh' ? '費用 HKD' : 'Fee HKD'), 'number'],
    ['language_en', fieldLabel('language_en', lang === 'zh' ? '授課語言' : 'Language'), 'text'],
    ['teaching_mode_en', fieldLabel('teaching_mode_en', lang === 'zh' ? '授課模式' : 'Teaching mode'), 'text'],
    ['venue_en', fieldLabel('venue_en', lang === 'zh' ? '地點（英）' : 'Venue (EN)'), 'text'],
    ['venue_zh', fieldLabel('venue_zh', lang === 'zh' ? '地點（中）' : 'Venue (ZH)'), 'text'],
    ['url', lang === 'zh' ? '官方來源 URL' : 'Official source URL', 'url'],
    ['registration_url', fieldLabel('registration_url', lang === 'zh' ? '報名 URL' : 'Registration URL'), 'url'],
    ['description_short_en', lang === 'zh' ? '簡介（英）' : 'Description (EN)', 'text'],
    // Not in REPORT_FIELDS_BY_KEY on purpose — apply_report has no
    // description_short_zh branch, and a picker entry without one submits and
    // then silently no-ops (see the crowdsource-fields.js header). The plain
    // <input> fallback below still lets a reviewer edit what the submitter sent.
    ['description_short_zh', lang === 'zh' ? '簡介（中）' : 'Description (中文)', 'text'],
  ];
  return (
    <div className="card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: 14.5 }}>
          {p.title_en || p.title_zh || (lang === 'zh' ? '(無標題)' : '(no title)')}
        </div>
        {p.provider_full_en && (
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{p.provider_full_en}</div>
        )}
        {item.is_restricted && (
          <span className="cap-pill tone-muted" style={{ marginTop: 6, display: 'inline-flex' }}>
            <Icon name="shield" size={10} stroke={1.8} />
            {' '}{item.restriction_note || (lang === 'zh' ? '受限' : 'Restricted')}
          </span>
        )}
      </div>

      {/* §89 — identity evidence, ABOVE the editable course fields. The reviewer
          meets what they are being asked to ratify before they start editing
          what they are being asked to correct. */}
      <SubmitterProviderHint item={item} payload={p} lang={lang} />

      {/* Layer 1A — deterministic provider-identity findings. Reads the
          boot-time PROVIDER_LABELS dictionary, so it costs no request per
          card. Every action below is a click; nothing here writes on render. */}
      <ProviderIdentityFindings
        payload={reviewed} lang={lang}
        onApplyPayload={patch => setReviewed(old => ({ ...old, ...patch }))}
        onAttachProvider={entityId => setDisposition(prev => ({
          ...prev, provider: { kind: 'attach', entity: entityId },
        }))}
        attachedProvider={disposition?.provider?.kind === 'attach'
          ? disposition.provider.entity : null} />

      {/* Layer 1B — completeness. Separate batch, separate component: identity
          answers "which organisation", completeness answers "what is missing
          from this row", and folding them would make one list whose entries
          are not comparable. */}
      <CompletenessFindings
        payload={reviewed} lang={lang}
        onApplyPayload={patch => setReviewed(old => ({ ...old, ...patch }))} />

      {/* Pairwise duplicate review. `corpus` reaches here from the queue, which
          fetches it once for all cards — see fetchDuplicateCandidates. */}
      <DuplicateReviewFindings item={item} corpus={duplicateCorpus} lang={lang} />

      {/* Layer 1C — convention. One finding shipped of three candidates; the
          other two were rejected and deferred BY MEASUREMENT, not by omission. */}
      <ConventionFindings payload={reviewed} lang={lang} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {reviewKeys.map(([key, label, type]) => (
          <label className="field" key={key}
            style={{ gridColumn: /title|provider|venue|url|description/.test(key) ? '1 / -1' : undefined }}>
            <span className="field-label">{label}</span>
            {fieldRegistry[key] ? (
              <ReportFieldValueInput
                fieldKey={key}
                value={reviewed[key] ?? ''}
                lang={lang}
                onChange={value => setReviewed(old => ({
                  ...old,
                  [key]: value === '' ? null
                    : ['number', 'integer'].includes(fieldRegistry[key]?.input?.kind)
                      ? Number(value) : value,
                }))}
                placeholder={label} />
            ) : (
              <input className="field-input" type={type} value={reviewed[key] ?? ''}
                onChange={e => setReviewed(old => ({
                  ...old,
                  [key]: e.target.value === '' ? null
                    : type === 'number' ? Number(e.target.value) : e.target.value,
                }))} />
            )}
          </label>
        ))}
      </div>

      {/* §156 — AI suggestions for the official / registration page. Read-only:
          a link reaches `reviewed` only when the reviewer taps 用作…, and the
          approval that follows is the ordinary one. */}
      <RegistrationLinkFinder submissionId={item.id} reviewed={reviewed} lang={lang}
        onUse={(field, url) => setReviewed(old => ({ ...old, [field]: url }))} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
        {p.start_date && <div><span className="muted">{lang === 'zh' ? '開始: ' : 'Start: '}</span>{p.start_date}</div>}
        {p.end_date && <div><span className="muted">{lang === 'zh' ? '結束: ' : 'End: '}</span>{p.end_date}</div>}
        {p.cne_points != null && <div><span className="muted">CNE: </span>{p.cne_points}</div>}
        {p.venue_en && <div style={{ gridColumn: '1 / -1' }}><span className="muted">{lang === 'zh' ? '地點: ' : 'Venue: '}</span>{p.venue_en}</div>}
        {p.registration_url && (
          <div style={{ gridColumn: '1 / -1' }}>
            <span className="muted">URL: </span>
            <a href={p.registration_url} target="_blank" rel="noopener noreferrer"
               style={{ color: 'var(--primary)' }}>
              {p.registration_url}
            </a>
          </div>
        )}
      </div>

      <div>
        <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>
          {lang === 'zh' ? '證據' : 'Evidence'}
        </div>
        <EvidenceList paths={item.evidence_urls} purgedAt={item.evidence_purged_at} purgedFileCount={item.evidence_purged_file_count} lang={lang} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div className="muted-2" style={{ fontSize: 11 }}>
          {lang === 'zh' ? '提交者' : 'Submitter'}: <span className="mono">{item.submitter_user_id?.slice(0, 8)}…</span>
          {item.source_origin === 'co_submit_from_log' && (
            <span style={{ marginLeft: 8 }}>· {lang === 'zh' ? '由「手動記錄」順帶提交' : 'via co-submit from log'}</span>
          )}
        </div>
        <ContributorStatLine stats={stats} kind="submissions" lang={lang} onOpenHistory={onOpenHistory} />
      </div>

      {/* Collapsed by default and lazily loaded: the candidate lists are a
          service-role round trip per card, and in the common case (a provider
          the contract layer already knows) the reviewer never needs to look. */}
      <button type="button" className="btn btn-ghost"
        style={{ alignSelf: 'flex-start', fontSize: 12, padding: '4px 8px' }}
        onClick={() => setIdentityOpen(v => !v)}>
        <Icon name={identityOpen ? 'chev-d' : 'chev-r'} size={13} stroke={2} />
        {lang === 'zh' ? '身分歸屬' : 'Identity'}
        {Object.keys(disposition).some(k => disposition[k]?.kind && disposition[k].kind !== 'auto') && (
          <span style={{ color: 'var(--primary)' }}> · {lang === 'zh' ? '已裁決' : 'set'}</span>
        )}
      </button>
      {identityOpen && (
        <IdentityDispositionPanel
          submissionId={item.id} payload={reviewed} lang={lang}
          disposition={disposition} setDisposition={setDisposition}
          serverDetail={identityDetail} />
      )}

      <textarea className="field-input" rows={2}
        placeholder={lang === 'zh' ? '管理員備註(可選)' : 'Admin notes (optional)'}
        value={notes} onChange={e => setNotes(e.target.value)} />

      {staleNotice && (
        <div role="alert" style={{
          fontSize: 12, lineHeight: 1.5, padding: '8px 10px', borderRadius: 8,
          background: 'color-mix(in oklch, var(--warning) 12%, transparent)',
          border: '0.5px solid color-mix(in oklch, var(--warning) 45%, transparent)',
        }}>
          <strong>{lang === 'zh' ? '提交者在你審核期間修改了這筆提交'
                                 : 'The submitter edited this while you were reviewing it'}</strong>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
            {lang === 'zh'
              ? '已重新載入最新版本,請再檢視一次才通過。'
              : 'The latest version has been reloaded — please review it again before approving.'}
            {typeof staleNotice === 'string' ? ` (${staleNotice})` : ''}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" style={{ flex: 1 }}
          disabled={anyBusy} onClick={async () => {
            // ‼️ ONLY EXPLICIT VERDICTS TRAVEL. A level left on 'auto' is stripped
            // here rather than sent as {kind:'auto'} — the RPC has no such kind and
            // would raise 22023, and more importantly "the admin did not answer"
            // and "the admin answered 'do whatever'" are different facts.
            const verdict = {};
            for (const level of ['provider', 'series', 'offering']) {
              const d = disposition[level];
              if (!d || !d.kind || d.kind === 'auto') continue;
              verdict[level] = d.kind === 'attach'
                ? { kind: 'attach', entity: d.entity }
                : { kind: d.kind };
            }
            setStaleNotice(null);
            const err = await onApprove(reviewed, Object.keys(verdict).length ? verdict : null,
                                        reviewedRevision);
            // ‼️ A STALE REVIEW IS NOT AN IDENTITY QUESTION. It means the
            // submitter corrected this submission while it was on screen, so the
            // remedy is to re-read it — opening the identity picker here would
            // point the reviewer at the wrong problem entirely.
            if (err && err.reason === 'stale_review') {
              setStaleNotice(err.detail || true);
              return;
            }
            // The refusal is the panel's cue: §76 blocks the approval precisely
            // BECAUSE a human has not answered yet, so open the questions rather
            // than leaving a toast the reviewer can do nothing about.
            //
            // ‼️ AN ALLOWLIST, NOT `reason !== 'not_pending'`. The version this
            // replaced opened the identity panel for ANY unrecognised reason,
            // which is the same denylist shape §87/§88 removed from the insert
            // guard: it admits the next addition silently. A future
            // `reason: 'quota_exceeded'` would have sent the reviewer to answer
            // an identity question that was not the problem — §77's failure
            // wearing different clothes.
            //
            // ‼️ AND IT MATTERS MORE FROM §90 ONWARDS. Passing the submitter's
            // hint makes identity_disposition_required a NORMAL workflow rather
            // than a rare exception: a reviewer who corrects a provider name on
            // a hinted submission moves the alias contract onto another entity,
            // and the resolver then refuses to pick. A mis-route here would be
            // seen every day instead of never.
            //
            // The set is pinned against the edge function by
            // scripts/test-approve-reason-routing.mjs, so adding a reason there
            // forces a decision here rather than inheriting one.
            if (err && IDENTITY_VERDICT_REASONS.includes(err.reason)) {
              setIdentityOpen(true);
              setIdentityDetail(err.detail || err.message || null);
            }
          }}>
          <Icon name="check" size={14} stroke={2} />
          {isBusy ? (lang === 'zh' ? '處理中…' : 'Working…') : (lang === 'zh' ? '通過' : 'Approve')}
        </button>
        <button className="btn btn-ghost" style={{ flex: 1, color: 'var(--danger)' }}
          disabled={anyBusy} onClick={onReject}>
          <Icon name="close" size={14} stroke={2} />
          {lang === 'zh' ? '拒絕' : 'Reject'}
        </button>
      </div>
    </div>
  );
}

function AdminReportCard({ item, lang, stats, onOpenHistory, courseTitle, fieldLocked, notes, setNotes, isBusy, anyBusy, mode, onAccept, onReject, onOpenApply }) {
  const fieldLabel = (key) => {
    const f = window.REPORT_FIELDS_BY_KEY?.[key];
    if (!f) return key;
    return lang === 'zh' ? f.zh : f.en;
  };
  return (
    <div className="card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{courseTitle}</div>
        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
          {item.report_type === 'incorrect_field' && (lang === 'zh' ? '欄位資料有誤' : 'Incorrect field')}
          {item.report_type === 'missing_field' && (lang === 'zh' ? '缺少資料' : 'Missing field')}
          {item.report_type === 'duplicate' && (lang === 'zh' ? '重複課程' : 'Duplicate')}
          {item.report_type === 'course_does_not_exist' && (lang === 'zh' ? '此課程已不存在' : 'Does not exist')}
          {item.report_type === 'other' && (lang === 'zh' ? '其他' : 'Other')}
          {item.field_path && <> · <span className="mono">{fieldLabel(item.field_path)}</span></>}
        </div>
      </div>

      {/* This field already carries an admin correction (it's in the course's
          locked_fields), so a prior reviewer resolved it and the scraper
          routine is preserving that value. Flag it so the reviewer can reject
          a duplicate instead of redoing the work. */}
      {fieldLocked && (
        <div style={{
          fontSize: 11.5, padding: '8px 10px', borderRadius: 8, lineHeight: 1.5,
          background: 'var(--primary-soft)', color: 'var(--primary)',
          display: 'flex', gap: 6, alignItems: 'flex-start',
        }}>
          <span style={{ flexShrink: 0, marginTop: 1, display: 'inline-flex' }}>
            <Icon name="shield" size={13} stroke={2} />
          </span>
          <span>
            {lang === 'zh'
              ? '此欄位已有管理員修正紀錄並鎖定中(routine 不會覆寫)。如問題已解決可直接拒絕;若仍要再改,於「套用佇列」套用會覆蓋並維持鎖定。'
              : 'This field already has an admin correction and is locked (the routine won’t overwrite it). If it’s already resolved you can reject; re-applying on the Apply queue will overwrite and keep it locked.'}
          </span>
        </div>
      )}

      {(item.current_value != null || item.suggested_value != null) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 4, fontSize: 12.5 }}>
          {item.current_value != null && (
            <div>
              <span className="muted">{lang === 'zh' ? '目前: ' : 'Current: '}</span>
              <span style={{ textDecoration: 'line-through', color: 'var(--fg-2)' }}>{item.current_value}</span>
            </div>
          )}
          {item.suggested_value != null && (
            <div>
              <span className="muted">{lang === 'zh' ? '建議: ' : 'Suggested: '}</span>
              <strong>{item.suggested_value}</strong>
            </div>
          )}
        </div>
      )}

      {item.reason && (
        <div style={{ fontSize: 12.5, padding: 8, background: 'var(--surface)', borderRadius: 6, color: 'var(--fg-2)' }}>
          “{item.reason}”
        </div>
      )}

      <div>
        <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>
          {lang === 'zh' ? '證據' : 'Evidence'}
        </div>
        <EvidenceList paths={item.evidence_urls} purgedAt={item.evidence_purged_at} purgedFileCount={item.evidence_purged_file_count} lang={lang} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div className="muted-2" style={{ fontSize: 11 }}>
          {lang === 'zh' ? '回報者' : 'Reporter'}: <span className="mono">{item.reporter_user_id?.slice(0, 8)}…</span>
        </div>
        <ContributorStatLine stats={stats} kind="reports" lang={lang} onOpenHistory={onOpenHistory} />
      </div>

      {mode === 'reports' && (
        <>
          <textarea className="field-input" rows={2}
            placeholder={lang === 'zh' ? '管理員備註(可選)' : 'Admin notes (optional)'}
            value={notes} onChange={e => setNotes(e.target.value)} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" style={{ flex: 1 }}
              disabled={anyBusy} onClick={onAccept}>
              <Icon name="check" size={14} stroke={2} />
              {isBusy ? (lang === 'zh' ? '處理中…' : 'Working…') : (lang === 'zh' ? '接受' : 'Accept')}
            </button>
            <button className="btn btn-ghost" style={{ flex: 1, color: 'var(--danger)' }}
              disabled={anyBusy} onClick={onReject}>
              <Icon name="close" size={14} stroke={2} />
              {lang === 'zh' ? '拒絕' : 'Reject'}
            </button>
          </div>
          <div className="muted-2" style={{ fontSize: 10.5 }}>
            {lang === 'zh'
              ? '提示：接受只是標示「我們同意有問題」。實際套用到主資料需在「套用佇列」分頁進行。'
              : 'Note: Accept just marks "we agree there\'s an issue". Actually patching the catalogue happens on the Apply queue tab.'}
          </div>
        </>
      )}

      {mode === 'apply' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" style={{ flex: 1 }}
            disabled={anyBusy} onClick={onOpenApply}>
            <Icon name="check" size={14} stroke={2} />
            {lang === 'zh' ? '套用到主資料…' : 'Apply to catalogue…'}
          </button>
        </div>
      )}
    </div>
  );
}

// Full submission + report history for one contributor. Opened from a
// card's inline stat line when the admin wants to dig deeper than the
// "5 submitted · 4 approved" summary — e.g. "what kind of mistakes does
// this user typically get rejected for?" or "have they ever co-submitted
// a course that turned out to be a duplicate?"
//
// Lazy-loaded (only fires the two queries when the dialog opens),
// capped at 100 rows per side in the db helper, sorted newest-first.
function ContributorHistoryDialog({ userId, lang, onClose }) {
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState({ submissions: [], reports: [], submissionsAtCap: false, reportsAtCap: false });
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const h = await window.DB.fetchContributorHistory(userId);
        if (!cancelled) setData(h);
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Compact status pill — tone keyed off the status string. Centralised
  // so both submissions and reports render with consistent colour intent.
  const StatusPill = ({ s }) => {
    const map = {
      draft:      { tone: 'muted',   en: 'draft',     zh: '草稿' },
      pending:    { tone: 'muted',   en: 'pending',   zh: '待審核' },
      approved:   { tone: 'success', en: 'approved',  zh: '已通過' },
      rejected:   { tone: 'danger',  en: 'rejected',  zh: '已拒絕' },
      duplicate:  { tone: 'muted',   en: 'duplicate', zh: '重複' },
      withdrawn:  { tone: 'muted',   en: 'withdrawn', zh: '已撤回' },
      accepted:   { tone: 'primary', en: 'accepted',  zh: '已接受' },
    };
    const info = map[s] || { tone: 'muted', en: s, zh: s };
    return (
      <span className={'cap-pill tone-' + info.tone} style={{ fontSize: 10 }}>
        {lang === 'zh' ? info.zh : info.en}
      </span>
    );
  };

  const fmtWhen = (iso) => {
    if (!iso) return '';
    return iso.slice(0, 10);
  };

  return (
    <Sheet open={true} onClose={onClose}
      title={lang === 'zh' ? '貢獻者紀錄' : 'Contributor history'}>
      <div style={{ padding: '0 20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="muted" style={{ fontSize: 11.5 }}>
          ID: <span className="mono">{userId}</span>
        </div>

        {/* High-engagement banner. The history cap was hit for at least one
            side, which means this contributor's activity exceeds 100 rows on
            one of the tables. That's a PRODUCT signal — they're among the
            platform's most active users and deserve consideration for elevated
            trust (Phase 3) — not just a technical "paginate this" reminder.
            Phrased positively so the admin acts on the engagement signal first. */}
        {!loading && (data.submissionsAtCap || data.reportsAtCap) && (
          <div style={{
            padding: '10px 12px', borderRadius: 10,
            background: 'color-mix(in oklch, var(--primary) 12%, transparent)',
            color: 'var(--primary)', fontSize: 12.5, lineHeight: 1.5,
            display: 'flex', alignItems: 'flex-start', gap: 8,
          }}>
            <Icon name="check-circ" size={14} stroke={1.8} />
            <div>
              <strong>{lang === 'zh' ? '高活躍貢獻者' : 'Highly active contributor'}</strong>
              <div style={{ marginTop: 2, opacity: 0.85 }}>
                {lang === 'zh'
                  ? `紀錄超過 100 筆 — 此人是平台最活躍的貢獻者之一，值得考慮給予更高 trust tier。同時建議為此 fetch 加上分頁。`
                  : `Over 100 entries on file — one of the platform's most active contributors. Consider for elevated trust tier. (Also: paginate this fetch when convenient.)`}
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="muted" style={{ textAlign: 'center', padding: 16 }}>
            {lang === 'zh' ? '載入中…' : 'Loading…'}
          </div>
        )}
        {error && !loading && (
          <div style={{ fontSize: 12, color: 'var(--danger)' }}>{error}</div>
        )}

        {!loading && !error && (
          <>
            {/* Submissions section */}
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                {lang === 'zh' ? '課程提交' : 'Submissions'} ({data.submissions.length})
              </div>
              {data.submissions.length === 0 ? (
                <div className="muted-2" style={{ fontSize: 11.5, fontStyle: 'italic' }}>
                  {lang === 'zh' ? '無紀錄' : 'None'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {data.submissions.map(s => (
                    <div key={s.id} style={{
                      padding: 8, border: '0.5px solid var(--border)', borderRadius: 8,
                      display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.payload?.title_en || s.payload?.title_zh || (lang === 'zh' ? '(無標題)' : '(no title)')}
                        </div>
                        <StatusPill s={s.status} />
                      </div>
                      <div className="muted-2" style={{ fontSize: 10.5, display: 'flex', gap: 8 }}>
                        <span>{fmtWhen(s.created_at)}</span>
                        {s.source_origin === 'co_submit_from_log' && (
                          <span>· {lang === 'zh' ? '由記錄順帶' : 'co-submit'}</span>
                        )}
                        {s.admin_notes && (
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            · {s.admin_notes}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Reports section */}
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                {lang === 'zh' ? '錯誤回報' : 'Reports'} ({data.reports.length})
              </div>
              {data.reports.length === 0 ? (
                <div className="muted-2" style={{ fontSize: 11.5, fontStyle: 'italic' }}>
                  {lang === 'zh' ? '無紀錄' : 'None'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {data.reports.map(r => (
                    <div key={r.id} style={{
                      padding: 8, border: '0.5px solid var(--border)', borderRadius: 8,
                      display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <span className="mono" style={{ fontSize: 11 }}>{r.field_path || r.report_type || '—'}</span>
                          {r.suggested_value && (
                            <span style={{ marginLeft: 6, color: 'var(--fg-2)' }}>
                              → <strong>{r.suggested_value}</strong>
                            </span>
                          )}
                        </div>
                        <StatusPill s={r.status} />
                      </div>
                      <div className="muted-2" style={{ fontSize: 10.5, display: 'flex', gap: 8 }}>
                        <span>{fmtWhen(r.created_at)}</span>
                        {r.applied_at && (
                          <span style={{ color: 'var(--success)' }}>
                            · {lang === 'zh' ? '已套用' : 'applied'}
                            {r.applied_value && r.applied_value !== r.suggested_value && (
                              <> ({lang === 'zh' ? '改為' : 'as'} <strong>{r.applied_value}</strong>)</>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <button className="btn btn-ghost btn-fill" onClick={onClose}>
          {lang === 'zh' ? '關閉' : 'Close'}
        </button>
      </div>
    </Sheet>
  );
}

// Apply dialog — admin reviews suggested_value, can override before
// committing, and adds a justification. apply-report Edge Function
// enforces the field-path whitelist + idempotency.
//
// The seed from `dlg.suggestedValue` may itself be invalid for the
// field's storage type (e.g. a reporter typed "未滿" into a date field
// before crowdsource-fields.js gained per-field input types). Pre-1.0
// reports may still carry such values; we sanitize before seeding so
// the admin isn't faced with a value-shaped field that silently shows
// blank.
//
// Policy by input kind:
//   date     — keep iff /^\d{4}-\d{2}-\d{2}$/, else drop (browser
//              <input type=date> refuses non-ISO and silently blanks)
//   number,
//   integer  — coerce; drop if not finite (NaN doesn't render)
//   enum     — keep iff seed ∈ options[].value (<select> falls to
//              placeholder otherwise)
//   suggest  — keep as-is. <input list> is a text input with hints,
//              not a closed set; free-form values are valid by design
//   url, text — keep as-is (text columns, no SQL cast, no crash risk)
function seedForFieldInput(fieldKey, seed) {
  if (seed == null || seed === '') return '';
  const meta = window.REPORT_FIELDS_BY_KEY?.[fieldKey];
  const kind = meta?.input?.kind;
  if (kind === 'date') {
    return /^\d{4}-\d{2}-\d{2}$/.test(seed) ? seed : '';
  }
  if (kind === 'number' || kind === 'integer') {
    const n = Number(seed);
    if (!Number.isFinite(n)) return '';
    return kind === 'integer' ? String(Math.trunc(n)) : String(n);
  }
  if (kind === 'enum') {
    return meta.input.options.some(o => o.value === seed) ? seed : '';
  }
  return String(seed);
}

function AdminApplyDialog({ dlg, lang, onClose, onApplied }) {
  // Compute once at mount: was the reporter's value rejected by the
  // type-aware seed guard? Using state — not a render-time comparison —
  // so the warning doesn't reappear when the admin manually clears a
  // valid pre-fill to retype it.
  const cleanedSeed = seedForFieldInput(dlg.fieldPath, dlg.suggestedValue);
  const [seedRejected] = React.useState(() => !!dlg.suggestedValue && cleanedSeed === '');
  const [applyValue, setApplyValue] = React.useState(cleanedSeed);
  const [justification, setJustification] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  // ── Re-keying warning (title corrections only) ─────────────────────────────
  // course_repeat_key is GENERATED from provider + title, and peer counts are
  // aggregated per repeat key across a programme's editions. So applying a
  // title correction silently MOVES this course between rollup groups, taking
  // other nurses' endorsements with it — inheriting a big group's numbers, or
  // stranding its own. apply_report is admin-gated, so a human is always in the
  // loop; this is what that human otherwise cannot see.
  //
  // Advisory only: the DB's generated column is authoritative and recomputes on
  // write. courseRepeatKey() is the existing JS twin of that expression — good
  // enough to warn with, deliberately not used to display any count.
  const rekey = React.useMemo(() => {
    if (dlg.fieldPath !== 'title_en' && dlg.fieldPath !== 'title_zh') return null;
    if (!applyValue) return null;
    const pool = (typeof window.allSearchableCourses === 'function')
      ? window.allSearchableCourses() : (window.COURSES || []);
    const course = pool.find(c => String(c.id) === String(dlg.courseId));
    if (!course || typeof window.courseRepeatKey !== 'function') return null;
    const before = window.courseRepeatKey(course);
    // Same course with the corrected title, and WITHOUT the stored key — that
    // column is what the DB will recompute, so reusing it would just echo the
    // old answer back.
    const after = window.courseRepeatKey({
      ...course,
      title: { ...(course.title || {}), [dlg.fieldPath === 'title_zh' ? 'zh' : 'en']: applyValue },
      raw: { ...(course.raw || {}), course_repeat_key: null, repeat_key_override: null },
    });
    if (!before || !after || before === after) return null;
    const size = (key) => pool.filter(c => window.courseRepeatKey(c) === key).length;
    return { before, after, beforeSize: size(before), afterSize: size(after) };
  }, [dlg.fieldPath, dlg.courseId, applyValue]);

  const submit = async (mode) => {
    if (busy) return;
    setBusy(true);
    try {
      const v = mode === 'clear' ? null : applyValue;
      const { error } = await window.DB.adminApplyReport(dlg.reportId, v, justification || null);
      if (error) {
        showVitalToast(lang === 'zh' ? `失敗: ${error.message}` : `Failed: ${error.message}`);
        setBusy(false);
        return;
      }
      showVitalToast(lang === 'zh' ? '已套用' : 'Applied');
      onApplied();
    } catch (e) {
      showVitalToast(lang === 'zh' ? `失敗: ${e?.message || e}` : `Failed: ${e?.message || e}`);
      setBusy(false);
    }
  };

  return (
    <Sheet open={true} onClose={onClose}
      title={lang === 'zh' ? '套用到主資料' : 'Apply to catalogue'}>
      <div style={{ padding: '0 20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="muted" style={{ fontSize: 12 }}>
          {dlg.courseTitle} · <span className="mono">{dlg.fieldPath}</span>
        </div>

        {dlg.currentValue != null && (
          <div className="muted-2" style={{ fontSize: 12 }}>
            {lang === 'zh' ? '目前值: ' : 'Current value: '}
            <span style={{ textDecoration: 'line-through' }}>{dlg.currentValue}</span>
          </div>
        )}

        <div className="field">
          <div className="field-label">{lang === 'zh' ? '套用值' : 'Apply value'}</div>
          <ReportFieldValueInput
            fieldKey={dlg.fieldPath}
            value={applyValue}
            onChange={setApplyValue}
            lang={lang}
            id="admin-apply"
            placeholder={lang === 'zh' ? '可編輯' : 'Editable'} />
          {seedRejected && !applyValue && (
            <div className="muted-2" style={{ fontSize: 10.5, marginTop: 4, color: 'var(--danger)' }}>
              {lang === 'zh'
                ? `回報者填寫的「${dlg.suggestedValue}」不符合此欄位格式,請改填正確值。`
                : `Reporter's value "${dlg.suggestedValue}" doesn't match this field's format — enter the correct value.`}
            </div>
          )}
          {rekey && (
            <div style={{ fontSize: 11, lineHeight: 1.45, marginTop: 6, padding: '8px 10px',
                          borderRadius: 8, background: 'var(--bg-2)', color: 'var(--fg-2)' }}>
              <strong style={{ color: 'var(--warning)' }}>
                {lang === 'zh' ? '⚠ 會改變同系列分組' : '⚠ Changes the repeat group'}
              </strong>
              <div style={{ marginTop: 3 }}>
                {rekey.afterSize === 0
                  ? (lang === 'zh'
                      ? `此課程會離開現時 ${rekey.beforeSize} 個版本的分組，自成一組。課程卡上的同儕評價按分組累計，所以佢會失去原本顯示的數字。`
                      : `This course leaves its current group of ${rekey.beforeSize} and stands alone. Peer counts on the card accumulate per group, so it loses the numbers it was showing.`)
                  : (lang === 'zh'
                      ? `此課程會離開現時 ${rekey.beforeSize} 個版本的分組，併入另一個已有 ${rekey.afterSize} 個版本的分組。課程卡上的同儕評價按分組累計，所以呢個更正亦會連帶改變佢顯示的數字 — 請先確認呢個真係同一個課程系列。`
                      : `This course leaves its group of ${rekey.beforeSize} and joins one that already has ${rekey.afterSize} edition(s). Peer counts on the card accumulate per group, so this correction also changes the numbers it shows — confirm these really are the same programme.`)}
              </div>
            </div>
          )}
          <div className="muted-2" style={{ fontSize: 10.5, marginTop: 4 }}>
            {lang === 'zh'
              ? '預設帶入回報者的建議值,你可以改寫(例如官方記錄是其他數字)。'
              : "Pre-filled with the reporter's suggestion. Override if your own check found a different value."}
          </div>
        </div>

        <div className="field">
          <div className="field-label">
            {lang === 'zh' ? '理由' : 'Justification'} <span className="muted-2">({lang === 'zh' ? '可選' : 'optional'})</span>
          </div>
          <textarea className="field-input" rows={2}
            value={justification} onChange={e => setJustification(e.target.value)}
            placeholder={lang === 'zh' ? '例:已對照 NCHK 官方記錄' : 'e.g. verified against NCHK record'} />
        </div>

        {/* Lock behaviour. Applying always adds this column to the course's
            locked_fields so the daily scraper routine stops overwriting it.
            If it's already locked, applying replaces the existing admin value
            and keeps the lock. */}
        <div className="muted-2" style={{ fontSize: 10.5, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <span style={{ flexShrink: 0, marginTop: 1, display: 'inline-flex' }}>
            <Icon name="shield" size={12} stroke={2} color="var(--primary)" />
          </span>
          <span>
            {dlg.alreadyLocked
              ? (lang === 'zh'
                  ? '此欄位已被鎖定。套用會覆蓋現有的管理員數值,並維持鎖定(routine 不會覆寫)。'
                  : 'This field is already locked. Applying overwrites the existing admin value and keeps it locked (the routine won’t overwrite it).')
              : (lang === 'zh'
                  ? '套用後此欄位會被鎖定,每日 routine 不會再覆寫。'
                  : 'Once applied, this field is locked — the daily routine will no longer overwrite it.')}
          </span>
        </div>

        <LoadingButton className="btn btn-primary btn-fill"
          isLoading={busy}
          disabled={!applyValue.trim()}
          loadingLabel={lang === 'zh' ? '套用中…' : 'Applying…'}
          leftIcon={<Icon name="check" size={16} stroke={2.2} />}
          onClick={() => submit('apply')}>
          {lang === 'zh' ? '套用此值' : 'Apply this value'}
        </LoadingButton>

        <button className="btn btn-ghost btn-fill"
          disabled={busy}
          onClick={() => submit('clear')}
          title={lang === 'zh' ? '清空此欄位' : 'Set this field to NULL'}>
          {lang === 'zh' ? '改為清空欄位' : 'Set field to NULL'}
        </button>

        <button className="btn btn-ghost btn-fill" disabled={busy} onClick={onClose}>
          {lang === 'zh' ? '取消' : 'Cancel'}
        </button>
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Edit Profile — standalone sub-page for an already-onboarded user to change
// the fields onboarding captured (display name, sex, workplace, role, cycle
// dates, required points). Referral source is deliberately NOT here: it's a
// one-time acquisition signal collected only in the onboarding ProfileStep.
// Reuses onboarding's field building blocks (initialForm / HospitalPicker /
// SEX_OPTIONS / ROLE_OPTIONS / ROLE_REQUIRED_POINTS / RequiredPointsInfo) so
// the two stay in lock-step. Unlike OnboardingFlow.finish() it does NOT touch
// `onboarded` or `baseline_points` — upsert only writes the columns in the
// patch, leaving the rest of the row intact.
// Map a set_community_handle RPC error to a friendly bilingual message.
function handleErrMessage(error, lang) {
  const m = (error && error.message ? error.message : '').toLowerCase();
  if (m.includes('taken') || (error && error.code === '23505')) return lang === 'zh' ? '此化名已被使用' : 'That handle is already taken';
  if (m.includes('reserved')) return lang === 'zh' ? '此化名為保留字,請換一個' : 'That handle is reserved';
  if (m.includes('2-20') || m.includes('letters')) return lang === 'zh' ? '化名須為 2–20 個英文字母、數字或底線' : 'Handle must be 2–20 letters, digits or underscore';
  return lang === 'zh' ? '無法設定化名,請稍後再試' : 'Could not set handle, please try again';
}

function ProfileEditSection({ title, hint, sectionRef, collapsible = false, defaultOpen = false, children }) {
  if (collapsible) {
    return (
      <details className="profile-edit-section profile-edit-disclosure" ref={sectionRef} open={defaultOpen || undefined}>
        <summary className="profile-edit-section-head">
          <span>
            <h2>{title}</h2>
            {hint ? <p>{hint}</p> : null}
          </span>
          <span className="profile-edit-disclosure-icon" aria-hidden="true">⌄</span>
        </summary>
        <div className="profile-edit-section-body">{children}</div>
      </details>
    );
  }
  return (
    <section className="profile-edit-section" ref={sectionRef}>
      <div className="profile-edit-section-head">
        <h2>{title}</h2>
        {hint ? <p>{hint}</p> : null}
      </div>
      <div className="profile-edit-section-body">{children}</div>
    </section>
  );
}

function EditProfilePage({ session, lang, profile, records = [], focusTarget, onFocusConsumed, onBack, onSaved }) {
  const userId = session.user.id;
  const email  = session.user.email;
  // Seed once from the live profile prop. initialForm also back-matches a
  // legacy free-text hospital to a key + sector so the dropdown shows the
  // user's workplace selected rather than blank.
  const initial = React.useMemo(() => initialForm(session, profile), [session, profile]);
  const [form, setForm] = React.useState(initial);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const officialNameRef = React.useRef(null);
  const professionalSectionRef = React.useRef(null);

  // Readiness gaps land on the exact professional field that resolves them.
  // Registration-number focus is completed inside LicenceCard because its
  // input is conditionally mounted when the card expands.
  React.useEffect(() => {
    if (!focusTarget) return;
    const timer = setTimeout(() => {
      if (focusTarget === 'name') {
        officialNameRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        officialNameRef.current?.focus?.({ preventScroll: true });
      } else {
        professionalSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      }
      onFocusConsumed?.();
    }, focusTarget === 'regNo' ? 220 : 100);
    return () => clearTimeout(timer);
  }, [focusTarget, onFocusConsumed]);

  // ── Community public settings — opt-in, seeded straight from profile (NOT
  // via onboarding's initialForm; these are never collected at cold start). ──
  const [attrVis, setAttrVis]         = React.useState(profile?.attribution_visibility || 'private');
  const [handle, setHandle]           = React.useState(profile?.community_handle || '');
  const [avatarHue, setAvatarHue]     = React.useState(typeof profile?.avatar_hue === 'number' ? profile.avatar_hue : null);
  const [avatarEmoji, setAvatarEmoji] = React.useState(profile?.avatar_emoji || null);
  // Held as the ARRAY now, not a comma-joined line: MembershipPicker owns both
  // halves of the input (chips + free text) and hands back the merged, already
  // sanitised list. Seeded through the same sanitiser so a legacy row that is
  // over-length or duplicated cannot make Save look dirty on open.
  const [membershipsList, setMembershipsList] = React.useState(
    () => (window.sanitiseMemberships
      ? window.sanitiseMemberships(profile?.memberships)
      : (Array.isArray(profile?.memberships) ? profile.memberships : [])),
  );
  const HANDLE_RE = /^[A-Za-z0-9_]{2,20}$/;
  const handleTrimmed = (handle || '').trim();
  // A handle is only required when the user chose to display one.
  const handleValid = attrVis !== 'handle' || HANDLE_RE.test(handleTrimmed);

  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  // Default-date hygiene — this profile still carries the untouched onboarding
  // seed dates (2024-01-01 → 2026-12-31). Until the user confirms them (or
  // edits them away), those dates must be treated as unreal: the sweeps skip
  // them and this banner asks for the real ones. 「日期正確」 stamps
  // prefs.dates_confirmed_at, which retires the state everywhere at once.
  const [datesConfirmed, setDatesConfirmed] = React.useState(false);
  const showSeedBanner = !datesConfirmed &&
    !!(window.profileHasSeedDates && window.profileHasSeedDates(profile));
  const confirmSeedDates = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await window.DB.setPrefs(userId, {
        dates_confirmed_at: new Date().toISOString().slice(0, 10),
      });
      setDatesConfirmed(true);
      if (data) onSaved && onSaved(data);
      if (window.showToast) window.showToast(lang === 'zh' ? '已確認日期' : 'Dates confirmed', { tone: 'success' });
    } finally {
      setBusy(false);
    }
  };
  // Whether the user actively changed the workplace picker this session. The
  // back-match in initialForm is a *display convenience only* — it can miss
  // (legacy free text not in the canonical list → blank dropdown) or, worse,
  // mis-match via matchHospital's loose substring logic. So we only ever WRITE
  // the hospital columns when the user explicitly touches the picker. An
  // untouched workplace is left exactly as stored — no forced re-pick, no
  // silent migration of a fuzzy guess.
  const [workplaceTouched, setWorkplaceTouched] = React.useState(false);

  // The user's workplace as it sits in the DB. A sector alone counts — a
  // 'text' sector (NGO / clinic / home / school / other) stores an OPTIONAL
  // organisation name, so "sector chosen, name blank" is a complete answer.
  const hadWorkplace = !!(profile?.hospital_key || profile?.hospital_en ||
    profile?.hospital_zh || profile?.hospital_sector);
  // For a LEGACY row this is free text with no canonical key; we surface it
  // next to the picker so it never looks "lost" when the back-match came up
  // blank. Excluded for 'text' sectors — there the free text is the answer, not
  // an unmigrated leftover, and the picker's own input already shows it.
  const storedKind = window.sectorKind ? window.sectorKind(profile?.hospital_sector) : null;
  const legacyFreeText = (storedKind !== 'text' && !profile?.hospital_key &&
      (profile?.hospital_en || profile?.hospital_zh))
    ? (lang === 'zh'
        ? (profile.hospital_zh || profile.hospital_en)
        : (profile.hospital_en || profile.hospital_zh))
    : '';

  // Validation:
  //  • name is always required.
  //  • the workplace must be a COMPLETE selection (a hospital for 'list'
  //    sectors, any 'text' sector on its own) only when the user is actively
  //    setting it, OR when nothing was ever on file. A legacy user whose free
  //    text didn't back-match can still edit other fields without being trapped
  //    behind a blank dropdown.
  // ⚠️ Never gate this on hospital_key alone — that is what stopped NGO and
  //    clinic nurses from saving ANY field, including their renewal date.
  const nameOk = (form.display_name || '').trim().length > 0;
  const workplaceRequired = workplaceTouched || !hadWorkplace;
  const workplaceOk = workplaceSelected(form);
  const hospitalOk = workplaceRequired ? workplaceOk : true;
  // Only persist hospital columns when the user actually changed the picker.
  const writeWorkplace = workplaceTouched && workplaceOk;
  const canSave = nameOk && hospitalOk && handleValid;
  // Why Save is disabled — rendered next to the button. A silently greyed-out
  // button reads as "the app is broken", which is exactly how the workplace
  // gate above was experienced before it was fixed.
  const blockReason = nameOk
    ? (hospitalOk
        ? (handleValid ? '' : (lang === 'zh' ? '化名須為 2–20 個英文字母、數字或底線' : 'Handle must be 2–20 letters, digits or underscore'))
        : (lang === 'zh' ? '請選擇工作單位（找不到？可選「其他」）' : 'Select a workplace (no match? choose "Other")'))
    : (lang === 'zh' ? '請輸入顯示名稱' : 'Enter a display name');

  // Only enable Save when something actually changed, so the button isn't a
  // no-op write. Compare the editable fields against the seed snapshot — but
  // count the workplace only when it'll actually be written, so a stable
  // back-matched key (display-only) doesn't masquerade as a pending change.
  const dirty = React.useMemo(() => {
    const keys = ['display_name', 'official_name_en', 'official_name_zh', 'sex', 'license_no',
      'role_en', 'role_zh', 'cycle_start', 'renewal_date', 'required_points', 'first_pc',
      // Dual-registration second certificate (flat license2_* keys).
      'has_license2', 'license2_role_en', 'license2_role_zh', 'license2_license_no',
      'license2_cycle_start', 'license2_renewal_date', 'license2_required_points',
      'license2_baseline_points'];
    const baseChanged = keys.some(k => String(form[k] ?? '') !== String(initial[k] ?? ''));
    const workplaceChanged = writeWorkplace && (
      String(form.hospital_key ?? '') !== String(initial.hospital_key ?? '') ||
      String(form.hospital_sector ?? '') !== String(initial.hospital_sector ?? '') ||
      // Compare the free-text name as it WILL be stored, so trailing spaces or
      // stripped control characters don't register as a pending change.
      String(window.sanitizeWorkplaceName ? window.sanitizeWorkplaceName(form.hospital_name) : '') !==
      String(window.sanitizeWorkplaceName ? window.sanitizeWorkplaceName(initial.hospital_name) : '')
    );
    return baseChanged || workplaceChanged;
  }, [form, initial, writeWorkplace]);

  const communityDirty =
    attrVis !== (profile?.attribution_visibility || 'private') ||
    handleTrimmed !== (profile?.community_handle || '') ||
    (avatarHue ?? null) !== (typeof profile?.avatar_hue === 'number' ? profile.avatar_hue : null) ||
    (avatarEmoji || null) !== (profile?.avatar_emoji || null);

  // Memberships — the input the route planner needs to price a course at the
  // tier she actually gets. Stored as text[] (see
  // supabase-migration-course-semantics), holding provider codes from the
  // picker's chips plus any free text she added; membershipMatchesCourse in
  // data.js reads both shapes.
  //
  // Dirty check compares against the SANITISED stored value for the same reason
  // the state is seeded that way: otherwise a legacy row with a trailing space
  // would mark the form dirty the moment it opened.
  const membershipsDirty = JSON.stringify(membershipsList)
    !== JSON.stringify(window.sanitiseMemberships
      ? window.sanitiseMemberships(profile?.memberships)
      : (profile?.memberships || []));

  const save = async () => {
    if (!canSave || busy) return;
    setBusy(true); setErr('');
    try {
      // Community handle goes through its own RPC (format + reserved + unique
      // checks) BEFORE the profile upsert, so a rejected handle aborts cleanly
      // and never half-writes. The client NEVER sends community_handle through
      // upsertProfile — the RPC is the sole write path (DB CHECK is the backstop).
      if (handleTrimmed !== (profile?.community_handle || '')) {
        const hres = await window.DB.setCommunityHandle(handleTrimmed || null);
        if (hres && hres.error) { setErr(handleErrMessage(hres.error, lang)); setBusy(false); return; }
      }
      const displayName = (form.display_name || '').trim() || email.split('@')[0];
      const shortName = displayName.split(' ')[0];
      // Preserve any existing prefs and fold in the current language choice,
      // matching onboarding's finish() so the two write the same shape.
      const existing = await window.DB.getProfile(userId).catch(() => null);
      const mergedPrefs = { ...(existing?.prefs || {}), lang };
      // Dual-registration: reassemble the flat form keys into the license2 jsonb
      // (its own role + cycle). Read-modify-write — when a second certificate is
      // present, merge over the stored blob so any field not surfaced in this
      // form is preserved; when the user removed it, write null.
      const license2New = license2FromForm(form);
      const license2Patch = license2New
        ? { ...(existing?.license2 && typeof existing.license2 === 'object' ? existing.license2 : {}), ...license2New }
        : null;
      // Manual rollover: the new certificate period starts AFTER the stored one
      // ended — the user renewed by editing dates directly instead of via the
      // rollover sheet. Same two effects as RolloverSheet: archive the closing
      // cycle to cycle_history, and end the first-PC exemption (otherwise the
      // 0-point exemption leaks into a cycle where CNE applies). Unlike the
      // sheet, baseline_points is left alone — a direct edit may be a data
      // correction, so only the safe, non-lossy actions apply.
      // ‼️ TWO LICENCES, TWO INTENTS, TWO TOKENS. The old name `manualRollover`
      // encoded an assumption — that only the primary certificate ever rolls
      // over — and the secondary path silently inherited it: a nurse who edited
      // her second registration's dates past their renewal boundary advanced the
      // cycle with no archive at all, losing the closed period. That predates
      // B5.3, but B5.3's invariant is that NO practising licence's cycle may be
      // advanced outside the rollover service, so it is in scope now.
      //
      // Both tokens come from the snapshot this form was OPENED with. Neither is
      // ever refreshed from a later read — see the dual-rollover note below.
      const prevRenewal = existing?.renewal_date || null;
      const existingL2 = (existing?.license2 && typeof existing.license2 === 'object')
        ? existing.license2 : null;
      const prevL2Renewal = existingL2?.renewal_date || null;
      const primaryManualRollover = !!(prevRenewal && form.cycle_start &&
        String(form.cycle_start) > String(prevRenewal));
      const secondaryManualRollover = !!(prevL2Renewal && form.license2_cycle_start &&
        String(form.license2_cycle_start) > String(prevL2Renewal));
      // ‼️ THIS IS NO LONGER A PATCH FIELD. It is the payload for the rollover
      // RPC, which owns the archive and the date advance. Building it here and
      // spreading it into the profile patch is what B5.3b removes.
      let rolloverCycle = null;
      if (primaryManualRollover) {
        const hist = Array.isArray(existing?.cycle_history) ? existing.cycle_history : [];
        const closedLic = (window.profileLicenses(existing) || [])[0];
        const closedEarned = closedLic
          ? (Number(closedLic.baseline) || 0) + cycleRecords(records, closedLic.cycleStart, closedLic.renewalDate)
              .reduce((s, r) => s + (Number(r.points) || 0), 0)
          : 0;
        rolloverCycle = {
          license: 'primary',
          role_en: existing?.role_en || 'Registered Nurse',
          role_zh: existing?.role_zh || '註冊護士',
          license_no: existing?.license_no || null,
          cycle_start: existing?.cycle_start || null,
          renewal_date: prevRenewal,
          required_points: closedLic ? closedLic.required : (Number(existing?.required_points) || 45),
          baseline_points: Number(existing?.baseline_points) || 0,
          first_pc: !!existing?.first_pc,
          earned_total: closedEarned,
          closed_at: new Date().toISOString().slice(0, 10),
        };
        void hist;   // read only to compute closedEarned; the server owns the array
      }

      // ‼️ THE CLOSING SNAPSHOT COMES FROM `existing`, NEVER FROM THE FORM.
      // What is being archived is the cycle that is ENDING, so its role, number,
      // dates and baseline are the stored ones. Building it from the form would
      // file the new period's facts under the old period's entry.
      let secondaryRolloverCycle = null;
      if (secondaryManualRollover && existingL2) {
        const closedEarnedL2 = (Number(existingL2.baseline_points) || 0)
          + cycleRecords(records, existingL2.cycle_start, prevL2Renewal)
              .reduce((sum, r) => sum + (Number(r.points) || 0), 0);
        secondaryRolloverCycle = {
          license: 'secondary',
          role_en: existingL2.role_en || 'Registered Nurse',
          role_zh: existingL2.role_zh || '註冊護士',
          license_no: existingL2.license_no || null,
          cycle_start: existingL2.cycle_start || null,
          renewal_date: prevL2Renewal,
          required_points: Number(existingL2.required_points) || 45,
          baseline_points: Number(existingL2.baseline_points) || 0,
          first_pc: !!existingL2.first_pc,
          earned_total: closedEarnedL2,
          closed_at: new Date().toISOString().slice(0, 10),
        };
      }
      const patch = {
        display_name: displayName,
        // Document identity is intentionally separate from display_name. A
        // nickname edit must never silently alter the name printed on Annex B.
        name_en: (form.official_name_en || '').trim() || null,
        name_zh: (form.official_name_zh || '').trim() || null,
        short_name_en: shortName,
        sex: form.sex || null,
        license_no: (form.license_no || '').trim() || null,
        // referral_source is a one-time onboarding acquisition signal, not
        // editable here — omitting it leaves the stored value untouched.
        // Workplace is written ONLY when the user explicitly changed it (see
        // workplaceTouched). Omitting these keys leaves the stored values
        // intact — so a name-only edit never rewrites/migrates a legacy
        // free-text workplace based on a fuzzy back-match. workplacePatch
        // (onboarding.jsx) is the single assembler, shared with onboarding, and
        // sanitises any free-text organisation name on the way out.
        ...(writeWorkplace ? (workplacePatch(form) || {}) : {}),
        role_en: form.role_en,
        role_zh: form.role_zh,
        required_points: Number(form.required_points) || 45,
        // ‼️ ROLLOVER-OWNED FIELDS ARE ABSENT BY CONSTRUCTION, NOT BY HABIT.
        // When this save is a rollover, cycle_start / renewal_date / first_pc /
        // cycle_history belong to archive_license_cycle() and are written by it,
        // in one transaction, against a stable licence identity. Including them
        // here would race the RPC and could overwrite what it just wrote with
        // the stale values this form was opened with.
        //
        // Outside a rollover these are ordinary corrections and stay editable —
        // the rule is not "the profile may never touch cycle dates", it is "the
        // profile may not advance a regulatory cycle".
        //
        // A future refactor that spreads the whole form object into this patch
        // silently undoes B5.3. scripts/test-license-rollover-client.mjs asserts
        // the absence, so that change fails a test rather than a later audit.
        ...(primaryManualRollover ? {} : {
          cycle_start: form.cycle_start,
          renewal_date: form.renewal_date,
          first_pc: !!form.first_pc,
        }),
        // ‼️ PIN, DO NOT DELETE. license2 is a nested object and its
        // `cycle_start` is the secondary slot's OCCUPANCY MARKER — sending a
        // license2 without it would read as "this licence no longer exists",
        // archiving the identity and unbinding the slot, after which the
        // rollover RPC could not find an identity to attribute the cycle to.
        //
        // So during a secondary rollover the object is sent whole, with the
        // rollover-owned values held at their STORED ones. Role, licence number
        // and required points still take the form's edits. The invariant is not
        // "these keys are absent from the JSON" — for a nested object that would
        // be actively harmful — it is that a general PATCH cannot advance them
        // to the next period.
        license2: (license2Patch && secondaryManualRollover && existingL2)
          ? { ...license2Patch,
              cycle_start: existingL2.cycle_start,
              renewal_date: existingL2.renewal_date,
              baseline_points: existingL2.baseline_points }
          : license2Patch,
        // Community public settings (handle excluded — see the RPC above).
        attribution_visibility: attrVis,
        avatar_hue: avatarHue,
        avatar_emoji: avatarEmoji,
        // Empty list stores as [] rather than null so "I checked and I belong to
        // none" is distinguishable from "never asked" — the planner treats the
        // latter as unknown and must keep labelling costs "from".
        //
        // Sanitised again at the write boundary even though the picker already
        // did it: this is the last line before PostgREST, and the CHECK
        // constraint on the other side (supabase-migration-memberships-bounds)
        // rejects with a 23514 the UI could not explain.
        memberships: window.sanitiseMemberships
          ? window.sanitiseMemberships(membershipsList) : membershipsList,
        prefs: mergedPrefs,
        // onboarded + baseline_points intentionally omitted — upsert leaves
        // unlisted columns untouched, so an edit can't reset either.
      };
      const { data, error } = await window.DB.upsertProfile(userId, patch);
      // ‼️ ORDINARY PATCH FIRST, ROLLOVER SECOND, AND THAT ORDER IS DELIBERATE.
      // If the same save also changed the registration type, the identity
      // trigger must see it BEFORE the cycle is archived — otherwise a cycle
      // could be filed against an identity that the very same save has just
      // called into question. Landing the profile first means the rollover then
      // fails closed on needs_resolution, which is the correct outcome.
      //
      // ‼️ AND THEY ARE NOT ONE TRANSACTION. Two network operations cannot be
      // made atomic by wishing, so partial success is a real state with its own
      // message rather than something papered over with a second PATCH.
      // ‼️ SEQUENTIAL, PRIMARY THEN SECONDARY, AND NEVER IN PARALLEL. Both RPCs
      // update the same profile row and each refetches it; running them together
      // would have them racing over one row for no gain on a two-item list.
      //
      // ‼️ EACH KEEPS ITS OWN ORIGINAL TOKEN. The second call must NOT adopt a
      // renewal date refetched after the first succeeded — that would launder a
      // genuinely stale secondary intent into an accepted one, which is the
      // exact failure the token exists to prevent.
      let latestProfile = data;
      const rolloverFailures = [];
      if (!error) {
        const jobs = [];
        if (primaryManualRollover && rolloverCycle) {
          jobs.push({ slot: 'primary', token: prevRenewal, cycle: rolloverCycle,
            nextStart: form.cycle_start, nextRenewal: form.renewal_date });
        }
        if (secondaryManualRollover && secondaryRolloverCycle) {
          jobs.push({ slot: 'secondary', token: prevL2Renewal, cycle: secondaryRolloverCycle,
            nextStart: form.license2_cycle_start, nextRenewal: form.license2_renewal_date });
        }
        for (const job of jobs) {
          const res = await window.DB.performLicenceRollover({
            userId, slot: job.slot,
            expectedRenewalDate: job.token,
            cycle: job.cycle,
            nextCycleStart: job.nextStart,
            nextRenewalDate: job.nextRenewal,
          });
          if (res.profile) latestProfile = res.profile;
          if (res.status !== 'completed' && res.status !== 'alreadyCompletedAfterRefetch') {
            rolloverFailures.push({ slot: job.slot, status: res.status });
          }
        }
      }
      // Partial success is a real state: the profile PATCH and each RPC are
      // separate network operations and cannot be made atomic by wishing. Say
      // what happened and show canonical state — never compensate with a second
      // PATCH, which would put the general path back in charge of cycle fields.
      if (rolloverFailures.length) {
        const isZh = lang === 'zh';
        const which = (slot) => slot === 'secondary'
          ? (isZh ? '第二張執業證書' : 'your second certificate')
          : (isZh ? '執業證書' : 'your certificate');
        setErr((isZh ? '個人資料已儲存，但執業週期未更新：' : 'Your profile was saved, but the practising cycle was not updated: ')
          + rolloverFailures.map((f) => `${which(f.slot)} — ${rolloverMessage(f.status, isZh)}`).join(' '));
        onSaved && onSaved(latestProfile || data);
        return;
      }
      if (latestProfile && latestProfile !== data) Object.assign(data || {}, latestProfile);
      if (error) {
        setErr(error.message || (lang === 'zh' ? '無法儲存,請稍後再試。' : 'Could not save. Please try again.'));
        return;
      }
      onSaved && onSaved(data);
      if (window.showToast) {
        window.showToast(
          primaryManualRollover && existing?.first_pc
            ? (lang === 'zh' ? '已開始新週期——首證豁免已結束' : 'New cycle started — the first-certificate exemption has ended')
            : (lang === 'zh' ? '已更新個人資料' : 'Profile updated'),
          { tone: 'success' });
      }
      onBack && onBack();
    } catch (e) {
      setErr(e?.message || (lang === 'zh' ? '無法儲存,請稍後再試。' : 'Could not save. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="scroll fade-in profile-edit-page">
      <ScreenHeader
        title={lang === 'zh' ? '編輯個人資料' : 'Edit profile'}
        onBack={onBack}
        lang={lang}
        sticky
        inlineBack
      />

      <div className="profile-edit-content">
        <div className="auth-form profile-edit-stack">
          <ProfileEditSection
            title={lang === 'zh' ? '基本資料' : 'Basic information'}
            hint={lang === 'zh' ? '這些資料用於 App 內顯示。' : 'These details are used inside the app.'}>
          {/* No autoFocus: on iOS focusing a field on mount pops the keyboard
              the moment Edit Profile lands — jarring, and it shoves the form
              up under the sub-16px label (triggering auto-zoom). The user
              taps the field when they actually want to edit. Mirrors the
              admin-secret input. */}
          <AuthField label={lang === 'zh' ? '顯示名稱' : 'Display name'}
            value={form.display_name} onChange={v => set('display_name', v)}
            placeholder={lang === 'zh' ? '陳大文' : 'Wing Yi Chan'} />

          {/* Sex */}
          <div className="onb-field">
            <div className="auth-label mono">{lang === 'zh' ? '性別' : 'Sex'}</div>
            <div className="onb-roles">
              {SEX_OPTIONS.map(o => (
                <button key={o.id} type="button"
                  className={'onb-chip' + (form.sex === o.id ? ' on' : '')}
                  onClick={() => set('sex', o.id)}>
                  {lang === 'zh' ? o.zh : o.en}
                </button>
              ))}
            </div>
          </div>
          </ProfileEditSection>

          {/* Practising certificate — role + cycle dates + required points folded
              into one COLLAPSIBLE card (summary when collapsed, editors when
              expanded), so the page reads as a tidy summary by default. */}
          <ProfileEditSection
            sectionRef={professionalSectionRef}
            title={lang === 'zh' ? '護士登記與執業證書' : 'Nursing registration'}
            hint={lang === 'zh'
              ? '至少填寫一個官方姓名；證書資料會預填至 NCHK 附件，不會公開顯示。'
              : 'Add at least one official name; certificate details pre-fill NCHK annexes and are never public.'}>
          <div className="profile-edit-official-names">
            <AuthField
              label={lang === 'zh' ? '官方英文姓名' : 'Official name in English'}
              value={form.official_name_en}
              onChange={v => set('official_name_en', v)}
              placeholder="CHAN Tai Man"
              inputRef={officialNameRef}
            />
            <AuthField
              label={lang === 'zh' ? '官方中文姓名（如適用）' : 'Official name in Chinese (if applicable)'}
              value={form.official_name_zh}
              onChange={v => set('official_name_zh', v)}
              placeholder="陳大文"
            />
          </div>
          <div className="onb-field" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="auth-label mono">{lang === 'zh' ? '執業證書' : 'Practising certificate'}</div>
            {showSeedBanner ? (
              <div className="card" style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10,
                border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--fg-2)' }}>
                  {lang === 'zh'
                    ? '呢個似乎係預設日期——請確認你的真實證書日期，我哋先可以準時提你續期。'
                    : 'These look like the default dates — please confirm your real certificate dates so renewal reminders arrive on time.'}
                </div>
                <button type="button" className="btn btn-ghost" disabled={busy}
                  onClick={confirmSeedDates}
                  style={{ alignSelf: 'flex-start', padding: '7px 14px', fontSize: 12.5 }}>
                  {lang === 'zh' ? '日期正確' : 'These dates are correct'}
                </button>
              </div>
            ) : null}
            <LicenceCard lang={lang} form={form} set={set}
              roleEnKey="role_en" roleZhKey="role_zh" licenseNoKey="license_no" requiredKey="required_points"
              startKey="cycle_start" endKey="renewal_date" defaultOpen={false}
              focusLicenseNumber={focusTarget === 'regNo'}
              exempt={!!form.first_pc} />

            {/* First-PC exemption — a first-time applicant's first certificate is
                CNE-exempt (NCHK). Flips the mandatory framing off via first_pc. */}
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', marginTop: -2 }}>
              <input type="checkbox" checked={!!form.first_pc}
                onChange={e => set('first_pc', e.target.checked)}
                style={{ marginTop: 3, flexShrink: 0, width: 16, height: 16, accentColor: 'var(--primary)' }} />
              <span style={{ fontSize: 13.5, lineHeight: 1.45, color: 'var(--fg-2)' }}>
                {lang === 'zh'
                  ? '這是我的第一張執業證書（首次申請 — 首個證書暫免 CNE 要求）'
                  : 'This is my first practising certificate (first-time applicant — CNE not required for the first certificate)'}
              </span>
            </label>

            {/* Second certificate — a prominent add affordance directly below the
                first card so a dual-registered nurse can't miss it; once added it
                becomes its own collapsible card (with a Remove control). */}
            {form.has_license2 ? (
              <LicenceCard lang={lang} form={form} set={set}
                roleEnKey="license2_role_en" roleZhKey="license2_role_zh" licenseNoKey="license2_license_no" requiredKey="license2_required_points"
                startKey="license2_cycle_start" endKey="license2_renewal_date"
                // Open on the field the card actually ASKS for — the expiry.
                // The start is derived from it, so keying this off the start
                // would leave the card collapsed on a half-filled certificate.
                defaultOpen={!form.license2_renewal_date}
                onRemove={() => {
                  set('has_license2', false);
                  ['license2_role_en', 'license2_role_zh', 'license2_license_no', 'license2_cycle_start',
                   'license2_renewal_date', 'license2_required_points', 'license2_baseline_points']
                    .forEach(k => set(k, ''));
                }} />
            ) : (
              <button type="button"
                onClick={() => {
                  set('has_license2', true);
                  if (form.license2_required_points === '' || form.license2_required_points == null)
                    set('license2_required_points', form.required_points || 45);
                }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  padding: '15px 16px', borderRadius: 14, cursor: 'pointer',
                  border: '1.5px dashed var(--primary)', background: 'transparent',
                  color: 'var(--primary)', fontWeight: 700, fontSize: 14.5,
                }}>
                <Icon name="plus" size={16} stroke={2.2} />
                {lang === 'zh' ? '我持有多於一張執業證書' : 'I hold more than one certificate'}
              </button>
            )}
          </div>
          </ProfileEditSection>

          <ProfileEditSection
            title={lang === 'zh' ? '工作資料' : 'Work information'}
            hint={lang === 'zh' ? '用於個人化課程及工作場所內容。' : 'Used to personalise workplace and course content.'}>
            {/* Workplace — sector toggle + hospital dropdown. Any interaction
                marks the workplace as touched, which is the only thing that
                lets the save write the hospital columns (see workplaceTouched). */}
            <HospitalPicker
              lang={lang}
              sector={form.hospital_sector}
              hospitalKey={form.hospital_key}
              orgName={form.hospital_name}
              onSector={v => { setWorkplaceTouched(true); set('hospital_sector', v); }}
              onHospital={v => { setWorkplaceTouched(true); set('hospital_key', v); }}
              onOrgName={v => { setWorkplaceTouched(true); set('hospital_name', v); }}
            />
            {legacyFreeText && !workplaceTouched ? (
              <div className="profile-edit-help">
                {lang === 'zh' ? `現有資料：${legacyFreeText}` : `On file: ${legacyFreeText}`}
                {!form.hospital_key
                  ? (lang === 'zh' ? ' — 請從清單重新選擇' : ' — please reselect from the list')
                  : ''}
              </div>
            ) : null}
          </ProfileEditSection>

          {/* ── Memberships ────────────────────────────────────────────────
              Why this is asked at all: 84% of the catalogue publishes member AND
              non-member prices, so without knowing her societies the planner can
              only ever say "from HK$X" — and, worse, could pick a course by a
              member price she cannot obtain.

              ‼️ THIS WAS A FREE-TEXT LINE UNTIL 2026-08-10, AND IT DID NOTHING.
              The matcher compared what she typed to each provider's full ENGLISH
              name, so both of the examples this very field used to suggest
              ("HKACCN", "香港護士協會") matched nothing at all. MembershipPicker
              (components.jsx) offers the providers that actually publish a split
              price — storing provider_code, which is what the matcher now keys
              on — and keeps a free-text box for everything else.

              Optional on purpose. Left empty, everything still works; costs stay
              labelled "from" and the plan stays honest. */}
          <ProfileEditSection
            title={lang === 'zh' ? '專業會籍' : 'Professional memberships'}
            hint={lang === 'zh' ? '選填；讓補分路線使用你實際可享的會員價。' : 'Optional; lets route planning use prices you can actually access.'}
            collapsible>
            <MembershipPicker lang={lang} value={membershipsList} onChange={setMembershipsList} />
          </ProfileEditSection>

          {/* ── Community public settings ──────────────────────────────────
              How this nurse is credited on courses they submit or correct.
              Private by default (anonymous "社群護士"). */}
          <ProfileEditSection
            title={lang === 'zh' ? '社群顯示' : 'Community identity'}
            hint={lang === 'zh' ? '控制你的課程貢獻如何顯示；預設保持匿名。' : 'Controls how course contributions are credited; anonymous by default.'}
            collapsible>
          <div className="onb-field">
            <div className="auth-label mono">{lang === 'zh' ? '社群公開方式' : 'Community visibility'}</div>
            <div className="onb-roles">
              {[
                { id: 'private', zh: '匿名',    en: 'Anonymous' },
                { id: 'handle',  zh: '化名',    en: 'Handle' },
                { id: 'name',    zh: '顯示名稱', en: 'Real name' },
              ].map(o => (
                <button key={o.id} type="button"
                  className={'onb-chip' + (attrVis === o.id ? ' on' : '')}
                  onClick={() => setAttrVis(o.id)}>
                  {lang === 'zh' ? o.zh : o.en}
                </button>
              ))}
            </div>
            <div className="muted-2" style={{ fontSize: 11.5, marginTop: 6 }}>
              {attrVis === 'private'
                ? (lang === 'zh' ? '你的貢獻會顯示為「社群護士」。' : 'Your contributions show as "Community nurse".')
                : attrVis === 'handle'
                  ? (lang === 'zh' ? '在課程頁以化名顯示你的貢獻。' : 'Your contributions show your handle on courses.')
                  : (lang === 'zh' ? '在課程頁以你的顯示名稱標註貢獻。' : 'Your contributions show your real display name.')}
            </div>
          </div>

          {attrVis === 'handle' && (
            <div className="auth-field">
              <div className="auth-label mono">{lang === 'zh' ? '化名' : 'Handle'}</div>
              <input className="auth-input" type="text" value={handle} maxLength={20}
                autoCapitalize="none" autoCorrect="off" spellCheck={false}
                onChange={e => setHandle(e.target.value)} placeholder="nurse_wing" />
              {handleTrimmed && !HANDLE_RE.test(handleTrimmed) ? (
                <div className="muted-2" style={{ fontSize: 11.5, marginTop: 4, color: 'var(--danger, #c0392b)' }}>
                  {lang === 'zh' ? '2–20 個英文字母、數字或底線' : '2–20 letters, digits or underscore'}
                </div>
              ) : null}
            </div>
          )}

          {attrVis !== 'private' && (
            <div className="onb-field">
              <div className="auth-label mono">{lang === 'zh' ? '頭像' : 'Avatar'}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <UserAvatar size={44}
                  displayText={attrVis === 'handle' ? (handleTrimmed || 'N') : (form.display_name || 'N')}
                  hue={typeof avatarHue === 'number' ? avatarHue : hashHue(userId)}
                  emoji={avatarEmoji} isPrivate={false} />
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                  {lang === 'zh' ? '選一個表情,或用名稱字首。' : 'Pick an emoji, or use your initial.'}
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <button type="button" onClick={() => setAvatarEmoji(null)}
                  className={'onb-chip' + (!avatarEmoji ? ' on' : '')} style={{ minWidth: 40 }}>
                  {lang === 'zh' ? '字首' : 'Aa'}
                </button>
                {AVATAR_EMOJI.map(e => (
                  <button key={e} type="button" onClick={() => setAvatarEmoji(e)}
                    className={'onb-chip' + (avatarEmoji === e ? ' on' : '')}
                    style={{ fontSize: 18, minWidth: 40 }}>{e}</button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                {[null, 20, 145, 200, 260, 320].map((hv, i) => {
                  const active = (avatarHue ?? null) === hv;
                  const swatchHue = hv == null ? hashHue(userId) : hv;
                  return (
                    <button key={i} type="button" aria-label={hv == null ? 'auto colour' : 'colour ' + hv}
                      onClick={() => setAvatarHue(hv)}
                      style={{ width: 26, height: 26, borderRadius: '50%', cursor: 'pointer',
                        background: `oklch(0.85 0.09 ${swatchHue})`,
                        border: active ? '2px solid var(--primary)' : '2px solid transparent' }} />
                  );
                })}
              </div>
            </div>
          )}
          </ProfileEditSection>

          <AuthError msg={err} lang={lang} />
        </div>
      </div>

      <div className="profile-edit-savebar">
        {/* The blocking reason sits DIRECTLY above Save, in the danger colour.
            It used to live mid-page in muted 11.5px text, so a user whose save
            was blocked just saw a dead grey button and concluded their edit
            hadn't been stored. */}
        {blockReason ? (
          <div role="alert" style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--danger, #c0392b)' }}>
            {blockReason}
          </div>
        ) : null}
        <LoadingButton className="btn btn-primary btn-fill"
          isLoading={busy}
          disabled={!canSave || (!dirty && !communityDirty && !membershipsDirty)}
          loadingLabel={lang === 'zh' ? '儲存中…' : 'Saving…'}
          leftIcon={<Icon name="check" size={16} stroke={2.2} />}
          onClick={save}>
          {lang === 'zh' ? '儲存變更' : 'Save changes'}
        </LoadingButton>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Profile · Feedback — a durable "leave a comment or suggestion" form.
// Writes public.app_feedback via window.DB.submitFeedback (RLS-scoped to the
// author). Mirrors the per-course Report-a-problem sheet's shape (category ·
// message · optional contact · success state) but is GENERAL product feedback
// with no course FK — it does not feed the crowdsource apply pipeline. Device
// context (app version / platform / locale) is captured at submit time so a
// report is actionable without a "what were you running?" follow-up.
// Signed-in only: the guest Profile has no overview and thus no entry point.
// Stays mounted while closed (so the Sheet close animation can play), so the
// form is reset whenever `open` flips true.
// ─────────────────────────────────────────────────────────────────
function FeedbackSheet({ open, onClose, lang, session }) {
  const [category, setCategory] = React.useState('suggestion');
  const [message, setMessage] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Reset to a clean form each time the sheet opens, and prefill the contact
  // email from the session so a reply-wanting user needn't retype it.
  React.useEffect(() => {
    if (!open) return;
    setCategory('suggestion');
    setMessage('');
    setEmail(session?.user?.email || '');
    setBusy(false);
    setDone(false);
    setError(null);
  }, [open, session?.user?.email]);

  const canSubmit = message.trim().length > 0 && !busy;

  const onSubmit = async () => {
    if (!canSubmit) return;
    const userId = session?.user?.id;
    if (!userId) { setError(L(STR.feedbackError, lang)); return; }
    setBusy(true);
    setError(null);

    // Device context — best-effort; a metadata failure never blocks the send.
    let appVersion = '';
    let platform = 'web';
    try {
      platform = globalThis.Capacitor?.getPlatform?.() || 'web';
      if (window.CapacitorApp?.getInfo) {
        const info = await window.CapacitorApp.getInfo();
        if (info?.version) appVersion = info.build ? `${info.version} (${info.build})` : String(info.version);
      }
    } catch (_) { /* metadata is optional */ }

    try {
      const { error: e } = await window.DB.submitFeedback({
        userId,
        category,
        message: message.trim(),
        contactEmail: email.trim() || null,
        appVersion,
        platform,
        locale: lang,
      });
      if (e) { setError(e.message || L(STR.feedbackError, lang)); setBusy(false); return; }
      setDone(true);
      setTimeout(onClose, 1400);
    } catch (err) {
      setError(err?.message || L(STR.feedbackError, lang));
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title={L(STR.feedbackTitle, lang)}>
      <div style={{ padding: '0 20px 24px' }}>
        {done ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '24px 0' }}>
            <div style={{ width: 48, height: 48, borderRadius: 24, background: 'var(--primary-soft)',
                          color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="check" size={22} stroke={2.2} />
            </div>
            <div style={{ fontWeight: 600, textAlign: 'center' }}>{L(STR.feedbackThanks, lang)}</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="muted-2" style={{ fontSize: 12, lineHeight: 1.5 }}>
              {L(STR.feedbackIntro, lang)}
            </div>

            <div className="field">
              <div className="field-label">{L(STR.feedbackCategory, lang)}</div>
              <select className="field-input" value={category} onChange={e => setCategory(e.target.value)}>
                <option value="suggestion">{L(STR.feedbackCatSuggestion, lang)}</option>
                <option value="bug">{L(STR.feedbackCatBug, lang)}</option>
                <option value="other">{L(STR.feedbackCatOther, lang)}</option>
              </select>
            </div>

            <div className="field">
              <div className="field-label">{L(STR.feedbackMessage, lang)} *</div>
              <textarea className="field-input" rows={4} value={message}
                        onChange={e => setMessage(e.target.value)}
                        placeholder={L(STR.feedbackMessagePlaceholder, lang)} />
            </div>

            <div className="field">
              <div className="field-label">
                {L(STR.feedbackEmail, lang)}{' '}
                <span className="muted-2">({L(STR.feedbackEmailOptional, lang)})</span>
              </div>
              <input className="field-input" type="email" inputMode="email"
                     autoComplete="email" value={email}
                     onChange={e => setEmail(e.target.value)}
                     placeholder="you@example.com" />
              <div className="muted-2" style={{ fontSize: 11, marginTop: 4 }}>
                {L(STR.feedbackEmailHint, lang)}
              </div>
            </div>

            {error && (
              <div role="alert" style={{ color: 'var(--danger)', fontSize: 12.5 }}>{error}</div>
            )}

            <button className="btn btn-primary btn-fill" disabled={!canSubmit}
                    style={{ opacity: canSubmit ? 1 : 0.55 }} onClick={onSubmit}>
              {busy ? L(STR.feedbackSending, lang) : L(STR.feedbackSubmit, lang)}
            </button>
          </div>
        )}
      </div>
    </Sheet>
  );
}

function maskLicenseNumber(value) {
  const s = String(value || '').trim();
  if (!s) return '';
  if (s.length <= 4) return '••' + s.slice(-2);
  const prefix = (s.match(/^[A-Za-z]+[- ]?/) || [''])[0];
  return prefix + '••••' + s.slice(-3);
}

function ProfileOverview({ u, profile, session, lang, records = [], qualifications = [], walletItems = null, qualificationsReady = true, appearanceBlock, preferencesBlock, notifUnread = 0, isPro = false, onOpenSheet, onSignOut, onDeleteAccount, onCompleteProfile, onEditProfile, onExportNchk, onOpenAudit, onLeaveFeedback, onOpenNotifications, onOpenQualifications, onGoTo }) {
  const readiness = window.auditReadiness
    ? window.auditReadiness({ profile, user: u, records, today: window.todayIso?.() })
    : null;
  return (
    <F>
      {/* Identity card — when the profile is incomplete (onCompleteProfile is
          present) tapping the card re-enters the onboarding flow, mirroring
          the "繼續/Continue" button below. Once onboarded it instead opens the
          Edit Profile page. onCompleteProfile takes priority so a not-yet-
          onboarded user is always routed to finish setup first. */}
      <div style={{ padding: '8px 20px 0' }}>
        {(() => {
        const onCard = onCompleteProfile || onEditProfile;
        return (
        <div
          className="card"
          style={{ display: 'flex', gap: 14, alignItems: 'center', cursor: onCard ? 'pointer' : undefined }}
          role={onCard ? 'button' : undefined}
          tabIndex={onCard ? 0 : undefined}
          aria-label={onCard ? (lang === 'zh' ? '編輯個人資料' : 'Edit profile') : undefined}
          onClick={onCard || undefined}
          onKeyDown={onCard ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCard(); }
          } : undefined}
        >
          {/* The Pro ring is the quietest possible "yes, your payment worked":
              visible every time she opens her own profile, and it costs no
              layout. It renders from the SERVER entitlement only. */}
          <div className={'avatar' + (isPro ? ' is-pro' : '')} style={{ width: 56, height: 56, fontSize: 18 }}>{u.initials}</div>
          {/* minWidth:0 lets this flex column shrink below its content width so
              the long, unbreakable privaterelay email truncates with an
              ellipsis instead of forcing the row wider (which previously
              compressed the avatar into an oval). */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* The name still needs to truncate, so the badge sits OUTSIDE the
                ellipsised span with flexShrink:0 — inside it, a long name
                would push "PRO" off the card or clip it mid-word. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
              <span className="h-row" style={{ fontSize: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{L(u.name, lang)}</span>
              {isPro && typeof ProBadge === 'function' ? <ProBadge lang={lang} /> : null}
            </div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{L(u.role, lang)}</div>
            {session?.user?.email ? (
              <div className="mono muted-2" style={{ fontSize: 10.5, letterSpacing: '0.06em', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {session.user.email}
              </div>
            ) : null}
          </div>
          {onCard ? <Icon name="chev-r" size={18} color="var(--fg-3)" stroke={1.6} /> : null}
        </div>
        );
        })()}
      </div>

      {/* Cycle stats — one card per practising certificate for a dual-registered
          nurse (each labelled with its register part); a single, unlabelled card
          unchanged for everyone else. */}
      {(u.licenses && u.licenses.length > 1) ? (
        <div style={{ padding: '12px 20px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {u.licenses.map(lic => (
            <div key={lic.key} className="card">
              <div className="eyebrow" style={{ marginBottom: 10 }}>{L(lic.role, lang)}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Stat
                  k={lang === 'zh' ? '本週期已修' : 'Cycle earned'}
                  v={lic.earned}
                  sub={`/ ${lic.required} ${L(STR.pts, lang)}`}
                />
                <Stat
                  k={L(STR.cycleEnds, lang)}
                  v={lic.renewalDate ? new Date(lic.renewalDate + 'T00:00:00').getFullYear() : '—'}
                  sub={lic.renewalDate ? fmtShort(lic.renewalDate, lang) : ''}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ padding: '12px 20px 0' }}>
          <div className="card" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Stat
              k={lang === 'zh' ? '本週期已修' : 'Cycle earned'}
              v={u.earned}
              sub={`/ ${u.required} ${L(STR.pts, lang)}`}
            />
            <Stat
              k={L(STR.cycleEnds, lang)}
              v={u.renewalDate ? new Date(u.renewalDate + 'T00:00:00').getFullYear() : '—'}
              sub={u.renewalDate ? fmtShort(u.renewalDate, lang) : ''}
            />
          </div>
        </div>
      )}

      {/* Complete-profile prompt — only shown when the user has skipped
          (or never completed) onboarding. Renders inline so it's the
          first action they see after the identity card + cycle stats.
          Tapping invokes the parent's onCompleteProfile callback which
          re-enters the OnboardingFlow. */}
      {onCompleteProfile ? (
        <div style={{ padding: '14px 20px 0' }}>
          <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14.5 }}>
                {lang === 'zh' ? '完善你的個人資料' : 'Complete your profile'}
              </div>
              <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                {lang === 'zh'
                  ? '加入顯示名稱、工作單位和續期週期,讓主畫面的數據顯示得準確一些。'
                  : 'Add your display name, workplace, and renewal cycle so the dashboard shows real numbers.'}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              style={{ flexShrink: 0, fontSize: 13, padding: '8px 14px' }}
              onClick={onCompleteProfile}
            >
              {lang === 'zh' ? '繼續' : 'Continue'}
            </button>
          </div>
        </div>
      ) : null}

      {onOpenQualifications ? (() => {
        // ‼️ THE SAME ARRAY THE WALLET RENDERS. Not the same source filtered the
        // same way — literally the same array, built once in app.jsx. Two
        // look-alike derivations is what produced "1 項資格" here and "0 項資格"
        // one tap away, and look-alike is not a property anything enforces.
        const active = walletItems || [];
        const attention = active.filter(i => window.walletItemState(i).needsAttention).length;
        const nearest = window.walletNearestExpiry(active);
        const dated = nearest ? [{ expiresOn: nearest.expiresOn }] : [];
        return <div style={{ padding: '14px 20px 0' }}><button type="button" className="card" onClick={onOpenQualifications} style={{ width:'100%',display:'flex',alignItems:'center',gap:12,textAlign:'left',font:'inherit',color:'inherit',cursor:'pointer' }}><div style={{width:42,height:42,borderRadius:12,background:'var(--surface-2)',display:'grid',placeItems:'center'}}><Icon name="shield" size={20}/></div><div style={{flex:1,minWidth:0}}><div className="h-row">{lang==='zh'?'我的資格':'My qualifications'}</div><div className="muted" style={{fontSize:12,marginTop:3}}>{!qualificationsReady ? (lang==='zh'?'載入中…':'Loading…') : active.length ? (lang==='zh'?`${active.length} 項資格${attention?` · ${attention} 項需留意`:''}`:`${active.length} qualification${active.length===1?'':'s'}${attention?` · ${attention} need attention`:''}`) : (lang==='zh'?'管理執業資格、學歷、急救證書及臨床技能':'Manage practising credentials, education, resuscitation and clinical skills')}</div>{qualificationsReady && dated[0] && <div className="muted-2" style={{fontSize:11,marginTop:2}}>{lang==='zh'?'最近到期：':'Next expiry: '}{fmtDate(dated[0].expiresOn,lang)}</div>}</div>{qualificationsReady && attention>0&&<span className="cap-pill tone-warning">{attention}</span>}<Icon name="chev-r" size={16}/></button></div>;
      })() : null}

      {/* Personal identity stays separate from regulated certificate identity. */}
      <ListSection title={lang === 'zh' ? '個人資料' : 'Personal information'} lang={lang}>
        <Row icon="user" label={L(u.hospital, lang)} sub={L(u.role, lang)} />
        {u.sex ? <Row icon="user" label={L(STR.sex, lang)} value={L(sexLabel(u.sex), lang)} /> : null}
        {/* Explicit entry point for discoverability — the identity card is
            also tappable, but a labelled row makes the affordance obvious.
            Only shown once onboarded; before that the "Complete profile"
            prompt above owns the editing entry point. */}
        {onEditProfile ? (
          <Row icon="edit" label={lang === 'zh' ? '編輯個人資料' : 'Edit profile'} onClick={onEditProfile} />
        ) : null}
      </ListSection>

      <ListSection title={lang === 'zh' ? '護士登記與執業證書' : 'Nursing registration'} lang={lang}>
        {(u.licenses || []).map((lic, index) => {
          const masked = maskLicenseNumber(lic.licenseNo);
          return (
            <Row key={lic.key} icon="shield"
              label={`${index + 1}. ${L(lic.role, lang)}`}
              sub={masked
                ? `${lang === 'zh' ? '編號' : 'No.'} ${masked}`
                : (lang === 'zh' ? '尚未填寫登記／註冊編號' : 'Registration number not added')}
              value={fmtDate(lic.renewalDate, lang) || '—'}
              onClick={onEditProfile} />
          );
        })}
      </ListSection>

      <ListSection title={lang === 'zh' ? 'CNE 文件' : 'CNE documents'} lang={lang}>
        {readiness ? (
          <Row icon="check-circ"
            label={lang === 'zh' ? '查核準備度' : 'Audit readiness'}
            sub={readiness.gaps.length
              ? (lang === 'zh'
                  ? `已完成 ${readiness.score}/${readiness.max} 項 · ${readiness.gaps.length} 項待補`
                  : `${readiness.score}/${readiness.max} complete · ${readiness.gaps.length} remaining`)
              : (lang === 'zh' ? '必要資料已齊備' : 'Required information is complete')}
            onClick={onOpenAudit} />
        ) : null}
        <Row icon="download" label={L(STR.exportPdf, lang)} value={lang === 'zh' ? '附件 A + B' : 'Annex A + B'}
          onClick={() => onExportNchk && onExportNchk()} />
      </ListSection>

      {appearanceBlock}
      {preferencesBlock}

      <SecuritySection session={session} lang={lang} />

      <ListSection title={lang === 'zh' ? '紀錄' : 'Record'} lang={lang}>
        <Row icon="bell" label={lang === 'zh' ? '提醒' : 'Reminders'} onClick={() => onOpenSheet && onOpenSheet('reminders')} />
        {/* Notification log — a history of past notifications, so it sits in
            the Record section next to Reminders. A distinct "list" icon keeps
            it from reading as a second bell. */}
        <Row icon="list" label={L(STR.notifCenterTitle, lang)}
             sub={L(STR.notifCenterSub, lang)}
             value={notifUnread > 0 ? String(notifUnread) : undefined}
             onClick={onOpenNotifications} />
        <Row icon="shield" label={lang === 'zh' ? '私隱與資料' : 'Privacy & data'} onClick={() => onOpenSheet && onOpenSheet('privacy')} />
      </ListSection>

      {/* Feedback — a durable "leave a comment or suggestion" entry point.
          Opens the FeedbackSheet (owned by ProfileScreen). Only rendered when
          the handler is present, i.e. for signed-in users; the guest Profile
          has no overview and therefore no feedback row. */}
      {onLeaveFeedback && (
        <ListSection title={L(STR.feedbackSection, lang)} lang={lang}>
          <Row icon="mail"
               label={L(STR.feedbackRowLabel, lang)}
               sub={L(STR.feedbackRowSub, lang)}
               onClick={onLeaveFeedback} />
          {/* Explicit, user-initiated path to an App Store rating — kept BELOW
              the feedback row so "something's wrong" finds the inbox first.
              Native-only: it deep-links into the App Store app's review
              composer, which has no web equivalent. */}
          {!!globalThis.Capacitor?.isNativePlatform?.() && (
            <Row icon="star"
                 label={L(STR.rateAppRowLabel, lang)}
                 sub={L(STR.rateAppRowSub, lang)}
                 onClick={openAppStoreReview} />
          )}
        </ListSection>
      )}

      <div style={{ padding: '20px 20px 0' }}>
        <button className="btn btn-ghost btn-fill" style={{ color: 'var(--danger)' }} onClick={onSignOut}>
          {L(STR.signOut, lang)}
        </button>
      </div>

      {onDeleteAccount ? (
        <div style={{ padding: '12px 20px 0' }}>
          <button
            className="btn btn-ghost btn-fill"
            style={{ color: 'var(--danger)', fontSize: 13 }}
            onClick={onDeleteAccount}
          >
            {lang === 'zh' ? '刪除帳戶' : 'Delete account'}
          </button>
        </div>
      ) : null}
      <div style={{ padding: '20px 20px 4px', textAlign: 'center' }}>
        <div className="mono muted-2" style={{ fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase' }}>Vital</div>
        <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
          {lang === 'zh' ? `CNE · 香港 · v${window.VITAL_APP_VERSION || 'dev'}` : `CNE companion · Hong Kong · v${window.VITAL_APP_VERSION || 'dev'}`}
        </div>
      </div>
    </F>
  );
}

// ─────────────────────────────────────────────────────────────────
// Profile · Notification center — durable history of every notification
// (scheduled reminders, community review outcomes, admin pings). Push is
// ephemeral; this is the source of truth. Reads public.user_notifications
// (RLS-scoped to the owner). A row greys (read) ONLY when tapped — tapping
// also deep-links via the same 'vital:notification-tap' event a push tap uses.
// Swipe a row left/right to remove it; "Mark all read" clears the highlight
// in bulk. (We intentionally do NOT mark everything read on open, so the
// unread badge reflects items the user hasn't actually pressed.)
// ─────────────────────────────────────────────────────────────────
// SwipeableNotification — wraps one Notification Center row so a horizontal
// drag (left OR right) past a threshold removes it, iOS-style. Mirrors the
// calendar's touch-swipe pattern (record-extras.jsx): lock the axis after a
// small deadzone so vertical scrolling still works, and swallow the click a
// swipe would otherwise synthesise so a swipe never opens the item. The
// foreground keeps an OPAQUE background to hide the red delete layer at rest,
// and stays a real <button> so taps remain keyboard-accessible.
function SwipeableNotification({ onTap, onDelete, deleteLabel, buttonStyle, children }) {
  const [dx, setDx] = React.useState(0);
  const [dragging, setDragging] = React.useState(false);
  const [exiting, setExiting] = React.useState(false);
  const drag = React.useRef({ x: 0, y: 0, dx: 0, axis: null, active: false, consumed: false });
  const THRESHOLD = 88; // px of horizontal travel that commits the delete

  const onTouchStart = (e) => {
    if (exiting || e.touches.length !== 1) { drag.current.active = false; return; }
    const t = e.touches[0];
    drag.current = { x: t.clientX, y: t.clientY, dx: 0, axis: null, active: true, consumed: false };
  };
  const onTouchMove = (e) => {
    const d = drag.current;
    if (!d.active) return;
    const t = e.touches[0];
    const ddx = t.clientX - d.x, ddy = t.clientY - d.y;
    if (!d.axis) {
      if (Math.abs(ddx) < 8 && Math.abs(ddy) < 8) return; // deadzone — undecided
      d.axis = Math.abs(ddx) > Math.abs(ddy) ? 'x' : 'y';
      if (d.axis === 'x') { setDragging(true); d.consumed = true; }
    }
    if (d.axis !== 'x') return;            // vertical → let the list scroll
    if (e.cancelable) e.preventDefault();  // own the horizontal gesture
    d.dx = ddx; setDx(ddx);
  };
  const onTouchEnd = () => {
    const d = drag.current;
    d.active = false;
    setDragging(false);
    if (d.axis !== 'x') { setDx(0); return; }
    if (Math.abs(d.dx) >= THRESHOLD) {
      const dir = d.dx < 0 ? -1 : 1;
      setExiting(true);
      setDx(dir * ((window.innerWidth || 480) + 40)); // slide fully off-screen
      window.setTimeout(() => onDelete && onDelete(), 200);
    } else {
      setDx(0); // didn't reach threshold → snap back
    }
  };
  // A swipe synthesises a click on release; swallow it once so the row doesn't
  // also open. A plain tap leaves consumed=false and passes through.
  const handleClick = () => {
    if (drag.current.consumed) { drag.current.consumed = false; return; }
    onTap && onTap();
  };

  const sideEnd = dx < 0; // dragging left reveals the action on the right edge
  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      borderBottom: '0.5px solid var(--border)',
      maxHeight: exiting ? 0 : 480, opacity: exiting ? 0 : 1,
      transition: exiting ? 'max-height .22s ease .18s, opacity .25s ease' : 'none',
    }}>
      {/* Red delete affordance, revealed under the finger on the drag side. */}
      <div aria-hidden="true" style={{
        position: 'absolute', inset: 0, background: 'var(--danger)', color: '#fff',
        display: 'flex', alignItems: 'center',
        justifyContent: sideEnd ? 'flex-end' : 'flex-start',
        padding: '0 22px', gap: 8, opacity: Math.abs(dx) > 4 ? 1 : 0,
      }}>
        <Icon name="trash" size={17} stroke={1.8} color="#fff" />
        <span style={{ fontSize: 13, fontWeight: 600 }}>{deleteLabel}</span>
      </div>
      {/* Foreground row — opaque bg covers the red layer when at rest. */}
      <button
        type="button"
        onClick={handleClick}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        style={{
          ...buttonStyle,
          position: 'relative',
          transform: `translateX(${dx}px)`,
          transition: dragging ? 'none' : 'transform .22s cubic-bezier(.2,.8,.2,1)',
          touchAction: 'pan-y',
        }}
      >
        {children}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LeadTimeSheet — how far ahead a class reminder fires.
//
// ‼️ IT REPLACED FIVE FIXED CHIPS (14/7/3/1/0), which were the ONLY thing
// stopping a nurse from choosing 10 days, or 14 AND 5. The storage never
// required them: db.js normaliseLeadDays and notify-cron's sessionReminders
// both accept any integer 0–14, de-duplicated, sorted and capped at 4 — and
// they have since multi-offset reminders shipped. The ladder was a rendering
// decision that became a limit.
//
// ‼️ THE BOUNDS ARE NOT THIS FILE'S TO CHOOSE. 0–14 and "at most 4" are enforced
// in db.js AND re-enforced server-side in notify-cron, because prefs are
// client-writable and each offset is another push per class per enrolled
// course. What is written here must stay inside them — a UI that offers 30 days
// would silently store 14 and look broken. If the range ever changes, change it
// in all three places or not at all.
//
// The common values stay one tap away. Removing them to "make it custom" would
// trade a real convenience for a principle nobody asked for; what was missing
// was the ability to add anything else, not the shortcuts.
const LEAD_MIN = 0;
const LEAD_MAX = 14;      // mirrors SESSION_REMINDER_MAX_LEAD (db.js)
const LEAD_CAP = 4;       // mirrors SESSION_REMINDER_MAX_OFFSETS (db.js)
const LEAD_QUICK = [14, 7, 3, 1, 0];

function LeadTimeSheet({ lang, value, busy, onClose, onSave }) {
  const zh = lang === 'zh';
  // Sorted far→near, the order they will fire in.
  const [list, setList] = React.useState(
    () => [...new Set((Array.isArray(value) ? value : []).filter(Number.isInteger))]
      .sort((a, b) => b - a).slice(0, LEAD_CAP),
  );
  const [custom, setCustom] = React.useState('');
  const [err, setErr] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const label = (v) => (v === 0
    ? (zh ? '當日' : 'Day of class')
    : (zh ? `${v} 日前` : `${v} day${v === 1 ? '' : 's'} before`));

  const add = (raw) => {
    const v = Number(raw);
    if (!Number.isInteger(v) || v < LEAD_MIN || v > LEAD_MAX) {
      setErr(zh ? `請輸入 ${LEAD_MIN}–${LEAD_MAX} 之間的日數。` : `Enter a whole number of days between ${LEAD_MIN} and ${LEAD_MAX}.`);
      return;
    }
    if (list.includes(v)) { setErr(zh ? '已經設定咗呢個時間。' : 'That lead time is already set.'); return; }
    if (list.length >= LEAD_CAP) { setErr(zh ? `最多 ${LEAD_CAP} 個。先移除一個再加。` : `Up to ${LEAD_CAP}. Remove one first.`); return; }
    setErr('');
    setCustom('');
    setList([...list, v].sort((a, b) => b - a));
  };
  // ‼️ NEVER LET HER EMPTY IT. An empty set silences class reminders while the
  // toggle on the previous screen still reads "on" — so the last chip has no
  // remove control at all, rather than a control that refuses.
  const remove = (v) => { setErr(''); setList(list.filter(x => x !== v)); };

  const save = async () => {
    if (saving || !list.length) return;
    setSaving(true);
    await onSave(list);
    setSaving(false);
    onClose?.();
  };

  return (
    <Sheet open={true} onClose={onClose} title={zh ? '提前時間' : 'Lead times'}>
      <div style={{ padding: '16px 20px 8px' }}>
        <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.55, marginTop: 0 }}>
          {zh
            ? `每個時間都會就同一堂課發出一次提醒。最多 ${LEAD_CAP} 個,可設 ${LEAD_MIN}–${LEAD_MAX} 日。`
            : `Each one sends its own reminder for the same class. Up to ${LEAD_CAP}, anywhere from ${LEAD_MIN} to ${LEAD_MAX} days.`}
        </p>

        <div className="auth-label mono" style={{ marginTop: 4 }}>
          {zh ? `已設定（${list.length} / ${LEAD_CAP}）` : `Set (${list.length} of ${LEAD_CAP})`}
        </div>
        <div className="onb-roles" style={{ flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
          {list.map(v => (
            <span key={v} className="onb-chip on"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {label(v)}
              {list.length > 1 && (
                <button type="button"
                  onClick={() => remove(v)}
                  aria-label={(zh ? '移除 ' : 'Remove ') + label(v)}
                  style={{
                    display: 'flex', alignItems: 'center', padding: 0, minWidth: 20, minHeight: 20,
                    justifyContent: 'center', background: 'none', border: 'none',
                    color: 'inherit', cursor: 'pointer', opacity: 0.8,
                  }}>
                  <Icon name="close" size={11} stroke={2.4} />
                </button>
              )}
            </span>
          ))}
        </div>
        {list.length === 1 && (
          <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 6 }}>
            {zh ? '至少要保留一個 —— 否則課堂提醒等於關咗。'
                : 'One has to stay — an empty list would switch class reminders off.'}
          </div>
        )}

        <div className="auth-label mono" style={{ marginTop: 16 }}>
          {zh ? '自訂' : 'Custom'}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 6 }}>
          <input
            type="number" inputMode="numeric" min={LEAD_MIN} max={LEAD_MAX} step={1}
            className="auth-input"
            style={{ flex: 1, minWidth: 0 }}
            value={custom}
            placeholder={zh ? `日數（${LEAD_MIN}–${LEAD_MAX}）` : `Days (${LEAD_MIN}–${LEAD_MAX})`}
            aria-label={zh ? '自訂提前日數' : 'Custom lead time in days'}
            onChange={(e) => { setCustom(e.target.value); setErr(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(custom); } }} />
          <button type="button" className="btn btn-ghost"
            style={{ padding: '10px 16px', fontSize: 13, flexShrink: 0 }}
            disabled={custom === '' || list.length >= LEAD_CAP}
            onClick={() => add(custom)}>
            {zh ? '加入' : 'Add'}
          </button>
        </div>
        {err && (
          <div role="alert" className="muted-2"
            style={{ fontSize: 11.5, marginTop: 6, color: 'var(--danger, inherit)' }}>
            {err}
          </div>
        )}

        {/* Shortcuts, not the menu. Already-set values are omitted rather than
            shown disabled — they are visible as chips a few lines above. */}
        {LEAD_QUICK.some(v => !list.includes(v)) && list.length < LEAD_CAP && (
          <React.Fragment>
            <div className="auth-label mono" style={{ marginTop: 16 }}>
              {zh ? '常用' : 'Common'}
            </div>
            <div className="onb-roles" style={{ flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
              {LEAD_QUICK.filter(v => !list.includes(v)).map(v => (
                <button key={v} type="button" className="onb-chip"
                  onClick={() => add(v)}>
                  + {label(v)}
                </button>
              ))}
            </div>
          </React.Fragment>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 20, alignItems: 'center' }}>
          <button className="btn btn-primary" style={{ flex: 1 }}
            disabled={busy || saving || !list.length} onClick={save}>
            {saving ? (zh ? '儲存中…' : 'Saving…') : (zh ? '儲存' : 'Save')}
          </button>
          <button type="button" className="btn btn-ghost"
            style={{ padding: '6px 14px', fontSize: 12.5 }}
            disabled={saving} onClick={onClose}>
            {zh ? '取消' : 'Cancel'}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

// The watch editor. Deliberately small: every control maps to a column the
// server sweep can read (see sweepCourseRule). Adding a control here without
// adding the column first builds a watch that silently never fires.
function RuleEditorSheet({ lang, draft, setDraft, busy, onCancel, onSave }) {
  const zh = lang === 'zh';
  const T = window.TOPIC_LABEL || {};
  const set = (k, v) => setDraft({ ...draft, [k]: v });
  const toggleTopic = (t) => set('topics',
    draft.topics.includes(t) ? draft.topics.filter(x => x !== t) : [...draft.topics, t]);
  // Tri-state: on → off → don't care. "Don't care" has to be reachable, or every
  // rule silently narrows to whatever the first tap happened to be.
  const cycle = (k) => set(k, draft[k] === true ? false : (draft[k] === false ? null : true));
  const triLabel = (v, onTxt, offTxt) => (v === true ? onTxt : v === false ? offTxt : (zh ? '不限' : 'Any'));
  // An empty rule matches every course, which would be a notification for every
  // new listing — so it cannot be saved. The sweep drops such rules too.
  const constrained = draft.topics.length > 0 || draft.free !== null
    || draft.online !== null || draft.weekend !== null || draft.minPoints !== null;

  return (
    <Sheet open={true} onClose={onCancel} title={zh ? '新增課程監察' : 'New course watch'}>
      <div style={{ padding: '16px 20px 8px' }}>
        <div className="auth-label mono" style={{ marginBottom: 6 }}>{zh ? '範疇' : 'Areas'}</div>
        <div className="onb-roles" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          {Object.keys(T).map(k => (
            <button key={k} type="button" aria-pressed={draft.topics.includes(k)}
              className={'onb-chip' + (draft.topics.includes(k) ? ' on' : '')}
              onClick={() => toggleTopic(k)}>{zh ? T[k].zh : T[k].en}</button>
          ))}
        </div>

        <div className="auth-label mono" style={{ marginBottom: 6 }}>{zh ? '條件' : 'Conditions'}</div>
        <div className="onb-roles" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          <button type="button" className={'onb-chip' + (draft.free !== null ? ' on' : '')}
            onClick={() => cycle('free')}>
            {zh ? `費用：${triLabel(draft.free, '免費', '收費')}` : `Cost: ${triLabel(draft.free, 'Free', 'Paid')}`}
          </button>
          <button type="button" className={'onb-chip' + (draft.online !== null ? ' on' : '')}
            onClick={() => cycle('online')}>
            {zh ? `形式：${triLabel(draft.online, '網上', '實體')}` : `Mode: ${triLabel(draft.online, 'Online', 'In person')}`}
          </button>
          <button type="button" className={'onb-chip' + (draft.weekend === true ? ' on' : '')}
            onClick={() => set('weekend', draft.weekend === true ? null : true)}>
            {zh ? '只要週末' : 'Weekends only'}
          </button>
        </div>

        <div className="auth-label mono" style={{ marginBottom: 6 }}>{zh ? '最低學分' : 'Minimum points'}</div>
        <div className="onb-roles" style={{ flexWrap: 'wrap', gap: 8 }}>
          {[null, 1, 2, 5, 10].map(v => (
            <button key={String(v)} type="button" aria-pressed={draft.minPoints === v}
              className={'onb-chip' + (draft.minPoints === v ? ' on' : '')}
              onClick={() => set('minPoints', v)}>
              {v === null ? (zh ? '不限' : 'Any') : `${v}+`}
            </button>
          ))}
        </div>

        {!constrained && (
          <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 12 }}>
            {zh ? '至少揀一項條件 —— 冇條件嘅監察會對每個新課程都通知你。'
                : 'Pick at least one condition — a watch with none would notify you about every new course.'}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="btn btn-primary" style={{ flex: 1 }}
            disabled={busy || !constrained}
            onClick={() => onSave({
              id: `r${Date.now().toString(36)}`,
              label: describeDraft(draft, lang),
              topics: draft.topics, providers: [],
              free: draft.free, online: draft.online, weekend: draft.weekend,
              minPoints: draft.minPoints,
            })}>
            {busy ? (zh ? '儲存中…' : 'Saving…') : (zh ? '儲存' : 'Save')}
          </button>
          <button className="btn btn-ghost" disabled={busy} onClick={onCancel}>
            {zh ? '取消' : 'Cancel'}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

// The saved label is generated, not typed: a watch she named "wound" but built
// as "free + online" would misdescribe itself in the notification.
function describeDraft(d, lang) {
  const zh = lang === 'zh';
  const T = window.TOPIC_LABEL || {};
  const bits = [];
  if (d.free === true) bits.push(zh ? '免費' : 'free');
  if (d.free === false) bits.push(zh ? '收費' : 'paid');
  if (d.online === true) bits.push(zh ? '網上' : 'online');
  if (d.online === false) bits.push(zh ? '實體' : 'in person');
  if (d.weekend === true) bits.push(zh ? '週末' : 'weekend');
  if (d.minPoints) bits.push(zh ? `≥${d.minPoints} 分` : `${d.minPoints}+ pts`);
  if (d.topics.length) bits.push(d.topics.map(t => (T[t] ? (zh ? T[t].zh : T[t].en) : t)).join(zh ? '、' : ', '));
  return bits.join(zh ? ' · ' : ' · ').slice(0, 60);
}

function NotificationCenterScreen({ lang, userId, onBack }) {
  const [items, setItems] = React.useState(null); // null = loading

  React.useEffect(() => {
    let cancelled = false;
    if (!userId || !window.DB?.getNotifications) { setItems([]); return; }
    (async () => {
      const rows = await window.DB.getNotifications(userId, { limit: 100 });
      if (cancelled) return;
      setItems(rows);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Every Candidate.kind produced by notify-cron must appear here, or the row
  // falls back to a generic bell. See the union in notify-cron/index.ts.
  const iconFor = (kind) => ({
    class: 'calendar', saved_deadline: 'bookmark', missing_record: 'check-circ',
    renewal: 'clock', community: 'check-circ', admin: 'shield',
    cert_expiry: 'shield', seat_alert: 'bookmark', ward: 'users',
    regulatory: 'shield',
    // 'target' (a goal to reach), NOT 'clock': pace is guidance about progress,
    // and a clock reads as a deadline she has missed. ‼️ Must be a name the Icon
    // component actually knows — an unknown one silently falls back to the
    // generic bell, which is exactly what this map exists to prevent.
    cne_pace: 'target',
    // A watch firing is a course arriving, so it takes the same mark as the
    // other saved-course alerts rather than inventing a new one.
    course_rule: 'bookmark',
  })[kind] || 'bell';

  const relTime = (iso) => {
    if (!iso) return '';
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (days <= 0) return L(STR.notifTimeToday, lang);
    if (days === 1) return L(STR.notifTimeYesterday, lang);
    return fmtShort(String(iso).slice(0, 10), lang);
  };

  // Mark ONE row read (the "seen" state). We no longer mark everything read on
  // open — a row greys only when the user presses it — so the unread badge now
  // tracks items the user hasn't engaged with. Optimistic + fire-and-forget.
  const markRead = (it) => {
    if (it.read_at) return;
    setItems((prev) => (prev || []).map((x) => (x.id === it.id ? { ...x, read_at: new Date().toISOString() } : x)));
    Promise.resolve(window.DB.markNotificationsRead?.(userId, [it.id]))
      .catch(() => {})
      .finally(() => window.dispatchEvent(new Event('vital:notifications-changed')));
  };

  const onTap = (it) => {
    markRead(it); // greys the row even for the one route that stays in-center (digest)
    try { window.dispatchEvent(new CustomEvent('vital:notification-tap', { detail: it.payload || {} })); } catch (_) {}
  };

  // Bulk escape hatch — clears the highlight on everything at once.
  const markAllRead = () => {
    const now = new Date().toISOString();
    setItems((prev) => (prev || []).map((x) => (x.read_at ? x : { ...x, read_at: now })));
    Promise.resolve(window.DB.markNotificationsRead?.(userId))
      .catch(() => {})
      .finally(() => window.dispatchEvent(new Event('vital:notifications-changed')));
  };

  // Optimistic remove with rollback: pull the row immediately, delete it
  // server-side, and on failure splice it back where it was so nothing is lost.
  const removeItem = (it) => {
    const idx = (items || []).findIndex((x) => x.id === it.id);
    setItems((prev) => (prev || []).filter((x) => x.id !== it.id));
    window.DB.deleteNotification?.(it.id).then((res) => {
      // Roll back on a hard error OR a silent 0-row delete (RLS policy missing).
      const failed = !res || res.error || res.deleted === 0;
      if (failed) {
        setItems((cur) => {
          const copy = (cur || []).slice();
          copy.splice(idx < 0 ? copy.length : Math.min(idx, copy.length), 0, it);
          return copy;
        });
      }
    }).catch(() => {})
      .finally(() => window.dispatchEvent(new Event('vital:notifications-changed')));
  };

  // Localize against the user's CURRENT language. Notification creators
  // (notify-cron + the crowdsource Edge Functions) stamp both languages into
  // payload.i18n = { title: { en, zh }, body: { en, zh } }, so the center
  // always reflects the active toggle — not the language that happened to be
  // set when the row was written. Falls back to the stored title/body for
  // legacy rows (written before i18n existed) so nothing renders blank.
  const localized = (it, field) => {
    const v = it.payload?.i18n?.[field]?.[lang];
    return (v != null && v !== '') ? v : it[field];
  };

  const hasUnread = Array.isArray(items) && items.some((it) => !it.read_at);

  return (
    <div className="scroll fade-in" style={{ paddingBottom: 24 }}>
      <ScreenHeader
        title={L(STR.notifCenterTitle, lang)}
        onBack={onBack}
        actions={hasUnread ? (
          <button className="btn btn-ghost" style={{ padding: '5px 11px', fontSize: 12 }} onClick={markAllRead}>
            {L(STR.notifMarkAllRead, lang)}
          </button>
        ) : null}
      />
      {items === null ? null : items.length === 0 ? (
        <div className="vital-empty" style={{ marginTop: 24 }}>
          <div className="vital-empty-icon"><Icon name="bell" size={22} stroke={1.7} /></div>
          <div className="vital-empty-title">{L(STR.notifCenterEmpty, lang)}</div>
          <div className="vital-empty-sub">{L(STR.notifCenterEmptySub, lang)}</div>
        </div>
      ) : (
        <div style={{ padding: '8px 20px' }}>
          <div className="card flush" style={{ overflow: 'hidden' }}>
            {items.map((it) => {
              const unread = !it.read_at;
              const high = it.priority === 'high';
              return (
                <SwipeableNotification
                  key={it.id}
                  onTap={() => onTap(it)}
                  onDelete={() => removeItem(it)}
                  deleteLabel={L(STR.notifDelete, lang)}
                  buttonStyle={{
                    display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%',
                    padding: '14px 16px', textAlign: 'left', font: 'inherit', color: 'inherit',
                    background: unread ? 'var(--primary-soft-2)' : 'var(--surface)',
                    border: 0, cursor: 'pointer',
                  }}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                    background: unread ? (high ? 'var(--primary)' : 'var(--primary-soft-2)') : 'var(--surface-2)',
                    color: unread ? (high ? '#fff' : 'var(--primary)') : 'var(--fg-3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon name={iconFor(it.kind)} size={16} stroke={1.7} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="h-row" style={{ fontSize: 14, fontWeight: unread ? 700 : 600, color: unread ? 'inherit' : 'var(--fg-2)' }}>{localized(it, 'title')}</div>
                    <div style={{ fontSize: 12, marginTop: 2, color: unread ? 'var(--fg-2)' : 'var(--fg-3)' }}>{localized(it, 'body')}</div>
                    <div style={{ fontSize: 11, marginTop: 4, color: 'var(--fg-3)' }}>{relTime(it.created_at)}</div>
                  </div>
                  {unread && <span style={{
                    width: 8, height: 8, borderRadius: '50%', background: 'var(--primary)',
                    flexShrink: 0, marginTop: 6,
                  }} />}
                </SwipeableNotification>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Course detail · 護士評價 — published review bodies from nurses who took this
// programme.
//
// ‼️ EVERY STRING HERE IS A REACT TEXT NODE AND MUST STAY THAT WAY. This is
// user-generated content rendered to other users: no dangerouslySetInnerHTML,
// no markdown renderer, no "let them bold a word". React escapes text nodes, so
// <img src=x onerror=…> pasted into a review shows up as those characters
// instead of running. scripts/test-notice-contract.mjs bans the raw-HTML path
// repo-wide precisely so this stays true as the file grows.
//
// Bodies come from the whole repeat-key group (BLS runs ×21 — one edition alone
// would be empty on almost every course), so EACH ONE CARRIES ITS OWN DATE. A
// 2023 comment must never read as if it were about this year's run.
//
// The date is a month, not a timestamp: precision buys nothing here and is one
// more correlate for anyone trying to work out who wrote it.
// ─────────────────────────────────────────────────────────────────
function CourseReviewsSection({ courseId, lang }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [unavailable, setUnavailable] = React.useState(false);
  const [busyId, setBusyId] = React.useState(null);
  const [acted, setActed] = React.useState({});   // review_id -> 'reported' | 'blocked'

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data, missing } = await window.DB.fetchCourseReviewsPublic(courseId, { limit: 20 });
        if (!alive) return;
        if (missing) { setUnavailable(true); return; }
        setRows(data || []);
      } catch (_) { /* best-effort: the section simply does not render */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [courseId]);

  const act = async (id, kind) => {
    if (busyId) return;
    setBusyId(id);
    try {
      // Both take a REVIEW id; the server resolves the author. The client never
      // learns who wrote a review — that is the basis of review anonymity, so
      // "block this author" deliberately cannot tell you who you blocked.
      if (kind === 'reported') await window.DB.reportCourseReview(id, null);
      else await window.DB.blockReviewAuthor(id);
      setActed(prev => ({ ...prev, [id]: kind }));
      if (kind === 'blocked') setRows(prev => prev.filter(r => r.review_id !== id));
    } finally { setBusyId(null); }
  };

  if (loading || unavailable || rows.length === 0) return null;

  return (
    <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>
        {lang === 'zh' ? '護士評價' : 'Nurse reviews'}
      </div>
      <div className="muted-2" style={{ fontSize: 10.5, lineHeight: 1.45 }}>
        {lang === 'zh'
          ? '由完成咗呢個課程(包括過往班次)嘅護士撰寫,經審核後匿名顯示。'
          : 'Written by nurses who completed this programme (including past runs), shown anonymously after review.'}
      </div>

      {rows.map(r => (
        <div key={r.review_id} style={{ padding: '10px 12px', borderRadius: 10,
                                        background: 'var(--surface)',
                                        display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3,
                           color: 'var(--warning)' }}>
              <Icon name="star" size={12} stroke={1.8} color="currentColor" fill="currentColor" />
              <span style={{ fontSize: 12.5, fontWeight: 600 }}>{r.course_stars}</span>
            </span>
            <span className="muted-2" style={{ fontSize: 11 }}>{r.created_month}</span>
            {r.course_version_title && (
              <span className="muted-2" style={{ fontSize: 10.5, flex: 1, minWidth: 0,
                                                 overflow: 'hidden', textOverflow: 'ellipsis',
                                                 whiteSpace: 'nowrap' }}>
                {r.course_version_title}
              </span>
            )}
          </div>
          {/* Text node. See the header note. */}
          <div style={{ fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{r.body}</div>
          <div style={{ display: 'flex', gap: 12 }}>
            {acted[r.review_id] === 'reported' ? (
              <span className="muted-2" style={{ fontSize: 10.5 }}>
                {lang === 'zh' ? '已回報,多謝你' : 'Reported — thank you'}
              </span>
            ) : (
              <>
                <button type="button" className="link-btn" disabled={!!busyId}
                  onClick={() => act(r.review_id, 'reported')}
                  style={{ fontSize: 10.5, background: 'none', border: 0,
                           color: 'var(--fg-3)', cursor: 'pointer', padding: 0 }}>
                  {lang === 'zh' ? '回報不當內容' : 'Report'}
                </button>
                <button type="button" className="link-btn" disabled={!!busyId}
                  onClick={() => act(r.review_id, 'blocked')}
                  style={{ fontSize: 10.5, background: 'none', border: 0,
                           color: 'var(--fg-3)', cursor: 'pointer', padding: 0 }}>
                  {lang === 'zh' ? '唔再顯示呢位作者' : 'Hide this author'}
                </button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Profile · My reviews — every rating and comment this nurse has written,
// with its moderation state.
//
// ‼️ THIS PAGE IS WHAT MAKES "SAVED PRIVATELY FIRST" HONEST. A review is held
// at status='pending' and counts toward nothing until an admin publishes it.
// Without a place to see her own words afterwards, that is indistinguishable
// from having been silently dropped — and the next thing she does is write it
// again, or stop writing.
//
// Everything here comes from get_my_reviews(), which is own-rows-only
// server-side. There is deliberately no path — here or anywhere — to read
// another nurse's review row; other people's opinions reach this app only as
// aggregates.
// ─────────────────────────────────────────────────────────────────
function MyReviewsScreen({ lang, onBack, onOpenCourse }) {
  const [loading, setLoading] = React.useState(true);
  const [rows, setRows] = React.useState([]);
  const [error, setError] = React.useState(null);
  // Reviews are switched off for this database — not migrated yet, or a kill
  // switch was pulled. A distinct state, NOT an error: see the note below.
  const [unavailable, setUnavailable] = React.useState(false);
  // The review being edited in a sheet, and the one awaiting a withdraw
  // confirmation. Editing from here is the second half of "she can find what
  // she wrote": seeing it is useless if she cannot change her mind.
  const [editing, setEditing] = React.useState(null);
  const [withdrawing, setWithdrawing] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const { data, error: err, missing } = await window.DB.fetchMyReviews();
      if (missing) { setUnavailable(true); return; }
      if (err) setError(err.message || String(err));
      else { setRows(data || []); setError(null); }
    } catch (e) {
      setError(e?.message || String(e));
    } finally { setLoading(false); }
  }, []);

  // ‼️ THE BADGE DESCRIBES THE COMMENT, NOT THE REVIEW. A rating is never
  // moderated, so a star-only review has NOTHING pending — showing "審核中" on
  // one used to read as though Vital were deciding whether to allow her 4★.
  // body_status 'none' means "no comment", and the card says so by simply not
  // showing a comment badge at all.
  // ‼️ THIS EFFECT MUST STAY BELOW `load`. `const` is hoisted into a temporal
  // dead zone, so referencing it in the dependency array from ABOVE its
  // declaration throws "Cannot access 'load' before initialization" DURING
  // RENDER — React then unmounts the tree and the screen goes blank with no
  // console error a user would ever see. That is exactly what shipped on
  // 2026-08-12: 我的評價 rendered as an empty page.
  React.useEffect(() => { load(); }, [load]);

  // ‼️ NEVER SHOW THE RAW ERROR HERE. On 2026-08-11 this screen rendered
  // "Could not find the function public.get_my_reviews without parameters in
  // the schema cache" — PostgREST internals, in English, on a Chinese page —
  // because the client shipped ahead of the reviews migration. That is an
  // ordinary deploy-order state and must read as "the feature is off".
  const badge = (bodyStatus, hasBody) => {
    if (!hasBody || bodyStatus === 'none') return null;
    if (bodyStatus === 'published') return { tone: 'tone-success', zh: '評語已公開', en: 'Comment published' };
    if (bodyStatus === 'rejected')  return { tone: 'tone-danger',  zh: '評語未能公開', en: 'Comment not published' };
    return { tone: 'tone-warning', zh: '評語審核中', en: 'Comment under review' };
  };

  const chipLabel = (key) => {
    const all = (window.REVIEW_COURSE_CHIPS || []).concat(window.REVIEW_PROVIDER_CHIPS || []);
    const found = all.find(c => c.key === key);
    return found ? (lang === 'zh' ? found.zh : found.en) : key;
  };

  return (
    <div className="scroll fade-in" style={{ paddingBottom: 80 }}>
      <ScreenHeader lang={lang} onBack={onBack}
        title={lang === 'zh' ? '我的評價' : 'My reviews'} />

      <div style={{ padding: '4px 20px 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
          {lang === 'zh'
            ? '只有你自己睇到呢一頁。評價經審核後,會以匿名統計計入課程評分 — 唔會顯示你嘅名。'
            : 'Only you can see this page. Once reviewed, ratings count toward the course score as anonymous aggregates — never under your name.'}
        </div>

        {loading && <div className="muted" style={{ fontSize: 13 }}>{lang === 'zh' ? '載入中…' : 'Loading…'}</div>}
        {error && !unavailable && <div style={{ fontSize: 12.5, color: 'var(--danger)' }}>{error}</div>}

        {!loading && unavailable && (
          <div className="card" style={{ textAlign: 'center', padding: '26px 18px' }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 4 }}>
              {lang === 'zh' ? '評價功能暫未開放' : 'Reviews are not available yet'}
            </div>
            <div className="muted-2" style={{ fontSize: 12, lineHeight: 1.5 }}>
              {lang === 'zh'
                ? '呢個功能仲未喺你嘅帳戶開啟。開啟後,你寫過嘅評價會喺呢度出現。'
                : "This feature isn't switched on yet. Anything you write will appear here once it is."}
            </div>
          </div>
        )}

        {!loading && !error && !unavailable && rows.length === 0 && (
          <div className="card" style={{ textAlign: 'center', padding: '26px 18px' }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 4 }}>
              {lang === 'zh' ? '仲未有評價' : 'No reviews yet'}
            </div>
            <div className="muted-2" style={{ fontSize: 12, lineHeight: 1.5 }}>
              {lang === 'zh'
                ? '喺「我的紀錄 → 已完成紀錄」揀一個課程就可以評價。'
                : 'Rate a course from My records → History.'}
            </div>
          </div>
        )}

        {rows.map(r => {
          const b = badge(r.body_status || r.status, !!r.body);
          const title = lang === 'zh' ? (r.title_zh || r.title_en) : (r.title_en || r.title_zh);
          const chips = (r.course_chips || []).concat(r.provider_chips || []);
          return (
            <div key={r.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                <button type="button"
                  onClick={() => onOpenCourse && r.course_id && onOpenCourse(r.course_id)}
                  style={{ flex: 1, minWidth: 0, textAlign: 'left', background: 'transparent',
                           border: 0, padding: 0, font: 'inherit', color: 'inherit',
                           cursor: onOpenCourse ? 'pointer' : 'default' }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 }}>
                    {title || r.course_id}
                  </div>
                </button>
                {b && (
                  <span className={'cap-pill ' + b.tone} style={{ flexShrink: 0 }}>
                    {lang === 'zh' ? b.zh : b.en}
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--warning)' }}>
                  <Icon name="star" size={13} stroke={1.8} color="currentColor" fill="currentColor" />
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{r.course_stars}</span>
                </span>
                {r.provider_stars != null && (
                  <span className="muted-2" style={{ fontSize: 11.5 }}>
                    {lang === 'zh' ? `機構 ${r.provider_stars}` : `Provider ${r.provider_stars}`}
                  </span>
                )}
                <span className="muted-2" style={{ fontSize: 11 }}>
                  {fmtShort(String(r.created_at).slice(0, 10), lang, true)}
                </span>
              </div>

              {chips.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {chips.map(k => <span key={k} className="chip">{chipLabel(k)}</span>)}
                </div>
              )}

              {r.body && (
                <div style={{ fontSize: 12.5, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                              padding: '8px 10px', borderRadius: 8, background: 'var(--bg-2)' }}>
                  {r.body}
                </div>
              )}

              {/* A rejection must say why, or it is just a disappearance. */}
              {(r.body_status || r.status) === 'rejected' && r.admin_notes && (
                <div style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--danger)' }}>
                  {lang === 'zh' ? '審核備註:' : 'Reviewer note: '}{r.admin_notes}
                </div>
              )}
              {/* Two lines, never one ambiguous badge: her rating counts even
                  when the comment is still queued or was refused. */}
              {typeof ReviewStatusLines === 'function' && (
                <ReviewStatusLines bodyStatus={r.body_status || r.status}
                                   hasBody={!!r.body} lang={lang} />
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                <button type="button" className="btn btn-ghost" style={{ flex: 1 }}
                  onClick={() => setEditing({ courseId: r.course_id, title })}>
                  {r.editable === false
                    ? (lang === 'zh' ? '查看' : 'View')
                    : (lang === 'zh' ? '修改' : 'Edit')}
                </button>
                {/* Withdrawing is always allowed, even past the 30-day edit
                    window — taking down your own words is not editing. */}
                <button type="button" className="btn btn-ghost"
                  style={{ flex: 0, color: 'var(--danger)' }}
                  onClick={() => setWithdrawing(r)}>
                  {lang === 'zh' ? '撤回' : 'Withdraw'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && typeof CourseReviewSheet === 'function' && (
        <CourseReviewSheet
          open={true} lang={lang}
          courseId={editing.courseId} title={editing.title}
          onSaved={load}
          onClose={() => { setEditing(null); load(); }} />
      )}

      {withdrawing && (
        <Sheet open={true} onClose={() => setWithdrawing(null)}
               title={lang === 'zh' ? '撤回評價' : 'Withdraw review'}>
          <div style={{ padding: '4px 20px 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, lineHeight: 1.55 }}>
              {lang === 'zh'
                ? '撤回之後,你嘅評分同評語會即刻消失,亦唔會再計入課程評分。呢個動作唔可以復原。'
                : 'Your rating and comment disappear immediately and stop counting toward the course score. This cannot be undone.'}
            </div>
            <button className="btn btn-primary" style={{ background: 'var(--danger)' }}
              onClick={async () => {
                try { await window.DB.withdrawCourseReview(withdrawing.course_id); }
                finally { setWithdrawing(null); load(); }
              }}>
              {lang === 'zh' ? '確認撤回' : 'Withdraw'}
            </button>
            <button className="btn btn-ghost" onClick={() => setWithdrawing(null)}>
              {lang === 'zh' ? '取消' : 'Cancel'}
            </button>
          </div>
        </Sheet>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Profile · My contributions — standalone page listing this user's
// submissions and reports with their admin-review state. Closes the
// loop after submitCourse / reportCourseIssue: before this page, a
// contributor had no way to see what they sent in or whether the
// admin had acted. RLS confines the fetches to the caller's rows.
// ─────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────
// Edit one of my own submissions.
//
// ‼️ WHY THIS EXISTS AT ALL. One of the four real duplicate pairs was a
// CORRECTION, not a mistake: the AI read the provider as "Hong Kong College
// ofPaediatric Society", and re-submitting was the only way the nurse had to fix
// it. A duplicate guard without an edit path would have turned that into a wall
// — "you already submitted this" with no way to change what she submitted.
//
// ‼️ THE SERVER STILL DECIDES. edit_own_submission re-runs the same self-scoped
// guard against every OTHER open submission, so this sheet renders refusals
// rather than assuming a save succeeded.
// ─────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────
// ProviderPicker — the submitter says WHICH provider, in her own words AND as
// an entity (RELEASE.md §92, B1-C).
//
// ‼️ TWO FACTS, TWO SLOTS, AND THE PICKER MAY NEVER COLLAPSE THEM.
//
//     rawText   what she / the poster / the AI actually wrote   → observation
//     selection the entity she picked, id + canonical labels    → identity claim
//
// Selecting X must NOT rewrite rawText. The durable payload keeps
// provider_full_en = rawText, and §90 hands the resolver the hint AND that raw
// text as two independent sources — a disagreement between them is a fact the
// resolver is built to act on. Overwriting the text with the canonical label
// would manufacture agreement, and would destroy the evidence Step 2C classifies
// alias candidates with: "she picked X while writing 養和" is exactly the pair
// that teaches the vocabulary. Substituting the label answers "what is X called"
// — a question nobody asked.
//
// ‼️ THE GUARD TEXT IS DERIVED, NOT A THIRD STATE. `providerGuardText` is
// computed from the two above. Storing it would create a state where the
// selection says X and the guard text says something else, which cannot happen
// in the real world and would then need an effect to keep it from happening.
//
// ‼️ AND IT IS EN-FIRST, NOT LOCALE-FIRST. The duplicate guard fingerprints
// provider_full_en, so keying it on the displayed label would change guardKey —
// and re-run the candidate check — merely because the user switched the app to
// English. The displayed label follows the locale; the guard input does not.
// ‼️ A SELECTION IS AN ANSWER TO THE TEXT IT WAS MADE AGAINST, and stops being
// one the moment that text changes. The nurse picks "Hospital Authority" while
// the provider reads HA, then edits the provider to a different body — the old
// entity id is now an answer to a question nobody asked, and sending it would
// attach her submission to an organisation she never named.
//
// So the id is withheld and the picker is put back into search: `stale` is not
// an error state, it is "ask again". Both halves come from one function because
// the UI and the wire must never disagree about whether a hint counts — showing
// a confirmed chip while sending null, or the reverse, is the failure this
// prevents.
//
// Normalises both sides: callers must not be able to make it wrong by passing a
// trimmed value on one side and a raw one on the other.
function providerHintState(selection, currentText) {
  const norm = (t) => String(t == null ? '' : t).trim();
  const stale = !!selection && norm(selection.forText) !== norm(currentText);
  return { stale, entityId: stale ? null : (selection?.entityId || null) };
}

const providerGuardTextOf = (rawText, selection) => (selection
  ? (selection.labelEn || selection.labelZh || '')
  : rawText);

// Local typeahead over ~105 rows (measured live): no pagination, no remote
// search. Matches EN and ZH so 「養和」 finds an entity whose English label
// shares no characters with it.
function providerMatches(query, limit) {
  const labels = (typeof window !== 'undefined' && window.PROVIDER_LABELS) || null;
  const q = String(query || '').trim().toLowerCase();
  if (!labels || !labels.size || !q) return [];
  const out = [];
  labels.forEach((v, id) => {
    const en = String(v?.en || '');
    const zh = String(v?.zh || '');
    if (en.toLowerCase().includes(q) || zh.toLowerCase().includes(q)) {
      out.push({ entityId: id, labelEn: en || null, labelZh: zh || null });
    }
  });
  out.sort((a, b) => String(a.labelEn || a.labelZh || '').localeCompare(String(b.labelEn || b.labelZh || '')));
  return out.slice(0, limit || 8);
}

// ‼️ THE PANEL MUST STAY INSIDE EVERY CLIPPING ANCESTOR, NOT JUST THE NEAREST.
// Measured on the real Sheet: `.sheet` is `overflow:hidden` with `max-height:90%`
// and the body components.jsx renders inside it is `{flex:1, overflowY:'auto'}`.
// A dropdown opened near the bottom overflowed BOTH by 114px of its 158 — and
// because it is absolutely positioned it does not extend scrollHeight, so it
// scrolls WITH the content and the hidden options can never be reached. Not a
// cosmetic overlap: those provider entities were unselectable.
//
// The visible band is the INTERSECTION of every ancestor that clips, plus the
// viewport. Taking only the scroll body would miss `.sheet`; taking only the
// sheet would miss the body.
const providerClipBand = (el) => {
  let top = 0;
  let bottom = (typeof window !== 'undefined' ? window.innerHeight : 0);
  let node = el && el.parentElement;
  while (node && node !== document.body) {
    const st = window.getComputedStyle(node);
    if (st.overflow !== 'visible' || st.overflowY !== 'visible') {
      const r = node.getBoundingClientRect();
      top = Math.max(top, r.top);
      bottom = Math.min(bottom, r.bottom);
    }
    node = node.parentElement;
  }
  return { top, bottom };
};

// ‼️ NO MINIMUM HEIGHT. A floor larger than the available space is the same
// clipping bug arriving by another route — the panel would again extend past the
// band it was just measured against. A two-row list that scrolls is usable; a
// four-row list with two rows unreachable is not.
const PROVIDER_PANEL_GUTTER = 8;
function providerPanelGeometry(inputEl) {
  if (!inputEl) return { dir: 'down', maxHeight: 320 };
  const r = inputEl.getBoundingClientRect();
  const band = providerClipBand(inputEl);
  const below = band.bottom - r.bottom - PROVIDER_PANEL_GUTTER;
  const above = r.top - band.top - PROVIDER_PANEL_GUTTER;
  const wantsDown = below >= 160 || below >= above;
  return {
    dir: wantsDown ? 'down' : 'up',
    maxHeight: Math.max(0, Math.round(wantsDown ? below : above)),
  };
}

function ProviderPicker({ lang, rawText, onRawText, selection, onSelect, placeholder }) {
  const zh = lang === 'zh';
  // ‼️ THREE STATES, AND AN EMPTY MAP IS TWO OF THEM. Rendering 「找不到相符機構」
  // for a dictionary that has merely not arrived pushes the nurse into free text
  // and loses the entity she could have picked — so 'loading' and 'failed' say
  // what they are instead of impersonating "no match".
  const status = (typeof window !== 'undefined' && window.PROVIDER_LABELS_STATUS) || 'loading';
  const dictSize = ((typeof window !== 'undefined' && window.PROVIDER_LABELS) || new Map()).size;
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef(null);
  const [geom, setGeom] = React.useState({ dir: 'down', maxHeight: 320 });
  const matches = React.useMemo(
    () => (status === 'ready' && !selection ? providerMatches(rawText, 8) : []),
    [rawText, selection, status, dictSize],
  );

  // ‼️ RE-MEASURED, NOT MEASURED ONCE. The band moves when the sheet scrolls or
  // the window resizes, and a panel positioned from a stale rect is clipped
  // again — silently, because nothing throws. Cheap: a rAF-throttled read of a
  // few rects, and only while the panel is open.
  React.useEffect(() => {
    if (!open || !matches.length) return undefined;
    let frame = 0;
    const measure = () => { frame = 0; setGeom(providerPanelGeometry(inputRef.current)); };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
    measure();
    window.addEventListener('resize', schedule);
    // capture: the scroll happens on an ancestor, and scroll does not bubble.
    window.addEventListener('scroll', schedule, true);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
    };
  }, [open, matches.length, rawText]);

  // SELECTED — the canonical label is displayed, the input is read-only, and the
  // only way out is an explicit action that also clears the hint. A silent
  // auto-clear on edit would leave no moment where the UI says what happened,
  // and a nameable state is one a test can assert.
  if (selection) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 11px',
                      border: '0.5px solid var(--border)', borderRadius: 10, background: 'var(--surface)' }}>
          <Icon name="check" size={15} stroke={2} color="var(--primary)" />
          <span style={{ fontSize: 13.5, flex: 1 }}>
            {zh ? (selection.labelZh || selection.labelEn) : (selection.labelEn || selection.labelZh)}
          </span>
        </div>
        {/* ‼️ 「待確認」, NEVER 「已驗證」 (§89). The submitter picked it; no
            reviewer has ratified it and the resolver may still refuse it. */}
        <div className="muted-2" style={{ fontSize: 11 }}>
          {zh ? '已配對到現有機構 · 待確認' : 'Matched to an existing organisation · pending confirmation'}
        </div>
        <button type="button" className="btn btn-ghost" style={{ alignSelf: 'flex-start', fontSize: 12 }}
                onClick={() => { onSelect(null); setOpen(false); }}>
          {zh ? '改用其他機構' : 'Use a different organisation'}
        </button>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <input ref={inputRef} className="field-input" value={rawText} placeholder={placeholder}
             onChange={(e) => { onRawText(e.target.value); setOpen(true); }}
             onFocus={() => setOpen(true)} />
      {status === 'loading' && (
        <div className="muted-2" style={{ fontSize: 11, marginTop: 4 }}>
          {zh ? '正在載入機構名單…' : 'Loading organisations…'}
        </div>
      )}
      {status === 'failed' && (
        <div className="muted-2" style={{ fontSize: 11, marginTop: 4 }}>
          {zh ? '機構名單暫時無法載入 — 直接輸入名稱即可。'
              : 'Organisation list unavailable — type the name instead.'}
        </div>
      )}
      {status === 'ready' && open && rawText.trim() && !matches.length && (
        <div className="muted-2" style={{ fontSize: 11, marginTop: 4 }}>
          {zh ? '沒有相符的機構 — 直接輸入名稱即可。' : 'No matching organisation — type the name instead.'}
        </div>
      )}
      {open && matches.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 40, left: 0, right: 0,
                      ...(geom.dir === 'up'
                        ? { bottom: '100%', marginBottom: 4 }
                        : { top: '100%', marginTop: 4 }),
                      maxHeight: geom.maxHeight,
                      // ‼️ auto, NOT hidden. When neither side fits, the options
                      // that do not fit must stay REACHABLE; hiding them is the
                      // defect this block exists to remove.
                      overflowY: 'auto', overflowX: 'hidden',
                      border: '0.5px solid var(--border)', borderRadius: 10, background: 'var(--bg)',
                      boxShadow: '0 10px 30px -12px rgba(0,0,0,0.25)' }}>
          {matches.map((m) => (
            <button key={m.entityId} type="button" className="btn btn-ghost"
                    style={{ display: 'block', width: '100%', textAlign: 'left', borderRadius: 0,
                             padding: '9px 11px', fontSize: 13 }}
                    onClick={() => { onSelect(m); setOpen(false); }}>
              <div>{m.labelEn || m.labelZh}</div>
              {m.labelZh && m.labelZh !== m.labelEn && (
                <div className="muted-2" style={{ fontSize: 11 }}>{m.labelZh}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EditSubmissionSheet({ lang, submission, onClose, onSaved }) {
  const p = submission.payload || {};
  const [form, setForm] = React.useState({
    titleEn: p.title_en || '', titleZh: p.title_zh || '',
    provider: p.provider_full_en || p.provider_full_zh || '',
    points: p.cne_points ?? '', venue: p.venue_en || '',
  });
  // ‼️ SEEDED FROM THE PERSISTED HINT, AND `touched` IS WHAT MAKES `_set` HONEST.
  // A sheet that always sent `_set: true` would rewrite the hint on every edit —
  // including edits made while the dictionary had not loaded, where the seeded
  // selection is null and the nurse's real selection would be silently cleared.
  // The flag says "the user acted on the picker", nothing else.
  const seededHint = submission.provider_entity_id_hint || null;
  const seededLabels = seededHint && typeof window !== 'undefined' && window.PROVIDER_LABELS
    ? window.PROVIDER_LABELS.get(String(seededHint)) : null;
  const [providerSelection, setProviderSelection] = React.useState(
    seededHint ? { entityId: seededHint, labelEn: seededLabels?.en || null, labelZh: seededLabels?.zh || null } : null,
  );
  const [providerTouched, setProviderTouched] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [confirm, setConfirm] = React.useState(null);

  const save = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      // ‼️ CHECK THE STATUS AND STOP. cnePointsForSubmission THROWS on invalid
      // rather than returning null, because "the nurse typed something
      // impossible" and "the nurse said nothing" are the two facts issue #42
      // exists to keep apart. Today all three doors silently wrote a negative
      // through; refusing it is the contract being enforced, not new policy.
      {
        const pts = normalizeCnePointsInput(form.points);
        if (pts.status === 'invalid') {
          setError(lang === 'zh' ? 'CNE 分數無效,請輸入 0 或正數。'
                                 : 'CNE points are invalid — enter 0 or a positive number.');
          setBusy(false);
          return;
        }
      }
      const payload = {
        ...p,
        title_en: form.titleEn.trim() || null,
        title_zh: form.titleZh.trim() || null,
        provider_full_en: form.provider.trim() || null,
        cne_points: cnePointsForSubmission(form.points),
        venue_en: form.venue.trim() || null,
      };
      const { data, error: e } = await window.DB.editMySubmission({
        submissionId: submission.id,
        // Only a real interaction patches the identity evidence. Untouched, the
        // server leaves the stored hint exactly as it was.
        providerEntityIdHint: providerSelection?.entityId || null,
        providerEntityIdHintSet: providerTouched,
        payload,
        confirmToken: confirm?.token || null,
      });
      if (e) { setError(e.message || (lang === 'zh' ? '儲存失敗' : 'Could not save')); setBusy(false); return; }
      if (data?.ok) { onSaved(); return; }
      // A refusal, not a crash — say which kind, because they need different
      // things from her.
      if (data?.code === 'NEEDS_CONFIRMATION') {
        setConfirm({ token: data.confirm_token });
        setError(lang === 'zh'
          ? '這與你另一筆提交很相似。如果確定是不同的一場，再按一次儲存。'
          : 'This looks like another of your submissions. If it is a different session, tap save again.');
      } else if (data?.code === 'ALREADY_PENDING' || data?.code === 'ALREADY_APPROVED') {
        setError(lang === 'zh'
          ? '你已經提交過完全相同的課程，所以這次修改沒有儲存。'
          : 'You already have an identical submission, so this edit was not saved.');
      } else if (data?.code === 'PENDING_EDIT_DISABLED') {
        setError(lang === 'zh'
          ? '這筆提交已在審核中，暫時未能修改。可以先撤回再重新提交。'
          : 'This submission is under review and cannot be edited yet. You can withdraw it and submit again.');
      } else {
        setError(lang === 'zh' ? '未能修改這筆提交。' : 'This submission could not be edited.');
      }
      setBusy(false);
    } catch (err) {
      setError(err?.message || (lang === 'zh' ? '儲存失敗' : 'Could not save'));
      setBusy(false);
    }
  };

  const field = (label, key, props = {}) => (
    <div className="field">
      <div className="field-label">{label}</div>
      <input className="field-input" value={form[key]} {...props}
             onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
    </div>
  );

  return (
    <Sheet open onClose={onClose} title={lang === 'zh' ? '修改提交' : 'Edit submission'}>
      <div style={{ padding: '4px 20px 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="muted" style={{ fontSize: 12, lineHeight: 1.5 }}>
          {lang === 'zh'
            ? '修改後會重新排隊等待審核。之前的版本會保留在紀錄中。'
            : 'Edits go back into the review queue. The previous version is kept in the record.'}
        </div>
        {field(lang === 'zh' ? '英文課程名稱' : 'English course title', 'titleEn')}
        {field(lang === 'zh' ? '中文課程名稱' : 'Chinese course title', 'titleZh')}
        <div className="field">
          <div className="field-label">{lang === 'zh' ? '主辦機構' : 'Provider'}</div>
          {/* Same two slots as the submit sheet: the raw wording stays in
              form.provider and reaches payload.provider_full_en unchanged; the
              selection patches provider_entity_id_hint. `touched` is set by any
              interaction so an untouched sheet sends _set:false and the server
              leaves the stored hint alone. */}
          <ProviderPicker
            lang={lang}
            rawText={form.provider}
            onRawText={(v) => setForm({ ...form, provider: v })}
            selection={providerSelection}
            onSelect={(sel) => { setProviderSelection(sel); setProviderTouched(true); }}
            placeholder={lang === 'zh' ? '主辦機構全名' : 'Organising body'} />
        </div>
        {field(lang === 'zh' ? 'CNE 分數' : 'CNE points', 'points', { type: 'number', inputMode: 'decimal' })}
        {field(lang === 'zh' ? '地點' : 'Venue', 'venue')}
        {error && (
          <div style={{ fontSize: 12, color: confirm ? 'var(--warning)' : 'var(--danger)', padding: '8px 10px',
                        background: `color-mix(in oklch, ${confirm ? 'var(--warning)' : 'var(--danger)'} 12%, transparent)`,
                        borderRadius: 8, lineHeight: 1.5 }}>
            {error}
          </div>
        )}
        <LoadingButton className="btn btn-primary btn-fill" isLoading={busy}
                       disabled={!form.titleEn.trim() && !form.titleZh.trim()}
                       loadingLabel={lang === 'zh' ? '儲存中…' : 'Saving…'}
                       leftIcon={<Icon name="check" size={16} stroke={2.2} />}
                       onClick={save}>
          {confirm ? (lang === 'zh' ? '確認並儲存' : 'Confirm and save')
                   : (lang === 'zh' ? '儲存' : 'Save')}
        </LoadingButton>
      </div>
    </Sheet>
  );
}

function MyContributionsScreen({ lang, userId, onBack, onOpenCourse, onGoDiscover }) {
  const [loading, setLoading] = React.useState(true);
  // The two affordances that exist so "submit it again" stops being the only
  // way to fix a mis-scanned field. `editing` holds the row being corrected;
  // `withdrawingSub` the one being taken down (which never deletes it).
  const [editing, setEditing] = React.useState(null);
  const [withdrawingSub, setWithdrawingSub] = React.useState(null);
  // ‼️ THE SERVER OWNS WHETHER PENDING IS EDITABLE, AND THE UI ASKS IT.
  // edit_own_submission already refuses a pending row while
  // submission_pending_edit_enabled() is false, so this is not the security
  // boundary — it exists so the nurse is not offered a 修改 button that the
  // server will decline. Draft editing and withdrawal are unaffected: neither
  // depends on the activation, and withdrawal is how she corrects a pending
  // submission until it lands. Defaults to OFF, so a failed read hides the
  // button rather than promising something that will not work.
  const [pendingEditOn, setPendingEditOn] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const on = await window.DB.isPendingEditEnabled();
      if (!cancelled) setPendingEditOn(on);
    })();
    return () => { cancelled = true; };
  }, []);
  const [submissions, setSubmissions] = React.useState([]);
  const [reports, setReports] = React.useState([]);
  const [feedback, setFeedback] = React.useState([]);
  const [error, setError] = React.useState(null);
  // Segmented view — one long scroll buried whichever section came last (a
  // user with 20+ reports never saw their comments). Tab ids mirror the
  // admin queue's vocabulary. repFilter is a client-side status filter for
  // the reports tab only (the one list that realistically grows long).
  const [tab, setTab] = React.useState('submissions'); // 'submissions' | 'reports' | 'feedback'
  const [repFilter, setRepFilter] = React.useState('all');
  // Contribution rewards: lifetime AI-scan credits earned (ledger bonus rows)
  // + the current usable balance. Both null when unavailable → strip adapts.
  const [rewards, setRewards] = React.useState(null);
  const [credit, setCredit] = React.useState(null);
  // Pick the default tab once per mount, after the first load: land on the
  // first section that has content so the page never opens onto an empty tab
  // while another tab holds the user's actual history.
  const tabInitRef = React.useRef(false);

  const load = async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const [sRes, rRes, fRes, rw, cs] = await Promise.all([
        window.DB.fetchMySubmissions(userId),
        window.DB.fetchMyReports(userId),
        window.DB.fetchMyFeedback(userId),
        // Both reward fetches are non-fatal (they return null on error, never
        // throw) — a ledger hiccup must not blank the contribution history.
        window.DB.fetchMyContributionRewards(userId),
        window.DB.getScanCreditStatus(),
      ]);
      if (sRes.error) throw sRes.error;
      if (rRes.error) throw rRes.error;
      if (fRes.error) throw fRes.error;
      setSubmissions(sRes.data || []);
      setReports(rRes.data || []);
      setFeedback(fRes.data || []);
      setRewards(rw);
      setCredit(cs);
      // Fresh data can empty the currently-selected report bucket (its chip
      // would vanish, stranding the filter) — reset to 'all' on every load.
      setRepFilter('all');
      if (!tabInitRef.current) {
        tabInitRef.current = true;
        const s = sRes.data || [], r = rRes.data || [], f = fRes.data || [];
        setTab(s.length ? 'submissions' : r.length ? 'reports' : f.length ? 'feedback' : 'submissions');
      }
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [userId]);

  // Status pill mirrors ContributorHistoryDialog so the visual
  // language is consistent between the contributor's own view and
  // the admin's. Same tone keys → same colours.
  const StatusPill = ({ s }) => {
    const map = {
      draft:     { tone: 'muted',   en: 'draft',     zh: '草稿' },
      pending:   { tone: 'muted',   en: 'pending',   zh: '待審核' },
      approved:  { tone: 'success', en: 'approved',  zh: '已通過' },
      rejected:  { tone: 'danger',  en: 'rejected',  zh: '未通過' },
      duplicate: { tone: 'muted',   en: 'duplicate', zh: '重複' },
      withdrawn: { tone: 'muted',   en: 'withdrawn', zh: '已撤回' },
      accepted:  { tone: 'primary', en: 'accepted',  zh: '已接受' },
    };
    const info = map[s] || { tone: 'muted', en: s, zh: s };
    return (
      <span className={'cap-pill tone-' + info.tone} style={{ fontSize: 10 }}>
        {lang === 'zh' ? info.zh : info.en}
      </span>
    );
  };

  // Shared 3-dot progress rail. Callers pass a `steps` array of { tone, label };
  // tone drives the dot + connector colour ('done' | 'active' | 'danger' |
  // 'muted' | 'todo'). Extracted so the submission/report tracker and the
  // feedback tracker render with identical visual language.
  const StepTrack = ({ steps }) => {
    const dotColor = (t) => t === 'done' ? 'var(--success)'
      : t === 'active' ? 'var(--primary)'
      : t === 'danger' ? 'var(--danger)'
      : t === 'muted' ? 'var(--fg-2)'
      : 'transparent';
    const lineColor = (next) => next.tone === 'todo' ? 'var(--border)'
      : next.tone === 'active' ? 'var(--primary)'
      : next.tone === 'danger' ? 'var(--danger)'
      : next.tone === 'muted' ? 'var(--border)'
      : 'var(--success)';
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', padding: '2px 4px 0' }}>
        {steps.map((st, i) => {
          const filled = st.tone !== 'todo';
          return (
            <React.Fragment key={i}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: '0 0 auto' }}>
                <div style={{
                  width: 12, height: 12, borderRadius: '50%',
                  background: filled ? dotColor(st.tone) : 'transparent',
                  border: filled ? 'none' : '1.5px solid var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {st.tone === 'active' && (
                    <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#fff' }} />
                  )}
                </div>
                <span style={{
                  fontSize: 9.5, lineHeight: 1.1, whiteSpace: 'nowrap',
                  color: 'var(--fg-2)', opacity: filled ? 1 : 0.6,
                  fontWeight: st.tone === 'active' ? 600 : 400,
                }}>{st.label}</span>
              </div>
              {i < steps.length - 1 && (
                <div style={{ flex: 1, height: 1.5, marginTop: 5, borderRadius: 2, background: lineColor(steps[i + 1]) }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // A submission/report is a small workflow: submitted → reviewed → outcome.
  // The outcome stage takes its tone/label from how the review resolved
  // (accepted / not accepted / duplicate), and stays a hollow "pending" dot
  // while still under review.
  const ProgressTrack = ({ status, finalLabels }) => {
    const resolved = status !== 'pending';
    const final = !resolved
      ? { tone: 'todo', label: lang === 'zh' ? '結果' : 'Outcome' }
      : status === 'rejected'
        ? { tone: 'danger', label: finalLabels.rejected }
        : status === 'duplicate'
          ? { tone: 'muted', label: finalLabels.duplicate }
          : { tone: 'done', label: finalLabels.approved };
    return <StepTrack steps={[
      { tone: 'done', label: lang === 'zh' ? '已提交' : 'Submitted' },
      { tone: resolved ? 'done' : 'active', label: lang === 'zh' ? '審核中' : 'In review' },
      final,
    ]} />;
  };

  // A comment/suggestion workflow: submitted → in review → replied/resolved.
  // 'new'/'reviewing' sit in review; a team reply or 'resolved' completes it;
  // an 'archived' item is closed without a distinct positive outcome.
  const FeedbackTrack = ({ status, hasReply }) => {
    const inReview = status === 'new' || status === 'reviewing';
    const final = inReview
      ? { tone: 'todo', label: lang === 'zh' ? '回覆' : 'Reply' }
      : status === 'archived'
        ? { tone: 'muted', label: lang === 'zh' ? '已結束' : 'Closed' }
        : { tone: 'done', label: hasReply ? (lang === 'zh' ? '已回覆' : 'Replied') : (lang === 'zh' ? '已處理' : 'Resolved') };
    return <StepTrack steps={[
      { tone: 'done', label: lang === 'zh' ? '已提交' : 'Submitted' },
      { tone: inReview ? 'active' : 'done', label: lang === 'zh' ? '處理中' : 'In review' },
      final,
    ]} />;
  };

  // Feedback status pill — its own vocabulary (new/reviewing/resolved/archived),
  // distinct from the submission/report StatusPill above.
  const FeedbackStatusPill = ({ s, hasReply }) => {
    const map = {
      new:       { tone: 'muted',   en: 'new',       zh: '待處理' },
      reviewing: { tone: 'primary', en: 'reviewing', zh: '處理中' },
      resolved:  { tone: 'success', en: hasReply ? 'replied' : 'resolved', zh: hasReply ? '已回覆' : '已處理' },
      archived:  { tone: 'muted',   en: 'closed',    zh: '已結束' },
    };
    const info = map[s] || { tone: 'muted', en: s, zh: s };
    return (
      <span className={'cap-pill tone-' + info.tone} style={{ fontSize: 10 }}>
        {lang === 'zh' ? info.zh : info.en}
      </span>
    );
  };

  // Category chip meta for a feedback row (mirrors AdminFeedbackCard's tones).
  const feedbackCat = (c) => ({
    suggestion: { tone: 'success', en: 'Suggestion', zh: '建議' },
    bug:        { tone: 'danger',  en: 'Bug',        zh: '問題' },
    other:      { tone: 'muted',   en: 'Other',      zh: '其他' },
  })[c] || { tone: 'muted', en: 'Other', zh: '其他' };

  // Explicit, intentional navigation to the course — replaces the old
  // whole-card tap target so reading the card never navigates by accident.
  const ViewCourseBtn = ({ courseId }) => (
    <button
      className="btn btn-ghost"
      style={{ alignSelf: 'flex-start', padding: '5px 11px', fontSize: 12 }}
      onClick={() => onOpenCourse && onOpenCourse(courseId)}
    >
      {lang === 'zh' ? '查看課程詳情' : 'View course'}
      <Icon name="arrow-r" size={13} stroke={1.8} />
    </button>
  );

  // Reports store only course_id — resolve to a display title against
  // the local COURSES catalogue; fall back to the id if not loaded yet.
  const courseTitle = (courseId) => {
    const c = (typeof COURSES !== 'undefined' ? COURSES : []).find(x => x.id === courseId);
    return c ? L(c.title, lang) : courseId;
  };

  const fmtWhen = (iso) => iso ? iso.slice(0, 10) : '';

  // Reports-tab status filter. Buckets: 審核中 (pending), 已採納 (accepted —
  // applied_at is authoritative, matching the card's tracker), 未通過
  // (rejected / duplicate). Chips only appear once the list is long enough
  // that scanning it hurts — short lists stay chrome-free.
  const REPORT_FILTER_MIN = 6;
  const repBucket = (r) => (r.applied_at || r.status === 'accepted') ? 'accepted'
    : r.status === 'pending' ? 'pending'
    : 'closed';
  const repTally = reports.reduce((acc, r) => {
    const b = repBucket(r); acc[b] = (acc[b] || 0) + 1; return acc;
  }, {});
  const repFilterLabels = {
    all:      { zh: '全部',   en: 'All' },
    pending:  { zh: '審核中', en: 'In review' },
    accepted: { zh: '已採納', en: 'Accepted' },
    closed:   { zh: '未通過', en: 'Not accepted' },
  };
  const visibleReports = repFilter === 'all' ? reports : reports.filter(r => repBucket(r) === repFilter);

  return (
    <div className="scroll fade-in" style={{ paddingBottom: 80 }}>
      <ScreenHeader
        title={lang === 'zh' ? '我的貢獻' : 'My contributions'}
        onBack={onBack}
        actions={
          <button className="icon-btn" onClick={load}
            aria-label={lang === 'zh' ? '重新整理' : 'Refresh'}
            disabled={loading}>
            <Icon name="refresh" size={16} stroke={1.7} />
          </button>
        }
      />

      {loading && (
        <div className="muted" style={{ textAlign: 'center', padding: 24, fontSize: 13 }}>
          {lang === 'zh' ? '載入中…' : 'Loading…'}
        </div>
      )}

      {error && !loading && (
        <div style={{ padding: '12px 20px', fontSize: 12.5, color: 'var(--danger)' }}>
          {error}
        </div>
      )}

      {/* Contribution rewards — the tangible thank-you. Lifetime credits
          earned from approved submissions / applied reports, plus the current
          usable balance. When nothing is earned yet the strip flips to the
          incentive line, so the mechanic is discoverable exactly where the
          user reviews their contribution history. Hidden while the ledger is
          unreadable (rewards === null) to avoid claiming a false zero. */}
      {!loading && !error && rewards !== null && (
        <div style={{ padding: '12px 20px 0' }}>
          <div className="card" style={{
            padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 10,
          }}>
            <span style={{
              width: 34, height: 34, borderRadius: 10, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'color-mix(in oklch, var(--primary) 12%, transparent)',
              color: 'var(--primary)',
            }}>
              <Icon name="sparkle" size={17} stroke={1.7} />
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              {rewards.earned > 0 ? (
                <>
                  <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 }}>
                    {lang === 'zh'
                      ? <>貢獻獎勵：已獲 <span style={{ color: 'var(--primary)' }}>{rewards.earned}</span> 次 AI 掃描額度</>
                      : <>Contribution rewards: <span style={{ color: 'var(--primary)' }}>{rewards.earned}</span> AI scan {rewards.earned === 1 ? 'credit' : 'credits'} earned</>}
                  </div>
                  <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.45 }}>
                    {lang === 'zh'
                      ? <>
                          {rewards.fromApprovals > 0 && `課程提交 ${rewards.fromApprovals} 次`}
                          {rewards.fromApprovals > 0 && rewards.fromReports > 0 && ' · '}
                          {rewards.fromReports > 0 && `錯誤回報 ${rewards.fromReports} 次`}
                          {credit != null && ` · 現時可用 ${credit.available} 次`}
                        </>
                      : <>
                          {rewards.fromApprovals > 0 && `${rewards.fromApprovals} from submissions`}
                          {rewards.fromApprovals > 0 && rewards.fromReports > 0 && ' · '}
                          {rewards.fromReports > 0 && `${rewards.fromReports} from reports`}
                          {credit != null && ` · ${credit.available} usable now`}
                        </>}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 }}>
                    {lang === 'zh' ? '貢獻可賺取 AI 掃描額度' : 'Contribute to earn AI scan credits'}
                  </div>
                  <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.45 }}>
                    {lang === 'zh'
                      ? '課程提交獲收錄,或錯誤回報被採納套用,每次 +1 次 AI 掃描額度。'
                      : 'Each approved course submission or applied report earns +1 AI scan credit.'}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Section tabs — the old single scroll stacked submissions → reports →
          comments, so whichever list came last was invisible behind the
          longest one. Counts live on the chips (the admin queue pattern), so
          the eyebrow headers inside each section are gone. */}
      {!loading && !error && (submissions.length > 0 || reports.length > 0 || feedback.length > 0) && (
        <div className="profile-tabs" role="tablist" style={{ margin: '12px 0 4px' }}>
          {[
            { id: 'submissions', label_zh: '課程提交', label_en: 'Submissions', count: submissions.length },
            { id: 'reports',     label_zh: '錯誤回報', label_en: 'Reports',     count: reports.length },
            { id: 'feedback',    label_zh: '意見與建議', label_en: 'Comments',   count: feedback.length },
          ].map(t => (
            <button key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              className={'profile-tab' + (tab === t.id ? ' on' : '')}
              onClick={() => setTab(t.id)}>
              {lang === 'zh' ? t.label_zh : t.label_en}
              {t.count > 0 && (
                <span style={{
                  marginLeft: 6, fontSize: 11, padding: '1px 6px',
                  borderRadius: 999, background: tab === t.id ? 'var(--primary)' : 'var(--border-strong)',
                  color: tab === t.id ? '#fff' : 'var(--fg-2)',
                }}>{t.count}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Per-tab empty states — the tab bar renders whenever ANY section has
          content, so an individual tab can still be empty; each hint points
          at the action that fills that tab. */}
      {!loading && !error && (submissions.length > 0 || reports.length > 0 || feedback.length > 0) && (
        <>
          {tab === 'submissions' && submissions.length === 0 && (
            <div className="muted" style={{ textAlign: 'center', padding: '28px 32px', fontSize: 12.5, lineHeight: 1.6 }}>
              {lang === 'zh'
                ? '未有課程提交。在「探索」找不到課程時,可以提交新課程。'
                : 'No course submissions yet. If a course is missing in Discover, you can submit it.'}
            </div>
          )}
          {tab === 'reports' && reports.length === 0 && (
            <div className="muted" style={{ textAlign: 'center', padding: '28px 32px', fontSize: 12.5, lineHeight: 1.6 }}>
              {lang === 'zh'
                ? '未有錯誤回報。在課程詳情按「回報問題」可指正資料錯誤。'
                : 'No reports yet. Tap "Report an issue" on a course detail to flag wrong info.'}
            </div>
          )}
          {tab === 'feedback' && feedback.length === 0 && (
            <div className="muted" style={{ textAlign: 'center', padding: '28px 32px', fontSize: 12.5, lineHeight: 1.6 }}>
              {lang === 'zh'
                ? '未有意見紀錄。在「個人 → 意見或建議」留言,團隊回覆會顯示在這裡。'
                : 'No comments yet. Leave one via Profile → "Leave a comment or suggestion" — team replies appear here.'}
            </div>
          )}
        </>
      )}

      {!loading && !error && submissions.length === 0 && reports.length === 0 && feedback.length === 0 && (
        <div className="vital-empty" style={{ marginTop: 8 }}>
          <div className="vital-empty-icon"><Icon name="check-circ" size={22} stroke={1.7} /></div>
          <div className="vital-empty-title">
            {lang === 'zh' ? '尚未有任何貢獻' : 'No contributions yet'}
          </div>
          <div className="vital-empty-sub">
            {lang === 'zh'
              ? '在「探索」搜尋課程：找不到課程時可以提交新課程,在課程詳情按「回報問題」可指正資料錯誤。你在「意見或建議」留下的意見亦會顯示在這裡。'
              : 'In Discover, search for a course — if it isn\'t listed you can submit it, or tap "Report an issue" on a course detail to flag wrong info. Comments you leave via "Leave a comment or suggestion" also appear here.'}
          </div>
          {onGoDiscover && (
            <div style={{ marginTop: 16 }}>
              <button className="btn btn-primary" onClick={onGoDiscover}>
                <Icon name="compass" size={14} stroke={2} />
                {lang === 'zh' ? '前往探索' : 'Go to Discover'}
              </button>
            </div>
          )}
        </div>
      )}

      {!loading && !error && tab === 'submissions' && submissions.length > 0 && (
        <div style={{ padding: '8px 20px 0' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {submissions.map(s => {
              const title = s.payload?.title_en || s.payload?.title_zh
                || (lang === 'zh' ? '(未命名課程)' : '(untitled course)');
              const canOpen = s.status === 'approved' && s.approved_course_id;
              return (
                <div
                  key={s.id}
                  style={{
                    padding: 12, border: '0.5px solid var(--border)', borderRadius: 10,
                    background: 'var(--surface)',
                    display: 'flex', flexDirection: 'column', gap: 8,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 }}>{title}</div>
                    <StatusPill s={s.status} />
                  </div>
                  <div className="muted-2" style={{ fontSize: 11, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span>{fmtWhen(s.created_at)}</span>
                    {s.source_origin === 'co_submit_from_log' && (
                      <span>· {lang === 'zh' ? '由記錄順帶提交' : 'from logged record'}</span>
                    )}
                  </div>
                  <ProgressTrack status={s.status} finalLabels={{
                    approved:  lang === 'zh' ? '已收錄' : 'In catalogue',
                    rejected:  lang === 'zh' ? '未通過' : 'Not accepted',
                    duplicate: lang === 'zh' ? '重複'   : 'Duplicate',
                    withdrawn: lang === 'zh' ? '已撤回' : 'Withdrawn',
                  }} />
                  {/* ‼️ THE AFFORDANCE THAT REPLACES "SUBMIT IT AGAIN".
                      The 兒科 duplicate was a CORRECTION: a nurse whose provider
                      name came back mangled from the AI had no way to fix it, so
                      she submitted a second time. Editing is only offered while
                      the row is still hers to change — an approved submission is
                      a catalogue row now, and its corrections go through 回報問題
                      instead. Withdraw never deletes. */}
                  {(s.status === 'draft' || s.status === 'pending') && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {(s.status === 'draft' || pendingEditOn) && (
                        <button className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 10px' }}
                                onClick={() => setEditing(s)}>
                          <Icon name="edit" size={13} stroke={1.9} />
                          {lang === 'zh' ? '修改' : 'Edit'}
                        </button>
                      )}
                      <button className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 10px' }}
                              onClick={() => setWithdrawingSub(s)}>
                        {lang === 'zh' ? '撤回' : 'Withdraw'}
                      </button>
                      {s.revision > 1 && (
                        <span className="muted-2" style={{ fontSize: 11, alignSelf: 'center' }}>
                          {lang === 'zh' ? `已修改 ${s.revision - 1} 次` : `edited ${s.revision - 1}×`}
                        </span>
                      )}
                    </div>
                  )}
                  {(s.status === 'rejected' || s.status === 'duplicate') && s.admin_notes && (
                    <div style={{
                      fontSize: 11.5, padding: '6px 8px', borderRadius: 6,
                      background: 'color-mix(in oklch, var(--fg) 5%, transparent)',
                      color: 'var(--fg-2)',
                    }}>
                      <strong>{lang === 'zh' ? '審核備註:' : 'Admin note:'}</strong> {s.admin_notes}
                    </div>
                  )}
                  {canOpen && <ViewCourseBtn courseId={s.approved_course_id} />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && !error && tab === 'reports' && reports.length > 0 && (
        <div style={{ padding: '8px 20px 0' }}>
          {reports.length >= REPORT_FILTER_MIN && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '0 0 10px' }}>
              {['all', 'pending', 'accepted', 'closed'].map(fk => {
                const n = fk === 'all' ? reports.length : (repTally[fk] || 0);
                if (fk !== 'all' && n === 0) return null; // don't offer empty buckets
                const on = repFilter === fk;
                return (
                  <button key={fk}
                    className={'filter-chip' + (on ? ' on' : '')}
                    style={{ padding: '5px 11px', fontSize: 12 }}
                    aria-pressed={on}
                    onClick={() => setRepFilter(fk)}>
                    {lang === 'zh' ? repFilterLabels[fk].zh : repFilterLabels[fk].en}
                    <span style={{ marginLeft: 5, opacity: 0.7 }}>{n}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {visibleReports.map(r => {
              const canOpen = !!r.course_id;
              // applied_at is the source of truth for "accepted"; fall back to
              // the stored status otherwise so the tracker matches the pill.
              const trackStatus = r.applied_at ? 'accepted' : r.status;
              return (
                <div
                  key={r.id}
                  style={{
                    padding: 12, border: '0.5px solid var(--border)', borderRadius: 10,
                    background: 'var(--surface)',
                    display: 'flex', flexDirection: 'column', gap: 8,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 }}>
                      {courseTitle(r.course_id)}
                    </div>
                    <StatusPill s={r.status} />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--fg-2)' }}>
                    <span className="mono" style={{ fontSize: 11 }}>{r.field_path || r.report_type || '—'}</span>
                    {r.suggested_value && (
                      <>
                        {' '}→ <strong>{r.suggested_value}</strong>
                      </>
                    )}
                  </div>
                  <div className="muted-2" style={{ fontSize: 11, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span>{fmtWhen(r.created_at)}</span>
                    {r.applied_at && (
                      <span style={{ color: 'var(--success)' }}>
                        · {lang === 'zh' ? '已採納並套用' : 'accepted & applied'}
                      </span>
                    )}
                  </div>
                  <ProgressTrack status={trackStatus} finalLabels={{
                    approved:  lang === 'zh' ? '已採納' : 'Accepted',
                    rejected:  lang === 'zh' ? '未通過' : 'Not accepted',
                    duplicate: lang === 'zh' ? '重複'   : 'Duplicate',
                  }} />
                  {r.applied_at && r.applied_value && r.applied_value !== r.suggested_value && (
                    <div style={{
                      fontSize: 11.5, padding: '6px 8px', borderRadius: 6,
                      background: 'color-mix(in oklch, var(--success) 8%, transparent)',
                      color: 'var(--fg-2)',
                    }}>
                      {lang === 'zh'
                        ? <>你的回報已被採納。Admin 經核實後將正確值填為 <strong>{r.applied_value}</strong>。</>
                        : <>Your report was accepted. After verification the admin set the value to <strong>{r.applied_value}</strong>.</>}
                    </div>
                  )}
                  {r.status === 'rejected' && r.admin_notes && (
                    <div style={{
                      fontSize: 11.5, padding: '6px 8px', borderRadius: 6,
                      background: 'color-mix(in oklch, var(--fg) 5%, transparent)',
                      color: 'var(--fg-2)',
                    }}>
                      <strong>{lang === 'zh' ? '審核備註:' : 'Admin note:'}</strong> {r.admin_notes}
                    </div>
                  )}
                  {canOpen && <ViewCourseBtn courseId={r.course_id} />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && !error && tab === 'feedback' && feedback.length > 0 && (
        <div style={{ padding: '8px 20px 0' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {feedback.map(f => {
              const cat = feedbackCat(f.category);
              const hasReply = !!(f.admin_reply && f.admin_reply.trim());
              return (
                <div
                  key={f.id}
                  style={{
                    padding: 12, border: '0.5px solid var(--border)', borderRadius: 10,
                    background: 'var(--surface)',
                    display: 'flex', flexDirection: 'column', gap: 8,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className={'cap-pill tone-' + cat.tone} style={{ fontSize: 10 }}>
                      {lang === 'zh' ? cat.zh : cat.en}
                    </span>
                    <FeedbackStatusPill s={f.status} hasReply={hasReply} />
                    <span className="muted-2" style={{ fontSize: 11, marginLeft: 'auto' }}>{fmtWhen(f.created_at)}</span>
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {f.message}
                  </div>
                  <FeedbackTrack status={f.status} hasReply={hasReply} />
                  {hasReply && (
                    <div style={{
                      fontSize: 12.5, padding: '8px 10px', borderRadius: 8, lineHeight: 1.5,
                      background: 'color-mix(in oklch, var(--primary) 8%, transparent)',
                      color: 'var(--fg)',
                      display: 'flex', flexDirection: 'column', gap: 4,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                      <div className="muted-2" style={{ fontSize: 10.5, letterSpacing: '0.04em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Icon name="shield" size={11} stroke={1.8} />
                        {lang === 'zh' ? '團隊回覆' : 'Team reply'}
                        {f.replied_at && (
                          <span style={{ marginLeft: 4, textTransform: 'none', letterSpacing: 0 }}>· {fmtWhen(f.replied_at)}</span>
                        )}
                      </div>
                      {f.admin_reply}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Edit a submission that is still hers to change ──────────────────
          ‼️ THE SERVER RE-RUNS THE DUPLICATE GUARD ON EVERY EDIT. Editing must
          not become a route around it: without that check a nurse could edit
          submission B's title until it matched A and manufacture exactly the
          duplicate the indexes exist to prevent. So a hard collision comes back
          refused, and a soft one asks for the same deliberate confirmation the
          submit sheet asks for. */}
      {editing && (
        <EditSubmissionSheet
          lang={lang}
          submission={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {/* ── Withdraw ────────────────────────────────────────────────────────
          ‼️ THIS DOES NOT DELETE. A submission that reached the admin queue is
          provenance: it is what a reviewer saw and what a credit may have been
          granted against. It becomes 'withdrawn' and stays visible here. */}
      {withdrawingSub && (
        <Sheet open onClose={() => setWithdrawingSub(null)}
               title={lang === 'zh' ? '撤回提交' : 'Withdraw submission'}>
          <div style={{ padding: '4px 20px 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, lineHeight: 1.55 }}>
              {lang === 'zh'
                ? '撤回後這筆提交不會再進入審核，但紀錄會保留在這裡。之後你可以重新提交同一堂課。'
                : "This submission stops going to review, but the record stays here. You can submit the same course again afterwards."}
            </div>
            <button className="btn btn-primary" style={{ background: 'var(--danger)' }}
              onClick={async () => {
                try { await window.DB.withdrawMySubmission(withdrawingSub.id); }
                finally { setWithdrawingSub(null); load(); }
              }}>
              {lang === 'zh' ? '確認撤回' : 'Withdraw'}
            </button>
            <button className="btn btn-ghost" onClick={() => setWithdrawingSub(null)}>
              {lang === 'zh' ? '取消' : 'Cancel'}
            </button>
          </div>
        </Sheet>
      )}

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Profile · Saved sub-section — embedded (non-sheet) version of SavedSheet
// ─────────────────────────────────────────────────────────────────
function ProfileSaved({ lang, savedIds, onOpenCourse }) {
  const saved = (typeof COURSES !== 'undefined' ? COURSES : [])
    .filter(c => savedIds && savedIds.has && savedIds.has(c.id));
  if (saved.length === 0) {
    return (
      <div className="vital-empty" style={{ marginTop: 8 }}>
        <div className="vital-empty-icon"><Icon name="bookmark" size={22} stroke={1.7} /></div>
        <div className="vital-empty-title">
          {lang === 'zh' ? '尚未收藏任何課程' : 'No saved courses yet'}
        </div>
        <div className="vital-empty-sub">
          {lang === 'zh' ? '在課程詳情頁面按書籤圖示即可收藏。' : 'Tap the bookmark icon on a course detail to save it here.'}
        </div>
      </div>
    );
  }
  return (
    <Stagger
      delay={24}
      initialDelay={20}
      style={{ padding: '12px 20px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {saved.map(c => (
        <CourseCard key={c.id} course={c} lang={lang} onClick={() => onOpenCourse && onOpenCourse(c.id)} />
      ))}
    </Stagger>
  );
}

function Stat({ k, v, sub }) {
  return (
    <div>
      <div className="mono muted-2" style={{ fontSize: 9.5, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 6 }}>
        {k}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span className="serif" style={{ fontSize: 28, lineHeight: 1, letterSpacing: '-0.02em' }}>{v}</span>
        <span className="muted-2" style={{ fontSize: 11 }}>{sub}</span>
      </div>
    </div>
  );
}

function ListSection({ title, children, lang }) {
  return (
    <div style={{ marginTop: 22 }}>
      <div className="eyebrow" style={{ padding: '0 20px 8px' }}>{title}</div>
      <div style={{ padding: '0 20px' }}>
        <div className="card flush" style={{ overflow: 'hidden' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function Row({ icon, label, sub, value, trailing, onClick }) {
  const isInteractive = !!onClick;
  const Tag = isInteractive ? 'button' : 'div';
  return (
    <Tag onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '14px 16px',
      width: '100%',
      borderBottom: '0.5px solid var(--border)',
      background: 'transparent',
      border: 0,
      borderBottomWidth: 0.5,
      borderBottomColor: 'var(--border)',
      borderBottomStyle: 'solid',
      font: 'inherit', color: 'inherit', textAlign: 'left',
      cursor: isInteractive ? 'pointer' : 'default',
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 9,
        background: 'var(--primary-soft-2)',
        color: 'var(--primary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon name={icon} size={16} stroke={1.7} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="h-row" style={{ fontSize: 14 }}>{label}</div>
        {sub && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{sub}</div>}
      </div>
      {value && <div className="muted" style={{ fontSize: 13 }}>{value}</div>}
      {trailing}
      {isInteractive && <Icon name="chev-r" size={15} color="var(--fg-3)" stroke={1.6} />}
    </Tag>
  );
}

Object.assign(window, {
  HomeScreen, DiscoverScreen, CourseDetailScreen, RecordScreen,
  SavedSheet, RemindersSheet, RemindersInlineList, LogSheet, ProfileScreen,
  DeleteAccountSheet, BiometricEnrolSheet, OfflineCatalogueScreen,
  PushSoftAskSheet,
});

;Object.assign(typeof window !== 'undefined' ? window : globalThis, { FIELD_LABELS, humanField, sexLabel, userFromProfile, profileFromSession, NoticeStack, CneCycleNotice, FreeCneCard, ROUTE_ROWS_COLLAPSED, RoutePlanCard, PlannerInterestCard, ProHomeSummary, useProTargetHighlight, HomeScreen, CatLegend, walletRingSegments, WalletRingGlyph, WalletStatusChip, WalletCycleNote, WalletCardBody, WalletCtaRow, WALLET_MOTION, WalletCard, CombinedWalletCard, UpcomingRow, UpcomingPreviewDialog, Section, LangPill, DiscoverScreen, GridCourseCard, shareWithReward, RESTRICT_NOTE_DISMISS_KEY, restrictNoteDismissedIds, rememberRestrictNoteDismissal, reactionOutcome, CourseDetailScreen, showVitalToast, openExternalUrl, APP_STORE_APP_ID, openAppStoreReview, PENDING_ENROL_KEY, PENDING_ENROL_TTL_MS, setPendingEnrol, readPendingEnrol, clearPendingEnrol, RegisterDialog, _nchkRowSeq, nchkRowId, NchkDateField, NchkModeChooser, NchkProfileGateForm, auditPackEvidence, NchkPreview, NchkExportSheet, RecordCountInfo, RecordScreen, PendingBlock, SavedSheet, PrivacySheet, RemindersSheet, RemindersInlineList, rolloverMessage, RolloverSheet, CommunityRestriction, buildRestrictionNote, normaliseTitleForMatch, isDuplicateInCycle, logFormFromCourse, logFormFromUnverifiedCourse, AiScanDialog, ContinueLoggingDialog, recordDestination, aiScanUrlProblem, AiUrlDialog, LogSheet, ScheduleFieldButton, SubmissionGuardNotice, SubmitCourseSheet, ScheduleReportInput, SuggestSelect, ReportFieldValueInput, REPORT_TYPE_CONFIG, ReportCourseSheet, OfflineCatalogueScreen, BiometricEnrolSheet, PushSoftAskSheet, ReviewAskSheet, HandleReportSheet, ContribRow, FieldContributionsDialog, HandleInviteSheet, WhatsNewDualLicenseSheet, DeleteAccountSheet, wardHue, WardMemberRow, deriveHandleSuggestion, HandleSetupDialog, WardGroupScreen, safeSectionNeedsCneInfo, ProfileScreen, SecuritySection, NotificationSettingsPage, AdminAiScanPanel, AdminGrowthPanel, AdminOnboardingPanel, AdminScreen, AdminPromoPanel, NOTICE_SEVERITIES, NOTICE_AUDIENCES, NOTICE_LABELS, noticeToLocalInput, noticeFromLocalInput, emptyNoticeForm, AdminNoticesPanel, AdminManagementPanel, AdminRoleConfirmDialog, EvidenceList, ContributorStatLine, AdminReviewCard, AdminFeedbackCard, identityPlan, occurrenceAnchorOk, providerCandidateMatches, IdentityDispositionPanel, SubmitterProviderHint, IDENTITY_VERDICT_REASONS, ProviderIdentityFindings, CompletenessFindings, ConventionFindings, DuplicateReviewFindings, RegistrationLinkFinder, AdminSubmissionCard, AdminReportCard, ContributorHistoryDialog, seedForFieldInput, AdminApplyDialog, handleErrMessage, ProfileEditSection, EditProfilePage, FeedbackSheet, maskLicenseNumber, ProfileOverview, SwipeableNotification, LEAD_MIN, LEAD_MAX, LEAD_CAP, LEAD_QUICK, LeadTimeSheet, RuleEditorSheet, describeDraft, NotificationCenterScreen, CourseReviewsSection, MyReviewsScreen, providerHintState, providerGuardTextOf, providerMatches, providerClipBand, PROVIDER_PANEL_GUTTER, providerPanelGeometry, ProviderPicker, EditSubmissionSheet, MyContributionsScreen, ProfileSaved, Stat, ListSection, Row });
